import React, { useState, useEffect, useReducer, useRef } from 'react';
import { io } from 'socket.io-client';
import { Toaster, toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { handleAuthExpired, handleMfaSetup } from './authExpired';
import ConsolePanel from './ConsolePanel';
import DesignAuditPanel from './DesignAuditPanel';
import ProjectMapPanel from './ProjectMapPanel';   // 🗺 Loyiha xaritasi (backend 205)
import SecretsCard from './SecretsCard';
import TodoPanel from './TodoPanel';
import UiAccessEditor from './UiAccessEditor';   // 🔐 Foydalanuvchi bo'yicha ko'rinish va ruxsatlar (backend docs/knowledge/208)
import ServerActivity from './ServerActivity';
import TeamPanel from './TeamPanel';
import TaskComments from './TaskComments';
import { useEmuAudio, EmuAudioBtn } from './emuAudio';   // 🔊 emulator ovozi (backend 204)
import SecurityPanel from './SecurityPanel';
import SecurityKbPanel from './SecurityKbPanel';
import SecurityModelCard from './SecurityModelCard';
import WaitingPanel from './WaitingPanel';
import { ConductorAdmin, ConductorChat } from './ConductorPanel';
import OperatorChat from './OperatorChat';
import { attachConfirmBlink } from './confirmBlink';   // 🔔 tasdiq kutilsa yorliq sarlavhasi miltillaydi
import OverseerPanel from './OverseerPanel';
import NarrationPlayer from './NarrationPlayer';
import RunVideo from './RunVideo';   // 🎥 etap videosi (docs/knowledge/198)
import { WebDevicesSection, WebDeviceScreen, wdAspect } from './WebDevices';
import PlaybookModal from './PlaybookPanel';
import { OwnerApprovalCard } from './ClientApproval';
import CallCenterPanel from './CallCenterPanel';
import { useTheme } from './theme';
import LangSwitcher from './LangSwitcher';

import { jsPDF } from 'jspdf';

// 🆔 Modul ID belgisi (2026-11-03, operator: «agent "Backend (#4)" deydi, chipda ID yo'q — ikkita frontend bo'lsa farqlab bo'lmaydi»).
// Agent, vazifa va 🔑 Sirlar modulni shu raqam bilan ataydi.
const ModId = ({ id, size = 'text-[11px]', className = '' }) => (id ? <span className={`font-mono ${size} font-normal text-gray-400 dark:text-gray-500 ${className}`} translate="no" title={`Modul ID: #${id} — agent va vazifalarda shu raqam bilan ataladi`}>#{id}</span> : null);

// Reja kartasidagi matndan qaysi konsol kerakligini taxmin qiladi. Topilmasa null —
// u holda operator konsolni o'zi tanlaydi (majburlash noto'g'ri natija berardi).
function guessConsoleProfile(text) {
  const t = String(text || '').toLowerCase();
  if (/app ?store|testflight|apple|asc|ios/.test(t)) return 'apple';
  if (/google|firebase|play ?console|maps|gcp|cloud/.test(t)) return 'google';
  return null;
}

// Sidebar/navbar ikonkalari (Heroicons outline, 24px). Flowbite qobig'ida har bo'lim
// emoji o'rniga ikonka bilan chiziladi; noma'lum nom → bo'sh doira.
const NAV_ICON_PATHS = {
  search: 'm21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z',
  chevronRight: 'm8.25 4.5 7.5 7.5-7.5 7.5',
  check: 'm4.5 12.75 6 6 9-13.5',
  xmark: 'M6 18 18 6M6 6l12 12',
  server: 'M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z',
  refresh: 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99',
  trash: 'm14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0',
  plus: 'M12 4.5v15m7.5-7.5h-15',
  menu: 'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5',
  folder: 'M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z',
  tasks: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z',
  cog: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  phone: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3',
  studio: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
  brush: 'M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  plug: 'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
  wrench: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z',
  chart: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  sparkles: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z',
  megaphone: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  call: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  book: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
  shield: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  lock: 'M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z',
  logout: 'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75',
  sun: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
  terminal: 'M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z',
  moon: 'M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z',
};
// 🌡 Yuklama rangi (operator, 2026-09-25): 🖥 serverlar va ⚡ sarf tugmasi yuklama oshgani sari
// YASHILdan QIZILga silliq o'tadi (bosqichli 3 rang emas). Rang CSS o'zgaruvchisi `--kh-h`
// (HSL hue) orqali beriladi — yorug'/tungi rejim ranglari index.css dagi `.kh-load` da.
// 0% → 142 (yashil) · 50% → 45 (sariq) · 100% → 0 (qizil).
function loadHue(pct) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return Math.round(p <= 50 ? 142 - (p / 50) * 97 : 45 - ((p - 50) / 50) * 45);
}
const loadStyle = (pct) => ({ '--kh-h': loadHue(pct) });

function NavIcon({ name, className = 'w-6 h-6' }) {
  const d = NAV_ICON_PATHS[name];
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.7" stroke="currentColor" aria-hidden="true">
      {d ? <path strokeLinecap="round" strokeLinejoin="round" d={d} /> : <circle cx="12" cy="12" r="9" />}
    </svg>
  );
}

// 🤖 Studio tabi ostidagi «qaysi AI» yozuvi (operator, 2026-09-25): tab qaysi rolni
// ishlatsa, o'sha rolning HOZIRGI modeli (Konfiguratsiya → Claude model darajalari).
// Ichida AI bilvosita ishlatilsa ham (Brauzer/Emulator — jonli sinov agenti, Fayllar — agent yozgan
// kod, Sirlar — agent nomini ko'radi) yozuv bor. AI umuman yo'q tablar
// (Database, Holat, Arxitektura, Publish) — yozuvsiz. [rol, tooltip uchun izoh]
const STUDIO_AI_ROLE = {
  canvas: ['design', 'sahifa dizaynini chizadi'], style: ['design', 'dizayn yo\'nalishi (brief) tuzadi'], logo: ['design', 'logo chizadi'],
  browser: ['qa', 'agent sinovi shu brauzerda ishlaydi'], emulator: ['qa', 'agent sinovi shu emulatorda ishlaydi'],
  files: ['developer', 'bu yerdagi kodni dasturchi agent yozadi'], docs: ['diagnostics', 'loyiha hujjatlarini tuzadi'],
  todo: ['architect', 'rejani tuzadi va vazifaga ajratadi'],
  qa: ['qa', 'sinov stsenariylari va agent sinovi'], secrets: ['developer', 'dasturchi agent sirning faqat NOMINI ko\'radi'],
  crashes: ['developer', 'crash tuzatish vazifasini bajaradi'], security: ['diagnostics', 'topilmalarni tahlil qiladi'],
};
const AI_ROLE_UZ = { architect: 'arxitektor', developer: 'dasturchi', qa: 'sinovchi', diagnostics: 'tahlilchi', design: 'dizayner' };
function prettyModel(id) {
  const m = String(id || '').match(/^claude-(opus|sonnet|haiku|fable)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/);
  if (!m) return String(id || '').replace(/^./, c => c.toUpperCase());
  return `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}${m[3] ? '.' + m[3] : ''}`;
}
function StudioAiTag({ view, ai }) {
  const [role, why] = STUDIO_AI_ROLE[view] || [];
  if (!role || !ai) return null;
  const t = (ai.tiers || []).find(x => x.role === role);
  if (!t) return null;
  const id = (ai.aliases && ai.aliases[t.model]) || t.model;
  return (
    <span translate="no"
      title={`Bu bo'limdagi AI — ${AI_ROLE_UZ[role] || role}: ${why}. Model: ${id}${t.effort ? ` (effort: ${t.effort})` : ''}. O'zgartirish: Konfiguratsiya → Claude model darajalari`}
      className="absolute left-1/2 -translate-x-1/2 top-full mt-0.5 text-[10px] leading-none font-mono font-medium whitespace-nowrap text-primary-600 dark:text-primary-400">
      {prettyModel(id)}{t.effort ? ` · ${t.effort}` : ''}
    </span>
  );
}

const SpinnerIcon = () => (
  <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// ── Poll gigiyenasi ──────────────────────────────────────────────────────────
// Sahifa ko'rinmayotganda (background tab) davriy poll'lar o'tkazib yuboriladi —
// fonda ochiq qolgan har tab serverga doimiy /tasks + /jobs/active + stats yukini
// bermasin. Tab qaytganda keyingi tick'da (≤ interval) o'zi yangilanadi.
const khPageVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

// Reducer to manage project list updates
const projectReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PROJECTS':
      return action.payload;
    case 'ADD_PROJECT':
      return [...state, action.payload];
    case 'UPDATE_PROJECT': {
      const updated = action.payload;
      return state.map(proj =>
        String(proj.id) === String(updated.id) ? { ...proj, ...updated } : proj
      );
    }
    case 'DELETE_PROJECT':
      return state.filter(proj => String(proj.id) !== String(action.payload));
    default:
      return state;
  }
};

// Helper to extract a readable error message from a failed response
const extractErrorMessage = async (response) => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      return data.error || data.message || data.detail || JSON.stringify(data);
    }
    const text = await response.text();
    return text || `Server xatosi (${response.status})`;
  } catch (e) {
    return `Server xatosi (${response.status})`;
  }
};

const FEATURE_LABELS = {
  ai_design: '🎨 AI Dizayn',
  page_redesign: '✏️ Sahifa redesign',
  tz_plan: '📋 TZ rejasi',
  tz_svg_page: '📋 TZ sahifa SVG',
  logo_design: '🏷️ Logo dizayn',
  qa_propose: '🧪 QA taklif',
  stack_pick: '🧱 Stack tanlash',
  deploy_diagnosis: '🩺 Deploy tahlil',
  docker_config: '🐳 Docker sozlash',
  codegen: '🤖 AI Agent',
  codegen_fix: '🔧 AI Agent fix',
  engine_developer: '⚙️ Engine dev',
  engine_qa: '⚙️ Engine QA',
  engine_architect: '⚙️ Engine arxitektor',
};

// ---------- Pipeline status helpers ----------
function PipelineStatus({ status }) {
  const map = { success: ['✅', 'text-green-600 dark:text-green-400'], failed: ['❌', 'text-red-500'], running: ['⏳', 'text-blue-500'], pending: ['🕐', 'text-yellow-500'], canceled: ['⛔', 'text-gray-400'] };
  const [icon, cls] = map[status] || ['❓', 'text-gray-400'];
  return <span className={`font-mono text-xs ${cls}`}>{icon} {status}</span>;
}

function HealthBadge({ status }) {
  if (status === 'up')      return <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">● Ishlayapti</span>;
  if (status === 'down')    return <span className="text-xs bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">● Ishlamayapti</span>;
  return <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">● Noma'lum</span>;
}

// ── Module Detail (Overview + Repository tabs) ─────────────────────────────
function EmulatorCard({ status, logs, onStart, onRestart, onStop, onOpen, onRefresh }) {
  React.useEffect(() => { if (onRefresh) onRefresh(); /* eslint-disable-next-line */ }, []);
  const badge = {
    running:      { t: '● Ishlayapti',          c: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' },
    starting:     { t: '● Ishga tushmoqda...',  c: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' },
    reconnecting: { t: '● Qayta ulanmoqda...',  c: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' },
    stopped:      { t: "● To'xtagan",            c: 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400' },
    not_created:  { t: '● Yaratilmagan',        c: 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400' },
    error:        { t: '● Xato',                 c: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' },
  }[status] || { t: "● Noma'lum", c: 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400' };
  const busy = status === 'starting' || status === 'reconnecting';
  const running = status === 'running';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">📱 Emulator</h3>
          <div className="mt-1"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${badge.c}`}>{badge.t}</span></div>
        </div>
        <button onClick={onRefresh} title="Holatni yangilash" className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">🔄</button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Mobil ilova shu Android emulatorda test qilinadi. Emulator har foydalanuvchi uchun alohida.</p>
      <div className="flex gap-2 flex-wrap">
        {!running && (
          <button onClick={onStart} disabled={busy}
            className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
            {busy ? '⏳ Ishga tushmoqda...' : '🚀 Emulator ishga tushirish'}
          </button>
        )}
        {running && (
          <button onClick={onOpen}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition">📺 Ekranni ochish</button>
        )}
        <button onClick={onRestart} disabled={busy}
          title="Emulatorni qayta ulash / qayta ishga tushirish"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition">🔄 Restart</button>
        {running && (
          <button onClick={onStop} disabled={busy}
            className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition">⏹ To'xtatish</button>
        )}
      </div>
      {logs && logs.length > 0 && (
        <div className="mt-3 bg-gray-900 rounded-lg p-3 max-h-40 overflow-y-auto">
          <div className="font-mono text-xs space-y-0.5">
            {logs.slice(-30).map((l, i) => (
              <div key={i} className={l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : 'text-gray-300'}>{l.msg}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Frontend modules are canonically typed 'web', but older / API-created modules may carry a
// specific framework type ('react', 'vue', …). Treat every frontend-ish type as "web" so the
// Studio 🌐 Brauzer / 🔀 Solishtirish / screenshot features work for any of them (this is why
// TezHal's 'react'-typed Frontend showed no Brauzer — it only matched module_type === 'web').
// Modul turi — YAGONA taksonomiya (backend `services/moduleTypes.js` bilan bir xil qoida).
// Bazada endi faqat web|backend|mobile turadi, lekin eski qatorlar/tashqi chaqiruvchilar
// framework nomini yuborishi mumkin, shuning uchun o'qish tomoni bag'rikeng qoladi.
const MOBILE_MODULE_TYPES  = ['mobile', 'react-native', 'react_native', 'reactnative', 'rn', 'expo', 'flutter', 'android', 'ios', 'kotlin', 'swift'];
const BACKEND_MODULE_TYPES = ['backend', 'node', 'nodejs', 'express', 'nest', 'nestjs', 'api', 'server', 'python', 'django', 'flask', 'fastapi', 'php', 'laravel', 'go', 'golang', 'java', 'spring', 'ruby', 'rails', 'dotnet', 'db', 'database'];
// Cheklov qachon bo'shashini odam o'qiydigan ko'rinishda ("1 soat 26 daq", "45 daq").
// «4s 40d» kun deb o'qilardi (2026-09-29) — birliklar to'liq yoziladi.
// 0 = to'siq yo'q, null = joriy oyna ichida o'zi ochilmaydi (cap juda past).
const fmtWait = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m} daq`;
  const h = Math.floor(m / 60), r = m % 60;
  if (h < 24) return r ? `${h} soat ${r} daq` : `${h} soat`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} kun ${h % 24} soat` : `${d} kun`;
};

// Oyna QACHON yangilanadi (Claude'dagi "Resets in 3 hr 58 min" / "Resets Mon 7:59 AM").
// 24 soatdan yaqin bo'lsa — sanoq ("3 soat 58 daq dan keyin"), uzoq bo'lsa — aniq kun+vaqt
// ("Du 07:59"), chunki "6 kun 4s" ni odam kalendarga solishtira olmaydi.
const UZ_WEEKDAYS = ['Yak', 'Du', 'Se', 'Chor', 'Pay', 'Jum', 'Shan'];
const fmtResetIn = (min) => {
  if (min === null || min === undefined) return null;
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 24 * 60) return `${fmtWait(m)} dan keyin`;   // "2 soat 55 daq dan keyin"
  const d = new Date(Date.now() + m * 60000);
  const p = (n) => String(n).padStart(2, '0');
  return `${UZ_WEEKDAYS[d.getDay()]} ${p(d.getHours())}:${p(d.getMinutes())} da`;   // "Yak 05:01 da"
};

// Deploy BOSQICHI (tasks.deploy_stage) — "🚀 Deploy qilindi" faqat oxirini ko'rsatardi,
// oradagi 4-6 daqiqa (merge → redeploy → APK qurish → telefonga o'rnatish) jim edi.
// 'done' uchun chip yo'q — u yerda allaqachon "🚀 Deploy qilindi" turadi.
// Chip ichidagi izoh QISQA bo'lishi shart: to'liq matn tooltipda. Uzun izoh
// (masalan «kodi main-da (tekshirildi: ahead=0); keyingi APK build…») kartani
// buzib, sarlavhani vertikal harflarga aylantirib qo'yardi.
const shortDetail = (d, max = 34) => {
  const s = String(d || '').replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
};

const DEPLOY_CHIPS = {
  merge:       { label: '🔀 Merge qilinmoqda',       cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', live: true },
  deploy:      { label: '🚀 Deploy qilinmoqda',      cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', live: true },
  apk_build:   { label: '🔨 APK qurilmoqda',         cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', live: true },
  apk_install: { label: '📲 Telefonga o‘rnatilmoqda', cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', live: true },
  prod:        { label: '🚀 Production deploy',      cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', live: true },
  installed:   { label: '📲 Ilova o‘rnatildi',       cls: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300' },
  // Kod main'ga qo'shildi va TEST muhitiga chiqdi, PRODUCTION esa eskiligicha.
  // Ilgari bu holat 'done' edi — chipi yo'q, yonida yashil «🚀 Deploy qilindi»
  // turardi va operator jonli chiqdi deb o'ylardi (#1050).
  test_only:   { label: '🧪 Faqat test env',         cls: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' },
  partial:     { label: '⚠️ Qisman deploy',          cls: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' },
  // Deploy oqimi API qayta ishga tushishi bilan uzilgan — natija NOMA'LUM.
  // Soxta "bajarildi" ko'rsatmaymiz; operator 🔁 Qayta tasdiqlash bilan tugatadi.
  interrupted: { label: '⚠️ Deploy uzildi',           cls: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' },
  failed:      { label: '❌ Deploy xato',            cls: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' },
};

const WEB_MODULE_TYPES     = ['web', 'react', 'vue', 'angular', 'svelte', 'next', 'nextjs', 'frontend'];
const rawModType = (m) => String((m && typeof m === 'object' ? m.module_type : m) || '').trim().toLowerCase();
const canonModType = (m) => {
  const v = rawModType(m);
  if (MOBILE_MODULE_TYPES.includes(v)) return 'mobile';
  if (BACKEND_MODULE_TYPES.includes(v)) return 'backend';
  return 'web';
};
// DIQQAT — predikatlar BO'SH/NULL modulga `false` qaytaradi. canonModType('') = 'web'
// (yozuv defaulti to'g'ri), lekin "modul yo'q" ni "veb" deb o'qish qisqa-tutashuvlarni
// (`isWebModule(x) && x.test_url`) qulatadi — jonli tekshiruvda oq ekran bergan edi.
const knownModType    = (m) => rawModType(m) !== '';
const isWebModule     = (m) => knownModType(m) && canonModType(m) === 'web';
const isMobileModule  = (m) => knownModType(m) && canonModType(m) === 'mobile';
const isBackendModule = (m) => knownModType(m) && canonModType(m) === 'backend';
const MODULE_TYPE_LABEL = { web: '🌐 Web', backend: '⚙️ Backend', mobile: '📱 Mobile' };
const modTypeLabel = (m) => MODULE_TYPE_LABEL[canonModType(m)];

// Claude model darajalari (Konfiguratsiya). «Aniq versiya» ro'yxati endi serverdan
// (`/claude/models` — CLI'ning o'zidan tekshirib topilgan modellar) keladi; KNOWN_MODELS
// faqat katalog yuklanmagan paytdagi ZAXIRA. Aliaslar eng yangi modelga ulanmasligi mumkin.
const MODEL_ALIASES = ['opus', 'sonnet', 'haiku'];
const KNOWN_MODELS = [
  // Eng yangisi TEPADA. NB: `opus`/`sonnet` aliaslari o'rnatilgan Claude Code CLI
  // versiyasiga bog'liq va eng yangi modelga ORQADA qolishi mumkin (o'lchangan
  // 2026-09-05: CLI 2.1.167 → opus=claude-opus-4-8, sonnet=claude-sonnet-4-6).
  // Eng yangi modelni ishlatish uchun uni SHU ro'yxatdan aniq tanlang.
  { value: 'claude-opus-5',              label: 'Opus 5' },
  { value: 'claude-opus-4-8',            label: 'Opus 4.8' },
  { value: 'claude-sonnet-5',            label: 'Sonnet 5' },
  { value: 'claude-haiku-4-5-20251001',  label: 'Haiku 4.5' },
  { value: 'claude-fable-5',             label: 'Fable 5' },
];
/**
 * Model katalogi + CLI holati. «Nimaga Opus 5.5 avtomat paydo bo'lmadi?» savolidan
 * tug'ildi: ro'yxat kodda qo'lda yozilgan edi VA serverdagi Claude Code CLI 3,5 oy
 * eskirib qolgan edi (yangi modellar 400 berardi). Endi server modellarni CLI'ning
 * o'zidan tekshirib topadi; CLI eski bo'lsa yangi model shu yerda ko'rinadi va
 * bitta tugma bilan CLI yangilanadi (bo'sh paytni kutadi, sinaydi, yiqilsa qaytaradi).
 */
function ModelCatalogBar({ authFetch, onModels }) {
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState('');
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const cbRef = React.useRef(onModels); cbRef.current = onModels;
  const load = React.useCallback(async (refresh) => {
    try {
      const r = await afRef.current(`${process.env.REACT_APP_API_URL}/claude/models${refresh ? '?refresh=1' : ''}`);
      const j = await r.json().catch(() => null);
      if (r.ok && j) { setD(j); setErr(''); if (cbRef.current) cbRef.current(j.models || []); }
      else setErr((j && j.error) || 'Katalog o‘qilmadi');
    } catch (e) { setErr(e.message); }
  }, []);
  React.useEffect(() => { load(false); }, [load]);
  const busy = d && (d.refreshing || (d.cliUpdate && d.cliUpdate.running));
  React.useEffect(() => { if (!busy) return; const t = setInterval(() => load(false), 5000); return () => clearInterval(t); }, [busy, load]);
  const startUpdate = async () => {
    if (!window.confirm("Serverdagi Claude Code CLI yangilansinmi?\n\nIshlab turgan vazifalar tugashini kutadi (15 daqiqagacha), yangilaydi va sinab ko'radi. Sinov yiqilsa eski versiyaga o'zi qaytadi.")) return;
    try {
      const r = await afRef.current(`${process.env.REACT_APP_API_URL}/claude/cli-update`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || 'Xato ' + r.status); return; }
      load(false);
    } catch (e) { setErr(e.message); }
  };
  if (!d && !err) return <p className="text-xs text-gray-400 mb-1">⏳ Modellar katalogi yuklanmoqda…</p>;
  const models = (d && d.models) || [];
  const ok = models.filter(m => m.status === 'ok');
  const newer = models.filter(m => m.status === 'cli_old');
  const need = newer.map(m => m.needCli).filter(Boolean).sort().pop();
  const cu = (d && d.cliUpdate) || {};
  const when = d && d.at ? new Date(d.at) : null;
  const hhmm = when ? `${String(when.getDate()).padStart(2, '0')}.${String(when.getMonth() + 1).padStart(2, '0')} ${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}` : '—';
  const resultText = cu.code === 0 ? '✅ CLI yangilandi' : cu.code === 3 ? '⏸ Vazifalar ishlab turgani uchun o‘tkazib yuborildi — keyinroq qayta bosing' : cu.code === 4 ? '↩ Sinov yiqildi — eski versiyaga qaytarildi' : cu.code != null ? `❌ Xato (kod ${cu.code})` : '';
  return (
    <div className="mb-2">
      <p className="text-xs text-gray-400 mb-1">
        <b>Claude Code CLI:</b> <code>{(d && d.cliVersion) || '?'}</code> · <b>{ok.length}</b> ta model ishlaydi · tekshirildi {hhmm}
        <button onClick={() => load(true)} disabled={busy}
          title="Modellarni CLI orqali qayta tekshirish (~1 daqiqa, fonda)"
          className="ml-2 text-[11px] text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-50">
          {d && d.refreshing ? '⏳ tekshirilmoqda…' : '↻ modellarni tekshirish'}
        </button>
      </p>
      {newer.length > 0 && (
        <div className="text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 rounded-lg px-3 py-2 mb-2">
          🆕 <b>Yangi model chiqqan:</b> {newer.map(m => m.label).join(', ')} — lekin serverdagi CLI eski
          (<code>{d.cliVersion}</code>{need ? <>, kerak <code>{need}+</code></> : null}), shuning uchun hozircha tanlab bo'lmaydi.
          <button onClick={startUpdate} disabled={cu.running}
            className="ml-2 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-medium disabled:opacity-50">
            {cu.running ? '⏳ Yangilanmoqda…' : "⬆ CLI'ni yangilash"}
          </button>
        </div>
      )}
      {(cu.running || resultText) && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {cu.running ? '⏳ CLI yangilanmoqda (bo‘sh paytni kutishi mumkin)…' : resultText}
          {cu.log && <details className="mt-1"><summary className="cursor-pointer text-primary-600 dark:text-primary-500">log</summary><pre className="mt-1 max-h-40 overflow-auto bg-gray-900 text-gray-200 p-2 rounded text-[11px] whitespace-pre-wrap">{cu.log.slice(-2500)}</pre></details>}
        </div>
      )}
      {err && <p className="text-xs text-red-600 dark:text-red-400 mb-1">⚠️ {err}</p>}
    </div>
  );
}

/**
 * Alias HOZIR qaysi modelga ulanayotganini ko'rsatadi. Bu savol real hayotda chiqdi:
 * panelda «opus (oxirgi Opus)» yozilgani uchun operator Opus 5 ishlayapti deb o'ylagan,
 * aslida CLI uni claude-opus-4-8 ga ulagan edi. Server 24 soat keshlaydi, shuning uchun
 * bu komponent so'rov yuborishdan qo'rqmaydi; ↻ majburiy qayta o'lchaydi (~20 soniya).
 */
function AliasMap({ authFetch, onLoad }) {
  const [d, setD] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const load = React.useCallback(async (refresh) => {
    setBusy(true);
    try {
      const r = await afRef.current(`${process.env.REACT_APP_API_URL}/claude/alias-map${refresh ? '?refresh=1' : ''}`);
      const j = await r.json().catch(() => null);
      if (r.ok && j && j.map) { setD(j); if (onLoad) onLoad(j.map); }
    } catch { /* jim — bu yordamchi ma'lumot, panel busiz ham ishlaydi */ }
    finally { setBusy(false); }
  }, [onLoad]);
  React.useEffect(() => { load(false); }, [load]);
  if (!d) return null;
  return (
    <p className="text-xs text-gray-400 mb-1">
      <b>Hozir alias ulanayotgan model:</b>{' '}
      {['opus', 'sonnet', 'haiku'].map(a => (
        <span key={a} className="inline-block mr-2">
          <code>{a}</code> → <code className="text-gray-600 dark:text-gray-400">{d.map[a] || '—'}</code>
        </span>
      ))}
      <button onClick={() => load(true)} disabled={busy}
        title="Qayta o'lchash — har alias uchun bitta juda qisqa so'rov (~20 soniya)"
        className="ml-1 text-[11px] text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-50">
        {busy ? '⏳' : '↻ tekshirish'}
      </button>
    </p>
  );
}

const KNOWN_MODEL_VALUES = new Set([...MODEL_ALIASES, ...KNOWN_MODELS.map(m => m.value)]);

function ModuleDetail({
  project, module: mod, onBack,
  emulatorStatus, emulatorLogs, onEmulatorStart, onEmulatorRestart, onEmulatorStop, onOpenEmulator, onRefreshEmulator,
  detailTab, setDetailTab,
  glUrl, setGlUrl, glToken, setGlToken, glBranch, setGlBranch,
  glConnecting, glConnectErr, glInfo, glInfoLoading,
  glCiTemplate, setGlCiTemplate, glCiLoading, glCiMsg,
  onGlConnect, onGlDisconnect, onGlCiSetup, onGlRefresh,
  serverHealth, serverLogs, serverRunning, serverEnvRequired,
  serverEnvVars, setServerEnvVars, onStartTestServer, onRefreshHealth, onProvideEnv,
  onSetupTestEnv, onRedeployTestEnv, onTeardownTestEnv, onProdDeploy,
  onTestEnvWizard, wizardSteps = [],
  aiTaskInstruction, setAiTaskInstruction, onRunAiAgent, onMobileBuild, onAabBuild,
  apiTarget = 'code', onApiTargetChange = () => {},
  authFetch, apiUrl, onModuleUpdate,
}) {
  const [editingConfig, setEditingConfig] = React.useState(false);
  const [cfg, setCfg] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  // Download the built APK to the computer (same as MobilePanel's /m download) — for
  // installing on a real phone. Always available; if no APK is built yet the endpoint
  // returns an error and we toast it.
  const downloadApk = async () => {
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/apk-link`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) { toast.error(data.error || 'APK topilmadi — avval Build + Install qiling'); return; }
      const mb = (data.sizeBytes / 1048576).toFixed(0);
      toast.success(`Yuklab olinmoqda: ${data.filename} (${mb} MB)`);
      window.location.href = `${apiUrl}${data.url}`;   // browser downloads the .apk natively
    } catch (e) { if (e.message !== 'unauthorized') toast.error('Xato: ' + (e.message || '')); }
  };

  // ── AAB (Play Store) + release keystore ────────────────────────────────────
  const [ks, setKs] = React.useState(null);            // {configured, alias, fingerprint, setAt}
  const [ksForm, setKsForm] = React.useState({ alias: 'upload', storePassword: '', keyPassword: '', validityYears: 30, o: '', c: 'UZ' });
  const [ksOpen, setKsOpen] = React.useState(false);   // generate/upload form expanded
  const [ksMode, setKsMode] = React.useState('generate'); // 'generate' | 'upload'
  const [ksFile, setKsFile] = React.useState(null);
  const [ksBusy, setKsBusy] = React.useState(false);

  const loadKeystore = React.useCallback(async () => {
    if (mod.module_type !== 'mobile') return;
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/keystore`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setKs(d);
    } catch (e) { /* unauthorized handled in authFetch */ }
  }, [mod.id, mod.module_type, authFetch, apiUrl]);
  React.useEffect(() => { loadKeystore(); }, [loadKeystore]);

  const generateKeystore = async () => {
    if (!ksForm.alias.trim()) { toast.error('Alias kiriting'); return; }
    if ((ksForm.storePassword || '').length < 6) { toast.error('Parol kamida 6 belgi'); return; }
    setKsBusy(true);
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/keystore/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alias: ksForm.alias.trim(), storePassword: ksForm.storePassword,
          keyPassword: ksForm.keyPassword, validityYears: ksForm.validityYears,
          o: ksForm.o, c: ksForm.c, cn: mod.name,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'Server xatosi');
      toast.success("✅ Keystore yaratildi — zaxira nusxasini yuklab oling (yo'qotsangiz Play update chiqmaydi)!");
      setKsOpen(false); setKsForm(f => ({ ...f, storePassword: '', keyPassword: '' }));
      loadKeystore();
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setKsBusy(false); }
  };

  const uploadKeystore = async () => {
    if (!ksFile) { toast.error('Keystore fayl tanlang (.keystore/.jks)'); return; }
    if (!ksForm.alias.trim() || !ksForm.storePassword) { toast.error('Alias va parol kerak'); return; }
    setKsBusy(true);
    try {
      const fd = new FormData();
      fd.append('keystore', ksFile);
      fd.append('alias', ksForm.alias.trim());
      fd.append('storePassword', ksForm.storePassword);
      fd.append('keyPassword', ksForm.keyPassword || ksForm.storePassword);
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/keystore/upload`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'Server xatosi');
      toast.success('✅ Keystore qabul qilindi');
      setKsOpen(false); setKsFile(null); setKsForm(f => ({ ...f, storePassword: '', keyPassword: '' }));
      loadKeystore();
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setKsBusy(false); }
  };

  const downloadKeystore = async () => {
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/keystore/download-link`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { toast.error(d.error || 'Keystore topilmadi'); return; }
      toast.success(`Yuklab olinmoqda: ${d.filename}`);
      window.location.href = `${apiUrl}${d.url}`;
    } catch (e) { if (e.message !== 'unauthorized') toast.error('Xato: ' + (e.message || '')); }
  };

  const downloadAab = async () => {
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}/mobile/aab-link`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { toast.error(d.error || 'AAB topilmadi — avval AAB Build qiling'); return; }
      const mb = (d.sizeBytes / 1048576).toFixed(0);
      toast.success(`Yuklab olinmoqda: ${d.filename} (${mb} MB)`);
      window.location.href = `${apiUrl}${d.url}`;
    } catch (e) { if (e.message !== 'unauthorized') toast.error('Xato: ' + (e.message || '')); }
  };

  const connected = !!mod.gitlab_project_id;
  const tabs = [
    { key: 'overview', label: '📋 Overview' },
    { key: 'repository', label: '🔗 Repository' },
  ];

  const startEdit = () => { setCfg({ ...mod }); setEditingConfig(true); };
  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${apiUrl}/project-modules/${mod.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
      });
      if (r.ok) { const updated = await r.json(); onModuleUpdate(updated); setEditingConfig(false); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Saqlanmadi'); }
    } finally { setSaving(false); }
  };

  const envForm = serverEnvRequired && (
    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-5">
      <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-3">📋 .env o'zgaruvchilari kerak</h3>
      {serverEnvRequired.envExample && <pre className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-2 mb-3 overflow-auto">{serverEnvRequired.envExample}</pre>}
      <div className="space-y-2 mb-4">
        {serverEnvRequired.envVars.map(v => (
          <div key={v} className="flex items-center gap-2">
            <span className="font-mono text-xs text-gray-600 dark:text-gray-400 w-48 shrink-0">{v}</span>
            <input type={v.toLowerCase().match(/pass|secret|key/) ? 'password' : 'text'} placeholder={v}
              value={serverEnvVars[v] || ''}
              onChange={e => setServerEnvVars(p => ({ ...p, [v]: e.target.value }))}
              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs font-mono focus:ring-2 focus:ring-amber-400 outline-none" />
          </div>
        ))}
      </div>
      <button onClick={onProvideEnv} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition">✅ Davom etish</button>
    </div>
  );

  const terminal = serverLogs.length > 0 && (
    <div className="bg-gray-900 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 font-mono">Terminal</span>
        {serverRunning && <span className="text-xs text-green-400 animate-pulse">● Ishlayapti</span>}
      </div>
      <div className="font-mono text-xs space-y-0.5 max-h-64 overflow-y-auto">
        {serverLogs.map((l, i) => (
          <div key={i} className={l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : l.type === 'ai' ? 'text-yellow-300 whitespace-pre-wrap' : 'text-gray-300'}>{l.log}</div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg">←</button>
        <span className="text-gray-400 text-sm">{project?.name}</span>
        <span className="text-gray-300">›</span>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">{mod.name} <ModId id={mod.id} size="text-sm" /></h1>
        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{mod.module_type}</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setDetailTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${detailTab === t.key ? 'bg-white dark:bg-gray-800 border border-b-white border-gray-200 dark:border-gray-700 -mb-px text-primary-700 dark:text-primary-500' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {detailTab === 'overview' && (
        <div className="space-y-4">
          {/* Config card — hidden for mobile (no server test-env) */}
          {mod.module_type !== 'mobile' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">⚙️ Server konfiguratsiya</h3>
              {!editingConfig && <button onClick={startEdit} className="text-xs text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200">✎ Tahrirlash</button>}
            </div>
            {editingConfig ? (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[['Remote Host', 'remote_host'], ['SSH User', 'ssh_user'], ['Deploy Path', 'deploy_path'], ['Restart Cmd', 'restart_cmd'], ['Test URL', 'test_url'], ['Production URL', 'production_url']].map(([label, key]) => (
                  <div key={key}>
                    <span className="text-gray-400 block mb-0.5">{label}</span>
                    <input value={cfg[key] || ''} onChange={e => setCfg(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-primary-500 outline-none" />
                  </div>
                ))}
                <div className="col-span-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">🚀 Production server</span>
                  <span className="text-gray-400"> (test'dan alohida — sizning serveringiz)</span>
                </div>
                {[['Prod Host', 'prod_remote_host'], ['Prod SSH User', 'prod_ssh_user'], ['Prod Deploy Path', 'prod_deploy_path']].map(([label, key]) => (
                  <div key={key}>
                    <span className="text-gray-400 block mb-0.5">{label}</span>
                    <input value={cfg[key] || ''} onChange={e => setCfg(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-primary-500 outline-none" />
                  </div>
                ))}
                <div className="col-span-2">
                  <span className="text-gray-400 block mb-0.5">Deploy/Restart Cmd <span className="text-gray-300">(masalan: git pull && docker compose up -d --build)</span></span>
                  <input value={cfg.prod_restart_cmd || ''} onChange={e => setCfg(p => ({ ...p, prod_restart_cmd: e.target.value }))}
                    placeholder="git pull && docker compose up -d --build"
                    className="w-full border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-primary-500 outline-none" />
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400 block mb-0.5">Prod .env fayli <span className="text-gray-300">(🔑 Sirlar shu faylga yoziladi; deploy path'ga nisbiy, vergul bilan. Bo'sh = .env, backend/.env)</span></span>
                  <input value={cfg.prod_env_file || ''} onChange={e => setCfg(p => ({ ...p, prod_env_file: e.target.value }))}
                    placeholder=".env.backend"
                    className="w-full border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-primary-500 outline-none" />
                </div>
                <div className="col-span-2 flex gap-2 mt-1">
                  <button onClick={saveConfig} disabled={saving} className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded text-xs hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50">Saqlash</button>
                  <button onClick={() => setEditingConfig(false)} className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-300 dark:hover:bg-gray-600">Bekor</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[['Remote Host', mod.remote_host], ['SSH User', mod.ssh_user], ['Deploy Path', mod.deploy_path], ['Restart Cmd', mod.restart_cmd], ['Test URL', mod.test_url, true], ['Production URL', mod.production_url, true], ['Prod Host', mod.prod_remote_host], ['Prod SSH User', mod.prod_ssh_user], ['Prod Deploy Path', mod.prod_deploy_path], ['Prod Deploy Cmd', mod.prod_restart_cmd], ['Prod .env fayli', mod.prod_env_file]].map(([label, val, isLink]) => val ? (
                  <div key={label}>
                    <span className="text-gray-400 block mb-0.5">{label}</span>
                    {isLink ? <a href={val} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-500 hover:underline font-mono break-all">{val}</a>
                      : <span className="font-mono text-gray-700 dark:text-gray-300 break-all">{val}</span>}
                  </div>
                ) : null)}
                {!mod.remote_host && !mod.test_url && <p className="text-gray-400 col-span-2">Konfiguratsiya yo'q</p>}
              </div>
            )}
          </div>
          )}

          {/* Mobile → emulator controls; otherwise → server test/prod cards */}
          {mod.module_type === 'mobile' ? (
            <>
            <EmulatorCard
              status={emulatorStatus}
              logs={emulatorLogs}
              onStart={onEmulatorStart}
              onRestart={onEmulatorRestart}
              onStop={onEmulatorStop}
              onOpen={() => onOpenEmulator && onOpenEmulator(mod)}
              onRefresh={onRefreshEmulator}
            />
            {/* Build + install the APK on the infra server, run it on the emulator */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">🔨 APK Build + Install</h3>
                <span className="text-xs text-gray-400">
                  {mod.gitlab_project_id ? '✅ Repo ulangan' : '⚠ avval GitLab ulang'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Server repo'dan APK quriladi (React Native → Gradle), so'ng sizning
                emulatoringizga o'rnatilib ishga tushiriladi. Bo'sh repo bo'lsa avtomatik
                RN shabloni bilan to'ldiriladi.
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={apiTarget}
                  onChange={e => onApiTargetChange(e.target.value)}
                  title="Ilova QAYSI backendga ulanishi (build oldidan BASE_URL almashtiriladi; repoga commit qilinmaydi)"
                  className={`border rounded-lg px-2 py-2 text-sm bg-white dark:bg-gray-800 focus:outline-none ${apiTarget === 'production' ? 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-400' : apiTarget === 'test' ? 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}
                >
                  <option value="code">🔗 API: koddagidek</option>
                  <option value="test">🧪 API: test env</option>
                  <option value="production">🚀 API: production</option>
                </select>
                <button
                  onClick={onMobileBuild}
                  disabled={serverRunning || !mod.gitlab_project_id}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition">
                  {serverRunning ? '⏳ Build qilinmoqda...' : '🔨 Build + Install'}
                </button>
                <button
                  onClick={downloadApk}
                  title="So'nggi build qilingan APK faylni kompyuterga yuklab olish — haqiqiy telefonga o'rnatish uchun"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                  📥 APK yuklab olish
                </button>
              </div>
            </div>

            {/* AAB (Android App Bundle) — Play Store uchun imzolangan bundle + keystore */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">📦 AAB (Google Play)</h3>
                <span className="text-xs text-gray-400">
                  {ks?.configured ? '🔐 Keystore tayyor' : '⚠ keystore kerak'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Google Play'ga yuklash uchun <b>imzolangan .aab</b> (Android App Bundle) quriladi.
                Imzolash uchun <b>release keystore</b> kerak — uni shu yerda yaratasiz (yoki mavjudini yuklaysiz).
              </p>

              {/* Keystore holati / yaratish */}
              {ks?.configured ? (
                <div className="mb-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800 rounded-lg">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-emerald-800 dark:text-emerald-300">
                      🔐 Keystore: <b>{ks.alias}</b>
                      {ks.fingerprint ? <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5 font-mono break-all">SHA-256: {ks.fingerprint}</span> : null}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={downloadKeystore} title="Keystore'ni zaxiraga yuklab oling — yo'qotsangiz Play'ga update chiqara olmaysiz!"
                        className="px-2.5 py-1 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition">📥 Keystore (zaxira)</button>
                      <button onClick={() => { setKsOpen(o => !o); setKsMode('generate'); }}
                        className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">{ksOpen ? '✕' : '♻ Almashtirish'}</button>
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-2">⚠️ Keystore'ni xavfsiz saqlang. Yo'qolsa yoki almashtirilsa, Play'dagi mavjud ilovaga update chiqarib bo'lmaydi (yangi ilova sifatida qayta yuklash kerak bo'ladi).</p>
                </div>
              ) : (
                <button onClick={() => { setKsOpen(o => !o); setKsMode('generate'); }}
                  className="mb-3 px-3 py-1.5 text-xs font-medium bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 transition">
                  {ksOpen ? '✕ Yopish' : '🔐 Keystore sozlash'}
                </button>
              )}

              {ksOpen && (
                <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
                  <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 text-xs mb-1">
                    <button type="button" onClick={() => setKsMode('generate')}
                      className={`flex-1 px-2 py-1.5 font-medium ${ksMode === 'generate' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>🔐 Yangi yaratish</button>
                    <button type="button" onClick={() => setKsMode('upload')}
                      className={`flex-1 px-2 py-1.5 font-medium ${ksMode === 'upload' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>⬆️ Mavjudini yuklash</button>
                  </div>
                  {ksMode === 'upload' && (
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400">Keystore fayl (.keystore / .jks)
                      <input type="file" accept=".keystore,.jks,.p12,.pfx" onChange={e => setKsFile(e.target.files?.[0] || null)}
                        className="block w-full mt-0.5 text-xs" />
                    </label>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400">Key alias
                      <input value={ksForm.alias} onChange={e => setKsForm(f => ({ ...f, alias: e.target.value }))}
                        placeholder="upload" className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1" />
                    </label>
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400">Validity (yil)
                      <input type="number" min="1" value={ksForm.validityYears} disabled={ksMode === 'upload'}
                        onChange={e => setKsForm(f => ({ ...f, validityYears: e.target.value }))}
                        className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1 disabled:bg-gray-100 dark:disabled:bg-gray-700" />
                    </label>
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400">Keystore parol
                      <input type="password" value={ksForm.storePassword} onChange={e => setKsForm(f => ({ ...f, storePassword: e.target.value }))}
                        placeholder="≥6 belgi" className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1" />
                    </label>
                    <label className="block text-[11px] text-gray-500 dark:text-gray-400">Key parol (ixtiyoriy)
                      <input type="password" value={ksForm.keyPassword} onChange={e => setKsForm(f => ({ ...f, keyPassword: e.target.value }))}
                        placeholder="bo'sh = keystore parol" className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1" />
                    </label>
                    {ksMode === 'generate' && <>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400">Tashkilot (O)
                        <input value={ksForm.o} onChange={e => setKsForm(f => ({ ...f, o: e.target.value }))}
                          placeholder={mod.name} className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1" />
                      </label>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400">Davlat kodi (C)
                        <input value={ksForm.c} maxLength={2} onChange={e => setKsForm(f => ({ ...f, c: e.target.value.toUpperCase() }))}
                          placeholder="UZ" className="w-full mt-0.5 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1" />
                      </label>
                    </>}
                  </div>
                  <button onClick={ksMode === 'generate' ? generateKeystore : uploadKeystore} disabled={ksBusy}
                    className="w-full mt-1 px-3 py-1.5 text-xs font-semibold bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
                    {ksBusy ? '⏳ Bajarilmoqda...' : ksMode === 'generate' ? '🔐 Keystore yaratish' : '⬆️ Yuklash va saqlash'}
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={onAabBuild}
                  disabled={serverRunning || !mod.gitlab_project_id || !ks?.configured}
                  title={!ks?.configured ? 'Avval keystore sozlang' : 'Imzolangan AAB build qiladi (~bir necha daqiqa)'}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition">
                  {serverRunning ? '⏳ Bajarilmoqda...' : '📦 AAB Build'}
                </button>
                <button
                  onClick={downloadAab}
                  title="So'nggi build qilingan imzolangan .aab faylni yuklab olish — Google Play Console'ga yuklash uchun"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                  📥 AAB yuklab olish
                </button>
              </div>
            </div>
            </>
          ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">🧪 Test Server</h3>
                  <div className="mt-1"><HealthBadge status={serverHealth?.test} /></div>
                  {mod.test_url && (
                    <a href={mod.test_url} target="_blank" rel="noreferrer" className="text-xs text-primary-700 dark:text-primary-500 hover:underline break-all font-mono">{mod.test_url}</a>
                  )}
                </div>
                <button onClick={onRefreshHealth} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">🔄</button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {mod.test_url && serverHealth?.test === 'up' && (
                  <a href={mod.test_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition">🌐 Ochish</a>
                )}
                {mod.module_type !== 'mobile' && !mod.test_url && (
                  <div className="w-full">
                    {/* BITTA TUGMA: repo ulash / kod yozish / env ko'tarish — hammasi shu yerda.
                        Ilgari operator uch joyni (Repository tabi → AI Agent → bu karta) bilishi
                        kerak edi va har biri faqat "avval X qiling" deb yozardi. */}
                    <button onClick={onTestEnvWizard} disabled={serverRunning}
                      title="Repo yo'q bo'lsa yaratadi, kod yo'q bo'lsa ishlaydigan skelet yozadi, keyin Docker + baza + subdomen + TLS ni ko'taradi"
                      className="w-full px-4 py-2.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
                      {serverRunning ? '⏳ Yaratilmoqda…' : '🚀 Test muhitini yaratish'}
                    </button>
                    <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">
                      Hammasi avtomatik: GitLab repo → boshlang'ich kod → Docker + baza + subdomen + TLS.
                      {!mod.gitlab_project_id && ' Repo hali ulanmagan — sehrgar uni o\'zi yaratadi.'}
                    </p>
                    {wizardSteps.length > 0 && (
                      <ol className="mt-3 space-y-1.5">
                        {wizardSteps.map(st => {
                          const icon = st.status === 'ok' ? '✅' : st.status === 'fail' ? '❌'
                            : st.status === 'running' ? '⏳' : '○';
                          const cls = st.status === 'ok' ? 'text-green-700 dark:text-green-400' : st.status === 'fail' ? 'text-red-600 dark:text-red-400'
                            : st.status === 'running' ? 'text-primary-800 dark:text-primary-300 font-medium' : 'text-gray-400';
                          return (
                            <li key={st.key} className={`text-xs flex items-start gap-2 ${cls}`}>
                              <span className={st.status === 'running' ? 'animate-pulse' : ''}>{icon}</span>
                              <span className="min-w-0">
                                {st.label}
                                {st.detail && <span className="text-gray-400 font-normal"> — {st.detail}</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                )}
                {mod.test_url && (
                  <>
                    <button onClick={onRedeployTestEnv} disabled={serverRunning}
                      title="git pull + container restart"
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition">
                      {serverRunning ? '⏳' : '♻ Redeploy'}
                    </button>
                    <button onClick={onTeardownTestEnv} disabled={serverRunning}
                      title="Test env to'liq o'chiriladi (kod GitLab'da qoladi)"
                      className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition">
                      🗑 O'chirish
                    </button>
                  </>
                )}
                {mod.module_type === 'mobile' && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 italic">Mobile: emulator ishlaydi</span>
                )}
              </div>
            </div>
            <SecretsCard
              moduleId={mod.id}
              authFetch={authFetch}
              apiUrl={process.env.REACT_APP_API_URL}
              serverRunning={serverRunning}
              hasProd={!!(mod.prod_remote_host && mod.prod_deploy_path)}
            />
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">🚀 Production Server</h3>
                  <div className="mt-1"><HealthBadge status={serverHealth?.production} /></div>
                  {mod.production_url && (
                    <a href={mod.production_url} target="_blank" rel="noreferrer" className="text-xs text-primary-700 dark:text-primary-500 hover:underline break-all font-mono">{mod.production_url}</a>
                  )}
                  {mod.prod_remote_host && <div className="text-[11px] text-gray-400 font-mono mt-0.5">🖥 {mod.prod_ssh_user}@{mod.prod_remote_host}</div>}
                </div>
                <button onClick={onRefreshHealth} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">🔄</button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(mod.prod_remote_host && mod.prod_deploy_path && mod.prod_restart_cmd) ? (
                  <button onClick={onProdDeploy} disabled={serverRunning}
                    title="Backend prod serverga SSH qiladi → deploy/restart buyrug'ini bajaradi"
                    className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
                    {serverRunning ? '⏳ Deploy...' : '🚀 Deploy'}
                  </button>
                ) : (
                  <span className="text-xs text-gray-400 italic">⚙️ Avval Production konfiguratsiyani to'ldiring (yuqorida ✎ Tahrirlash)</span>
                )}
                {mod.production_url && serverHealth?.production === 'up' && (
                  <a href={mod.production_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition">🌐 Ochish</a>
                )}
              </div>
            </div>
          </div>
          )}
          {/* AI Agent panel — sends a Claude Code task to the module's repo (all module types) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">🤖 AI Agent (Claude Code)</h3>
                <span className="text-xs text-gray-400">
                  {mod.gitlab_project_id ? '✅ Repo ulangan' : '⚠ avval GitLab ulang'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Vazifa beriling — Claude Code <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">AGENTS.md</code>'ni
                o'qiydi, kod yozadi, commit qiladi va GitLab'ga push qiladi{mod.module_type === 'mobile' ? '. Keyin APK build qilib emulatorda test qiling.' : ' va test env avto-redeploy bo\'ladi.'}
              </p>
              <textarea
                value={aiTaskInstruction}
                onChange={e => setAiTaskInstruction(e.target.value)}
                placeholder={mod.module_type === 'mobile'
                  ? "Misol: React Native bilan login ekrani yarating — email/parol input va 'Kirish' tugmasi bilan"
                  : "Misol: Express + Postgres bilan oddiy 'GET /api/regions' endpoint yarating, regions jadval bilan migration qo'shing"}
                rows={4}
                disabled={serverRunning}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none font-mono resize-y disabled:bg-gray-50 dark:disabled:bg-gray-700"
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-400">
                  Model: Claude (developer darajasi)
                </span>
                <button
                  onClick={onRunAiAgent}
                  disabled={serverRunning || !aiTaskInstruction.trim() || !mod.gitlab_project_id}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition"
                >
                  {serverRunning ? '⏳ Ishlamoqda...' : '🚀 Vazifani yuborish'}
                </button>
              </div>
            </div>
          {envForm}
          {terminal}
        </div>
      )}

      {/* Repository */}
      {detailTab === 'repository' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300">GitLab ulanish</h2>
              {connected && <button onClick={onGlDisconnect} className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300">Uzish</button>}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <input value={glUrl} onChange={e => setGlUrl(e.target.value)} placeholder="https://gitlab.com/namespace/repo"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none font-mono" />
              <input value={glToken} onChange={e => setGlToken(e.target.value)} placeholder={connected ? "🔒 Token saqlangan — o'zgartirish uchun yangisini kiriting (bo'sh = eskisi qoladi)" : "Personal Access Token (glpat-...)"} type="password"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none font-mono" />
              <div className="flex gap-2 items-center">
                <input value={glBranch} onChange={e => setGlBranch(e.target.value)} placeholder="main"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm w-32 focus:ring-2 focus:ring-primary-500 outline-none" />
                <button onClick={onGlConnect} disabled={glConnecting || !glUrl || (!glToken && !connected)}
                  className="px-4 py-2 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
                  {glConnecting ? 'Ulanmoqda...' : connected ? '🔄 Yangilash' : '🔗 Ulash'}
                </button>
              </div>
              {glConnectErr && <p className="text-red-500 text-xs">{glConnectErr}</p>}
              {connected && <p className="text-green-600 dark:text-green-400 text-xs">✅ Ulangan · Project ID: {mod.gitlab_project_id}</p>}
            </div>
          </div>
          {connected && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-700 dark:text-gray-300">Repo ma'lumotlari</h2>
                <button onClick={onGlRefresh} className="text-xs text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200">🔄 Yangilash</button>
              </div>
              {glInfoLoading ? <p className="text-gray-400 text-sm">Yuklanmoqda...</p> : glInfo ? (
                <div className="space-y-4">
                  <div className="flex gap-4 text-sm flex-wrap">
                    <span><span className="text-gray-400">Nomi: </span><span className="font-medium">{glInfo.project?.name}</span></span>
                    <span><span className="text-gray-400">Branch: </span><span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{mod.gitlab_branch}</span></span>
                    <a href={glInfo.project?.web_url} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-500 hover:underline text-xs">GitLab'da ochish →</a>
                  </div>
                  {glInfo.lastCommit && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-xs">
                      <span className="text-gray-400 block mb-1">Oxirgi commit</span>
                      <span className="font-mono text-gray-700 dark:text-gray-300">{glInfo.lastCommit.short_id}</span>
                      <span className="mx-2 text-gray-400">·</span>
                      <span className="text-gray-700 dark:text-gray-300">{glInfo.lastCommit.title}</span>
                      <span className="mx-2 text-gray-400">·</span>
                      <span className="text-gray-500 dark:text-gray-400">{glInfo.lastCommit.author_name}</span>
                    </div>
                  )}
                  {glInfo.pipelines?.length > 0 && (
                    <div>
                      <span className="text-gray-400 text-xs block mb-2">So'nggi pipelinelar</span>
                      <div className="space-y-1">
                        {glInfo.pipelines.map(pl => (
                          <div key={pl.id} className="flex items-center gap-3 text-xs bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                            <PipelineStatus status={pl.status} />
                            <span className="text-gray-400">#{pl.id}</span>
                            <span className="font-mono text-gray-500 dark:text-gray-400">{pl.ref}</span>
                            <span className="text-gray-400 ml-auto">{new Date(pl.created_at).toLocaleDateString('uz-UZ')}</span>
                            <a href={pl.web_url} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-500 hover:underline">Ko'rish</a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : <p className="text-gray-400 text-sm">Ma'lumot yuklanmadi</p>}
            </div>
          )}
          {connected && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">CI/CD sozlash</h2>
              <div className="flex gap-3 items-center flex-wrap">
                <select value={glCiTemplate} onChange={e => setGlCiTemplate(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none">
                  <option value="node">Node.js</option>
                  <option value="react">React</option>
                  <option value="flutter">Flutter</option>
                  <option value="react-native">React Native</option>
                </select>
                <button onClick={onGlCiSetup} disabled={glCiLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition">
                  {glCiLoading ? 'Yuklanmoqda...' : '⚙️ .gitlab-ci.yml yaratish'}
                </button>
                {glCiMsg && <span className="text-sm">{glCiMsg}</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Project Detail (modules list) ───────────────────────────────────────────
function ProjectDetail({ project, modules, onBack, onOpenModule, onAddModule, onDeleteModule }) {
  // Kanonik uchta tur — platforma AYNAN shular bo'yicha shoxlanadi (emulator/APK =
  // mobile, DB+migratsiya = backend, qolgani web). Ilgari bu yerda framework nomlari
  // ('react-native', 'node'...) turardi va ular modulni jimgina VEB qilib qo'yardi.
  const MODULE_TYPES = [
    { value: 'web',     label: '🌐 Web (sayt / admin panel)' },
    { value: 'backend', label: '⚙️ Backend (API + baza)' },
    { value: 'mobile',  label: '📱 Mobile (emulator + APK)' },
  ];
  const [newName, setNewName]           = React.useState('');
  const [newPath, setNewPath]           = React.useState('');
  const [newType, setNewType]           = React.useState('web');
  const [adding, setAdding]             = React.useState(false);
  const [showAdd, setShowAdd]           = React.useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    await onAddModule({ name: newName.trim(), workspace_path: newPath.trim(), module_type: newType });
    setNewName(''); setNewPath(''); setNewType('web'); setShowAdd(false); setAdding(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg">←</button>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">{project.name}</h1>
        {project.description && <span className="text-gray-500 dark:text-gray-400 text-sm">{project.description}</span>}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-700 dark:text-gray-300">Modullar</h2>
        <button onClick={() => setShowAdd(v => !v)} className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-800 dark:hover:bg-primary-700 transition">
          + Modul qo'shish
        </button>
      </div>

      {showAdd && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4 grid grid-cols-2 gap-2">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Modul nomi (masalan: Backend)"
            className="col-span-2 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
          <input value={newPath} onChange={e => setNewPath(e.target.value)} placeholder="Workspace path (masalan: /var/www/backend)"
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
          <select value={newType} onChange={e => setNewType(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none">
            {MODULE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="col-span-2 flex gap-2">
            <button onClick={handleAdd} disabled={adding || !newName.trim()}
              className="px-4 py-2 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">
              {adding ? 'Qo\'shilmoqda...' : '+ Qo\'shish'}
            </button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition">Bekor</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {modules.length === 0 && <p className="text-gray-400 text-sm">Hali modul qo'shilmagan</p>}
        {modules.map(m => (
          <div key={m.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 flex items-center gap-4 hover:border-primary-200 dark:hover:border-primary-700 transition">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900 dark:text-white">{m.name} <ModId id={m.id} /></span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${isMobileModule(m) ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400' : isBackendModule(m) ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'}`}>{modTypeLabel(m)}</span>
                {m.gitlab_project_id && <span className="text-xs bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">🔗 GitLab</span>}
                {m.remote_host && <span className="text-xs bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">🖥 {m.remote_host}</span>}
              </div>
              {m.workspace_path && <p className="text-xs text-gray-400 font-mono truncate">{m.workspace_path}</p>}
              {m.test_url && <p className="text-xs text-primary-500 font-mono truncate">{m.test_url}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => onOpenModule(m)}
                className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-800 dark:hover:bg-primary-700 transition">
                ⚙ Ochish
              </button>
              <button onClick={() => onDeleteModule(m.id)}
                className="px-2 py-1.5 text-red-400 hover:text-red-600 dark:hover:text-red-300 text-xs transition">✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TokenStats({ authFetch, apiUrl, flyout }) {
  const [summary, setSummary] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  // ⚡ flyout rejimi (tepadagi tugma): popup ochiq-yopiqligi
  const [open, setOpen] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [histTotal, setHistTotal] = React.useState(0);
  const [histOffset, setHistOffset] = React.useState(0);
  // 📅 Tarix modalidagi SANA FILTRI — 'YYYY-MM-DD' (brauzer zonasi); bo'sh = hammasi.
  // Davr uchun jami token + xususiyat taqsimoti backenddan keladi (chiplar davrga ergashadi).
  const [histRange, setHistRange] = React.useState({ from: '', to: '' });
  const [histTokens, setHistTokens] = React.useState(0);
  const [histFeat, setHistFeat] = React.useState(null);
  // «Nimaga sarflandi» bloki ochiq/yopiq — tanlov brauzerda eslab qolinadi (default: yopiq).
  const [featOpen, setFeatOpen] = React.useState(() => {
    try { return localStorage.getItem('kh_tok_feat_open') === '1'; } catch { return false; }
  });
  const toggleFeat = () => setFeatOpen(o => {
    const n = !o;
    try { localStorage.setItem('kh_tok_feat_open', n ? '1' : '0'); } catch {}
    return n;
  });

  const loadSummary = React.useCallback(() => {
    authFetch(`${apiUrl}/token-usage/summary`)
      .then(r => r.json()).then(setSummary).catch(() => {});
  }, [authFetch, apiUrl]);

  // range = { from, to } 'YYYY-MM-DD'; kun boshi/oxiri BRAUZER zonasida ISO ga o'giriladi
  // (backend UTC bilan solishtiradi — config/db.js timezone:'Z').
  const loadHistory = React.useCallback((offset = 0, range = { from: '', to: '' }) => {
    const qs = new URLSearchParams({ limit: 20, offset });
    if (range.from) qs.set('from', new Date(`${range.from}T00:00:00`).toISOString());
    if (range.to)   qs.set('to',   new Date(`${range.to}T23:59:59.999`).toISOString());
    authFetch(`${apiUrl}/token-usage/history?${qs}`)
      .then(r => r.json())
      .then(d => {
        setHistory(d.rows || []); setHistTotal(d.total || 0); setHistOffset(offset);
        setHistTokens(Number(d.tokensTotal) || 0);
        setHistFeat(Array.isArray(d.byFeature) ? d.byFeature : null);
      })
      .catch(() => {});
  }, [authFetch, apiUrl]);
  const applyRange = (r) => { setHistRange(r); loadHistory(0, r); };
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // Oxirgi N kun (bugun bilan birga): 1 = bugun, 7 = bir hafta, 30 = bir oy.
  const presetRange = (days) => { const to = new Date(); const from = new Date(); from.setDate(from.getDate() - (days - 1)); return { from: ymd(from), to: ymd(to) }; };
  const rangeActive = !!(histRange.from || histRange.to);

  React.useEffect(() => {
    loadSummary();
    const t = setInterval(() => { if (khPageVisible()) loadSummary(); }, 60000);
    return () => clearInterval(t);
  }, [loadSummary]);

  if (!summary) return null;
  // MENING sarfim (myLimits) — global emas. Ilgari bu panel BUTUN engine sarfini
  // ko'rsatardi, ya'ni foydalanuvchi boshqalarning tokenini o'ziniki deb o'ylardi.
  // cap === null → admin bu foydalanuvchiga shaxsiy cheklov qo'ymagan.
  const limits = summary.myLimits || summary.limits || {};
  const five = limits.fiveHour || { tokens: 0, cap: null, percent: 0, requests: 0 };
  const week = limits.weekly   || { tokens: 0, cap: null, percent: 0, requests: 0 };
  const fmtTok = (n) => { n = Number(n) || 0; return n >= 1e6 ? (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M' : n >= 1e3 ? Math.round(n / 1e3) + 'K' : String(n); };
  const barColor = (p) => p >= 100 ? 'bg-red-500' : p >= 80 ? 'bg-orange-400' : p >= 50 ? 'bg-yellow-400' : 'bg-emerald-400';
  const txtColor = (p) => p >= 100 ? 'text-red-400' : p >= 80 ? 'text-orange-400' : 'text-gray-300';

  const pct5 = five.cap > 0 ? Number(five.percent) || 0 : null;
  // Cheklov yo'q bo'lsa tugma xira (foiz yo'q); bo'lsa rang foizga qarab silliq — loadStyle().
  const flyBtnCls = 'bg-white/70 dark:bg-gray-800/70 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800';
  const pctTxt = (p) => p >= 100 ? 'text-red-600 dark:text-red-400' : p >= 80 ? 'text-orange-600 dark:text-orange-400' : p >= 50 ? 'text-yellow-700 dark:text-yellow-400' : 'text-emerald-700 dark:text-emerald-400';
  // Bitta oyna (5 soatlik / haftalik) — popup uchun batafsil blok.
  const renderWindow = (icon, label, w) => (
    <div key={label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">{icon} {label}</div>
        {w.cap > 0
          ? <div className={`text-2xl font-bold leading-none ${pctTxt(w.percent)}`}>{w.percent}%</div>
          : <div className="text-2xl font-bold leading-none text-gray-900 dark:text-white">{fmtTok(w.tokens)}</div>}
      </div>
      {w.cap > 0 ? (
        <>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 overflow-hidden mt-2">
            <div className={`h-2 rounded-full transition-all ${barColor(w.percent)}`} style={{ width: `${Math.min(w.percent, 100)}%` }}/>
          </div>
          <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1.5">{fmtTok(w.tokens)} / {fmtTok(w.cap)} token{w.requests ? ` · ${w.requests} ta so'rov` : ''}</div>
        </>
      ) : (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">shaxsiy cheklov qo'yilmagan{w.requests ? ` · ${w.requests} ta so'rov` : ''}</div>
      )}
      {fmtResetIn(w.resetsInMinutes) && (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5"
             title="Oynadagi eng eski sarf shu paytda eskiradi (Claude'dagi blok tugashi kabi) — hisob shunda bo'shaydi. Yangi sarf qo'shilsa vaqt suriladi.">
          ♻️ {fmtResetIn(w.resetsInMinutes)} yangilanadi
        </div>
      )}
      {w.cap > 0 && w.percent >= 100 && (
        <div className="text-[11px] mt-1 font-medium text-orange-700 dark:text-orange-300">
          {w.freeInMinutes > 0 ? `⏳ ${fmtWait(w.freeInMinutes)} dan keyin o'zi davom etadi` : '⏸ cheklov tugadi — engine yangi ish boshlamaydi'}
        </div>
      )}
    </div>
  );

  return (
    <>
      {flyout ? (
        <>
          {/* ⚡ Tepadagi tugma — 5 soatlik foiz ikonkaning o'zida (⏳ Jarayonlar / 🩺 naqshi) */}
          <button onClick={() => { setOpen(o => !o); loadSummary(); }}
            title={pct5 === null ? `Mening sarfim — 5 soatda ${fmtTok(five.tokens)} token (cheklov yo'q)` : `Mening sarfim — 5 soatlik ${five.percent}% · haftalik ${week.percent}%`}
            className={`h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm text-sm font-medium transition border
              ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600' : pct5 === null ? flyBtnCls : 'kh-load'}`}
            style={!open && pct5 !== null ? loadStyle(pct5) : undefined}>
            <span className="text-base leading-none">⚡</span>
            <span className="text-xs font-bold font-mono leading-none">{pct5 === null ? fmtTok(five.tokens) : `${pct5}%`}</span>
          </button>
          {open && (
            <div className="fixed top-16 right-4 z-50 w-[26rem] max-w-[92vw] max-h-[75vh] overflow-auto rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">⚡ Mening sarfim</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">engine tokenlari — faqat sizning vazifalaringiz</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={loadSummary} title="Yangilash" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm px-1">↻</button>
                  <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none">✕</button>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {renderWindow('⏱', '5 soatlik', five)}
                {renderWindow('📅', 'Haftalik', week)}
                <div className="grid grid-cols-3 gap-2">
                  {[['Bugun', summary.totals?.today], ['7 kun', summary.totals?.week], ['Jami', summary.totals?.all_time]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2.5 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{l}</div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white font-mono">{fmtTok(v)}</div>
                    </div>
                  ))}
                </div>
                {(summary.byFeature || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Nimaga sarflandi</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(summary.byFeature || []).slice(0, 8).map(f => (
                        <span key={f.feature} className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-[11px]">
                          <span className="font-medium text-gray-800 dark:text-gray-200">{FEATURE_LABELS[f.feature] || f.feature}</span>
                          <span className="text-gray-400 ml-1">{fmtTok(f.tokens)} · {f.calls}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 leading-snug">
                  Bu Khorezmai engine'ining O'Z hisobi (sizning vazifalaringiz) — Anthropic'ning haqiqiy Max hisoblagichi emas.
                  Hisoblagich kesh-o'qishlarni ham sanaydi, shuning uchun raqam kattaroq ko'rinadi.
                </p>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5">
                <button onClick={() => { setOpen(false); setShowHistory(true); loadHistory(0, histRange); }}
                  className="w-full text-left text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline">
                  🕐 To'liq tarix — har so'rov, xususiyat va tokenlar →
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
      <div
        className="mb-3 px-2 cursor-pointer group"
        onClick={() => { setShowHistory(true); loadHistory(0, histRange); }}
        title="Token tarixini ko'rish"
      >
        {/* Engine's OWN token usage — 5-hour + weekly windows (NOT the real shared Max limit) */}
        <div className="mb-1 p-2 rounded-lg bg-gray-800 space-y-2">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide">Mening sarfim</div>
          {[{ icon: '⏱', label: '5 soatlik', w: five }, { icon: '📅', label: 'Haftalik', w: week }].map(({ icon, label, w }) => (
            <div key={label}>
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-gray-400">{icon} {label}</span>
                {w.cap > 0
                  ? <span className={`font-mono font-semibold ${txtColor(w.percent)}`}>{w.percent}%</span>
                  : <span className="font-mono text-gray-500 dark:text-gray-400">{fmtTok(w.tokens)}</span>}
              </div>
              {w.cap > 0 ? (
                <>
                  <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-1.5 rounded-full transition-all ${barColor(w.percent)}`} style={{ width: `${Math.min(w.percent, 100)}%` }}/>
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{fmtTok(w.tokens)} / {fmtTok(w.cap)} token{w.requests ? ` · ${w.requests} ta` : ''}</div>
                  {fmtResetIn(w.resetsInMinutes) && (
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5"
                         title="Oynadagi eng eski sarf shu paytda eskiradi (Claude'dagi blok tugashi kabi) — hisob shunda bo'shaydi. Yangi sarf qo'shilsa vaqt suriladi.">
                      ♻️ {fmtResetIn(w.resetsInMinutes)} yangilanadi
                    </div>
                  )}
                  {/* Cheklovga yetgan bo'lsa — QACHON o'zi bo'shashini aytamiz.
                      Busiz operator "nega to'xtab qoldi, qachongacha?" deb qoladi. */}
                  {w.percent >= 100 && (
                    <div className="text-[10px] mt-0.5 text-orange-300">
                      {w.freeInMinutes === null || w.freeInMinutes === undefined
                        ? '⏸ cheklov tugadi'
                        : w.freeInMinutes > 0
                          ? `⏳ ${fmtWait(w.freeInMinutes)} dan keyin o'zi davom etadi`
                          : '⏸ cheklov tugadi'}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                  cheklov yo'q{w.requests ? ` · ${w.requests} ta so'rov` : ''}
                  {fmtResetIn(w.resetsInMinutes) ? ` · ♻️ ${fmtResetIn(w.resetsInMinutes)} yangilanadi` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 px-1 group-hover:text-gray-400 transition">🕐 Tarixni ko'rish</p>
      </div>
      )}

      {showHistory && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">📊 Token tarixi</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Hafta: <b className={week.percent >= 100 ? 'text-red-500' : 'text-emerald-500'}>{fmtTok(week.tokens)} token ({week.percent}%)</b> &nbsp;•&nbsp;
                  Bugun: <b>{(parseInt(summary.totals?.today) || 0).toLocaleString()} tok</b>
                </p>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl">×</button>
            </div>

            {/* 📅 Sana filtri — presetlar + aniq oraliq. Quyidagi taqsimot ham, jadval ham shu davrga ergashadi. */}
            <div className="px-6 py-2 border-b dark:border-gray-700 flex flex-wrap items-center gap-1.5 shrink-0">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mr-1">📅 Davr:</span>
              {[['Bugun', presetRange(1)], ['7 kun', presetRange(7)], ['30 kun', presetRange(30)], ['Hammasi', { from: '', to: '' }]].map(([l, r]) => {
                const act = histRange.from === r.from && histRange.to === r.to;
                return (
                  <button key={l} type="button" onClick={() => applyRange(r)}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition ${act
                      ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                    {l}
                  </button>
                );
              })}
              <span className="mx-1 text-gray-300 dark:text-gray-600">|</span>
              <input type="date" value={histRange.from} max={histRange.to || undefined} title="Boshlanish sanasi"
                onChange={e => applyRange({ ...histRange, from: e.target.value })}
                className="px-2 py-1 rounded-lg text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" />
              <span className="text-xs text-gray-400">–</span>
              <input type="date" value={histRange.to} min={histRange.from || undefined} title="Tugash sanasi"
                onChange={e => applyRange({ ...histRange, to: e.target.value })}
                className="px-2 py-1 rounded-lg text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" />
              {rangeActive && (
                <button type="button" onClick={() => applyRange({ from: '', to: '' })} title="Filtrni tozalash"
                  className="ml-1 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">✕</button>
              )}
            </div>

            {/* Xususiyat bo'yicha taqsimot — ochib-yopiladigan (tanlov eslab qolinadi).
                Davr tanlangan bo'lsa backenddan kelgan davr taqsimoti, aks holda umumiy. */}
            {(() => {
              const feat = histFeat || summary.byFeature || [];
              const featTotal = feat.reduce((a, f) => a + (parseInt(f.tokens) || 0), 0);
              return (
                <div className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700 shrink-0">
                  <button type="button" onClick={toggleFeat}
                    className="w-full flex items-center justify-between gap-3 px-6 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 shrink-0">
                      <span className="inline-block w-3 text-gray-400">{featOpen ? '▾' : '▸'}</span>Nimaga sarflandi
                      <span className="font-normal text-gray-400 ml-1">— {feat.length} ta xususiyat · {fmtTok(featTotal)} tok{rangeActive ? ' (tanlangan davr)' : ''}</span>
                    </span>
                    {!featOpen && feat.length > 0 && (
                      <span className="text-[11px] text-gray-400 truncate">
                        {feat.slice(0, 3).map(f => `${FEATURE_LABELS[f.feature] || f.feature} ${fmtTok(f.tokens)}`).join(' · ')}{feat.length > 3 ? ` · +${feat.length - 3}` : ''}
                      </span>
                    )}
                  </button>
                  {featOpen && (
                    <div className="px-6 pb-3 flex flex-wrap gap-2 max-h-44 overflow-y-auto">
                      {feat.length === 0 && <span className="text-xs text-gray-400">Bu davrda sarf yo'q</span>}
                      {feat.map(f => (
                        <span key={f.feature} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1 text-xs">
                          <span className="font-medium">{FEATURE_LABELS[f.feature] || f.feature}</span>
                          <span className="text-gray-400 ml-1">{parseInt(f.tokens).toLocaleString()} tok · {f.calls} marta</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* History table */}
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Sana</th>
                    <th className="text-left px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Xususiyat</th>
                    <th className="text-left px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">So'rov</th>
                    <th className="text-right px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Tokenlar</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400">
                      {rangeActive ? 'Bu davrda sarf yo\'q' : 'Hali sarf yo\'q'}
                    </td></tr>
                  )}
                  {history.map(row => (
                    <tr key={row.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString('uz-UZ', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap">
                        <span className="bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-0.5 rounded-full">
                          {FEATURE_LABELS[row.feature] || row.feature}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate" title={row.prompt_preview}>
                        {row.prompt_preview || '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-right font-mono font-semibold text-gray-900 dark:text-white">
                        {(row.total_tokens || 0).toLocaleString()} tok
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination + davr jami */}
            <div className="px-6 py-3 border-t dark:border-gray-700 flex justify-between items-center shrink-0">
              <span className="text-xs text-gray-400">
                {histTotal > 0 ? `${histOffset + 1}–${Math.min(histOffset + 20, histTotal)} / ` : ''}{histTotal} ta so'rov
                <span className="ml-1">· <b className="font-mono text-gray-600 dark:text-gray-300">{histTokens.toLocaleString()} tok</b>{rangeActive ? ' (tanlangan davr)' : ' (jami)'}</span>
              </span>
              {histTotal > 20 && (
                <div className="flex gap-2">
                  <button disabled={histOffset === 0} onClick={() => loadHistory(histOffset - 20, histRange)}
                    className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">← Oldingi</button>
                  <button disabled={histOffset + 20 >= histTotal} onClick={() => loadHistory(histOffset + 20, histRange)}
                    className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">Keyingi →</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Studio inspector: for a real page, show which backend API(s) it calls and which
// DB tables those touch. Self-contained — fetches /projects/:id/page-api-map on demand.
function BackendLinkInfo({ projectId, real, authFetch, apiUrl }) {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  // Re-analyze when the selected real page changes.
  React.useEffect(() => { setData(null); setErr(''); }, [real && real.relPath, real && real._moduleId]);

  const analyze = async () => {
    setLoading(true); setErr(''); setData(null);
    try {
      const r = await authFetch(`${apiUrl}/projects/${projectId}/page-api-map?moduleId=${real._moduleId}&relPath=${encodeURIComponent(real.relPath)}`);
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || d.reason || 'tahlil xatosi');
      setData(d);
    } catch (e) { setErr((e && e.message) || 'Tahlil xatosi'); }
    finally { setLoading(false); }
  };

  const mColor = (m) => m === 'GET' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400' : m === 'POST' ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : m === 'DELETE' ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400';

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">⚙️ Backend bog'lanishi</p>
        <button onClick={analyze} disabled={loading} className="text-[11px] text-primary-700 dark:text-primary-500 hover:text-primary-900 dark:hover:text-primary-200 disabled:opacity-50 font-semibold">{loading ? '⏳...' : data ? '↻ Qayta' : '🔍 Tahlil'}</button>
      </div>
      {err && <p className="text-[11px] text-red-500">{err}</p>}
      {!data && !err && !loading && <p className="text-[11px] text-gray-400">Qaysi API + jadval ishlatilganini ko'rish uchun "Tahlil".</p>}
      {data && ((data.calls || []).length === 0 ? (
        <p className="text-[11px] text-gray-400">API chaqiruvi topilmadi.</p>
      ) : (
        <div className="space-y-1.5">
          {(data.calls || []).map((c, i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-[11px]">
              <div className="flex items-center gap-1.5">
                <span className={`font-mono font-bold px-1.5 py-0.5 rounded ${mColor(c.method)}`}>{c.method}</span>
                <span className="font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{c.path}</span>
              </div>
              {c.matched ? (
                <>
                  <p className="text-gray-400 font-mono mt-0.5 truncate">→ {c.handler.file}:{c.handler.line}</p>
                  {c.tables.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.tables.map(t => <span key={t} className="bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 px-1.5 py-0.5 rounded font-mono">🗄 {t}</span>)}
                    </div>
                  )}
                </>
              ) : <p className="text-amber-500 mt-0.5">⚠ backend handler topilmadi</p>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Admin: engine self-throttle. Shows the engine's OWN 5h/weekly token usage and
// lets the admin set caps + flip enforcement so overnight work won't exhaust the
// shared Max limit the owner needs. (Not the real Max counter — see engineLimit.js.)
// ⏳ Jarayonlar — barcha loyihalardagi engine vazifalari + fon jarayonlari
// 📦 Modullar holati — har bir modul (backend/web/mobile) qaysi vazifa (task) kodida
// ishlayotgani, u tasdiqlangan (main) yoki review branch (kh/task-N) ekani, va mobil
// APK qachon build qilingani. Chalkashmaslik uchun — qaysi qism qaysi versiyada.
// 🏛 Arxitektura — loyiha modullari HAQIQATAN qaysi texnologiyalarda qurilgani (backend repo
// manifestlaridan aniqlaydi: package.json/requirements/composer + test-env stack + infra). Taxmin emas.
const ARCH_CATS = [
  ['runtime', 'Til / muhit', '🧬', 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'],
  ['framework', 'Freymvork', '🏗️', 'bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'],
  ['mobile', 'Mobil', '📱', 'bg-purple-50 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'],
  ['db', "Ma'lumotlar bazasi", '🗄️', 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  ['auth', 'Auth va xavfsizlik', '🔐', 'bg-red-50 text-red-800 dark:bg-red-900/40 dark:text-red-200'],
  ['realtime', 'Real vaqt', '⚡', 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'],
  ['ai', 'AI / OCR', '🤖', 'bg-pink-50 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200'],
  ['integration', 'Integratsiyalar', '🔌', 'bg-teal-50 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'],
  ['ui', 'UI', '🎨', 'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200'],
  ['testing', 'Test', '🧪', 'bg-green-50 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
  ['tooling', 'Asboblar', '🧰', 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'],
  ['infra', 'Infratuzilma', '☁️', 'bg-sky-50 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
];
const ARCH_CAT = Object.fromEntries(ARCH_CATS.map(([k, label, icon, cls]) => [k, { label, icon, cls }]));

// 📥 Mavjud loyihani qo'shish — repo havolalari (GitLab/GitHub) → Khorezmai loyihasi + modullar (turi koddan) +
// workspace klon + (ixtiyoriy) test muhiti. Fon-job; qadamlar jonli ko'rinadi.
function ImportProjectModal({ open, onClose, authFetch, apiUrl, onDone }) {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [repos, setRepos] = React.useState([{ url: '', token: '', module_type: '', name: '', branch: '' }]);
  const [provision, setProvision] = React.useState(true);
  const [job, setJob] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => { const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Xato ' + r.status); return j; }, [apiUrl]);
  React.useEffect(() => { if (!job || job.status !== 'running') return; const t = setInterval(() => api(`/projects/import/${job.id}`).then(setJob).catch(() => {}), 3000); return () => clearInterval(t); }, [job, api]);
  if (!open) return null;
  const setRepo = (i, k, v) => setRepos(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try { const r = await api('/projects/import', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, description, repos: repos.filter(x => x.url.trim()), provision }) }); const j = await api(`/projects/import/${r.importId}`); setJob(j); }
    catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const done = job && job.status !== 'running';
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto p-5">
        <div className="flex items-start justify-between mb-3"><div><h2 className="font-bold text-gray-900 dark:text-white text-lg">📥 Mavjud loyihani qo'shish</h2><p className="text-xs text-gray-500 dark:text-gray-400">Tayyor kod bilan davom eting: repo havolalarini bering — Khorezmai modul turini koddan aniqlaydi, klonlaydi va (xohlasangiz) test muhitini ko'taradi. Token bilan berilgan GitHub, Gitea yoki o'z GitLab serveringizdagi repo to'g'ridan ulanadi (nusxa qilinmaydi); tokensiz ochiq repo GitLab'ga import qilinadi. Bir egadagi repolar uchun tokenni bir marta kiritish yetadi.</p></div><button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white text-xl leading-none">✕</button></div>
        {!job ? (
          <form onSubmit={submit} className="space-y-3 text-sm">
            <div className="grid gap-2 sm:grid-cols-2"><input required value={name} onChange={e => setName(e.target.value)} placeholder="Loyiha nomi" className="border border-gray-300 rounded-lg px-3 py-2" /><input value={description} onChange={e => setDescription(e.target.value)} placeholder="Tavsif (ixtiyoriy)" className="border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div className="space-y-2">
              {repos.map((r, i) => (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 grid gap-2 sm:grid-cols-6">
                  <input required={i === 0} value={r.url} onChange={e => setRepo(i, 'url', e.target.value)} placeholder="https://gitlab.com/… yoki https://github.com/…" className="border border-gray-300 rounded-lg px-2.5 py-1.5 sm:col-span-6" />
                  <select value={r.module_type} onChange={e => setRepo(i, 'module_type', e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 sm:col-span-2"><option value="">Tur: avtomatik (koddan)</option><option value="backend">⚙️ Backend</option><option value="web">🌐 Web</option><option value="mobile">📱 Mobile</option></select>
                  <input value={r.name} onChange={e => setRepo(i, 'name', e.target.value)} placeholder="Modul nomi (ixtiyoriy)" className="border border-gray-300 rounded-lg px-2.5 py-1.5 sm:col-span-2" />
                  <input value={r.branch} onChange={e => setRepo(i, 'branch', e.target.value)} placeholder="branch (default)" className="border border-gray-300 rounded-lg px-2.5 py-1.5" />
                  <input value={r.token} onChange={e => setRepo(i, 'token', e.target.value)} placeholder="token (privat bo'lsa)" type="password" className="border border-gray-300 rounded-lg px-2.5 py-1.5" />
                  {repos.length > 1 && <button type="button" onClick={() => setRepos(rs => rs.filter((_, j) => j !== i))} className="text-xs text-red-500 text-left sm:col-span-6">🗑 olib tashlash</button>}
                </div>))}
              <button type="button" onClick={() => setRepos(rs => [...rs, { url: '', token: '', module_type: '', name: '', branch: '' }])} className="text-sm text-primary-700 dark:text-primary-400 hover:underline">➕ yana repo (backend / frontend / mobil alohida)</button>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={provision} onChange={e => setProvision(e.target.checked)} /> Backend/web modullar uchun test muhitini darhol ko'tarish (Docker + HTTPS, ~1 daqiqa/modul)</label>
            {err && <div className="text-sm text-red-600">❌ {err}</div>}
            <div className="flex gap-2 justify-end"><button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600">Bekor</button><button disabled={busy} className="px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white disabled:opacity-50">{busy ? '⏳ …' : '📥 Qo\'shish'}</button></div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-300">{job.status === 'running' ? '⏳ Ishlayapti… (yopib ketsangiz ham davom etadi — ⏳ Jarayonlar)' : job.status === 'done' ? '🎉 Tayyor' : '❌ Xato: ' + (job.error || '')}</div>
            <div className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs max-h-72 overflow-auto font-mono space-y-0.5">{(job.log || []).map((l, i) => <div key={i} className={l.level === 'error' ? 'text-red-300' : l.level === 'warn' ? 'text-amber-300' : ''}>{l.m}</div>)}</div>
            {job.result && (job.result.modules || []).length > 0 && (
              <div className="text-sm"><div className="font-medium text-gray-900 dark:text-white mb-1">Modullar:</div><ul className="space-y-0.5">{job.result.modules.map(m => <li key={m.id} className="text-gray-700 dark:text-gray-300">{m.type === 'backend' ? '⚙️' : m.type === 'mobile' ? '📱' : '🌐'} <b>{m.name}</b> · {m.branch} · <span className="text-gray-500">{m.detected}</span>{m.testUrl && <> · <a className="text-primary-700 dark:text-primary-400 hover:underline" href={m.testUrl} target="_blank" rel="noreferrer">🧪 test</a></>}</li>)}</ul>
                {(job.result.warnings || []).length > 0 && <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">{job.result.warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}</div>}</div>)}
            <div className="flex gap-2 justify-end">{done && job.project_id && <button onClick={() => { onDone && onDone(job.project_id); onClose(); }} className="px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white">🏗️ Loyihaga o'tish</button>}<button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600">Yopish</button></div>
          </div>)}
      </div>
    </div>
  );
}

// 📚 INTEGRATSIYA HUJJATLARI — hamkorning protokoli integratsiya kartasining ichida.
// Operator: «Integratsiyalarni ichida kerakli dokumentatsiyalarni ham qo'y».
// Fayl (.docx/PDF/HTML/matn), yopishtirilgan matn yoki havola. Yuklash paytida
// MATN ajratiladi — shundan keyin uni dev agent ham, Telegram guruh agenti ham o'qiydi.
// Komponent TOP-LEVEL: karta ichida e'lon qilinsa har renderda qayta yaratilib fokus ketardi.
function IntegrationDocs({ apiUrl, authFetch, integrationId, integrationName, projectId, onChanged }) {
  const [items, setItems] = React.useState(null);
  const [mode, setMode] = React.useState('');      // '' | 'text' | 'url'
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [view, setView] = React.useState(null);    // ochilgan hujjat matni
  const fileRef = React.useRef(null);
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => {
    const r = await afRef.current(`${apiUrl}${path}`, opts);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Xato ' + r.status);
    return j;
  }, [apiUrl]);
  const load = React.useCallback(() => api(`/integrations/${integrationId}/docs${projectId ? '?projectId=' + projectId : ''}`)
    .then(d => setItems(d.items || [])).catch(e => setErr(e.message)), [api, integrationId, projectId]);
  React.useEffect(() => { load(); }, [load]);

  const post = async (payload) => {
    setBusy(true); setErr('');
    try {
      const r = await api(`/integrations/${integrationId}/docs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, projectId: projectId || undefined }),
      });
      if (r.warn) setErr(r.warn);
      setMode(''); setTitle(''); setBody('');
      await load(); if (onChanged) onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true); setErr('');
    try {
      for (const f of Array.from(files).slice(0, 5)) {
        const fd = new FormData(); fd.append('file', f);
        const r = await afRef.current(`${apiUrl}/upload-file`, { method: 'POST', body: fd });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'Yuklanmadi');
        await post({ kind: 'file', path: j.path, name: j.name, size: j.size, title: j.name });
      }
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const del = async (d) => {
    if (!window.confirm(`«${d.title}» hujjati o'chirilsinmi?`)) return;
    try { await api(`/integrations/docs/${d.id}`, { method: 'DELETE' }); await load(); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const show = async (d) => {
    try { const full = await api(`/integrations/docs/${d.id}`); setView(full); } catch (e) { setErr(e.message); }
  };
  const icon = (d) => d.kind === 'url' ? '🔗' : d.kind === 'text' ? '📝' : '📄';
  const inp = 'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-2 py-1.5 text-xs';

  return (
    <div className="mt-2 border-t border-gray-200 dark:border-gray-700 pt-2">
      {err && <div className="text-[11px] text-amber-700 dark:text-amber-400 mb-1">⚠️ {err}</div>}
      {items === null && <div className="text-[11px] text-gray-400">⏳</div>}
      {items && items.length === 0 && <div className="text-[11px] text-gray-400">Hujjat yo'q — hamkor bergan protokolni shu yerga qo'ying.</div>}
      {items && items.map(d => (
        <div key={d.id} className="flex items-center gap-1.5 text-[11px] py-0.5">
          <button onClick={() => show(d)} className="truncate text-left text-primary-700 dark:text-primary-400 hover:underline flex-1" title={d.title}>
            {icon(d)} {d.title}
          </button>
          {!d.project_id && <span className="text-[9px] px-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">umumiy</span>}
          <span className={`text-[9px] px-1 rounded ${d.text_len ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}
                title={d.text_len ? 'AI shu matnni o’qiydi' : 'Matn ajratilmadi — AI o’qiy olmaydi'}>
            {d.text_len ? `${Math.round(d.text_len / 1000)}k` : '0'}
          </span>
          <button onClick={() => del(d)} className="text-gray-400 hover:text-red-500">✕</button>
        </div>))}
      <div className="flex gap-1.5 mt-2 flex-wrap">
        <input ref={fileRef} type="file" multiple accept=".docx,.pdf,.md,.txt,.html,.htm,.csv,.json,.yml,.yaml,.xml" className="hidden"
               onChange={e => { upload(e.target.files); e.target.value = ''; }} />
        <button disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">📎 Fayl</button>
        <button disabled={busy} onClick={() => setMode(m => m === 'text' ? '' : 'text')} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">📝 Matn</button>
        <button disabled={busy} onClick={() => setMode(m => m === 'url' ? '' : 'url')} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">🔗 Havola</button>
        {busy && <span className="text-[11px] text-gray-400 self-center">⏳ yuklanmoqda…</span>}
      </div>
      {mode && (
        <div className="mt-2 space-y-1.5">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Sarlavha (masalan: Merchant API protokoli)" className={inp} />
          {mode === 'text'
            ? <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} placeholder="Hujjat matnini shu yerga yopishtiring — AI aynan shuni o'qiydi" className={inp} />
            : <input value={body} onChange={e => setBody(e.target.value)} placeholder="https://developer.help.paycom.uz/…" className={inp} />}
          <div className="flex gap-1.5">
            <button disabled={busy || !body.trim()} onClick={() => post(mode === 'text' ? { kind: 'text', title, text: body } : { kind: 'url', title, url: body })}
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-primary-700 dark:bg-primary-600 text-white disabled:opacity-50">Saqlash</button>
            <button onClick={() => { setMode(''); setBody(''); setTitle(''); }} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600">Bekor</button>
          </div>
        </div>)}
      {view && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 dark:text-white truncate">{view.title}</div>
                <div className="text-xs text-gray-500">{integrationName}{view.url ? ' · ' : ''}{view.url && <a href={view.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{view.url}</a>}</div>
              </div>
              <div className="flex items-center gap-2">
                {view.file_path && <a href={`${apiUrl}${view.file_path}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600">⬇ Fayl</a>}
                <button onClick={() => setView(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">✕</button>
              </div>
            </div>
            <pre className="p-4 overflow-auto text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200">{view.text_content || '(matn ajratilmadi)'}</pre>
          </div>
        </div>)}
    </div>
  );
}

// 🤖 TELEGRAM GURUH AGENTI — hamkor guruhlari (Payme, Click, bank…) ro'yxati va roli.
// Operator: «o'sha gruppaga qo'shsa o'zi savollarga javob beradi… botni qo'shganda
// biz rolini tayinlaymiz». Guruh o'zi ro'yxatga tushadi (bot qo'shilganda),
// bu yerda unga LOYIHA + INTEGRATSIYA + ROL + REJIM biriktiriladi.
function TgGroupsPanel({ apiUrl, authFetch, projects, integrations }) {
  const [data, setData] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [openId, setOpenId] = React.useState(null);
  const [chat, setChat] = React.useState(null);      // {groupId, items}
  const [say, setSay] = React.useState('');
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => {
    const r = await afRef.current(`${apiUrl}${path}`, opts);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Xato ' + r.status);
    return j;
  }, [apiUrl]);
  const load = React.useCallback(() => api('/tg-groups').then(setData).catch(e => setMsg('❌ ' + e.message)), [api]);
  React.useEffect(() => { load(); }, [load]);
  const save = async (g, patch) => {
    try { await api(`/tg-groups/${g.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }); setMsg('✅ Saqlandi'); load(); }
    catch (e) { setMsg('❌ ' + e.message); }
  };
  const del = async (g) => {
    if (!window.confirm(`«${g.title || g.chat_id}» ro'yxatdan o'chirilsinmi? (Bot guruhdan chiqmaydi — uni Telegram'dan o'zingiz chiqarasiz)`)) return;
    try { await api(`/tg-groups/${g.id}`, { method: 'DELETE' }); load(); } catch (e) { setMsg('❌ ' + e.message); }
  };
  const openChat = async (g) => {
    try { const r = await api(`/tg-groups/${g.id}/messages`); setChat({ group: g, items: r.items || [] }); } catch (e) { setMsg('❌ ' + e.message); }
  };
  const sendSay = async () => {
    if (!chat || !say.trim()) return;
    try { await api(`/tg-groups/${chat.group.id}/say`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: say }) }); setSay(''); openChat(chat.group); }
    catch (e) { setMsg('❌ ' + e.message); }
  };
  const template = async (g) => {
    const it = (integrations || []).find(i => i.id === g.integration_id);
    const pr = (projects || []).find(p => String(p.id) === String(g.project_id));
    try {
      const r = await api(`/tg-groups/role-template?partner=${encodeURIComponent(it ? it.name : 'hamkor')}&project=${encodeURIComponent(pr ? pr.name : 'loyiha')}&integration=${encodeURIComponent(it ? it.name : 'integratsiya')}`);
      save(g, { role_prompt: r.text });
    } catch (e) { setMsg('❌ ' + e.message); }
  };
  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4';
  const MODES = [['off', '🔇 Jim (faqat o’qiydi)'], ['draft', '✍️ Tasdiq bilan'], ['auto', '⚡ Avtomatik']];
  const bot = data && data.bot;
  const items = (data && data.items) || [];
  return (
    <div className="space-y-3">
      <div className={card}>
        <div className="font-semibold text-gray-900 dark:text-white">🤖 Khorezmai Telegram agenti</div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Botni hamkor guruhiga (Payme, Click, bank…) qo'shing — u guruhni o'zi shu ro'yxatga yozadi.
          Keyin loyiha, integratsiya va rolini belgilaysiz: agent hamkor savollariga loyihaning haqiqiy
          ma'lumotlari va yuklangan hujjatlar asosida javob beradi, tashlangan protokol fayllarini o'zi saqlaydi.
        </p>
        {bot && (
          <div className="mt-2 text-xs space-y-1">
            <div className="text-gray-600 dark:text-gray-300">Bot: <b>@{bot.username}</b></div>
            {!bot.canJoinGroups && <div className="text-red-600 dark:text-red-400">🔴 Bot guruhlarga qo'shila olmaydi — @BotFather → /setjoingroups → Enable</div>}
            {!bot.canReadAll && <div className="text-amber-700 dark:text-amber-400">
              ⚠️ Maxfiylik rejimi YOQILGAN: bot guruhda faqat <b>@{bot.username}</b> bilan chaqirilgan, o'ziga javob berilgan va <b>/buyruq</b> xabarlarni ko'radi.
              Butun suhbatni ko'rishi uchun: @BotFather → <b>/setprivacy</b> → botni tanlang → <b>Disable</b>, so'ng botni guruhdan chiqarib qayta qo'shing.
            </div>}
            {bot.canReadAll && <div className="text-green-700 dark:text-green-400">✅ Maxfiylik rejimi o'chirilgan — bot butun suhbatni ko'radi.</div>}
          </div>)}
      </div>
      {msg && <div className="text-sm">{msg}</div>}
      {!data && <div className="text-gray-400 py-8 text-center">⏳</div>}
      {data && items.length === 0 && <div className={card + ' text-sm text-gray-500 dark:text-gray-400'}>Hali guruh yo'q. Botni guruhga qo'shing — u o'zi shu yerda paydo bo'ladi.</div>}
      <div className="grid gap-3 lg:grid-cols-2">
        {items.map(g => (
          <div key={g.id} className={card}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 dark:text-white truncate">{g.title || '(nomsiz guruh)'}</div>
                <div className="text-[11px] text-gray-500">chat {g.chat_id} · {g.msg_count} xabar{g.pending > 0 ? ` · ⏳ ${g.pending} tasdiq kutmoqda` : ''}</div>
              </div>
              <div className="flex items-center gap-1">
                {!g.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">chiqarilgan</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${g.mode === 'auto' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : g.mode === 'draft' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                  {(MODES.find(m => m[0] === g.mode) || ['', g.mode])[1]}
                </span>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-1.5 mt-2 text-xs">
              <select value={g.project_id || ''} onChange={e => save(g, { project_id: e.target.value || null })} className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-2 py-1.5">
                <option value="">Loyiha…</option>
                {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={g.integration_id || ''} onChange={e => save(g, { integration_id: e.target.value || null })} className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-2 py-1.5">
                <option value="">Integratsiya…</option>
                {(integrations || []).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              <select value={g.mode} onChange={e => save(g, { mode: e.target.value })} className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-2 py-1.5">
                {MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="mt-2">
              <button onClick={() => setOpenId(openId === g.id ? null : g.id)} className="text-xs text-primary-700 dark:text-primary-400 hover:underline">
                {openId === g.id ? '▾' : '▸'} 🎭 Rol {g.role_prompt ? '(belgilangan)' : '(belgilanmagan)'}
              </button>
              {openId === g.id && (
                <div className="mt-1.5 space-y-1.5">
                  <textarea defaultValue={g.role_prompt || ''} rows={6} id={`tgrole-${g.id}`}
                            placeholder="Masalan: Sen TezHal loyihasi uchun Payme integratsiyasini olib borayotgan Khorezmai agentisan…"
                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-2 py-1.5 text-xs" />
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => { const el = document.getElementById(`tgrole-${g.id}`); save(g, { role_prompt: el ? el.value : '' }); }}
                            className="text-[11px] px-2.5 py-1 rounded-lg bg-primary-700 dark:bg-primary-600 text-white">Saqlash</button>
                    <button onClick={() => template(g)} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600">📝 Namuna</button>
                  </div>
                </div>)}
            </div>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              <button onClick={() => openChat(g)} className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">💬 Suhbat</button>
              <button onClick={() => del(g)} className="text-[11px] text-red-500 hover:underline ml-auto">🗑</button>
            </div>
          </div>))}
      </div>
      {chat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setChat(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="font-semibold text-gray-900 dark:text-white">💬 {chat.group.title || chat.group.chat_id}</div>
              <button onClick={() => setChat(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">✕</button>
            </div>
            <div className="p-4 overflow-auto space-y-2 flex-1">
              {chat.items.length === 0 && <div className="text-sm text-gray-400">Xabar yo'q.</div>}
              {chat.items.map(m => (
                <div key={m.id} className={`text-sm ${m.direction === 'out' ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-[85%] px-3 py-1.5 rounded-xl ${m.direction === 'out' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'}`}>
                    <div className="text-[10px] opacity-70">{m.from_name || 'Hamkor'}</div>
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  </div>
                </div>))}
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <input value={say} onChange={e => setSay(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendSay(); }}
                     placeholder="Guruhga bot orqali yozish…" className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-1.5 text-sm" />
              <button onClick={sendSay} disabled={!say.trim()} className="px-3 py-1.5 rounded-lg bg-primary-700 dark:bg-primary-600 text-white text-sm disabled:opacity-50">Yuborish</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}

// 🔌 Integratsiyalar bazasi — toifalangan servislar katalogi (to'lov, xarita, OFD, yetkazish, savdo, ijtimoiy, CRM,
// buxgalteriya, davlat, SMS/push, auth, AI, analitika, infra); ➕ boyitish; loyihaga «Ulash» → vazifa; ✅ ulangan belgisi.
function IntegrationsPanel({ authFetch, apiUrl, projectId, projects, onSelectProject, isAdmin, userId, onOpenConsole, onOpenPlaybook }) {
  const [data, setData] = React.useState(null);
  const [cat, setCat] = React.useState('all');
  const [q, setQ] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ category: 'payment', name: '', country: 'UZ', description: '', docs_url: '', auth_type: '', notes: '' });
  const [msg, setMsg] = React.useState('');
  const [sub, setSub] = React.useState('catalog');   // catalog | telegram
  const [openRow, setOpenRow] = React.useState(null); // qaysi qator ochiq (texnik yo'l + 📚 hujjatlar)
  const [sort, setSort] = React.useState({ key: null, dir: 1 }); // jadval ustuni bo'yicha saralash
  const [collapsed, setCollapsed] = React.useState({}); // yig'ilgan toifa guruhlari
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => { const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Xato ' + r.status); return j; }, [apiUrl]);
  const load = React.useCallback(() => api(`/integrations${projectId ? '?projectId=' + projectId : ''}`).then(setData).catch(e => setMsg('❌ ' + e.message)), [api, projectId]);
  React.useEffect(() => { load(); }, [load]);
  const cats = (data && data.categories) || [];
  const items = ((data && data.items) || []).filter(i => (cat === 'all' || i.category === cat) && (!q.trim() || (i.name + ' ' + (i.description || '') + ' ' + (i.notes || '')).toLowerCase().includes(q.trim().toLowerCase())));
  const connect = async (it) => {
    if (!projectId) return setMsg('❌ Avval loyihani tanlang');
    // 🧭 Ssenariysi bor integratsiya (Payme…) — bitta vazifa emas, qadamma-qadam ssenariy:
    // shartnoma savoli → kod → kabinet (konsol) → kalitlar → tekshiruv. Faol bo'lsa o'shani ochadi.
    if (it.playbook) {
      if (it.run && onOpenPlaybook) return onOpenPlaybook(it.run.id);
      try {
        const run = await api(`/integrations/${it.id}/playbook`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId }) });
        load();
        if (onOpenPlaybook) onOpenPlaybook(run.id);
      } catch (e) { setMsg('❌ ' + e.message); }
      return;
    }
    const note = window.prompt(`«${it.name}» ni loyihaga ulash uchun vazifa yaratiladi. Qo'shimcha izoh (ixtiyoriy):`, ''); if (note === null) return;
    try {
      const r = await api(`/integrations/${it.id}/connect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId, note }) });
      setMsg(`✅ Vazifa #${r.taskId} yaratildi — ⏳ Jarayonlar'da ko'ring`);
      load();
      // 🔐 Shu servisning konsoli bo'lsa — darhol o'sha yerga o'tamiz va sessiyani
      // ochamiz. Aks holda operator Konsollar bo'limiga kirib, profilni topib,
      // «Sessiyani ochish» ni qo'lda bosishiga to'g'ri kelardi.
      if (it.console_key && onOpenConsole) goConsole(it, r.taskId);
    } catch (e) { setMsg('❌ ' + e.message); }
  };
  // Konsolni shu integratsiya profilida ochadi va vazifa matnini to'ldiradi.
  const goConsole = (it, taskId = null) => {
    if (!it.console_key || !onOpenConsole) return;
    const pr = (projects || []).find(p => String(p.id) === String(projectId));
    const wish = `«${(pr && pr.name) || 'loyiha'}» loyihasi uchun ${it.name} integratsiyasini konsolda sozlang.`
      + (taskId ? ` (Kod uchun vazifa #${taskId} navbatga qo'yildi.)` : '');
    onOpenConsole(it.console_key, { task: wish, projectId: projectId || undefined });
  };
  const setState = async (it, status) => { if (!projectId) return; try { await api(`/integrations/${it.id}/state`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId, status }) }); load(); } catch (e) { setMsg('❌ ' + e.message); } };
  const add = async (e) => { e.preventDefault(); try { await api('/integrations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); setAdding(false); setForm({ category: form.category, name: '', country: 'UZ', description: '', docs_url: '', auth_type: '', notes: '' }); setMsg('✅ Qo\'shildi'); load(); } catch (err) { setMsg('❌ ' + err.message); } };
  const del = async (it) => { if (!window.confirm(`«${it.name}» katalogdan olib tashlansinmi?`)) return; try { await api(`/integrations/${it.id}`, { method: 'DELETE' }); load(); } catch (e) { setMsg('❌ ' + e.message); } };
  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4';
  // 🔴 HOLAT HAR DOIM KO'RINADI. Ilgari ulanmagan integratsiyada chip umuman
  // chiqmasdi, kartada esa «✅ ulangan» degan TUGMA turardi — operator uni holat
  // deb o'qib «hammasi ulangan» deb tushunardi. Endi ulanmagan ham ochiq aytiladi,
  // tugma esa «Ulangan deb belgilash» bo'ldi (quyida).
  // Loyiha tanlanmagan bo'lsa chip YO'Q — holat loyihaga bog'liq, «ulanmagan» deb
  // ko'rsatish yolg'on bo'lardi.
  const stateChip = (s, hasProject) => !hasProject ? null
    : !s ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">○ ulanmagan</span>
    : s.status === 'connected' ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">✅ ulangan</span>
    : s.status === 'failed' ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">❌ muammo</span>
    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">🛠 {s.task_id ? `#${s.task_id} · ${s.task_status || 'rejada'}` : 'rejada'}</span>;
  // Saralash: «Holat» ustuni — ulangan → jarayonda → muammo → ulanmagan.
  const stRank = (st) => !st ? 3 : st.status === 'connected' ? 0 : st.status === 'failed' ? 2 : 1;
  const sorted = !sort.key ? items : [...items].sort((a, b) => sort.dir * (
    sort.key === 'name' ? a.name.localeCompare(b.name) : stRank(a.state) - stRank(b.state)));
  // Toifalar bo'yicha guruhlar (katalogdagi toifa tartibida); noma'lum toifa — «Boshqa».
  const known = new Set(cats.map(c => c[0]));
  const groups = [...cats.map(([k, l]) => [k, l, sorted.filter(i => i.category === k)]),
    ['_other', 'Boshqa', sorted.filter(i => !known.has(i.category))]].filter(g => g[2].length);
  const searching = !!q.trim();
  const allFolded = groups.length > 0 && groups.every(g => collapsed[g[0]]);
  const sortBy = (key) => setSort(s0 => ({ key, dir: s0.key === key ? -s0.dir : 1 }));
  const allCount = ((data && data.items) || []).length;
  const Th = ({ k, children, className = '' }) => (
    <th scope="col" className={`px-4 py-3 ${className}`}>
      {k ? <button type="button" onClick={() => sortBy(k)} className="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white">
        {children}<span className={sort.key === k ? 'text-primary-700 dark:text-primary-400' : 'opacity-30'}>{sort.key === k && sort.dir < 0 ? '▼' : '▲'}</span>
      </button> : children}
    </th>);
  const iconBtn = 'p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white';
  const inputCls = 'text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white';
  return (
    <div className="p-5 space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">Integratsiyalar bazasi</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Ulash mumkin bo'lgan servislar — toifalab. «Ulash» bossangiz tanlangan loyihaga vazifa yaratiladi (hujjat, auth turi, texnik yo'l bilan). Ro'yxatni o'zingiz boyitasiz.</p>
      </div>
      <div className="text-sm font-medium text-center text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px">
          {[['catalog', 'Katalog'], ['telegram', 'Telegram guruhlari']].map(([k, l]) => (
            <li key={k} className="me-2"><button type="button" onClick={() => setSub(k)}
              className={`inline-block px-4 py-2.5 border-b-2 rounded-t-lg ${sub === k ? 'text-primary-700 border-primary-700 dark:text-primary-400 dark:border-primary-400' : 'border-transparent hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300'}`}>{l}</button></li>))}
        </ul>
      </div>
      {msg && <div className="text-sm">{msg}</div>}
      {sub === 'telegram' && <TgGroupsPanel apiUrl={apiUrl} authFetch={authFetch} projects={projects} integrations={(data && data.items) || []} />}
      {sub === 'catalog' && <>
      {!projectId && (
        <div className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
          Loyiha tanlanmagan — qaysi servis ulangani ko'rinmaydi (holat har loyihada alohida). Jadval tepasidan loyihani tanlang.
        </div>)}
      {adding && (
        <form onSubmit={add} className={card + ' grid gap-3 sm:grid-cols-3 text-sm'}>
          <div className="sm:col-span-3 font-semibold text-gray-900 dark:text-white">Yangi integratsiya</div>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inputCls + ' px-2.5 py-2'}>{cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <input required placeholder="Nomi (masalan: Paylov)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls + ' px-2.5 py-2'} />
          <input placeholder="Mamlakat (UZ/RU/Global)" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className={inputCls + ' px-2.5 py-2'} />
          <input placeholder="Qisqa tavsif" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputCls + ' px-2.5 py-2 sm:col-span-2'} />
          <input placeholder="Hujjat havolasi (https://…)" value={form.docs_url} onChange={e => setForm({ ...form, docs_url: e.target.value })} className={inputCls + ' px-2.5 py-2'} />
          <input placeholder="Auth turi (API key / OAuth / shartnoma)" value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })} className={inputCls + ' px-2.5 py-2'} />
          <input placeholder="Texnik yo'l (agent uchun qisqa: endpointlar, oqim)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls + ' px-2.5 py-2 sm:col-span-2'} />
          <div className="sm:col-span-3 flex gap-2">
            <button className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white">Saqlash</button>
            <button type="button" onClick={() => setAdding(false)} className="px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Bekor</button>
          </div>
        </form>)}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative w-full xl:w-80">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400"><NavIcon name="search" className="w-4 h-4" /></div>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Qidirish: nomi, tavsif, texnik yo'l…" className={inputCls + ' block w-full pl-9 pr-3 py-2'} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={cat} onChange={e => setCat(e.target.value)} className={inputCls + ' px-2.5 py-2'} title="Toifa">
              <option value="all">Barcha toifalar ({allCount})</option>
              {cats.map(([k, l]) => { const n = ((data && data.items) || []).filter(i => i.category === k).length; return n ? <option key={k} value={k}>{l} ({n})</option> : null; })}
            </select>
            {projects && projects.length > 0 && <select value={projectId || ''} onChange={e => onSelectProject && onSelectProject(e.target.value)} className={inputCls + ' px-2.5 py-2'} title="Holat shu loyiha bo'yicha ko'rsatiladi"><option value="">Loyiha (ulash uchun)…</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            <button type="button" onClick={() => setAdding(a => !a)} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white"><NavIcon name="plus" className="w-4 h-4" />Qo'shish</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <Th k="name">Servis</Th>
                <Th className="hidden xl:table-cell">Kalitlar / auth</Th>
                {projectId && <Th k="status">Holat</Th>}
                <Th className="text-right">Amallar</Th>
              </tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Yuklanmoqda…</td></tr>}
              {data && !sorted.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Hech narsa topilmadi — qidiruv yoki toifani o'zgartiring.</td></tr>}
              {groups.map(([gk, gl, list]) => {
                // Qidiruv paytida yig'ilgan guruh ham ochiq ko'rinadi — natija yashirinmasin.
                const folded = !searching && !!collapsed[gk];
                const nConn = projectId ? list.filter(i => i.state && i.state.status === 'connected').length : 0;
                return (
                <React.Fragment key={'g-' + gk}>
                  <tr className="bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700">
                    <td colSpan={5} className="px-4 py-2.5">
                      <button type="button" onClick={() => setCollapsed(c => ({ ...c, [gk]: !c[gk] }))} title={folded ? 'Ochish' : "Yig'ish"}
                        className="w-full flex items-center gap-2 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        <NavIcon name="chevronRight" className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${folded ? '' : 'rotate-90'}`} />
                        <span>{gl}</span>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-300">{list.length}</span>
                        {nConn > 0 && <span className="text-xs font-medium text-green-700 dark:text-green-400">✅ {nConn} ulangan</span>}
                      </button>
                    </td>
                  </tr>
                  {!folded && list.map(it => {
                const open = openRow === it.id;
                const notConnected = projectId && (!it.state || it.state.status !== 'connected');
                const nDocs = Number(it.docs_count) || 0;
                return (
                <React.Fragment key={it.id}>
                  <tr onClick={() => { if (String(window.getSelection && window.getSelection()).trim()) return; setOpenRow(open ? null : it.id); }}
                      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpenRow(open ? null : it.id); } }}
                      tabIndex={0} aria-expanded={open} title={open ? 'Yopish' : "Batafsil: texnik yo'l va hujjatlar"}
                      className={`cursor-pointer border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700/50 ${open ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-2">
                        <span className="p-1.5 -ml-1.5 mt-[-2px] text-gray-500 dark:text-gray-400" aria-hidden="true">
                          <NavIcon name="chevronRight" className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white whitespace-nowrap">
                            <span translate="no">{it.name}</span>
                            {it.country && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{it.country}</span>}
                          </div>
                          {it.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 max-w-md">{it.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top hidden xl:table-cell text-xs max-w-[14rem]">{it.auth_type || <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                    {projectId && <td className="px-4 py-3 align-top whitespace-nowrap">{stateChip(it.state, true)}</td>}
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-1 whitespace-nowrap cursor-default" onClick={(e) => e.stopPropagation()}>
                        {projectId && it.playbook && (it.run
                          ? <button type="button" onClick={() => onOpenPlaybook && onOpenPlaybook(it.run.id)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${it.run.question ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'}`} title={it.run.question || 'Ulash ssenariysi ketmoqda — holatini ko\'rish'}>🧭 {it.run.question ? 'Savol bor' : 'Ssenariy'}</button>
                          : <button type="button" onClick={() => connect(it)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white" title="Qadamma-qadam ulash ssenariysi: shartnoma → kod → kabinet (konsol) → kalitlar → test muhitida tekshiruv">🧭 Ulash</button>)}
                        {notConnected && !it.playbook && <button type="button" onClick={() => connect(it)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white" title="Loyihaga ulash uchun vazifa yaratiladi">Ulash</button>}
                        {it.console_key && onOpenConsole && <button type="button" onClick={() => goConsole(it)} className={iconBtn} title={`${it.name} konsolini ochish — siz login qilasiz, qolganini agent qiladi`}><NavIcon name="lock" className="w-4 h-4" /></button>}
                        <button type="button" onClick={() => setOpenRow(open ? null : it.id)} className={iconBtn + ' inline-flex items-center gap-1'} title="Hamkor bergan protokol/qo'llanma — AI shu matndan javob beradi">
                          <NavIcon name="book" className="w-4 h-4" />{nDocs > 0 && <span className="text-[11px] font-semibold text-primary-700 dark:text-primary-400">{nDocs}</span>}
                        </button>
                        {notConnected && <button type="button" onClick={() => setState(it, 'connected')} className={iconBtn} title="Ulangan deb belgilash — kod allaqachon yozilgan bo'lsa, vazifa yaratmasdan"><NavIcon name="check" className="w-4 h-4" /></button>}
                        {((isAdmin || it.created_by === userId) && it.source === 'user') || (isAdmin && it.source === 'seed')
                          ? <button type="button" onClick={() => del(it)} className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30" title={it.source === 'seed' ? 'Katalogdan yashirish' : "O'chirish"}><NavIcon name="trash" className="w-4 h-4" /></button> : null}
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                      <td colSpan={5} className="px-4 py-4">
                        <div className="grid gap-3 md:grid-cols-2 text-xs">
                          <div><div className="font-semibold uppercase text-gray-700 dark:text-gray-300 mb-1">Kalitlar / auth</div><div>{it.auth_type || '—'}</div></div>
                          <div><div className="font-semibold uppercase text-gray-700 dark:text-gray-300 mb-1">Rasmiy hujjat</div>{it.docs_url ? <a href={it.docs_url} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-400 hover:underline break-all">{it.docs_url}</a> : '—'}</div>
                          {it.notes && <div className="md:col-span-2"><div className="font-semibold uppercase text-gray-700 dark:text-gray-300 mb-1">Texnik yo'l</div><div className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{it.notes}</div></div>}
                        </div>
                        {projectId && (
                          <div className="flex items-center gap-2 flex-wrap mt-3">
                            {notConnected && <button type="button" onClick={() => setState(it, 'connected')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700" title="Kod allaqachon yozilgan bo'lsa — vazifa yaratmasdan"><NavIcon name="check" className="w-3.5 h-3.5" />Ulangan deb belgilash</button>}
                            {it.state && <button type="button" onClick={() => setState(it, null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"><NavIcon name="xmark" className="w-3.5 h-3.5" />Holatni tozalash</button>}
                          </div>)}
                        <IntegrationDocs apiUrl={apiUrl} authFetch={authFetch} integrationId={it.id} integrationName={it.name} projectId={projectId} onChanged={load} />
                      </td>
                    </tr>)}
                </React.Fragment>);
              })}
                </React.Fragment>);
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          <span>Ko'rsatilmoqda <span className="font-semibold text-gray-900 dark:text-white">{sorted.length}</span> / <span className="font-semibold text-gray-900 dark:text-white">{allCount}</span></span>
          <div className="flex items-center gap-4">
            {groups.length > 1 && !searching && <button type="button" onClick={() => setCollapsed(allFolded ? {} : Object.fromEntries(groups.map(g => [g[0], true])))} className="text-primary-700 dark:text-primary-400 hover:underline">{allFolded ? 'Hammasini ochish' : "Hammasini yig'ish"}</button>}
            {(q || cat !== 'all') && <button type="button" onClick={() => { setQ(''); setCat('all'); }} className="text-primary-700 dark:text-primary-400 hover:underline">Filtrni tozalash</button>}
          </div>
        </div>
      </div>
      </>}
    </div>
  );
}

// 🔧 Reengineering — texnologik yangilanish auditi: kutubxona/runtime/DB/OS/xavfsizlik/unumdorlik/UI/kod/
// masshtab tavsiyalari (haqiqiy versiyalar npm registry + OSV + prod server SSH dan), har biri bir bosishda vazifa.
const PRIO = { critical: ['Kritik', 'bg-red-600 text-white'], high: ['Yuqori', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'], medium: ['O\'rta', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'], low: ['Past', 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'] };
function ReengineeringPanel({ authFetch, apiUrl, projectId, projects, onSelectProject }) {
  const [rep, setRep] = React.useState(null);
  const [cat, setCat] = React.useState('all');
  const [view, setView] = React.useState(() => { try { return localStorage.getItem('kh_reeng_view') || 'compare'; } catch { return 'compare'; } });
  const switchView = (v) => { setView(v); try { localStorage.setItem('kh_reeng_view', v); } catch {} };
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => { const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Xato ' + r.status); return j; }, [apiUrl]);
  const load = React.useCallback(() => { if (!projectId) return; api(`/reengineering/${projectId}`).then(setRep).catch(e => setMsg('❌ ' + e.message)); }, [api, projectId]);
  React.useEffect(() => { setRep(null); load(); }, [load]);
  const generating = rep && (rep.status === 'generating' || rep.running);
  React.useEffect(() => { if (!generating) return; const t = setInterval(load, 6000); return () => clearInterval(t); }, [generating, load]);
  const run = async () => { if (rep && rep.status === 'ready' && !window.confirm('Qayta tahlil qilinsinmi? (2–5 daqiqa)')) return; setBusy(true); try { await api(`/reengineering/${projectId}/run`, { method: 'POST' }); setRep(r => ({ ...(r || {}), status: 'generating', running: true })); } catch (e) { setMsg('❌ ' + e.message); } finally { setBusy(false); } };
  const mkTask = async (it) => { if (!window.confirm(`«${it.title}» — vazifa yaratilsinmi?`)) return; try { const r = await api(`/reengineering/${projectId}/items/${it.id}/task`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); setMsg(`✅ Vazifa #${r.taskId} yaratildi`); load(); } catch (e) { setMsg('❌ ' + e.message); } };
  const cats = (rep && rep.categories) || [];
  const items = (rep && rep.result && rep.result.items) || [];
  const shown = items.filter(i => cat === 'all' || i.category === cat).sort((a, b) => ['critical', 'high', 'medium', 'low'].indexOf(a.priority) - ['critical', 'high', 'medium', 'low'].indexOf(b.priority));
  const taskMap = (rep && rep.task_map) || {};
  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 sm:p-5';
  const f = rep && rep.facts;
  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div><h2 className="font-bold text-gray-900 dark:text-white text-lg">🔧 Reengineering</h2><p className="text-xs text-gray-500 dark:text-gray-400">Barcha texnologik yangilanishlar: server/OS, baza, til, kutubxonalar, xavfsizlik, unumdorlik, UI/UX, kod sifati, masshtab. Haqiqiy versiyalar (npm registry), zaifliklar (OSV), prod server holati (SSH) va kod asosida; har tavsiya bir bosishda vazifaga aylanadi.</p></div>
        <div className="flex items-center gap-2">
          {projects && projects.length > 0 && <select value={projectId || ''} onChange={e => onSelectProject && onSelectProject(e.target.value)} className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"><option value="">Loyiha…</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
          <button onClick={run} disabled={!projectId || busy || generating} className="px-3 py-1.5 text-sm rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 text-white disabled:opacity-50">{generating ? '⏳ Tahlil…' : rep && rep.status === 'ready' ? '🔄 Qayta tahlil' : '🔍 Tahlil qilish'}</button>
        </div>
      </div>
      {msg && <div className="text-sm">{msg}</div>}
      {!projectId && <div className="text-center py-16 text-gray-400">Proyekt tanlanmagan</div>}
      {projectId && rep && rep.status === 'none' && !generating && <div className="text-center py-16 text-gray-400"><div className="text-4xl mb-2">🔧</div>Hali tahlil qilinmagan — «🔍 Tahlil qilish» bosing (2–5 daqiqa; ⏳ Jarayonlar'da ko'rinadi).</div>}
      {generating && <div className="text-center py-16 text-gray-500 dark:text-gray-400"><div className="text-3xl mb-2 animate-pulse">⏳</div>Faktlar yig'ilmoqda (npm registry, OSV, prod server) va AI tavsiyalarni tuzmoqda…</div>}
      {rep && rep.status === 'error' && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">❌ {rep.error}</div>}
      {rep && rep.status === 'ready' && rep.result && (
        <>
          <div className={card + ' flex items-center gap-4'}>
            <div className="w-[4.5rem] h-[4.5rem] rounded-full border-4 border-primary-500 flex flex-col items-center justify-center text-gray-900 dark:text-white shrink-0 leading-none" title="Texnologik sog'lomlik bali (0 — eskirgan, 10 — zamonaviy)"><span className="text-lg font-bold">{rep.result.score}/10</span><span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">ball</span></div>
            <div><div className="text-sm text-gray-700 dark:text-gray-300">{rep.result.summary}</div><div className="text-xs text-gray-400 mt-1">{rep.generated_at ? new Date(rep.generated_at).toLocaleString('uz-UZ', { hour12: false }) : ''} · {rep.model}{rep.tokens ? ` · ${(rep.tokens / 1000).toFixed(0)}k token` : ''} · {items.length} tavsiya</div></div>
          </div>
          {f && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 text-sm">
              {(f.runtime || []).slice(0, 4).map((r, i) => <div key={i} className={card}><div className="text-xs text-gray-500">{r.what} · {r.module}</div><div className="text-lg font-bold text-gray-900 dark:text-white">v{r.version}</div><div className={`text-xs ${r.eol && r.eol < new Date().toISOString().slice(0, 10) ? 'text-red-600' : 'text-gray-500'}`}>{r.eol ? (r.eol < new Date().toISOString().slice(0, 10) ? '⛔ EOL o\'tgan: ' : 'EOL: ') + r.eol : ''}</div></div>)}
              {(f.dependencies || []).map((d, i) => <div key={'d' + i} className={card}><div className="text-xs text-gray-500">📦 {d.module}</div><div className="text-lg font-bold text-gray-900 dark:text-white">{d.total} paket</div><div className="text-xs text-gray-500">{d.majorBehind.length} ta major orqada · {d.vulnerable.length} ta zaif · {d.deprecated.length} ta eskirgan</div></div>)}
              {(f.servers || []).map((s, i) => <div key={'s' + i} className={card}><div className="text-xs text-gray-500">🖥 {s.host}</div><div className={`text-sm font-medium ${s.reachable ? 'text-gray-900 dark:text-white' : 'text-red-600'}`}>{s.reachable ? 'ulandi' : 'ulanmadi'}</div>{s.raw && <pre className="text-[10px] text-gray-500 whitespace-pre-wrap max-h-20 overflow-auto mt-1">{s.raw.split('\n').slice(0, 4).join('\n')}</pre>}</div>)}
            </div>)}
          <div className="flex flex-wrap items-center gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1.5">
            {[['all', 'Hammasi'], ...cats].map(([k, l]) => { const n = k === 'all' ? items.length : items.filter(i => i.category === k).length; if (k !== 'all' && !n) return null; return <button key={k} onClick={() => setCat(k)} className={`px-3 py-1.5 rounded-lg text-sm transition ${cat === k ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{l} <span className="opacity-60">{n}</span></button>; })}
            <div className="ml-auto flex items-center gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5" title="Ko'rinish">
              <button onClick={() => switchView('compare')} className={`px-2.5 py-1 rounded-md text-xs transition ${view === 'compare' ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-300'}`}>⚖️ Taqqoslash</button>
              <button onClick={() => switchView('list')} className={`px-2.5 py-1 rounded-md text-xs transition ${view === 'list' ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-300'}`}>📋 Ro'yxat</button>
            </div>
          </div>
          {view === 'compare' && (
            <div className="space-y-4">
              {/* Ikki yonma-yon holat: chap = HOZIR (nima bor), o'ng = TAVSIYA (nima bo'lishi kerak) */}
              <div className="hidden md:grid grid-cols-[minmax(0,1fr)_2.25rem_minmax(0,1fr)_8.5rem] gap-3 px-1 sticky top-16 z-10 py-1 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur">
                <div className="text-xs font-bold uppercase tracking-wide text-red-700 dark:text-red-400 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>Hozirgi holat</div>
                <div></div>
                <div className="text-xs font-bold uppercase tracking-wide text-green-700 dark:text-green-400 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>Tavsiya etilgan holat</div>
                <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Amal</div>
              </div>
              {(cat === 'all' ? cats : cats.filter(c => c[0] === cat)).map(([ck, cl]) => { const grp = shown.filter(i => i.category === ck); if (!grp.length) return null; return (
                <div key={ck} className={card + ' p-0 sm:p-0 overflow-hidden'}>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">{cl}<span className="text-xs font-normal text-gray-500">{grp.length} ta</span></div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {grp.map(it => { const p = PRIO[it.priority] || PRIO.low; const tid = taskMap[it.id]; return (
                      <div key={it.id} className="grid md:grid-cols-[minmax(0,1fr)_2.25rem_minmax(0,1fr)_8.5rem] gap-3 px-4 py-3 items-stretch">
                        <div className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-900/15 p-3 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${p[1]}`}>{p[0]}</span>{it.module && <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/70 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{it.module}</span>}</div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{it.title}</div>
                          <div className="text-sm text-red-900 dark:text-red-200 mt-1">{it.current || '—'}</div>
                          {it.why && <div className="text-xs text-gray-600 dark:text-gray-400 mt-1.5">{it.why}</div>}
                        </div>
                        <div className="hidden md:flex items-center justify-center text-2xl text-gray-300 dark:text-gray-600">→</div>
                        <div className="rounded-lg border border-green-200 dark:border-green-900/60 bg-green-50/60 dark:bg-green-900/15 p-3 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1 text-[11px] text-gray-500"><span className="px-1.5 py-0.5 rounded bg-white/70 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">ish: {it.effort}</span><span className={`px-1.5 py-0.5 rounded border ${it.risk === 'high' ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800' : it.risk === 'medium' ? 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800' : 'bg-white/70 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>xavf: {it.risk}</span>{it.owner === 'operator' && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">👤 siz qilasiz</span>}</div>
                          <div className="text-sm font-semibold text-green-900 dark:text-green-200">{it.recommended || '—'}</div>
                          {(it.steps || []).length > 0 && <details className="mt-1.5"><summary className="text-xs text-primary-700 dark:text-primary-400 cursor-pointer">Qadamlar ({it.steps.length})</summary><ol className="list-decimal pl-5 text-xs text-gray-700 dark:text-gray-300 mt-1 space-y-0.5">{it.steps.map((st, i) => <li key={i}>{st}</li>)}</ol></details>}
                        </div>
                        <div className="flex md:flex-col items-center md:items-stretch justify-center gap-1.5">
                          {tid ? <span className="text-xs text-center px-2 py-1.5 rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">🛠 #{tid}</span>
                            : it.owner === 'operator' && !it.taskInstruction ? <span className="text-xs text-center text-gray-400">qo'lda</span>
                            : <button onClick={() => mkTask(it)} className="px-3 py-1.5 text-xs rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 text-white whitespace-nowrap">🛠 Vazifa qilish</button>}
                        </div>
                      </div>); })}
                  </div>
                </div>); })}
              {shown.length === 0 && <div className="text-center py-10 text-gray-400">Bu toifada tavsiya yo'q</div>}
            </div>)}
          {view === 'list' && <div className="space-y-3">
            {shown.map(it => { const p = PRIO[it.priority] || PRIO.low; const tid = taskMap[it.id]; return (
              <div key={it.id} className={card}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${p[1]}`}>{p[0]}</span><span className="text-xs text-gray-500">{(cats.find(c => c[0] === it.category) || [])[1] || it.category}</span>{it.module && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{it.module}</span>}<span className="text-xs text-gray-400">ish: {it.effort} · xavf: {it.risk}</span>{it.owner === 'operator' && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">👤 siz qilasiz</span>}</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{it.title}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1"><span className="text-gray-400">Hozir:</span> {it.current} <span className="text-gray-300 mx-1">→</span> <span className="text-gray-400">Kerak:</span> <b>{it.recommended}</b></div>
                    {it.why && <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{it.why}</div>}
                    {(it.steps || []).length > 0 && <details className="mt-1"><summary className="text-xs text-primary-700 dark:text-primary-400 cursor-pointer">Qadamlar ({it.steps.length})</summary><ol className="list-decimal pl-5 text-sm text-gray-600 dark:text-gray-300 mt-1 space-y-0.5">{it.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></details>}
                  </div>
                  <div className="shrink-0">
                    {tid ? <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">🛠 #{tid}</span>
                      : it.owner === 'operator' && !it.taskInstruction ? <span className="text-xs text-gray-400">qo'lda</span>
                      : <button onClick={() => mkTask(it)} className="px-3 py-1.5 text-sm rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 text-white">🛠 Vazifa qilish</button>}
                  </div>
                </div>
              </div>); })}
          </div>}
        </>)}
    </div>
  );
}

// 🎛 Dizayn → Uslub: loyiha dizayn yo'nalishi (tayyor uslub / havola / skrinshot / brief).
// Saqlangan tanlov agentlarga ko'rsatma bo'lib boradi (engine UI subtasklari + App Builder SVG).
const MOODS = ['zamonaviy', 'minimal', 'jiddiy', 'premium', 'do\'stona', 'o\'yinchoq', 'milliy', 'texnologik', 'iliq', 'sokin'];
function DesignStylePanel({ authFetch, apiUrl, projectId }) {
  const [styles, setStyles] = React.useState([]);
  const [data, setData] = React.useState(null);
  const [url, setUrl] = React.useState('');
  const [analyzing, setAnalyzing] = React.useState(false);
  const [preview, setPreview] = React.useState(null);   // analyze natijasi (hali saqlanmagan)
  const [brief, setBrief] = React.useState({});
  const [prompt, setPrompt] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => { const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Xato ' + r.status); return j; }, [apiUrl]);
  const load = React.useCallback(() => { if (!projectId) return; api(`/design-brief/${projectId}`).then(d => { setData(d); setBrief(d.brief || {}); }).catch(e => setMsg('❌ ' + e.message)); api(`/design-brief/${projectId}/prompt`).then(p => setPrompt(p.text || null)).catch(() => {}); }, [api, projectId]);
  React.useEffect(() => { api('/design-brief/styles').then(d => setStyles(d.styles || [])).catch(() => {}); }, [api]);
  React.useEffect(() => { load(); }, [load]);
  const save = async (patch) => { try { const d = await api(`/design-brief/${projectId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }); setData(d); setBrief(d.brief || {}); setMsg('✅ Saqlandi'); setTimeout(() => setMsg(''), 2500); api(`/design-brief/${projectId}/prompt`).then(p => setPrompt(p.text || null)).catch(() => {}); } catch (e) { setMsg('❌ ' + e.message); } };
  const analyzeUrl = async () => { if (!url.trim()) return; setAnalyzing(true); setPreview(null); setMsg(''); try { setPreview(await api(`/design-brief/${projectId}/analyze-url`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: url.trim() }) })); } catch (e) { setMsg('❌ ' + e.message); } finally { setAnalyzing(false); } };
  const uploadImages = async (files) => {
    for (const f of [...files]) {
      if (!/^image\//.test(f.type)) continue;
      setAnalyzing(true); setMsg(`⏳ ${f.name} tahlil qilinmoqda…`);
      try { const fd = new FormData(); fd.append('image', f); const r = await authFetchRef.current(`${apiUrl}/upload`, { method: 'POST', body: fd }); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Yuklanmadi');
        const ref = await api(`/design-brief/${projectId}/analyze-image`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: j.path || j.url }) });
        await save({ references: [...((data && data.references) || []), ref] }); }
      catch (e) { setMsg('❌ ' + e.message); }
      finally { setAnalyzing(false); }
    }
  };
  const addPreview = () => { if (!preview) return; save({ references: [...((data && data.references) || []), preview] }); setPreview(null); setUrl(''); };
  const removeRef = (i) => save({ references: (data.references || []).filter((_, j) => j !== i) });
  if (!projectId) return <div className="text-center py-16 text-gray-400">Proyekt tanlanmagan</div>;
  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 sm:p-5';
  const sel = data && data.styleKey;
  const refView = (r, i, actions) => (
    <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex gap-3">
      {(r.screenshot || r.image) && <img src={`${apiUrl}${r.screenshot || r.image}`} alt="" className="w-40 h-28 object-cover object-top rounded border border-gray-200 dark:border-gray-700 shrink-0 cursor-zoom-in" onClick={() => window.open(`${apiUrl}${r.screenshot || r.image}`, '_blank')} />}
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-medium text-gray-900 dark:text-white truncate">{r.type === 'url' ? <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline">🔗 {r.title || r.url}</a> : '🖼 Skrinshot'}</div>
        {(r.ai && r.ai.summary) && <div className="text-gray-600 dark:text-gray-300 mt-0.5">{r.ai.summary}</div>}
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">{((r.ai && r.ai.palette) || r.palette || []).slice(0, 8).map((c, k) => <span key={k} title={c} className="w-5 h-5 rounded border border-gray-300 dark:border-gray-600" style={{ background: c }} />)}{r.fonts && r.fonts.length > 0 && <span className="text-xs text-gray-500 ml-2">🔤 {r.fonts.join(', ')}</span>}{r.ai && r.ai.mood && <span className="text-xs text-gray-500 ml-2">✨ {r.ai.mood}</span>}</div>
        <div className="mt-2 flex gap-2">{actions}</div>
      </div>
    </div>);
  return (
    <div className="p-5 space-y-4">
      <div><h2 className="font-bold text-gray-900 dark:text-white text-lg">🎛 Dizayn yo'nalishi</h2><p className="text-xs text-gray-500 dark:text-gray-400">Bu yerda tanlangan uslub, namunalar va brief HAR bir UI vazifasi va dizayn chizmasiga ko'rsatma bo'lib boradi — agentlar o'z didiga emas, shunga amal qiladi.</p>{msg && <div className="text-sm mt-1">{msg}</div>}</div>

      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">1. Tayyor uslublar</h3><p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Bittasini tanlang — freymvork, ranglar, radius va shrift shunga ergashadi.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {styles.map(s => (
            <button key={s.key} onClick={() => save({ styleKey: sel === s.key ? null : s.key })} className={`text-left rounded-lg border-2 p-3 transition ${sel === s.key ? 'border-primary-600 ring-2 ring-primary-200 dark:ring-primary-900' : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'}`}>
              {/* mini-preview: uslubning o'z ranglari/radiusi bilan chizilgan "ekran" */}
              <div className="rounded-md overflow-hidden border mb-2" style={{ background: s.palette[2], borderColor: s.palette[3], fontFamily: s.font }}>
                <div className="h-6 flex items-center px-2 gap-1" style={{ background: s.key === 'dark_saas' ? s.palette[3] : '#fff', borderBottom: `1px solid ${s.palette[3]}` }}><span className="w-2 h-2 rounded-full" style={{ background: s.palette[0] }} /><span className="text-[9px] font-semibold" style={{ color: s.palette[1] }}>{s.name.split(' ')[0]}</span></div>
                <div className="p-2 space-y-1.5">
                  <div className="h-2 w-2/3 rounded" style={{ background: s.palette[1], opacity: .85 }} /><div className="h-1.5 w-1/2 rounded" style={{ background: s.palette[3] }} />
                  <div className="flex gap-1.5 pt-1"><span className="text-[9px] px-2 py-0.5 text-white" style={{ background: s.palette[0], borderRadius: s.radius, boxShadow: s.key === 'neobrutal' ? '2px 2px 0 #000' : 'none', border: s.key === 'neobrutal' ? '1.5px solid #000' : 'none' }}>Tugma</span><span className="text-[9px] px-2 py-0.5" style={{ border: `1px solid ${s.palette[3]}`, borderRadius: s.radius, color: s.palette[1] }}>Ikkilamchi</span></div>
                  <div className="h-8 rounded" style={{ background: s.key === 'dark_saas' ? s.palette[3] : '#fff', border: `1px solid ${s.palette[3]}`, borderRadius: s.radius }} />
                </div>
              </div>
              <div className="font-semibold text-sm text-gray-900 dark:text-white">{sel === s.key ? '✅ ' : ''}{s.name}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{s.desc}</div>
            </button>))}
        </div>
      </div>

      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">2. Yoqqan sayt havolasi orqali</h3><p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Sayt skrinshoti olinadi, ranglar va shriftlar ajratiladi, AI uslubni so'z bilan ta'riflaydi — keyin namunalarga qo'shasiz.</p>
        <div className="flex gap-2 flex-wrap"><input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" className="flex-1 min-w-[240px] border border-gray-300 rounded-lg px-3 py-2 text-sm" /><button onClick={analyzeUrl} disabled={analyzing || !url.trim()} className="px-3 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm disabled:opacity-50">{analyzing ? '⏳ Tahlil…' : '🔍 Tahlil qilish'}</button></div>
        {preview && <div className="mt-3">{refView(preview, 'p', <><button onClick={addPreview} className="px-2.5 py-1 rounded-lg bg-primary-700 text-white text-xs">➕ Namunalarga qo'shish</button><button onClick={() => setPreview(null)} className="px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs">Bekor</button></>)}</div>}
      </div>

      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">3. Skrinshot / rasmlar orqali</h3><p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Yoqqan ilova/sayt skrinshotlarini yuklang — AI har birini ta'riflab namunalarga qo'shadi; agentlar rasmni ham ko'radi.</p>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-400 dark:border-gray-600 cursor-pointer text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">🖼 Rasm tanlash (bir nechta bo'lishi mumkin)<input type="file" accept="image/*" multiple hidden onChange={e => { uploadImages(e.target.files); e.target.value = ''; }} /></label>
        {(data && data.references && data.references.length > 0) && <div className="mt-3 space-y-2"><div className="text-xs uppercase text-gray-400">Namunalar ({data.references.length})</div>{data.references.map((r, i) => refView(r, i, <button onClick={() => removeRef(i)} className="text-xs text-red-500 hover:underline">🗑 olib tashlash</button>))}</div>}
      </div>

      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">4. Dizayn brief (shablon)</h3><p className="text-xs text-gray-500 dark:text-gray-400 mb-3">To'ldiring — bo'sh qoldirilgan maydonlar e'tiborga olinmaydi.</p>
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          {[['brand', 'Brend nomi', 'masalan: TezHal'], ['tagline', 'Shior / bir jumlali tavsif', 'masalan: Uyingizga usta 15 daqiqada'], ['audience', 'Auditoriya', 'kim ishlatadi: yosh, kasb, shahar…'], ['font', 'Shrift', 'Inter / Roboto / Manrope…'], ['tone', 'Matn ohangi', 'rasmiy / do\'stona / qisqa…'], ['mustHave', 'Bo\'lishi shart', 'masalan: tungi rejim, katta tugmalar, o\'zbek/rus'], ['avoid', 'Bo\'lmasin', 'masalan: qizil rang, gradientlar, ko\'p animatsiya']].map(([k, l, ph]) => (
            <label key={k} className="text-xs text-gray-500 dark:text-gray-400">{l}<input value={brief[k] || ''} onChange={e => setBrief({ ...brief, [k]: e.target.value })} placeholder={ph} className="block w-full mt-0.5 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900" /></label>))}
          <label className="text-xs text-gray-500 dark:text-gray-400">Asosiy rang<div className="flex items-center gap-2 mt-0.5"><input type="color" value={brief.primaryColor || '#1A56DB'} onChange={e => setBrief({ ...brief, primaryColor: e.target.value })} className="w-10 h-9 border border-gray-300 rounded" /><input value={brief.primaryColor || ''} onChange={e => setBrief({ ...brief, primaryColor: e.target.value })} placeholder="#1A56DB" className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 w-32" /></div></label>
          <label className="text-xs text-gray-500 dark:text-gray-400">Ikkilamchi rang<div className="flex items-center gap-2 mt-0.5"><input type="color" value={brief.secondaryColor || '#F59E0B'} onChange={e => setBrief({ ...brief, secondaryColor: e.target.value })} className="w-10 h-9 border border-gray-300 rounded" /><input value={brief.secondaryColor || ''} onChange={e => setBrief({ ...brief, secondaryColor: e.target.value })} placeholder="#F59E0B" className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 w-32" /></div></label>
          <div className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">Kayfiyat<div className="flex flex-wrap gap-1.5 mt-1">{MOODS.map(m => { const on = String(brief.mood || '').split(',').map(x => x.trim()).includes(m); return <button type="button" key={m} onClick={() => { const arr = String(brief.mood || '').split(',').map(x => x.trim()).filter(Boolean); setBrief({ ...brief, mood: (on ? arr.filter(x => x !== m) : [...arr, m]).join(', ') }); }} className={`px-2.5 py-1 rounded-full text-xs border ${on ? 'bg-primary-700 text-white border-primary-700' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{m}</button>; })}</div></div>
          <label className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">Qo'shimcha izoh<textarea value={brief.notes || ''} onChange={e => setBrief({ ...brief, notes: e.target.value })} rows={3} className="block w-full mt-0.5 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900" /></label>
        </div>
        <button onClick={() => save({ brief })} className="mt-3 px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm">💾 Briefni saqlash</button>
      </div>

      <details className={card}><summary className="cursor-pointer font-semibold text-gray-900 dark:text-white">🤖 Agentlar ko'radigan ko'rsatma (shaffoflik)</summary>
        {prompt ? <pre className="mt-2 text-xs whitespace-pre-wrap text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">{prompt}</pre> : <div className="mt-2 text-sm text-gray-400">Hali hech narsa tanlanmagan — agentlar o'z didi bilan chizadi.</div>}</details>
    </div>
  );
}

// 📈 Prognoz — loyiha BOSHLANISHIDAN OLDIN: tannarx, deadline, kerakli narsalar. Backend Khorezmai'ning
// haqiqiy tarixi (vazifa/token/kun) bilan kalibrlab AI'dan JSON oladi; bu yerda forma + natija + tarix.
const money = (v, cur = 'USD') => (v == null || isNaN(Number(v)) ? '—' : (cur === 'USD' ? '$' : '') + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }) + (cur !== 'USD' ? ' ' + cur : ''));
const fmtDate = (v) => { try { return v ? new Date(v).toLocaleDateString('uz-UZ') : '—'; } catch { return v; } };
function ForecastPanel({ authFetch, apiUrl, projects }) {
  const [items, setItems] = React.useState([]);
  const [cur, setCur] = React.useState(null);
  const [form, setForm] = React.useState({ title: '', description: '', modules: ['backend', 'web', 'mobile'], projectId: '' });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => { const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Xato ' + r.status); return j; }, [apiUrl]);
  const loadList = React.useCallback(() => api('/forecast').then(d => setItems(d.items || [])).catch(() => {}), [api]);
  const open = React.useCallback((id) => api(`/forecast/${id}`).then(setCur).catch(e => setErr(e.message)), [api]);
  React.useEffect(() => { loadList(); }, [loadList]);
  const generating = cur && (cur.status === 'generating' || cur.running);
  React.useEffect(() => { if (!generating) return; const t = setInterval(() => { open(cur.id); loadList(); }, 5000); return () => clearInterval(t); }, [generating, cur && cur.id, open, loadList]);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try { const r = await api('/forecast', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, projectId: form.projectId || null }) }); await open(r.id); loadList(); }
    catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm('Prognoz o\'chirilsinmi?')) return; try { await api(`/forecast/${id}`, { method: 'DELETE' }); if (cur && cur.id === id) setCur(null); loadList(); } catch (e) { alert(e.message); } };
  const toggleMod = (m) => setForm(f => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter(x => x !== m) : [...f.modules, m] }));
  const download = () => { if (!cur || !cur.result) return; const blob = new Blob([JSON.stringify(cur.result, null, 2)], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `prognoz-${cur.id}.json`; a.click(); };
  const printIt = () => { const el = document.getElementById('kh-forecast-body'); if (!el) return; const w = window.open('', '_blank'); if (!w) return; w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Prognoz — ${escHtml(cur.title || '')}</title><script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script><style>body{font-family:system-ui;padding:32px;color:#111}</style></head><body>${el.innerHTML}</body></html>`); w.document.close(); setTimeout(() => w.print(), 600); };

  const r = cur && cur.result;
  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 sm:p-5';
  const ownerChip = (o) => <span className={`text-[10px] px-1.5 py-0.5 rounded ${o === 'operator' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'}`}>{o === 'operator' ? '👤 siz' : '🤖 Khorezmai'}</span>;
  const cplx = { low: ['oson', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'], medium: ['o\'rta', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'], high: ['murakkab', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'] };

  return (
    <div className="p-5 grid gap-4 lg:grid-cols-[320px_1fr]">
      <div className="space-y-4">
        <form onSubmit={submit} className={card}>
          <h2 className="font-bold text-gray-900 dark:text-white text-lg mb-1">📈 Prognoz</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Loyiha g'oyasi/TZ ni yozing — taxminiy tannarx, muddat va boshlash uchun kerakli narsalar ro'yxati chiqadi. Hisob Khorezmai'ning haqiqiy loyihalar tarixi bilan kalibrlanadi.</p>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Loyiha nomi" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2" />
          <textarea required minLength={20} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={9} placeholder="Nima quriladi? Kim uchun? Asosiy funksiyalar, rollar, integratsiyalar (to'lov, SMS, xarita…), tillar, platformalar…" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2" />
          <div className="flex gap-2 mb-2">{[['backend', '⚙️ Backend'], ['web', '🌐 Web'], ['mobile', '📱 Mobil']].map(([m, l]) => <button type="button" key={m} onClick={() => toggleMod(m)} className={`px-2.5 py-1 rounded-lg text-xs border transition ${form.modules.includes(m) ? 'bg-primary-700 text-white border-primary-700' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{l}</button>)}</div>
          {projects && projects.length > 0 && <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"><option value="">Mavjud loyihaga bog'lanmagan</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
          {err && <div className="text-xs text-red-600 mb-2">❌ {err}</div>}
          <button disabled={busy} className="w-full px-3 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50">{busy ? '⏳ …' : '📈 Hisoblash'}</button>
        </form>
        <div className={card}>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Tarix</div>
          {items.length === 0 ? <div className="text-sm text-gray-400">Hali prognoz yo'q.</div> : (
            <ul className="space-y-1">{items.map(it => (
              <li key={it.id} className={`group flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${cur && cur.id === it.id ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`} onClick={() => open(it.id)}>
                <div className="min-w-0"><div className="font-medium text-gray-900 dark:text-white truncate">{it.title || 'Prognoz'} {it.status === 'generating' && <span className="text-amber-500">⏳</span>}{it.status === 'error' && <span className="text-red-500">❌</span>}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">{fmtDate(it.created_at)}{it.features ? ` · ${it.features} funksiya` : ''}{it.days ? ` · ${it.days} kun` : ''}{it.total_one_time != null ? ` · ${money(it.total_one_time)}` : ''}</div></div>
                <button onClick={(e) => { e.stopPropagation(); del(it.id); }} className="opacity-0 group-hover:opacity-100 text-xs text-red-500">🗑</button>
              </li>))}</ul>)}
        </div>
      </div>

      <div id="kh-forecast-body" className="min-w-0">
        {!cur && <div className="text-center py-20 text-gray-400"><div className="text-4xl mb-2">📈</div>Chapda loyihani yozib «Hisoblash» bosing yoki tarixdan tanlang.</div>}
        {cur && generating && <div className="text-center py-20 text-gray-500 dark:text-gray-400"><div className="text-3xl mb-2 animate-pulse">⏳</div>Hisoblanmoqda (arxitektor modeli, 1–3 daqiqa)… ⏳ Jarayonlar'da ham ko'rinadi.</div>}
        {cur && cur.status === 'error' && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">❌ {cur.error}</div>}
        {cur && r && cur.status === 'ready' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div><h2 className="font-bold text-gray-900 dark:text-white text-xl">{cur.title || 'Prognoz'}</h2><div className="text-xs text-gray-500 dark:text-gray-400">{fmtDate(cur.created_at)} · {cur.model}{cur.tokens ? ` · ${(cur.tokens / 1000).toFixed(0)}k token` : ''}{cur.project_id && projects ? ` · ${(projects.find(p => p.id === cur.project_id) || {}).name || ''}` : ''}</div></div>
              <div className="flex gap-2"><button onClick={download} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">⬇ JSON</button><button onClick={printIt} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">🖨 Chop etish / PDF</button></div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{r.summary}</p>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[['📅 Deadline', r.timeline?.deadline ? fmtDate(r.timeline.deadline) : '—', `${r.timeline?.calendarDays ?? '—'} kalendar kun${r.timeline?.mvpDays ? ` · MVP ${r.timeline.mvpDays} kun` : ''}`],
                ['💵 Bir martalik', money(r.cost?.totalOneTime, r.cost?.currency), 'server, do\'kon akkauntlari, kalitlar…'],
                ['🔁 Oylik', money(r.cost?.totalMonthly, r.cost?.currency) + '/oy', 'server, API, SMS…'],
                ['🧩 Hajm', `${r.scope?.totalFeatures ?? '—'} funksiya`, `${r.scope?.totalTasks ?? '—'} vazifa · ~${r.cost?.tokens?.estimate ? (r.cost.tokens.estimate / 1e6).toFixed(0) + 'M' : '—'} token`]].map(([t, v, s], i) => (
                <div key={i} className={card}><div className="text-xs text-gray-500 dark:text-gray-400">{t}</div><div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{v}</div><div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s}</div></div>))}
            </div>
            {r.timeline?.phases?.length > 0 && (
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-3">⏱ Muddat va bosqichlar</h3>
                {(() => { const total = r.timeline.phases.reduce((a, p) => a + (Number(p.days) || 0), 0) || 1; let acc = 0; return (
                  <div className="space-y-2">{r.timeline.phases.map((p, i) => { const left = acc / total * 100; const w = (Number(p.days) || 0) / total * 100; acc += Number(p.days) || 0; return (
                    <div key={i}><div className="flex justify-between text-sm"><span className="font-medium text-gray-800 dark:text-gray-200">{p.name}</span><span className="text-gray-500 dark:text-gray-400">{p.days} kun</span></div>
                      <div className="h-3 rounded bg-gray-100 dark:bg-gray-700 relative my-1"><div className="absolute h-3 rounded bg-primary-500" style={{ left: left + '%', width: Math.max(2, w) + '%' }} /></div>
                      {(p.deliverables || []).length > 0 && <div className="text-xs text-gray-500 dark:text-gray-400">{p.deliverables.join(' · ')}</div>}</div>); })}</div>); })()}
                {(r.timeline.assumptions || []).length > 0 && <div className="mt-3 text-xs text-gray-500 dark:text-gray-400"><b>Taxminlar:</b> {r.timeline.assumptions.join(' · ')}</div>}
              </div>)}
            <div className="grid gap-4 md:grid-cols-2">
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-2">💵 Bir martalik xarajatlar</h3>
                <table className="w-full text-sm"><tbody>{(r.cost?.oneTime || []).map((c, i) => <tr key={i} className="border-t border-gray-100 dark:border-gray-700"><td className="py-1.5 pr-2"><div className="text-gray-800 dark:text-gray-200">{c.name} {ownerChip(c.owner)}</div>{c.note && <div className="text-xs text-gray-500 dark:text-gray-400">{c.note}</div>}</td><td className="py-1.5 text-right font-medium whitespace-nowrap text-gray-900 dark:text-white">{money(c.amount, r.cost?.currency)}</td></tr>)}
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold"><td className="py-1.5 text-gray-900 dark:text-white">Jami</td><td className="py-1.5 text-right text-gray-900 dark:text-white">{money(r.cost?.totalOneTime, r.cost?.currency)}</td></tr></tbody></table>
                {r.cost?.operatorHours ? <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">👤 Sizning vaqtingiz (tekshirish/tasdiqlash): ~{r.cost.operatorHours} soat</div> : null}
              </div>
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-2">🔁 Oylik xarajatlar</h3>
                <table className="w-full text-sm"><tbody>{(r.cost?.monthly || []).map((c, i) => <tr key={i} className="border-t border-gray-100 dark:border-gray-700"><td className="py-1.5 pr-2"><div className="text-gray-800 dark:text-gray-200">{c.name}</div>{c.note && <div className="text-xs text-gray-500 dark:text-gray-400">{c.note}</div>}</td><td className="py-1.5 text-right font-medium whitespace-nowrap text-gray-900 dark:text-white">{money(c.amount, r.cost?.currency)}</td></tr>)}
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold"><td className="py-1.5 text-gray-900 dark:text-white">Jami / oy</td><td className="py-1.5 text-right text-gray-900 dark:text-white">{money(r.cost?.totalMonthly, r.cost?.currency)}</td></tr></tbody></table>
                {r.cost?.tokens && <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">🧠 AI tokenlar: ~{(r.cost.tokens.estimate / 1e6).toFixed(0)}M — {r.cost.tokens.note}</div>}
                {r.cost?.note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{r.cost.note}</div>}
              </div>
            </div>
            {(r.needs || []).length > 0 && (
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-1">🧰 Loyiha boshida kerak bo'ladi</h3><p className="text-xs text-gray-500 dark:text-gray-400 mb-3">👤 siz — faqat siz bera olasiz (akkaunt, kalit, qaror); 🤖 Khorezmai — platforma o'zi qiladi.</p>
                <div className="grid gap-4 md:grid-cols-2">{r.needs.map((g, i) => (
                  <div key={i}><div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">{g.category}</div>
                    <ul className="space-y-1.5">{(g.items || []).map((it, j) => <li key={j} className="text-sm flex gap-2"><span className="shrink-0">{it.owner === 'operator' ? '☐' : '🤖'}</span><div><div className="text-gray-800 dark:text-gray-200">{it.name} {it.urgent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">shoshilinch</span>} {ownerChip(it.owner)}</div>{it.why && <div className="text-xs text-gray-500 dark:text-gray-400">{it.why}</div>}</div></li>)}</ul></div>))}</div>
              </div>)}
            {(r.scope?.modules || []).length > 0 && (
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-2">🧩 Hajm — funksiyalar bo'yicha</h3>
                <div className="grid gap-4 md:grid-cols-3">{r.scope.modules.map((m, i) => (
                  <div key={i}><div className="font-medium text-gray-800 dark:text-gray-200 mb-1">{m.type === 'backend' ? '⚙️' : m.type === 'mobile' ? '📱' : '🌐'} {m.label || m.type} <span className="text-xs text-gray-400">{(m.features || []).length}</span></div>
                    <ul className="space-y-1">{(m.features || []).map((f, j) => <li key={j} className="text-sm flex items-center gap-1.5 flex-wrap"><span className="text-gray-800 dark:text-gray-200">{f.name}</span><span className={`text-[10px] px-1.5 py-0.5 rounded ${(cplx[f.complexity] || cplx.medium)[1]}`}>{(cplx[f.complexity] || cplx.medium)[0]}</span>{f.tasks > 1 && <span className="text-[10px] text-gray-400">{f.tasks} vazifa</span>}</li>)}</ul></div>))}</div>
              </div>)}
            {(r.risks || []).length > 0 && (
              <div className={card}><h3 className="font-semibold text-gray-900 dark:text-white mb-2">⚠️ Xavflar</h3><ul className="space-y-1.5">{r.risks.map((x, i) => <li key={i} className="text-sm"><span className="text-gray-800 dark:text-gray-200">{x.risk}</span>{x.mitigation && <span className="text-gray-500 dark:text-gray-400"> — {x.mitigation}</span>}</li>)}</ul></div>)}
          </div>)}
      </div>
    </div>
  );
}

// 📄 Hujjatlar — loyiha hujjatlari (texnik / API / biznes sxema / sertifikatlar / sifat).
// Backend faktlarni deterministik yig'ib AI'ga beradi; natija project_docs da. Bu yerda faqat ko'rsatish.
const DOC_KINDS = [
  ['technical', '📘', 'Texnik hujjat', 'Arxitektura, modullar, ma\'lumotlar modeli, sozlash, deploy'],
  ['api', '🔌', 'API hujjati', 'Barcha endpointlar: parametrlar, javob, xatolar (+ OpenAPI)'],
  ['business', '🗺️', 'Biznes sxemasi', 'Aktyorlar va jarayon — vizual (SVG)'],
  ['sequence', '📐', 'Ketma-ketlik', 'Backend, web va ilovalar qanday ishlashi — qadamma-qadam UML diagrammalar'],
  ['certificates', '📜', 'Sertifikatlar', 'ISO 9001 / 27001 / … tayyorlik + yuklangan sertifikatlar'],
  ['quality', '⭐', 'Sifat', 'Xavfsizlik, masshtab, ishonchlilik, unumdorlik, test'],
];
const escHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Kichik Markdown → HTML (kutubxonasiz): sarlavha, ro'yxat, jadval, kod, qalin/kursiv, havola.
function mdToHtml(md) {
  const lines = String(md || '').replace(/\r/g, '').split('\n');
  const out = []; let i = 0;
  const inline = (t) => escHtml(t)
    .replace(/`([^`]+)`/g, '<code class="px-1 rounded bg-gray-100 dark:bg-gray-700 text-[0.9em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>').replace(/(^|[^*])\*([^*]+)\*/g, '$1<i>$2</i>')
    .replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, '<a class="text-primary-700 dark:text-primary-400 underline" target="_blank" rel="noreferrer" href="$2">$1</a>');
  while (i < lines.length) {
    const l = lines[i];
    if (/^```/.test(l)) { const buf = []; i++; while (i < lines.length && !/^```/.test(lines[i])) buf.push(lines[i++]); i++; out.push(`<pre class="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-auto my-2"><code>${escHtml(buf.join('\n'))}</code></pre>`); continue; }
    const h = l.match(/^(#{1,6})\s+(.*)/);
    if (h) { const n = h[1].length; const cls = n === 1 ? 'text-2xl font-bold mt-6 mb-3' : n === 2 ? 'text-xl font-bold mt-6 mb-2 pb-1 border-b border-gray-200 dark:border-gray-700' : 'text-base font-semibold mt-4 mb-1'; out.push(`<h${n} class="${cls} text-gray-900 dark:text-white">${inline(h[2])}</h${n}>`); i++; continue; }
    if (/^\s*[-*_]{3,}\s*$/.test(l)) { out.push('<hr class="my-4 border-gray-200 dark:border-gray-700">'); i++; continue; }
    if (/^\s*\|/.test(l) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const rows = []; while (i < lines.length && /^\s*\|/.test(lines[i])) rows.push(lines[i++]);
      const cells = (r) => r.trim().replace(/^\||\|$/g, '').split('|').map(c => inline(c.trim()));
      const head = cells(rows[0]); const body = rows.slice(2).map(cells);
      out.push(`<div class="overflow-auto my-2"><table class="text-sm w-full border-collapse"><thead><tr>${head.map(c => `<th class="text-left px-2 py-1 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">${c}</th>`).join('')}</tr></thead><tbody>${body.map(r => `<tr>${r.map(c => `<td class="px-2 py-1 border-b border-gray-100 dark:border-gray-700 align-top">${c}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`); continue;
    }
    if (/^\s*([-*+]|\d+\.)\s+/.test(l)) {
      const ordered = /^\s*\d+\./.test(l); const items = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i])) { let t = lines[i].replace(/^\s*([-*+]|\d+\.)\s+/, ''); i++; while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*+]|\d+\.)\s+/.test(lines[i])) t += ' ' + lines[i++].trim(); items.push(t); }
      out.push(`<${ordered ? 'ol' : 'ul'} class="${ordered ? 'list-decimal' : 'list-disc'} pl-6 my-2 space-y-0.5">${items.map(t => `<li>${inline(t)}</li>`).join('')}</${ordered ? 'ol' : 'ul'}>`); continue;
    }
    if (!l.trim()) { i++; continue; }
    const para = []; while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|```|\s*\||\s*([-*+]|\d+\.)\s)/.test(lines[i])) para.push(lines[i++]);
    out.push(`<p class="my-2 leading-relaxed">${inline(para.join(' '))}</p>`);
  }
  return out.join('\n');
}
const METHOD_CLS = { GET: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', PUT: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', PATCH: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' };
const scoreCls = (v, max) => { const p = v / max; return p >= 0.7 ? 'bg-green-500' : p >= 0.4 ? 'bg-amber-500' : 'bg-red-500'; };

function DocsPanel({ authFetch, apiUrl, projectId, projectName, readOnly = false }) {
  const [sub, setSub] = React.useState(() => { try { return localStorage.getItem('kh_docs_sub') || 'technical'; } catch { return 'technical'; } });
  const [svgFit, setSvgFit] = React.useState(true);   // keng sxema: sig'dirish (kichik) yoki 1:1 (gorizontal scroll)
  const [list, setList] = React.useState([]);
  const [doc, setDoc] = React.useState(null);
  const [certs, setCerts] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [certForm, setCertForm] = React.useState({ name: '', standard: 'ISO 9001:2015', issuer: '', number: '', issued_at: '', valid_until: '', file_path: '', file_name: '' });
  const [sources, setSources] = React.useState([]);   // 📎 mijoz bergan hujjatlar (docx/pdf/pptx) — generatsiyada faktlarga qo'shiladi
  const [srcBusy, setSrcBusy] = React.useState('');
  const [apiQ, setApiQ] = React.useState('');
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const api = React.useCallback(async (path, opts) => {
    const r = await authFetchRef.current(`${apiUrl}${path}`, opts); const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || ('Xato ' + r.status)); return j;
  }, [apiUrl]);
  const loadList = React.useCallback(() => { if (!projectId) return; api(`/docs/${projectId}`).then(d => setList(d.docs || [])).catch(() => {}); }, [api, projectId]);
  const loadDoc = React.useCallback(() => { if (!projectId) return; api(`/docs/${projectId}/${sub}`).then(setDoc).catch(e => setDoc({ status: 'error', error: e.message })); }, [api, projectId, sub]);
  const loadCerts = React.useCallback(() => { if (!projectId) return; api(`/docs/${projectId}/certificates`).then(d => setCerts(d.items || [])).catch(() => {}); }, [api, projectId]);
  const loadSources = React.useCallback(() => { if (!projectId) return; api(`/docs/${projectId}/sources`).then(d => setSources(d.items || [])).catch(() => {}); }, [api, projectId]);
  React.useEffect(() => { setSources([]); loadSources(); }, [loadSources]);
  const srcPending = sources.some(x => x.digest_status === 'pending');
  React.useEffect(() => { if (!srcPending) return; const t = setInterval(loadSources, 5000); return () => clearInterval(t); }, [srcPending, loadSources]);
  const uploadSources = async (files) => {
    for (const file of Array.from(files || [])) {
      setSrcBusy(file.name);
      try {
        const fd = new FormData(); fd.append('file', file);
        const r = await authFetchRef.current(`${apiUrl}/upload-file`, { method: 'POST', body: fd }); const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'Yuklanmadi');
        await api(`/docs/${projectId}/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file_path: j.path, name: file.name }) });
      } catch (e) { alert(`${file.name}: ${e.message}`); }
    }
    setSrcBusy(''); loadSources();
  };
  const delSource = async (id) => { if (!window.confirm('Manba hujjat o\'chirilsinmi? (Keyingi generatsiyalarda hisobga olinmaydi)')) return; try { await api(`/docs/${projectId}/sources/${id}`, { method: 'DELETE' }); loadSources(); } catch (e) { alert(e.message); } };
  const redigest = async (id) => { try { await api(`/docs/${projectId}/sources/${id}/digest`, { method: 'POST' }); loadSources(); } catch (e) { alert(e.message); } };
  const downloadSvg = (svg, name) => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })); a.download = `${(projectName || 'loyiha').replace(/\s+/g, '-')}-${name}.svg`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
  React.useEffect(() => { setDoc(null); loadList(); loadDoc(); if (sub === 'certificates') loadCerts(); }, [loadList, loadDoc, loadCerts, sub]);
  React.useEffect(() => { try { localStorage.setItem('kh_docs_sub', sub); } catch {} }, [sub]);
  // Yaratilayotgan paytda 5s poll (job tugagach mazmun keladi)
  const anyRunning = list.some(d => d.running || d.status === 'generating') || (doc && (doc.running || doc.status === 'generating'));
  React.useEffect(() => { if (!anyRunning) return; const t = setInterval(() => { loadList(); loadDoc(); }, 5000); return () => clearInterval(t); }, [anyRunning, loadList, loadDoc]);

  const generate = async () => {
    if (doc && doc.status === 'ready' && !window.confirm('Hujjat qayta yaratilsinmi? Hozirgisi almashtiriladi.')) return;
    setBusy(true);
    try { await api(`/docs/${projectId}/${sub}/generate`, { method: 'POST' }); setDoc(d => ({ ...(d || {}), status: 'generating', running: true })); loadList(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const download = () => {
    if (!doc || doc.status !== 'ready') return;
    const ext = doc.format === 'json' ? 'json' : doc.format === 'svg' ? 'svg' : 'md';
    const blob = new Blob([doc.format === 'json' ? JSON.stringify(doc.data, null, 2) : doc.content], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${(projectName || 'loyiha').replace(/\s+/g, '-')}-${sub}.${ext}`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const printDoc = () => {
    const el = document.getElementById('kh-doc-body'); if (!el) return;
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(projectName || '')} — ${escHtml(DOC_KINDS.find(k => k[0] === sub)?.[2] || '')}</title><script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script><style>body{font-family:system-ui;padding:32px;color:#111}pre{background:#111;color:#eee;padding:12px;border-radius:8px;font-size:12px;overflow:auto}table{border-collapse:collapse}th,td{border:1px solid #ddd;padding:4px 8px;font-size:13px}svg{max-width:100%;height:auto}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); setTimeout(() => w.print(), 600);
  };
  const uploadCertFile = async (file) => {
    if (!file) return;
    const isImg = /^image\//.test(file.type);
    const fd = new FormData(); fd.append(isImg ? 'image' : 'file', file);
    try { const r = await authFetchRef.current(`${apiUrl}/${isImg ? 'upload' : 'upload-file'}`, { method: 'POST', body: fd }); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'Yuklanmadi'); setCertForm(f => ({ ...f, file_path: j.path || j.url || '', file_name: file.name })); }
    catch (e) { alert(e.message); }
  };
  const addCert = async (e) => {
    e.preventDefault();
    try { await api(`/docs/${projectId}/certificates`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(certForm) }); setCertForm({ name: '', standard: 'ISO 9001:2015', issuer: '', number: '', issued_at: '', valid_until: '', file_path: '', file_name: '' }); loadCerts(); }
    catch (err) { alert(err.message); }
  };
  const delCert = async (id) => { if (!window.confirm('Sertifikat o\'chirilsinmi?')) return; try { await api(`/docs/${projectId}/certificates/${id}`, { method: 'DELETE' }); loadCerts(); } catch (e) { alert(e.message); } };

  if (!projectId) return <div className="text-center py-16 text-gray-400">Proyekt tanlanmagan</div>;
  const meta = DOC_KINDS.find(k => k[0] === sub) || DOC_KINDS[0];
  const st = (doc && doc.status) || 'none';
  const generating = st === 'generating' || (doc && doc.running);
  const fmtDt = (v) => { try { return v ? new Date(v).toLocaleString('uz-UZ', { hour12: false }) : ''; } catch { return v; } };
  const statusOf = (k) => { const d = list.find(x => x.kind === k); return !d ? 'none' : (d.running || d.status === 'generating') ? 'generating' : d.status; };

  const renderBody = () => {
    if (!doc || st === 'none') return (
      <div className="text-center py-14 text-gray-400">
        <div className="text-4xl mb-2">{meta[1]}</div>
        <div className="font-medium text-gray-600 dark:text-gray-300">{meta[2]} hali yaratilmagan</div>
        <div className="text-sm mt-1">{meta[3]}. «✨ Yaratish» bosing — AI loyiha kodidan faktlar asosida yozadi (1–3 daqiqa).</div>
      </div>);
    if (generating) return <div className="text-center py-14 text-gray-500 dark:text-gray-400"><div className="text-3xl mb-2 animate-pulse">⏳</div>Hujjat yaratilmoqda… (faktlar yig'iladi, keyin AI yozadi) — ⏳ Jarayonlar'da ham ko'rinadi.</div>;
    if (st === 'error') return <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">❌ {doc.error || 'Xato'} — qayta urinib ko'ring.</div>;
    if (doc.format === 'md') return <div className="prose-kh text-[15px] text-gray-800 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: mdToHtml(doc.content) }} />;
    if (doc.format === 'svg') return (
      <div className="relative">
        <button onClick={() => setSvgFit(f => !f)} className="absolute top-3 right-3 z-10 px-2 py-1 text-xs rounded-lg bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm" title={svgFit ? "Asl o'lchamda ko'rish (gorizontal aylantirib)" : "Butun sxemani ekranga sig'dirish"}>{svgFit ? '🔍 1:1' : '⤢ Sig\'dirish'}</button>
        <div className={`overflow-auto bg-white rounded-lg border border-gray-200 dark:border-gray-700 p-2 [&_svg]:h-auto ${svgFit ? '[&_svg]:w-full' : '[&_svg]:w-auto [&_svg]:max-w-none'}`} dangerouslySetInnerHTML={{ __html: doc.content }} />
      </div>);
    const d = doc.data; if (!d) return <div className="text-red-500">Ma'lumot o'qilmadi</div>;
    if (sub === 'sequence') {
      const flows = d.flows || [];
      return (
        <div className="space-y-6">
          <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <div className="font-medium mb-1.5">{flows.length} ta oqim — har biri: kim boshlaydi → qaysi ilova/sahifa → qaysi endpoint → backend nima qiladi → javob. Uzuq strelka = javob, ko'k ramka = shart (alt/else).</div>
            <div className="flex flex-wrap gap-1.5">{flows.map((f, i) => <a key={i} href={`#kh-seq-${i}`} onClick={(e) => { e.preventDefault(); document.getElementById(`kh-seq-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="px-2 py-0.5 rounded-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-primary-500">{i + 1}. {f.title}</a>)}</div>
          </div>
          {flows.map((f, i) => (
            <div key={i} id={`kh-seq-${i}`} className="scroll-mt-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold text-gray-900 dark:text-white text-lg">{i + 1}. {f.title}</h3>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => setSvgFit(v => !v)} className="px-2 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700" title={svgFit ? "Asl o'lchamda ko'rish" : "Ekranga sig'dirish"}>{svgFit ? '🔍 1:1' : '⤢ Sig\'dirish'}</button>
                  <button onClick={() => downloadSvg(f.svg, f.id || `oqim-${i + 1}`)} className="px-2 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">⬇ SVG</button>
                </div>
              </div>
              {f.summary && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{f.summary}</p>}
              <div className={`overflow-auto bg-white rounded-lg border border-gray-200 dark:border-gray-700 p-2 [&_svg]:h-auto ${svgFit ? '[&_svg]:w-full [&_svg]:max-w-[1400px]' : '[&_svg]:w-auto [&_svg]:max-w-none'}`} dangerouslySetInnerHTML={{ __html: f.svg }} />
            </div>))}
        </div>);
    }
    const q = apiQ.trim().toLowerCase();
    const apiGroups = sub !== 'api' ? [] : (d.groups || []).map(g => ({ ...g, endpoints: !q ? (g.endpoints || []) : (g.endpoints || []).filter(e => `${e.method} ${e.path} ${e.summary || ''} ${g.name}`.toLowerCase().includes(q)) })).filter(g => g.endpoints.length);
    if (sub === 'api') return (
      <div className="space-y-5">
        <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
          {d.source === 'openapi' && <div className="mb-1">📡 Test muhitidagi OpenAPI sxemasidan aynan olingan{d.total ? ` — ${d.total} endpoint, ${(d.groups || []).length} guruh` : ''} (AI taxmini emas)</div>}
          {d.baseUrls?.test && <div>🧪 Test: <code className="text-xs">{d.baseUrls.test}</code></div>}
          {d.baseUrls?.prod && <div>🚀 Prod: <code className="text-xs">{d.baseUrls.prod}</code></div>}
          {d.auth && <div className="mt-1">🔐 {d.auth}</div>}
        </div>
        <input value={apiQ} onChange={e => setApiQ(e.target.value)} placeholder="🔎 Endpoint qidirish (yo'l, metod, izoh)…" className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-1.5 text-sm" />
        {q && <div className="text-xs text-gray-500">{apiGroups.reduce((n, g) => n + g.endpoints.length, 0)} ta mos</div>}
        {apiGroups.map((g, gi) => (
          <div key={gi}>
            <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">{g.name} <span className="text-xs font-normal text-gray-400">{(g.endpoints || []).length} ta</span></h3>
            {g.description && <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{g.description}</p>}
            <div className="space-y-2">
              {(g.endpoints || []).map((e, ei) => (
                <details key={ei} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${METHOD_CLS[e.method] || 'bg-gray-100 text-gray-700'}`}>{e.method}</span>
                    <code className="text-sm font-mono text-gray-900 dark:text-white">{e.path}</code>
                    {e.auth && <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">🔐 auth</span>}
                    <span className="text-sm text-gray-500 dark:text-gray-400">{e.summary}</span>
                  </summary>
                  <div className="px-3 pb-3 text-sm space-y-2">
                    {(e.params || []).length > 0 && (
                      <table className="w-full text-xs"><thead><tr className="text-gray-400 text-left"><th className="py-1">Parametr</th><th>Qayerda</th><th>Tur</th><th>Majburiy</th><th>Izoh</th></tr></thead>
                        <tbody>{e.params.map((p, pi) => <tr key={pi} className="border-t border-gray-100 dark:border-gray-700"><td className="py-1 font-mono">{p.name}</td><td>{p.in}</td><td>{p.type}</td><td>{p.required ? '✓' : ''}</td><td className="text-gray-600 dark:text-gray-300">{p.description}</td></tr>)}</tbody></table>)}
                    {e.requestExample && Object.keys(e.requestExample).length > 0 && <div><div className="text-xs text-gray-400 mb-0.5">So'rov namunasi</div><pre className="bg-gray-900 text-gray-100 rounded p-2 text-xs overflow-auto">{JSON.stringify(e.requestExample, null, 2)}</pre></div>}
                    {e.response && <div><div className="text-xs text-gray-400 mb-0.5">Javob</div><div className="text-gray-700 dark:text-gray-300">{typeof e.response === 'string' ? e.response : <pre className="bg-gray-900 text-gray-100 rounded p-2 text-xs overflow-auto">{JSON.stringify(e.response, null, 2)}</pre>}</div></div>}
                    {e.responseExample && Object.keys(e.responseExample).length > 0 && <pre className="bg-gray-900 text-gray-100 rounded p-2 text-xs overflow-auto">{JSON.stringify(e.responseExample, null, 2)}</pre>}
                    {(e.errors || []).length > 0 && <div className="text-xs text-gray-500 dark:text-gray-400">Xatolar: {e.errors.join(' · ')}</div>}
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>);
    if (sub === 'quality') return (
      <div className="space-y-4">
        {d.overall && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center gap-4">
            <div className="w-[4.5rem] h-[4.5rem] rounded-full border-4 border-primary-500 flex flex-col items-center justify-center text-gray-900 dark:text-white shrink-0 leading-none" title="Umumiy sifat bali (0 — past, 10 — a'lo)"><span className="text-lg font-bold">{d.overall.score}/10</span><span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">ball</span></div>
            <div className="text-sm text-gray-700 dark:text-gray-300">{d.overall.summary}</div>
          </div>)}
        <div className="grid gap-4 md:grid-cols-2">
          {(d.areas || []).map((a, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2"><div className="font-semibold text-gray-900 dark:text-white">{a.title}</div><div className="text-sm font-bold text-gray-700 dark:text-gray-200">{a.score}/10 <span className="text-xs font-normal text-gray-500">ball</span></div></div>
              <div className="h-2 rounded bg-gray-100 dark:bg-gray-700 mb-3"><div className={`h-2 rounded ${scoreCls(a.score, 10)}`} style={{ width: `${Math.max(3, Math.min(100, a.score * 10))}%` }} /></div>
              {(a.done || []).length > 0 && <div className="mb-2"><div className="text-xs uppercase text-gray-400 mb-1">✅ Qilingan</div><ul className="text-sm space-y-0.5 text-gray-700 dark:text-gray-300">{a.done.map((x, j) => <li key={j}>• {x}</li>)}</ul></div>}
              {(a.gaps || []).length > 0 && <div className="mb-2"><div className="text-xs uppercase text-gray-400 mb-1">⚠️ Bo'shliqlar</div><ul className="text-sm space-y-0.5 text-amber-800 dark:text-amber-300">{a.gaps.map((x, j) => <li key={j}>• {x}</li>)}</ul></div>}
              {(a.recommendations || []).length > 0 && <div><div className="text-xs uppercase text-gray-400 mb-1">💡 Tavsiyalar</div><ul className="text-sm space-y-0.5 text-gray-600 dark:text-gray-400">{a.recommendations.map((x, j) => <li key={j}>• {x}</li>)}</ul></div>}
            </div>))}
        </div>
      </div>);
    if (sub === 'certificates') return (
      <div className="space-y-4">
        {d.summary && <p className="text-sm text-gray-700 dark:text-gray-300">{d.summary}</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {(d.standards || []).map((s, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 mb-1"><div><div className="font-semibold text-gray-900 dark:text-white">{s.code}</div><div className="text-xs text-gray-500 dark:text-gray-400">{s.name}</div></div><div className="text-lg font-bold text-gray-800 dark:text-gray-100">{s.readiness}%</div></div>
              <div className="h-2 rounded bg-gray-100 dark:bg-gray-700 mb-3"><div className={`h-2 rounded ${scoreCls(s.readiness, 100)}`} style={{ width: `${Math.max(3, Math.min(100, s.readiness))}%` }} /></div>
              {[['✅ Bajarilgan', s.met, 'text-gray-700 dark:text-gray-300'], ['🟡 Qisman', s.partial, 'text-amber-800 dark:text-amber-300'], ['❌ Yetishmaydi', s.missing, 'text-red-700 dark:text-red-300'], ['➡️ Keyingi qadamlar', s.nextSteps, 'text-gray-600 dark:text-gray-400']].map(([t, arr, cls]) => (arr || []).length > 0 && (
                <div key={t} className="mb-2"><div className="text-xs uppercase text-gray-400 mb-1">{t}</div><ul className={`text-sm space-y-0.5 ${cls}`}>{arr.map((x, j) => <li key={j}>• {x}</li>)}</ul></div>))}
            </div>))}
        </div>
        {d.disclaimer && <p className="text-xs text-gray-400">{d.disclaimer}</p>}
      </div>);
    return <pre className="text-xs overflow-auto">{JSON.stringify(d, null, 2)}</pre>;
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div><h2 className="font-bold text-gray-900 dark:text-white text-lg">📄 Hujjatlar</h2><p className="text-xs text-gray-500 dark:text-gray-400">Loyiha kodi va 📎 manba hujjatlar asosida yaratiladi (kod ustun); yuklab olish va chop etish mumkin.</p></div>
      </div>
      {!readOnly && (
      <details className="mb-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2 flex-wrap">
          📎 Manba hujjatlar <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({sources.length}) — TZ, taqdimot, integratsiya hujjatlari: AI ularni o'qib barcha hujjatlarga qo'shadi</span>
          {srcPending && <span className="text-xs text-amber-600 dark:text-amber-400 animate-pulse">⏳ xulosa tayyorlanmoqda…</span>}
        </summary>
        <div className="px-3 pb-3">
          {sources.length > 0 && (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 mb-2">
              {sources.map(x => (
                <li key={x.id} className="py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><span className="font-medium text-gray-900 dark:text-white" translate="no">{x.name}</span> <span className="text-xs text-gray-500 dark:text-gray-400">· {Math.round((x.chars || 0) / 1000)}k belgi · {x.digest_status === 'ready' ? '✅ o\'qildi' : x.digest_status === 'pending' ? '⏳ o\'qilmoqda' : '❌ ' + (x.digest_error || 'xato')}</span></div>
                    <div className="flex gap-2 shrink-0">{x.digest_status === 'error' && <button onClick={() => redigest(x.id)} className="text-xs text-primary-700 dark:text-primary-400 hover:underline">♻️ Qayta</button>}<button onClick={() => delSource(x.id)} className="text-xs text-red-500 hover:underline">🗑</button></div>
                  </div>
                  {x.digest_preview && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{x.digest_preview.replace(/[#*`]/g, '').slice(0, 260)}</div>}
                </li>))}
            </ul>)}
          <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-dashed border-gray-400 dark:border-gray-500 cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
            {srcBusy ? `⏳ ${srcBusy} yuklanmoqda…` : '➕ Hujjat qo\'shish (docx, pdf, pptx, md, txt)'}
            <input type="file" multiple hidden disabled={!!srcBusy} accept=".docx,.pdf,.pptx,.md,.txt,.html,.htm,.csv,.json" onChange={e => { uploadSources(e.target.files); e.target.value = ''; }} />
          </label>
          <p className="text-xs text-gray-400 mt-1.5">Kod bilan zid joyda kod ustun; faqat hujjatda bo'lgan narsa «(rejalashtirilgan)» deb belgilanadi. Yangi hujjat qo'shgach hujjatlarni ♻️ qayta yarating.</p>
        </div>
      </details>
      )}
      <div className="flex flex-wrap gap-1 mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1.5">
        {DOC_KINDS.map(([k, ic, t]) => { const s = statusOf(k); return (
          <button key={k} onClick={() => setSub(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${sub === k ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            {ic} {t}<span className={`ml-0.5 w-2 h-2 rounded-full ${s === 'ready' ? 'bg-green-400' : s === 'generating' ? 'bg-amber-400 animate-pulse' : s === 'error' ? 'bg-red-400' : 'bg-gray-300 dark:bg-gray-600'}`} title={s === 'ready' ? 'tayyor' : s === 'generating' ? 'yaratilmoqda' : s === 'error' ? 'xato' : 'yaratilmagan'} />
          </button>); })}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {st === 'ready' && <>Yaratildi: {fmtDt(doc.generated_at)}{doc.model ? ` · ${doc.model}` : ''}{doc.tokens ? ` · ${(doc.tokens / 1000).toFixed(0)}k token` : ''}</>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && <button onClick={generate} disabled={busy || generating} className="px-3 py-1.5 text-sm rounded-lg bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white disabled:opacity-50 transition">{generating ? '⏳ Yaratilmoqda…' : st === 'ready' ? '♻️ Qayta yaratish' : '✨ Yaratish'}</button>}
          {st === 'ready' && <button onClick={download} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition">⬇ {doc.format === 'json' ? 'JSON' : doc.format === 'svg' ? 'SVG' : 'Markdown'}</button>}
          {st === 'ready' && sub === 'api' && <a href={`${apiUrl}/docs/${projectId}/api/openapi.json`} onClick={async (e) => { e.preventDefault(); try { const r = await authFetchRef.current(`${apiUrl}/docs/${projectId}/api/openapi.json`); const b = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'openapi.json'; a.click(); } catch {} }} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition">⬇ OpenAPI</a>}
          {st === 'ready' && <button onClick={printDoc} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition">🖨 Chop etish / PDF</button>}
        </div>
      </div>
      <div id="kh-doc-body" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">{renderBody()}</div>

      {sub === 'certificates' && (
        <div className="mt-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">📜 Yuklangan sertifikatlar</h3>
          {certs.length === 0 ? <p className="text-sm text-gray-400 mb-3">Hali sertifikat yuklanmagan.</p> : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 mb-3">
              {certs.map(c => (
                <li key={c.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <div><div className="font-medium text-gray-900 dark:text-white">{c.name} {c.standard && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ml-1">{c.standard}</span>}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{[c.issuer, c.number && `№ ${c.number}`, c.issued_at && `berilgan ${String(c.issued_at).slice(0, 10)}`, c.valid_until && `amal qiladi ${String(c.valid_until).slice(0, 10)}`].filter(Boolean).join(' · ')}</div></div>
                  <div className="flex gap-2 shrink-0">{c.file_path && <a className="text-primary-700 dark:text-primary-400 text-xs hover:underline" href={`${apiUrl}${c.file_path}`} target="_blank" rel="noreferrer">📎 fayl</a>}<button onClick={() => delCert(c.id)} className="text-xs text-red-500 hover:underline">🗑</button></div>
                </li>))}
            </ul>)}
          {!readOnly && <form onSubmit={addCert} className="grid gap-2 sm:grid-cols-3 text-sm">
            <input required placeholder="Nomi (masalan: Sifat menejmenti sertifikati)" value={certForm.name} onChange={e => setCertForm({ ...certForm, name: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1.5 sm:col-span-2" />
            <select value={certForm.standard} onChange={e => setCertForm({ ...certForm, standard: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1.5">
              {['ISO 9001:2015', 'ISO/IEC 27001:2022', 'ISO/IEC 27017', 'ISO/IEC 27018', 'ISO 22301', 'PCI DSS', 'SOC 2', 'GDPR', 'Boshqa'].map(s => <option key={s}>{s}</option>)}
            </select>
            <input placeholder="Bergan organ" value={certForm.issuer} onChange={e => setCertForm({ ...certForm, issuer: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1.5" />
            <input placeholder="Raqami" value={certForm.number} onChange={e => setCertForm({ ...certForm, number: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1.5" />
            <label className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 cursor-pointer text-gray-600 dark:text-gray-300">📎 {certForm.file_name || 'Fayl (PDF/rasm)'}<input type="file" accept=".pdf,image/*" hidden onChange={e => uploadCertFile(e.target.files[0])} /></label>
            <label className="text-xs text-gray-500">Berilgan sana<input type="date" value={certForm.issued_at} onChange={e => setCertForm({ ...certForm, issued_at: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-900" /></label>
            <label className="text-xs text-gray-500">Amal qilish muddati<input type="date" value={certForm.valid_until} onChange={e => setCertForm({ ...certForm, valid_until: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-900" /></label>
            <div className="flex items-end"><button type="submit" className="px-3 py-1.5 rounded-lg bg-primary-700 hover:bg-primary-800 text-white w-full">➕ Qo'shish</button></div>
          </form>}
        </div>)}
    </div>
  );
}

function ArchitecturePanel({ authFetch, apiUrl, projectId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;   // refetch-storm gotcha
  const load = React.useCallback((refresh) => {
    if (!projectId) return;
    setLoading(true); setErr('');
    authFetchRef.current(`${apiUrl}/projects/${projectId}/architecture${refresh ? '?refresh=1' : ''}`)
      .then(r => r.json().catch(() => ({})).then(d => (r.ok ? d : Promise.reject(new Error(d.error || 'Xato ' + r.status)))))
      .then(d => setData(d))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [apiUrl, projectId]);
  React.useEffect(() => { setData(null); load(false); }, [load]);

  if (!projectId) return <div className="text-center py-16 text-gray-400">Proyekt tanlanmagan</div>;

  const chip = (t, key) => {
    const c = ARCH_CAT[t.cat] || ARCH_CAT.tooling;
    return (
      <span key={key} title={t.version ? `${t.name} ${t.version}` : t.name}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${c.cls}`}>
        {t.name}{t.version && <span className="opacity-60 font-normal">{t.version}</span>}
      </span>
    );
  };
  const mods = (data && data.modules) || [];
  const sourceLabel = { workspace: 'workspace', 'test-env': 'test muhiti', 'mobile-build': 'mobil build', gitlab: 'GitLab' };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="font-bold text-gray-900 dark:text-white text-lg">🏛 Arxitektura</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Har modul <b>haqiqatan</b> qaysi texnologiyalarda qurilgani — repo manifestlari (package.json, requirements…) va test muhiti stack'idan o'qiladi, taxmin qilinmaydi.</p>
        </div>
        <button onClick={() => load(true)} disabled={loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition">{loading ? '⏳ …' : '↻ Yangilash'}</button>
      </div>
      {err && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 mb-3">❌ {err}</div>}
      {!data && loading && <div className="text-gray-400 py-10 text-center">⏳ Repolar o'qilmoqda…</div>}
      {data && (
        <>
          {/* Umumiy stack — bir qarashda */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 sm:p-5 mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Umumiy stack · {data.project.name}</div>
            {data.summary.length ? (
              <div className="flex flex-wrap gap-1.5">{data.summary.map((t, i) => chip(t, i))}</div>
            ) : <div className="text-sm text-gray-400">Hali kod yo'q — repolar bo'sh.</div>}
          </div>
          {/* Modullar */}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {mods.map(m => {
              const groups = ARCH_CATS.map(([k]) => [k, [...(m.tech || []), ...(m.infra || [])].filter(t => t.cat === k)]).filter(([, l]) => l.length);
              return (
                <div key={m.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 sm:p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl shrink-0">{m.icon}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white truncate">{m.name} <span className="text-xs font-normal text-gray-400">· {m.type}</span></div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        {[m.language, m.framework].filter(Boolean).join(' · ') || (m.empty ? 'Repo bo\'sh' : '—')}
                        {m.runtime && m.runtime !== m.language && <span className="text-gray-400"> · {m.runtime}</span>}
                      </div>
                    </div>
                  </div>
                  {m.error && <div className="text-xs text-red-600 dark:text-red-400 mb-2">❌ {m.error}</div>}
                  {m.empty && !m.error && <div className="text-sm text-gray-400 mb-2">Bu modulda hali kod yo'q — vazifa bering, stack shu yerda paydo bo'ladi.</div>}
                  <div className="space-y-2.5 flex-1">
                    {groups.map(([k, list]) => (
                      <div key={k}>
                        <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">{ARCH_CAT[k].icon} {ARCH_CAT[k].label}</div>
                        <div className="flex flex-wrap gap-1.5">{list.map((t, i) => chip(t, k + i))}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
                    {m.repo && <a href={m.repo.url} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-400 hover:underline">🦊 GitLab · {m.repo.branch}</a>}
                    {m.urls && m.urls.test && <a href={m.urls.test} target="_blank" rel="noreferrer" className="hover:underline">🧪 test</a>}
                    {m.urls && m.urls.prod && <a href={m.urls.prod} target="_blank" rel="noreferrer" className="hover:underline">🚀 prod</a>}
                    {m.source && <span title="Ma'lumot qayerdan o'qildi">📖 {sourceLabel[m.source] || m.source}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-gray-400 mt-3">Yangilangan: {new Date(data.generatedAt).toLocaleString('uz-UZ', { hour12: false })} · 5 daqiqa keshlanadi</div>
        </>
      )}
    </div>
  );
}

function DeployStatusPanel({ authFetch, apiUrl, projectId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    authFetch(`${apiUrl}/projects/${projectId}/deploy-status`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setData((d && d.modules) || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch, apiUrl, projectId]);
  React.useEffect(() => { load(); }, [load]);

  const icon = (t) => (t === 'mobile' ? '📱' : t === 'backend' ? '⚙️' : '🌐');
  const fmt = (s) => { try { return s ? new Date(s).toLocaleString('uz-UZ', { hour12: false }) : '—'; } catch { return s; } };
  const mods = data || [];
  const taskIds = [...new Set(mods.filter(m => m.superTaskId).map(m => m.superTaskId))];
  const mixed = taskIds.length > 1;

  if (!projectId) return <div className="text-center py-16 text-gray-400">Avval proyekt tanlang.</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-bold text-gray-900 dark:text-white">📦 Modullar holati</h2>
        <button onClick={load} disabled={loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition">{loading ? '⏳ ...' : '↻ Yangilash'}</button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Har bir modul qaysi vazifa (task) kodida ishlayapti va u <b>tasdiqlangan (main)</b> yoki hali <b>tasdiqlanmagan review branch</b> (kh/task-N) ekani.</p>
      {mixed && (
        <div className="mb-3 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
          ⚠️ Modullar har xil vazifada: {taskIds.map(t => `#${t}`).join(', ')}. Agar ular bir-biriga bog'liq bo'lsa (masalan backend + mobil bitta funksiya), hammasini bir xil holatga keltiring (tasdiqlang).
        </div>
      )}
      <div className="space-y-2">
        {mods.map(m => (
          <div key={m.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3.5 flex items-start gap-3">
            <div className="text-2xl shrink-0">{icon(m.module_type)}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 dark:text-white">{m.name} <ModId id={m.id} /></span>
                <span className="text-xs text-gray-400">{m.module_type}</span>
                {m.superTaskId
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 font-medium">Task #{m.superTaskId}</span>
                  : <span className="text-xs text-gray-400">deploy qilinmagan</span>}
                {m.branch && (m.approved
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 font-medium">✅ main (tasdiqlangan)</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 font-medium">⏳ {m.branch} (tasdiqlanmagan)</span>)}
                {/* Emulatordagi APK repo kodidan ORTDA — "kod main'da bor, ilovada yo'q"
                    holatining yagona ko'rinadigan belgisi (#713 shu tarzda chalkashtirgan). */}
                {m.module_type === 'mobile' && m.apkStale && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 font-medium"
                    title={`APK ${m.apkCommit} dan qurilgan, repo esa ${m.commit} — qayta build kerak`}>
                    ⚠️ APK eski — qayta build kerak
                  </span>)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {m.commit && <span title="git commit">📌 {m.commit}</span>}
                {m.module_type === 'mobile'
                  ? <span title={m.apkSubject || ''}>
                      📲 APK: {m.apkCommit
                        ? <code className={m.apkStale ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}>{m.apkCommit}</code>
                        : <span className="text-gray-400">commit noma'lum</span>} · {fmt(m.apkBuiltAt)}
                    </span>
                  : <span className={m.container === 'running' ? 'text-green-600 dark:text-green-400' : 'text-red-500'}>● {m.container === 'running' ? 'ishlayapti' : (m.container || "yo'q")}</span>}
                {m.deployedAt && <span title="oxirgi commit/deploy vaqti">🕑 {fmt(m.deployedAt)}</span>}
                {m.test_url && <a href={m.test_url} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-500 hover:underline">{m.test_url.replace(/^https?:\/\//, '')}</a>}
              </div>
            </div>
          </div>
        ))}
        {!loading && mods.length === 0 && <p className="text-gray-400 text-center py-8">Modul yo'q yoki hali deploy qilinmagan.</p>}
      </div>
    </div>
  );
}

// 📁 Fayllar — read-only file browser for a module's workspace (the code the AI agent
// wrote). Lets the operator inspect files / find values (bundle ID, config, endpoints)
// without a shell. Self-contained (own module select + path + tree + file viewer).
function FileBrowserPanel({ authFetch, apiUrl, modules, defaultModuleId }) {
  const mods = (modules || []); // all module types have a workspace to browse
  const [modId, setModId] = React.useState(defaultModuleId || (mods[0] && mods[0].id) || null);
  const [dir, setDir] = React.useState(null);      // { entries, path, name }
  const [curPath, setCurPath] = React.useState('');
  const [file, setFile] = React.useState(null);    // { path, content?, binary?, tooBig?, size }
  const [loading, setLoading] = React.useState(false);
  const [cloning, setCloning] = React.useState(false);   // workspace repodan klonlanmoqda
  const [err, setErr] = React.useState(null);

  const icon = (t) => (t === 'mobile' ? '📱' : t === 'backend' ? '⚙️' : '🌐');
  const fmtSize = (n) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
  const fileIcon = (name) => {
    const e = (name.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(e)) return '🖼️';
    if (['json'].includes(e)) return '📋';
    if (['md', 'txt'].includes(e)) return '📄';
    if (['js', 'jsx', 'ts', 'tsx'].includes(e)) return '📜';
    if (['gradle', 'kt', 'java', 'xml', 'plist'].includes(e)) return '⚙️';
    if (['png'].includes(e)) return '🖼️';
    return '📄';
  };

  const load = React.useCallback((mId, p, asFile) => {
    if (!mId) return;
    setLoading(true); setErr(null);
    authFetch(`${apiUrl}/project-modules/${mId}/files?path=${encodeURIComponent(p || '')}`)
      .then(r => r.json().catch(() => ({})))
      .then(d => {
        if (!d || !d.ok) { setErr(d && d.reason ? d.reason : 'error'); return; }
        if (d.type === 'dir') { setDir(d); setCurPath(d.path || ''); setCloning(!!d.cloning); if (!asFile) setFile(null); }
        else { setFile(d); }
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [authFetch, apiUrl]);

  // Re-fetch the root listing ONLY when the module changes — NOT when `load`'s identity
  // changes. `load` depends on the `authFetch` prop, whose identity changes on every App
  // re-render (socket/polls every ~7s), so keeping `load` in the deps re-ran this effect
  // constantly → setFile(null) wiped any open file/dir within a second.
  React.useEffect(() => { if (modId) { setFile(null); setDir(null); setCloning(false); load(modId, ''); } }, [modId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Workspace bo'sh bo'lsa backend repodan FONDA klonlaydi — tugaguncha 3s da bir so'raymiz.
  const loadRef = React.useRef(load); loadRef.current = load;
  React.useEffect(() => {
    if (!cloning || !modId) return;
    const t = setInterval(() => loadRef.current(modId, ''), 3000);
    return () => clearInterval(t);
  }, [cloning, modId]);

  const join = (base, name) => (base ? base + '/' + name : name);
  const openEntry = (ent) => {
    const np = join(curPath, ent.name);
    if (ent.type === 'dir') { load(modId, np); }
    else { load(modId, np, true); }
  };
  const goTo = (p) => { load(modId, p); };
  const segments = curPath ? curPath.split('/') : [];

  if (mods.length === 0) return <div className="text-center py-16 text-gray-400">Bu loyihada modul yo'q.</div>;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h2 className="font-bold text-gray-900 dark:text-white">📁 Fayllar</h2>
        <span className="text-xs text-gray-400">faqat o'qish — AI agent yozgan kodni ko'rish</span>
        <button onClick={() => load(modId, curPath)} disabled={loading} className="ml-auto px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition">{loading ? '⏳' : '↻'}</button>
      </div>

      {/* Module selector */}
      {mods.length > 1 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {mods.map(m => (
            <button key={m.id} onClick={() => setModId(m.id)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${String(modId) === String(m.id) ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              {icon(m.module_type)} {m.name} <ModId id={m.id} className={String(modId) === String(m.id) ? '!text-white/70' : ''} />
            </button>
          ))}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 mb-2 text-sm flex-wrap">
        <button onClick={() => goTo('')} className="text-primary-700 dark:text-primary-500 hover:underline font-mono">🏠</button>
        {segments.map((s, i) => {
          const p = segments.slice(0, i + 1).join('/');
          return (<span key={i} className="flex items-center gap-1">
            <span className="text-gray-300">/</span>
            <button onClick={() => goTo(p)} className="text-primary-700 dark:text-primary-500 hover:underline font-mono">{s}</button>
          </span>);
        })}
      </div>

      {err && (
        <div className="text-sm bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2 mb-3">
          {err === 'no_workspace' ? "Bu modulning kod-papkasi (workspace) hali yo'q — avval Repository ulang / test env sozlang." :
           err === 'no_module' ? "Modul topilmadi." :
           err === 'forbidden' ? "Ruxsat yo'q." :
           err === 'not_found' ? "Fayl/papka topilmadi." : `Xatolik: ${err}`}
        </div>
      )}

      <div className="flex gap-3 items-start">
        {/* Directory listing */}
        <div className="w-72 shrink-0 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="max-h-[62vh] overflow-y-auto">
            {curPath !== '' && (
              <button onClick={() => goTo(segments.slice(0, -1).join('/'))} className="w-full text-left px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 font-mono">⬆ ..</button>
            )}
            {dir && dir.entries && dir.entries.length > 0 ? dir.entries.map(ent => (
              <button key={ent.name} onClick={() => openEntry(ent)}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/40 flex items-center gap-2 border-b border-gray-50 dark:border-gray-700 ${file && file.path === join(curPath, ent.name) ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}>
                <span className="shrink-0">{ent.type === 'dir' ? '📁' : fileIcon(ent.name)}</span>
                <span className="truncate flex-1 font-mono text-gray-700 dark:text-gray-300">{ent.name}</span>
                {ent.type === 'file' && <span className="text-[10px] text-gray-400 shrink-0">{fmtSize(ent.size)}</span>}
              </button>
            )) : (!loading && (cloning
              ? <div className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"><div className="text-2xl mb-1 animate-pulse">📥</div>Kod GitLab repodan klonlanmoqda…<div className="text-xs text-gray-400 mt-1">bir marta, bir necha soniya</div></div>
              : <div className="px-3 py-6 text-center text-sm text-gray-400">Bo'sh papka</div>))}
          </div>
        </div>

        {/* File viewer */}
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {!file ? (
            <div className="px-4 py-16 text-center text-gray-400 text-sm">← Ko'rish uchun faylni tanlang</div>
          ) : (
            <div>
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <span className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{file.path}</span>
                <span className="text-[11px] text-gray-400 shrink-0">{fmtSize(file.size)}</span>
              </div>
              {file.tooBig ? (
                <div className="px-4 py-10 text-center text-sm text-gray-400">Fayl juda katta ({fmtSize(file.size)}) — ko'rsatilmaydi.</div>
              ) : file.binary ? (
                <div className="px-4 py-10 text-center text-sm text-gray-400">Binary fayl — matn sifatida ko'rsatib bo'lmaydi.</div>
              ) : (
                <pre className="text-xs font-mono text-gray-900 dark:text-white p-3 overflow-auto max-h-[62vh] whitespace-pre leading-relaxed">{file.content}</pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// (logo / TZ dizayn) bitta sahifada: jonli status, progress va ⏹ to'xtatish.
// Sahifa yopilsa ham backend ishlayveradi — bu sahifa faqat holatni ko'rsatadi.
// ── 📱 iPhone'da sinash (Expo Go) — jonli tunnel QR ──────────────────────────
// Infra serverdagi Expo/Metro dev-server tunnel URL'ini /expo/tunnel-status dan oladi
// (URL har Metro restart'ida O'ZGARADI — shuning uchun jonli so'raladi) va QR ko'rsatadi;
// Metro o'chiq bo'lsa ▶ bilan ishga tushiriladi (POST /expo/start + poll).
// 🚀 Publish — EAS Build (iOS) → TestFlight / App Store. Android hozircha "tez orada".
// /publish/status'ni poll qiladi (eas build:list = authoritative). Build ~15 daqiqa (EAS bulut).
// 💥 Crashlar — ilovaning O'ZI yuborgan real xatolari (POST /crash-report).
// Server hash bo'yicha dedup qiladi (takror crash yangi qator emas, ×N), fatal xato
// darhol [CRASH-FIX] vazifa yaratadi. Bu panel: ro'yxat + stack + qo'lda vazifa yaratish.
// 🐞 TOPILMALAR — agentik QA paytida agent YO'L-YO'LAKAY ko'rgan kamchiliklar.
// Vazifa AVTOMATIK yaratilmaydi (shovqin bo'lmasin): operator ko'rib chiqib bir
// bosishda vazifaga aylantiradi yoki rad etadi. Dedup: takroriy topilma ×N bo'ladi.
// 🤖 AGENT SINOVI — operator "shuni sinab ko'rsat" deb yozadi, agent ilovani/saytni
// HAQIQATAN ishlatib tekshiradi va xulosa beradi. Ilgari agentik QA faqat vazifa
// quvurida ishlardi (vazifa berish shart edi); bu panel uni vazifasiz ishga tushiradi.
// Jonli harakatlar shu ko'rinishning O'ZIDA (emulator katagi / brauzer paneli) ko'rinadi.
// ── Agent chiqishini XAVFSIZ ko'rsatish ────────────────────────────────────
// Agent JSON sxemasi satrlar massivini so'raydi ("steps": ["..."]), lekin model
// ba'zan obyekt qaytaradi ({step, action, result}). React obyektni bola sifatida
// render QILA OLMAYDI → "Objects are not valid as a React child" → butun daraxt
// uzilib ErrorBoundary ekrani chiqadi (jonli uchradi 2026-09-09: "Qadamlar va
// dalillar" tugmasi). Engine log tomonida allaqachon String(s) himoyasi bor edi;
// bu uning frontend egizagi. Model chiqishini HECH QACHON to'g'ridan-to'g'ri
// render qilmang — shu helper orqali o'tkazing.
const KH_ORD_KEY = /^(step|index|idx|n|no|num|order|number)$/i;
function khNode(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(khNode).filter(Boolean).join(' \u00b7 ');
  if (typeof v === 'object') {
    const txt = Object.entries(v)
      .filter(([k, x]) => !KH_ORD_KEY.test(k) && x !== null && x !== undefined && x !== '')
      .map(([, x]) => (typeof x === 'object' ? khNode(x) : String(x)))
      .filter(Boolean)
      .join(' \u2014 ');
    if (txt) return txt;
    try { return JSON.stringify(v).slice(0, 300); } catch (e) { return '[obyekt]'; }
  }
  return String(v);
}

function AgentQaBar({ authFetch, apiUrl, moduleId, surface, emulators, webModules, defaultWebModuleId, mobileModuleId: mobileIdProp, wide = false }) {
  const [wish, setWish] = React.useState('');
  const [narrate, setNarrate] = React.useState(false);   // 🔊 ovozli sinov — har safar o'zi tanlanadi (default ovozsiz)
  const [record, setRecord] = React.useState(false);     // 🎥 etap videosi — sinov yozib olinib montaj qilinadi (default yo'q)
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [detail, setDetail] = React.useState(false);
  const [shot, setShot] = React.useState(null);
  const [tick, setTick] = React.useState(0);
  // ▶ tugmasi NIMA UCHUN ishlamaganini AYTADI. Ilgari matn qisqa bo'lsa start()
  // JIMGINA return qilardi va nishon yo'q bo'lsa bloklovchi alert() chiqarardi —
  // ikkalasi ham "bosdim, hech narsa bo'lmadi" holatini bergan (jonli uchradi
  // 2026-09-09: emulatorlar hali yuklanayotganda bosilgan, so'rov serverga
  // umuman ketmagan). Endi sabab panelning O'ZIDA ko'rinadi.
  const [warn, setWarn] = React.useState('');
  // KO'P EMULATOR: qaysi qurilma(lar)da sinalsin. Standart — hozir ISHLAB TURGANLARI
  // (operator aynan ularga qarab turibdi). Bo'sh qoldirilsa backend biriktiruvga qarab tanlaydi.
  const [pickedSlots, setPickedSlots] = React.useState(null);
  // 🌐 Brauzer nishoni: qaysi web modul (ro'yxatdan tanlanadi). null = tanlanmagan.
  const [pickedWeb, setPickedWeb] = React.useState(undefined);   // undefined = standart
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;
  const isMobileView = surface !== 'web';
  // Web-only loyihada emulator nishoni bo'lmasin: mobileModuleId=null berilsa chiplar chiqmaydi
  // va so'rovga mobil modul yuborilmaydi (moduleId — holat/loyiha uchun langar, web bo'lishi mumkin).
  const mobileModuleId = mobileIdProp === undefined ? moduleId : mobileIdProp;
  // 🌐 Brauzer ko'rinishida `emuList` yuklanmaydi (u faqat Emulator ko'rinishida
  // pollanadi) — lekin bitta sinovda emulator + brauzer birga bo'lishi mumkin, shuning
  // uchun ro'yxat bo'sh bo'lsa o'zimiz bir marta olamiz.
  const [emuSelf, setEmuSelf] = React.useState(null);
  React.useEffect(() => {
    if (!mobileModuleId || (emulators && emulators.length)) return;
    let dead = false;
    (async () => {
      try {
        const r = await authFetchRef.current(`${apiUrl}/emulator/list`);
        const d = await r.json().catch(() => null);
        if (!dead && r.ok && d && Array.isArray(d.emulators)) setEmuSelf(d.emulators);
      } catch { /* jim */ }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileModuleId, apiUrl, (emulators || []).length]);
  const emuAll = (emulators && emulators.length) ? emulators : (emuSelf || []);
  const runningSlots = emuAll.filter(e => e.status === 'running').map(e => Number(e.slot)).sort((a, b) => a - b);
  // Standart: emulator ko'rinishida ishlab turgan qurilmalar, brauzer ko'rinishida — joriy sayt.
  const slotsSel = pickedSlots === null ? (isMobileView ? runningSlots : []) : pickedSlots;
  const webSel = pickedWeb === undefined ? (isMobileView ? null : (defaultWebModuleId || null)) : pickedWeb;
  const toggleSlot = (s) => setPickedSlots(prev => {
    const cur = prev === null ? (isMobileView ? runningSlots : []) : prev;
    return cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s].sort((a, b) => a - b);
  });
  const toggleWeb = (id) => setPickedWeb(webSel === id ? null : id);
  const nTargets = slotsSel.length + (webSel ? 1 : 0);

  const load = React.useCallback(async () => {
    if (!moduleId) return;
    try {
      const r = await authFetchRef.current(`${apiUrl}/agent-qa/status?moduleId=${moduleId}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d) setState(d.active === false && !d.status ? null : d);
    } catch { /* o'tkinchi xato — eski holat qoladi */ }
  }, [apiUrl, moduleId]);

  React.useEffect(() => {
    setState(null); setDetail(false);
    try { setWish(localStorage.getItem(`kh_agentqa_wish_${moduleId}`) || ''); } catch { setWish(''); }
    if (!moduleId) return;
    load();
    let k = 0;
    const t = setInterval(() => {
      if (!khPageVisible()) return;
      load(); setTick(x => x + 1);
      // Emulator ro'yxati tashqaridan berilmasa (🧪 Sinov tabi) — har 10s o'zimiz yangilaymiz,
      // aks holda keyin yoqilgan emulator chiplarda «⏻» bo'lib qolaverardi.
      if (k++ % 2 === 1 && mobileModuleId && !(emulators && emulators.length)) {
        authFetchRef.current(`${apiUrl}/emulator/list`).then(r => r.ok ? r.json() : null)
          .then(d => { if (d && Array.isArray(d.emulators)) setEmuSelf(d.emulators); }).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(t);
  }, [moduleId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const setWishClear = (t) => { setWish(t); if (warn) setWarn(''); };

  const start = async () => {
    const text = wish.trim();
    if (text.length < 5) {
      setWarn("Avval nimani sinashni yozing — kamida bitta jumla (masalan: «Yo'lovchi buyurtma bersin, haydovchi qabul qilsin»).");
      return;
    }
    if (!nTargets) {
      const notReady = emuAll.filter(e => e.status !== 'running').length;
      setWarn(emuAll.length && notReady
        ? `Nishon tanlanmagan — emulator hali tayyor emas (${notReady} ta o'chiq/yuklanmoqda). ▶ bilan yoqing, «ishlayapti» bo'lgach chip o'zi belgilanadi.`
        : "Kamida bitta nishon tanlang — ☑ Emulator yoki ☑ 🌐 sayt.");
      return;
    }
    setWarn('');
    setBusy(true); setDetail(false);
    try {
      try { localStorage.setItem(`kh_agentqa_wish_${moduleId}`, text); } catch {}
      const r = await authFetchRef.current(`${apiUrl}/agent-qa/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          moduleId,
          ...(slotsSel.length ? { mobileModuleId, slots: slotsSel } : {}),
          ...(webSel ? { webModuleId: webSel } : {}),
          instruction: text,
          ...(narrate ? { narrate: true } : {}),
          ...(record ? { record: true } : {}),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setWarn(d.error || "Sinovni boshlab bo'lmadi"); return; }
      setState({ active: true, status: 'running', instruction: text, startedAt: Date.now(), shots: [] });
      setTimeout(load, 1500);
    } catch (e) { setWarn('Xato: ' + (e.message || e)); } finally { setBusy(false); }
  };

  // ⏹ Noto'g'ri boshlangan sinovni to'xtatish. Busiz operator (masalan qurilmada
  // eski ilova qolgani ma'lum bo'lsa) 30 daqiqa tugashini kutishga majbur edi.
  const stop = async () => {
    if (!window.confirm("Sinovni to'xtatasizmi? Agent darhol to'xtaydi va xulosa bo'lmaydi.")) return;
    setBusy(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/agent-qa/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Server xatosi');
      setTimeout(load, 1500);
    } catch (e) { alert('Xato: ' + (e.message || e)); } finally { setBusy(false); }
  };

  if (!moduleId) return null;
  const running = state && state.status === 'running';
  const v = state && state.verdict;
  const secs = running && state.startedAt ? Math.max(0, Math.round((Date.now() - state.startedAt) / 1000)) : 0;

  return (
    <div className={`mb-3 ${surface === 'web' || wide ? '' : 'max-w-3xl mx-auto'}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm px-3 py-2.5">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0 mt-2">🤖 Agent sinovi</span>
          {/* Ko'p qadamli stsenariy bir qatorga sig'maydi (operator uzun matn yozadi) →
              textarea: o'zi o'sadi (max ~8 qator), Enter = yangi qator, Ctrl/⌘+Enter = boshlash. */}
          <textarea
            value={wish}
            onChange={e => setWishClear(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !running && !busy) { e.preventDefault(); start(); }
            }}
            disabled={running}
            rows={2}
            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 176) + 'px'; } }}
            placeholder={surface === 'web'
              ? "Nimani sinaylik? masalan: admin foydalanuvchini o'chira olishini tekshir"
              : "Nimani sinaylik? masalan: yangi zakaz yaratib, ikkita usta taklif yuborsin — mijozda ustaning bahosi va reytingi ko'rinsinmi, tekshir"}
            className="flex-1 min-w-[240px] px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm leading-snug resize-y focus:ring-2 focus:ring-primary-500 focus:outline-none disabled:bg-gray-50 dark:disabled:bg-gray-700"
          />
          <div className="flex flex-col items-stretch gap-1 shrink-0">
            <button
              onClick={start}
              disabled={running || busy || wish.trim().length < 5}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600">
              {running ? `⏳ Sinayapti… ${secs}s` : '▶ Sinab ko\'r'}
            </button>
            {!running && (
              <label className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                title="Agent har bosqichni ovoz bilan aytib boradi (ekran pastida subtitr + ovoz). Belgilanmasa — odatdagidek ovozsiz.">
                <input type="checkbox" checked={narrate} onChange={e => setNarrate(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                🔊 Ovozli
              </label>
            )}
            {!running && (
              <label className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                title="Sinov videoga yoziladi: faol ekran katta, qolganlari kichik, subtitr, ovoz (🔊 bilan), boshida sarlavha, oxirida xulosa; bo'sh kutishlar qisqartiriladi. Mijozga yuborish uchun.">
                <input type="checkbox" checked={record} onChange={e => setRecord(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                🎥 Video
              </label>
            )}
            {running
              ? <button
                  onClick={stop}
                  disabled={busy}
                  title="Agentni darhol to'xtatadi (xulosa bo'lmaydi)"
                  className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 dark:disabled:bg-gray-600">
                  ⏹ To'xtatish
                </button>
              : <span className="text-[10px] text-gray-400 text-center">⌘/Ctrl + Enter</span>}
          </div>
        </div>

        {(emuAll.length > 0 || (webModules || []).length > 0) && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            <span className="text-[11px] text-gray-400 shrink-0">Nishonlar:</span>
            {(mobileModuleId ? emuAll : []).map(e => {
              const s = Number(e.slot);
              const on = slotsSel.includes(s);
              return (
                <button key={`e${s}`} disabled={running} onClick={() => toggleSlot(s)}
                  title={e.status === 'running' ? 'Ishlab turibdi' : "To'xtatilgan — sinov boshlanganda yoqiladi"}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition ${on
                    ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'} ${running ? 'opacity-60 cursor-default' : ''}`}>
                  {on ? '☑' : '☐'} Emulator {s + 1}{e.status === 'running' ? '' : ' ⏻'}
                </button>
              );
            })}
            {(webModules || []).map(w => {
              const on = webSel === w.id;
              return (
                <button key={`w${w.id}`} disabled={running} onClick={() => toggleWeb(w.id)}
                  title={w.test_url}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition ${on
                    ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'} ${running ? 'opacity-60 cursor-default' : ''}`}>
                  {on ? '☑' : '☐'} 🌐 {w.name}
                </button>
              );
            })}
            {nTargets > 1 && (
              <span className="text-[11px] text-primary-700 dark:text-primary-500 font-medium">
                — agent {nTargets} nishonni birga boshqaradi{slotsSel.length > 1 ? " (kim kirganini o'zi aniqlaydi)" : ''}
              </span>
            )}
          </div>
        )}

        {!running && warn && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-2 py-1 mt-1.5">
            ⚠️ {warn}
          </p>
        )}

        {running && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
            Agent {(() => {
              const parts = [];
              if (state && state.slots && state.slots.length) parts.push(state.slots.length > 1 ? `${state.slots.length} ta emulatorda` : 'emulatorda');
              if (state && state.webModuleId) parts.push('brauzerda');
              return parts.length ? parts.join(' va ') : 'ishlayapti';
            })()} ishlayapti — jonli ko'rinadi (📱 Emulator / 🌐 Brauzer). Istagan payt <b>✋ Boshqaruvni olish</b> bilan to'xtata olasiz — butun sinov to'xtaydi.
            {state && state.recording && <span className="text-rose-600 dark:text-rose-400 font-medium"> 🎥 Video yozilmoqda — tugagach montaj qilinadi.</span>}
          </p>
        )}

        {!running && state && state.status === 'error' && (
          <p className="text-[11px] text-red-600 dark:text-red-400 mt-1.5">❌ {khNode(state.error)}</p>
        )}

        {!running && v && (
          /* UCH holat: ✅ o'tdi · ❌ o'tmadi · ⚠️ MUHIT (test'da o'tdi, lekin PRODUCTION'da
             o'zgarish yo'q — foydalanuvchilarda hali ishlamaydi). Ilgari faqat ikki holat
             bor edi va envBlocked qizil ❌ bo'lib "noto'g'ri qilingan" degan noto'g'ri
             ma'noni berardi (#880). */
          <div className={`mt-2 rounded-lg border px-3 py-2 ${v.envBlocked ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700' : v.passed ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-start gap-2">
              <span className="text-base leading-none mt-0.5">{v.envBlocked ? '⚠️' : v.passed ? '✅' : '❌'}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${v.envBlocked ? 'text-amber-800 dark:text-amber-300' : v.passed ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-800 dark:text-red-300'}`}>
                  {v.envBlocked
                    ? 'Faqat TEST muhitida tasdiqlandi — PRODUCTION\'ga chiqmagan'
                    : v.passed ? 'Talab bajarilmoqda' : 'Talab BAJARILMAYAPTI'}
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap">{khNode(v.summary)}</p>
                <button onClick={() => setDetail(d => !d)} className="text-[11px] text-primary-700 dark:text-primary-500 hover:underline mt-1">
                  {detail ? '▲ Yopish' : '▼ Qadamlar va dalillar'}
                </button>
              </div>
            </div>
            {detail && (
              <div className="mt-2 pl-6 space-y-1.5">
                {(v.steps || []).length > 0 && (
                  <ol className="list-decimal list-inside text-[11px] text-gray-600 dark:text-gray-400 space-y-0.5">
                    {v.steps.slice(0, 12).map((s, i) => <li key={i}>{khNode(s)}</li>)}
                  </ol>
                )}
                {(v.evidence || []).length > 0 && (
                  <div className="text-[11px] text-gray-600 dark:text-gray-400">
                    {v.evidence.slice(0, 6).map((e, i) => <div key={i}>👁 {khNode(e)}</div>)}
                  </div>
                )}
                {(state.shots || []).length > 0 && (
                  <div className="flex gap-1.5 flex-wrap pt-1">
                    {state.shots.slice(0, 12).map((s, i) => (
                      <img key={i} src={`${apiUrl}${s}`} alt="" onClick={() => setShot(`${apiUrl}${s}`)}
                        className="h-16 rounded border border-gray-200 dark:border-gray-700 cursor-zoom-in" />
                    ))}
                  </div>
                )}
                {(v.findings || []).length > 0 && (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 pt-1">🐞 {v.findings.length} ta yo'l-yo'lakay topilma — Studio → 🐞 Topilmalar</p>
                )}
              </div>
            )}
          </div>
        )}
        {!running && state && state.video && <RunVideo video={state.video} apiUrl={apiUrl} />}
      </div>
      {shot && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-6" onClick={() => setShot(null)}>
          <img src={shot} alt="" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

// ── 🤖 TO'LIQ EKRAN: agent SINOVI holati (run darajasida) ────────────────
// ⛶ overlay 🤖 Agent sinovi qatorini to'sib qo'yadi, shuning uchun to'liq rejimda
// operator sinov BOSHLANGANMI — bilmasdi. Katakdagi 🤖 belgi faqat agent qurilmaga
// O'TGACH chiqadi; tayyorlanish (emulatorni yoqish, ilovani tekshirish), eski-APK
// qo'riqchisi TO'SGAN holat va yakuniy ✅/❌ umuman ko'rinmasdi (2026-09-11: run #4
// «Eski ilova — 📲 bilan yangilang» bilan 2 soniyada to'xtagan, to'liq ekranda esa
// hech nima chiqmagan → "agent ishlayaptimi, bilmayapman"). Bu strip `/agent-qa/status`
// ni 4 s da so'raydi va TO'RT holatni ko'rsatadi: tayyorlanmoqda · agent ishlayapti
// (oxirgi harakat bilan) · boshlanmadi/xato · tugadi (xulosa). Bir nechta loyiha
// nomzod bo'lsa (biriktiruv, tanlangan loyiha, ketayotgan run) — ketayotgani,
// bo'lmasa eng oxirgi tugagani tanlanadi.
// ⭐ To'liq ekran JOYLASHUVI (2026-10-29, operator: «planshetni katta qilib ko'rish kerak, emulyatorni sal chetroqda»).
// Ilgari har qurilmaga ekranning teng 1/n kengligi berilardi — gorizontal planshet telefon bilan bir xil kenglikka
// siqilib kichrayardi (operator shuning uchun ikkita /emu oyna ochib o'tirardi).
//  · 'fit'  — umumiy ekran balandligi h: Σ(aspect·h) + oraliqlar ≤ kenglik → har ustun kengligi aspect·h
//             (gorizontal planshet o'zi ko'proq joy oladi, telefon chetda turadi);
//  · 'main' — ⭐ asosiy qurilma chapda katta, qolganlari o'ng ustunda ustma-ust (ustun kengligi = side × kenglik, sudraladi).
// items: [{k, ar: kenglik/balandlik, chrome: ekran ostidagi qatorlar (px)}]; vp: {w, h}.
const KH_FS_RAIL = 104, KH_FS_GAP = 16, KH_FS_SPLIT = 12;
function khAr(asp, dflt) {
  const m = String(asp || '').match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
  const v = m ? Number(m[1]) / Number(m[2]) : 0;
  return v > 0.1 && v < 10 ? v : dflt;
}
// 🔔 Agent tasdiq so'radi (socket `operator:confirmation`) — operator boshqa oynada/ilovada bo'lsa Chrome bildirishnomasi
// (2026-10-31, operator: «tasdiqlash chiqib qolsa bilmay qolayapman»). Ruxsat agent panelidagi 🔔 tugmasidan. Bir nechta
// Khorezmai yorlig'i ochiq bo'lsa ham `tag` tufayli bitta bildirishnoma. Matn — buyruq emas, «Nega» (ma'nosi).
function khNotifyConfirm(d) {
  if (!d || d.status !== 'pending' || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;      // ko'rib turibdi
  const sum = String(d.summary || '');
  const why = ((/\nNega: ([^\n]+)/.exec(sum) || [])[1] || sum.split('\n')[0] || '').slice(0, 180);
  const n = new Notification(`🧠 ${d.projectName ? d.projectName + ' — ' : ''}agent tasdiq so'rayapti`, { body: why, tag: `kh-confirm-${d.id}`, requireInteraction: true });
  n.onclick = () => { try { window.focus(); n.close(); } catch (e) { } };
}

function khFsLayout(items, cfg, vp) {
  const W = Math.max(320, (vp.w || 1280) - KH_FS_RAIL - 12);
  const H = Math.max(300, (vp.h || 800) - 16);
  const n = items.length;
  const cols = {};
  const mainK = cfg && cfg.mode === 'main' && n > 1 && items.some(i => i.k === cfg.main) ? cfg.main : null;
  if (!mainK) {
    const sumAr = items.reduce((s, i) => s + i.ar, 0) || 1;
    const maxChrome = Math.max(0, ...items.map(i => i.chrome));
    const h = Math.max(160, Math.min(H - maxChrome, (W - KH_FS_GAP * (n - 1)) / sumAr));
    // Ustun bo'yi = ekran + ostidagi qatorlar (H emas): aks holda ekran qutisi bo'yiga cho'zilib, kenglik bo'yicha
    // qisilgan qurilma tepasi/pastida qora bo'shliq qolardi (letterbox).
    for (const i of items) cols[i.k] = { width: Math.max(150, Math.round(i.ar * h)), height: Math.min(H, Math.round(h + i.chrome)) };
    return { mode: 'fit', cols, W, H };
  }
  const sideW = Math.round(Math.min(W * 0.6, Math.max(220, W * (Number(cfg.side) || 0.28))));
  const mainW = W - sideW - KH_FS_SPLIT;
  const main = items.find(i => i.k === mainK);
  cols[mainK] = { width: mainW, height: Math.min(H, Math.round(mainW / main.ar + main.chrome)) };
  const rest = items.filter(i => i.k !== mainK);
  const cellH = Math.floor((H - KH_FS_GAP * (rest.length - 1)) / rest.length);
  for (const i of rest) cols[i.k] = { width: sideW, height: Math.min(cellH, Math.round(sideW / i.ar + i.chrome)) };
  return { mode: 'main', cols, W, H, main: mainK, sideW, mainW, rest: rest.map(i => i.k) };
}

// 🏷 Agent kim va nima uchun ishlayapti (server `label`): «💬 Vazifa suhbati (viloyatga) · «…»», «🧪 Agent sinovi — vazifa #N · «…»».
// Yorliq bo'lmasa (eski agent) vazifa raqami; u ham bo'lmasa — umumiy matn. + qancha vaqtdan beri.
function khAgentWho(ag) {
  if (!ag) return '';
  const lbl = ag.label || (ag.taskId ? `Vazifa #${ag.taskId}` : '');
  if (!ag.startedAt) return lbl;
  const m = Math.max(0, Math.round((Date.now() - Number(ag.startedAt)) / 60000));
  return `${lbl}${lbl ? ' · ' : ''}${m >= 60 ? `${Math.floor(m / 60)} soat ${m % 60} daq` : `${m} daq`} oldin boshlangan`;
}

function AgentQaFsStrip({ authFetch, apiUrl, projectIds, slots, emuAgent, onControl, rail = false }) {
  const [st, setSt] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [hidden, setHidden] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [, setTick] = React.useState(0);
  const ref = React.useRef(authFetch);
  ref.current = authFetch;
  const key = (projectIds || []).map(Number).filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i).slice(0, 3).join(',');

  React.useEffect(() => {
    if (!key) { setSt(null); return; }
    let dead = false;
    const load = async () => {
      const ids = key.split(',').map(Number);
      const rs = await Promise.all(ids.map(async (pid) => {
        try {
          const r = await ref.current(`${apiUrl}/agent-qa/status?projectId=${pid}`);
          const d = await r.json().catch(() => null);
          return r.ok && d && d.status ? { ...d, projectId: pid } : null;
        } catch { return null; }
      }));
      if (dead) return;
      const live = rs.filter(Boolean);
      const pick = live.find(x => x.status === 'running')
        || live.sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0))[0] || null;
      setSt(pick);
    };
    load();
    const t = setInterval(() => { if (khPageVisible()) { load(); setTick(x => x + 1); } }, 4000);
    return () => { dead = true; clearInterval(t); };
  }, [key, apiUrl]);

  const runId = st ? `${st.projectId}:${st.startedAt || ''}:${st.finishedAt || ''}` : '';
  React.useEffect(() => { setOpen(false); }, [runId]);
  if (!st) return null;
  const running = st.status === 'running';
  // Tugagan natija faqat 1 soat ko'rinadi (restartdan tiklangan kechagi xulosa
  // to'liq ekranda shovqin bo'lmasin) va ✕ bilan yopiladi.
  if (!running && (hidden === runId || !st.finishedAt || Date.now() - st.finishedAt > 60 * 60 * 1000)) return null;

  const fmt = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`; };
  const lastLog = (st.log || []).slice(-1)[0] || '';
  const actSlot = (slots || []).find(s => emuAgent[s]?.active);
  const onDevice = actSlot !== undefined;
  const paused = onDevice && !!emuAgent[actSlot].paused;
  const v = st.verdict;
  const notStarted = !running && st.status !== 'done' && (
    (st.warnings || []).some(w => /BOSHLANMADI/i.test(String(w))) || /^Eski ilova/i.test(String(st.error || '')));

  const stop = async () => {
    if (!window.confirm("Sinovni butunlay to'xtatasizmi? Agent darhol to'xtaydi va xulosa bo'lmaydi.")) return;
    setBusy(true);
    try {
      const r = await ref.current(`${apiUrl}/agent-qa/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: st.projectId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Server xatosi');
      toast.success("⏹ Sinov to'xtatildi");
    } catch (e) { toast.error('Xato: ' + (e.message || e)); } finally { setBusy(false); }
  };

  let cls, icon, title, sub;
  if (running) {
    cls = paused ? 'bg-amber-600/90' : 'bg-sky-600/90';
    icon = paused ? '⏸' : (onDevice ? '🤖' : '⏳');
    title = paused ? 'Boshqaruv sizda — agent kutmoqda' : (onDevice ? 'Agent sinayapti' : 'Sinov tayyorlanmoqda');
    const parts = [];
    if (st.slots && st.slots.length) parts.push(st.slots.length > 1 ? `${st.slots.length} ta emulator` : 'emulator');
    if (st.webModuleId) parts.push('brauzer');
    sub = `${fmt(Date.now() - (st.startedAt || Date.now()))}${parts.length ? ' · ' + parts.join(' + ') : ''}${lastLog ? ' · ' + String(lastLog).trim().slice(0, 90) : ''}`;
  } else if (v) {
    cls = v.envBlocked ? 'bg-amber-600/90' : v.passed ? 'bg-emerald-600/90' : 'bg-red-600/90';
    icon = v.envBlocked ? '⚠️' : v.passed ? '✅' : '❌';
    title = v.envBlocked ? 'Faqat TEST muhitida tasdiqlandi' : v.passed ? 'Talab bajarilmoqda' : 'Talab BAJARILMAYAPTI';
    sub = String(v.summary || '').slice(0, 120);
  } else {
    cls = notStarted ? 'bg-amber-600/90' : 'bg-red-600/90';
    icon = notStarted ? '⛔' : '❌';
    title = notStarted ? 'Sinov BOSHLANMADI' : (st.status === 'stopped' ? "Sinov to'xtatildi" : 'Sinov xato bilan tugadi');
    sub = String(st.error || (st.warnings || []).slice(-1)[0] || '').slice(0, 140);
  }

  // ── CHAP USTUN (rail) rejimi: ixcham karta, bosilsa o'ngga popover ochiladi ──
  if (rail) {
    const railTitle = running
      ? (paused ? 'Siz' : (onDevice ? 'Agent' : 'Tayyorlanmoqda'))
      : v ? (v.envBlocked ? 'Faqat test' : v.passed ? 'Bajarildi' : 'Bajarilmadi')
        : (notStarted ? 'Boshlanmadi' : (st.status === 'stopped' ? "To'xtatildi" : 'Xato'));
    return (
      <div className="relative w-[76px] shrink-0" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(o => !o)} title={`${title} — ${sub}`}
          className={`w-full rounded-lg px-1 py-2 text-white text-[10px] leading-tight flex flex-col items-center gap-0.5 shadow-lg ${cls}`}>
          <span className={`text-lg leading-none ${running && !paused ? 'animate-pulse' : ''}`}>{icon}</span>
          <span className="text-center break-words w-full">{railTitle}</span>
          {running && <span className="font-mono opacity-90">{fmt(Date.now() - (st.startedAt || Date.now()))}</span>}
        </button>
        {open && (
          /* fixed — ustunning overflow-y-auto'si absolute popover'ni kesib qo'yadi (overflow-x ham auto bo'lib qoladi) */
          <div className="fixed left-[100px] top-[64px] w-[380px] max-w-[70vw] rounded-lg bg-black/90 backdrop-blur text-white text-xs p-3 shadow-2xl border border-white/10 z-[70]">
            <div className="font-semibold">{icon} {title}</div>
            {sub && <div className="mt-1 opacity-90 whitespace-pre-wrap">{sub}</div>}
            {!running && v && (v.steps || []).length > 0 && <div className="mt-1 opacity-70">{v.steps.length} qadam · {(v.evidence || []).length} dalil · {(st.shots || []).length} skrinshot</div>}
            {!running && (st.warnings || []).slice(-2).map((w, i) => <div key={i} className="text-amber-200 mt-1">⚠️ {khNode(w)}</div>)}
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {running && onDevice && (
                <button onClick={() => onControl && onControl(actSlot, paused ? 'resume' : 'pause')}
                  className={`px-2 py-1 rounded font-semibold ${paused ? 'bg-sky-600 hover:bg-sky-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                  {paused ? '▶ Agentga qaytarish' : '✋ Boshqaruvni olish'}
                </button>
              )}
              {running && <button onClick={stop} disabled={busy} className="px-2 py-1 rounded font-semibold bg-rose-600 hover:bg-rose-700 disabled:opacity-50">⏹ To'xtatish</button>}
              {!running && <button onClick={() => { setHidden(runId); setOpen(false); }} className="px-2 py-1 rounded bg-white/15 hover:bg-white/25">✕ Belgini olib tashlash</button>}
              <button onClick={() => setOpen(false)} className="px-2 py-1 rounded bg-white/15 hover:bg-white/25 ml-auto">Yopish</button>
            </div>
            {!running && <div className="mt-1.5 text-gray-400">To'liq tafsilot: 🧪 Sinov tabi.</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start min-w-0 max-w-full pointer-events-auto" onClick={(e) => e.stopPropagation()}>
      <div className={`rounded-lg px-3 py-1.5 backdrop-blur text-white text-xs shadow-lg flex items-center gap-2 max-w-full ${cls}`}>
        <span className={`font-semibold whitespace-nowrap ${running && !paused ? 'animate-pulse' : ''}`}>{icon} {title}</span>
        {sub && <span className="opacity-90 truncate min-w-0" title={sub}>{sub}</span>}
        {running && onDevice && (
          <button onClick={() => onControl && onControl(actSlot, paused ? 'resume' : 'pause')}
            title={paused ? 'Boshqaruvni agentga qaytarish' : "Agentni to'xtatib, o'zingiz boshqarish"}
            className={`shrink-0 px-2 py-0.5 rounded font-semibold ${paused ? 'bg-sky-700 hover:bg-sky-800' : 'bg-amber-700 hover:bg-amber-800'}`}>
            {paused ? '▶ Agentga' : '✋ Olish'}
          </button>
        )}
        {running && (
          <button onClick={stop} disabled={busy} title="Sinovni butunlay to'xtatish (xulosa bo'lmaydi)"
            className="shrink-0 px-2 py-0.5 rounded font-semibold bg-rose-700 hover:bg-rose-800 disabled:opacity-50">⏹</button>
        )}
        {!running && (
          <>
            <button onClick={() => setOpen(o => !o)} className="shrink-0 px-1.5 py-0.5 rounded bg-white/15 hover:bg-white/25" title="Tafsilot">{open ? '▲' : '▼'}</button>
            <button onClick={() => setHidden(runId)} className="shrink-0 px-1.5 py-0.5 rounded bg-white/15 hover:bg-white/25" title="Yopish">✕</button>
          </>
        )}
      </div>
      {open && !running && (
        <div className="mt-1.5 rounded-lg bg-black/75 text-gray-100 text-[11px] px-3 py-2 max-w-[560px] whitespace-pre-wrap">
          {v && v.summary ? khNode(v.summary) : null}
          {!v && st.error ? <div>{khNode(st.error)}</div> : null}
          {(st.warnings || []).slice(-3).map((w, i) => <div key={i} className="text-amber-200 mt-1">⚠️ {khNode(w)}</div>)}
          {v && (v.steps || []).length > 0 && <div className="mt-1 opacity-80">{v.steps.length} qadam · {(v.evidence || []).length} dalil · {(st.shots || []).length} skrinshot</div>}
          <div className="mt-1.5 text-gray-400">To'liq tafsilot (qadamlar, dalillar, skrinshotlar): ✕ Yopish → 🤖 Agent sinovi qatori.</div>
        </div>
      )}
    </div>
  );
}

// ── 📦 Platforma versiyasi + o'zgarishlar tarixi ───────────────────────────
// Manba: backend GET /version (data/changelog.json). BIR MARTA yuklanadi —
// changelog kamdan-kam o'zgaradi, shuning uchun polling YO'Q (refetch-storm
// tuzog'iga tushmaslik uchun authFetch ref orqali olinadi).
const KH_CHG_TYPE = {
  feature: { icon: '\u2728', cls: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800', label: 'Yangi' },
  fix:     { icon: '\ud83d\udc1e', cls: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800', label: 'Tuzatildi' },
  improve: { icon: '\u26a1', cls: 'bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border-primary-200 dark:border-primary-800', label: 'Yaxshilandi' },
};

function VersionBadge({ authFetch, apiUrl }) {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState({});
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetchRef.current(`${apiUrl}/version`);
        if (!res.ok) return;
        const d = await res.json();
        if (alive && d && Array.isArray(d.releases)) setData(d);
      } catch (e) { /* versiya o'qilmasa ham panel ishlayveradi */ }
    })();
    return () => { alive = false; };
  }, [apiUrl]);

  if (!data || !data.current) return null;
  const cur = data.currentRelease;
  const older = (data.releases || []).filter(r => !cur || r.version !== cur.version);

  const renderChange = (c, i) => {
    const t = KH_CHG_TYPE[c.type] || KH_CHG_TYPE.improve;
    return (
      <li key={i} className="flex gap-2 items-start text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium ${t.cls}`}>
          {t.icon} {t.label}
        </span>
        <span className="min-w-0">{c.text}</span>
      </li>
    );
  };

  const renderRelease = (r, isCurrent) => (
    <div key={r.version} className={`rounded-lg border ${isCurrent ? 'border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-900/30' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
      <button
        onClick={() => setExpanded(e => ({ ...e, [r.version]: !e[r.version] }))}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition"
      >
        <span className={`shrink-0 px-2 py-0.5 rounded font-mono text-xs font-bold ${isCurrent ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'}`}>
          v{r.version}
        </span>
        <span className="flex-1 min-w-0 font-medium text-gray-900 dark:text-white text-sm">{r.title}</span>
        <span className="shrink-0 text-xs text-gray-400">{r.date}</span>
        <span className="shrink-0 text-xs text-gray-400">{(r.changes || []).length} ta</span>
        <span className="shrink-0 text-gray-400 text-xs">{expanded[r.version] ? '\u25b2' : '\u25bc'}</span>
      </button>
      {expanded[r.version] && (
        <ul className="px-4 pb-4 pt-1 space-y-2">{(r.changes || []).map(renderChange)}</ul>
      )}
    </div>
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="O'zgarishlar tarixini ko'rish"
        className="inline-flex items-center gap-1 text-[10px] font-mono leading-none text-gray-400 dark:text-gray-500 hover:text-primary-700 dark:hover:text-primary-300 transition"
      >
        v{data.current}
        <span className="text-[8px] opacity-70">{'\u2197'}</span>
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
             onClick={() => setOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col text-gray-900 dark:text-white"
               onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold">{'\ud83d\udce6'} Khorezmai o{'\u2019'}zgarishlar tarixi</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Joriy versiya <span className="font-mono font-bold text-primary-700 dark:text-primary-500">v{data.current}</span>
                  {' \u00b7 '}{(data.releases || []).length} ta versiya{' \u00b7 '}{data.totalChanges} ta o{'\u2019'}zgarish
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none px-2">{'\u2715'}</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {cur && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Joriy versiyada nimalar qo{'\u2019'}shildi
                  </div>
                  <div className="rounded-lg border border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-900/30 px-4 py-3">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-0.5 rounded bg-primary-700 dark:bg-primary-600 text-white font-mono text-xs font-bold">v{cur.version}</span>
                      <span className="flex-1 min-w-0 font-medium text-gray-900 dark:text-white text-sm">{cur.title}</span>
                      <span className="text-xs text-gray-400">{cur.date}</span>
                    </div>
                    <ul className="space-y-2">{(cur.changes || []).map(renderChange)}</ul>
                  </div>
                </div>
              )}

              {older.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Oldingi versiyalar{' \u2014 '}bosib oching
                  </div>
                  <div className="space-y-2">{older.map(r => renderRelease(r, false))}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── ❓ YORDAM vidjeti ──────────────────────────────────────────────────────
// Uchta rejim: (1) ekrandagi elementni tanlab "bu nima?", (2) "endi nima qilay?",
// (3) erkin savol. Javob backendda AVVAL qo'lda yozilgan bazadan qidiriladi
// (aniq, darhol, 0 token), topilmasa AI shu baza asosida javob beradi.
// Vidjet HECH NARSANI BAJARMAYDI - faqat tushuntiradi (ataylab: tugma bosadigan
// yordamchi adashib deploy qilib yuborishi mumkin).

// Bosilgan nuqtadan mazmunli elementni topamiz (tugma/havola/maydon), yordam
// vidjetining O'ZI tanlanmasin.
function khPickTarget(el) {
  if (!el || (el.closest && el.closest('[data-kh-helpui]'))) return null;
  const INTERACTIVE = ['button', 'a', 'label', 'input', 'select', 'textarea', 'summary'];
  let n = el;
  for (let i = 0; i < 8 && n && n !== document.body; i++) {
    const tag = (n.tagName || '').toLowerCase();
    if (INTERACTIVE.indexOf(tag) >= 0 || (n.getAttribute && n.getAttribute('role') === 'button')) return n;
    n = n.parentElement;
  }
  return (el.innerText || '').trim() ? el : null;
}

function khDescribe(el) {
  let heading = '';
  let p = el;
  for (let i = 0; i < 8 && p; i++) {
    const h = p.querySelector && p.querySelector('h1,h2,h3');
    if (h && (h.innerText || '').trim()) { heading = h.innerText.trim().slice(0, 120); break; }
    p = p.parentElement;
  }
  return {
    text: (el.innerText || el.value || (el.getAttribute && el.getAttribute('placeholder')) || '').trim().replace(/\s+/g, ' ').slice(0, 200),
    label: (el.getAttribute && el.getAttribute('aria-label')) || '',
    title: (el.getAttribute && el.getAttribute('title')) || '',
    tag: el.tagName || '',
    heading,
  };
}

// 🖱 «Bu nima?» — navbar'dagi tugma (2026-10-28, operator: o'ng-pastdagi ❓ tugma va menyu ekranni to'sardi —
// «🧭 Endi nima qilay?» va savol maydoni olib tashlandi). Bosilsa darhol tanlash rejimi; javob navbar ostida kichik oynada.
function HelpWidget({ authFetch, apiUrl, area, projectId }) {
  const [picking, setPicking] = React.useState(false);
  const [answer, setAnswer] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [hl, setHl] = React.useState(null);
  const authFetchRef = React.useRef(authFetch); authFetchRef.current = authFetch;
  const areaRef = React.useRef(area); areaRef.current = area;
  const projRef = React.useRef(projectId); projRef.current = projectId;

  const askElement = React.useCallback(async (el) => {
    setBusy(true); setAnswer(null);
    try {
      const res = await authFetchRef.current(`${apiUrl}/help/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ element: khDescribe(el), area: areaRef.current, projectId: projRef.current }),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok || !d) throw new Error((d && d.error) || 'server');
      setAnswer(d);
    } catch (e) {
      setAnswer({ source: 'none', text: 'Javob olib bo‘lmadi — biroz o‘tib qayta urinib ko‘ring.' });
    } finally { setBusy(false); }
  }, [apiUrl]);

  // Tanlash rejimi: bosishni CAPTURE fazasida ushlaymiz va bekor qilamiz —
  // aks holda "bu nima?" deb bosgan tugma HAQIQATAN bosilib ketardi
  // (vazifa o'chirish / deploy kabi qaytarib bo'lmaydigan amal ham bor).
  React.useEffect(() => {
    if (!picking) { setHl(null); return undefined; }
    const move = (e) => {
      const t = khPickTarget(document.elementFromPoint(e.clientX, e.clientY));
      if (!t) { setHl(null); return; }
      const r = t.getBoundingClientRect();
      setHl({ top: r.top, left: r.left, w: r.width, h: r.height });
    };
    const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onClick = (e) => {
      e.preventDefault(); e.stopPropagation();
      const t = khPickTarget(e.target);
      setPicking(false);
      if (t) askElement(t);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPicking(false); };
    document.addEventListener('mousemove', move, true);
    document.addEventListener('pointerdown', swallow, true);
    document.addEventListener('mousedown', swallow, true);
    document.addEventListener('mouseup', swallow, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', move, true);
      document.removeEventListener('pointerdown', swallow, true);
      document.removeEventListener('mousedown', swallow, true);
      document.removeEventListener('mouseup', swallow, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [picking, askElement]);

  const close = () => { setAnswer(null); setBusy(false); };
  return (
    <>
      {/* Tanlash rejimi: ko'rsatkich ostidagi elementni yoritamiz */}
      {picking && hl && (
        <div className="fixed pointer-events-none z-[85] border-2 border-primary-600 bg-primary-500/15 rounded transition-all duration-75"
             style={{ top: hl.top - 2, left: hl.left - 2, width: hl.w + 4, height: hl.h + 4 }} />
      )}
      {picking && (
        <div data-kh-helpui className="fixed top-0 left-0 right-0 z-[86] bg-primary-700 dark:bg-primary-600 text-white text-sm py-2 px-4 text-center shadow-lg">
          Ekrandagi istalgan tugma yoki maydonni bosing — nima ekanini tushuntiraman.
          <span className="opacity-75"> (Esc — bekor qilish. Tugma haqiqatan bosilmaydi.)</span>
        </div>
      )}

      <button data-kh-helpui type="button" onClick={() => { if (picking) setPicking(false); else { close(); setPicking(true); } }}
              title={picking ? 'Bekor qilish (Esc)' : '🖱 Bu nima? — bosing, keyin ekrandagi tushunarsiz tugma yoki maydonni tanlang'}
              aria-label="Bu nima?"
              className={`h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-lg shadow-sm border text-base transition ${picking ? 'bg-rose-600 border-rose-600 text-white hover:bg-rose-700' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
        {picking ? '✕' : '❔'}
      </button>

      {(busy || answer) && !picking && (
        <div data-kh-helpui className="fixed top-16 right-4 z-[80] w-[22rem] max-w-[94vw] max-h-[60vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 bg-gray-50 dark:bg-gray-700">
            <span className="font-semibold text-sm text-gray-900 dark:text-white flex-1">🖱 Bu nima?</span>
            <button type="button" onClick={() => { close(); setPicking(true); }} title="Boshqa narsani tanlash"
                    className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600">↺ Yana</button>
            <button type="button" onClick={close} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none px-1">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 text-sm">
            {busy && <div className="text-gray-500 dark:text-gray-400">⏳ Qarab chiqyapman...</div>}
            {answer && !busy && (
              <div>
                {answer.title && <div className="font-semibold text-gray-900 dark:text-white mb-1">{answer.title}</div>}
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{answer.text}</div>
                {(answer.tips || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {answer.tips.map((t, i) => (
                      <li key={i} className="text-gray-600 dark:text-gray-400 text-[13px] flex gap-1.5">
                        <span className="text-amber-500 shrink-0">💡</span><span>{t}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {answer.source === 'ai' && (
                  <div className="mt-3 text-[11px] text-gray-400">AI javobi — noaniqlik bo‘lsa operatordan so‘rang.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function FindingsPanel({ authFetch, apiUrl, projectId, onCount = null, readOnly = false }) {
  const [rows, setRows] = React.useState(null);
  const [fixId, setFixId] = React.useState(null);   // qaysi topilma uchun 🔎 tuzatish ochiq
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [shot, setShot] = React.useState(null);
  const [showDismissed, setShowDismissed] = React.useState(false);
  // ✔ Tuzatilganlar (fix vazifasi tasdiqlangan — backend o'zi yopadi, docs/knowledge/200) sukut bo'yicha yashirin.
  const [showFixed, setShowFixed] = React.useState(false);
  // 🔎 Qidiruv: «#14970» yoki «14970» — aynan shu raqam (agent topilmaga shu raqam bilan murojaat qiladi), aks holda so'z bo'yicha.
  const [q, setQ] = React.useState('');
  const pollRef = React.useRef(null);
  const authFetchRef = React.useRef(authFetch);     // refetch-storm gotchasi
  authFetchRef.current = authFetch;

  const load = async (spin = true) => {
    if (spin) setLoading(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/agent-findings${projectId ? `?projectId=${projectId}` : ''}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && Array.isArray(d.findings)) {
        setRows(d.findings);
        // ⏳ Jarayonlar panelidagi bo'lim sarlavhasi uchun — ochiq (yangi) topilmalar soni.
        if (onCount) onCount(d.findings.filter(f => f.status === 'new').length);
      }
    } catch { /* o'tkinchi xato — eski ro'yxat qoladi */ } finally { if (spin) setLoading(false); }
  };

  React.useEffect(() => {
    setRows(null);
    if (projectId) load();
    pollRef.current = setInterval(() => { if (khPageVisible() && projectId) load(false); }, 20000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const act = async (id, what) => {
    setBusy(`${id}:${what}`);
    try {
      const url = what === 'delete' ? `${apiUrl}/agent-findings/${id}` : `${apiUrl}/agent-findings/${id}/${what}`;
      const r = await authFetchRef.current(url, { method: what === 'delete' ? 'DELETE' : 'POST', headers: { 'Content-Type': 'application/json' }, body: what === 'delete' ? undefined : '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert(d.error || 'Xatolik'); return; }
      await load(false);
    } catch { alert('Xatolik'); } finally { setBusy(''); }
  };

  const fmt = (t) => { try { const d = new Date(t); const p = n => String(n).padStart(2, '0'); return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`; } catch { return ''; } };
  const SEV = { high: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', medium: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', low: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' };
  const qq = q.trim().toLowerCase();
  const qIds = (qq.match(/#\d+|\b\d{4,}\b/g) || []).map(x => Number(x.replace('#', '')));   // «500» — so'z, «#500»/«14970» — raqam
  const visible = (rows || []).filter(f => {
    if (qIds.length) return qIds.includes(Number(f.id));
    if (!showDismissed && f.status === 'dismissed') return false;
    if (!showFixed && f.status === 'fixed') return false;
    return !qq || `${f.title} ${f.detail || ''} ${f.module_name || ''}`.toLowerCase().includes(qq);
  });

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-gray-900 dark:text-white">🐞 Topilmalar</h3>
          <p className="text-xs text-gray-400">Agent va xavfsizlik skaneri topgan kamchiliklar · <b className="text-emerald-600 dark:text-emerald-400">💻 kod</b> — Khorezmai tuzatadi · <b className="text-orange-600 dark:text-orange-400">🖥 server</b> / <b className="text-sky-600 dark:text-sky-400">👤 operator</b> — 📋 rejaga tushadi, siz bajarasiz</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔎 #raqam yoki so'z"
            title="Agent topilmaga #raqam bilan murojaat qiladi — shu raqamni yozsangiz o'sha topilma(lar) chiqadi (bir nechtasini bo'sh joy bilan)"
            className="w-40 px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500" />
          <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <input type="checkbox" checked={showDismissed} onChange={e => setShowDismissed(e.target.checked)} /> rad etilganlar
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" title="Tuzatish vazifasi tasdiqlangan topilmalar — o'zi «tuzatildi» bo'ladi; xuddi shu kamchilik qayta topilsa yana ochiladi">
            <input type="checkbox" checked={showFixed} onChange={e => setShowFixed(e.target.checked)} /> tuzatilganlar{(rows || []).some(f => f.status === 'fixed') ? ` (${(rows || []).filter(f => f.status === 'fixed').length})` : ''}
          </label>
          <button onClick={() => load()} className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg">↻ Yangilash</button>
        </div>
      </div>

      {rows === null && loading ? (
        <div className="py-10 text-center text-gray-400 text-sm">Yuklanmoqda...</div>
      ) : visible.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">
          <p className="text-3xl mb-2">✨</p>
          <p>Topilma yo'q.</p>
          <p className="text-xs mt-1">Agentik QA ishlaganda, agent ko'rgan kamchiliklar shu yerda paydo bo'ladi.</p>
        </div>
      ) : visible.map(f => (
        <div key={f.id} className={`mb-2 rounded-lg border p-3 ${f.status === 'dismissed' || f.status === 'fixed' ? 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 opacity-60' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
          <div className="flex items-start gap-3">
            {f.screenshot_url && (
              <img src={`${apiUrl}${f.screenshot_url}`} alt="" onClick={() => setShot(`${apiUrl}${f.screenshot_url}`)}
                className="w-14 h-24 object-cover rounded border border-gray-200 dark:border-gray-700 cursor-zoom-in shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span translate="no" title="Topilma raqami — agent unga shu raqam bilan murojaat qiladi (bosing — nusxa olinadi)"
                  onClick={() => { try { navigator.clipboard.writeText(`#${f.id}`); toast.success(`#${f.id} nusxa olindi`); } catch (e) {} }}
                  className="text-[11px] font-mono font-semibold text-gray-500 dark:text-gray-400 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400">#{f.id}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${SEV[f.severity] || SEV.low}`}>{f.severity}</span>
                {/* Manba: QA agenti ilovani sinab ko'rgani vs xavfsizlik skaneri topgani —
                    ikkisi butunlay boshqa ish, shuning uchun ro'yxatda ajratib turadi. */}
                {f.source === 'security' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 font-semibold"
                    title="Security Khorezmai skaneri jonli manzilda topgan zaiflik">🛡️ xavfsizlik</span>
                )}
                {/* KIM tuzata oladi. Engine agenti FAQAT repo kodini o'zgartiradi — sshd/sudo/
                    docker/UFW unga yetmaydi, shuning uchun bunday topilma vazifa emas, 📋 rejaga
                    tushadi (aks holda agent "bajardim" deb bo'sh o'zgarish deploy qilardi). */}
                {f.fixKind && (
                  <span title={f.fixWhy || ''} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                    f.fixKind === 'code' ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                      : f.fixKind === 'server' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400'
                      : 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-400'}`}>
                    {f.fixKind === 'code' ? '💻 kod' : f.fixKind === 'server' ? '🖥 server' : '👤 operator'}
                  </span>
                )}
                <span className="font-semibold text-gray-900 dark:text-white text-sm">{f.title}</span>
                {f.occurrences > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">×{f.occurrences}</span>}
                {(f.status === 'task' || (f.status === 'fixed' && f.fix_task_id)) && (
                  <button onClick={() => { if (!readOnly) setFixId(fixId === f.id ? null : f.id); }}
                    title="Nima tuzatildi, qaysi commit, test/production'da bormi"
                    className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/70">
                    🛠 #{f.fix_task_id}{f.task_status ? ` · ${f.task_status}` : ''} {fixId === f.id ? '▲' : '🔎'}
                  </button>
                )}
                {f.status === 'plan' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-400 font-semibold" title="📋 Reja doskasiga operator kartasi sifatida qo'shilgan">📋 rejada</span>}
                {f.status === 'dismissed' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400">rad etilgan</span>}
                {f.status === 'fixed' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 font-semibold" title="Tuzatish vazifasi tasdiqlangan">✔ tuzatildi{f.fixed_at ? ` · ${fmt(f.fixed_at)}` : ''}</span>}
              </div>
              {f.detail && <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">{f.detail}</p>}
              <p className="text-[11px] text-gray-400 mt-1">
                {f.project_name}{f.module_name ? ` · ${f.module_name}` : ''}{f.task_id ? ` · vazifa #${f.task_id}` : ''} · {fmt(f.last_seen)}
              </p>
              {fixId === f.id && f.fix_task_id && (
                <FixTrace authFetch={authFetch} apiUrl={apiUrl} taskId={f.fix_task_id} />
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {!readOnly && f.status !== 'task' && f.status !== 'plan' && f.status !== 'fixed' && (
                f.fixKind && f.fixKind !== 'code' ? (
                  <button onClick={() => act(f.id, 'plan')} disabled={busy === `${f.id}:plan`}
                    title={`${f.fixWhy || ''} — Khorezmai agenti buni tuzata olmaydi, 📋 Reja doskasiga sizning kartangiz bo'lib tushadi`}
                    className="px-2 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap">
                    {busy === `${f.id}:plan` ? '⏳' : '📋 Rejaga'}
                  </button>
                ) : (
                  <button onClick={() => act(f.id, 'task')} disabled={busy === `${f.id}:task`}
                    className="px-2 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap">
                    {busy === `${f.id}:task` ? '⏳' : '✅ Vazifa'}
                  </button>
                )
              )}
              {!readOnly && f.status === 'new' && (
                <button onClick={() => act(f.id, 'dismiss')} disabled={busy === `${f.id}:dismiss`}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg whitespace-nowrap">⏭ Rad etish</button>
              )}
              {!readOnly && <button onClick={() => { if (window.confirm('Topilmani o\'chirasizmi?')) act(f.id, 'delete'); }}
                className="px-2 py-1 text-xs text-gray-300 hover:text-red-500">🗑</button>}
            </div>
          </div>
        </div>
      ))}

      {shot && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-6" onClick={() => setShot(null)}>
          <img src={shot} alt="" className="max-h-[90vh] rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

// 🔎 «Nima tuzatildi → qaysi commit → qayerda bor» — vazifa izi.
// Crash/topilma tuzatilgach operator faqat `🛠 #N · completed` chipini ko'rardi.
// Bu blok vazifaning HISOBOTINI, COMMIT(lar)ini va TEST/PRODUCTION holatini ko'rsatadi.
function FixTrace({ authFetch, apiUrl, taskId, url }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  React.useEffect(() => {
    let dead = false;
    (async () => {
      setData(null); setErr('');
      try {
        const r = await authFetchRef.current(url || `${apiUrl}/tasks/${taskId}/trace`);
        const d = await r.json().catch(() => null);
        if (dead) return;
        if (!r.ok || !d) setErr((d && d.error) || 'Ma\'lumot olinmadi');
        else if (d.noTask) setErr('Bu yozuv uchun tuzatish vazifasi yo\'q');
        else setData(d);
      } catch { if (!dead) setErr('Ma\'lumot olinmadi'); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, url, apiUrl]);

  if (err) return <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">{err}</div>;
  if (!data) return <div className="mt-2 text-[11px] text-gray-400">yuklanmoqda…</div>;

  const badge = (ok, label, title) => (
    <span title={title || ''}
      className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${ok === true ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400'
        : ok === false ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
      {ok === true ? '✅' : ok === false ? '❌' : '—'} {label}
    </span>
  );

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3 space-y-2">
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        Vazifa <b>#{data.task.id}</b> · {data.task.status}
        {data.task.approvedAt ? ' · ✅ tasdiqlangan (main ga merge)' : ' · ⏳ tasdiqlanmagan (review branch)'}
      </div>

      {/* NIMA TUZATILDI */}
      {(data.subtasks.length > 0 || data.ownReport) && (
        <div>
          <div className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Nima qilindi</div>
          {data.subtasks.length ? data.subtasks.map(st => (
            <details key={st.id} className="mb-1">
              <summary className="text-[11px] text-gray-700 dark:text-gray-300 cursor-pointer">
                #{st.id} {st.moduleName ? `· ${st.moduleName}` : ''} · {st.status}
              </summary>
              <pre className="mt-1 text-[10px] leading-relaxed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                {st.report || '(hisobot yo\'q)'}
              </pre>
            </details>
          )) : (
            <pre className="text-[10px] leading-relaxed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {data.ownReport}
            </pre>
          )}
        </div>
      )}

      {/* COMMIT + QAYERDA */}
      {data.modules.map(m => (
        <div key={m.moduleId} className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2">
          <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">📦 {m.name} <span className="font-normal text-gray-400">({m.type})</span></div>
          <div className="mt-1 text-[10px] font-mono text-gray-600 dark:text-gray-400">
            {m.commits.length ? m.commits.map(c => (
              <div key={c.sha}><span className="text-primary-800 dark:text-primary-300">{c.sha}</span> {c.subject}</div>
            )) : <span className="text-gray-400 font-sans">commit topilmadi (kod o'zgarmagan bo'lishi mumkin)</span>}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {badge(m.test.checked ? m.test.found : null, '🧪 Test',
              m.test.head ? `Test env HEAD: ${m.test.head.sha} ${m.test.head.subject}` : '')}
            {m.production.storeBuild
              ? badge(null, '🚀 Do\'kon build\'i', 'Mobil ilova — App Store / Play build\'ida borligini avtomatik aniqlab bo\'lmaydi')
              : !m.production.configured
                ? badge(null, '🚀 Prod sozlanmagan', '')
                : !m.production.reachable
                  ? badge(null, '🚀 Prod javob bermadi', m.production.host || '')
                  : badge(m.production.found, '🚀 Production',
                    m.production.head ? `Prod HEAD: ${m.production.head.sha} ${m.production.head.subject}` : '')}
            {m.test.apkCommit && <span className="text-[10px] text-gray-400 font-mono">APK: {m.test.apkCommit}</span>}
          </div>
          {m.production.configured && m.production.reachable && !m.production.found && (
            <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">
              ⚠️ Bu tuzatish PRODUCTION'da yo'q — modul sahifasidagi 🚀 Deploy tugmasi bilan chiqaring.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// 🩺 SERVISLAR SALOMATLIGI — sayt/API/SSL/domen/FCM/disk… davriy tekshiriladi,
// buzilsa Telegram + push orqali xabar keladi. Admin-only.
// (authFetch ref orqali — refetch-storm gotchasi; 20s poll + khPageVisible.)
// ════════════════════════════════════════════════════════════════════════════
// 📚 TAJRIBA BAZASI — muammo→yechim retseptlari + reference kod. Engine vazifa matnini
// bu bazaga mos keltirib (token sarfsiz) agentga beradi. Bu panel: ro'yxat, qo'shish/tahrir,
// o'chirish, va «🔍 Sinov» — istalgan vazifa matnini kiritib QAYSI retsept mos kelishini
// oldindan ko'rish. authFetch REF orqali (refetch-storm tuzog'i), polling YO'Q.
function KnowledgePanel({ authFetch, apiUrl }) {
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState('');
  const [open, setOpen] = React.useState({});
  const [form, setForm] = React.useState(null);   // {id?, title, problem, solution, keywords, module_types, ref_module_id, ref_paths}
  const [modules, setModules] = React.useState([]);
  const [testText, setTestText] = React.useState('');
  const [testRes, setTestRes] = React.useState(null);
  const [toastMsg, setToastMsg] = React.useState('');
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;
  const say = (m) => { setToastMsg(m); setTimeout(() => setToastMsg(''), 3500); };

  const load = React.useCallback(async () => {
    setBusy('load');
    try {
      const r = await authFetchRef.current(`${apiUrl}/knowledge?all=1`);
      const d = await r.json().catch(() => []);
      if (Array.isArray(d)) setItems(d);
    } catch (e) {}
    // Reference modul tanlagichi uchun loyiha+modullar (admin: /projects, user: /auth/my-projects;
    // modules maydoni bo'lmasa har loyiha uchun /projects/:id/modules). Fail-open.
    try {
      const mods = [];
      let projs = [];
      for (const u of ['/projects', '/auth/my-projects']) {
        try { const r2 = await authFetchRef.current(`${apiUrl}${u}`); const p = await r2.json().catch(() => null); if (Array.isArray(p) && p.length) { projs = p; break; } } catch (e) {}
      }
      for (const pr of projs) {
        let ms = pr.modules;
        if (!Array.isArray(ms)) { try { const r3 = await authFetchRef.current(`${apiUrl}/projects/${pr.id}/modules`); ms = await r3.json().catch(() => []); } catch (e) { ms = []; } }
        for (const m of (Array.isArray(ms) ? ms : [])) mods.push({ id: m.id, label: `${pr.name} · ${m.name} (${m.module_type})` });
      }
      setModules(mods);
    } catch (e) {}
    setBusy('');
  }, [apiUrl]);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title || !form.problem || !form.solution) { say('⚠️ Sarlavha, muammo va yechim shart'); return; }
    setBusy('save');
    try {
      const body = { ...form, ref_module_id: form.ref_module_id ? Number(form.ref_module_id) : null };
      const r = await authFetchRef.current(`${apiUrl}/knowledge${form.id ? '/' + form.id : ''}`, { method: form.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'xato');
      say(form.id ? '✅ Yangilandi' : '✅ Retsept qo\'shildi');
      setForm(null); load();
    } catch (e) { say('❌ ' + e.message); }
    setBusy('');
  };
  const del = async (it) => {
    if (!window.confirm(`«${it.title}» retsepti o'chirilsinmi?`)) return;
    try { await authFetchRef.current(`${apiUrl}/knowledge/${it.id}`, { method: 'DELETE' }); say('🗑 O\'chirildi'); load(); } catch (e) { say('❌ ' + e.message); }
  };
  const runTest = async () => {
    if (testText.trim().length < 5) return;
    setBusy('test');
    try {
      const r = await authFetchRef.current(`${apiUrl}/knowledge/match`, { method: 'POST', body: JSON.stringify({ instruction: testText }) });
      const d = await r.json().catch(() => []);
      setTestRes(Array.isArray(d) ? d : []);
    } catch (e) { setTestRes([]); }
    setBusy('');
  };
  const blank = { title: '', problem: '', solution: '', keywords: '', module_types: 'mobile', ref_module_id: '', ref_paths: '' };
  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border rounded-lg px-3 py-2 text-sm';

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">📚 Tajriba bazasi</h1>
        <span className="text-sm text-gray-400">{items.length} ta retsept</span>
        <button onClick={load} className="ml-auto px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">↻</button>
        <button onClick={() => setForm({ ...blank })} className="px-3 py-1.5 text-sm font-semibold bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700">➕ Retsept qo'shish</button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Bir marta hal qilingan qiyin muammo — muammo ta'rifi, qadam-baqadam yechim va boshqa loyihada ISHLAB TURGAN reference kod. Vazifa berilganda engine mos retseptni o'zi topib (kalit so'zlar bo'yicha, token sarfsiz) agentga beradi va reference fayllarni ko'rsatadi. Tugagan vazifadan qo'shish: ⏳ Jarayonlar → kartada «📚 Tajribaga qo'sh».</p>
      {toastMsg && <div className="mb-3 px-4 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200 text-sm">{toastMsg}</div>}

      {/* 🔍 SINOV — matn → qaysi retsept mos kelishi */}
      <div className="mb-5 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 px-4 py-3">
        <div className="font-semibold text-sky-900 dark:text-sky-200 text-sm mb-1">🔍 Sinov: vazifa matnini kiriting — engine qaysi retseptni topishini ko'ring</div>
        <div className="flex gap-2">
          <input value={testText} onChange={e => setTestText(e.target.value)} onKeyDown={e => e.key === 'Enter' && runTest()} placeholder="masalan: Tashqi kartani ochsam yo'lovchi haydovchiga lokatsiyasini uzatmay qo'ydi" className={inp} />
          <button onClick={runTest} disabled={busy === 'test'} className="px-3 py-2 text-sm font-semibold bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 whitespace-nowrap">{busy === 'test' ? '⏳' : 'Tekshir'}</button>
        </div>
        {testRes !== null && (
          <div className="mt-2 text-sm">
            {testRes.length === 0 ? <span className="text-gray-500 dark:text-gray-400">— mos retsept yo'q (engine oddiy ishlaydi)</span>
              : testRes.map(r => <div key={r.id} className="text-sky-900 dark:text-sky-200">✅ <b>#{r.id} {r.title}</b> <span className="text-xs text-gray-500 dark:text-gray-400">ball {r.score}{r.hits && r.hits.length ? ' · ' + r.hits.join(', ') : ''}</span></div>)}
          </div>
        )}
      </div>

      {/* FORMA */}
      {form && (
        <div className="mb-5 rounded-lg border-2 border-primary-200 dark:border-primary-800 bg-white dark:bg-gray-800 p-4 space-y-2">
          <div className="font-bold text-gray-900 dark:text-white">{form.id ? `✏️ Retsept #${form.id}` : '➕ Yangi retsept'}</div>
          <input value={form.title} onChange={e => fld('title', e.target.value)} placeholder="Sarlavha — muammo sinfi (masalan: Fon lokatsiya ulashish — native foreground service)" className={inp} />
          <textarea value={form.problem} onChange={e => fld('problem', e.target.value)} rows={3} placeholder="MUAMMO: simptom + ildiz sabab (boshqa loyihada ham tanib olinadigan ta'rif)" className={inp} />
          <textarea value={form.solution} onChange={e => fld('solution', e.target.value)} rows={8} placeholder="YECHIM: qadam-baqadam (1., 2., …), tuzoqlar bilan. Agentga aynan shu beriladi." className={`${inp} font-mono text-xs`} />
          <input value={form.keywords} onChange={e => fld('keywords', e.target.value)} placeholder="Kalit so'zlar (vergul bilan) — vazifa matnida uchraydigan o'zbek/ingliz variantlar: fon lokatsiya, background location, tashqi karta…" className={inp} />
          <div className="flex gap-2 flex-wrap">
            <select value={form.module_types || ''} onChange={e => fld('module_types', e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Barcha modul turlari</option><option value="mobile">mobile</option><option value="web">web</option><option value="backend">backend</option><option value="mobile,backend">mobile,backend</option><option value="web,backend">web,backend</option>
            </select>
            {modules.length ? (
              <select value={form.ref_module_id || ''} onChange={e => fld('ref_module_id', e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[16rem]">
                <option value="">Reference kod moduli (ixtiyoriy)</option>
                {modules.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            ) : (
              <input type="number" value={form.ref_module_id || ''} onChange={e => fld('ref_module_id', e.target.value)} placeholder="Reference modul ID (ixtiyoriy)" className="border rounded-lg px-3 py-2 text-sm w-56" />
            )}
          </div>
          <input value={form.ref_paths} onChange={e => fld('ref_paths', e.target.value)} placeholder="Reference fayl yo'llari (vergul bilan, modul repo'siga nisbatan): src/lib/x.ts, android/app/…/Service.kt" className={`${inp} font-mono text-xs`} />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy === 'save'} className="px-4 py-2 text-sm font-semibold bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50">{busy === 'save' ? '⏳' : '💾 Saqlash'}</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
          </div>
        </div>
      )}

      {/* RO'YXAT */}
      {busy === 'load' && !items.length ? <div className="text-gray-400 py-8 text-center">⏳ Yuklanmoqda…</div>
        : !items.length ? <div className="text-gray-400 py-8 text-center border border-dashed rounded-lg">Hali retsept yo'q — «➕ Retsept qo'shish» yoki vazifa kartasida «📚 Tajribaga qo'sh».</div>
        : (
          <div className="space-y-3">
            {items.map(it => (
              <div key={it.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <button onClick={() => setOpen(o => ({ ...o, [it.id]: !o[it.id] }))} className="text-left flex-1 min-w-[16rem]">
                    <div className="font-semibold text-gray-900 dark:text-white">{open[it.id] ? '▾' : '▸'} #{it.id} {it.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {it.module_types ? `🧩 ${it.module_types} · ` : ''}{it.uses ? `📈 ${it.uses} marta ishlatilgan · ` : 'hali ishlatilmagan · '}
                      {it.ref_module_id ? `📎 reference: modul ${it.ref_module_id} (${String(it.ref_paths || '').split(',').filter(Boolean).length} fayl) · ` : ''}
                      {it.source === 'claude' ? '🤖 Claude tajribasi' : it.source && it.source.startsWith('task:') ? `🛠 ${it.source}` : '✍️ qo\'lda'}
                      {it.verified ? '' : ' · ⏳ tasdiqlanmagan'}
                    </div>
                  </button>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => setForm({ id: it.id, title: it.title, problem: it.problem, solution: it.solution, keywords: it.keywords || '', module_types: it.module_types || '', ref_module_id: it.ref_module_id || '', ref_paths: it.ref_paths || '' })} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">✏️</button>
                    <button onClick={() => del(it)} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 hover:text-red-700 dark:hover:text-red-300">🗑</button>
                  </div>
                </div>
                {it.keywords && <div className="mt-2 flex flex-wrap gap-1">{String(it.keywords).split(/[,\n;]+/).map(k => k.trim()).filter(Boolean).slice(0, 14).map((k, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[11px]">{k}</span>)}</div>}
                {open[it.id] && (
                  <div className="mt-3 space-y-2 text-sm">
                    <div><div className="font-semibold text-gray-700 dark:text-gray-300">MUAMMO</div><div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{it.problem}</div></div>
                    <div><div className="font-semibold text-gray-700 dark:text-gray-300">YECHIM</div><pre className="text-gray-900 dark:text-white whitespace-pre-wrap font-mono text-xs bg-gray-50 dark:bg-gray-700 border rounded-lg p-3">{it.solution}</pre></div>
                    {it.ref_paths && <div><div className="font-semibold text-gray-700 dark:text-gray-300">REFERENCE FAYLLAR (modul {it.ref_module_id})</div><div className="font-mono text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{String(it.ref_paths).split(/[,\n;]+/).map(x => x.trim()).filter(Boolean).join('\n')}</div></div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function HealthPanel({ authFetch, apiUrl }) {
  const [data, setData] = React.useState(null);
  const [kinds, setKinds] = React.useState([]);
  const [busy, setBusy] = React.useState('');
  const [toast, setToast] = React.useState('');
  const [form, setForm] = React.useState(null);      // {id?, name, kind, intervalMin, notify, cfg:{}}
  const [tg, setTg] = React.useState({ token: '', chatId: '' });
  const [tgOpen, setTgOpen] = React.useState(false);
  const [events, setEvents] = React.useState({});    // {id: [events]}
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  const load = React.useCallback(async (spin = true) => {
    if (spin) setBusy('load');
    try {
      const r = await authFetchRef.current(`${apiUrl}/health-checks`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && Array.isArray(d.checks)) setData(d);
    } catch (e) { /* transient — eski ro'yxat qoladi */ }
    finally { if (spin) setBusy(''); }
  }, [apiUrl]);

  React.useEffect(() => {
    load();
    authFetchRef.current(`${apiUrl}/health-checks/kinds`)
      .then(r => r.json()).then(d => setKinds(d.kinds || [])).catch(() => {});
    const t = setInterval(() => { if (khPageVisible()) load(false); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const S = {
    ok:      { icon: '✅', cls: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800', label: 'Ishlayapti' },
    warn:    { icon: '⚠️', cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800', label: "E'tibor" },
    fail:    { icon: '❌', cls: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800', label: 'Ishlamayapti' },
    unknown: { icon: '❔', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700', label: 'Tekshirilmagan' },
  };
  const st = (s) => S[s] || S.unknown;
  const ago = (t) => {
    if (!t) return 'hech qachon';
    const m = Math.round((Date.now() - new Date(t).getTime()) / 60000);
    if (m < 1) return 'hozir';
    if (m < 60) return `${m} daq oldin`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h} soat oldin` : `${Math.floor(h / 24)} kun oldin`;
  };

  const runOne = async (id) => {
    setBusy('run' + id);
    try {
      const r = await authFetchRef.current(`${apiUrl}/health-checks/${id}/run`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'xato');
      setToast(`${st(d.result?.status).icon} ${d.check?.name}: ${d.result?.detail || ''}`);
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const runAll = async () => {
    setBusy('runall'); setToast('⏳ Hammasi tekshirilmoqda...');
    try {
      await authFetchRef.current(`${apiUrl}/health-checks/run-all`, { method: 'POST' });
      setTimeout(() => { load(false); setToast('✅ Tekshiruv yakunlandi'); setBusy(''); }, 9000);
    } catch (e) { setToast('❌ ' + (e.message || e)); setBusy(''); }
  };

  const toggle = async (c, field) => {
    try {
      await authFetchRef.current(`${apiUrl}/health-checks/${c.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: !c[field] }),
      });
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" tekshiruvi o'chirilsinmi?`)) return;
    try {
      await authFetchRef.current(`${apiUrl}/health-checks/${c.id}`, { method: 'DELETE' });
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
  };

  const openNew = () => setForm({ name: '', kind: 'http', intervalMin: 5, notify: true, cfg: {} });
  const openEdit = (c) => setForm({ id: c.id, name: c.name, kind: c.kind, intervalMin: c.intervalMin, notify: c.notify, cfg: { ...(c.config || {}) } });

  const save = async () => {
    if (!form.name.trim()) { setToast('❌ Nom kerak'); return; }
    setBusy('save');
    try {
      const body = JSON.stringify({ name: form.name, kind: form.kind, config: form.cfg, intervalMin: form.intervalMin, notify: form.notify });
      const r = await authFetchRef.current(`${apiUrl}/health-checks${form.id ? '/' + form.id : ''}`,
        { method: form.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'saqlanmadi');
      setForm(null); setToast('✅ Saqlandi'); setTimeout(() => load(false), 1500);
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const showEvents = async (id) => {
    if (events[id]) { setEvents(s => ({ ...s, [id]: null })); return; }
    try {
      const r = await authFetchRef.current(`${apiUrl}/health-checks/${id}/events`);
      const d = await r.json().catch(() => ({}));
      setEvents(s => ({ ...s, [id]: d.events || [] }));
    } catch (e) { /* jim */ }
  };

  const saveTg = async () => {
    setBusy('tg');
    try {
      const body = {};
      if (tg.token.trim()) body.token = tg.token.trim();
      if (tg.chatId.trim()) body.chatId = tg.chatId.trim();
      const r = await authFetchRef.current(`${apiUrl}/health-checks/telegram`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'saqlanmadi');
      setToast('✅ Telegram saqlandi'); setTg({ token: '', chatId: '' }); load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const detectChat = async () => {
    setBusy('tgdetect');
    try {
      const r = await authFetchRef.current(`${apiUrl}/health-checks/telegram/detect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tg.token.trim() ? { token: tg.token.trim() } : {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'topilmadi');
      setToast(`✅ Chat topildi: ${d.title || d.chatId}`); load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const testTg = async () => {
    setBusy('tgtest');
    try {
      const r = await authFetchRef.current(`${apiUrl}/health-checks/telegram/test`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'yuborilmadi');
      setToast('✅ Telegram\'ga sinov xabari yuborildi');
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const kindDef = kinds.find(k => k.key === (form && form.kind));
  const FIELD_LABEL = {
    url: 'URL', host: 'Host', port: 'Port', domain: 'Domen', path: 'JSON yo\'li / papka',
    expectStatus: 'Kutilgan HTTP kod', expectText: 'Bo\'lishi shart matn', notText: 'Bo\'lmasligi kerak matn',
    okBelow500: 'Faqat 5xx = nosozlik (API root uchun)', equals: 'Teng bo\'lsin', min: 'Eng kam', max: 'Eng ko\'p',
    headers: 'Headerlar (JSON)', warnDays: 'Ogohlantirish (kun)', failDays: 'Xavf (kun)',
    warnPct: 'Ogohlantirish (%)', failPct: 'Xavf (%)', serviceAccountPath: 'Service-account JSON yo\'li',
  };
  const sum = data?.summary || {};

  // Mijoz (admin EMAS) — backend faqat o'z loyihalari tekshiruvlarini qaytaradi
  // va `readOnly: true` deydi. Shu bayroq bo'yicha o'zgartirish boshqaruvlari
  // yashiriladi (backend baribir 403 beradi — bu UI tomoni).
  const readOnly = !!(data && data.readOnly);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">🩺 Servislar salomatligi</h1>
        {!readOnly && (
        <div className="flex items-center gap-2">
          <button onClick={() => setTgOpen(o => !o)}
            className={`text-sm px-3 py-1.5 rounded-lg border font-medium ${data?.telegram?.configured ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700'}`}>
            {data?.telegram?.configured ? '📨 Telegram ulangan' : '📨 Telegram sozlanmagan'}
          </button>
          <button onClick={runAll} disabled={busy === 'runall'}
            className="text-sm px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">
            {busy === 'runall' ? '⏳ Tekshirilmoqda...' : '▶▶ Hammasini tekshirish'}
          </button>
          <button onClick={openNew}
            className="text-sm px-3 py-1.5 rounded-lg bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 font-medium">
            ➕ Tekshiruv qo'shish
          </button>
        </div>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Har servis o'z davrida avtomatik tekshiriladi. Buzilsa yoki muddati tugay boshlasa —
        <b> Telegram</b> va <b>push</b> orqali xabar keladi (o'tkinchi uzilish uchun emas: ketma-ket 2 marta yiqilsa).
      </p>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {[['ok', sum.ok], ['warn', sum.warn], ['fail', sum.fail], ['unknown', sum.unknown]].map(([k, n]) => (
          <span key={k} className={`px-3 py-1 rounded-full text-sm font-semibold border ${st(k).cls}`}>
            {st(k).icon} {st(k).label}: {n || 0}
          </span>
        ))}
      </div>

      {toast && <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 break-words">{toast}</div>}

      {tgOpen && !readOnly && (
        <div className="mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">📨 Telegram xabarnoma</div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            1) Telegram'da <b>@BotFather</b> → <code>/newbot</code> → tokenni shu yerga qo'ying va saqlang.
            2) O'z botingizga (yoki guruhga qo'shib) bitta xabar yozing → <b>Chatni aniqlash</b> tugmasi ID ni o'zi topadi.
            3) <b>Sinov</b> bilan tekshiring. {data?.telegram?.chatId && <>Joriy chat: <code>{data.telegram.chatId}</code></>}
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-[11px] text-gray-500 dark:text-gray-400">Bot token
              <input type="password" value={tg.token} placeholder={data?.telegram?.configured ? '••••••  (o\'zgartirish uchun yozing)' : '123456:ABC-DEF...'}
                onChange={e => setTg(s => ({ ...s, token: e.target.value }))}
                className="block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-72" />
            </label>
            <label className="text-[11px] text-gray-500 dark:text-gray-400">Chat ID
              <input value={tg.chatId} placeholder={data?.telegram?.chatId || '-100... yoki 12345'}
                onChange={e => setTg(s => ({ ...s, chatId: e.target.value }))}
                className="block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-44" />
            </label>
            <button onClick={saveTg} disabled={busy === 'tg'} className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50">💾 Saqlash</button>
            <button onClick={detectChat} disabled={busy === 'tgdetect'} className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">🔎 Chatni aniqlash</button>
            <button onClick={testTg} disabled={busy === 'tgtest'} className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">📨 Sinov xabari</button>
          </div>
        </div>
      )}

      {form && !readOnly && (
        <div className="mb-4 bg-white dark:bg-gray-800 border-2 border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="font-semibold text-gray-700 dark:text-gray-300 mb-3">{form.id ? '✏️ Tekshiruvni tahrirlash' : '➕ Yangi tekshiruv'}</div>
          <div className="flex items-end gap-2 flex-wrap mb-3">
            <label className="text-[11px] text-gray-500 dark:text-gray-400">Nom
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Masalan: Google Maps kaliti"
                className="block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-64" />
            </label>
            <label className="text-[11px] text-gray-500 dark:text-gray-400">Turi
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value, cfg: {} }))}
                className="block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800">
                {kinds.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>
            <label className="text-[11px] text-gray-500 dark:text-gray-400">Har necha daqiqada
              <input type="number" min="1" value={form.intervalMin}
                onChange={e => setForm(f => ({ ...f, intervalMin: Number(e.target.value) }))}
                className="block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-28" />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 pb-1.5">
              <input type="checkbox" checked={!!form.notify} onChange={e => setForm(f => ({ ...f, notify: e.target.checked }))} />
              Buzilsa xabar berilsin
            </label>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            {(kindDef?.fields || []).map(fl => (
              <label key={fl} className="text-[11px] text-gray-500 dark:text-gray-400">{FIELD_LABEL[fl] || fl}
                {fl === 'okBelow500' ? (
                  <input type="checkbox" checked={!!form.cfg[fl]}
                    onChange={e => setForm(f => ({ ...f, cfg: { ...f.cfg, [fl]: e.target.checked } }))}
                    className="block mt-2" />
                ) : (
                  <input
                    value={fl === 'headers' ? (typeof form.cfg[fl] === 'object' ? JSON.stringify(form.cfg[fl]) : (form.cfg[fl] || '')) : (form.cfg[fl] ?? '')}
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f => ({ ...f, cfg: { ...f.cfg, [fl]: fl === 'headers' ? (() => { try { return JSON.parse(v); } catch { return v; } })() : v } }));
                    }}
                    placeholder={fl === 'url' ? 'https://...' : fl === 'headers' ? '{"Authorization":"Bearer ..."}' : ''}
                    className={`block mt-0.5 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm ${['url', 'headers'].includes(fl) ? 'w-96' : 'w-40'}`} />
                )}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={save} disabled={busy === 'save'} className="px-4 py-2 text-sm bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 font-medium">💾 Saqlash</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
          </div>
        </div>
      )}

      {!data && <div className="text-sm text-gray-400 py-10 text-center">Yuklanmoqda...</div>}

      <div className="space-y-2">
        {(data?.checks || []).map(c => (
          <div key={c.id} className={`border rounded-lg p-3 bg-white dark:bg-gray-800 ${c.status === 'fail' ? 'border-red-200 dark:border-red-800' : c.status === 'warn' ? 'border-amber-200 dark:border-amber-800' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${st(c.status).cls}`}>{st(c.status).icon} {st(c.status).label}</span>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">{c.name}</span>
                  {!c.enabled && <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">o'chirilgan</span>}
                  {!c.notify && <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">🔕 xabarsiz</span>}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">{c.detail || '—'}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {c.kind} · har {c.intervalMin} daq · tekshirildi: {ago(c.lastRunAt)}
                  {c.latencyMs != null && ` · ${c.latencyMs} ms`}
                  {c.status === 'fail' && c.failCount > 1 && ` · ketma-ket ${c.failCount} marta`}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => runOne(c.id)} disabled={busy === 'run' + c.id}
                  className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">
                  {busy === 'run' + c.id ? '⏳' : '▶ Tekshirish'}
                </button>
                <button onClick={() => showEvents(c.id)} className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600">🕑 Tarix</button>
                {!readOnly && (<>
                <button onClick={() => openEdit(c)} className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600">✏️</button>
                <button onClick={() => toggle(c, 'enabled')} title={c.enabled ? "O'chirish" : 'Yoqish'}
                  className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600">{c.enabled ? '⏸' : '▶'}</button>
                <button onClick={() => remove(c)} className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-300">🗑</button>
                </>)}
              </div>
            </div>
            {events[c.id] && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
                {events[c.id].length === 0 && <div className="text-[11px] text-gray-400">Holat hali o'zgarmagan</div>}
                {events[c.id].map((e, i) => (
                  <div key={i} className="text-[11px] text-gray-500 dark:text-gray-400">
                    {st(e.status).icon} <span className="text-gray-400">{new Date(e.created_at).toLocaleString('uz-UZ', { hour12: false })}</span> — {e.detail}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {data && data.checks.length === 0 && (
          <div className="text-center py-14 text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            Hali tekshiruv yo'q — «➕ Tekshiruv qo'shish» bilan boshlang.
          </div>
        )}
      </div>
    </div>
  );
}

// 💥 "ALLAQACHON TUZATILGANMI?" belgisi. Backend har crash qatoriga `fix`
// obyektini qo'shadi (services/crashFix.js): tuzatilgan crash ESKI ilovalardan
// kelaverishi NORMAL (foydalanuvchi yangilamagan) — operator uni yangi muammo
// deb o'ylamasligi kerak. Aksincha, tuzatishdan KEYIN qurilgan ilovadan kelsa —
// bu REGRESSIYA va u baland ovozda ko'rsatiladi.
const CRASH_FIX_CHIP = {
  fixed_old:     { cls: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', icon: '✅' },
  fixed_unknown: { cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400',     icon: '✅' },
  regressed:     { cls: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',         icon: '🔴' },
};
function crashFixText(fix, fmt) {
  if (!fix || !fix.state) return null;
  const v = fix.version ? fix.version + ' da tuzatilgan' : 'tuzatilgan';
  if (fix.state === 'regressed') return {
    chip: 'Tuzatishdan keyin QAYTDI',
    note: '🔴 Regressiya: bu crash allaqachon bir marta tuzatilgan edi'
        + (fix.version ? ' (' + fix.version + ')' : '')
        + ', lekin tuzatishdan KEYIN qurilgan ilovada qayta yuz berdi'
        + (fix.regressedAt ? ' · ' + fmt(fix.regressedAt) : '') + '.',
  };
  if (fix.state === 'fixed_old') return {
    chip: v,
    note: 'Bu hisobot ESKI ilovadan keldi'
        + (fix.buildTime ? ' (build ' + fmt(fix.buildTime) + ')' : '')
        + ' — yangi muammo emas: foydalanuvchi ilovani yangilasa yo\'qoladi.',
  };
  return {
    chip: v,
    note: 'Ilova versiyasi noma\'lum (eski build build-vaqtini yubormaydi) — '
        + 'eski ilovadan bo\'lishi mumkin. Keyingi build\'dan boshlab aniq ko\'rinadi.',
  };
}

function CrashesPanel({ authFetch, apiUrl, projectId, onActiveCount, readOnly = false }) {
  const [rows, setRows] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [openId, setOpenId] = React.useState(null);
  const [fixId, setFixId] = React.useState(null);      // qaysi crash uchun 🔎 tuzatish ochiq
  const [detail, setDetail] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [toast, setToast] = React.useState('');
  // Zanjir SOG'MI? — panel jim bo'lishi "ilova barqaror" degani ham, "hisobot
  // umuman kelmayapti" degani ham bo'lishi mumkin edi. Endi farqlanadi.
  const [health, setHealth] = React.useState(null);
  const pollRef = React.useRef(null);
  // authFetch har render'da YANGI identity — effekt dep'iga qo'yilsa refetch-storm
  // bo'ladi (hujjatlangan gotcha), shuning uchun ref orqali o'qiymiz.
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  const load = async (spin = true) => {
    if (spin) setLoading(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/crash-reports?projectId=${projectId}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && Array.isArray(d.crashes)) {
        setRows(d.crashes);
        setHealth({ lastReportAt: d.lastReportAt || null, totalEver: Number(d.totalEver || 0), wiring: d.mobileWiring || null });
        // Nav badge'i (💥 Crashlar · N) darhol yangilansin — o'chirish/vazifa yaratishdan keyin
        // App'ning 30s poll'ini kutmay. Server bermasa qatorlardan hisoblaymiz (bir xil shart).
        if (typeof onActiveCount === 'function') {
          const n = d.activeCount != null ? Number(d.activeCount)
            : d.crashes.filter(c => !c.fix || c.fix.state === 'regressed').length;
          onActiveCount(n);
        }
      }
    } catch { /* transient — eski ro'yxat qoladi */ } finally { if (spin) setLoading(false); }
  };

  React.useEffect(() => {
    setRows(null); setOpenId(null); setFixId(null); setDetail(null); setToast('');
    if (projectId) load();
    pollRef.current = setInterval(() => { if (khPageVisible() && projectId) load(false); }, 20000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // uz-UZ locale "M07 27" beradi → qo'lda DD.MM HH:MM
  const fmt = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (isNaN(d)) return '—';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const toggleStack = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id); setDetail(null);
    try {
      const r = await authFetchRef.current(`${apiUrl}/crash-reports/${id}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && d.crash) setDetail(d.crash);
    } catch { setDetail(null); }
  };

  const makeTask = async (id) => {
    setBusy('task:' + id); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/crash-reports/${id}/fix-task`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'yaratilmadi');
      setToast(d.already ? `Vazifa allaqachon bor: #${d.taskId}` : `🛠 [CRASH-FIX] vazifa #${d.taskId} yaratildi — ⏳ Jarayonlarda ko'rinadi`);
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); } finally { setBusy(''); }
  };

  const removeCrash = async (id) => {
    if (!window.confirm("Bu crash yozuvi ro'yxatdan o'chirilsinmi? (vazifaga tegmaydi)")) return;
    setBusy('del:' + id);
    try {
      await authFetchRef.current(`${apiUrl}/crash-reports/${id}`, { method: 'DELETE' });
      setRows((prev) => (prev || []).filter((c) => c.id !== id));
    } catch { /* keyingi poll to'g'rilaydi */ } finally { setBusy(''); }
  };

  const TASK_LABEL = {
    pending_architecture: 'rejalashtirilmoqda', pending: 'navbatda', in_progress: 'bajarilmoqda',
    completed: 'bajarildi', failed: 'xato', cancelled: 'bekor qilindi',
    awaiting_clarification: 'savol berdi', awaiting_plan_approval: 'reja tasdiq kutmoqda',
    awaiting_owner_approval: 'ruxsat kutmoqda',
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          💥 Crashlar
          {rows && rows.length > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400">{rows.length}</span>
          )}
        </h3>
        <button onClick={() => load()} disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          {loading ? '⏳' : '↻'} Yangilash
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Ilovaning o'zi yuborgan real xatolar. Bir xil xato takrorlansa yangi qator emas — <b>×N</b> oshadi.
        Fatal xato darhol <b>[CRASH-FIX]</b> vazifa yaratadi; boshqalari 3 martadan keyin (yoki quyidagi tugma bilan qo'lda).
      </p>

      {toast && <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{toast}</div>}

      {rows === null && <div className="text-sm text-gray-400 py-10 text-center">Yuklanmoqda...</div>}

      {/* ⚠️ ZANJIR TEKSHIRUVI — "jim" ikki xil ma'noni bildirmasin. Loyihadan
          HECH QACHON hisobot kelmagan bo'lsa, bu odatda ilova tomonidagi ulanish
          (yoki telefondagi eski build) muammosi; shuni ochiq aytamiz. */}
      {health && health.totalEver === 0 && (
        <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
          ⚠️ Bu loyihadan <b>hech qachon</b> crash hisoboti kelmagan.
          {Array.isArray(health.wiring) && health.wiring.length > 0 && (
            <> Mobil modul(lar):{' '}
              {health.wiring.map(w => (
                <span key={w.moduleId} className="font-medium">
                  {w.name}: {w.wired === true ? '✅ ulangan' : w.wired === false ? '❌ ulanmagan' : '❓ tekshirilmadi'}{' '}
                </span>
              ))}
            </>
          )}
          <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
            Ulangan bo'lsa ham hisobot kelmasa — telefondagi ilova ESKI build bo'lishi mumkin.
            Yangi APK'ni 📦 modul sahifasidan yuklab o'rnating.
          </div>
        </div>
      )}
      {health && health.lastReportAt && (
        <div className="mb-3 text-[11px] text-gray-400">📡 Oxirgi hisobot: {fmt(health.lastReportAt)}</div>
      )}

      {rows && rows.length === 0 && (
        <div className="text-center py-14 text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="text-3xl mb-2">🎉</div>
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Hali crash yo'q</div>
          <div className="text-xs mt-1">Ilovada xato yuz bersa shu yerda ko'rinadi va avtomatik tuzatish vazifasi yaratiladi.</div>
        </div>
      )}

      <div className="space-y-2">
        {(rows || []).map((c) => (
          <div key={c.id} className={'border rounded-lg p-3 bg-white dark:bg-gray-800 ' + (
            c.fix && c.fix.state === 'regressed' ? 'border-red-300 dark:border-red-700'
            : c.fix ? 'border-emerald-200 dark:border-emerald-800 opacity-75' : 'border-gray-200 dark:border-gray-700')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${c.fatal ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'}`}>
                    {c.fatal ? '💀 FATAL' : '⚠️ non-fatal'}
                  </span>
                  {c.occurrences > 1 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      title={c.fix && c.fix.postFixCount ? c.fix.postFixCount + ' tasi tuzatilgandan KEYIN keldi' : ''}>
                      ×{c.occurrences}{c.fix && c.fix.postFixCount ? ' (' + c.fix.postFixCount + ' tuzatishdan keyin)' : ''}
                    </span>
                  )}
                  {(() => {
                    const t = crashFixText(c.fix, fmt);
                    if (!t) return null;
                    const st = CRASH_FIX_CHIP[c.fix.state] || CRASH_FIX_CHIP.fixed_unknown;
                    return (
                      <span className={'text-[10px] font-bold px-2 py-0.5 rounded ' + st.cls}>
                        {st.icon} {t.chip}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {[c.platform, c.app_version && 'v' + c.app_version, c.device, c.user_ref && '👤 ' + c.user_ref].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="text-xs font-mono text-gray-900 dark:text-white break-words">{c.message}</div>
                <div className="text-[10px] text-gray-400 mt-1">
                  birinchi: {fmt(c.first_seen)} · oxirgi: {fmt(c.last_seen)}
                </div>
                {(() => {
                  const t = crashFixText(c.fix, fmt);
                  if (!t) return null;
                  return (
                    <div className={'text-[10px] mt-1 ' + (c.fix.state === 'regressed' ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-emerald-700 dark:text-emerald-400')}>
                      {t.note}
                    </div>
                  );
                })()}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                {c.task_id ? (
                  <span className="text-[10px] px-2 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 font-semibold whitespace-nowrap">
                    🛠 #{c.task_id}{c.task_status ? ' · ' + (TASK_LABEL[c.task_status] || c.task_status) : ''}
                  </span>
                ) : readOnly ? null : (
                  <button onClick={() => makeTask(c.id)} disabled={busy === 'task:' + c.id}
                    className="text-[10px] px-2 py-1 rounded-lg bg-primary-700 dark:bg-primary-600 text-white font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap">
                    {busy === 'task:' + c.id ? '⏳' : '🛠 Vazifa yaratish'}
                  </button>
                )}
                <div className="flex gap-1">
                  <button onClick={() => toggleStack(c.id)}
                    className="text-[10px] px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                    {openId === c.id ? '▲ stack' : '▼ stack'}
                  </button>
                  {c.task_id && !readOnly && (
                    <button onClick={() => setFixId(fixId === c.id ? null : c.id)}
                      title="Nima tuzatildi, qaysi commit, test/production'da bormi"
                      className="text-[10px] px-2 py-1 rounded-lg border border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/40">
                      {fixId === c.id ? '▲ tuzatish' : '🔎 tuzatish'}
                    </button>
                  )}
                  {!readOnly && (
                  <button onClick={() => removeCrash(c.id)} disabled={busy === 'del:' + c.id}
                    title="Ro'yxatdan o'chirish" className="text-[10px] px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-300 disabled:opacity-50">
                    🗑
                  </button>
                  )}
                </div>
              </div>
            </div>
            {openId === c.id && (
              <pre className="mt-2 text-[10px] leading-relaxed bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto whitespace-pre">
                {detail && detail.id === c.id ? (detail.stack || '(stack yo\'q — hisobot stack\'siz kelgan)') : 'yuklanmoqda...'}
              </pre>
            )}
            {fixId === c.id && c.task_id && (
              <FixTrace authFetch={authFetch} apiUrl={apiUrl} taskId={c.task_id}
                url={`${apiUrl}/crash-reports/${c.id}/fix`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PublishPanel({ authFetch, apiUrl, projectId, canOpenConsole, onOpenConsole }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(''); // 'build' | 'submit:<id>' | 'prod:<id>' | ''
  const [toast, setToast] = React.useState('');
  // 📱 Loyihada bir nechta mobil ilova bo'lishi mumkin (Idora: mijoz / worker / haydovchi) — Publish qaysi biri uchun.
  // Loyiha boshiga eslab qolinadi; bo'sh = server tanlaydi (birinchisi).
  const [pubModuleId, setPubModuleId] = React.useState(() => { try { return localStorage.getItem(`kh_pub_mod_${projectId}`) || ''; } catch { return ''; } });
  React.useEffect(() => { try { setPubModuleId(localStorage.getItem(`kh_pub_mod_${projectId}`) || ''); } catch { setPubModuleId(''); } }, [projectId]);
  const pickPubModule = (v) => { setPubModuleId(v); try { localStorage.setItem(`kh_pub_mod_${projectId}`, v); } catch {} };
  const pubMod = pubModuleId ? Number(pubModuleId) : undefined;
  // 🍏 EAS'siz (Flutter) ilova uchun App Store Connect kaliti
  const [showAscKey, setShowAscKey] = React.useState(false);
  const [ascForm, setAscForm] = React.useState({ issuerId: '', keyId: '', appId: '', keyP8: '', fileName: '' });
  // 🕓 «TestFlight'ga yuborilmoqda» holati: POST tez qaytadi, lekin eas submit EAS'da
  // bir necha daqiqa ishlaydi → tugma «⏳ Yuborilmoqda…» bo'lib turadi (build ASC/TestFlight'da
  // paydo bo'lgunча yoki ~15 daqiqa o'tguncha). {buildId: startMs}. localStorage — reload'da saqlanadi.
  const [submitting, setSubmitting] = React.useState({});
  React.useEffect(() => {
    try { setSubmitting(JSON.parse(localStorage.getItem(`kh_ios_submitting_${projectId}`) || '{}') || {}); }
    catch { setSubmitting({}); }
  }, [projectId]);
  React.useEffect(() => {
    try { localStorage.setItem(`kh_ios_submitting_${projectId}`, JSON.stringify(submitting)); } catch {}
  }, [submitting, projectId]);
  // Build TestFlight'da (ASC builds) paydo bo'lsa → «Yuborilmoqda» holatini olib tashlaymiz
  // (u endi «✅ TestFlight'da» bo'ladi). ~15 daqiqadan keyin ham xavfsizlik uchun tozalaymiz
  // (submit yiqilgan bo'lsa tugma abadiy bloklanib qolmasin).
  React.useEffect(() => {
    if (!data) return;
    const tf = new Set((data?.asc?.builds || []).map(b => String(b.buildNumber)));
    const numById = {};
    (data?.iosBuilds || []).forEach(b => { numById[b.id] = String(b.buildNumber); });
    setSubmitting(s => {
      let changed = false; const next = { ...s };
      Object.keys(next).forEach(id => {
        const num = numById[id];
        const onTf = num && tf.has(num);
        const expired = Date.now() - Number(next[id] || 0) > 15 * 60 * 1000;
        if (onTf || expired) { delete next[id]; changed = true; }
      });
      return changed ? next : s;
    });
  }, [data]);
  // 🔗 iOS build qaysi API manziliga qurilsin: koddagidek / test env / production.
  // (Android 🔨 Build + Install'dagi bilan bir xil; loyiha bo'yicha eslab qolinadi.)
  const [apiTarget, setApiTarget] = React.useState(
    () => localStorage.getItem(`kh_ios_api_target_${projectId}`) || 'code');
  React.useEffect(() => {
    setApiTarget(localStorage.getItem(`kh_ios_api_target_${projectId}`) || 'code');
  }, [projectId]);
  const pickApiTarget = (v) => {
    setApiTarget(v);
    try { localStorage.setItem(`kh_ios_api_target_${projectId}`, v); } catch {}
  };
  // 📲 Build turi: production (TestFlight/App Store) | preview (ad-hoc — ro'yxatdagi iPhone'ga havola orqali).
  const [buildProfile, setBuildProfile] = React.useState(
    () => localStorage.getItem(`kh_ios_profile_${projectId}`) || 'production');
  React.useEffect(() => {
    setBuildProfile(localStorage.getItem(`kh_ios_profile_${projectId}`) || 'production');
  }, [projectId]);
  const pickBuildProfile = (v) => { setBuildProfile(v); try { localStorage.setItem(`kh_ios_profile_${projectId}`, v); } catch {} };
  // 📝 What's New (release notes) — build vaqtida yoziladi, build bilan saqlanadi, relizda
  // ASC versiyasiga qo'yiladi. localStorage'da (loyiha bo'yicha) — build formasi uchun.
  const [whatsNew, setWhatsNew] = React.useState('');
  React.useEffect(() => {
    try { setWhatsNew(localStorage.getItem(`kh_ios_whatsnew_${projectId}`) || ''); } catch { setWhatsNew(''); }
  }, [projectId]);
  const onWhatsNew = (v) => { setWhatsNew(v); try { localStorage.setItem(`kh_ios_whatsnew_${projectId}`, v); } catch {} };
  const pollRef = React.useRef(null);
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;
  // 📦 Reliz ssenariysi (bir bosishda App Store + Play) — oxirgi run holati; batafsili PlaybookModal'da.
  const [rel, setRel] = React.useState(null);
  const [relOpen, setRelOpen] = React.useState(null);
  const [relVer, setRelVer] = React.useState('');
  const [relBusy, setRelBusy] = React.useState(false);
  const loadRel = async () => {
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/release?projectId=${projectId}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d) setRel(d.run || null);
    } catch {}
  };
  const startRelease = async () => {
    const v = relVer.trim();
    if (v && !/^\d+\.\d+(\.\d+)?$/.test(v)) { setToast('Versiya formati: 1.15.0'); return; }
    if (!window.confirm(`📦 Reliz${v ? ' ' + v : ''}${data?.name ? ' — ' + data.name : ''} boshlansinmi?${!data?.configured ? "\n(Bu ilovada iOS/EAS sozlanmagan — reliz faqat Android/Google Play bilan)" : ''}\n\nKhorezmai o'zi: versiyani hisoblaydi, What's New yozadi, iOS (EAS — oylik limitdan 1 ta) va Android AAB quradi, App Store ko'rib chiqishiga yuboradi, Play production'ga 20% chiqaradi va 24 soat crash'siz bo'lsa 100% ga kengaytiradi. Rad etilsa tuzatish vazifasi berib qayta yuboradi.`)) return;
    setRelBusy(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/release`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId, moduleId: pubMod || data?.moduleId, version: v || undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setToast('❌ ' + (d.error || 'Reliz boshlanmadi')); return; }
      setRel(d); setRelOpen(d.id);
      if (d.reused) setToast('Bu loyihada reliz allaqachon ketmoqda — o\'sha ochildi.');
    } catch { setToast('❌ Ulanib bo\'lmadi'); } finally { setRelBusy(false); }
  };

  // Holat SERVERDA keshlanadi (EAS build:list + Apple ASC = sekin). Oddiy ochish/poll
  // keshdan darhol keladi; `force` — ↻ tugmasi bosilganda majburiy qayta o'qish.
  const load = async (spin = true, force = false) => {
    if (spin) setLoading(true);
    try {
      const r = await authFetchRef.current(
        `${apiUrl}/publish/status?projectId=${projectId}${pubModuleId ? '&moduleId=' + pubModuleId : ''}${force ? '&refresh=1' : ''}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d) setData(d);
    } catch {} finally { if (spin) setLoading(false); }
  };

  React.useEffect(() => {
    setData(null); setBusy(''); setToast(''); setRel(null); setRelOpen(null);
    if (projectId) { load(); loadRel(); }
    // poll har 15s — ishlab turgan build/submit holatini kuzatish uchun
    pollRef.current = setInterval(() => { if (khPageVisible() && projectId) { load(false); loadRel(); } }, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pubModuleId]);

  const kickBuild = async (skipPodCheck = false) => {
    const targetLabel = apiTarget === 'code' ? 'koddagidek' : (apiTarget === 'test' ? '🧪 TEST env' : '🚀 PRODUCTION');
    const kindLabel = buildProfile === 'preview' ? "📲 iPhone (ad-hoc) — ro'yxatdagi iPhone'ga havola orqali o'rnatiladi, TestFlight'ga yuborilmaydi" : '🏪 TestFlight / App Store';
    if (!window.confirm(skipPodCheck
      ? `⚠️ pod install tekshiruvisiz iOS build boshlansinmi?\n\nTekshiruv xato topgan — EAS'da ham yiqilishi mumkin, shunda ham oylik limitdan 1 ta ketadi.\n\n📦 Turi: ${kindLabel}\n🔗 API manzili: ${targetLabel}`
      : `iOS build boshlansinmi?\n\n📦 Turi: ${kindLabel}\n🔗 API manzili: ${targetLabel}\n(Avval serverda pod install tekshiriladi — xato chiqsa EAS'ga yuborilmaydi. EAS bulutda ~15 daqiqa; EAS oylik limitidan 1 ta)`)) return;
    setBusy('build'); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/ios/build`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // async: javob darhol — tayyorlanish (sync, versiya, pod tekshiruvi, EAS'ga yuklash) serverda fonda, holat iosBuildPrep'da.
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, apiTarget, whatsNew, profile: buildProfile, async: true, skipPodCheck: !!skipPodCheck }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'build_failed');
      if (d.async) {
        setToast("🍏 iOS build tayyorlanmoqda — avval serverda pod install tekshiriladi (bepul), o'tsa EAS'ga yuboriladi. Holati shu yerda ko'rinadi.");
        setTimeout(() => load(false), 3000);
        return;
      }
      setToast(d.profile === 'preview'
        ? `📲 Ad-hoc build navbatga qo'yildi (API: ${d.apiBaseUrl || targetLabel}) — tayyor bo'lgach ro'yxatda «📲 O'rnatish» havolasi chiqadi (~15 daqiqa).`
        : `🍏 iOS build navbatga qo'yildi (API: ${d.apiBaseUrl || targetLabel}) — pastdagi ro'yxatda holati ko'rinadi (~15 daqiqa).`);
      setTimeout(() => load(false), 8000);
    } catch (e) { setToast('❌ Build boshlanmadi: ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  // 🚀 Production serverga deploy — Studio'dan chiqmasdan (avval faqat admin → modul
  // Overview'da bor edi). Mavjud POST /project-modules/:id/prod/deploy ishlatiladi.
  // 🔎 Prod holati: har modulda PROD va TEST'da qaysi commit turibdi + prod tirikmi.
  // Maqsad: (1) allaqachon deploy qilinganini TAKROR deploy qilmaslik, (2) deploy
  // prod'ni buzmaganini ko'rish. SSH bor — SEKIN, shuning uchun 15s pollingda EMAS,
  // faqat ochilganda va ↻ bosilganda yuklanadi.
  const [pstat, setPstat] = React.useState(null);
  const [pstatLoading, setPstatLoading] = React.useState(false);
  const loadProdStatus = async () => {
    if (!projectId) return;
    setPstatLoading(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/prod-status?projectId=${projectId}`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && d.ok) setPstat(d);
    } catch {} finally { setPstatLoading(false); }
  };
  React.useEffect(() => { setPstat(null); }, [projectId]);
  const pinfo = (id) => (pstat?.modules || []).find(x => x.id === id) || null;

  // 📜 Oxirgi deploy logi — socketdagi oqim oyna yopilishi bilan yo'qoladi, shuning
  // uchun serverda saqlangan nusxasini KEYIN ham ochib ko'rish mumkin.
  const [dlog, setDlog] = React.useState(null);       // {moduleId, at, success, note, log}
  const [dlogFor, setDlogFor] = React.useState(null);
  const loadDeployLog = async (m) => {
    if (dlogFor === m.id) { setDlogFor(null); setDlog(null); return; }
    setDlogFor(m.id); setDlog(null);
    try {
      const r = await authFetchRef.current(`${apiUrl}/project-modules/${m.id}/prod/deploy-log`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && d.ok) setDlog({ ...d, moduleId: m.id });
      else setDlog({ moduleId: m.id, log: null });
    } catch { setDlog({ moduleId: m.id, log: null }); }
  };

  const prodDeploy = async (m) => {
    if (!window.confirm(`«${m.name}» PRODUCTION serverga deploy qilinsinmi?\n\n${m.production_url || ''}\nJonli foydalanuvchilarga ta'sir qiladi.`)) return;
    setBusy('prod:' + m.id); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/project-modules/${m.id}/prod/deploy`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'deploy_failed');
      setToast(`🚀 «${m.name}» deploy boshlandi — tugagach holat avtomatik yangilanadi (bir necha daqiqa).`);
      // Deploy tugashini kutib, prod holatini QAYTA o'qiymiz — yangi commit tushdimi
      // va server hamon ishlayaptimi (buzilmadimi) shu yerda ko'rinadi.
      setTimeout(() => loadProdStatus(), 45000);
      setTimeout(() => loadProdStatus(), 120000);
    } catch (e) { setToast('❌ Deploy boshlanmadi: ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  const submit = async (buildId) => {
    if (!window.confirm('Ushbu build App Store Connect / TestFlight\'ga yuborilsinmi?')) return;
    setBusy('submit:' + buildId); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/ios/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, buildId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'submit_failed');
      setSubmitting(s => ({ ...s, [buildId]: Date.now() }));  // «Yuborilmoqda…» — TestFlight'da paydo bo'lgunча
      setToast('📤 TestFlight\'ga yuborilmoqda — bir necha daqiqada TestFlight\'da paydo bo\'ladi (holati shu yerda yangilanadi).');
      setTimeout(() => load(false), 8000);
    } catch (e) { setToast('❌ Submit boshlanmadi: ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  // 🚀 App Store'ga chiqarish (yarim-avtomatik): oxirgi PRODUCTION build'ni tanlab, ASC API
  // orqali mexanik qadamlarni bajaramiz (versiya + build biriktirish + export compliance), keyin
  // nima qolganini ko'rsatib, konsolni ochamiz — What's New / skrinshot / Submit siznikida.
  const [preparing, setPreparing] = React.useState(false);
  const [prepareResult, setPrepareResult] = React.useState(null);
  const prepareRelease = async () => {
    const tf = new Set((data?.asc?.builds || []).map(b => String(b.buildNumber)));
    const finished = (data?.iosBuilds || []).filter(b => b.status === 'FINISHED');
    // Nomzod: TestFlight'da (ASC'ga yuklangan) + TEST bo'lmagan build, eng yangisi.
    const cands = finished.filter(b => tf.has(String(b.buildNumber)) && b.apiEnv !== 'test');
    if (!cands.length) {
      const prodNotTf = finished.find(b => b.apiEnv === 'production' && !tf.has(String(b.buildNumber)));
      if (prodNotTf) { setToast(`🚀 Production build (Build ${prodNotTf.buildNumber}) bor, lekin hali TestFlight'da emas — avval «📤 TestFlight'ga» yuboring.`); return; }
      const testOnly = finished.some(b => b.apiEnv === 'test');
      setToast(testOnly
        ? "App Store relizi uchun TEST build yaramaydi — 🔗 «🚀 production» bilan yangi build qilib, «TestFlight'ga» yuboring."
        : "App Store relizi uchun avval 🔗 «🚀 production» bilan build qilib, «TestFlight'ga» yuboring.");
      return;
    }
    const cand = cands[0];
    const envNote = cand.apiEnv === 'production' ? '🚀 production' : '❓ endpoint noma\'lum (production ekanini tekshiring)';
    if (!window.confirm(`App Store relizi tayyorlansinmi?\n\nBuild ${cand.buildNumber} (v${cand.version || '?'}, ${envNote}).\nVersiya yaratib/topib, build biriktirilib, export compliance qo'yiladi. So'ng konsol ochiladi — What's New + Submit'ni SIZ qilasiz.`)) return;
    setPreparing(true); setPrepareResult(null); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/ios/prepare-release`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, buildNumber: cand.buildNumber }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setToast('❌ ' + (d.error || 'Tayyorlab bo\'lmadi')); return; }
      setPrepareResult(d);
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
    finally { setPreparing(false); }
  };

  // 📝 Build ro'yxatidan «What's New» yozish/tahrirlash (build vaqtida kiritilmagan bo'lsa ham).
  const [editNote, setEditNote] = React.useState(null);   // {buildNumber} tahrir rejimida
  const [noteDraft, setNoteDraft] = React.useState('');
  const [savingNote, setSavingNote] = React.useState(false);
  const startEditNote = (b) => { setEditNote({ buildNumber: b.buildNumber }); setNoteDraft(b.whatsNew || ''); };
  const saveBuildNote = async () => {
    if (!editNote) return;
    setSavingNote(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/ios/build-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, buildNumber: editNote.buildNumber, whatsNew: noteDraft }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setToast('❌ ' + (d.error || 'Saqlab bo\'lmadi')); return; }
      setEditNote(null); setToast('📝 What\'s New saqlandi.'); load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
    finally { setSavingNote(false); }
  };

  // 🤖 Android — mavjud imzolangan-AAB quvuri (viloyatga shu yo'l bilan Play'ga chiqarilgan).
  // Tayyor APK faylini yuklab olish (loyiha bo'yicha — mobil modulni server tanlaydi).
  const downloadApk = async () => {
    try {
      const r = await authFetchRef.current(`${apiUrl}/projects/${projectId}/mobile/apk-link`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { setToast('❌ ' + (d.error || 'APK topilmadi — avval build qiling')); return; }
      setToast(`📥 Yuklab olinmoqda: ${d.filename} (${Math.round((d.sizeBytes || 0) / 1048576)} MB)`);
      window.location.href = `${apiUrl}${d.url}`;
    } catch (e) { setToast('❌ ' + (e.message || 'xato')); }
  };

  // Build (~5 daqiqa, server_log) → tugagach /publish/status'dagi android.aab.builtAt yangilanadi.
  const aabStartRef = React.useRef(null); // build bosilgandagi oxirgi AAB vaqti
  const kickAab = async () => {
    const nextVc = playNextVersionCode;
    if (!window.confirm(`Imzolangan AAB build qilinsinmi?\n\n🔗 API: 🚀 PRODUCTION (AAB doim shu bilan quriladi)\n🔢 versionCode: ${nextVc ? nextVc : 'repodagidek'}${versionName ? `\n📛 versionName: ${versionName}` : ''}\n\n~5 daqiqa, so'nggi kod.`)) return;
    aabStartRef.current = data?.android?.aab?.builtAt || '0';
    setBusy('aab'); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/project-modules/${data.moduleId}/mobile/aab/build`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versionName: versionName || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'aab_failed');
      setToast("📦 AAB build boshlandi (~5 daqiqa) — tugagach «AAB yuklab olish» faollashadi.");
    } catch (e) { setToast('❌ AAB build boshlanmadi: ' + (e.message || e)); setBusy(''); }
  };
  const downloadAab = async (versionCode) => {
    try {
      const q = versionCode ? `?versionCode=${versionCode}` : '';
      const r = await authFetchRef.current(`${apiUrl}/project-modules/${data.moduleId}/mobile/aab-link${q}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error(d.error || 'link_failed');
      window.open(apiUrl + d.url, '_blank');
    } catch (e) { setToast('❌ Yuklab olinmadi: ' + (e.message || e)); }
  };
  // AAB build tugaganini aniqlash: builtAt aabStartRef'dan yangi bo'lsa → build tugadi
  React.useEffect(() => {
    if (busy === 'aab' && data?.android?.aab?.builtAt && aabStartRef.current && data.android.aab.builtAt !== aabStartRef.current) {
      setBusy(''); setToast('✅ Imzolangan AAB tayyor — endi «🚀 Play\'ga yuborish» yoki 📥 yuklab olib qo\'lda yuklang.');
    }
  }, [data, busy]);

  // ── 🤖 Google Play API ─────────────────────────────────────────────────────
  // Kalit (service account JSON) ulanmagan bo'lsa Play holati ham, yuborish ham ishlamaydi.
  const [playTrack, setPlayTrack] = React.useState(
    () => localStorage.getItem(`kh_play_track_${projectId}`) || 'internal');
  React.useEffect(() => {
    setPlayTrack(localStorage.getItem(`kh_play_track_${projectId}`) || 'internal');
  }, [projectId]);
  const pickTrack = (v) => { setPlayTrack(v); try { localStorage.setItem(`kh_play_track_${projectId}`, v); } catch {} };
  // 🔢 versionName (ixtiyoriy) — versionCode Play'dagi eng kattadan avtomatik +1 bo'ladi.
  // 🔴 SAQLANADI: ilgari saqlanmasdi va sahifa yangilanganda jimgina yo'qolardi
  // (operator 1.0.1 yozgan edi, AAB esa eski 1.0 bilan chiqib ketdi).
  const [versionName, setVersionName] = React.useState(
    () => localStorage.getItem(`kh_version_name_${projectId}`) || '');
  React.useEffect(() => {
    setVersionName(localStorage.getItem(`kh_version_name_${projectId}`) || '');
  }, [projectId]);
  const onVersionName = (v) => {
    setVersionName(v);
    try { localStorage.setItem(`kh_version_name_${projectId}`, v); } catch {}
  };
  const onAscFile = (f) => {
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      const txt = String(rd.result || '');
      const m = /AuthKey_([A-Z0-9]{10})\.p8$/i.exec(f.name || '');
      setAscForm(x => ({ ...x, keyP8: txt, fileName: f.name, keyId: x.keyId || (m ? m[1].toUpperCase() : '') }));
    };
    rd.readAsText(f);
  };
  const saveAscKey = async () => {
    const a = ascForm;
    if (!a.keyP8 || !a.issuerId.trim() || !a.keyId.trim() || !a.appId.trim()) { setToast('❌ Issuer ID, Key ID, ilova Apple ID si va .p8 fayl kerak'); return; }
    setBusy('asckey'); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/ios/asc-key`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod || data?.moduleId, issuerId: a.issuerId.trim(), keyId: a.keyId.trim(), appId: a.appId.trim(), keyP8: a.keyP8 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setToast('❌ ' + (d.error || 'Kalit saqlanmadi')); return; }
      setShowAscKey(false); setAscForm({ issuerId: '', keyId: '', appId: '', keyP8: '', fileName: '' });
      setToast(`🍏 App Store Connect ulandi: ${d.appName || ''} (${d.bundleId}) — TestFlight build'lari: ${d.builds}`);
      load(false, true);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
    finally { setBusy(''); }
  };
  const onPlayKeyFile = (f) => {
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setPlayKeyText(String(rd.result || ''));
    rd.readAsText(f);
  };
  const [showPlayKey, setShowPlayKey] = React.useState(false);
  const [playKeyText, setPlayKeyText] = React.useState('');

  const savePlayKey = async () => {
    if (!playKeyText.trim()) { setToast('❌ Kalit JSON matnini joylashtiring'); return; }
    setBusy('playkey'); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/android/play-key`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, keyJson: playKeyText }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setToast('❌ ' + (d.error || 'Kalit saqlanmadi')); return; }
      setShowPlayKey(false); setPlayKeyText('');
      setToast(`🔑 Play kaliti ulandi: ${d.clientEmail} — endi Play Console → Users and permissions'da shu email'ga ilova huquqini bering.`);
      load(false);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  const submitToPlay = async (versionCode) => {
    const aabEnv = data?.android?.aab?.env;
    if (!data?.android?.aab?.builtAt) { setToast('❌ Avval 📦 AAB build qiling.'); return; }
    if (aabEnv === 'test' && playTrack === 'production') {
      setToast("🧪 Bu AAB TEST serverga ulanadi — PRODUCTION uchun 🔗 «🚀 production» bilan qayta build qiling.");
      return;
    }
    const envNote = aabEnv === 'production' ? '🚀 production API'
      : aabEnv === 'test' ? '🧪 TEST API (!)' : '❓ API noma\'lum';
    const trackNote = playTrack === 'production' ? 'PRODUCTION — jonli foydalanuvchilarga chiqadi!' : `${playTrack} (sinov treki)`;
    if (!window.confirm(`AAB Google Play'ga yuborilsinmi?\n\nTrek: ${trackNote}\nAPI: ${envNote}\n\nYuklash 1-3 daqiqa.`)) return;
    setBusy('play'); setToast('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/publish/android/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), moduleId: pubMod, track: playTrack, whatsNew,
          versionCode: versionCode || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setToast('❌ ' + (d.error || 'Yuborilmadi')); return; }
      setToast(`🚀 Play'ga yuborilmoqda (${d.package} → ${d.track}) — pastdagi log'da natija ko'rinadi.`);
      setTimeout(() => load(false), 10000);
    } catch (e) { setToast('❌ ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  // Play trek holati yorliqlari
  const PLAY_REL = {
    completed: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '✅ Chiqarilgan'],
    inProgress: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400', '📶 Bosqichma-bosqich'],
    draft: ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', '📝 Qoralama'],
    halted: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '⛔ To\'xtatilgan'],
  };
  // Play'dagi eng katta versionCode — keyingi build shundan +1 bo'ladi (backend ham shunday hisoblaydi).
  const playMaxVersionCode = (data?.android?.play?.tracks || [])
    .flatMap(t => (t.releases || []).flatMap(r => (r.versionCodes || []).map(Number)))
    .filter(n => Number.isFinite(n))
    .reduce((a, b) => Math.max(a, b), 0);
  const playNextVersionCode = playMaxVersionCode ? playMaxVersionCode + 1 : null;
  const TRACK_LABEL = { production: '🚀 Production', beta: '🧪 Beta (open)', alpha: '🔬 Alpha (closed)', internal: '👥 Internal' };

  const STATUS = {
    FINISHED: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '✅ Tayyor'],
    IN_PROGRESS: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400', '⏳ Build ketmoqda'],
    IN_QUEUE: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '⏳ Navbatda'],
    NEW: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '⏳ Boshlanmoqda'],
    PENDING_CANCEL: ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', 'Bekor qilinmoqda'],
    CANCELED: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', 'Bekor qilingan'],
    ERRORED: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Xato'],
  };
  // 🍏 App Store Connect ilova-versiya holati (ko'rib chiqish bosqichi) — Apple enum'lari.
  const ASC_STATE = {
    PREPARE_FOR_SUBMISSION: ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', '📝 Tayyorlanmoqda'],
    WAITING_FOR_REVIEW: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '⏳ Ko\'rib chiqish navbatida'],
    IN_REVIEW: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400', '🔎 Ko\'rib chiqilmoqda'],
    PROCESSING_FOR_APP_STORE: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400', '⚙️ App Store uchun ishlanmoqda'],
    PENDING_DEVELOPER_RELEASE: ['bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300', '🚀 Chiqarishga tayyor'],
    PENDING_APPLE_RELEASE: ['bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300', '🚀 Apple chiqarishini kutmoqda'],
    PENDING_CONTRACT: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '📄 Shartnoma kutilmoqda'],
    WAITING_FOR_EXPORT_COMPLIANCE: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '📄 Export compliance kutilmoqda'],
    READY_FOR_SALE: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '✅ App Store\'da (jonli)'],
    READY_FOR_DISTRIBUTION: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '✅ App Store\'da (jonli)'],
    REJECTED: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Rad etildi'],
    METADATA_REJECTED: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Metadata rad etildi'],
    INVALID_BINARY: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Yaroqsiz binary'],
    DEVELOPER_REJECTED: ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', 'Dasturchi rad etdi'],
    DEVELOPER_REMOVED_FROM_SALE: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', 'Sotuvdan olindi'],
    REMOVED_FROM_SALE: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', 'Sotuvdan olindi'],
    REPLACED_WITH_NEW_VERSION: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', 'Yangi versiya bilan almashtirildi'],
    NOT_APPLICABLE: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', '—'],
  };
  // TestFlight'dagi build qayta ishlanish holati (VALID = sinovga/submit'ga tayyor).
  const ASC_PROC = {
    VALID: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '✅ Tayyor (TestFlight)'],
    PROCESSING: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400', '⚙️ Qayta ishlanmoqda'],
    INVALID: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Yaroqsiz'],
    FAILED: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Xato'],
  };
  const ascVersions = (data?.asc?.configured && Array.isArray(data.asc.versions)) ? data.asc.versions : [];
  const ascBuilds = (data?.asc?.configured && Array.isArray(data.asc.builds)) ? data.asc.builds.filter(b => !b.expired) : [];
  // 🔗 Har build QAYSI endpoint bilan qurilgan (build vaqtida yozib qo'yilgan). null =
  // yozuv yo'q (eski build) → «noma'lum».
  const ENV_BADGE = {
    test: ['bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', '🧪 test'],
    production: ['bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400', '🚀 production'],
    other: ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', '🌐 boshqa'],
    unknown: ['bg-gray-100 dark:bg-gray-700 text-gray-400', '❓ noma\'lum'],
  };
  const envBadge = (e) => ENV_BADGE[e] || ENV_BADGE.unknown;
  // ✅ Allaqachon TestFlight'ga (ASC'ga) yuklangan build raqamlari — «TestFlight'ga»
  // tugmasi o'rniga «✅ TestFlight'da» ko'rsatish uchun.
  const tfBuildNums = new Set((data?.asc?.builds || []).map(b => String(b.buildNumber)));
  const activeBuild = (data?.iosBuilds || []).some(b => ['IN_PROGRESS', 'IN_QUEUE', 'NEW'].includes(b.status));
  // 🍏 Build EAS'ga yetguncha (1–3 daq) holat SERVERDA — sahifa yangilansa ham ko'rinadi, tugma qayta bosilmaydi.
  const iosPrep = data?.iosBuildPrep || null;
  const buildLog = (() => {
    const m = (data?.logs || '').split('@@build@@')[1]; return m ? m.split('@@submit@@')[0].trim() : '';
  })();
  const submitLog = (() => {
    // NB @@play@@ ham shu satrda keladi — submit log'iga qo'shilib ketmasin.
    const m = (data?.logs || '').split('@@submit@@')[1]; return m ? m.split('@@play@@')[0].trim() : '';
  })();
  const playLog = (() => {
    const m = (data?.logs || '').split('@@play@@')[1]; return m ? m.trim() : '';
  })();

  if (loading && !data) return <div className="p-8 text-gray-400">⏳ Tekshirilmoqda...</div>;

  // Mobil modul yo'q — prod deploy sozlangan bo'lsa panel shu karta bilan ochiladi.
  if (data?.noMobile && !(data.prodModules || []).length) return (
    <div className="max-w-xl mx-auto mt-16 text-center text-gray-500 dark:text-gray-400">
      <div className="text-5xl mb-4">🚀</div>
      <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">Publish</p>
      <p>Bu loyihada mobil modul yo'q — App Store / TestFlight'ga chiqarish faqat mobil modulli loyihalar uchun.</p>
    </div>
  );


  return (
    <div className="w-full p-1 pb-10">
      {/* App meta */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">🚀 Publish — {data?.name}</h2>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {data?.bundleId && <span>📦 <span className="font-mono">{data.bundleId}</span></span>}
            {data?.version && <span>v{data.version}</span>}
            {data?.ascAppId && <span>ASC ID {data.ascAppId}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
        {(data?.mobileModules || []).length > 1 && (
          <select value={pubModuleId || String(data?.moduleId || '')} onChange={e => pickPubModule(e.target.value)}
            title="Bu loyihada bir nechta mobil ilova bor — Publish qaysi biri uchun ko'rsatilsin"
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg px-2 py-1.5 text-xs font-medium">
            {data.mobileModules.map(m => <option key={m.id} value={m.id}>📱 {m.name}</option>)}
          </select>
        )}
        <button onClick={() => load(true, true)} disabled={loading}
          title={data && data.cached && data.cachedAgeMs != null
            ? `Ko'rsatilayotgan ma'lumot ${Math.round(data.cachedAgeMs / 1000)} soniya oldin olingan — bosilsa do'konlardan qaytadan so'raladi`
            : "Do'konlardan qaytadan so'raladi"}
          className="px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 font-medium shrink-0">
          {loading ? '⏳ ...' : '↻ Yangilash'}
        </button>
        </div>
      </div>

      {toast && <div className="mb-4 text-sm bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 text-primary-900 dark:text-primary-200 rounded-lg px-3 py-2">{toast}</div>}

      {!data?.noMobile && (<>
      {/* 📦 RELIZ — bir bosishda ikkala do'konga (services/releasePlaybook.js). Faol bo'lsa holati, aks holda tugma. */}
      {(() => {
        const live = rel && ['active', 'waiting'].includes(rel.status);
        const cur = rel && (rel.steps || []).find(s => s.state === 'current' || s.state === 'failed');
        const r2 = (rel && rel.refs && rel.refs.release) || {};
        const ask = live && rel.wait && ['ask', 'form'].includes(rel.wait.kind);
        return (
          <div className={`mb-4 rounded-lg border p-4 shadow-sm ${ask ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 dark:text-white">📦 Reliz — App Store + Google Play {r2.target ? <span className="font-mono text-sm">{r2.target}</span> : null}</div>
                {live ? (
                  <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                    {ask ? '👤 ' : '⏳ '}{cur ? cur.title : rel.step}{rel.wait && rel.wait.text ? ` — ${rel.wait.text}` : ''}
                  </div>
                ) : rel ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Oxirgi reliz{r2.target ? ` ${r2.target}` : ''}: {rel.status === 'done' ? '✅ chiqdi' : rel.status === 'failed' ? `❌ ${rel.lastError || 'xato'}` : '✖ bekor qilingan'}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Bir bosish: versiya + What's New + build + yuborish + kuzatish + 20%→100% Play + rad etilsa tuzatib qayta yuborish.</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {rel && <button onClick={() => setRelOpen(rel.id)} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">{ask ? '👤 Javob berish' : '📋 Batafsil'}</button>}
                {!live && (<>
                  <input value={relVer} onChange={e => setRelVer(e.target.value)} placeholder="versiya (avto)"
                    title="Bo'sh qoldiring — do'kondagi jonli versiyadan keyingisi (masalan 1.14.1 → 1.15.0) hisoblanadi"
                    className="w-28 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  <button onClick={startRelease} disabled={relBusy}
                    className="px-3 py-1.5 text-xs rounded-lg bg-primary-700 hover:bg-primary-800 text-white font-semibold disabled:opacity-50">{relBusy ? '⏳ ...' : '📦 Reliz qilish'}</button>
                </>)}
              </div>
            </div>
          </div>
        );
      })()}
      {relOpen && (
        <PlaybookModal authFetch={authFetch} apiUrl={apiUrl} runId={relOpen}
          onClose={() => { setRelOpen(null); loadRel(); }} onOpenConsole={canOpenConsole ? () => { setRelOpen(null); onOpenConsole && onOpenConsole(); } : null} />
      )}
      {/* 📝 What's New — App Store (ASC versiyasi) VA Google Play (reliz izohi) uchun BITTA matn.
          iOS'da build bilan saqlanadi; Android'da Play'ga yuborishda izoh sifatida ketadi. */}
      <div className="mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">📝 What's New / Reliz izohi — 🍏 App Store va 🤖 Google Play uchun</label>
        <textarea value={whatsNew} onChange={e => onWhatsNew(e.target.value)} rows={2}
          placeholder="Bu versiyada nima yangilandi? (iOS build bilan saqlanadi; Play'ga yuborishda do'kon tillariga qo'yiladi)"
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y" />
      </div>

      {/* iOS + Android — keng ekranda yonma-yon (2 ustun), torda ketma-ket */}
      <div className="grid gap-5 xl:grid-cols-2 items-start mb-5">
      {/* iOS card — EAS sozlanmagan bo'lsa faqat shu karta o'rnida izoh (Android/Production yashirinmaydi) */}
      {!data?.configured ? (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm min-w-0">
        <div className="flex items-center gap-2 text-gray-900 dark:text-white font-semibold text-lg mb-3">🍏 iOS — App Store / TestFlight</div>
        {data?.asc ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-700 dark:text-gray-300">✅ App Store Connect ulangan: <b translate="no">{data.asc.appName || '—'}</b> <span className="font-mono text-xs text-gray-500" translate="no">{data.asc.bundleId}</span> · Apple ID {data.asc.ascAppId}</div>
            {(data.asc.versions || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">App Store versiyalari</div>
                {data.asc.versions.map((v, i) => <div key={i} className="text-xs text-gray-700 dark:text-gray-300 flex gap-2"><span className="font-mono">{v.version}</span><span className="text-gray-500">{v.state}</span></div>)}
              </div>
            )}
            {(data.asc.builds || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">TestFlight build'lari</div>
                {data.asc.builds.map((b, i) => <div key={i} className="text-xs text-gray-700 dark:text-gray-300 flex gap-2"><span className="font-mono">#{b.buildNumber}</span><span className="text-gray-500">{b.processingState}{b.expired ? ' · muddati o‘tgan' : ''}</span><span className="text-gray-400">{b.uploadedAt ? String(b.uploadedAt).slice(0, 10) : ''}</span></div>)}
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Bu ilova EAS'siz (masalan Flutter) — App Store holati shu yerda ko'rinadi, iOS build'i esa hozircha Khorezmai'dan qurilmaydi (Mac/Xcode kerak).</p>
          </div>
        ) : (<>
        <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">iOS publish hali sozlanmagan</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Expo ilovada EAS project + Apple credential'lar sozlanadi — shunda shu yerda "iOS build" tugmasi paydo bo'ladi.
          EAS'siz ilova (Flutter) uchun App Store Connect API kalitini ulang: TestFlight build'lari va ko'rib chiqish holati shu yerda ko'rinadi.</p>
        {showAscKey ? (
          <div className="mt-3 space-y-2">
            <ol className="text-xs text-gray-500 dark:text-gray-400 list-decimal pl-4 space-y-0.5">
              <li>App Store Connect → <b>Users and Access → Integrations → App Store Connect API → Team keys</b> → «+»; ruxsat <b>App Manager</b></li>
              <li><b>AuthKey_XXXX.p8</b> faylni yuklab oling (bir marta beriladi); Issuer ID va Key ID shu sahifada</li>
              <li>Apple ID — ilovaning <b>App Information → Apple ID</b> (raqam)</li>
            </ol>
            <input value={ascForm.issuerId} onChange={e => setAscForm({ ...ascForm, issuerId: e.target.value })} placeholder="Issuer ID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)" className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg px-2 py-1.5 text-xs font-mono" />
            <div className="flex gap-2">
              <input value={ascForm.keyId} onChange={e => setAscForm({ ...ascForm, keyId: e.target.value })} placeholder="Key ID" className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg px-2 py-1.5 text-xs font-mono" />
              <input value={ascForm.appId} onChange={e => setAscForm({ ...ascForm, appId: e.target.value })} placeholder="Ilova Apple ID (raqam)" className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg px-2 py-1.5 text-xs font-mono" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              📎 {ascForm.fileName ? <span translate="no">{ascForm.fileName}</span> : '.p8 faylni tanlash'}
              <input type="file" accept=".p8" className="hidden" onChange={e => onAscFile(e.target.files && e.target.files[0])} />
            </label>
            <div className="flex gap-2">
              <button onClick={saveAscKey} disabled={busy === 'asckey'} className="px-3 py-1.5 text-xs bg-primary-700 hover:bg-primary-800 text-white rounded-lg disabled:opacity-50 font-semibold">{busy === 'asckey' ? '⏳ Apple bilan tekshirilmoqda…' : '🍏 Kalitni ulash'}</button>
              <button onClick={() => { setShowAscKey(false); setAscForm({ issuerId: '', keyId: '', appId: '', keyP8: '', fileName: '' }); }} className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg">Bekor</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowAscKey(true)} className="mt-3 px-3 py-1.5 text-xs bg-primary-700 hover:bg-primary-800 text-white rounded-lg font-semibold">🍏 App Store Connect kalitini ulash</button>
        )}
        </>)}
      </div>
      ) : (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm min-w-0">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white font-semibold text-lg whitespace-nowrap">🍏 iOS — App Store / TestFlight</div>
          {data?.easUsage && (
            <span
              title={data.easUsage.official
                ? `Expo'ning rasmiy hisobi (eas account:usage) — ${data.easUsage.plan || ''} tarif, butun akkaunt (barcha loyihalar bitta limitni bo'lishadi). iOS: ${data.easUsage.used}${data.easUsage.limit != null ? '/' + data.easUsage.limit : ''}, Android: ${data.easUsage.androidUsed ?? 0}${data.easUsage.androidLimit != null ? '/' + data.easUsage.androidLimit : ''}; bu loyihada shu oy ${data.easUsage.projectUsed ?? 0} ta iOS build. Muvaffaqiyatsiz build'larning bir qismini Expo hisobga olmaydi (waived).${data.easUsage.overageCents ? ` Ortiqcha: ${data.easUsage.overageCount} ta, $${(data.easUsage.overageCents / 100).toFixed(2)}.` : ''}${data.easUsage.periodEnd ? ` Davr ${new Date(data.easUsage.periodEnd).toISOString().slice(0, 10)} da yangilanadi.` : ''}`
                : `Shu oyda EAS bulutida qurilgan build'lar — BUTUN AKKAUNT bo'yicha (barcha loyihalar bitta limitni bo'lishadi; ad-hoc ham, production ham sanaladi): ${data.easUsage.used} ta${data.easUsage.projectUsed != null ? `, shundan bu loyihada ${data.easUsage.projectUsed} ta` : ''}${Array.isArray(data.easUsage.perProject) ? ` (${data.easUsage.perProject.map(p => `${p.dir}: ${p.used}`).join(', ')})` : ''}. Bepul tarif chegarasi ${data.easUsage.limit} ta/oy (Konfiguratsiyadan o'zgartiriladi). Bu Khorezmai'ning O'Z hisobi — EAS tarixidan sanaladi, Expo'ning rasmiy hisoblagichi emas.${data.easUsage.approx ? ' (50 tadan ko\'p build bor — son taxminiy)' : ''}`}
              className={`ml-2 text-[11px] px-2 py-0.5 rounded-full font-semibold align-middle ${
                data.easUsage.remaining === 0 ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                : data.easUsage.remaining != null && data.easUsage.remaining <= 3 ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
              {data.easUsage.official ? (<>
                📊 EAS{data.easUsage.plan && data.easUsage.plan !== 'Free' ? ` ${data.easUsage.plan}` : ''}: {data.easUsage.used}{data.easUsage.limit != null ? `/${data.easUsage.limit}` : ''} iOS build
                {data.easUsage.remaining == null ? '' : data.easUsage.remaining === 0 ? ' — limit tugadi' : ` · ${data.easUsage.remaining} qoldi`}
                {data.easUsage.overageCents ? ` · ortiqcha $${(data.easUsage.overageCents / 100).toFixed(2)}` : ''}
                {data.easUsage.periodEnd ? (() => { const d = new Date(data.easUsage.periodEnd); return ` · ${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')} da yangilanadi`; })() : ''}
              </>) : (<>
                📊 EAS: {data.easUsage.used}/{data.easUsage.limit} shu oyda{data.easUsage.accountWide ? ' (akkaunt)' : ''}
                {data.easUsage.remaining === 0 ? ' — limit tugadi' : ` · ${data.easUsage.remaining} qoldi`}
              </>)}
            </span>
          )}
          <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
            {/* 🔗 API manzili — .ipa ichidagi BASE_URL build vaqtida almashtiriladi */}
            <select value={buildProfile} onChange={e => pickBuildProfile(e.target.value)}
              title="Build turi: TestFlight/App Store (App Store imzosi) yoki iPhone ad-hoc (faqat EAS'da ro'yxatdan o'tgan iPhone'lar, havola orqali o'rnatiladi, Apple tekshiruvisiz). Ikkalasi ham EAS oylik limitidan bittadan oladi."
              className={`px-2 py-2 text-xs border rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 ${buildProfile === 'preview' ? 'border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-400 font-semibold' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
              <option value="production">🏪 TestFlight / App Store</option>
              <option value="preview">📲 iPhone (ad-hoc)</option>
            </select>
            <select value={apiTarget} onChange={e => pickApiTarget(e.target.value)}
              title="Ilova qaysi backendga ulansin — .ipa ichidagi API manzili build vaqtida shunga o'zgartiriladi"
              className={`px-2 py-2 text-xs border rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 ${apiTarget === 'production' ? 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 font-semibold' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
              <option value="code">🔗 API: koddagidek</option>
              <option value="test" disabled={!data?.apiTargets?.test}>🧪 API: test env</option>
              <option value="production" disabled={!data?.apiTargets?.production}>🚀 API: production</option>
            </select>
            <button onClick={() => kickBuild(false)} disabled={busy === 'build' || activeBuild || (!!iosPrep && iosPrep.stage !== 'failed')}
              className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-40 font-semibold shrink-0">
              {busy === 'build' || (iosPrep && !['queued', 'failed'].includes(iosPrep.stage)) ? '⏳ Tayyorlanmoqda...' : activeBuild ? '⏳ Build ketmoqda...' : iosPrep && iosPrep.stage === 'queued' ? '⏳ EAS navbatida...' : '🍏 Yangi iOS build'}
            </button>
          </div>
        </div>
        {iosPrep && iosPrep.stage === 'failed' && (
          <div className="mb-3 text-xs rounded-lg px-3 py-2 border bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
            <div className="font-semibold mb-1">❌ iOS build to'xtatildi — EAS'ga yuborilmadi (oylik limit sarflanmadi)</div>
            <div>{iosPrep.error}</div>
            {Array.isArray(iosPrep.lines) && iosPrep.lines.length > 0 && (
              <pre translate="no" className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-white/70 dark:bg-gray-900/60 p-2 text-[11px] text-gray-800 dark:text-gray-200">{iosPrep.lines.join('\n')}</pre>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={() => kickBuild(false)} className="px-3 py-1.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black">🔁 Qayta tekshirib build</button>
              <button onClick={() => kickBuild(true)} className="px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40">⚠️ Tekshiruvsiz build</button>
            </div>
          </div>
        )}
        {iosPrep && iosPrep.stage !== 'failed' && !activeBuild && (
          <div className="mb-3 text-xs rounded-lg px-3 py-2 border bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800 text-sky-800 dark:text-sky-300">
            ⏳ iOS build {iosPrep.profile === 'preview' ? '(ad-hoc) ' : ''}tayyorlanmoqda — <b>{iosPrep.stageLabel}</b> · {iosPrep.ageSec} s{iosPrep.by ? <> · <span translate="no">{iosPrep.by}</span></> : null}.
            {' '}Sahifani yangilasangiz ham davom etadi; EAS ro'yxatida odatda 1–3 daqiqada paydo bo'ladi.
            {iosPrep.podWarn && <div className="mt-1 text-amber-700 dark:text-amber-400">⚠️ pod install tekshiruvi ishlamadi ({iosPrep.podWarn}) — build baribir EAS'ga yuborildi.</div>}
          </div>
        )}
        {apiTarget !== 'code' && (
          <div className={`mb-3 text-xs rounded-lg px-3 py-2 border ${apiTarget === 'production' ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400' : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'}`}>
            {apiTarget === 'production'
              ? <>🚀 Bu build <b>PRODUCTION</b> backendga ulanadi: <b>{data?.apiTargets?.production}</b> — TestFlight/App Store uchun.</>
              : <>🧪 Bu build <b>TEST</b> backendga ulanadi: <b>{data?.apiTargets?.test}</b> — jonli ma'lumotga tegmaydi.</>}
          </div>
        )}
        {/* ⚠️ Versiya qo'riqchisi — EAS `autoIncrement` faqat BUILD raqamini oshiradi,
            marketing versiyani EMAS. Do'konda chiqqan versiya bilan bir xil qolsa
            Apple build'ni rad etadi (ITMS-90186 / ITMS-90062). Jonli bo'lgan holat:
            1.0.1 chiqqach ham build'lar 1.0.1 (18, 19) bo'lib ketaverdi. */}
        {data?.versionWarning && (
          <div className="mb-3 text-xs rounded-lg px-3 py-2 border bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-800 dark:text-red-300">
            {data.versionWarning.message}
            <div className="mt-1 text-[11px] text-red-700 dark:text-red-400">
              Wrapper: <code>/root/expo-dev/m{data.moduleId}-expo/app.json</code> → <code>expo.version</code> ni
              {' '}<b>{data.versionWarning.suggested}</b> ga o'zgartiring, keyin «🍏 Yangi iOS build».
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
          «Yangi iOS build» so'nggi kodni oladi → EAS bulutida <b>.ipa</b> quradi (~15 daqiqa) → tayyor bo'lgach
          «TestFlight'ga yuborish» bilan App Store Connect'ga jo'natasiz. Keyin ASC'da metadata/screenshot to'ldirib
          «Add for Review» qilinadi.
        </p>



        {/* 🍏 App Store Connect (Apple tarafdagi) holat — EAS build:list bermaydigan
            ma'lumot: ilova ko'rib chiqishning qaysi bosqichida + TestFlight'da build tayyormi. */}
        {data?.asc?.configured && (
          <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">🍏 App Store Connect holati</span>
              <div className="flex items-center gap-2 shrink-0">
                {/* 🚀 Yarim-avtomatik reliz: mexanik qadamlarni ASC API bajaradi (versiya +
                    build biriktirish + export compliance), keyin konsol ochiladi — inson qismi
                    (What's New / skrinshot / Submit) siznikida. */}
                {data?.ascAppId && canOpenConsole && onOpenConsole && (
                  <button
                    onClick={prepareRelease} disabled={preparing}
                    title="Oxirgi production build uchun App Store versiyasini tayyorlaydi (versiya + build + export compliance), keyin konsolni ochadi — What's New va Submit'ni siz qilasiz"
                    className="px-2.5 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 font-semibold">
                    {preparing ? '⏳ Tayyorlanmoqda…' : "🚀 App Store'ga chiqarish"}
                  </button>
                )}
                {data?.ascAppId && (
                  <a href={`https://appstoreconnect.apple.com/apps/${data.ascAppId}/distribution`}
                    target="_blank" rel="noreferrer" className="text-[11px] text-primary-700 dark:text-primary-500 hover:underline">ASC'da ochish ↗</a>
                )}
              </div>
            </div>
            {/* 🚀 Tayyorlash natijasi: bajarilgan mexanik qadamlar + endi INSON qismi + konsolni ochish */}
            {prepareResult && prepareResult.ok && (
              <div className="mb-2 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 p-2.5 text-xs">
                <div className="font-semibold text-primary-900 dark:text-primary-200 mb-1">✅ Reliz tayyorlandi{prepareResult.versionString ? ` — v${prepareResult.versionString}` : ''}</div>
                {(prepareResult.steps || []).map((s, i) => <div key={i} className="text-emerald-700 dark:text-emerald-400 flex gap-1.5"><span>✓</span><span>{khNode(s)}</span></div>)}
                {(prepareResult.warnings || []).map((s, i) => <div key={i} className="text-amber-700 dark:text-amber-400 flex gap-1.5 mt-0.5"><span>⚠️</span><span>{khNode(s)}</span></div>)}
                {(prepareResult.needs || []).length > 0 && <div className="mt-1.5 text-gray-700 dark:text-gray-300 font-medium">Endi konsolda SIZ:</div>}
                {(prepareResult.needs || []).map((s, i) => <div key={i} className="text-gray-600 dark:text-gray-400 flex gap-1.5"><span>•</span><span>{khNode(s)}</span></div>)}
                <button
                  onClick={() => onOpenConsole('apple', `https://appstoreconnect.apple.com/apps/${data.ascAppId}/distribution`)}
                  className="mt-2 px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 font-semibold">🍏 Konsolni ochib yakunlash →</button>
              </div>
            )}
            {ascVersions.length === 0 ? (
              <div className="text-xs text-gray-400">Versiya holati topilmadi.</div>
            ) : (
              <div className="space-y-1.5">
                {ascVersions.map((v, i) => {
                  const [cls, label] = ASC_STATE[v.state] || ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', v.state || '—'];
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${cls}`}>{label}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">v{v.version || '?'}</span>
                      {v.createdAt && <span className="text-[11px] text-gray-400">{new Date(v.createdAt).toLocaleDateString('uz')}</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {ascBuilds.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700">
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">📲 TestFlight build'lari</div>
                <div className="flex flex-wrap gap-1.5">
                  {ascBuilds.map((b, i) => {
                    const [cls, label] = ASC_PROC[b.processingState] || ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', b.processingState || '—'];
                    return (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} title={b.uploadedAt ? new Date(b.uploadedAt).toLocaleString('uz') : ''}>
                        Build {b.buildNumber || '?'} · {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {(data?.iosBuilds || []).length === 0 ? (
          <div className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">Hali build yo'q — «Yangi iOS build» bilan boshlang.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
            {data.iosBuilds.map(b => {
              const [cls, label] = STATUS[b.status] || ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', b.status];
              const [ecls, elabel] = envBadge(b.apiEnv);
              const onTf = tfBuildNums.has(String(b.buildNumber));
              const isSubmitting = !onTf && !!submitting[b.id];
              const editingThis = editNote && String(editNote.buildNumber) === String(b.buildNumber);
              return (
                <div key={b.id} className="px-3 py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${cls}`}>{label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
                        v{b.version || '?'} · Build {b.buildNumber || '?'}
                        {/* 🔗 qaysi endpoint bilan qurilgan */}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ecls}`}
                          title={b.apiUrl ? `API: ${b.apiUrl}` : 'Build vaqtidagi endpoint yozib olinmagan (eski build)'}>{elabel}</span>
                        {String(b.distribution || '').toUpperCase() === 'INTERNAL' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"
                            title="Ad-hoc: faqat EAS'da ro'yxatdan o'tgan iPhone'larga havola orqali o'rnatiladi; TestFlight'ga yuborilmaydi">📲 ad-hoc</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">{b.createdAt ? new Date(b.createdAt).toLocaleString('uz') : ''}</div>
                      {b.submitBlocked && <div className="text-[11px] text-red-600 dark:text-red-400 mt-0.5">{b.submitBlockedReason}</div>}
                    </div>
                    {b.url && <a href={b.url} target="_blank" rel="noreferrer" className="px-2 py-1 text-xs text-primary-700 dark:text-primary-500 hover:underline shrink-0">⬇ .ipa</a>}
                    {b.status === 'FINISHED' && String(b.distribution || '').toUpperCase() === 'INTERNAL' ? (
                      b.installUrl
                        ? <a href={b.installUrl} target="_blank" rel="noreferrer"
                            title="iPhone'da Safari'da oching → Install (faqat ro'yxatdagi qurilmalar). Kompyuterda ochsangiz QR kod chiqadi — iPhone kamerasi bilan skaner qiling."
                            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium shrink-0">📲 O'rnatish</a>
                        : <span className="text-xs text-gray-400 shrink-0">📲 ad-hoc</span>
                    ) : b.status === 'FINISHED' && (
                      onTf
                        ? <span className="px-2.5 py-1 text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg font-medium shrink-0" title="Bu build allaqachon App Store Connect / TestFlight'da">✅ TestFlight'da</span>
                        : isSubmitting
                          ? <span className="px-2.5 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg font-medium shrink-0 flex items-center gap-1" title="eas submit EAS'da ishlamoqda — bir necha daqiqada TestFlight'da paydo bo'ladi">⏳ Yuborilmoqda…</span>
                          /* ❌ Versiyasi do'konda chiqqan build — Apple KAFOLATLI rad etadi
                             (ITMS-90186). Tugmani ko'rsatib turishning ma'nosi yo'q. */
                          : b.submitBlocked
                            ? <span className="px-2.5 py-1 text-xs bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg font-medium shrink-0"
                                title={b.submitBlockedReason}>❌ Yuborib bo'lmaydi</span>
                          : <button onClick={() => submit(b.id)} disabled={busy === 'submit:' + b.id}
                              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 font-medium shrink-0">
                              {busy === 'submit:' + b.id ? '⏳...' : '📤 TestFlight\'ga'}
                            </button>
                    )}
                  </div>
                  {/* 📝 What's New — shu build uchun (relizda ASC versiyasiga qo'yiladi); ad-hoc build'da kerak emas */}
                  {String(b.distribution || '').toUpperCase() !== 'INTERNAL' && <div className="mt-1.5 ml-1">
                    {editingThis ? (
                      <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/30 p-2">
                        <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={2} autoFocus
                          placeholder="Bu versiyada nima yangilandi?"
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y" />
                        <div className="flex gap-2 mt-1.5">
                          <button onClick={saveBuildNote} disabled={savingNote}
                            className="px-2.5 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 font-medium">{savingNote ? '⏳...' : '💾 Saqlash'}</button>
                          <button onClick={() => setEditNote(null)} className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">Bekor</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        {b.whatsNew
                          ? <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap flex-1"><span className="text-gray-400">📝</span> {b.whatsNew}</div>
                          : <div className="text-xs text-gray-400 flex-1">📝 What's New yozilmagan</div>}
                        <button onClick={() => startEditNote(b)} className="text-[11px] text-primary-700 dark:text-primary-500 hover:underline shrink-0">✏️ {b.whatsNew ? 'Tahrirlash' : 'Qo\'shish'}</button>
                      </div>
                    )}
                  </div>}
                </div>
              );
            })}
          </div>
        )}

        {buildLog && (
          <details className="mt-3"><summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">🛠 Build log</summary>
            <pre className="mt-1 text-[11px] bg-gray-900 text-gray-100 rounded-lg p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">{buildLog}</pre></details>
        )}
        {submitLog && (
          <details className="mt-2" open><summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">📤 Submit log</summary>
            <pre className="mt-1 text-[11px] bg-gray-900 text-gray-100 rounded-lg p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">{submitLog}</pre></details>
        )}
      </div>
      )}

      {/* Android card — imzolangan AAB → Google Play (viloyatga shu yo'l bilan chiqarilgan) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm min-w-0">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white font-semibold text-lg">🤖 Android — Google Play</div>
          {data?.android?.configured && (
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={kickAab} disabled={busy === 'aab'}
                className="px-4 py-2 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-40 font-semibold">
                {busy === 'aab' ? '⏳ Build ketmoqda...' : '📦 AAB build'}
              </button>
              {data?.android?.aab?.builtAt && (
                <button onClick={() => downloadAab()} className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 font-medium">📥 AAB yuklab olish</button>
              )}
            </div>
          )}
        </div>

        {/* 📱 To'g'ridan-to'g'ri APK — do'konsiz tarqatish (mijozga yuborish, real
            telefonda sinash). Keystore SHART EMAS, shuning uchun har doim ko'rinadi. */}
        <div className="mb-3 flex items-center gap-2 flex-wrap border-b border-gray-100 dark:border-gray-700 pb-3">
          <button onClick={downloadApk}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
            📥 APK yuklab olish
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            So'nggi build qilingan <b>.apk</b> — do'konsiz, to'g'ridan-to'g'ri telefonga o'rnatish uchun
            (mijozga yuborish, real qurilmada sinash). Play uchun esa pastdagi <b>AAB</b> kerak.
          </span>
        </div>
        {/* Play holati va kaliti keystore'ga BOG'LIQ EMAS — keystore yo'q ilovada ham (Idora client/worker) ulash mumkin */}
        {data?.android && (<>
            {/* 🤖 GOOGLE PLAY HOLATI — treklar/relizlar (androidpublisher API).
                Kalit ulanmagan bo'lsa qanday ulashni ko'rsatamiz. */}
            {data.android.play?.configured && data.android.play?.ok ? (
              <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">🤖 Google Play holati</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {canOpenConsole && onOpenConsole && (
                      <button
                        onClick={() => onOpenConsole('google', `https://play.google.com/console/u/0/developers?package=${data.android.package}`)}
                        title="Konsolda (login qilingan Play Console sessiyasi) ochadi — Data safety, skrinshot, ko'rib chiqishga yuborish"
                        className="px-2.5 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 font-semibold">
                        🎛 Play Console'da ochish
                      </button>
                    )}
                    <a href="https://play.google.com/console" target="_blank" rel="noreferrer"
                      className="text-[11px] text-primary-700 dark:text-primary-500 hover:underline">Play Console ↗</a>
                  </div>
                </div>
                {(data.android.play.tracks || []).length === 0 ? (
                  <div className="text-xs text-gray-400">Hali reliz yo'q — pastdan trekka yuboring.</div>
                ) : (
                  <div className="space-y-1.5">
                    {data.android.play.tracks.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-28 shrink-0">{TRACK_LABEL[t.track] || t.track}</span>
                        {t.releases.map((r, j) => {
                          const [rcls, rlabel] = PLAY_REL[r.status] || ['bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', r.status || '—'];
                          return (
                            <span key={j} className="flex items-center gap-1.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rcls}`}>{rlabel}</span>
                              <span className="text-gray-700 dark:text-gray-300 font-medium">v{r.versionCodes.join(', ')}</span>
                              {r.userFraction != null && <span className="text-[11px] text-blue-600 dark:text-blue-400">{Math.round(r.userFraction * 100)}%</span>}
                            </span>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3 text-xs text-amber-900 dark:text-amber-200">
                <div className="font-semibold mb-1">🔑 Google Play API hali ulanmagan</div>
                {data.android.play?.error
                  ? <div className="mb-1.5 text-red-700 dark:text-red-400">❌ {data.android.play.error}</div>
                  : <div className="mb-1.5">Ulansa: Play holati (qaysi trekda qaysi versiya) shu yerda ko'rinadi va AAB'ni <b>bir tugma bilan</b> yuborasiz (72 MB faylni brauzerda yuklash shart emas).</div>}
                <div className="mb-2">Kalitni <b>siz</b> yaratasiz (bizda sizning Google akkauntingizga kirish yo'q):</div>
                <ol className="list-decimal ml-4 space-y-0.5 mb-2">
                  <li>Google Cloud → <b>IAM → Service accounts</b> → service account yarating (rol shart emas) va <b>Google Play Android Developer API</b> ni yoqing</li>
                  <li>Service account → <b>Keys → Add key → JSON</b> (fayl yuklanadi)</li>
                  <li>Play Console → <b>Users and permissions</b> → o'sha service-account email'ini taklif qiling → <b>Release</b> huquqlari (Release to production/testing) + shu ilova</li>
                  <li>JSON faylni «📎 JSON faylni tanlash» bilan tanlang (yoki matnini joylang)</li>
                </ol>
                {showPlayKey ? (
                  <div>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer mb-1.5 px-2.5 py-1 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60">
                      📎 JSON faylni tanlash
                      <input type="file" accept=".json,application/json" className="hidden" onChange={e => onPlayKeyFile(e.target.files && e.target.files[0])} />
                    </label>
                    <textarea value={playKeyText} onChange={e => setPlayKeyText(e.target.value)} rows={4}
                      placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN PRIVATE KEY-----...","client_email":"...@....iam.gserviceaccount.com",...}'
                      className="w-full border border-amber-300 dark:border-amber-700 rounded-lg px-2 py-1.5 text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y" />
                    <div className="flex gap-2 mt-1.5">
                      <button onClick={savePlayKey} disabled={busy === 'playkey'}
                        className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-semibold">
                        {busy === 'playkey' ? '⏳...' : '🔑 Kalitni ulash'}
                      </button>
                      <button onClick={() => { setShowPlayKey(false); setPlayKeyText(''); }} className="px-3 py-1.5 text-xs border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60">Bekor</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setShowPlayKey(true)}
                    className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold">🔑 Play kalitini ulash</button>
                )}
              </div>
            )}
        </>)}
        {data?.android?.configured ? (
          <>
            {/* 🔢 Versiyalar — Play har yuklamada versionCode oshishini talab qiladi */}
            <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <span className="font-semibold text-gray-600 dark:text-gray-400">🔢 Versiya</span>
                {playMaxVersionCode
                  ? <span className="text-gray-500 dark:text-gray-400">Play'da eng katta: <b className="text-gray-700 dark:text-gray-300">{playMaxVersionCode}</b></span>
                  : <span className="text-gray-400">Play'da hali reliz yo'q</span>}
                <span className="text-gray-400">→</span>
                <span className="text-gray-600 dark:text-gray-400">keyingi build: <b className="text-emerald-700 dark:text-emerald-400">versionCode {playNextVersionCode || 'repodagidek'}</b> <span className="text-gray-400">(avtomatik)</span></span>
                {data.android.aab?.versionCode && (
                  <span className="text-gray-400">· so'nggi AAB: v{data.android.aab.versionCode}{data.android.aab.versionName ? ` (${data.android.aab.versionName})` : ''}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <label className="text-xs text-gray-500 dark:text-gray-400">📛 versionName (ixtiyoriy):</label>
                <input value={versionName} onChange={e => onVersionName(e.target.value)}
                  placeholder="masalan 1.0.1 — bo'sh qoldirilsa repodagidek"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs w-64 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <span className="text-[11px] text-gray-400">Foydalanuvchi ko'radigan versiya — <b>keyingi 📦 AAB build</b>'da qo'llanadi (tayyor AAB'ga ta'sir qilmaydi).</span>
              </div>
              {/* ⚠️ Yozilgan nom TAYYOR AAB'dagidan farq qilsa — yuborishdan oldin ogohlantiramiz,
                  aks holda «1.0.1 yozdim, lekin 1.0 chiqdi» holati jimgina takrorlanadi. */}
              {versionName.trim() && data.android.aab?.versionName && versionName.trim() !== data.android.aab.versionName && (
                <div className="mt-2 text-[11px] rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-2 py-1.5">
                  ⚠️ Tayyor AAB'da <b>versionName {data.android.aab.versionName}</b> turibdi, siz esa <b>{versionName.trim()}</b> yozdingiz.
                  Yangi nom chiqishi uchun <b>📦 AAB build</b> ni qayta bosing — hozir yuborilsa <b>{data.android.aab.versionName}</b> ketadi.
                </div>
              )}
            </div>

            {/* 📚 AAB BUILD TARIXI — iOS build ro'yxatiga o'xshash: qaysi versiya, qachon,
                qaysi API bilan. Oxirgi 3 tasi saqlanadi (fayli bilan), shuning uchun
                eskisini ham yuklab olish yoki Play'ga yuborish mumkin. */}
            {(data.android.aabHistory || []).length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">📚 So'nggi AAB build'lar <span className="font-normal text-gray-400">(oxirgi 3 tasi saqlanadi)</span></div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                  {data.android.aabHistory.map((b, i) => {
                    const [ecls, elabel] = envBadge(b.env);
                    const inPlay = (data.android.play?.tracks || []).some(t =>
                      (t.releases || []).some(r => (r.versionCodes || []).map(Number).includes(Number(b.versionCode))));
                    const isLatest = i === 0;
                    return (
                      <div key={b.versionCode} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${isLatest ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                          {isLatest ? '🆕 So\'nggi' : 'Eski'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
                            versionCode {b.versionCode}{b.versionName ? ` · v${b.versionName}` : ''}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ecls}`}
                              title={b.url ? `API: ${b.url}` : 'Endpoint yozib olinmagan'}>{elabel}</span>
                            {inPlay && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400" title="Bu versiya Google Play'da bor">🤖 Play'da</span>}
                          </div>
                          <div className="text-xs text-gray-400">
                            {b.at ? new Date(b.at).toLocaleString('uz') : ''}{b.sizeBytes ? ` · ${Math.round(b.sizeBytes / 1048576)} MB` : ''}
                          </div>
                        </div>
                        {b.hasFile ? (
                          <>
                            <button onClick={() => downloadAab(b.versionCode)}
                              className="px-2 py-1 text-xs text-primary-700 dark:text-primary-500 hover:underline shrink-0">📥 .aab</button>
                            {data.android.play?.configured && !inPlay && (
                              <button onClick={() => submitToPlay(b.versionCode)} disabled={busy === 'play'}
                                title={`v${b.versionCode} ni «${playTrack}» trekiga yuborish`}
                                className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 font-medium shrink-0">🚀 Yuborish</button>
                            )}
                          </>
                        ) : (
                          <span className="text-[11px] text-gray-400 shrink-0" title="Fayl arxivdan chiqarilgan (faqat oxirgi 3 ta saqlanadi)">fayl yo'q</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 🚀 Play'ga yuborish — trek tanlab, bir tugma bilan */}
            {data.android.aab?.builtAt && (
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <select value={playTrack} onChange={e => pickTrack(e.target.value)}
                  title="Qaysi trekka chiqsin — internal = darhol sinovchilarga, production = jonli foydalanuvchilarga (ko'rib chiqishdan keyin)"
                  className={`px-2 py-2 text-xs border rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 ${playTrack === 'production' ? 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 font-semibold' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                  <option value="internal">👥 Internal testing</option>
                  <option value="alpha">🔬 Closed testing (alpha)</option>
                  <option value="beta">🧪 Open testing (beta)</option>
                  <option value="production">🚀 Production</option>
                </select>
                <button onClick={() => submitToPlay()} disabled={busy === 'play' || !data.android.play?.configured}
                  title={!data.android.play?.configured ? 'Avval Play kalitini ulang' : "AAB'ni Play'ga yuklaydi (1-3 daqiqa)"}
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 font-semibold">
                  {busy === 'play' ? '⏳ Yuborilmoqda...' : "🚀 Play'ga yuborish"}
                </button>
                {/* AAB qaysi API bilan qurilgan — Play'ga TEST build ketmasin */}
                {(() => {
                  const [ecls, elabel] = envBadge(data.android.aab.env);
                  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ecls}`}
                    title={data.android.aab.url ? `API: ${data.android.aab.url}` : 'AAB build vaqtidagi endpoint yozib olinmagan (eski AAB)'}>{elabel}</span>;
                })()}
                <span className="text-[11px] text-gray-400">
                  {Math.round((data.android.aab.sizeBytes || 0) / 1048576)} MB · {new Date(data.android.aab.builtAt).toLocaleString('uz')}
                </span>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              «📦 AAB build» so'nggi kodni oladi → <b>🚀 production API</b> bilan <b>imzolangan .aab</b> quradi
              (~5 daqiqa, versionCode avtomatik oshadi) → «🚀 Play'ga yuborish» bilan to'g'ridan-to'g'ri Play'ga
              chiqadi (yoki 📥 yuklab olib qo'lda yuklaysiz).
              Skrinshot / Data safety / ko'rib chiqishga yuborish — Play Console'da.
            </p>
            <div className="mt-2 text-xs text-gray-400 flex flex-wrap gap-x-3">
              {data.android.package && <span>📦 <span className="font-mono">{data.android.package}</span></span>}
              {data.android.alias && <span>🔑 kalit: {data.android.alias}</span>}
              {data.android.play?.clientEmail && <span title="Play API service account">🤖 {data.android.play.clientEmail}</span>}
            </div>
            {playLog && (
              <details className="mt-2" open><summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">🚀 Play yuborish log</summary>
                <pre className="mt-1 text-[11px] bg-gray-900 text-gray-100 rounded-lg p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">{playLog}</pre></details>
            )}
          </>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            🔐 Android imzolash kaliti (keystore) hali sozlanmagan. Modul sahifasi → <b>📦 AAB (Google Play)</b> kartasida
            keystore yarating yoki mavjud <b>upload key</b>'ingizni yuklang — so'ng shu yerda «📦 AAB build» paydo bo'ladi.
          </div>
        )}
      </div>
      </div>
      </>)}

      {/* 🚀 Production serverga deploy — avval faqat admin → modul Overview'da bor edi;
          endi Studio'dan chiqmasdan deploy qilinadi (backend/web/website modullari). */}
      {(data?.prodModules || []).length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2 text-gray-900 dark:text-white font-semibold text-lg">🚀 Production serverga deploy</div>
            <button onClick={loadProdStatus} disabled={pstatLoading}
              title="Serverlarning HOZIRGI holatini qayta o'qish (prod/test commit + ishlayaptimi)"
              className="px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 font-medium shrink-0">
              {pstatLoading ? '⏳ Tekshirilmoqda...' : '↻ Holatni yangilash'}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
            Har modulning o'z prod serveriga so'nggi kodni chiqaradi (modul sozlamalaridagi deploy buyrug'i ishlaydi).
            <b className="text-red-600 dark:text-red-400"> Jonli foydalanuvchilarga darhol ta'sir qiladi.</b>
            {!pstat && !pstatLoading && <> — «↻ Holatni yangilash» bilan prod/test farqini va serverlar ishlayotganini ko'ring.</>}
          </p>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
            {data.prodModules.map(m => {
              const st = pinfo(m.id);
              // HAQIQIY manba = git main. 'current' = prod main bilan bir xil (deploy
              // shart emas), 'behind' = main'da yangi kod bor. 'same/diff' = eski
              // (test-env bilan) solishtirish — main SHA olinmagan holat uchun zaxira.
              const same = st && (st.state === 'current' || st.state === 'same');
              const diff = st && (st.state === 'behind' || st.state === 'diff');
              const alive = st?.health ? st.health.ok : null;
              return (
                <div key={m.id} className="px-3 py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 shrink-0">
                      {m.module_type === 'backend' ? '⚙️' : '🌐'} {m.module_type}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-700 dark:text-gray-300 truncate flex items-center gap-2">
                        {m.name}
                        {/* Prod tirikmi — deploy buzmaganini ko'rsatadi */}
                        {alive === true && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400">🟢 Ishlayapti</span>}
                        {alive === false && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400">🔴 Ishlamayapti ({st.health.code || 'ulanmadi'})</span>}
                      </div>
                      {m.production_url && <a href={m.production_url} target="_blank" rel="noreferrer"
                        className="text-xs text-primary-600 dark:text-primary-500 hover:underline truncate block">{m.production_url}</a>}
                    </div>
                    <button onClick={() => prodDeploy(m)} disabled={busy === 'prod:' + m.id}
                      title={same ? 'Prod allaqachon test bilan bir xil — deploy shart emas' : 'Prod serverga so\'nggi kodni chiqarish'}
                      className={`px-3 py-1.5 text-xs rounded-lg font-semibold shrink-0 disabled:opacity-40 ${same ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                      {busy === 'prod:' + m.id ? '⏳ Deploy...' : (same ? '↻ Qayta deploy' : '🚀 Deploy')}
                    </button>
                  </div>
                  {/* 🔎 TEST vs PROD — takror deploy qilmaslik uchun */}
                  {st && (
                    <div className="mt-1.5 pl-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
                      <div className="text-gray-500 dark:text-gray-400">
                        🧪 <b>Test</b>: {st.test
                          ? <><code className="text-gray-700 dark:text-gray-300">{st.test.commit}</code> · {st.test.when}</>
                          : <span className="text-gray-400">test muhit yo'q</span>}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        🚀 <b>Prod</b>: {st.prod
                          ? <><code className="text-gray-700 dark:text-gray-300">{st.prod.commit}</code> · {st.prod.when}</>
                          : <span className="text-gray-400">aniqlanmadi</span>}
                      </div>
                      {/* Manba = git {branch}. Test-env ORTDA bo'lishi mumkin — u faqat
                          alohida redeploy qilinganda yangilanadi, shuning uchun "deploy
                          kerakmi" savoliga MAIN javob beradi, test emas. */}
                      <div className="text-gray-500 dark:text-gray-400">
                        📦 <b>{st.branch || 'main'}</b>: {st.mainSha
                          ? <code className="text-gray-700 dark:text-gray-300">{st.mainSha}</code>
                          : <span className="text-gray-400">o'qib bo'lmadi</span>}
                      </div>
                      <div className="sm:col-span-2">
                        {/* 🔴 Git checkout yangi bo'lishi "nashr bo'ldi" degani EMAS: deploy skripti
                            nusxalash/build qadamida yiqilsa checkout yangi, tarqatilayotgan fayl esa
                            ESKI qoladi (2026-08-31, viloyatga frontend). Shuning uchun oxirgi deploy
                            XATO bilan tugagan bo'lsa yashil xabar o'rniga OGOHLANTIRISH chiqadi. */}
                        {same && st.lastDeploy && st.lastDeploy.ok === false && (
                          <span className="text-red-700 dark:text-red-400 font-medium">
                            ❌ Kod tortilgan, lekin oxirgi deploy XATO bilan tugagan ({st.lastDeploy.note || 'xato'})
                            — jonli sayt ESKI versiyada bo'lishi mumkin. «↻ Qayta deploy» bosing.
                          </span>
                        )}
                        {same && !(st.lastDeploy && st.lastDeploy.ok === false) && <span className="text-green-700 dark:text-green-400 font-medium">✅ Prod eng so'nggi kodda ({st.branch || 'main'} bilan bir xil) — deploy shart emas</span>}
                        {diff && <span className="text-amber-700 dark:text-amber-400 font-medium">⚠️ {st.branch || 'main'}'da yangi kod bor — deploy kerak</span>}
                        {st.state === 'no_test' && <span className="text-gray-400">Solishtirib bo'lmadi</span>}
                        {st.test && st.prod && st.test.commit !== st.prod.commit &&
                          <span className="text-gray-400"> · 🧪 test-env ortda (faqat ma'lumot uchun)</span>}
                        {st.prod && <span className="text-gray-400 truncate"> · {st.prod.subject}</span>}
                        {st.lastDeploy && (
                          <button onClick={() => loadDeployLog(m)}
                            title="Oxirgi deploy buyrug'ining to'liq chiqishi (saqlangan)"
                            className="ml-2 text-[11px] text-primary-700 dark:text-primary-500 hover:text-primary-900 dark:hover:text-primary-200">
                            📜 Oxirgi deploy logi {dlogFor === m.id ? '▲' : '▼'}
                          </button>
                        )}
                        {dlogFor === m.id && (
                          dlog === null ? <div className="text-xs text-gray-400 mt-1">Yuklanmoqda…</div>
                          : !dlog.log ? <div className="text-xs text-gray-400 mt-1">Log saqlanmagan — keyingi deploydan boshlab yoziladi.</div>
                          : <pre className="mt-1 text-[11px] bg-gray-900 text-gray-100 rounded-md p-2 max-h-72 overflow-auto whitespace-pre-wrap">{dlog.log}</pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// 📍 Bu mobil ilova qaysi test-nishonda TO'LIQ / QISMAN ishlaydi — ilovaning
// native imkoniyatlariga (xarita/push/kamera/GPS) qarab. Backend deps'dan
// aniqlaydi (GET /project-modules/:id/mobile/compat).
function EmuCompatCard({ authFetch, apiUrl, moduleId, moduleName }) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;
  React.useEffect(() => { setData(null); }, [moduleId]);   // loyiha almashsa qayta yuklaydi
  React.useEffect(() => {
    if (!moduleId || !open || data) return;
    setLoading(true);
    authFetchRef.current(`${apiUrl}/project-modules/${moduleId}/mobile/compat`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.ok && !d.notMobile) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [moduleId, open, data, apiUrl]);
  if (!moduleId) return null;
  return (
    <div className="mb-3 border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-sky-800 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/60 transition">
        <span>📍 Bu ilova qayerda ishlaydi?{moduleName ? ` — ${moduleName}` : ''}</span>
        <span className="text-sky-500 text-xs">{open ? '▲ Yopish' : '▼ Ko\'rish'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3">
          {loading ? <div className="text-xs text-gray-500 dark:text-gray-400 py-2">Yuklanmoqda…</div>
            : !data ? <div className="text-xs text-gray-500 dark:text-gray-400 py-2">Ma'lumot yo'q</div>
              : (
                <>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Ilova ishlatadigan native imkoniyatlar:{' '}
                    {data.usedFeatures && data.usedFeatures.length
                      ? <b className="text-gray-900 dark:text-white">{data.usedFeatures.join(' · ')}</b>
                      : <span className="text-gray-400">yo'q (oddiy ilova — hamma joyda to'liq)</span>}
                  </div>
                  <div className="space-y-1.5">
                    {data.targets.map(t => (
                      <div key={t.key} className={`rounded-lg px-3 py-2 border ${t.status === 'full' ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-900 dark:text-white">{t.icon} {t.label} {t.liveReload && <span className="text-[10px] text-primary-600 dark:text-primary-500 font-semibold">⚡ jonli reload</span>}</span>
                          <span className={`text-xs font-bold whitespace-nowrap ${t.status === 'full' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>{t.badge}</span>
                        </div>
                        {t.problems && t.problems.length > 0 && (
                          <ul className="mt-1 text-[11px] text-amber-800 dark:text-amber-300 list-disc list-inside space-y-0.5">
                            {t.problems.map((p, i) => <li key={i}>{p.note || `${p.feature} — ${p.level === 'no' ? 'ishlamaydi' : 'cheklangan'}`}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-2">💡 To'liq sinash: Google Play (GMS) emulatori, EAS Dev build yoki TestFlight. Expo Go + oddiy emulator — tez UI sinovi uchun.</div>
                </>
              )}
        </div>
      )}
    </div>
  );
}

// 📋 Expo Go LOGLARI — iPhone'dagi ilovaning JS xato/ogohlantirishlari Metro orqali serverga keladi
// (GET /expo/logs). Screenshot'siz: xatoni belgilab «🐞 Vazifa qilish» — log matni bilan vazifa.
function ExpoLogsPanel({ authFetch, apiUrl, projectId, apiTarget }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [showAll, setShowAll] = React.useState(false);
  const [showRaw, setShowRaw] = React.useState(false);
  const [openIdx, setOpenIdx] = React.useState(null);
  const [picked, setPicked] = React.useState({});
  const [draft, setDraft] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await authFetchRef.current(`${apiUrl}/expo/logs?projectId=${projectId}`);
      const d = await r.json().catch(() => null);
      if (!r.ok || !d?.ok) throw new Error(d?.error || 'Log yuklanmadi');
      setData(d);
      const cur = (d.issues || []).filter(i => i.session === d.sessions);
      const def = {};
      // Faqat ILOVA xatolari avtomatik belgilanadi — Expo/Metro ichki xatosi va Expo Go cheklovi vazifa emas
      // Vazifasi bor xato qayta belgilanmaydi — faqat vazifadan keyin yana chiqqan bo'lsa (🔁)
      cur.forEach((i) => { if (i.level === 'error' && (i.origin || 'app') === 'app' && (!i.task || i.recurred)) def[i.level + '|' + i.head] = true; });
      setPicked(def);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  React.useEffect(() => { if (projectId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId]);

  const issues = (data?.issues || []).filter(i => showAll || i.session === data.sessions);
  const keyOf = (i) => i.level + '|' + i.head;
  const isApp = (i) => (i.origin || 'app') === 'app';
  const chosen = issues.filter(i => isApp(i) && picked[keyOf(i)]);
  const ORIGIN = {
    expo: { t: '⚙️ Expo ichki xatosi', c: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
    expogo: { t: '📵 Expo Go cheklovi', c: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  };
  const nonApp = issues.filter(i => !isApp(i)).length;
  const LV = {
    error: { t: 'XATO', c: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    warn: { t: 'OGOHLANTIRISH', c: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
    crash: { t: 'SERVER YIQILDI', c: 'bg-gray-800 text-white dark:bg-gray-600' },
  };
  const apiTxt = apiTarget === 'production' ? 'production API' : apiTarget === 'test' ? 'test API' : 'API koddagidek';

  const makeDraft = () => {
    const parts = chosen.map((i, n) => {
      const body = (i.body || '').split('\n').slice(0, 14).join('\n');
      return `${n + 1}) ${i.level === 'warn' ? 'WARN' : i.level === 'crash' ? 'METRO' : 'ERROR'} ×${i.count}: ${i.head}${body ? '\n' + body : ''}`;
    });
    setDraft(`Mobil ilovani iPhone'da (Expo Go, ${apiTxt}) sinashda quyidagi xato(lar) chiqdi — sababini toping va tuzating. ` +
      `Faqat Expo Go'ga xos bo'lsa (native modul yo'qligi va h.k.) buni hisobotda ayting.\n\n${parts.join('\n\n')}\n\n` +
      `(Manba: Metro logi, Khorezmai 📱 Expo kartasi, ${new Date().toLocaleString('uz-UZ')})`);
  };
  const sendTask = async () => {
    if (!draft || !draft.trim()) return;
    setSending(true);
    try {
      const r = await authFetchRef.current(`${apiUrl}/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: Number(projectId), instruction: draft.trim(), task_type: 'developer' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.message || 'Vazifa yaratilmadi');
      toast.success(`🐞 Vazifa yaratildi${d.id ? ' — #' + d.id : ''}`);
      setDraft(null);
      // xato ↔ vazifa bog'lanishi — sahifa yangilansa ham qayta «vazifa qil» deb turmasin
      if (d.id) {
        try {
          await authFetchRef.current(`${apiUrl}/expo/logs/task-link`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectId: Number(projectId), taskId: d.id,
              issues: chosen.map(i => ({ level: i.level, head: i.head, session: i.session, count: i.count })) }),
          });
        } catch {}
        load();
      }
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  return (
    <div className="mt-3 border-t border-primary-100 dark:border-primary-800 pt-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white">📋 Loglar</span>
        <span className="text-xs text-gray-400">iPhone'dagi ilova xatolari (Metro){data?.updatedAt ? ` · oxirgi yozuv ${new Date(data.updatedAt).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={load} disabled={loading} className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 disabled:opacity-50">{loading ? '⏳' : '↻'} Yangilash</button>
          <button onClick={() => setShowAll(v => !v)} className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300">{showAll ? 'Faqat joriy sessiya' : 'Oldingi sessiyalar ham'}</button>
          <button onClick={() => setShowRaw(v => !v)} className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300">{showRaw ? 'Xatolar ro’yxati' : 'Xom log'}</button>
        </div>
      </div>
      {err && <div className="text-xs text-red-600 dark:text-red-400 mb-2">{err}</div>}
      {(data?.events || []).filter(e => showAll || e.session === data.sessions).slice(-3).map((e, k) => (
        <div key={k} className="text-xs text-gray-500 dark:text-gray-400">🐕 {e.text}</div>
      ))}
      {showRaw ? (
        <pre className="text-[11px] leading-snug bg-gray-900 text-gray-100 rounded-lg p-2 max-h-80 overflow-auto whitespace-pre-wrap break-all" translate="no">{data?.tail || ''}</pre>
      ) : !data ? null : issues.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 py-1">✅ Joriy sessiyada xato yoki ogohlantirish yo'q.</div>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-auto">
          {nonApp > 0 && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400">⚙️/📵 belgili yozuvlar ({nonApp}) ilova kodining xatosi emas — Expo serverining o'zi yoki Expo Go cheklovi; ular vazifaga qo'shilmaydi.</div>
          )}
          {issues.map((i, idx) => {
            const k = keyOf(i); const lv = LV[i.level] || LV.error;
            return (
              <div key={k + idx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5">
                <div className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" disabled={!isApp(i)} checked={isApp(i) && !!picked[k]}
                    title={isApp(i) ? "Vazifaga qo'shish" : "Bu ilova kodining xatosi emas — vazifaga qo'shilmaydi"}
                    onChange={e => setPicked(p => ({ ...p, [k]: e.target.checked }))} />
                  <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${lv.c}`}>{lv.t}{i.count > 1 ? ` ×${i.count}` : ''}</span>
                  {ORIGIN[i.origin] && <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${ORIGIN[i.origin].c}`}>{ORIGIN[i.origin].t}</span>}
                  {i.task && (
                    <span title={`Bu xatodan ${new Date(i.task.at).toLocaleString('uz-UZ')} da vazifa berilgan`}
                      className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                      🐞 #{i.task.id} · {({ completed: 'bajarildi', in_progress: 'ishlanmoqda', pending: 'navbatda', pending_architecture: 'navbatda', failed: 'yiqildi', awaiting_clarification: 'savol bor', awaiting_plan_approval: 'reja tasdig\u2019i', awaiting_owner_approval: 'ruxsat kutilmoqda' })[i.task.status] || i.task.status || '—'}
                    </span>
                  )}
                  {i.task && i.recurred && (
                    <span title="Vazifa berilgandan keyin bu xato yana chiqdi" className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">🔁 yana chiqdi</span>
                  )}
                  <button onClick={() => setOpenIdx(openIdx === idx ? null : idx)} className={`text-left text-xs break-all min-w-0 ${isApp(i) ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`} translate="no">{i.head}</button>
                </div>
                {i.originNote && <div className="ml-6 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">ℹ️ {i.originNote}</div>}
                {openIdx === idx && i.body && (
                  <pre className="mt-1 text-[11px] leading-snug bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded p-2 max-h-60 overflow-auto whitespace-pre-wrap break-all" translate="no">{i.body}</pre>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!showRaw && issues.length > 0 && !draft && (
        <button onClick={makeDraft} disabled={!chosen.length}
          title="Belgilangan xatolar log matni bilan vazifaga aylanadi — yuborishdan oldin matnni tahrirlashingiz mumkin"
          className="mt-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">🐞 Vazifa qilish ({chosen.length})</button>
      )}
      {draft !== null && (
        <div className="mt-2 space-y-2">
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={10}
            className="w-full text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200" />
          <div className="flex gap-2">
            <button onClick={sendTask} disabled={sending || !draft.trim()} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">{sending ? '⏳ Yuborilmoqda...' : '🐞 Vazifani yuborish'}</button>
            <button onClick={() => setDraft(null)} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300">Bekor qilish</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpoQrCard({ authFetch, apiUrl, projectId, apiTarget = 'code' }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [info, setInfo] = React.useState(null); // {running, url, svg}
  const [starting, setStarting] = React.useState(false);
  const pollRef = React.useRef(null);
  // GOTCHA: authFetch App har render'ida yangi identity oladi — effect unga dep bo'lsa
  // cheksiz refetch bo'ladi (hujjatlashtirilgan App.js gotcha'si). Ref orqali o'qiymiz.
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;

  // Metro ko'tarilguncha statusni poll qilish (birinchi provision ~2-3 daqiqa → 36×5s)
  const beginPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await authFetchRef.current(`${apiUrl}/expo/tunnel-status?projectId=${projectId}`);
        const dd = await r.json().catch(() => null);
        if (r.ok && dd?.running) {
          setInfo(dd); setStarting(false);
          clearInterval(pollRef.current); pollRef.current = null;
        }
      } catch {}
      if (tries > 36) { setStarting(false); clearInterval(pollRef.current); pollRef.current = null; }
    }, 5000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetchRef.current(`${apiUrl}/expo/tunnel-status?projectId=${projectId}`);
      const d = await res.json().catch(() => null);
      if (res.ok && d) {
        setInfo(d);
        if (d.starting && !d.running) beginPoll();
      }
    } catch {} finally { setLoading(false); }
  };

  React.useEffect(() => {
    setInfo(null);
    if (open && projectId) load();
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  // 🔗 apiTarget — 📱 Emulatorlar paneldagi «API» tanlovi. keepRunning: Metro'ni o'chirmasdan faqat
  // sync + API almashtirish (Fast Refresh ochiq Expo Go'ga yetkazadi, QR o'zgarmaydi).
  const [applying, setApplying] = React.useState(false);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const startMetro = async (keepRunning = false) => {
    if (keepRunning) setApplying(true); else setStarting(true);
    try {
      const res = await authFetchRef.current(`${apiUrl}/expo/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), apiTarget, keepRunning }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || 'start_failed');
      if (keepRunning) {
        toast.success("🔗 API almashtirilmoqda — Expo Go o'zi yangilanadi (~20 soniya)");
        setTimeout(load, 15000); setTimeout(() => { load(); setApplying(false); }, 30000);
      } else beginPoll();
    } catch (e) {
      setStarting(false); setApplying(false);
      if (e && e.message && e.message !== 'start_failed') toast.error(e.message);
    }
  };
  const apiLabel = (t) => t === 'production' ? '🚀 production' : t === 'test' ? '🧪 test env' : '🔗 koddagidek';
  const liveUrls = info?.apiUrls || [];
  const liveIsTest = liveUrls.some(u => /\.test\.khorezmai\.dev/.test(u));
  const apiMismatch = info?.running && (info.apiTarget || 'code') !== apiTarget;

  // QR SVG'ga aniq o'lcham beramiz (qrcode faqat viewBox chiqaradi)
  const qrHtml = info?.svg ? info.svg.replace('<svg ', '<svg width="168" height="168" ') : '';

  return (
    <div className="mb-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 rounded-lg px-3 py-2">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-sm text-primary-900 dark:text-primary-200 font-medium">
        <span>📱 iPhone'da sinash (Expo Go)</span>
        <span className="text-primary-500 text-xs">{open ? '▲ Yopish' : '▼ Ochish'}</span>
      </button>
      {open && (
        <div className="mt-2 pb-1">
          {loading && !info && <div className="text-sm text-gray-400 py-3">⏳ Tekshirilmoqda...</div>}
          {info?.running && info.svg ? (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-primary-100 dark:border-primary-800 shrink-0" dangerouslySetInnerHTML={{ __html: qrHtml }} />
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 min-w-0">
                <div className="font-medium text-gray-900 dark:text-white">iPhone'dagi oddiy Kamera ilovasi bilan skanerlang → «Open in Expo Go»</div>
                <div className="font-mono text-xs bg-white dark:bg-gray-800 border border-primary-100 dark:border-primary-800 rounded px-2 py-1 break-all select-all">{info.url}</div>
                {liveUrls.length > 0 && (
                  <div className={`text-xs rounded px-2 py-1 border ${liveIsTest ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300' : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'}`}>
                    {liveIsTest ? '🧪 Ilova TEST backendga ulanadi' : '🚀 Ilova PRODUCTION backendga ulanadi'}: <span className="font-mono break-all" translate="no">{liveUrls.join(', ')}</span>
                  </div>
                )}
                {apiMismatch && (
                  <div className="text-xs text-amber-700 dark:text-amber-400">
                    Panelda «{apiLabel(apiTarget)}» tanlangan, Expo esa «{apiLabel(info.apiTarget || 'code')}» bilan ishlayapti.
                  </div>
                )}
                <div className="text-xs text-gray-400">iPhone'da App Store'dan Expo Go o'rnatilgan bo'lsin. Birinchi yuklanish 30–60 soniya. QR ishlamasa URL'ni Expo Go'ga qo'lda kiriting. Metro qayta ishga tushsa URL o'zgaradi — ↻ bosing.</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={load} disabled={loading} className="px-2.5 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 font-medium">↻ Yangilash</button>
                  {apiMismatch && (
                    <button onClick={() => startMetro(true)} disabled={applying}
                      title="Metro'ni o'chirmasdan ilovani panelda tanlangan backendga ulaydi — QR o'zgarmaydi, ochiq Expo Go o'zi yangilanadi"
                      className="px-2.5 py-1 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium">{applying ? '⏳ Almashtirilmoqda...' : "🔗 API'ni qo'llash"}</button>
                  )}
                </div>
              </div>
            </div>
          ) : info?.noMobile ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-1">Bu loyihada mobil modul yo'q — Expo Go sinovi faqat mobil modulli loyihalar uchun.</div>
          ) : info && !info.running ? (
            <div className="flex items-center gap-3 py-1 flex-wrap">
              <span className="text-sm text-gray-500 dark:text-gray-400">{(starting || info.starting) ? "⏳ Expo server ishga tushmoqda — yangi loyihada birinchi marta ~2-3 daqiqa (QR avtomatik chiqadi)..." : "Bu loyiha uchun Expo dev-server hozir ishlamayapti."}</span>
              {!(starting || info.starting) && (
                <button onClick={() => startMetro(false)} title={`Expo dev-server'ni ishga tushiradi — ilova «${apiLabel(apiTarget)}» backendga ulanadi (📱 Emulatorlar paneldagi API tanlovi)`} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">▶ Ishga tushirish · {apiLabel(apiTarget)}</button>
              )}
            </div>
          ) : null}
          {info && !info.noMobile && (
            <div className="mt-2">
              <button onClick={() => setLogsOpen(v => !v)}
                title="iPhone'dagi Expo Go ilovasining xato va ogohlantirishlari (Metro logi) — screenshot shart emas"
                className="px-2.5 py-1 text-xs border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/40 font-medium">{logsOpen ? '▲ Loglarni yopish' : '📋 Loglar'}</button>
              {logsOpen && <ExpoLogsPanel authFetch={authFetch} apiUrl={apiUrl} projectId={projectId} apiTarget={info.apiTarget || apiTarget} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 📝 REJA TASDIG'I kartasi — vazifa «📝 Reja tasdig'i» bilan berilgan bo'lsa engine
// kod yozishdan OLDIN «qaysi fayl / nima / nega» rejasini tuzib to'xtaydi. Bu yerda
// operator uni o'qiydi va ✅ tasdiqlaydi yoki ✏️ izoh bilan qaytaradi. Tasdiqlangan
// reja keyin ham (📝 Tasdiqlangan reja) yig'ilgan holda ko'rinadi.
function PlanReviewCard({ authFetch, apiUrl, task, onChanged, onStop, compact = false }) {
  const awaiting = task.status === 'awaiting_plan_approval';
  const [open, setOpen] = React.useState(awaiting);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [reviseOpen, setReviseOpen] = React.useState(false);
  const [feedback, setFeedback] = React.useState('');
  const round = task.plan_round || 0;
  React.useEffect(() => { if (awaiting) setOpen(true); }, [awaiting, round]);
  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    setErr('');
    authFetch(`${apiUrl}/tasks/${task.id}/plan`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!alive) return; if (!ok) setErr(d.error || 'Reja yuklanmadi'); else setData(d); })
      .catch(() => { if (alive) setErr('Reja yuklanmadi — tarmoq xatosi'); });
    return () => { alive = false; };
    // authFetch har render'da yangi — unga bog'lanmaymiz (cheksiz qayta yuklash bo'lardi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task.id, round, apiUrl]);

  const post = async (kind, body) => {
    setBusy(kind);
    try {
      const res = await authFetch(`${apiUrl}/tasks/${task.id}/plan/${kind}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        // ⚠️ Reja tuzilmagan (#2759) — server force'siz tasdiqlamaydi; karta eski bo'lsa ham shu yerda so'raymiz.
        if (kind === 'approve' && d.needsForce && !(body && body.force)) {
          setBusy('');
          if (confirmNoPlan(d.failReason)) return post('approve', { force: true });
          return;
        }
        throw new Error(d.error || 'Server xatosi');
      }
      if (kind === 'approve') toast.success(body && body.force ? "⚠️ Rejasiz tasdiqlandi — qism vazifalar matni bilan ishlanadi" : "✅ Reja tasdiqlandi — kod yozish boshlanadi");
      else { toast.success(body && body.feedback ? "✏️ Izoh yuborildi — reja qayta tuziladi" : "🔁 Reja qayta tuzilmoqda"); setFeedback(''); setReviseOpen(false); }
      setTimeout(() => onChanged && onChanged(), 1000);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(''); }
  };
  const confirmNoPlan = (reason) => window.confirm(
    `Batafsil reja TUZILMAGAN${reason ? ` (${String(reason).replace(/^Batafsil reja tuzilmadi:\s*/i, '')})` : ''}.\n\n`
    + "Dasturchi qaysi fayl / nima / nega rejasisiz — faqat qism vazifa matni bilan ishlaydi.\n\n"
    + "Baribir REJASIZ tasdiqlaysizmi? (Bekor → «🔁 Rejani qayta tuzish» tavsiya etiladi)");

  if (!awaiting && !open) {
    return (
      <div className="mt-2">
        <button type="button" onClick={() => setOpen(true)}
          className="text-[11px] text-primary-700 dark:text-primary-400 hover:underline">📝 Tasdiqlangan reja ▸</button>
      </div>
    );
  }
  const plan = data && data.plan;
  const failed = !!(plan && plan.failed);
  const modName = (id) => {
    const m = ((data && data.modules) || []).find(x => String(x.id) === String(id));
    if (!m) return `Modul #${id}`;
    const ic = m.module_type === 'backend' ? '⚙️' : m.module_type === 'mobile' ? '📱' : '🌐';
    return `${ic} ${m.name}`;
  };
  const ACT = {
    modify: { t: "✏️ o'zgaradi", c: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' },
    create: { t: '➕ yangi', c: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' },
    delete: { t: "🗑 o'chadi", c: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300' },
  };
  const List = ({ icon, title, items, tone = 'text-gray-700 dark:text-gray-300' }) => (items && items.length) ? (
    <div className="mt-2">
      <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">{icon} {title}</div>
      <ul className={`text-xs ${tone} list-disc ml-5 space-y-0.5`}>{items.map((x, i) => <li key={i} translate="no">{x}</li>)}</ul>
    </div>
  ) : null;
  const nFiles = plan ? (plan.modules || []).reduce((a, m) => a + ((m.changes || []).length), 0) : 0;

  return (
    <div className={`mt-3 border-t pt-3 ${awaiting ? 'border-indigo-100 dark:border-indigo-800' : 'border-gray-100 dark:border-gray-700'}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className={`text-xs font-semibold ${awaiting && failed ? 'text-amber-700 dark:text-amber-300' : awaiting ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300'}`}>
          {awaiting && failed ? '⚠️ Reja TUZILMADI — avval qayta tuzdiring (kod hali yozilmagan)' : awaiting ? "📝 O'zgarish rejasi — tasdiqlang (kod hali yozilmagan)" : failed ? '⚠️ Rejasiz tasdiqlangan' : '📝 Tasdiqlangan reja'}
          {round > 1 && <span className="ml-1.5 font-normal text-gray-400">· {round}-variant</span>}
          {plan && nFiles > 0 && <span className="ml-1.5 font-normal text-gray-400">· {nFiles} ta fayl</span>}
        </div>
        {!awaiting && <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">▾ yopish</button>}
      </div>
      {err && <div className="text-xs text-red-600 dark:text-red-400">{err}</div>}
      {!err && !data && <div className="text-xs text-gray-400">⏳ Reja yuklanmoqda…</div>}
      {data && !plan && <div className="text-xs text-gray-400">Reja saqlanmagan.</div>}
      {plan && (
        <div className={compact ? 'max-h-[28rem] overflow-y-auto pr-1' : ''}>
          {plan.failed && (
            <div className="mb-2 text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-2 border-amber-300 dark:border-amber-700 rounded-lg px-2.5 py-2">
              <div className="font-semibold">⚠️ {plan.reason || "Batafsil reja tuzilmadi"}</div>
              <div className="mt-0.5">Fayl darajasidagi reja (qaysi fayl / nima / nega) YO'Q — quyida faqat arxitektor bo'lgan qism vazifalar matni.
                {awaiting ? " Avval «🔁 Rejani qayta tuzish»ni bosing (izohsiz ham bo'ladi). Rejasiz davom etmoqchi bo'lsangiz — «⚠️ Rejasiz tasdiqlash»." : ''}</div>
            </div>
          )}
          {plan.summary && <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap" translate="no">{plan.summary}</div>}
          <List icon="👤" title="Foydalanuvchi nimani ko'radi" items={plan.userVisible} />
          {(plan.modules || []).map((m, mi) => (
            <div key={mi} className="mt-2.5 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="px-2.5 py-1.5 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-700 dark:text-gray-200">{modName(m.module_id)}</div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {(m.changes || []).map((c, ci) => {
                  const a = ACT[c.action] || ACT.modify;
                  return (
                    <div key={ci} className="px-2.5 py-1.5 text-xs">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.c}`}>{a.t}</span>
                        <code className="font-mono text-[11px] text-gray-800 dark:text-gray-200 break-all" translate="no">{c.file}</code>
                      </div>
                      {c.what && <div className="mt-0.5 text-gray-700 dark:text-gray-300" translate="no">{c.what}</div>}
                      {c.why && <div className="mt-0.5 text-gray-500 dark:text-gray-400" translate="no"><span className="font-semibold">Nega:</span> {c.why}</div>}
                    </div>
                  );
                })}
                {m.db && <div className="px-2.5 py-1.5 text-xs text-gray-700 dark:text-gray-300" translate="no">🗄 <span className="font-semibold">DB:</span> {m.db}</div>}
                {m.api && <div className="px-2.5 py-1.5 text-xs text-gray-700 dark:text-gray-300" translate="no">🔌 <span className="font-semibold">API:</span> {m.api}</div>}
                {m.note && <div className="px-2.5 py-1.5 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap" translate="no">{m.note}</div>}
              </div>
            </div>
          ))}
          <List icon="⚠️" title="Xavflar" items={plan.risks} />
          <List icon="🧪" title="Qanday tekshiriladi" items={plan.verify} />
          <List icon="🔒" title="Tegilmaydi" items={plan.notTouched} />
          {plan.openQuestions && plan.openQuestions.length > 0 && (
            <div className="mt-2 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-0.5">❓ Hal qilinmagan savollar — javobni ✏️ izohda yozing</div>
              <ul className="list-disc ml-5 text-amber-900 dark:text-amber-200 space-y-0.5">{plan.openQuestions.map((q, i) => <li key={i} translate="no">{q}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      {awaiting && (
        <div className="mt-3">
          <div className="flex items-center gap-2 flex-wrap">
            {failed ? (<>
              <button type="button" onClick={() => post('revise', {})} disabled={!!busy}
                title="Arxitektor rejani qaytadan tuzadi (izoh shart emas)"
                className="px-3 py-1.5 text-xs font-semibold bg-primary-700 text-white rounded-lg hover:bg-primary-800 disabled:opacity-50 transition">
                {busy === 'revise' ? '⏳ Yuborilmoqda…' : '🔁 Rejani qayta tuzish'}</button>
              <button type="button" onClick={() => { if (confirmNoPlan(plan.reason)) post('approve', { force: true }); }} disabled={!!busy}
                title="Batafsil rejasiz — dasturchi faqat qism vazifa matni bilan ishlaydi"
                className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 transition">
                {busy === 'approve' ? '⏳ Tasdiqlanmoqda…' : '⚠️ Rejasiz tasdiqlash'}</button>
            </>) : (
              <button type="button" onClick={() => post('approve')} disabled={!!busy || !plan}
                className="px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition">
                {busy === 'approve' ? '⏳ Tasdiqlanmoqda…' : '✅ Rejani tasdiqlash'}</button>
            )}
            <button type="button" onClick={() => setReviseOpen(v => !v)} disabled={!!busy}
              className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition">
              ✏️ Izoh bilan qaytarish</button>
            {onStop && (
              <button type="button" onClick={onStop} disabled={!!busy}
                className="px-3 py-1.5 text-xs font-semibold bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 disabled:opacity-50 transition">
                ⏹ Bekor qilish</button>
            )}
          </div>
          {reviseOpen && (
            <div className="mt-2">
              <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3}
                placeholder="Nimani o'zgartirish kerak? Masalan: «2-faylga tegma», «yangi jadval emas, mavjudiga ustun qo'sh», savollarga javob…"
                className="w-full text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              <button type="button" onClick={() => post('revise', { feedback })} disabled={!!busy || !feedback.trim()}
                className="mt-1.5 px-3 py-1.5 text-xs font-semibold bg-primary-700 text-white rounded-lg hover:bg-primary-800 disabled:opacity-50 transition">
                {busy === 'revise' ? '⏳ Yuborilmoqda…' : '📨 Rejani qayta tuzish'}</button>
            </div>
          )}
          <div className="mt-1.5 text-[10px] text-gray-400">Tasdiqlanmaguncha hech qanday kod yozilmaydi. {failed ? "Rejasiz tasdiqlasangiz developer agent faqat qism vazifa matni bilan ishlaydi." : "Tasdiqlasangiz developer agent aynan shu reja bo'yicha ishlaydi."}</div>
        </div>
      )}
    </div>
  );
}

// 📁 «Faqat tanlangan loyiha» — tanlov brauzerda eslab qolinadi (flyout va to'liq sahifa bitta kalit).
// 📡 Navbatchi o'z kaliti bilan (`kh_duty_project`), sukut bo'yicha YOQIQ (operator, 2026-10-30: «Idora'da turibman,
// Navbatchi TezHal'ni ko'rsatyapti»).
function useProcProjectOnly(key = 'kh_proc_project', def = false) {
  const [on, setOnRaw] = React.useState(() => { try { const v = localStorage.getItem(key); return v === null ? def : v === '1'; } catch (e) { return def; } });
  const setOn = React.useCallback((v) => { setOnRaw(v); try { localStorage.setItem(key, v ? '1' : '0'); } catch (e) {} }, [key]);
  return [on, setOn];
}

// Vazifa formasidagi «📝 Reja tasdig'i» / «🚀 Avto-deploy» chiplari uslubida (operator, 2026-09-25:
// «nimaga oddiy checkbox qo'yding»). Loyiha tanlanmagan bo'lsa o'chiq turadi.
function ProcProjectChip({ on, setOn, project, tip = null }) {
  const has = !!(project && project.id);
  const active = on && has;
  const cls = active
    ? 'bg-primary-700 dark:bg-primary-600 border-primary-700 text-white'
    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700';
  return (
    <button type="button" disabled={!has} onClick={() => setOn(!on)}
      title={has ? (tip || `Yoqilsa — faqat tanlangan loyiha (${project.name || ''}) vazifalari va fon jarayonlari ko'rinadi`) : 'Avval tepadan loyihani tanlang'}
      className={`inline-flex items-center gap-1 max-w-[13rem] px-2 py-1 rounded-lg border text-[11px] font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}>
      <span className="shrink-0">📁</span>
      <span className="truncate">Faqat {has && project.name ? <span translate="no">{project.name}</span> : 'tanlangan loyiha'}</span>
      {active && <span className="shrink-0">✓</span>}
    </button>
  );
}

// 🖼 Rasmni katta ko'rish (operator, 2026-10-06: «vazifaga paste qilingan rasmni katta qilib ko'ra olmayapman»).
// Formadagi paste/yuklangan rasmlar va vazifa oynasidagi rasmlar uchun BITTA popup: ← → varaqlash,
// Esc / fonni bosish yopadi, ↗ yangi oynada ochadi. z-[95] — vazifa oynasi (z-[80]) va panel ustida.
function ImageLightbox({ images = [], index = 0, onClose }) {
  const [i, setI] = React.useState(index);
  const n = images.length;
  const go = React.useCallback((d) => setI(x => (x + d + n) % n), [n]);
  const closeRef = React.useRef(onClose); closeRef.current = onClose;
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); }
      else if (e.key === 'ArrowRight' && n > 1) { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft' && n > 1) { e.preventDefault(); go(-1); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [n, go]);
  if (!n) return null;
  const idx = Math.min(i, n - 1);
  const src = images[idx];
  const btn = 'w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white text-xl leading-none flex items-center justify-center transition';
  return (
    <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <img src={src} alt={`Rasm ${idx + 1}`} onClick={(e) => e.stopPropagation()}
        className="max-w-[94vw] max-h-[86vh] object-contain rounded-lg shadow-2xl" />
      <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {n > 1 && <span className="text-white/80 text-sm mr-1">{idx + 1} / {n}</span>}
        <a href={src} target="_blank" rel="noopener noreferrer" title="Yangi oynada ochish" className={btn}>↗</a>
        <button type="button" onClick={onClose} title="Yopish (Esc)" className={btn}>✕</button>
      </div>
      {n > 1 && (
        <>
          <button type="button" title="Oldingi (←)" onClick={(e) => { e.stopPropagation(); go(-1); }}
            className={`absolute left-4 top-1/2 -translate-y-1/2 ${btn}`}>‹</button>
          <button type="button" title="Keyingi (→)" onClick={(e) => { e.stopPropagation(); go(1); }}
            className={`absolute right-4 top-1/2 -translate-y-1/2 ${btn}`}>›</button>
        </>
      )}
    </div>
  );
}

function ProcessesPanel({ authFetch, apiUrl, tasks, refreshTasks: refreshTasksProp, compact = false, onClose = null,
                          onLoadMore = null, hasMore = false, loadingMore = false, onGoConsole = null,
                          refreshSignal = null, selectedProject = null,
                          projOnly: projOnlyProp = null, setProjOnly: setProjOnlyProp = null,
                          onAgentTest = null, agentTestOk = null }) {
  const [bgJobs, setBgJobs] = React.useState([]);
  const [busy, setBusy] = React.useState({}); // 't{id}' / 'j{id}' -> true (so'rov ketmoqda)
  // Qaysi qism vazifa (subtask) to'liq matni ochilgan — ProcessesPanel'da saqlanadi
  // (kartalar inline render qilinadi — poll'da remount bo'lmaydi, ochiq holat saqlanadi).
  const [openSubs, setOpenSubs] = React.useState({}); // subtask id -> ochiqmi
  const toggleSub = (id) => setOpenSubs(s => ({ ...s, [id]: !s[id] }));
  // 🐞 Topilmalar bo'limi (ochiq/yopiq brauzerda eslab qolinadi; sarlavhada ochiq topilmalar soni).
  const [findOpen, setFindOpenRaw] = React.useState(() => { try { return localStorage.getItem('kh_proc_find') !== '0'; } catch (e) { return true; } });
  const setFindOpen = (v) => { setFindOpenRaw(v); try { localStorage.setItem('kh_proc_find', v ? '1' : '0'); } catch (e) {} };
  const [findCount, setFindCount] = React.useState(null);
  React.useEffect(() => { setFindCount(null); }, [selectedProject && selectedProject.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // 👥 «Meniki» — jamoaviy loyihada faqat O'ZIM bergan vazifalar (tanlov eslab qolinadi).
  const myId = (() => { try { return Number(JSON.parse(localStorage.getItem('khorezmai_user') || '{}').id) || 0; } catch (e) { return 0; } })();
  const [mineOnly, setMineOnlyRaw] = React.useState(() => { try { return localStorage.getItem('kh_proc_mine') === '1'; } catch (e) { return false; } });
  const setMineOnly = (v) => { setMineOnlyRaw(v); try { localStorage.setItem('kh_proc_mine', v ? '1' : '0'); } catch (e) {} };
  // 📁 «Faqat tanlangan loyiha vazifalari» (operator, 2026-09-25) — tepadagi loyiha tanlagichga
  // ergashadi; tanlov brauzerda eslab qolinadi. Loyiha tanlanmagan bo'lsa filtr ishlamaydi.
  // Flyout'da holat YUQORIDA (sarlavhadagi chip ↻ yonida) — prop bilan keladi; to'liq sahifada o'zida.
  const [projOnlyOwn, setProjOnlyOwn] = useProcProjectOnly();
  const projOnly = projOnlyProp === null ? projOnlyOwn : projOnlyProp;
  const setProjOnly = setProjOnlyProp || setProjOnlyOwn;
  const projFilterOn = projOnly && !!(selectedProject && selectedProject.id);
  const projFilterId = projFilterOn ? selectedProject.id : null;
  const inProject = (x) => !projFilterOn || String(x.project_id) === String(selectedProject.id);
  // Filtr SERVERDA (`/tasks?project_id=`): App oxirgi 10 tani BARCHA loyihalardan oladi — klientda
  // filtrlansa tanlangan loyiha vazifalari o'sha 10 ga kirmay «yo'q» chiqardi (operator: «3 varaqlamaguncha
  // yo'q deb chiqardi», 2026-09-25). Shuning uchun panel o'z ro'yxatini loyiha bo'yicha yuklaydi.
  const [projTasks, setProjTasks] = React.useState(null);      // null = hali yuklanmagan
  const [projLoadingMore, setProjLoadingMore] = React.useState(false);
  const projLimitRef = React.useRef(10);
  const projIdRef = React.useRef(null);
  projIdRef.current = projFilterId;
  const loadProjTasks = React.useCallback((more = false) => {
    const pid = projIdRef.current;
    if (!pid) return Promise.resolve();
    if (more) { projLimitRef.current += 10; setProjLoadingMore(true); }
    const lim = projLimitRef.current;
    return authFetch(`${apiUrl}/tasks?limit=${lim}&project_id=${encodeURIComponent(pid)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (Array.isArray(d) && projIdRef.current === pid) setProjTasks(d); })
      .catch(() => {})
      .finally(() => { if (more) setProjLoadingMore(false); });
  }, [authFetch, apiUrl]);
  const loadProjRef = React.useRef(loadProjTasks);
  loadProjRef.current = loadProjTasks;
  React.useEffect(() => {
    projLimitRef.current = 10;
    setProjTasks(null);
    if (projFilterId) loadProjRef.current();
  }, [projFilterId]);
  // Panel ichidagi har «yangila» (stop/approve/↻/poll) ikkala ro'yxatni ham yangilaydi.
  const refreshTasks = () => { if (refreshTasksProp) refreshTasksProp(); if (projIdRef.current) loadProjRef.current(); };
  const [approvingId, setApprovingId] = React.useState(null); // merge+redeploy ketayotgan super-task
  const [approveStartedAt, setApproveStartedAt] = React.useState(null); // spinner qotib qolmasligi uchun
  const [clarAnswers, setClarAnswers] = React.useState({}); // {taskId: {qIdx: answer}}
  const [clarSubmitting, setClarSubmitting] = React.useState(null);
  // 🧪 Sinash — QAYSI emulator(lar)da sinalsin. Ilgari nishonni server o'zi tanlardi
  // (vazifa tegan modul + biriktiruv), shuning uchun ikki rolli oqimni kartadan sinab
  // bo'lmasdi va operator qaysi qurilma ishlatilishini oldindan bilmasdi.
  const [verifyPick, setVerifyPick] = React.useState(null); // {taskId, emus[], slots[], loading}
  // ⏸ Engine o'z token cheklovida to'xtab turibdimi (global + shaxsiy) — banner uchun.
  const [enginePause, setEnginePause] = React.useState(null);
  // 📄 Vazifa to'liq matni popup'da (sarlavha qisqartirilgan — bosilsa to'lig'i + nusxa).
  const [detailTask, setDetailTask] = React.useState(null);
  const [imgViewer, setImgViewer] = React.useState(null);   // 🖼 {images, index} — katta ko'rish
  const [detailCopied, setDetailCopied] = React.useState(false);
  const copyDetail = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); setDetailCopied(true); setTimeout(() => setDetailCopied(false), 1600); }
    catch {
      try { const ta = document.createElement('textarea'); ta.value = text || ''; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); setDetailCopied(true); setTimeout(() => setDetailCopied(false), 1600); } catch {}
    }
  };

  const parseQuestions = (t) => {
    try { const q = JSON.parse(t.clarification_questions || '[]'); return Array.isArray(q) ? q : []; }
    catch { return []; }
  };
  // Transient failures (API event-loop freeze, brief nginx congestion, a 502/503) made a single
  // POST fail with "Failed to fetch" or a 5xx → the user had to click "Javob berish" 5-6 times.
  // Retry the request internally a few times with backoff so ONE click reliably lands. Safe: the
  // mutation endpoints (clarify/stop/resume) are idempotent server-side. authFetch handles 401 by
  // redirecting, so a network throw here is a genuine transient error worth retrying.
  const retryFetch = async (url, opts, tries = 4) => {
    let lastErr;
    for (let i = 0; i < tries; i++) {
      try {
        const res = await authFetch(url, opts);
        if (res.ok || ![429, 502, 503, 504].includes(res.status)) return res; // done (ok or a non-transient error)
        lastErr = new Error("Server hozir band — qayta urinilyapti");
      } catch (e) { lastErr = e; } // network error ("Failed to fetch")
      if (i < tries - 1) await new Promise(r => setTimeout(r, 500 * (i + 1))); // 0.5s / 1s / 1.5s
    }
    throw lastErr;
  };

  const submitClarification = async (t) => {
    const qs = parseQuestions(t);
    const answers = qs.map((_, i) => (clarAnswers[t.id]?.[i] || '').trim());
    if (answers.every(a => !a)) { toast.error('Kamida bitta javob kiriting'); return; }
    setClarSubmitting(t.id);
    try {
      const res = await retryFetch(`${apiUrl}/tasks/${t.id}/clarify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || (res.status === 503 || res.status === 429 ? "Server hozir band — biroz kuting va qayta urining" : 'Server xatosi'));
      toast.success('✅ Javob yuborildi — vazifa davom etadi');
      setClarAnswers(s => { const c = { ...s }; delete c[t.id]; return c; });
      setTimeout(refreshTasks, 1200);
    } catch (e) { toast.error('Xato: ' + (e.message || 'yuborilmadi') + ' — matn saqlanib qoldi, qayta bosing'); }
    finally { setClarSubmitting(null); }
  };

  const loadJobs = React.useCallback(() => {
    // GOTCHA — don't wipe bgJobs on a transient bad poll → it caused the logo/TZ cards
    // to flicker in & out ("bir ko'rinib bir ko'rinmay"). Under engine load some /jobs/active
    // polls return 500/502/timeout; the old code set bgJobs=[] on any non-ok/non-array
    // response, blanking the "Yaqinda tugaganlar" list, then the next OK poll refilled it.
    // Fix: only replace the list on a VALID array (a legit empty [] still clears); keep the
    // previous list on any HTTP error / non-array / network error.
    authFetch(`${apiUrl}/jobs/active`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (Array.isArray(d)) setBgJobs(d); })
      .catch(() => {});
  }, [authFetch, apiUrl]);
  // ↻ flyout sarlavhasida (✕ yonida) — bosilganda signal oshadi, panel shu yerda yangilanadi.
  const refreshFnsRef = React.useRef(null);
  refreshFnsRef.current = { loadJobs, refreshTasks };
  React.useEffect(() => {
    if (!refreshSignal) return;
    const f = refreshFnsRef.current;
    f.loadJobs(); if (f.refreshTasks) f.refreshTasks();
  }, [refreshSignal]);

  // authFetch/refreshTasks har App render'da yangi identity oladi — effekt ularga bog'lansa
  // har render'da qayta o'rnatilib DARHOL qo'shimcha poll otadi (refetch-storm — PublishPanel'dagi
  // authFetchRef gotcha'sining aynan o'zi). Ref orqali doim so'nggi funksiyalar ishlatiladi.
  const loadPause = React.useCallback(() => {
    authFetch(`${apiUrl}/engine/pause-state`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.paused === 'boolean') setEnginePause(d); })
      .catch(() => {});
  }, [authFetch, apiUrl]);
  const pollFnsRef = React.useRef({ loadJobs, refreshTasks, loadPause });
  pollFnsRef.current = { loadJobs, refreshTasks, loadPause };

  React.useEffect(() => {
    let n = 0;
    const run = () => {
      const f = pollFnsRef.current; f.loadJobs(); if (f.refreshTasks) { try { f.refreshTasks(); } catch {} }
      if (n++ % 4 === 0) f.loadPause();   // ~28s — server 20s keshlaydi, tez o'zgarmaydi
    };
    run();
    const t = setInterval(() => { if (khPageVisible()) run(); }, 7000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopTask = async (id) => {
    if (!window.confirm(`Vazifa #${id} to'xtatilsinmi? Bajarilayotgan agent jarayoni o'ldiriladi va o'zgarishlar bekor qilinadi (push/deploy bo'lmaydi).`)) return;
    setBusy(s => ({ ...s, ['t' + id]: true }));
    try {
      const res = await retryFetch(`${apiUrl}/tasks/${id}/stop`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || (res.status === 503 || res.status === 429 ? "Server hozir band — qayta urinib ko'ring" : 'Server xatosi'));
      toast.success(`⏹ To'xtatish so'raldi (vazifa #${id})`);
      setTimeout(refreshTasks, 1500);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['t' + id]: false })); }
  };

  // ▶ Davom ettirish — yiqilgan (masalan Claude limiti tugab qolgan) vazifani qayta
  // navbatga qo'yadi: super-task'ning failed/cancelled bolalari 'pending'ga qaytadi,
  // engine ularni avtomatik oladi. Yarim qilingan ish saqlanadi (workspace + sessiya).
  // Yiqilgan sub-task'dan KEYIN ayni modul+turdagi sub-task (masalan QA qayta tekshiruvi) bo'lsa —
  // u eskirgan: tugma chiqmaydi, backend ham uni qayta yurgizmaydi (#1623: QA yiqildi → fix tugadi,
  // lekin «Davom ettirish» chiqib turardi va operator tasdiqlashini bilmay qolardi).
  const isSuperseded = (st, all) => all.some(o => o.id > st.id && o.module_id === st.module_id
    && o.task_type === st.task_type && !['failed', 'cancelled'].includes(o.status));
  const canResume = (t) => !t.approved_at && (
    ['failed', 'cancelled'].includes(t.status)
    || (Array.isArray(t.sub_tasks) && t.sub_tasks.some(st => ['failed', 'cancelled'].includes(st.status) && !isSuperseded(st, t.sub_tasks)))
  );
  const resumeTask = async (id) => {
    if (!window.confirm(`Vazifa #${id} davom ettirilsinmi? Yiqilgan qism vazifalar qayta navbatga qo'yiladi (yarim qilingan ish saqlanadi).`)) return;
    setBusy(s => ({ ...s, ['t' + id]: true }));
    try {
      const res = await retryFetch(`${apiUrl}/tasks/${id}/resume`, { method: 'POST' });
      // authFetch faqat 401'da throw qiladi; 503 (nginx rate-limit) / 5xx HTML sahifa qaytaradi,
      // shuning uchun res.json() ni himoyalab chaqiramiz — aks holda "Unexpected token '<'".
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || (res.status === 503 || res.status === 429
        ? "Server hozir band (ko'p so'rov) — bir lahzadan keyin qayta bosing" : 'Server xatosi'));
      toast.success(`▶ ${d.resumed} ta vazifa qayta navbatga qo'yildi`);
      setTimeout(refreshTasks, 1500);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['t' + id]: false })); }
  };

  // 🗑 Vazifani butunlay o'chirish (adashib berilgan / to'xtatilgan / tugagan). Faqat ro'yxat
  // yozuvini o'chiradi — deploy qilingan kod o'zgarmaydi. in_progress bo'lsa backend rad etadi.
  // 📚 Tugagan vazifadan RETSEPT ajratib Tajriba bazasiga saqlash (AI hisobotdan).
  const addToKnowledge = async (id) => {
    if (!window.confirm(`Vazifa #${id} yechimi Tajriba bazasiga retsept qilib saqlansinmi?\n\nAI hisobotdan muammo→yechim ajratadi (bir necha soniya). Keyin engine shunday vazifalarda uni o'zi ishlatadi.`)) return;
    setBusy(s => ({ ...s, ['k' + id]: true }));
    try {
      const res = await authFetch(`${apiUrl}/knowledge/from-task/${id}`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Server xatosi');
      if (d.saved) toast.success(`📚 Retsept saqlandi: «${d.entry.title}»`);
      else toast(`ℹ️ ${d.reason || 'Saqlanmadi'}${d.proposal && d.proposal.title ? ' — ' + d.proposal.title : ''}`);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['k' + id]: false })); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm(`Vazifa #${id} butunlay o'chirilsinmi?\n\nBu faqat ro'yxatdagi yozuvni o'chiradi (deploy qilingan kod o'zgarmaydi). Bekor qilib bo'lmaydi.`)) return;
    setBusy(s => ({ ...s, ['t' + id]: true }));
    try {
      const res = await authFetch(`${apiUrl}/tasks/${id}`, { method: 'DELETE' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || (res.status === 503 || res.status === 429 ? "Server hozir band — qayta urinib ko'ring" : 'Server xatosi'));
      toast.success(`🗑 Vazifa #${id} o'chirildi`);
      setTimeout(refreshTasks, 700);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['t' + id]: false })); }
  };

  // ✅/❌ TEKSHIRUV BELGISI — operator o'zi qo'yadi. Bir xil belgini qayta bossa
  // olib tashlanadi (uchinchi holat = belgilanmagan). Belgi bazada saqlanadi.
  const setVerify = async (id, status) => {
    setBusy(s => ({ ...s, ['v' + id]: true }));
    try {
      const res = await authFetch(`${apiUrl}/tasks/${id}/verify`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || (res.status === 503 || res.status === 429 ? "Server hozir band — qayta urinib ko'ring" : 'Server xatosi'));
      toast.success(status === 'ok' ? '✅ To\'g\'ri deb belgilandi' : status === 'bad' ? '❌ Noto\'g\'ri deb belgilandi' : 'Belgi olib tashlandi');
      setTimeout(refreshTasks, 400);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['v' + id]: false })); }
  };

  // 🧪 SINASH — agent ilovada tekshirib, natijaga qarab O'ZI ✅/❌ belgilaydi.
  // 🧪 bosilganda — avval qurilma tanlash oynasi (emulator bo'lmasa to'g'ridan-to'g'ri).
  const openVerifyPick = async (id) => {
    setVerifyPick({ taskId: id, emus: [], slots: [], loading: true });
    try {
      const res = await authFetch(`${apiUrl}/emulator/list`);
      const d = await res.json().catch(() => ({}));
      const emus = (res.ok && Array.isArray(d.emulators)) ? d.emulators : [];
      // Standart tanlov — hozir ISHLAB TURGANLARI (operator aynan ularga qarab turibdi).
      const running = emus.filter(e => e.status === 'running').map(e => Number(e.slot));
      setVerifyPick({ taskId: id, emus, slots: running, loading: false });
    } catch (e) {
      setVerifyPick({ taskId: id, emus: [], slots: [], loading: false });
    }
  };

  const verifyRun = async (id, slots) => {
    setBusy(s => ({ ...s, ['v' + id]: true }));
    setVerifyPick(null);
    try {
      const res = await authFetch(`${apiUrl}/tasks/${id}/verify-run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.isArray(slots) && slots.length ? { slots } : {}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Server xatosi');
      toast.success('🧪 Agent sinovi boshlandi — tugagach belgi qo\'yiladi');
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['v' + id]: false })); }
  };

  const cancelJob = async (id) => {
    if (!window.confirm(`Jarayon #${id} to'xtatilsinmi? Tayyor bo'lgan natijalar saqlanib qoladi.`)) return;
    setBusy(s => ({ ...s, ['j' + id]: true }));
    try {
      const res = await authFetch(`${apiUrl}/jobs/${id}/cancel`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || (res.status === 503 || res.status === 429 ? "Server hozir band — qayta urinib ko'ring" : 'Server xatosi'));
      toast.success("⏹ To'xtatish so'raldi");
      setTimeout(loadJobs, 1200);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(s => ({ ...s, ['j' + id]: false })); }
  };

  // Tasdiqlash (merge + redeploy) — MobilePanel'dagi bilan bir xil mantiq. Faqat
  // hamma qism vazifa tugagan (kamida bittasi 'completed') va hali tasdiqlanmagan
  // super-task uchun ko'rinadi. POST progress'i new_log'da oqadi; approved_at o'rnatilgach
  // spinner o'chadi (tasks poll'i 7s + 1.5s setTimeout orqali yangilanadi).
  const canApprove = (t) => !t.approved_at && Array.isArray(t.sub_tasks) && t.sub_tasks.length > 0
    && t.sub_tasks.every(st => ['completed', 'failed', 'cancelled'].includes(st.status))
    && t.sub_tasks.some(st => st.status === 'completed');

  // QAYTA tasdiqlash (force) — qisman merge bo'lgan vazifa uchun. approved_at merge'dan
  // darhol keyin yoziladi, shuning uchun bitta modul yiqilsa vazifa "tasdiqlangan" bo'lib
  // qolardi va uni tugatishning yo'li YO'Q edi (#800: mobil merge 405 bilan yiqildi).
  const canReapprove = (t) => ['partial', 'failed', 'interrupted'].includes(t.deploy_stage) && !isTaskActive(t);

  // 🔁 Nazoratchi: «tugagach tasdiqlab sina» / «endi sina» (cancel=true — bekor qilish).
  const afterTestTask = async (id, cancel = false) => {
    let scenario = null;
    if (!cancel) {
      scenario = window.prompt(`Vazifa #${id}: nimani sinash kerak?\n\nBo'sh qoldirsangiz — vazifa matnidagi har band sinaladi. Khorezmai tasdiqlaydi (hali tasdiqlanmagan bo'lsa), deploy/APK tugashini kutadi, sinaydi; kamchilik chiqsa tuzatish vazifasini ochib qayta sinaydi.`, '');
      if (scenario === null) return;
    }
    setBusy(b => ({ ...b, ['at' + id]: true }));
    try {
      const res = await authFetch(`${apiUrl}/tasks/${id}/after-test`, {
        method: cancel ? 'DELETE' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: cancel ? undefined : JSON.stringify({ scenario: scenario || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) throw new Error(d.error || 'Server xatosi');
      toast.success(cancel ? 'Bekor qilindi' : (d.note ? `Belgilandi — ${d.note}` : 'Belgilandi: tasdiq/deploydan keyin Nazoratchi sinaydi'));
      setTimeout(refreshTasks, 800);
    } catch (e) { toast.error('Xato: ' + (e.message || '')); }
    finally { setBusy(b => ({ ...b, ['at' + id]: false })); }
  };

  // ❓ Nazoratchi savoliga shu yerda javob (2026-10-31, operator: «Telegram'da so'ragan narsa Khorezmai'ning o'zida yo'q»).
  // Ilgari kartada faqat qizil belgi bor edi — savol matni va javob faqat Telegram / telefon panelida edi.
  const [svOpen, setSvOpen] = useState(null);     // savoli ochiq vazifa id
  const [testOpen, setTestOpen] = useState({});   // 🧪 sinov xulosasi to'liq ochilgan kartalar
  const [svAns, setSvAns] = useState({});
  // 🔁 «Sina» → 🧠 Loyiha agenti (2026-11-02, operator: «menga agent sinashi yoqdi — topilmalar topadi, oxirida hisobot beradi,
  // topilmadan keyin yana sinaydi; Sina bosilganda agentga yozuv bo'lib tushsin; ovozli, fonda, videoni Telegram'ga — tanlansin»).
  // Agent vazifani o'qib to'liq ssenariy yozadi va supervisor_test {task_id} bilan Nazoratchining o'sha «tugagach tasdiqlab sina»
  // elementini ochadi (kartadagi holat chipi o'zgarmaydi), natijalar agent suhbatiga keladi (backend supervisor.reportToAgent).
  const [sinaOpen, setSinaOpen] = useState(null);   // tanlovlar paneli ochiq vazifa id
  const [sinaNote, setSinaNote] = useState('');
  const [sinaOpts, setSinaOptsRaw] = useState(() => { try { return JSON.parse(localStorage.getItem('kh_sina_opts') || '{}') || {}; } catch (e) { return {}; } });
  const toggleSinaOpt = (k) => setSinaOptsRaw(o => { const n = { ...o, [k]: !o[k] }; try { localStorage.setItem('kh_sina_opts', JSON.stringify(n)); } catch (e) { } return n; });
  const sinaText = (t) => {
    const first = String(t.instruction || '').split('\n').map(x => x.trim()).find(Boolean) || '';
    const subs = (t.sub_tasks || []).map(st => `#${st.id}${st.module_name ? ` ${st.module_name}` : ''}`);
    const chips = [sinaOpts.narrate && '🔊 ovozli (narrate:true)', sinaOpts.record && "🎥 video → Telegram, sinov o'tsa (record:true)", sinaOpts.ux && '🎨 qulaylik (UX) ham (ux:true)'].filter(Boolean);
    const state = isTaskActive(t) ? 'hali bajarilmoqda — tugagach tasdiqlab sinash' : t.approved_at ? 'tasdiqlangan, test muhitiga chiqqan' : 'tugagan, hali tasdiqlanmagan — tasdiqlab sinash';
    return [
      `[🔁 SINOV SO'ROVI — vazifa #${t.id}] «${first.slice(0, 200)}${first.length > 200 ? '…' : ''}»`,
      `Holat: ${state}`,
      subs.length ? `Qism vazifalar: ${subs.join(', ')}` : '',
      `Tanlovlar: ${chips.length ? chips.join(' · ') : "yo'q (ovozsiz, videosiz)"}`,
      sinaNote.trim() ? `Operator izohi: ${sinaNote.trim()}` : '',
      `Shu vazifani sinang: o'zgargan oqimni boshidan oxirigacha (rol, kirish, qadamlar, nima tekshiriladi) ssenariy qilib yozing va supervisor_test({task_id: ${t.id}, ...tanlovlar}) bilan bering. Topilma bo'lsa tuzatilib qayta sinaladi — har natijada menga qisqa hisobot bering.`,
    ].filter(Boolean).join('\n');
  };
  const sendSina = async (t) => {
    if (!onAgentTest) return;
    setBusy(b => ({ ...b, ['at' + t.id]: true }));
    try {
      const ok = await onAgentTest(t, sinaText(t), !!sinaOpts.bg);
      if (ok) { setSinaOpen(null); setSinaNote(''); }
    } finally { setBusy(b => ({ ...b, ['at' + t.id]: false })); }
  };
  const svAct = async (taskId, itemId, action, body = {}) => {
    setBusy(b => ({ ...b, ['sv' + taskId]: true }));
    try {
      const res = await authFetch(`${apiUrl}/supervisor/items/${itemId}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) throw new Error(d.error || 'Server xatosi');
      toast.success(action === 'skip' ? "⏭ O'tkazib yuborildi" : '✍️ Javob qabul qilindi — sinov davom etadi');
      setSvOpen(null); setSvAns(a => ({ ...a, [taskId]: '' }));
      setTimeout(refreshTasks, 800);
    } catch (e) { toast.error('Xato: ' + (e.message || '')); }
    finally { setBusy(b => ({ ...b, ['sv' + taskId]: false })); }
  };

  const approveTask = async (id, force = false) => {
    const msg = force
      ? `Vazifa #${id} QAYTA tasdiqlansinmi?\n\nMerge bo'lmagan modul(lar) qaytadan merge qilinadi (allaqachon merge bo'lganlarга tegilmaydi) va deploy takrorlanadi.`
      : `Vazifa #${id} tasdiqlansinmi?\n\nReview branch (kh/task-${id}) asosiy branchga merge qilinadi va test-muhit qayta deploy qilinadi.`;
    if (!window.confirm(msg)) return;
    setApprovingId(id);
    setApproveStartedAt(Date.now());
    toast('Merge + redeploy boshlandi…', { icon: '🚀' });
    try {
      const res = await authFetch(`${apiUrl}/tasks/${id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) throw new Error(d.error || 'Server xatosi');
      setTimeout(refreshTasks, 1500);
    } catch (e) { toast.error('Tasdiqlashda xato: ' + (e.message || '')); setApprovingId(null); setApproveStartedAt(null); }
  };
  // approved_at o'rnatilgach spinnerni tozala (server merge+deploy ni tugatdi)
  React.useEffect(() => {
    if (approvingId != null) {
      const t = (tasks || []).find(x => String(x.id) === String(approvingId));
      if (t && t.approved_at) { setApprovingId(null); setApproveStartedAt(null); toast.success('Deploy qilindi 🚀'); }
    }
  }, [tasks, approvingId]);
  // ⏳ QOTIB QOLISHDAN HIMOYA: yuqoridagi effekt FAQAT muvaffaqiyatni (approved_at)
  // kutadi — merge konflikt bersa yoki server xato qaytarsa u hech qachon kelmaydi va
  // tugma abadiy "⏳ Merge…" bo'lib qolardi (faqat sahifani yangilash yordam berardi).
  // Endi belgilangan vaqtdan keyin spinner o'zi tozalanadi va aniq xabar beriladi.
  // Cheklov mobil APK build'iga ham yetadi (odatda 4-8 daqiqa).
  React.useEffect(() => {
    if (approvingId == null || !approveStartedAt) return;
    const MAX_MS = 12 * 60 * 1000;
    const iv = setInterval(() => {
      if (Date.now() - approveStartedAt > MAX_MS) {
        setApprovingId(null);
        setApproveStartedAt(null);
        toast.error('Tasdiqlash yakunlanmadi (merge konflikti yoki server xatosi bo\'lishi mumkin) — log\'ni ko\'ring va qayta urinib ko\'ring', { duration: 8000 });
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [approvingId, approveStartedAt]);

  const sColors = {
    pending_architecture: 'bg-purple-200 dark:bg-purple-800/60 text-purple-900 dark:text-purple-200',
    pending: 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-900 dark:text-yellow-200',
    completed: 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-200',
    in_progress: 'bg-blue-200 dark:bg-blue-800/60 text-blue-900 dark:text-blue-200',
    failed: 'bg-red-200 dark:bg-red-800/60 text-red-900 dark:text-red-200',
    cancelled: 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300',
    awaiting_clarification: 'bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-200',
    awaiting_plan_approval: 'bg-indigo-200 dark:bg-indigo-800/60 text-indigo-900 dark:text-indigo-200',
    awaiting_owner_approval: 'bg-violet-200 dark:bg-violet-800/60 text-violet-900 dark:text-violet-200',
  };
  const sLabels = {
    pending_architecture: '🧠 Rejalashtirilmoqda',
    pending: '⏳ Kutilmoqda',
    completed: '✅ Bajarildi',
    in_progress: '🔄 Jarayonda',
    failed: '❌ Xatolik',
    cancelled: "⏹ To'xtatilgan",
    awaiting_clarification: '❓ Aniqlik kerak',
    awaiting_plan_approval: '📝 Reja tasdiq kutmoqda',
    awaiting_owner_approval: '🙋 Ruxsat kutmoqda',
  };
  const ACTIVE = ['pending', 'pending_architecture', 'in_progress', 'awaiting_clarification', 'awaiting_plan_approval', 'awaiting_owner_approval'];
  const isTaskActive = (t) => ACTIVE.includes(t.status) || (t.sub_tasks || []).some(st => ACTIVE.includes(st.status));
  // O'chirish mumkin — AYNAN hozir bajarilmayotgan bo'lsa (in_progress emas). Pending/cancelled/
  // failed/completed hammasi o'chiriladi (backend ham faqat in_progress'ni himoya qiladi).
  const canDelete = (t) => t.status !== 'in_progress' && !(t.sub_tasks || []).some(st => st.status === 'in_progress');
  const baseTasks = projFilterOn ? (projTasks || []) : (tasks || []);
  const projLoading = projFilterOn && projTasks === null;
  const hasTeamTasks = baseTasks.some(t => t.created_by && Number(t.created_by) !== myId);
  const scopedTasks = ((mineOnly && hasTeamTasks) ? baseTasks.filter(t => Number(t.created_by) === myId) : baseTasks).filter(inProject);
  const effLoadMore = projFilterOn ? () => loadProjTasks(true) : onLoadMore;
  const effHasMore = projFilterOn ? (projTasks || []).length >= projLimitRef.current : hasMore;
  const effLoadingMore = projFilterOn ? projLoadingMore : loadingMore;
  const activeTasks = scopedTasks.filter(isTaskActive);
  // Qirqim YUKLANGAN oynaga moslashadi: App `tasksLimit` bilan cheklab oladi (10 tadan
  // o'sadi), shuning uchun bu yerda qayta qirqish faqat zarar qilardi — operator
  // "Ko'proq yuklash" bossa ham ro'yxat 15/20 da qotib turardi.
  const doneTasks = scopedTasks.filter(t => !isTaskActive(t));
  const isJobActive = (j) => j.status === 'running' || j.status === 'cancelling';
  const activeJobs = bgJobs.filter(j => isJobActive(j) && inProject(j));
  const doneJobs = bgJobs.filter(j => !isJobActive(j) && inProject(j)).slice(0, 10);
  // "Yaqinda tugaganlar" — job'lar va task'larni VAQT bo'yicha aralashtirib saralaymiz
  // (avval doneJobs hammasi tepada, keyin doneTasks edi → eski logo'lar yangi task'dan tepada chiqardi)
  const doneMerged = [
    ...doneJobs.map(j => ({ kind: 'job', item: j, t: new Date(j.created_at).getTime() || 0 })),
    ...doneTasks.map(t => ({ kind: 'task', item: t, t: new Date(t.created_at).getTime() || 0 })),
  ].sort((a, b) => b.t - a.t);

  const jobIcon = (t) => (t === 'logo' ? '🏷️' : t === 'design_logo' ? '🖼️' : t === 'tz' ? '📐' : t === 'keyfix' ? '🔑' : '⚙️');
  const jStatus = {
    running: ['🔄 Bajarilmoqda', 'bg-blue-200 dark:bg-blue-800/60 text-blue-900 dark:text-blue-200'],
    cancelling: ["⏹ To'xtatilmoqda...", 'bg-orange-200 dark:bg-orange-800/60 text-orange-900 dark:text-orange-200'],
    done: ['✅ Tugadi', 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-200'],
    error: ['❌ Xato', 'bg-red-200 dark:bg-red-800/60 text-red-900 dark:text-red-200'],
    cancelled: ["⏹ To'xtatilgan", 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'],
  };
  const fmtTime = (s) => { try { return new Date(s).toLocaleString('uz-UZ', { hour12: false }); } catch { return s; } };

  const JobCard = ({ j }) => {
    const [label, cls] = jStatus[j.status] || [j.status, 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'];
    const pct = j.total > 0 ? Math.min(100, Math.round((j.done / j.total) * 100)) : null;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white text-sm truncate">{jobIcon(j.job_type)} {j.title || `Jarayon #${j.id}`}</div>
            <div className="text-xs text-gray-400 mt-0.5">
              {j.project_name ? `📁 ${j.project_name} · ` : ''}#{j.id} · {fmtTime(j.created_at)}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls} ${isJobActive(j) ? 'animate-pulse' : ''}`}>{label}</span>
            {isJobActive(j) && (
              <button
                onClick={() => cancelJob(j.id)}
                disabled={busy['j' + j.id] || j.status === 'cancelling'}
                className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-800/70 disabled:opacity-50 transition"
              >
                ⏹ To'xtatish
              </button>
            )}
          </div>
        </div>
        {isJobActive(j) && pct !== null && (
          <div className="mt-2">
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div className="h-2 rounded-full bg-primary-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-gray-400 mt-1">{j.done}/{j.total}</div>
          </div>
        )}
        {j.detail && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap leading-relaxed max-h-56 overflow-auto bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1.5">
            {j.detail}
          </div>
        )}
        {j.job_type === 'keyfix' && onGoConsole && (
          <button
            onClick={onGoConsole}
            title="Agent konsolda nima qilayotganini jonli ko'rish"
            className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-primary-700 dark:bg-primary-600 text-white font-semibold hover:bg-primary-800 dark:hover:bg-primary-700">
            🔐 Konsolda ko'rish
          </button>
        )}
      </div>
    );
  };

  const renderTaskCard = (t) => (
    <div key={'t' + t.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className={`gap-3 ${compact ? '' : 'flex items-start justify-between flex-wrap'}`}>
        {/* Sarlavha ustuni: `basis` + `min-w` bilan HECH QACHON 0 ga siqilmaydi —
            uzun chip qatori uni siqib, matnni vertikal (1 harf/qator) qilib qo'yardi. */}
        <div className={`min-w-0 ${compact ? '' : 'flex-1 basis-[22rem] min-w-[14rem]'}`}>
          <button
            type="button"
            onClick={() => { setDetailCopied(false); setDetailTask(t); }}
            title="To'liq matnni ko'rish / nusxa olish"
            className="font-semibold text-gray-900 dark:text-white text-sm break-words text-left hover:text-primary-700 dark:hover:text-primary-300 hover:underline decoration-dotted underline-offset-2 cursor-pointer"
          >⚡ {(t.instruction || '').slice(0, 140)}{(t.instruction || '').length > 140 ? '…' : ''}</button>
          <div className="text-xs text-gray-400 mt-0.5">
            {t.project_name ? `📁 ${t.project_name} · ` : ''}#{t.id} · {fmtTime(t.created_at)}
            {(taskImages(t).length > 0 || taskFiles(t).length > 0) && (
              <button type="button" onClick={() => { setDetailCopied(false); setDetailTask(t); }}
                title="Biriktirilgan rasm va fayllarni ko'rish" className="ml-1 hover:text-primary-600 dark:hover:text-primary-300">
                · {taskImages(t).length > 0 ? `🖼 ${taskImages(t).length}` : ''}{taskImages(t).length > 0 && taskFiles(t).length > 0 ? ' ' : ''}{taskFiles(t).length > 0 ? `📎 ${taskFiles(t).length}` : ''}
              </button>
            )}
            {t.created_by_name && <span translate="no"> · 👤 {t.created_by_name}</span>}
            {t.approved_by_name && <span translate="no"> · ✅ {t.approved_by_name}</span>}
          </div>
        </div>
        <div className={`flex items-center gap-2 flex-wrap min-w-0 ${compact ? 'mt-2 justify-start' : 'justify-end'}`}>
          {t.run_mode === 'sequential' && !t.approved_at && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 whitespace-nowrap"
              title="Ketma-ket: oldingi vazifa merge+deploy bo'lgach boshlanadi"
            >⛓ Ketma-ket</span>
          )}
          {Number(t.auto_deploy) >= 1 && !t.approved_at && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 whitespace-nowrap"
              title="Tugagach avtomatik merge + deploy qilinadi"
            >🚀 Avto-deploy</span>
          )}
          {t.answer && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 whitespace-nowrap" title="Bu vazifa savol edi — javob berildi">💬 Javob</span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(t.status === 'awaiting_plan_approval' && t.plan_failed) ? sColors.awaiting_clarification : (sColors[t.status] || 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300')} ${ACTIVE.includes(t.status) ? 'animate-pulse' : ''}`}>
            {(t.status === 'awaiting_plan_approval' && t.plan_failed) ? '⚠️ Reja tuzilmadi' : (sLabels[t.status] || t.status)}
          </span>
          {!t.client_view && (<>
          {isTaskActive(t) && (
            <button
              onClick={() => stopTask(t.id)}
              disabled={busy['t' + t.id]}
              className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-800/70 disabled:opacity-50 transition"
            >
              ⏹ To'xtatish
            </button>
          )}
          {/* Deploy bosqichi — merge/redeploy/APK qurish/o'rnatish jonli ko'rinadi. */}
          {DEPLOY_CHIPS[t.deploy_stage] && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 ${DEPLOY_CHIPS[t.deploy_stage].cls} ${DEPLOY_CHIPS[t.deploy_stage].live ? 'animate-pulse' : ''}`}
              title={t.deploy_detail ? `${DEPLOY_CHIPS[t.deploy_stage].label} — ${t.deploy_detail}` : DEPLOY_CHIPS[t.deploy_stage].label}
            >
              {DEPLOY_CHIPS[t.deploy_stage].label}{t.deploy_detail ? ` · ${shortDetail(t.deploy_detail)}` : ''}
            </span>
          )}
          {t.approved_at && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              t.deploy_stage === 'test_only' ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400' : 'bg-primary-200 dark:bg-primary-800/60 text-primary-900 dark:text-primary-200'}`}
              title={t.deploy_stage === 'test_only'
                ? "Kod main'ga qo'shildi va test muhitiga chiqdi — PRODUCTION'ga chiqarish uchun 🚀 Publish bo'limidan Deploy qiling"
                : undefined}>
              {t.deploy_stage === 'test_only' ? '✅ Tasdiqlandi' : '🚀 Deploy qilindi'}
            </span>
          )}
          {canResume(t) && (
            <button
              onClick={() => resumeTask(t.id)}
              disabled={busy['t' + t.id]}
              title="Yiqilgan qism vazifalarni qayta navbatga qo'yish (masalan Claude limiti tugab qolganda). Yarim qilingan ish saqlanadi."
              className="px-2 py-1 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition whitespace-nowrap"
            >
              ▶ Davom ettirish
            </button>
          )}
          {canReapprove(t) && (
            <button
              onClick={() => approveTask(t.id, true)}
              disabled={String(approvingId) === String(t.id)}
              title="Merge bo'lmagan modul(lar)ni qaytadan merge qilib deploy qiladi (allaqachon merge bo'lganiga tegilmaydi)"
              className="px-2 py-1 text-xs font-semibold bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition whitespace-nowrap"
            >
              {String(approvingId) === String(t.id) ? '⏳ Merge…' : '🔁 Qayta tasdiqlash'}
            </button>
          )}
          {canApprove(t) && (
            <button
              onClick={() => approveTask(t.id)}
              disabled={String(approvingId) === String(t.id)}
              title={`Review branch (kh/task-${t.id}) ni asosiy branchga merge qilib, test-muhitni qayta deploy qiladi`}
              className="px-2 py-1 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition whitespace-nowrap"
            >
              {String(approvingId) === String(t.id) ? '⏳ Merge…' : '✅ Tasdiqlash'}
            </button>
          )}
          {!t.parent_id && (() => {
            // 🔁 Nazoratchi: tugagach (yoki hozir) tasdiqlab sinash. Holat — /tasks dagi after_test.
            const at = t.after_test;
            const AT = {
              proposed: '🔁 Sinaymi?', waiting: '🔁 Tasdiq/deploy kutilmoqda', approved: '🔁 Sinov navbatda',
              running: '🔄 Sinalmoqda', fixing: '🛠 Tuzatilmoqda', passed: '✅ Sinovdan o\'tdi', verified: '✅ Tuzatildi, sinovdan o\'tdi',
              question: '❓ Nazoratchi savoli', skipped: '⏭ Sinov o\'tkazildi', error: '⚠️ Sinov xatosi',
            };
            const st = at && at.status;
            const canMark = !at || ['proposed', 'skipped', 'error'].includes(st);
            const fixes = (at && at.fix_task_ids) || [];
            return (
              <>
                {at && st === 'question' && (
                  <button onClick={() => setSvOpen(svOpen === t.id ? null : t.id)}
                    title="Nazoratchi sizdan javob kutyapti — bosing: savol matni va javob berish"
                    className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/70 animate-pulse">
                    ❓ Nazoratchi savoli {svOpen === t.id ? '▲' : '▼'}
                  </button>
                )}
                {at && st === 'question' && svOpen === t.id && (
                  <div className="basis-full w-full mt-1 p-3 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 text-left">
                    <div className="text-xs font-semibold text-red-800 dark:text-red-300 mb-1">🧑‍💼 Nazoratchi so'rayapti (sinov #{at.last_id || at.id}):</div>
                    <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words mb-2">{at.question || "Savol matni topilmadi — telefon panelidagi 🧑‍💼 Nazoratchi kartasini oching."}</div>
                    {Array.isArray(at.options) && at.options.length ? (
                      <div className="flex flex-wrap gap-2">
                        {at.options.filter(o => o !== 'skip').map(o => (
                          <button key={o} disabled={busy['sv' + t.id]} onClick={() => svAct(t.id, at.last_id || at.id, 'answer', { answer: o })}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 ${o === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-primary-700 hover:bg-primary-800'}`}>
                            {({ 'retry-fix': '🔁 Tuzatishni qayta yurit', accept: "✅ O'tdi deb qabul", fix: '🛠 Tuzatish och', retry: '🔁 Qayta sina' })[o] || '🔁 Qayta sina'}
                          </button>
                        ))}
                        <button disabled={busy['sv' + t.id]} onClick={() => svAct(t.id, at.last_id || at.id, 'skip')}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">⏭ O'tkazib yubor</button>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-start">
                        <textarea rows={2} value={svAns[t.id] || ''} onChange={e => setSvAns(a => ({ ...a, [t.id]: e.target.value }))}
                          placeholder="Javobingiz — masalan: «B1 brauzerda sina» yoki «ruxsat, davom et»"
                          className="flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <button disabled={busy['sv' + t.id] || !(svAns[t.id] || '').trim()} onClick={() => svAct(t.id, at.last_id || at.id, 'answer', { answer: svAns[t.id] })}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-700 hover:bg-primary-800 text-white disabled:opacity-40">📨 Javob</button>
                        <button disabled={busy['sv' + t.id]} onClick={() => svAct(t.id, at.last_id || at.id, 'skip')} title="Sinovni o'tkazib yuborish"
                          className="px-2 py-1.5 rounded-lg text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">⏭</button>
                      </div>
                    )}
                  </div>
                )}
                {at && st !== 'proposed' && st !== 'question' && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                    st === 'passed' || st === 'verified' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : st === 'question' || st === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'}`}
                    title="Nazoratchi: tugagach tasdiqlab sinash. Batafsil — telefon panelidagi 🧑‍💼 Nazoratchi kartasi.">
                    {AT[st] || st}{st === 'fixing' && fixes.length ? ` #${fixes.join(', #')}` : ''}
                  </span>
                )}
                {at && st === 'waiting' && (
                  <button onClick={() => afterTestTask(t.id, true)} disabled={busy['at' + t.id]}
                    title="Tugagach tasdiqlab sinashni bekor qilish"
                    className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-red-600 disabled:opacity-50">✕</button>
                )}
                {canMark && (
                  <button onClick={() => (onAgentTest && (!agentTestOk || agentTestOk(t)) ? (setSinaOpen(sinaOpen === t.id ? null : t.id), setSinaNote('')) : afterTestTask(t.id))}
                    disabled={busy['at' + t.id]}
                    title={isTaskActive(t)
                      ? "Vazifa tugagach Khorezmai O'ZI tasdiqlaydi (merge + test deploy/APK) va qurilmada/brauzerda sinaydi; kamchilik chiqsa tuzatib qayta sinaydi"
                      : "Khorezmai tasdiqlaydi (hali tasdiqlanmagan bo'lsa), deploy/APK tugashini kutadi va qurilmada/brauzerda sinaydi; kamchilik chiqsa tuzatib qayta sinaydi"}
                    className="px-2 py-1 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition whitespace-nowrap">
                    {isTaskActive(t) ? '🔁 Tugagach tasdiqla+sina' : (t.approved_at ? '🔁 Sina' : '🔁 Tasdiqla va sina')}
                  </button>
                )}
                {canMark && sinaOpen === t.id && (() => {
                  const chip = (k, label, title) => (
                    <button key={k} type="button" onClick={() => toggleSinaOpt(k)} title={title} aria-pressed={!!sinaOpts[k]}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${sinaOpts[k]
                        ? 'bg-primary-700 text-white border-primary-700'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                      {label}
                    </button>
                  );
                  return (
                    <div className="basis-full w-full mt-1 p-3 rounded-lg border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/30 text-left">
                      <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">🧠 Agent sinovi — vazifa #{t.id}</div>
                      <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                        Agent vazifani o'qib to'liq ssenariy yozadi (rol, kirish, boshidan oxirigacha), qurilmada/brauzerda sinaydi, topilmani tuzatishga berib
                        qayta sinaydi va har natijada hisobot beradi.{t.approved_at || isTaskActive(t) ? '' : ' Vazifa hali tasdiqlanmagan — avval tasdiqlanib test muhitiga chiqadi.'}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {chip('narrate', '🔊 Ovozli sinov', "Agent har bosqichni ovoz bilan aytib boradi — ekranda subtitr va ovoz")}
                        {chip('record', '🎥 Video → Telegram', "Sinov videoga yoziladi; sinov o'tsa video Telegram'ga o'zi yuboriladi (o'tmasa faqat kartada)")}
                        {chip('ux', '🎨 Qulaylik (UX) ham', "Xatolardan tashqari noqulay joylarni ham topadi va tuzatishga beradi")}
                        {chip('bg', '🌙 Fonda', "Agent oynasi ochilmaydi — sinov fonda ketadi, natija 🧠 Agent suhbatida va Telegram'da")}
                      </div>
                      <textarea rows={2} value={sinaNote} onChange={e => setSinaNote(e.target.value)}
                        placeholder="Nimaga e'tibor bersin (ixtiyoriy) — masalan: «haydovchi so'raydi, HR web'da tasdiqlaydi, PDF ochilsin»"
                        className="mt-2 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button type="button" disabled={busy['at' + t.id]} onClick={() => sendSina(t)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">🧠 Agentga yuborish</button>
                        <button type="button" disabled={busy['at' + t.id]} onClick={() => { setSinaOpen(null); afterTestTask(t.id); }}
                          title="Agentsiz: Nazoratchi vazifa matnining o'zi bo'yicha sinaydi (tanlovlar va suhbatdagi hisobot yo'q)"
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">⚡ Agentsiz</button>
                        <button type="button" onClick={() => setSinaOpen(null)} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">Bekor</button>
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}
          {/* ✅/❌ TEKSHIRUV — operator o'zi ko'rib belgilaydi (uch holat:
              to'g'ri / noto'g'ri / belgilanmagan). 🧪 Sinash esa agentni yuborib
              ilovada tekshiradi va natijaga qarab O'ZI belgilaydi. */}
          {!isTaskActive(t) && (
            <span className="inline-flex items-center gap-1 ml-1 pl-2 border-l border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setVerify(t.id, t.verify_status === 'ok' ? null : 'ok')}
                disabled={busy['v' + t.id]}
                title={t.verify_status === 'ok'
                  ? `To'g'ri deb belgilangan${t.verify_by === 'agent' ? ' (agent)' : ''}${t.verify_note ? ' — ' + t.verify_note : ''}. Bosilsa belgi olib tashlanadi.`
                  : "Tekshirdim — to'g'ri bajarilgan"}
                className={`px-2 py-1 text-xs rounded-lg transition disabled:opacity-50 ${
                  t.verify_status === 'ok'
                    ? 'bg-green-600 text-white font-semibold'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/60 hover:text-green-700 dark:hover:text-green-300'}`}
              >
                ✅{t.verify_status === 'ok' && t.verify_by === 'agent' ? '🤖' : ''}
              </button>
              <button
                onClick={() => setVerify(t.id, t.verify_status === 'bad' ? null : 'bad')}
                disabled={busy['v' + t.id]}
                title={t.verify_status === 'bad'
                  ? `Noto'g'ri deb belgilangan${t.verify_by === 'agent' ? ' (agent)' : ''}${t.verify_note ? ' — ' + t.verify_note : ''}. Bosilsa belgi olib tashlanadi.`
                  : "Tekshirdim — noto'g'ri / ishlamayapti"}
                className={`px-2 py-1 text-xs rounded-lg transition disabled:opacity-50 ${
                  t.verify_status === 'bad'
                    ? 'bg-red-600 text-white font-semibold'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/60 hover:text-red-700 dark:hover:text-red-300'}`}
              >
                ❌{t.verify_status === 'bad' && t.verify_by === 'agent' ? '🤖' : ''}
              </button>
              <button
                onClick={() => openVerifyPick(t.id)}
                disabled={busy['v' + t.id]}
                title="Agent ilovani ochib tekshiradi va natijaga qarab o'zi belgilaydi (token sarflaydi)"
                className="px-2 py-1 text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/60 disabled:opacity-50 transition whitespace-nowrap"
              >
                🧪 Sinash
              </button>
            </span>
          )}
          {t.status === 'completed' && (
            <button
              onClick={() => addToKnowledge(t.id)}
              disabled={busy['k' + t.id]}
              title="Bu vazifadagi yechimni Tajriba bazasiga RETSEPT qilib saqlash (AI hisobotdan ajratadi) — engine keyingi shunday vazifada mustaqil ishlatadi"
              className="px-2 py-1 text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60 disabled:opacity-50 transition whitespace-nowrap"
            >
              {busy['k' + t.id] ? '⏳ Ajratilmoqda…' : '📚 Tajribaga qo\'sh'}
            </button>
          )}
          {canDelete(t) && (
            <button
              onClick={() => deleteTask(t.id)}
              disabled={busy['t' + t.id]}
              title="Vazifani ro'yxatdan butunlay o'chirish (deploy qilingan kod o'zgarmaydi)"
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 transition"
            >
              🗑 O'chirish
            </button>
          )}
          </>)}
        </div>
      </div>
      {t.status === 'awaiting_clarification' && (() => {
        const qs = parseQuestions(t);
        return (
          <div className="mt-3 border-t border-amber-100 dark:border-amber-800 pt-3">
            <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">❓ Aniqlashtirish kerak — quradan oldin javob bering:</div>
            <div className="space-y-2">
              {qs.map((q, i) => (
                <div key={i}>
                  <div className="text-xs text-gray-700 dark:text-gray-300 mb-1">{i + 1}. {q}</div>
                  <input
                    type="text"
                    value={clarAnswers[t.id]?.[i] || ''}
                    onChange={e => setClarAnswers(s => ({ ...s, [t.id]: { ...(s[t.id] || {}), [i]: e.target.value } }))}
                    onKeyDown={e => { if (e.key === 'Enter') submitClarification(t); }}
                    placeholder="Javobingiz..."
                    className="w-full text-xs border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={() => submitClarification(t)}
              disabled={String(clarSubmitting) === String(t.id)}
              className="mt-2 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition"
            >
              {String(clarSubmitting) === String(t.id) ? '⏳ Yuborilmoqda…' : '📨 Javob berish'}
            </button>
          </div>
        );
      })()}
      {(t.status === 'awaiting_plan_approval' || t.plan_ready) && (
        <PlanReviewCard authFetch={authFetch} apiUrl={apiUrl} task={t} compact={compact}
          onChanged={refreshTasks} onStop={() => stopTask(t.id)} />
      )}
      {t.status === 'awaiting_owner_approval' && (
        <OwnerApprovalCard authFetch={authFetch} apiUrl={apiUrl} task={t} onChanged={refreshTasks} />
      )}
      {(() => {
        // 🧪 Sinov natijasi KARTANING O'ZIDA (2026-10-31, operator: «#2498 testdan o'tmadi — nimaga ekanini bilish uchun Sinov
        // tabiga kirishim kerak, tuzatish shu vazifaga bog'liqligini ham bilmayman»): xulosa, topilmalar, tuzatish vazifasi, manba.
        const at = t.after_test;
        const fixes = (at && at.fix_task_ids) || [];
        const svBad = !!at && (at.verdict_passed === false || ['fixing', 'question', 'error'].includes(at.status))
          && !!(at.verdict_summary || (at.verdict_findings || []).length);
        const agentBad = t.verify_status === 'bad' && t.verify_by === 'agent' && !!t.verify_note && !svBad;
        if (!svBad && !agentBad && !t.fix_of) return null;
        const open = !!testOpen[t.id];
        const sum = svBad ? (at.verdict_summary || '') : (agentBad ? t.verify_note : '');
        return (
          <div className="mt-2 space-y-1.5 text-left">
            {t.fix_of && (
              <div className="text-xs text-sky-700 dark:text-sky-300">
                ↩ Bu — <b>#{t.fix_of.task_id}</b> vazifasining Nazoratchi sinovidan chiqqan tuzatish. Tugab deploy bo'lgach #{t.fix_of.task_id} qayta sinaladi.
              </div>
            )}
            {(svBad || agentBad) && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap text-xs font-semibold text-red-800 dark:text-red-300">
                  <span>{svBad ? '❌ Nazoratchi sinovi o\'tmadi' : '❌ 🤖 Agent sinovi o\'tmadi'}</span>
                  {svBad && fixes.length > 0 && <span className="font-normal text-gray-600 dark:text-gray-300">→ 🛠 tuzatish: {fixes.map(f => `#${f}`).join(', ')}{at.status === 'fixing' ? ' (deploydan keyin qayta sinaladi)' : ''}</span>}
                  {svBad && at.status === 'question' && <span className="font-normal text-gray-600 dark:text-gray-300">→ ❓ javobingizni kutyapti (yuqoridagi «Nazoratchi savoli»)</span>}
                </div>
                {sum && (
                  <div className={`mt-1 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words ${open ? '' : 'line-clamp-3'}`}>{sum}</div>
                )}
                {svBad && (at.verdict_findings || []).length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {(open ? at.verdict_findings : at.verdict_findings.slice(0, 3)).map((f, i) => (
                      <li key={i} className="text-xs text-gray-700 dark:text-gray-300 flex gap-1.5">
                        <span className="shrink-0">{f.severity === 'high' ? '🔴' : f.severity === 'medium' ? '🟠' : '🟡'}</span>
                        <span className="break-words">{f.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(String(sum).length > 220 || (svBad && (at.verdict_findings || []).length > 3)) && (
                  <button type="button" onClick={() => setTestOpen(o => ({ ...o, [t.id]: !open }))}
                    className="mt-1 text-[11px] text-primary-700 dark:text-primary-300 hover:underline">{open ? '▲ Qisqartirish' : '▼ To\'liq ko\'rish'}</button>
                )}
              </div>
            )}
          </div>
        );
      })()}
      <TaskComments authFetch={authFetch} apiUrl={apiUrl} taskId={t.id} count={t.comment_count} />
      {t.answer && (
        <div className="mt-3 border-t border-primary-100 dark:border-primary-800 pt-3">
          <div className="text-xs font-semibold text-primary-800 dark:text-primary-300 mb-1.5">💬 Javob</div>
          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words bg-primary-50 dark:bg-primary-900/30 rounded-lg px-3 py-2">
            {String(t.answer).replace(/^\[JAVOB\]\s*/, '')}
          </div>
        </div>
      )}
      {(t.sub_tasks || []).length > 0 && (
        <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 space-y-1">
          {t.sub_tasks.map(st => {
            const open = !!openSubs[st.id];
            return (
            <div key={st.id}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => toggleSub(st.id)}
                  title="To'liq matnni ko'rish uchun bosing"
                  className="flex items-center gap-1 text-left min-w-0 flex-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
                >
                  <span className="shrink-0 text-gray-300">{open ? '▾' : '▸'}</span>
                  {/* #ID doim ko'rinsin — agent sub-taskka shu raqam bilan murojaat qiladi («#2576 ni tasdiqla»), operator topa olmasdi. */}
                  <span className="shrink-0 text-gray-400 dark:text-gray-500 tabular-nums" translate="no">#{st.id}</span>
                  <span className="truncate">
                    {st.module_name ? `[${st.module_name}] ` : ''}{st.task_type === 'qa' ? '🧪' : '👨‍💻'} {(st.instruction || '').slice(0, 90)}
                  </span>
                </button>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${sColors[st.status] || 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'}`}>{sLabels[st.status] || st.status}</span>
                  {ACTIVE.includes(st.status) && !t.client_view && (
                    <button
                      onClick={() => stopTask(st.id)}
                      disabled={busy['t' + st.id]}
                      title="Faqat shu qism vazifani to'xtatish"
                      className="px-1.5 py-0.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/60 disabled:opacity-50 transition"
                    >
                      ⏹
                    </button>
                  )}
                </span>
              </div>
              {open && (
                <div className="mt-1 mb-2 ml-4 p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                  {st.instruction || "(matn yo'q)"}
                  <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400">
                    #{st.id} · {st.task_type === 'qa' ? '🧪 QA' : '👨‍💻 developer'}{st.module_name ? ` · ${st.module_name}` : ''}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // 🖼 Vazifaga biriktirilgan rasmlar va fayllar (operator, 2026-10-05: «vazifani ustiga bosib ko'rganda
  // yuklangan rasmlar chiqmadi» — oyna faqat matnni chizardi). image_url = bitta yo'l YOKI JSON massiv
  // (handleAddTask), attachments = [{path, name, size}] JSON. /uploads API'da statik (auth'siz).
  const taskImages = (t) => {
    const raw = t && t.image_url;
    if (!raw) return [];
    let list;
    try { const v = JSON.parse(raw); list = Array.isArray(v) ? v : [v]; } catch { list = [raw]; }
    return list.filter(x => typeof x === 'string' && x.trim())
      .map(x => (/^https?:\/\//.test(x) ? x : `${apiUrl}${x.startsWith('/') ? '' : '/'}${x}`));
  };
  const taskFiles = (t) => {
    try { const v = JSON.parse((t && t.attachments) || '[]'); return (Array.isArray(v) ? v : []).filter(f => f && f.path); }
    catch { return []; }
  };
  const fileUrl = (f) => (/^https?:\/\//.test(f.path) ? f.path : `${apiUrl}${f.path.startsWith('/') ? '' : '/'}${f.path}`);

  // 🧪 Qurilma tanlash oynasi. Emulator yo'q loyihada (faqat web) ro'yxat bo'sh
  // bo'ladi — o'shanda tanlovsiz boshlanadi (server nishonni o'zi topadi).
  const detailModal = detailTask && (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setDetailTask(null)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0 flex-1">
            <div className="font-bold text-gray-900 dark:text-white text-base">📄 Vazifa matni</div>
            <div className="text-xs text-gray-400 mt-0.5">
              {detailTask.project_name ? `📁 ${detailTask.project_name} · ` : ''}#{detailTask.id} · {fmtTime(detailTask.created_at)}
            </div>
          </div>
          <button onClick={() => copyDetail(detailTask.instruction || '')}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition whitespace-nowrap ${detailCopied ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700'}`}>
            {detailCopied ? '✓ Nusxa olindi' : '📋 Nusxa olish'}
          </button>
          <button onClick={() => setDetailTask(null)} title="Yopish"
            className="px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">✕</button>
        </div>
        <div className="overflow-auto">
          <div className="px-5 py-4 text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words leading-relaxed">
            {detailTask.instruction || '(matn yo\'q)'}
          </div>
          {taskImages(detailTask).length > 0 && (
            <div className="px-5 pb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">🖼 Biriktirilgan rasmlar ({taskImages(detailTask).length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {taskImages(detailTask).map((src, i) => (
                  <button type="button" key={src + i} onClick={() => setImgViewer({ images: taskImages(detailTask), index: i })} title="Katta qilib ko'rish"
                    className="block w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:ring-2 hover:ring-primary-400 transition cursor-zoom-in">
                    <img src={src} alt={`Rasm ${i + 1}`} className="w-full h-36 object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {taskFiles(detailTask).length > 0 && (
            <div className="px-5 pb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">📎 Biriktirilgan fayllar ({taskFiles(detailTask).length})</div>
              <div className="space-y-1">
                {taskFiles(detailTask).map((f, i) => (
                  <a key={f.path + i} href={fileUrl(f)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-primary-700 dark:text-primary-300">
                    <span className="truncate" translate="no">📎 {f.name || f.path.split('/').pop()}</span>
                    {f.size ? <span className="ml-auto shrink-0 text-xs text-gray-400">{f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}</span> : null}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const verifyPickModal = verifyPick && (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
         onClick={() => setVerifyPick(null)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4" onClick={e => e.stopPropagation()}>
        <div className="font-semibold text-gray-900 dark:text-white mb-1">🧪 Vazifa #{verifyPick.taskId} ni sinash</div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Agent ilovani ochib, vazifa haqiqatan bajarilganini tekshiradi va natijaga qarab
          ✅/❌ belgilaydi. Bir necha daqiqa oladi va token sarflaydi.
        </p>
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Qaysi qurilmada sinalsin?</div>
        {verifyPick.loading ? (
          <div className="text-xs text-gray-400 py-2">⏳ Emulatorlar yuklanmoqda…</div>
        ) : verifyPick.emus.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
            Emulator topilmadi — sinov nishonini server o'zi tanlaydi (web moduli bo'lsa brauzerda).
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {verifyPick.emus.map(e => {
              const sl = Number(e.slot);
              const on = verifyPick.slots.includes(sl);
              const live = e.status === 'running';
              return (
                <button key={sl}
                  onClick={() => setVerifyPick(p => ({
                    ...p,
                    slots: p.slots.includes(sl) ? p.slots.filter(x => x !== sl) : [...p.slots, sl].sort((x, y) => x - y),
                  }))}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${on
                    ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'}`}>
                  {on ? '☑' : '☐'} Emulator {sl + 1}{live ? '' : ' ⏻'}
                </button>
              );
            })}
          </div>
        )}
        {verifyPick.slots.length > 1 && (
          <p className="text-[11px] text-primary-700 dark:text-primary-500 mb-2">
            — agent {verifyPick.slots.length} qurilmani birga boshqaradi (ikki rolli oqim uchun)
          </p>
        )}
        {!verifyPick.loading && verifyPick.emus.some(e => verifyPick.slots.includes(Number(e.slot)) && e.status !== 'running') && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-2 py-1 mb-2">
            ⚠️ Tanlanganlar orasida o'chiq qurilma bor — sinov uni o'zi yoqadi, lekin yuklanishi
            bir necha daqiqa olishi mumkin.
          </p>
        )}
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={() => setVerifyPick(null)}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
          <button onClick={() => verifyRun(verifyPick.taskId, verifyPick.slots)}
            disabled={verifyPick.loading}
            className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600">
            ▶ Sinovni boshlash
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {verifyPickModal}
      {detailModal}
      {imgViewer && <ImageLightbox images={imgViewer.images} index={imgViewer.index} onClose={() => setImgViewer(null)} />}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-gray-900 dark:text-white inline-flex items-center gap-2`}><NavIcon name="clock" className={compact ? 'w-5 h-5 shrink-0' : 'w-6 h-6 shrink-0'} />Jarayonlar</h1>
        {!compact && <span className="text-sm text-gray-400">Barcha loyihalar — engine vazifalari + fon jarayonlari (logo/TZ). Sahifani yopsangiz ham ishlar davom etadi.</span>}
        {refreshSignal === null && (
          <div className="ml-auto flex items-center gap-1.5">
            <ProcProjectChip on={projOnly} setOn={setProjOnly} project={selectedProject} />
            <button onClick={() => { loadJobs(); refreshTasks(); }} title="Yangilash" className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">↻</button>
          </div>
        )}
        {onClose && <button onClick={onClose} title="Yopish" className="px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">✕</button>}
      </div>

      {/* ⏸ ENGINE CHEKLOVDA — navbat nega turganini HAR rol ko'rsin. Ilgari bu faqat pm2
          logida va admin EngineLimitPanel'ida edi; operator (`user` roli) navbat turganini
          ko'rib "engine qotib qoldimi?" derdi, holbuki u o'z 5-soatlik capida kutayotgan
          bo'lardi (2026-09-12: 56M/50M, 4 vazifa ~100 daqiqa). */}
      {enginePause && (enginePause.paused || enginePause.me?.blocked) && (() => {
        const M = (n) => `${(Number(n || 0) / 1e6).toFixed(1)}M`;
        const when = (p) => {
          if (p.freeInMinutes === null || p.freeInMinutes === undefined) return "oyna ichida o'zi ochilmaydi — cheklov sarfdan past, uni ko'tarish kerak";
          const at = p.resumesAt ? new Date(p.resumesAt) : new Date(Date.now() + p.freeInMinutes * 60000);
          const pad = (n) => String(n).padStart(2, '0');
          return `${fmtResetIn(p.freeInMinutes)} (≈ ${pad(at.getHours())}:${pad(at.getMinutes())}) o'zi davom etadi`;
        };
        return (
          <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
            {enginePause.paused && (
              <div>
                <span className="font-semibold">⏸ Engine to'xtab turibdi — UMUMIY (global) {enginePause.window} token cheklovi:</span>{' '}
                {M(enginePause.used)} / {M(enginePause.cap)}. Navbatdagi vazifalar <b>{when(enginePause)}</b>.
                Hech narsa yiqilmagan — ishlab turganlari tugaydi, yangilari kutadi.
                {enginePause.me && !enginePause.me.blocked && <span className="opacity-80"> Foydalanuvchilar sahifasidagi SHAXSIY cheklovingiz bu emas — u to'smayapti.</span>}
              </div>
            )}
            {enginePause.me?.blocked && (
              <div className={enginePause.paused ? 'mt-1.5' : ''}>
                <span className="font-semibold">⏸ Sizning shaxsiy cheklovingiz tugagan ({enginePause.me.window}):</span>{' '}
                {M(enginePause.me.used)} / {M(enginePause.me.cap)}. Siz bergan vazifalar <b>{when(enginePause.me)}</b>; jamoadagi boshqa a'zolar ishlayveradi.
              </div>
            )}
            <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5">Cheklov — Khorezmai'ning o'z tejamkorlik chegarasi (Anthropic'niki emas). {enginePause.paused ? "Umumiyni o'zgartirish: admin hisobi → Konfiguratsiya → ⚙️ Engine cheklovi (yoki Foydalanuvchilar kartasidagi «↑ Umumiyni ham … qilish»)." : ''}{enginePause.me?.blocked ? " Shaxsiy: admin → Foydalanuvchilar → cheklovni ko'tarish yoki ♻️ nolga tushirish." : ''}</div>
          </div>
        );
      })()}

      {hasTeamTasks && <div className="flex items-center gap-x-4 gap-y-2 flex-wrap mb-3">
        {hasTeamTasks && (
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setMineOnly(false)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${!mineOnly ? 'bg-primary-700 border-primary-700 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'}`}>👥 Hammasi</button>
            <button type="button" onClick={() => setMineOnly(true)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${mineOnly ? 'bg-primary-700 border-primary-700 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'}`}>👤 Meniki</button>
          </div>
        )}
      </div>}
      <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
        Hozir bajarilmoqda ({activeJobs.length + activeTasks.length})
      </h2>
      {activeJobs.length + activeTasks.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 mb-6">
          {projLoading ? '⏳ Yuklanmoqda…' : projFilterOn
            ? <>Bu loyihada hozir faol jarayon yo'q. <span className="text-gray-500" translate="no">({selectedProject.name || ''})</span></>
            : "Hozircha faol jarayon yo'q. Studio'da vazifa qo'shing yoki Dizayn → 🏷 Logo'da logo yarating."}
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {activeJobs.map(j => <JobCard key={'j' + j.id} j={j} />)}
          {activeTasks.map(t => renderTaskCard(t))}
        </div>
      )}

      {/* 🐞 Topilmalar (operator, 2026-11-01: «topilmalarni ham jarayonlarda ko'rish — u yoqdan bu yoqqa sakrab yuribman»):
          Studio → 🧪 Sinov → Topilmalar bilan bir xil panel (qidiruv, ✅ Vazifa, Rad etish), tanlangan loyiha bo'yicha. */}
      {selectedProject && selectedProject.id && (
        <div className="mb-6">
          <button type="button" onClick={() => setFindOpen(!findOpen)}
            title="Agent sinovi va xavfsizlik skaneri topgan kamchiliklar — shu yerdan vazifa qilish yoki rad etish mumkin"
            className="w-full flex items-center justify-between gap-2 mb-2 text-left">
            <span className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              🐞 Topilmalar{findCount !== null ? ` (${findCount} ochiq)` : ''} · <span className="normal-case font-semibold" translate="no">{selectedProject.name || `#${selectedProject.id}`}</span>
            </span>
            <span className="text-gray-400 text-xs">{findOpen ? '▾ yig\'ish' : '▸ ochish'}</span>
          </button>
          {findOpen && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <FindingsPanel authFetch={authFetch} apiUrl={apiUrl} projectId={selectedProject.id} onCount={setFindCount} readOnly={['client', 'viewer'].includes(selectedProject.my_role)} />
            </div>
          )}
        </div>
      )}

      <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Yaqinda tugaganlar</h2>
      {doneJobs.length + doneTasks.length === 0 ? (
        <div className="text-center py-8 text-gray-300 select-none">—</div>
      ) : (
        <div className="space-y-3 opacity-80">
          {doneMerged.map(d => d.kind === 'job'
            ? <JobCard key={'j' + d.item.id} j={d.item} />
            : renderTaskCard(d.item))}
          {/* Eskilarini yuklash. App faqat oxirgi `tasksLimit` (10 dan boshlab) vazifani
              oladi; infinite-scroll kuzatuvchisi esa FAQAT admin "Vazifalar" tabida
              o'rnatiladi, shuning uchun ⏳ Jarayonlarda pastga aylantirish hech narsa
              yuklamasdi. Tugma ikkala joyda ham (tab va flyout popup) ishonchli ishlaydi. */}
          {effLoadMore && effHasMore && (
            <button
              onClick={effLoadMore}
              disabled={effLoadingMore}
              className="w-full py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60">
              {effLoadingMore ? '⏳ Yuklanmoqda...' : '⬇️ Eskilarini yuklash (yana 10 ta)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Global floating "Jarayonlar" button + popup, pinned top-right on EVERY tab. Lets the user
// peek at running engine tasks / background jobs (with stop/approve/clarify) WITHOUT leaving
// the Emulator / Database / Browser view they're on. Always mounted → polls for a live badge
// count; the popup reuses ProcessesPanel (compact mode) so behaviour stays identical.
// 📌 Jarayonlar panelini YONGA QADASH (2026-10-29, operator: agentni ko'rib turib Khorezmai'ni ham boshqarish uchun ikkita
// Chrome oynani yonma-yon ochib o'tirardi — suzuvchi panel sahifaning o'ng qismini to'sardi). Qadalganda panel o'ng
// ustunga aylanadi, asosiy kontent (`.kh-main-wrap`) torayadi (index.css, faqat ≥1024px), kenglik sudraladi; qadalgan
// panel ochiq/tab holati sahifa yangilansa ham saqlanadi.
const khLs = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const khLsSet = (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} };
const khProcDockW = (w) => Math.round(Math.min(Math.max(340, Number(w) || 460), Math.max(360, Math.min(1100, window.innerWidth * 0.65))));
function khProcRestore() {
  const on = khLs('kh_proc_dock') === '1' && khLs('kh_proc_open') === '1';
  return { open: on, tab: on ? khLs('kh_proc_tab') : null };
}

function ProcessesFlyout({ authFetch, apiUrl, tasks, refreshTasks, onLoadMore = null, hasMore = false, loadingMore = false, onGoConsole = null, taskForm = null, selectedProject = null,
  canProcesses = true, canAgent = true, token = null, onUiAction = null, ctxPanel = null, agentDraftExt = null,
  onAgentTest = null, agentTestOk = null }) {
  // 💬 Suhbat tabi (🎙 Boshqaruvchi) — ilgari navbar'da alohida 🎙 tugma edi. Push/Telegram havolasi
  // `/?conductor=1` paneli shu tabda ochadi; aks holda standart 📋 Vazifa (operator, 2026-10-04).
  const deepChat = (() => { try { return new URLSearchParams(window.location.search).get('conductor') === '1'; } catch (e) { return false; } })();
  // 🧠 Loyiha agenti (docs/knowledge/190): push havolasi `/?agent=<loyiha>` paneli 🧠 Agent tabida ochadi.
  const deepAgent = (() => { try { return !!new URLSearchParams(window.location.search).get('agent'); } catch (e) { return false; } })();
  // 2026-10-28 (operator): operator uchun 💬 Suhbat olib tashlandi — 🎤 ovoz va 📞 qo'ng'iroq 🧠 Agent'ga ko'chdi, 📡 Navbatchi
  // o'z tabida. 💬 Suhbat faqat MIJOZLARGA qoladi (operator emas — ular Boshqaruvchi bilan shu orqali gaplashadi).
  // `/?conductor=1` (Navbatchi push/hisobot havolasi) operatorda 📡 Navbatchi'ni, mijozda 💬 Suhbat'ni ochadi.
  const restoreRef = React.useRef(null);
  if (restoreRef.current === null) restoreRef.current = (deepChat || deepAgent) ? { open: false, tab: null } : khProcRestore();
  const [open, setOpen] = React.useState(deepChat || deepAgent || restoreRef.current.open);
  const [tab, setTab] = React.useState(deepAgent ? 'agent' : deepChat ? 'duty' : restoreRef.current.tab || (!canProcesses ? 'chat' : 'task'));
  const [me, setMe] = React.useState(null);                 // /conductor/me: ruxsat + yoqilgan + operator
  const [chatMounted, setChatMounted] = React.useState(deepChat || restoreRef.current.tab === 'chat');   // birinchi ochilganda mount, panel yopilguncha turadi
  const [agentMounted, setAgentMounted] = React.useState(deepAgent || restoreRef.current.tab === 'agent');
  const [agentDraft, setAgentDraft] = React.useState(null);   // 📡 «🧠 Agentdan so'rash» → Agent yozuv maydoni
  const [dutyCount, setDutyCount] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    authFetch(`${apiUrl}/conductor/me`).then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setMe(d); }).catch(() => {});
    return () => { alive = false; };
  }, [apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  const isOperator = !!(me && me.operator === true);
  // ⏳ 2026-10-30 (operator: «Vazifa bo'limi ahamiyati qolmadi»): operatorda vazifa formasi yo'q — vazifa 🧠 Agent orqali (chiplar
  // bilan). Tab «⏳ Jarayonlar» — faqat ro'yxat + modullar kontekst oynasi. Mijozlarda (Agent yo'q) forma o'z joyida qoladi.
  const hideForm = isOperator;
  const showTask = canProcesses;
  const showChat = !!(me && me.access && me.enabled && !isOperator);
  const showDuty = isOperator;
  // 🧠 ruxsatni server tekshiradi (operator / owner / developer) — 👁/🔍/🤝 rollarida tab umuman ko'rinmasin (207),
  // egasi 🔎 agentga savol ruxsatini bergan bo'lsa (my_agent_ask) — ko'rinadi, agent faqat savol-javob rejimida.
  const showAgent = canAgent && !!(selectedProject && selectedProject.id) && (isOperator || !['viewer', 'reviewer', 'client'].includes(selectedProject.my_role) || !!selectedProject.my_agent_ask);
  const agentOnly = showAgent && !showTask && !showChat && !showDuty;   // 🔎 faqat Agent tabi — tugma «Agent» deb ataladi
  const tabsAvail = [showTask && 'task', showAgent && 'agent', showDuty && 'duty', showChat && 'chat'].filter(Boolean);
  const wantTab = tab === 'duty' && !showDuty && showChat ? 'chat' : tab;   // mijozda `?conductor=1` → 💬 Suhbat
  const curTab = tabsAvail.includes(wantTab) ? wantTab : (tabsAvail[0] || 'task');
  const pickTab = (t) => { setTab(t); if (t === 'chat') setChatMounted(true); if (t === 'agent') setAgentMounted(true); };
  const askAgent = (text) => { setAgentDraft({ text, at: Date.now() }); pickTab('agent'); };
  // Tashqaridan (Studio 🌐 Brauzer «🛠 Shu sahifadan vazifa») — Agent yozuv maydoniga; Agent yo'q bo'lsa vazifa formasiga.
  // 🗺 Xarita «🧠 Agent bilan maslahat» — `send: true` + rasmlar: OperatorChat suhbat yuklangach o'zi yuboradi (bir marta, `at`).
  React.useEffect(() => {
    if (!agentDraftExt || !agentDraftExt.text) return;
    setOpen(true);
    if (showAgent) { if (agentDraftExt.send) { setAgentDraft(agentDraftExt); pickTab('agent'); } else askAgent(agentDraftExt.text); } else pickTab('task');
  }, [agentDraftExt]); // eslint-disable-line react-hooks/exhaustive-deps
  const closePanel = () => { setOpen(false); setChatMounted(false); setAgentMounted(false); setTab(canProcesses ? 'task' : 'chat'); };
  const [refreshSignal, setRefreshSignal] = React.useState(0);   // sarlavhadagi ↻ → ProcessesPanel
  // 📌 Yonga qadash (khProcRestore izohi). Sudrash paytida kenglik saqlanmaydi, qo'yib yuborilganda saqlanadi.
  const [dock, setDock] = React.useState(() => khLs('kh_proc_dock') === '1');
  const [dockW, setDockW] = React.useState(() => khProcDockW(khLs('kh_proc_dock_w')));
  const toggleDock = () => setDock(v => { const n = !v; khLsSet('kh_proc_dock', n ? '1' : '0'); return n; });
  const dockDown = (e) => { e.preventDefault(); try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) {} };
  const dockMove = (e) => { if (e.buttons & 1) setDockW(khProcDockW(window.innerWidth - e.clientX)); };
  const dockUp = (e) => { try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (er) {} khLsSet('kh_proc_dock_w', String(dockW)); };
  // ⛶ To'liq ekran (operator, 2026-10-21: «polniy ekran qo'ymasa noqulay — o'qish va scroll»). Tanlov eslab qolinadi; Esc chiqaradi.
  const [full, setFull] = React.useState(() => { try { return localStorage.getItem('kh_proc_full') === '1'; } catch (e) { return false; } });
  const toggleFull = () => setFull(v => { const n = !v; try { localStorage.setItem('kh_proc_full', n ? '1' : '0'); } catch (e) {} return n; });
  const dockOn = open && dock && !full;   // ⛶ to'liq ekran qadashdan ustun
  React.useEffect(() => {
    const el = document.documentElement;
    if (!dockOn) { el.classList.remove('kh-docked'); return undefined; }
    el.classList.add('kh-docked');
    el.style.setProperty('--kh-dock-w', `${dockW}px`);
    return () => el.classList.remove('kh-docked');
  }, [dockOn, dockW]);
  React.useEffect(() => {
    if (!dockOn) return undefined;
    const on = () => setDockW(w => khProcDockW(w));
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [dockOn]);
  // Qadalgan panel ochiq/yopiq va tab — sahifa yangilansa shunday qaytadi (khProcRestore).
  React.useEffect(() => { if (dock) { khLsSet('kh_proc_open', open ? '1' : '0'); if (open) khLsSet('kh_proc_tab', curTab); } }, [dock, open, curTab]);
  React.useEffect(() => {
    if (!open || !full) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setFull(false); try { localStorage.setItem('kh_proc_full', '0'); } catch (_) {} } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, full]);
  const [projOnly, setProjOnly] = useProcProjectOnly();          // 📁 chip ham sarlavhada, ↻ yonida
  const [dutyProjOnly, setDutyProjOnly] = useProcProjectOnly('kh_duty_project', true);   // 📡 Navbatchi — o'z chipi
  const dutyProject = dutyProjOnly && selectedProject && selectedProject.id ? selectedProject : null;
  // Popup ochiq bo'lsa ichidagi ProcessesPanel o'zi 7s'da poll qiladi — badge tick'i
  // dublikat so'rov yubormasin (tick closure'ida `open` eskirib qoladi → ref).
  const openRef = React.useRef(false);
  openRef.current = open;
  const [activeJobs, setActiveJobs] = React.useState(0);
  const [paused, setPaused] = React.useState(false);   // ⏸ engine cheklovda — tugmada ko'rinsin
  const ACTIVE = ['pending', 'pending_architecture', 'in_progress', 'awaiting_clarification', 'awaiting_plan_approval', 'awaiting_owner_approval'];
  const activeTaskCount = (tasks || []).filter(t =>
    ACTIVE.includes(t.status) || (t.sub_tasks || []).some(s => ACTIVE.includes(s.status))
  ).length;
  const total = activeTaskCount + activeJobs;

  // Poll for the badge count on ALL tabs (ProcessesPanel only polls when the popup is open).
  // authFetch/refreshTasks identity'lari har App render'da o'zgaradi — dep qilinsa effekt har
  // render'da qayta o'rnatilib darhol tick otadi (refetch-storm). Ref bilan barqaror.
  const flyFnsRef = React.useRef({ authFetch, refreshTasks });
  flyFnsRef.current = { authFetch, refreshTasks };
  React.useEffect(() => {
    let stopped = false; let n = 0;
    const tick = () => {
      if (!khPageVisible() || openRef.current || !canProcesses) return;
      const f = flyFnsRef.current;
      if (f.refreshTasks) { try { f.refreshTasks(); } catch {} }
      if (n++ % 3 === 0) {   // ~27s: engine cheklovda turibdimi (server 20s keshlaydi)
        f.authFetch(`${apiUrl}/engine/pause-state`)
          .then(r => (r.ok ? r.json() : null))
          .then(d => { if (!stopped && d && typeof d.paused === 'boolean') setPaused(!!(d.paused || d.me?.blocked)); })
          .catch(() => {});
      }
      f.authFetch(`${apiUrl}/jobs/active`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (!stopped && Array.isArray(d)) setActiveJobs(d.filter(j => j.status === 'running' || j.status === 'cancelling').length); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 9000);
    return () => { stopped = true; clearInterval(id); };
  }, [apiUrl, canProcesses]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showTask && !showChat && !showAgent && !showDuty) return null;
  return (
    <>
      <button
        onClick={() => { if (open) closePanel(); else { setOpen(true); if (curTab === 'chat') setChatMounted(true); if (curTab === 'agent') setAgentMounted(true); } }}
        title={agentOnly ? "🧠 Loyiha agenti — savol bering: kod, test muhit, vazifalar holati (🔎 savol rejimi)" : paused ? "Engine token cheklovida to'xtab turibdi — navbat kutmoqda (ichida sabab va vaqt)" : `Jarayonlar — vazifa berish, engine vazifalari + fon jarayonlari${showChat ? ', 💬 Boshqaruvchi bilan suhbat' : ''}`}
        className={`h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm transition border text-sm font-medium ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 hover:bg-primary-800 dark:hover:bg-primary-700' : paused ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/60' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
      >
        {paused ? <span className="text-base leading-none">⏸</span> : agentOnly ? <span className="text-base leading-none">🧠</span> : <NavIcon name="clock" className="w-5 h-5 shrink-0" />}
        <span className="text-sm font-medium hidden sm:inline">{agentOnly ? 'Agent' : 'Jarayonlar'}</span>
        {total > 0 && (
          <span className="min-w-[20px] h-5 px-1 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">{total}</span>
        )}
      </button>
      {open && (
        // Bitta panel, bir necha tab. Balandlik qat'iy (top-16…bottom-4; ❓ endi navbar'da — pastda joy kerak emas), tab
        // almashganda o'lcham sakramaydi. Ikkala tab ham MOUNT turadi (yashirin): suhbat/ovoz/📞 va vazifa
        // matni tab almashganda yo'qolmaydi; panel yopilganda chat unmount (avvalgi 🎙 oynasidek).
        <div className={`${full ? 'kh-proc-full fixed inset-2 sm:inset-4 z-[60] rounded-lg border' : dockOn ? 'kh-proc-dock fixed top-16 right-0 bottom-0 z-40 border-l max-w-[94vw]' : 'fixed top-16 right-4 bottom-4 z-50 w-[28rem] max-w-[94vw] rounded-lg border'} flex flex-col bg-gray-50 dark:bg-gray-900 shadow-lg border-gray-200 dark:border-gray-700 overflow-hidden`}
          style={dockOn ? { width: dockW } : undefined}>
          {dockOn && (
            // ↔ Chap chetini sudrab kenglikni o'zgartirish (qadalgan rejimda).
            <div onPointerDown={dockDown} onPointerMove={dockMove} onPointerUp={dockUp} title="Sudrab panel kengligini o'zgartiring"
              className="absolute left-0 top-0 bottom-0 w-1.5 z-10 cursor-col-resize hover:bg-primary-500/60 active:bg-primary-500 transition touch-none" />
          )}
          {/* ✕ ENG TEPADA (operator, 2026-09-25) — endi tablar bilan bir qatorda. ⏳ Jarayonlar tabida (📁 chip + ↻) tor oynaga
              (suzuvchi 28rem) sig'maydi — o'ng guruh 2-qatorga o'tadi; ilgari overflow-hidden 📌 ⛶ ✕ ni yashirardi (2026-10-30).
              Boshqa tablarda o'ralmaydi — ular bir qatorga sig'adi. */}
          <div className={`shrink-0 px-3 py-2 flex ${curTab === 'task' ? 'flex-wrap ' : ''}items-center justify-between gap-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700`}>
            {tabsAvail.length > 1 ? (
              <div className="inline-flex p-0.5 rounded-lg bg-gray-200/70 dark:bg-gray-800 shrink-0" role="tablist">
                {[['task', hideForm ? '⏳ Jarayonlar' : '📋 Vazifa', hideForm ? "Engine vazifalari va fon jarayonlari: holat, ✅ tasdiqlash, javob, qayta urinish. Vazifa berish — 🧠 Agent'da" : "Vazifa berish (matn aynan engine'ga, tekin) + engine vazifalari va fon jarayonlari"],
                  ['agent', '🧠 Agent', "Loyiha agenti — doimiy suhbat (Claude): holat, tashxis, server, vazifa, tasdiq, sinov, deploy/publish; o'chmaydi, eslab qoladi. 🎤 ovozli xabar va 📞 qo'ng'iroq ham shu yerda"],
                  ['duty', '📡 Navbatchi', "Navbatchi — har 10 daqiqada topilgan muammolar, o'zi tuzatganlari va ☀️ kunlik hisobot"],
                  ['chat', '💬 Suhbat', "🎙 Boshqaruvchi — savol bering yoki buyruq bering (OpenRouter, pullik; kunlik limit)"]].filter(([k]) => tabsAvail.includes(k)).map(([k, label, tip]) => (
                  <button key={k} type="button" role="tab" aria-selected={curTab === k} onClick={() => pickTab(k)} title={tip}
                    className={`relative px-3 py-1 rounded-md text-sm font-medium transition ${curTab === k ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
                    {label}
                    {k === 'duty' && dutyCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center">{dutyCount}</span>}
                  </button>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 min-w-0 text-sm font-semibold text-gray-900 dark:text-white">{agentOnly ? <span className="text-base leading-none">🧠</span> : <NavIcon name="clock" className="w-4 h-4 shrink-0" />}<span className="truncate">{agentOnly ? 'Loyiha agenti' : showTask ? 'Vazifa va jarayonlar' : '🎙 Boshqaruvchi'}</span></span>
            )}
            <div className="flex items-center gap-1 shrink-0 min-w-0 ml-auto">
              {curTab === 'task' && <ProcProjectChip on={projOnly} setOn={setProjOnly} project={selectedProject} />}
              {curTab === 'task' && (
                <button type="button" onClick={() => setRefreshSignal(n => n + 1)} title="Yangilash"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition">
                  <NavIcon name="refresh" className="w-5 h-5" />
                </button>
              )}
              {curTab === 'chat' && selectedProject && selectedProject.id && (
                <span className="max-w-[10rem] truncate text-xs px-2 py-1 rounded-md bg-gray-200/70 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                  title="Boshqaruvchi shu loyihani nazarda tutadi (boshqasini aytmasangiz). Loyiha tepadagi tanlagichdan.">
                  📁 <span translate="no">{selectedProject.name || `#${selectedProject.id}`}</span>
                </span>
              )}
              {!full && (
                <button type="button" onClick={toggleDock}
                  title={dock ? "Suzuvchi oyna qilish — panel yana sahifa ustida turadi" : "📌 Yonga qadash — panel sahifani to'smaydi, uni yon tomonga suradi: agent bilan gaplashib turib Khorezmai'ni ham bosasiz (kenglik chap chetini sudrab o'zgaradi)"}
                  aria-label={dock ? 'Suzuvchi oyna' : 'Yonga qadash'}
                  className={`p-1.5 rounded-lg transition text-base leading-none w-8 h-8 inline-flex items-center justify-center ${dock ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700'}`}>
                  📌
                </button>
              )}
              <button type="button" onClick={toggleFull} title={full ? "Kichraytirish (Esc)" : "To'liq ekran — o'qish va suhbat uchun"}
                aria-label={full ? 'Kichraytirish' : "To'liq ekran"}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition text-base leading-none w-8 h-8 inline-flex items-center justify-center">
                {full ? '🗗' : '⛶'}
              </button>
              <button type="button" onClick={closePanel} title="Yopish"
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition">
                <NavIcon name="xmark" className="w-5 h-5" />
              </button>
            </div>
          </div>
          {showAgent && agentMounted && (
            <div className={curTab === 'agent' ? 'flex-1 min-h-0' : 'hidden'}>
              <OperatorChat authFetch={authFetch} apiUrl={apiUrl} token={token} projectId={selectedProject.id}
                projectName={selectedProject.name} active={curTab === 'agent'} draft={agentDraft} />
            </div>
          )}
          {showDuty && (
            <div className={curTab === 'duty' ? 'flex-1 min-h-0' : 'hidden'}>
              <div className={full ? 'max-w-4xl mx-auto h-full' : 'h-full'}>
                <OverseerPanel authFetch={authFetch} apiUrl={apiUrl} active={open && curTab === 'duty'} onCount={setDutyCount}
                  projectId={dutyProject ? dutyProject.id : null} projectName={dutyProject ? (dutyProject.name || `#${dutyProject.id}`) : null}
                  filterChip={<ProcProjectChip on={dutyProjOnly} setOn={setDutyProjOnly} project={selectedProject}
                    tip={`Yoqilsa — faqat ${(selectedProject && selectedProject.name) || 'tanlangan loyiha'} bo'yicha Navbatchi topilmalari va ☀️ hisobot; o'chirsangiz — barcha loyihalar`} />}
                  onAsk={showAgent ? askAgent : null} />
              </div>
            </div>
          )}
          {showChat && chatMounted && (
            <div className={curTab === 'chat' ? 'flex-1 min-h-0' : 'hidden'}>
              <ConductorChat authFetch={authFetch} apiUrl={apiUrl} token={token} channel="web" embedded
                projectId={selectedProject && selectedProject.id ? selectedProject.id : null}
                onUiAction={(ui) => { if (ui && ui.action === 'task_draft') pickTab('task'); if (onUiAction) onUiAction(ui); }}
                active={curTab === 'chat'} onOpenTask={() => pickTab('task')}
                topSlot={<ChatNowStrip authFetch={authFetch} apiUrl={apiUrl} selectedProject={selectedProject}
                  active={curTab === 'chat'} onOpen={() => pickTab('task')} />} />
            </div>
          )}
          {showTask && (
          <div className={curTab === 'task' ? 'flex-1 min-h-0 overflow-auto p-4' : 'hidden'}>
          <div className={full ? 'max-w-5xl mx-auto' : ''}>
          {taskForm && !hideForm && <div className="mb-3">{taskForm}</div>}
          {hideForm && ctxPanel && <div className="mb-2">{ctxPanel}</div>}
          <ProcessesPanel
            authFetch={authFetch}
            apiUrl={apiUrl}
            tasks={tasks}
            refreshTasks={refreshTasks}
            onGoConsole={onGoConsole}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            loadingMore={loadingMore}
            compact
            refreshSignal={refreshSignal}
            selectedProject={selectedProject}
            projOnly={projOnly}
            setProjOnly={setProjOnly}
            onAgentTest={onAgentTest}
            agentTestOk={agentTestOk}
          />
          </div>
          </div>
          )}
        </div>
      )}
    </>
  );
}


// 🗂 B2 (2026-10-04): 💬 Suhbat tabining tepasidagi «Hozir bajarilmoqda» qatori — tanlangan loyihaning
// faol vazifalari + ishlayotgan fon jarayonlari (loyiha tanlanmagan bo'lsa — hammasi). Yopiq holda faqat
// soni; ochilsa ⏹ bilan ro'yxat. Faol ish yo'q bo'lsa umuman chizilmaydi (joy egallamaydi).
// `/tasks?limit=1` — server faol super-tasklarni limitdan qat'i nazar DOIM qo'shadi.
function ChatNowStrip({ authFetch, apiUrl, selectedProject, active, onOpen }) {
  const [tasks, setTasks] = React.useState([]);
  const [jobs, setJobs] = React.useState([]);
  const [busy, setBusy] = React.useState({});
  const [open, setOpenRaw] = React.useState(() => { try { return localStorage.getItem('kh_chat_now_open') === '1'; } catch (e) { return false; } });
  const setOpen = (v) => { setOpenRaw(v); try { localStorage.setItem('kh_chat_now_open', v ? '1' : '0'); } catch (e) {} };
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const pid = selectedProject && selectedProject.id ? String(selectedProject.id) : '';
  const ACT = ['pending', 'pending_architecture', 'in_progress', 'awaiting_clarification', 'awaiting_plan_approval', 'awaiting_owner_approval'];
  const LBL = { pending_architecture: '🧠 Rejalashtirilmoqda', pending: '⏳ Kutilmoqda', in_progress: '🔄 Jarayonda',
    awaiting_clarification: '❓ Aniqlik kerak', awaiting_plan_approval: '📝 Reja kutmoqda', awaiting_owner_approval: '🙋 Ruxsat kutmoqda' };
  const JOB_ICON = { logo: '🏷️', design_logo: '🖼️', tz: '📐', keyfix: '🔑' };
  const load = React.useCallback(async () => {
    try {
      const [rt, rj] = await Promise.all([
        afRef.current(`${apiUrl}/tasks?limit=1${pid ? `&project_id=${encodeURIComponent(pid)}` : ''}`),
        afRef.current(`${apiUrl}/jobs/active`),
      ]);
      const t = rt.ok ? await rt.json().catch(() => []) : [];
      const j = rj.ok ? await rj.json().catch(() => []) : [];
      setTasks((Array.isArray(t) ? t : []).filter(x => ACT.includes(x.status) || (x.sub_tasks || []).some(s => ACT.includes(s.status))));
      setJobs((Array.isArray(j) ? j : []).filter(x => (x.status === 'running' || x.status === 'cancelling') && (!pid || String(x.project_id) === pid)));
    } catch (e) {}
  }, [apiUrl, pid]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!active) return undefined;
    load();
    const t = setInterval(() => { if (khPageVisible()) load(); }, 9000);
    return () => clearInterval(t);
  }, [active, load]);
  const stop = async (kind, id) => {
    if (kind === 't' && !window.confirm(`Vazifa #${id} to'xtatilsinmi? Bajarilayotgan agent jarayoni o'ldiriladi va o'zgarishlar bekor qilinadi (push/deploy bo'lmaydi).`)) return;
    setBusy(b => ({ ...b, [kind + id]: true }));
    try {
      const r = await afRef.current(kind === 't' ? `${apiUrl}/tasks/${id}/stop` : `${apiUrl}/jobs/${id}/cancel`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || (kind === 'j' && !d.ok)) throw new Error(d.error || 'Server xatosi');
      toast.success(kind === 't' ? `⏹ To'xtatish so'raldi (vazifa #${id})` : "⏹ To'xtatish so'raldi");
      setTimeout(load, 1500);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(b => ({ ...b, [kind + id]: false })); }
  };
  const n = tasks.length + jobs.length;
  if (!n) return null;
  const stopBtn = 'shrink-0 px-1.5 py-0.5 text-[11px] bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-800/70 disabled:opacity-50';
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 bg-amber-50/70 dark:bg-amber-900/10">
      <button type="button" onClick={() => setOpen(!open)} title="Hozir ishlayotgan vazifalar va fon jarayonlari"
        className="w-full px-4 py-1.5 flex items-center gap-2 text-xs font-medium text-amber-900 dark:text-amber-200">
        <span className="animate-pulse">●</span>
        <span>Hozir bajarilmoqda ({n}){pid && selectedProject.name ? <> · <span translate="no">{selectedProject.name}</span></> : ''}</span>
        <span className="ml-auto text-amber-700 dark:text-amber-300">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-3 pb-2 space-y-1 max-h-48 overflow-y-auto">
          {tasks.map(t => (
            <div key={'t' + t.id} className="flex items-center gap-2 text-xs bg-white/80 dark:bg-gray-800/80 rounded-md px-2 py-1">
              <button type="button" onClick={onOpen} title="📋 Vazifa tabida ochish" className="font-semibold text-primary-700 dark:text-primary-400 hover:underline shrink-0">#{t.id}</button>
              <span className="truncate text-gray-700 dark:text-gray-300 flex-1 min-w-0" translate="no" title={t.instruction}>{t.instruction}</span>
              <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">{LBL[t.status] || LBL[((t.sub_tasks || []).find(s => ACT.includes(s.status)) || {}).status] || t.status}</span>
              <button type="button" onClick={() => stop('t', t.id)} disabled={busy['t' + t.id]} title="To'xtatish" className={stopBtn}>⏹</button>
            </div>
          ))}
          {jobs.map(j => (
            <div key={'j' + j.id} className="flex items-center gap-2 text-xs bg-white/80 dark:bg-gray-800/80 rounded-md px-2 py-1">
              <span className="shrink-0">{JOB_ICON[j.job_type] || '⚙️'}</span>
              <span className="truncate text-gray-700 dark:text-gray-300 flex-1 min-w-0" title={j.detail || j.title}>{j.title || `Jarayon #${j.id}`}</span>
              <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">{j.total ? `${j.done || 0}/${j.total}` : '…'}</span>
              <button type="button" onClick={() => stop('j', j.id)} disabled={busy['j' + j.id] || j.status === 'cancelling'} title="To'xtatish" className={stopBtn}>⏹</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 🩺 Salomatlik flyout — ⏳ Jarayonlar naqshi (2026-09-18, operator: «Salomatlikni jarayonlarga
// o'xshatib tepaga chiqar va badge qo'sh»). Har tabda tepada tugma + badge: QIZIL = ishlamayotgan
// (fail) soni, AMBER = faqat e'tibor (warn) bor; hech biri yo'q — xira tugma. Popup faqat NOSOZ
// tekshiruvlarni ko'rsatadi (✅ larni emas — 41 ta qator popup uchun shovqin), har birida ▶ va
// pastda to'liq sahifaga havola. Manba — mavjud `GET /health-checks` (scoped, `summary` bilan;
// yengil — stack/ssh yo'q), 30s poll (monitor ham 30s tik). authFetch ref orqali (refetch-storm).
// 📜 Engine loglari — navbar'dagi tugma (operator, 2026-11-04: Studio pastidagi «Engine loglar & Vazifa berish» paneli
// sahifa ostida joy egallardi — endi ❓ / Serverlar / ⚡ qatorida, bosilsa panel). Qatorlar App socket'idan (`new_log`,
// oxirgi 100 ta; server faqat a'zo loyihalaringiz vazifalarini yuboradi). Yopiq tugmada 🟢 — oxirgi 15 s da log kelgan.
function EngineLogFlyout({ logs, onClear }) {
  const [open, setOpen] = React.useState(false);
  const [freshAt, setFreshAt] = React.useState(0);
  const [, setTick] = React.useState(0);
  const boxRef = React.useRef(null);
  const stickRef = React.useRef(true);          // pastda turgan bo'lsa yangi qatorga ergashadi; yuqoriga o'qisa — joyida qoladi
  const last = logs.length ? logs[logs.length - 1] : null;
  React.useEffect(() => { if (last) setFreshAt(Date.now()); }, [last]);
  React.useEffect(() => {
    if (!freshAt) return undefined;
    const t = setTimeout(() => setTick(x => x + 1), 15000);
    return () => clearTimeout(t);
  }, [freshAt]);
  React.useEffect(() => {
    const el = boxRef.current;
    if (open && el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [open, logs]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const live = !!freshAt && Date.now() - freshAt < 15000;
  return (
    <>
      <button onClick={() => { stickRef.current = true; setOpen(o => !o); }}
        data-kh="engine-logs"
        title={live ? 'Engine loglari — hozir yozilmoqda' : 'Engine loglari'}
        aria-label="Engine loglari"
        className={`relative h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm text-sm font-medium transition border
          ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
        <NavIcon name="terminal" className="w-5 h-5" />
        {live && !open && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />}
      </button>
      {open && (
        <div className="fixed top-16 right-4 z-50 w-[46rem] max-w-[94vw] rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 dark:text-white">Engine loglari</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Loyihalaringizdagi vazifalar jonli · oxirgi {logs.length} ta yozuv</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {logs.length > 0 && onClear && (
                <button onClick={onClear} title="Ko'rinishni tozalash (loglarning o'zi o'chmaydi)"
                  className="text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Tozalash</button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Yopish" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none px-1">✕</button>
            </div>
          </div>
          <div ref={boxRef}
            onScroll={(e) => { const el = e.currentTarget; stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
            className="bg-gray-900 rounded-b-lg p-4 overflow-y-auto" style={{ height: '60vh' }}>
            <div className="text-green-400 font-mono text-sm whitespace-pre-wrap break-words">
              {logs.length === 0 ? (
                <div>[Kutish rejimi] Engine loglari bu yerda ko'rinadi...</div>
              ) : (
                logs.map((log, index) => (
                  <span key={index} className={log.isError ? 'text-red-400' : 'text-green-400'}>
                    {log.taskId ? `[Task ${log.taskId}] ` : ''}
                    {log.finished ? '\n---- TUGADI ----\n' : log.output}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HealthFlyout({ authFetch, apiUrl, onOpenFull }) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);     // {checks, summary}
  const [busy, setBusy] = React.useState('');
  const afRef = React.useRef(authFetch); afRef.current = authFetch;
  const load = React.useCallback(async () => {
    try {
      const r = await afRef.current(`${apiUrl}/health-checks`);
      const d = await r.json().catch(() => null);
      if (r.ok && d && Array.isArray(d.checks)) setData(d);
    } catch { /* transient — eski holat qoladi */ }
  }, [apiUrl]);
  React.useEffect(() => {
    load();
    const t = setInterval(() => { if (khPageVisible()) load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);
  const fail = data?.summary?.fail || 0;
  const warn = data?.summary?.warn || 0;
  const problems = (data?.checks || []).filter(c => c.status === 'fail' || c.status === 'warn');
  const ago = (t) => {
    if (!t) return 'hech qachon';
    const m = Math.round((Date.now() - new Date(t).getTime()) / 60000);
    if (m < 1) return 'hozir'; if (m < 60) return `${m} daq oldin`;
    const h = Math.floor(m / 60); return h < 24 ? `${h} soat oldin` : `${Math.floor(h / 24)} kun oldin`;
  };
  const runOne = async (id) => {
    setBusy('run' + id);
    try { await afRef.current(`${apiUrl}/health-checks/${id}/run`, { method: 'POST' }); await load(); }
    catch { /* natija keyingi poll'da */ } finally { setBusy(''); }
  };
  if (!data) return null;
  const tone = fail ? 'fail' : warn ? 'warn' : 'ok';
  return (
    <>
      <button onClick={() => { setOpen(o => !o); load(); }}
        title={fail || warn ? `${fail} ta ishlamayapti · ${warn} ta e'tibor talab qiladi` : "Servislar salomatligi — hammasi ishlayapti"}
        className={`h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm text-sm font-medium transition border
          ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600'
            : tone === 'fail' ? 'bg-white dark:bg-gray-800 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30'
            : tone === 'warn' ? 'bg-white dark:bg-gray-800 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/30'
            : 'bg-white/70 dark:bg-gray-800/70 text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800'}`}>
        <span className="text-base leading-none">🩺</span>
        {fail > 0 && <span className="min-w-[20px] h-5 px-1 flex items-center justify-center text-xs font-bold text-white bg-red-600 rounded-full animate-pulse">{fail}</span>}
        {fail === 0 && warn > 0 && <span className="min-w-[20px] h-5 px-1 flex items-center justify-center text-xs font-bold text-white bg-amber-500 rounded-full">{warn}</span>}
      </button>
      {open && (
        <div className="fixed top-16 right-4 z-50 w-[28rem] max-w-[92vw] max-h-[70vh] overflow-auto rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">🩺 Salomatlik</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {data.summary?.total || 0} tekshiruv · ✅ {data.summary?.ok || 0} · ⚠️ {warn} · ❌ {fail}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} title="Yangilash" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm px-1">↻</button>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none">✕</button>
            </div>
          </div>
          {problems.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-emerald-700 dark:text-emerald-400">✅ Barcha servislar ishlayapti</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {problems.map(c => (
                <div key={c.id} className="px-4 py-2.5 flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">{c.status === 'fail' ? '❌' : '⚠️'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={c.name}>{c.name}</div>
                    {c.detail && <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{String(c.detail).slice(0, 160)}</div>}
                    <div className="text-[11px] text-gray-400 mt-0.5">{ago(c.lastRunAt)}{c.failCount > 1 ? ` · ${c.failCount} marta ketma-ket` : ''}</div>
                  </div>
                  <button onClick={() => runOne(c.id)} disabled={busy === 'run' + c.id} title="Hozir qayta tekshirish"
                    className="shrink-0 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    {busy === 'run' + c.id ? '⏳' : '▶'}
                  </button>
                </div>
              ))}
            </div>
          )}
          {onOpenFull && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <button onClick={() => { setOpen(false); onOpenFull(); }}
                className="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline">🩺 To'liq sahifa — barcha tekshiruvlar, sozlash, Telegram →</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function EngineLimitPanel({ authFetch, apiUrl }) {
  const [data, setData] = React.useState(null);

  const load = React.useCallback(() => {
    authFetch(`${apiUrl}/token-usage/limits`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) return;
        setData(d);
      }).catch(() => {});
  }, [authFetch, apiUrl]);

  React.useEffect(() => { load(); const t = setInterval(() => { if (khPageVisible()) load(); }, 60000); return () => clearInterval(t); }, [load]);
  if (!data) return null;

  return (
    <div className="mb-3 px-2">
      <div className="bg-gray-800 rounded-lg p-2 space-y-1.5">
        <div className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide">
          ⚙️ Engine {data.enabled
            ? <span className="text-emerald-400 normal-case">• cheklov yoniq</span>
            : <span className="text-gray-600 dark:text-gray-400 normal-case">• cheklov o'chiq</span>}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          🧵 Parallellik: <span className="text-gray-300 font-semibold">{data.concurrency || 1}</span> ta vazifa bir vaqtda
        </div>
        {/* Token bar'lari ATAYIN yo'q — sarf va cap Konfiguratsiya sahifasida
            (u yerda cap raqamlari ham shu yerda ko'rinadi), har foydalanuvchiniki
            esa Foydalanuvchilar sahifasida. Yon menyu faqat qisqa holat. */}
        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug pt-1.5 border-t border-gray-700">
          Sozlash: <span className="text-gray-400">Konfiguratsiya</span> → ⚙️ Engine cheklovi.<br/>
          Har foydalanuvchiga alohida: <span className="text-gray-400">Foydalanuvchilar</span>.
        </p>
      </div>
    </div>
  );
}

// Claude-style "Kontekst oynasi" panel for the task-creation form: shows each
// module's Claude Code session context-window fill + cache effectiveness, and a
// continue-vs-new-session toggle (controls the engine's --resume behaviour for the
// next task). Pure presentational — data comes from /token-usage/context/:projectId.
// ctxOnly — faqat modullar kontekst oynasi (⏳ Jarayonlar sarlavhasi uchun: operatorda vazifa formasi yo'q, vazifa 🧠 Agent orqali).
function ContextWindowPanel({ sessions, newSessionMode, setNewSessionMode, runMode, setRunMode, autoDeploy, setAutoDeploy, planReview, setPlanReview, afterTest, setAfterTest, ctxOnly = false }) {
  // IXCHAM rejim: modul-kontekst barlari default yig'ilgan (🧠 chip → ▾ batafsil);
  // uzun izohlar tooltip'larga ko'chirilgan — funksiya yo'qolmagan, joy tejaladi.
  const [ctxOpen, setCtxOpen] = React.useState(false);
  const active = (sessions || []).filter(s => s.active);
  const barColor = (pct) => pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const pctColor = (pct) => pct >= 90 ? 'text-red-600 dark:text-red-400' : pct >= 70 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
  const fmt = (n) => (n || 0).toLocaleString();
  const icon = (t) => t === 'backend' ? '⚙️' : t === 'mobile' ? '📱' : '🌐';
  const segBtn = (on) => `px-2 py-1 font-medium transition ${on ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`;
  const chipBtn = (on) => `px-2 py-1 rounded-lg border text-[11px] font-medium transition ${on ? 'bg-primary-700 dark:bg-primary-600 border-primary-700 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`;
  return (
    <div className="mb-3 border-t border-gray-100 dark:border-gray-700 pt-2.5 space-y-1.5">
      {/* Qator 1: sessiya (davom/yangi) + 🧠 modul kontekstlari (ixcham chip, ▾ = batafsil) */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {!ctxOnly && (
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 text-[11px] shrink-0">
          <button
            type="button"
            onClick={() => setNewSessionMode(false)}
            title="Modullar oldingi Claude sessiyasini davom ettiradi — kontekst to'planadi va prompt-kesh ishlaydi (tezroq, allowance tejaladi)."
            className={segBtn(!newSessionMode)}
          >♻️ Davom</button>
          <button
            type="button"
            onClick={() => setNewSessionMode(true)}
            title="Toza sessiya — oldingi kontekst tashlanadi (kontekst chalkash bo'lib qolganda yoki yangi yo'nalishda foydali)."
            className={segBtn(newSessionMode)}
          >🆕 Yangi</button>
        </div>
        )}
        {ctxOnly && <span className="text-[11px] text-gray-500 dark:text-gray-400">Modullar sessiyasi:</span>}
        {active.length > 0 ? (
          <button
            type="button"
            onClick={() => setCtxOpen(o => !o)}
            title="Kontekst oynasi (Claude sessiyasi) — har modul sessiyasining to'lganligi. Batafsil: bosing."
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-[11px] text-gray-500 dark:text-gray-400 transition"
          >
            <span>🧠</span>
            {active.map(s => (
              <span key={s.module_id} className="whitespace-nowrap">{icon(s.module_type)}<b className={pctColor(s.percent)}>{s.percent}%</b></span>
            ))}
            <span className="text-gray-400">{ctxOpen ? '▴' : '▾'}</span>
          </button>
        ) : (
          <span className="text-[10px] text-gray-400">
            {!ctxOnly && newSessionMode ? 'Toza sessiyadan boshlanadi' : "🧠 Hali sessiya yo'q"}
          </span>
        )}
      </div>
      {ctxOpen && active.length > 0 && (
        <div className="space-y-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg p-2">
          {active.map(s => (
            <div key={s.module_id}>
              <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5 gap-2">
                <span className="truncate font-medium text-gray-600 dark:text-gray-400">{icon(s.module_type)} {s.module_name}</span>
                <span className="tabular-nums shrink-0">{fmt(s.context_tokens)}/{fmt(s.window_tokens)} · {s.percent}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full ${barColor(s.percent)} rounded-full transition-all`} style={{ width: `${Math.min(100, s.percent)}%` }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">
                ♻️ kesh {s.cache_percent || 0}%{s.num_turns ? ` · ${s.num_turns} navbat` : ''}{s.model ? ` · ${s.model}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* Qator 2: navbat rejimi + avto-deploy + video-test — chip'lar (izoh tooltip'da) */}
      {(setRunMode || setAutoDeploy || setPlanReview) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {setPlanReview && (
            <button
              type="button"
              onClick={() => setPlanReview(!planReview)}
              title="Reja tasdig'i: kod yozishdan OLDIN Khorezmai qaysi faylni, nimani va nega o'zgartirishini batafsil ko'rsatadi. Siz ✅ tasdiqlaguningizcha (yoki ✏️ izoh bilan qayta tuzdirguningizcha) ish boshlanmaydi."
              className={chipBtn(!!planReview)}
            >📝 Reja tasdig'i{planReview ? ' ✓' : ''}</button>
          )}
          {setAfterTest && (
            <button
              type="button"
              onClick={() => setAfterTest(!afterTest)}
              title="Tugagach tasdiqlab sina: vazifa tugagach Khorezmai O'ZI tasdiqlaydi (merge + test deploy/APK), Nazoratchi qurilmada/brauzerda sinaydi, kamchilik chiqsa bitta tuzatish vazifasini ochib qayta sinaydi. Belgilanmasa — vazifa tugagach faqat so'raydi."
              className={chipBtn(!!afterTest)}
            >🔁 Tugagach sina{afterTest ? ' ✓' : ''}</button>
          )}
          {/* ⚡Parallel / ⛓Ketma-ket tugmalari OLIB TASHLANDI (operator so'rovi):
              qarorni ENGINE o'zi qabul qiladi. Ikki vazifa bitta modulga tegsa
              (bir xil workspace + review branch), engine ularni split bosqichida
              avtomat ketma-ket qiladi; tegmasa — parallel ketaveradi. Rejadan
              (📋 Reja) tug'ilgan vazifalar esa doska tartibida navbatga turadi.
              Vazifa kartasidagi «⛓ Ketma-ket» chipi qaror natijasini ko'rsatadi. */}
          {setAutoDeploy && (
            <button
              type="button"
              onClick={() => setAutoDeploy(!autoDeploy)}
              title="Avto-merge + deploy: vazifa tugagach review branch avtomatik asosiy branchga merge qilinib deploy qilinadi — qo'lda ✅ Tasdiqlash bosish shart emas."
              className={chipBtn(!!autoDeploy)}
            >🚀 Avto-deploy{autoDeploy ? ' ✓' : ''}</button>
          )}
        </div>
      )}
    </div>
  );
}

// ADMIN: barcha foydalanuvchilar emulatorlari ro'yxati — kim, qaysi emulator, qurilma
// turi, ishlayaptimi/to'xtaganmi, xotira/CPU sarfi, uptime. Jonli docker holati backenddan
// (GET /emulator/admin/list). 10s da avto-yangilanadi.
function AdminEmulatorsPanel({ authFetch, apiUrl }) {
  const [data, setData] = React.useState(null);   // {count, running, emulators:[]}
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [stopping, setStopping] = React.useState(null);   // e.id — ⏹ ketayotgan qator

  const load = React.useCallback((spin = false) => {
    if (spin) setLoading(true);
    authFetch(`${apiUrl}/emulator/admin/list`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('xato')))
      .then(d => { if (d && Array.isArray(d.emulators)) { setData(d); setErr(''); } })
      .catch(() => setErr("Ma'lumot olinmadi"))
      .finally(() => setLoading(false));
  }, [authFetch, apiUrl]);

  React.useEffect(() => {
    load(true);
    const t = setInterval(() => { if (khPageVisible()) load(false); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  // ⏹ Admin istalgan foydalanuvchining emulatorini to'xtatadi (ilovalar/ma'lumot saqlanadi). Agent sinovi
  // ketayotgan bo'lsa backend 409 qaytaradi — ikkinchi tasdiq bilan force.
  const stopEmu = async (e) => {
    if (!window.confirm(`${e.username} — ${e.emulatorLabel} to'xtatilsinmi?\n\nIlovalar va ma'lumot saqlanadi, foydalanuvchi keyin qayta yoqishi mumkin. Unga sabab ko'rsatiladi.`)) return;
    setStopping(e.id);
    try {
      const send = (force) => authFetch(`${apiUrl}/emulator/admin/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: e.userId, slot: e.slot, force }) });
      let r = await send(false);
      let d = await r.json().catch(() => ({}));
      if (r.status === 409 && d.agentBusy) {
        if (!window.confirm(`⚠️ ${d.error}.\n\nBaribir to'xtatilsinmi?`)) return;
        r = await send(true); d = await r.json().catch(() => ({}));
      }
      if (!r.ok) throw new Error(d.error || "To'xtatib bo'lmadi");
      toast.success(`⏹ ${e.username} — ${e.emulatorLabel} to'xtatildi`);
      load(false);
    } catch (x) { toast.error(x.message || "To'xtatib bo'lmadi"); }
    finally { setStopping(null); }
  };

  // 👁 Istalgan emulatorni alohida oynada jonli ko'rish (+ ✋ boshqaruvni olish) — AdminEmuViewer (/emu-view).
  const openView = (e) => window.open(`/emu-view?user=${e.userId}&slot=${e.slot}&label=${encodeURIComponent(`${e.username} · ${e.emulatorLabel}`)}`,
    `kh_emuview_${e.userId}_${e.slot}`, 'width=560,height=1020');

  const fmtDate = (s) => { try { return s ? new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'; } catch { return '—'; } };
  const statusOf = (e) => {
    if (e.running) return { dot: '🟢', label: 'Ishlayapti', cls: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' };
    if (e.liveState === 'exited') return { dot: '⚪', label: "To'xtatilgan", cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' };
    if (e.liveState === 'created') return { dot: '🟡', label: 'Ishlamayapti', cls: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400' };
    return { dot: '⚫', label: 'Yaratilmagan', cls: 'bg-gray-50 dark:bg-gray-700 text-gray-400' };
  };

  const emus = data?.emulators || [];
  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">📱 Emulatorlar</h1>
        <div className="flex items-center gap-3">
          {data && (
            <div className="flex items-center gap-2 text-sm">
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full font-medium">Jami: {data.count}</span>
              <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-3 py-1 rounded-full font-medium">🟢 Ishlayapti: {data.running}</span>
            </div>
          )}
          <button onClick={() => load(true)} className="text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition" title="Yangilash">↻</button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4">Jonli holat (docker) — har 10 soniyada avtomatik yangilanadi. Emulatorlar va test muhitlari backend serverda ishlaydi.</p>

      {err && <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2">{err}</div>}
      {loading && !data && <p className="text-gray-400 text-center py-10">Yuklanmoqda…</p>}
      {data && emus.length === 0 && <p className="text-gray-400 text-center py-10">Hech qanday emulator yo'q</p>}

      {emus.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="px-4 py-3 font-semibold">Foydalanuvchi</th>
                <th className="px-4 py-3 font-semibold">Emulator</th>
                <th className="px-4 py-3 font-semibold">Qurilma</th>
                <th className="px-4 py-3 font-semibold">Holat</th>
                <th className="px-4 py-3 font-semibold">Xotira</th>
                <th className="px-4 py-3 font-semibold">CPU</th>
                <th className="px-4 py-3 font-semibold">Ishlash vaqti</th>
                <th className="px-4 py-3 font-semibold">ADB / Port</th>
                <th className="px-4 py-3 font-semibold">Oxirgi faollik</th>
              </tr>
            </thead>
            <tbody>
              {emus.map(e => {
                const st = statusOf(e);
                return (
                  <tr key={e.id} className="border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{e.username}</div>
                      <div className="text-xs text-gray-400">{e.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{e.emulatorLabel}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700 dark:text-gray-300">{e.deviceLabel}</div>
                      <div className="text-xs text-gray-400">Android {e.android} · {e.resolution}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>
                        <span>{st.dot}</span>{st.label}
                      </span>
                      {e.running && (
                        <div className="flex flex-col items-start gap-1 mt-1.5">
                          <button onClick={() => openView(e)}
                            title="Emulatorni alohida oynada jonli ko'rish; u yerda «✋ Boshqaruvni olish» — egasi shu payt boshqara olmaydi"
                            className="text-xs px-2.5 py-1 rounded-lg border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition">
                            👁 Ko'rish
                          </button>
                          {e.adminLock && <span className="text-[11px] text-amber-600 dark:text-amber-400" title={`${new Date(e.adminLock.since).toLocaleTimeString('ru-RU')} dan beri`}>🔒 {e.adminLock.by} boshqaryapti</span>}
                          <button onClick={() => stopEmu(e)} disabled={stopping === e.id}
                            title="Emulatorni to'xtatish — ilovalar va ma'lumot saqlanadi, foydalanuvchi qayta yoqishi mumkin"
                            className="text-xs px-2.5 py-1 rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition">
                            {stopping === e.id ? '⏳ To\'xtatilmoqda…' : '⏹ To\'xtatish'}
                          </button>
                          {e.agent && <span className="text-[11px] text-amber-600 dark:text-amber-400" title={e.agent.label || ''}>🤖 agent sinovi ishlayapti</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {e.memUsage
                        ? <div><span className="text-gray-700 dark:text-gray-300">{e.memUsage}</span>{e.memPerc && <span className="text-xs text-gray-400 ml-1">({e.memPerc})</span>}</div>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{e.cpuPerc || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">{e.uptime || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{e.adbDevice}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{fmtDate(e.lastUsed)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function AdminTokenStats({ authFetch, apiUrl }) {
  // Token-markazli (Claude Max — $ narx/profit yo'q): foydalanuvchi bo'yicha
  // jami/bugungi tokenlar + to'liq tarix.
  const [users, setUsers] = React.useState([]);
  const [show, setShow] = React.useState(false);
  const [tab, setTab] = React.useState('summary');       // 'summary' | 'history'
  const [hist, setHist] = React.useState([]);
  const [histTotal, setHistTotal] = React.useState(0);
  const [histOffset, setHistOffset] = React.useState(0);
  const [histTokens, setHistTokens] = React.useState(0); // jami tokenlar (barcha tarix)

  const load = React.useCallback(() => {
    authFetch(`${apiUrl}/token-usage/users-spending`)
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [authFetch, apiUrl]);

  const loadHistory = React.useCallback((offset = 0) => {
    authFetch(`${apiUrl}/token-usage/admin-history?limit=20&offset=${offset}`)
      .then(r => r.json())
      .then(d => {
        setHist(d.rows || []); setHistTotal(d.total || 0); setHistOffset(offset);
        setHistTokens(d.tokensTotal || 0);
      }).catch(() => {});
  }, [authFetch, apiUrl]);

  React.useEffect(() => {
    load();
    const t = setInterval(() => { if (khPageVisible()) load(); }, 120000);
    return () => clearInterval(t);
  }, [load]);

  const totalTok = users.reduce((s, u) => s + parseInt(u.total_tokens || 0), 0);
  const todayTok = users.reduce((s, u) => s + parseInt(u.today_tokens || 0), 0);

  return (
    <>
      <div className="mb-3 px-2 cursor-pointer group" onClick={() => { setShow(true); load(); }} title="Token sarfi (foydalanuvchilar)">
        <div className="bg-gray-800 rounded-lg p-2">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-400">👥 Token sarfi</span>
            <span className="text-gray-300 font-mono">{totalTok.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>jami token</span>
            <span className="text-gray-400">bugun: {todayTok.toLocaleString()}</span>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 px-1 group-hover:text-gray-400 transition mt-0.5">📊 Batafsil ko'rish</p>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShow(false)}>
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full ${tab === 'history' ? 'max-w-4xl' : 'max-w-xl'} max-h-[80vh] flex flex-col`}>
            <div className="px-6 py-4 border-b shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">👥 Token sarfi</h2>
                <button onClick={() => setShow(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl">×</button>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setTab('summary')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${tab === 'summary' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  📊 Jami
                </button>
                <button onClick={() => { setTab('history'); loadHistory(0); }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${tab === 'history' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  🕑 Tarix
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {tab === 'summary' ? (
                  <>Jami: <b>{totalTok.toLocaleString()} token</b> &nbsp;•&nbsp; Bugun: <b>{todayTok.toLocaleString()} token</b></>
                ) : (
                  <>Jami: <b>{histTokens.toLocaleString()} token</b> &nbsp;•&nbsp; {histTotal} ta chaqiruv (Claude Max — to'lov yo'q)</>
                )}
              </p>
            </div>
            {tab === 'summary' && (
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Foydalanuvchi</th>
                    <th className="text-right px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Jami token</th>
                    <th className="text-right px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Bugun token</th>
                    <th className="text-right px-4 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Chaqiruvlar</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300 text-xs">{u.username}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-gray-700 dark:text-gray-300">{parseInt(u.total_tokens).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-gray-500 dark:text-gray-400">{parseInt(u.today_tokens).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-gray-500 dark:text-gray-400">{parseInt(u.calls || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {tab === 'history' && (
              <>
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Sana</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Foydalanuvchi</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Xususiyat</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">So'rov</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Model</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 dark:text-gray-400 font-medium">Tokenlar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hist.map(row => (
                        <tr key={row.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">{new Date(row.created_at).toLocaleString('uz-UZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.username || '—'}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap"><span className="bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-0.5 rounded-full">{FEATURE_LABELS[row.feature] || row.feature}</span></td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={row.prompt_preview}>{row.prompt_preview || '—'}</td>
                          <td className="px-3 py-2 text-xs text-right font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">{(row.model || '').replace(/^openrouter\//, '')}</td>
                          <td className="px-3 py-2 text-xs text-right font-mono text-gray-700 dark:text-gray-300">{(row.total_tokens || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      {!hist.length && <tr><td colSpan="6" className="text-center py-10 text-gray-400 text-sm">Maʼlumot yo'q</td></tr>}
                    </tbody>
                  </table>
                </div>
                {histTotal > 20 && (
                  <div className="px-6 py-3 border-t flex justify-between items-center shrink-0">
                    <span className="text-xs text-gray-400">{histOffset + 1}–{Math.min(histOffset + 20, histTotal)} / {histTotal}</span>
                    <div className="flex gap-2">
                      <button disabled={histOffset === 0} onClick={() => loadHistory(histOffset - 20)} className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">← Oldingi</button>
                      <button disabled={histOffset + 20 >= histTotal} onClick={() => loadHistory(histOffset + 20)} className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">Keyingi →</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}


function ServerStats({ authFetch, apiUrl, flyout }) {
  const [data, setData] = React.useState(null);
  // 🖥 flyout rejimi (navbar tugmasi) — popup ochiq-yopiqligi
  const [open, setOpen] = React.useState(false);
  // Collapsed by default — persists across sessions via localStorage
  const [expanded, setExpanded] = React.useState(() => {
    try { return localStorage.getItem('serverStats.expanded') === '1'; } catch { return false; }
  });
  const toggle = () => {
    setExpanded(v => {
      const next = !v;
      try { localStorage.setItem('serverStats.expanded', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  React.useEffect(() => {
    // 403 = bu foydalanuvchiga server ko'rsatkichlari ko'rsatilmaydi →
    // `data` null qoladi va blok UMUMAN chizilmaydi (pastdagi `if (!data) return null`).
    // Xato javobni setData qilib qo'ysak nol qiymatli jadval chiqib qolardi.
    const fetchStats = () => authFetch(`${apiUrl}/server/stats`)
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setData(d); }).catch(() => {});
    fetchStats();
    const t = setInterval(() => { if (khPageVisible()) fetchStats(); }, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  const bar = (pct, color) => (
    <div className="w-full bg-gray-700 rounded-full h-1 overflow-hidden">
      <div className={`h-1 rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct,100)}%` }}/>
    </div>
  );

  // Tarmoq tezligi — backend bayt/sekundda beradi, o'qishga qulay birlikka o'tkazamiz
  const fmtRate = (bps) => {
    const b = Number(bps) || 0;
    if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB/s`;
    if (b >= 1024) return `${Math.round(b / 1024)} KB/s`;
    return `${b} B/s`;
  };
  // Yig'ilgan header tor — u yerda qisqartirilgan ko'rinish (2.3M / 15K)
  const fmtRateShort = (bps) => {
    const b = Number(bps) || 0;
    if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)}M`;
    if (b >= 1024) return `${Math.round(b / 1024)}K`;
    return '0';
  };

  // Backwards compat: if backend hasn't been redeployed yet, `servers` won't
  // exist — fall back to the old single-server shape.
  const servers = Array.isArray(data.servers) && data.servers.length
    ? data.servers
    : [{ name: 'Backend', role: '', host: 'local', cpu: data.cpu, ram: data.ram, disk: data.disk, net: data.net, uptime: data.uptime }];

  // Worst-case stats across all servers — shown in collapsed header
  const live = servers.filter(s => !s.error);
  const maxCpu  = live.length ? Math.max(...live.map(s => s.cpu || 0)) : 0;
  const maxRam  = live.length ? Math.max(...live.map(s => (s.ram && s.ram.percent) || 0)) : 0;
  const maxDisk = live.length ? Math.max(...live.map(s => (s.disk && s.disk.percent) || 0)) : 0;
  // Tarmoq — barcha serverlar yig'indisi (header'da umumiy oqim ko'rinsin)
  const sumRx = live.reduce((a, s) => a + ((s.net && s.net.rx) || 0), 0);
  const sumTx = live.reduce((a, s) => a + ((s.net && s.net.tx) || 0), 0);
  const hasNet = live.some(s => s.net);
  const anyOffline = servers.some(s => s.error);
  const overall = Math.max(maxCpu, maxRam, maxDisk);
  const overallColor = anyOffline ? 'text-red-400'
    : overall > 80 ? 'text-red-400'
    : overall > 60 ? 'text-yellow-400'
    : 'text-green-400';

  if (flyout) {
    // Navbar tugmasi: ⚡ Mening sarfim naqshi — tugmada eng yuqori CPU foizi; rang shu raqam bo'yicha
    // yashildan qizilga silliq (loadStyle), server javob bermasa — to'liq qizil.
    const track = (pct, color) => (
      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 overflow-hidden">
        <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }}/>
      </div>
    );
    const row = (label, right, pct, color) => (
      <div>
        <div className="flex justify-between text-[11px] text-gray-600 dark:text-gray-400 mb-0.5"><span>{label}</span><span className="font-mono">{right}</span></div>
        {track(pct, color)}
      </div>
    );
    return (
      <>
        <button onClick={() => setOpen(o => !o)}
          title={`Serverlar (${servers.length}) — CPU ${maxCpu}% · RAM ${maxRam}% · Disk ${maxDisk}%${anyOffline ? ' · ⚠ offline bor' : ''}`}
          className={`h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm text-sm font-medium transition border
            ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600' : 'kh-load'}`}
          style={open ? undefined : loadStyle(anyOffline ? 100 : maxCpu)}>
          <NavIcon name="server" className="w-5 h-5 shrink-0" />
          <span className="text-xs font-bold font-mono leading-none">{anyOffline ? '⚠' : `${maxCpu}%`}</span>
        </button>
        {open && (
          <div className="fixed top-16 right-4 z-50 w-[24rem] max-w-[92vw] max-h-[75vh] overflow-auto rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1.5"><NavIcon name="server" className="w-4 h-4 shrink-0" />Serverlar <span className={`text-xs ${overallColor}`}>{anyOffline ? '⚠' : '●'} {servers.length}</span></div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  CPU {maxCpu}% · RAM {maxRam}% · Disk {maxDisk}%{hasNet ? ` · ↓${fmtRateShort(sumRx)} ↑${fmtRateShort(sumTx)}` : ''}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none">✕</button>
            </div>
            <div className="p-4 space-y-3">
              {servers.map(s => (
                <div key={s.name} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{s.name}</span>
                    {s.error
                      ? <span className="text-[11px] font-medium text-red-600 dark:text-red-400" title={s.error}>⚠ offline</span>
                      : s.uptime ? <span className="text-[11px] text-gray-400" title="Uptime">⏱ {s.uptime}</span> : null}
                  </div>
                  {s.error ? (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 italic">{s.error}</div>
                  ) : (
                    <div className="space-y-2">
                      {row('CPU', `${s.cpu}%`, s.cpu, s.cpu > 80 ? 'bg-red-500' : s.cpu > 50 ? 'bg-yellow-500' : 'bg-emerald-500')}
                      {row('RAM', `${s.ram.used} / ${s.ram.total} MB`, s.ram.percent, s.ram.percent > 85 ? 'bg-red-500' : s.ram.percent > 65 ? 'bg-yellow-500' : 'bg-primary-600')}
                      {s.disk && row('Disk', `${s.disk.used} / ${s.disk.total} GB`, s.disk.percent, s.disk.percent > 85 ? 'bg-red-500' : s.disk.percent > 65 ? 'bg-yellow-500' : 'bg-orange-400')}
                      {s.net && (
                        <div className="flex justify-between text-[11px] text-gray-600 dark:text-gray-400">
                          <span>Tarmoq</span>
                          <span className="flex items-center gap-2 font-mono">
                            <span title="Yuklab olish (rx)" className="text-cyan-600 dark:text-cyan-400">↓ {fmtRate(s.net.rx)}</span>
                            <span title="Yuklash (tx)" className="text-purple-600 dark:text-purple-400">↑ {fmtRate(s.net.tx)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {/* ⚙️ Hozir nima band qilyapti — jarayonlar + build navbati (docs/knowledge/187) */}
              <ServerActivity authFetch={authFetch} apiUrl={apiUrl} />
              <p className="text-[10px] text-gray-400">30 soniyada yangilanadi · CPU 0.4 s oynada o'lchanadi, tarmoq — bayt/soniya.</p>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mb-3 px-2">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between py-1.5 px-1 hover:bg-gray-800 rounded transition text-left"
        title={expanded ? "Yopish" : "Ko'rsatish"}
      >
        <span className="text-xs font-semibold text-gray-300 flex items-center gap-2">
          <span>Serverlar</span>
          <span className={`text-[10px] ${overallColor}`}>
            {anyOffline ? '⚠' : '●'} {servers.length}
          </span>
        </span>
        <span className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
          <span>CPU {maxCpu}%</span>
          <span>RAM {maxRam}%</span>
          {hasNet && (
            <span title={`Tarmoq — yuklab olish: ${fmtRate(sumRx)} · yuklash: ${fmtRate(sumTx)}`}>
              ↓{fmtRateShort(sumRx)} ↑{fmtRateShort(sumTx)}
            </span>
          )}
          <span className={`transition-transform ${expanded ? 'rotate-180' : ''}`}>▾</span>
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 mt-2 pl-1">
          {servers.map(s => (
            <div key={s.name} className="border-b border-gray-700 pb-2 last:border-b-0">
              <div className="text-xs font-semibold text-gray-300 mb-1 flex items-center justify-between">
                <span>{s.name}</span>
                {s.error && <span className="text-red-400" title={s.error}>⚠ offline</span>}
              </div>
              {s.error ? (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 italic">{s.error}</div>
              ) : (
                <div className="space-y-1.5">
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                      <span>CPU</span><span>{s.cpu}%</span>
                    </div>
                    {bar(s.cpu, s.cpu > 80 ? 'bg-red-500' : s.cpu > 50 ? 'bg-yellow-500' : 'bg-green-500')}
                  </div>
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                      <span>RAM</span><span>{s.ram.used}MB / {s.ram.total}MB</span>
                    </div>
                    {bar(s.ram.percent, s.ram.percent > 85 ? 'bg-red-500' : s.ram.percent > 65 ? 'bg-yellow-500' : 'bg-blue-500')}
                  </div>
                  {s.disk && (
                    <div>
                      <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                        <span>Disk</span><span>{s.disk.used}GB / {s.disk.total}GB</span>
                      </div>
                      {bar(s.disk.percent, s.disk.percent > 85 ? 'bg-red-500' : s.disk.percent > 65 ? 'bg-yellow-500' : 'bg-orange-400')}
                    </div>
                  )}
                  {s.net && (
                    <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
                      <span>Tarmoq</span>
                      <span className="flex items-center gap-2">
                        <span title="Yuklab olish (rx)" className="text-cyan-400/80">↓ {fmtRate(s.net.rx)}</span>
                        <span title="Yuklash (tx)" className="text-purple-400/80">↑ {fmtRate(s.net.tx)}</span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PageScreenshot({ src, alt }) {
  const [errored, setErrored] = React.useState(false);
  if (errored || !src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-600 dark:text-gray-400 gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        <span className="text-xs opacity-40">Screenshot yo'q</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-full h-full object-contain" onError={() => setErrored(true)} />;
}

// CLIENT (non-admin) sidebar sections whose per-user visibility the admin
// grants/revokes (Foydalanuvchilar tab). Keys MUST stay in sync with the backend
// services/sections.js SECTION_KEYS. Admins always see everything.
const CLIENT_SECTIONS = [
  { key: 'studio', label: '🏗️ Studio' },
  { key: 'design', label: '🖼️ Dizayn' },
  { key: 'processes', label: '⏳ Jarayonlar' },
  { key: 'logo', label: '🏷 Logo (Dizayn ichida)' },
  { key: 'smm', label: '📣 SMM' },
  { key: 'callcenter', label: '☎️ Call Center' },
  { key: 'health', label: '🩺 Salomatlik (tepadagi tugma)' },
  { key: 'console', label: '🔐 Konsollar' },
  { key: 'knowledge', label: '📚 Tajriba bazasi' },
  { key: 'forecast', label: '📈 Prognoz' },
  { key: 'reengineering', label: '🔧 Reengineering' },
  { key: 'integrations', label: '🔌 Integratsiyalar' },
];
const CLIENT_SECTION_KEYS = CLIENT_SECTIONS.map(s => s.key);

// ───────────────────────── 📣 SMM — DIZAYN REJASI (statik mockup) ─────────────────────────
// Bu bo'limning KELAJAKDAGI ko'rinishi: Instagram / Telegram / YouTube kanallarini AI bilan
// yuritish — kontent tayyorlash, odam tasdiqlashi, kalendar bo'yicha rejalash, avto-chiqarish
// va statistika. HOZIRCHA HECH NARSA ULANMAGAN — har bir tugma "hali dizayn" eslatmasini
// ko'rsatadi. Real implementatsiya bosqichma-bosqich qilinadi (Telegram → kalendar → IG/YT).
function SmmPanel() {
  const [note, setNote] = useState('');
  const noteTimer = useRef(null);
  // Har qanday amal tugmasi uchun yagona javob — bu faqat dizayn.
  const mock = () => {
    setNote("🚧 Bu dizayn ko'rinishi — funksiya hali ulanmagan");
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(''), 2600);
  };

  const CH = {
    tg: { icon: '✈️', name: 'Telegram' },
    ig: { icon: '📸', name: 'Instagram' },
    yt: { icon: '▶️', name: 'YouTube' },
  };
  const ST = {
    done:  { label: '✅ Chiqdi', cls: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' },
    sched: { label: '🕓 Rejalashtirilgan', cls: 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800' },
    wait:  { label: '⏳ Tasdiq kutmoqda', cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
    draft: { label: '✍️ Qoralama', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700' },
  };
  // Namuna hafta — kontent kalendari
  const week = [
    { d: 'Du', date: '13.07', posts: [{ t: '09:00', ch: ['tg'], txt: '📊 Kunlik statistika', st: 'done' }] },
    { d: 'Se', date: '14.07', posts: [{ t: '18:00', ch: ['ig', 'tg'], txt: "🎉 Yangi imkoniyat e'loni", st: 'wait' }] },
    { d: 'Cho', date: '15.07', posts: [{ t: '12:00', ch: ['tg'], txt: '💡 Foydali maslahat', st: 'sched' }] },
    { d: 'Pa', date: '16.07', posts: [{ t: '09:00', ch: ['tg'], txt: '📊 Kunlik statistika', st: 'sched' }] },
    { d: 'Ju', date: '17.07', posts: [
      { t: '17:00', ch: ['yt'], txt: '🎬 Video obzor', st: 'draft' },
      { t: '20:00', ch: ['ig'], txt: '🖼 Aksiya posti', st: 'sched' },
    ] },
    { d: 'Sha', date: '18.07', posts: [{ t: '10:00', ch: ['ig'], txt: '📷 Foydalanuvchi hikoyasi', st: 'sched' }] },
    { d: 'Ya', date: '19.07', posts: [] },
  ];
  // Tasdiqlash navbati — AI tayyorlaydi, odam ko'rib tasdiqlaydi (chiqmasidan oldin)
  const approvals = [
    {
      id: 1, ch: ['ig', 'tg'], when: 'Bugun 18:00', kind: '🎉 Yangi imkoniyat',
      img: 'from-primary-500 to-primary-600', imgLabel: '🖼 AI tayyorlagan rasm (1080×1080)',
      text: "🎉 TezHal'da katta yangilik! Endi buyurtmangizni xaritada JONLI kuzatib borasiz — haydovchi qayerda, qachon yetib keladi, hammasi ko'z oldingizda. Yangilanish bugun Play Market va App Store'da! 📲",
      tags: '#TezHal #yangilik #jonlikuzatuv',
    },
    {
      id: 2, ch: ['tg'], when: 'Ertaga 09:00', kind: '📊 Haftalik statistika',
      img: 'from-emerald-400 to-teal-500', imgLabel: '📊 Statistika kartochkasi (avto)',
      text: "📊 Bu hafta TezHal'da: 1 240 ta yangi foydalanuvchi qo'shildi, 3 580 ta buyurtma bajarildi, o'rtacha yetkazish vaqti — 24 daqiqa. Bizga ishonganingiz uchun rahmat! 🚀",
      tags: '#TezHal #statistika #hafta',
    },
  ];
  // Avto-jadval qoidalari — AI shu jadval bo'yicha qoralama tayyorlab tasdiqqa qo'yadi
  const rules = [
    { on: true,  icon: '🕘', txt: 'Har kuni 09:00 — kunlik statistika posti', ch: ['tg'] },
    { on: true,  icon: '📅', txt: 'Har dushanba 10:00 — haftalik yangiliklar dayjesti', ch: ['ig', 'tg'] },
    { on: true,  icon: '✨', txt: "Yangi imkoniyat deploy bo'lganda — avto-e'lon posti", ch: ['ig', 'tg', 'yt'] },
    { on: false, icon: '🎬', txt: 'Har oyning 1-sanasida — oylik hisobot videosi', ch: ['yt'] },
  ];
  const chChip = (k) => (
    <span key={k} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-full px-2 py-0.5 text-gray-600 dark:text-gray-400">
      {CH[k].icon} {CH[k].name}
    </span>
  );

  return (
    <div>
      {/* Sarlavha + loyiha tanlagich + mockup banner */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl shrink-0">📣 SMM — Ijtimoiy tarmoqlar studiyasi</h1>
        <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400" value="tezhal" onChange={mock}>
          <option value="tezhal">TezHal</option>
          <option value="viloyatga">viloyatga</option>
        </select>
      </div>
      <div className="mb-5 inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm rounded-lg px-3 py-2">
        <span>🚧</span>
        <span><b>Dizayn rejasi (mockup)</b> — bo'lim hali ishlamaydi; kelajakdagi ko'rinishni tasavvur qilish uchun.</span>
      </div>

      {/* Kanallar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        {[
          { k: 'tg', handle: '@tezhal_uz', subs: '8 924', delta: '+212 / hafta', last: 'Oxirgi post: bugun 09:00', ok: true, bars: [40, 55, 45, 70, 60, 80, 75] },
          { k: 'ig', handle: '@tezhal.uz', subs: '12 480', delta: '+508 / hafta', last: 'Oxirgi post: kecha 18:00', ok: true, bars: [50, 45, 65, 60, 75, 70, 90] },
          { k: 'yt', handle: 'TezHal Rasmiy', subs: '—', delta: '', last: 'Kanal hali ulanmagan', ok: false, bars: [] },
        ].map(c => (
          <div key={c.k} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{CH[c.k].icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white text-sm">{CH[c.k].name}</p>
                <p className="text-xs text-gray-400 truncate">{c.handle}</p>
              </div>
              {c.ok
                ? <span className="text-[11px] bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full px-2 py-0.5">✅ Ulangan</span>
                : <button onClick={mock} className="text-[11px] bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 text-white rounded-full px-3 py-1">🔗 Ulash</button>}
            </div>
            {c.ok ? (
              <>
                <div className="flex items-end justify-between mt-2">
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{c.subs}</p>
                    <p className="text-[11px] text-gray-400">obunachi · <span className="text-green-600 dark:text-green-400">{c.delta}</span></p>
                  </div>
                  <div className="flex items-end gap-0.5 h-10">
                    {c.bars.map((h, i) => <div key={i} className="w-2 rounded-t bg-primary-300" style={{ height: `${h}%` }} />)}
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">{c.last}</p>
              </>
            ) : (
              <p className="text-xs text-gray-400 mt-3">{c.last}. Bot token / API kalit bilan bir marta ulanadi, keyin postlar avtomatik chiqadi.</p>
            )}
          </div>
        ))}
      </div>

      {/* Kalendar + Tasdiqlash navbati */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
        <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <p className="font-semibold text-gray-700 dark:text-gray-300">🗓 Kontent kalendari</p>
            <span className="text-xs text-gray-400">13 — 19 iyul</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={mock} className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">Hafta</button>
              <button onClick={mock} className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">Oy</button>
              <button onClick={mock} className="text-xs bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 text-white rounded-lg px-3 py-1">+ Post rejalash</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {week.map(day => (
              <div key={day.d} className="border border-gray-100 dark:border-gray-700 rounded-lg p-1.5 min-h-[110px] bg-gray-50/50 dark:bg-gray-700/50">
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1 text-center">{day.d} <span className="text-gray-300">{day.date}</span></p>
                {day.posts.map((p, i) => (
                  <button key={i} onClick={mock} className={`w-full text-left border rounded-md px-1.5 py-1 mb-1 ${ST[p.st].cls}`}>
                    <p className="text-[10px] leading-tight font-medium">{p.txt}</p>
                    <p className="text-[10px] opacity-70">{p.t} · {p.ch.map(k => CH[k].icon).join(' ')}</p>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {Object.values(ST).map(s => <span key={s.label} className={`text-[11px] border rounded-full px-2 py-0.5 ${s.cls}`}>{s.label}</span>)}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">⏳ Tasdiqlash navbati <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 rounded-full px-2 py-0.5 ml-1">2</span></p>
          <p className="text-[11px] text-gray-400 mb-3">AI tayyorlagan post odam tasdiqlamaguncha chiqmaydi. Push-bildirishnoma keladi — telefondan ham tasdiqlash mumkin.</p>
          {approvals.map(a => (
            <div key={a.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{a.kind}</span>
                <span className="text-[11px] text-gray-400">· {a.when}</span>
                <span className="ml-auto flex gap-1">{a.ch.map(chChip)}</span>
              </div>
              <div className={`h-16 rounded-lg bg-gradient-to-br ${a.img} flex items-center justify-center text-white/90 text-[11px] mb-2`}>{a.imgLabel}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-1">{a.text}</p>
              <p className="text-[11px] text-primary-600 dark:text-primary-500 mb-2">{a.tags}</p>
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={mock} className="text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-1.5">✅ Tasdiqlash</button>
                <button onClick={mock} className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">✏️ Tahrirlash</button>
                <button onClick={mock} className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">🔁 Qayta yozdirish</button>
                <button onClick={mock} className="text-xs border border-red-200 dark:border-red-800 rounded-lg px-2.5 py-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/40">🗑</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI kontent studiyasi + Avto-jadval qoidalari */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
        <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">✨ AI kontent studiyasi</p>
          <p className="text-[11px] text-gray-400 mb-3">Mavzuni yozasiz — AI matn, rasm va video ssenariy tayyorlaydi. App'dagi real yangiliklar (deploy, yangi imkoniyat) va statistikani o'zi biladi.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <textarea readOnly onClick={mock} rows={4}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-pointer"
                value={"Nima haqida post kerak?\nMas: \"Jonli kuzatuv chiqqani haqida qiziqarli post + rasm\""} />
              <div className="flex gap-2 mt-2 flex-wrap items-center">
                <select onChange={mock} className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  <option>Turi: Yangilik</option><option>Statistika</option><option>Aksiya</option><option>Maslahat</option><option>Video ssenariy</option>
                </select>
                <select onChange={mock} className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  <option>Ohang: Do'stona</option><option>Rasmiy</option><option>Qiziqarli</option>
                </select>
                <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {['tg', 'ig', 'yt'].map(k => (
                    <label key={k} className="flex items-center gap-0.5 cursor-pointer"><input type="checkbox" defaultChecked={k !== 'yt'} onChange={mock} /> {CH[k].icon}</label>
                  ))}
                </span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={mock} className="text-sm bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 text-white rounded-lg px-4 py-2">✨ Matn yaratish</button>
                <button onClick={mock} className="text-sm border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/40 rounded-lg px-3 py-2">🖼 Rasm</button>
                <button onClick={mock} className="text-sm border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/40 rounded-lg px-3 py-2">🎬 Video ssenariy</button>
              </div>
            </div>
            <div className="border border-dashed border-primary-200 dark:border-primary-800 rounded-lg p-3 bg-primary-50/40 dark:bg-primary-900/30">
              <p className="text-[11px] text-primary-600 dark:text-primary-500 font-semibold mb-2">👁 Jonli preview — post kanalda qanday ko'rinadi</p>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="h-20 rounded-md bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white/90 text-[11px] mb-2">🖼 AI rasm shu yerda</div>
                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">🎉 TezHal'da katta yangilik! Endi buyurtmangizni xaritada JONLI kuzatib borasiz...</p>
                <p className="text-[11px] text-primary-600 dark:text-primary-500 mt-1">#TezHal #yangilik</p>
              </div>
              <div className="flex gap-1.5 mt-2">
                <button onClick={mock} className="text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg px-3 py-1.5">📅 Rejaga qo'shish</button>
                <button onClick={mock} className="text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg px-3 py-1.5">📤 Hozir chiqarish</button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">🔁 Avto-jadval qoidalari</p>
          <p className="text-[11px] text-gray-400 mb-3">AI shu jadval bo'yicha qoralama tayyorlab, tasdiqlash navbatiga qo'yadi.</p>
          {rules.map((r, i) => (
            <div key={i} className="flex items-start gap-2 border-b border-gray-100 dark:border-gray-700 last:border-0 py-2">
              <span className="text-lg">{r.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-700 dark:text-gray-300">{r.txt}</p>
                <div className="flex gap-1 mt-1 flex-wrap">{r.ch.map(chChip)}</div>
              </div>
              <button onClick={mock} className={`w-9 h-5 rounded-full relative transition shrink-0 mt-0.5 ${r.on ? 'bg-primary-700 dark:bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white dark:bg-gray-800 rounded-full transition ${r.on ? 'right-0.5' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
          <button onClick={mock} className="mt-3 w-full text-xs border border-dashed border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/40 rounded-lg px-3 py-2">+ Qoida qo'shish</button>
        </div>
      </div>

      {/* Statistika */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-5">
        <p className="font-semibold text-gray-700 dark:text-gray-300 mb-3">📈 Statistika (oxirgi 30 kun)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { l: "Jami ko'rishlar", v: '48 200', d: '+18%' },
            { l: 'Yangi obunachilar', v: '+1 240', d: '+9%' },
            { l: 'Engagement', v: '6.4%', d: '+0.8%' },
            { l: 'Chiqqan postlar', v: '42', d: "36 avto · 6 qo'lda" },
          ].map(s => (
            <div key={s.l} className="border border-gray-100 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-700/50">
              <p className="text-[11px] text-gray-400">{s.l}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{s.v}</p>
              <p className="text-[11px] text-green-600 dark:text-green-400">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-1 h-24">
          {[35, 42, 38, 55, 48, 62, 58, 70, 66, 74, 60, 68, 72, 80, 76, 64, 70, 78, 84, 90, 74, 68, 82, 88, 92, 78, 84, 96, 90, 100].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary-600 to-primary-300" style={{ height: `${h}%` }} title={`${i + 1}-kun`} />
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Kunlik ko'rishlar — barcha kanallar birlashtirilgan</p>
      </div>

      {note && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm rounded-lg px-4 py-2.5 shadow-lg z-50">
          {note}
        </div>
      )}
    </div>
  );
}

// ☎️ CALL CENTER — haqiqiy panel alohida faylda (src/CallCenterPanel.js, Boshqaruvchi F6, docs/knowledge/152).

// 👥 Loyihadagi rollar (backend projectAccess.ROLES) — admin Foydalanuvchilar panelida biriktirish/o'zgartirish uchun.
const PROJECT_ROLE_OPTS = [['owner', '👑 Egasi'], ['developer', '👨‍💻 Dasturchi'], ['reviewer', '🔍 Tekshiruvchi'], ['viewer', '👁 Kuzatuvchi'], ['client', '🤝 Mijoz']];
// 🤝 Mijoz roli (backend docs/knowledge/207): Studio'da faqat shu ko'rinishlar (kod, baza, emulator, holat, reja — yo'q).
const CLIENT_STUDIO_VIEWS = ['map', 'qa', 'docs', 'crashes'];

function App({ emulatorOnly = false, dbOnly = false }) {
  const { token, user, login, logout } = useAuth();
  // Per-user section access. Admins → everything. Non-admin: allowed_sections is
  // either an array (restricted to those keys) or null/undefined (= all allowed,
  // back-compat default so existing users are never locked out).
  const allowedSectionKeys = (user?.role === 'admin')
    ? CLIENT_SECTION_KEYS
    : (Array.isArray(user?.allowed_sections) ? user.allowed_sections : CLIENT_SECTION_KEYS);
  const canSee = (key) => allowedSectionKeys.includes(key);
  // 🔐 Konsol — jonli login qilingan Firebase/Play/ASC sessiyasiga kirish demak.
  // Shuning uchun `canSee` dan FARQLI: `allowed_sections` NULL ("hammasi ochiq")
  // yetarli emas, ruxsat AYNIQSA belgilangan bo'lishi kerak (backend ham shunday).
  const canSeeConsole = user?.role === 'admin'
    || (Array.isArray(user?.allowed_sections) && user.allowed_sections.includes('console'));
  const authFetch = async (url, opts = {}) => {
    const headers = { Authorization: `Bearer ${token}`, ...opts.headers };
    const res = await fetch(url, { ...opts, headers });
    // Expired/invalid token → clean logout + redirect (never let a 401 body
    // reach a data handler; that crashed the app to a blank screen).
    if (res.status === 401) { handleAuthExpired(); throw new Error('unauthorized'); }
    // Hisob o'chirilgan bo'lsa token hali "yaroqli", lekin server har so'rovni
    // 403 qiladi — aks holda foydalanuvchi sababsiz xatolar ichida qolib ketardi.
    // Boshqa 403'lar (huquq yetmasligi) TEGILMAYDI — faqat shu kod.
    if (res.status === 403) {
      const copy = res.clone();
      let body = null;
      try { body = await copy.json(); } catch (e) {}
      // 🌐 ADMIN_NET_ONLY — admin hisobi faqat Tailscale orqali (docs/knowledge/148)
      if (body && (body.code === 'ACCOUNT_DISABLED' || body.code === 'ADMIN_NET_ONLY')) {
        try { toast.error(body.error || "Hisobingiz o'chirilgan"); } catch (e) {}
        handleAuthExpired();
        throw new Error('account_disabled');
      }
      // 🔐 2FA majburiy, hali sozlanmagan → sozlash sahifasi (docs/knowledge/148)
      if (body && body.code === 'MFA_SETUP_REQUIRED') { handleMfaSetup(); throw new Error('mfa_setup_required'); }
    }
    return res;
  };

  const [activeTab, setActiveTab] = useState('tasks');
  // 🔗 Publish → «App Store'ga chiqarish» tugmasi ASC konsolini release sahifasida ochadi.
  const [consoleDeepLink, setConsoleDeepLink] = useState(null);
  // `extra` — ConsolePanel tushunadigan qo'shimcha maydonlar: {task, projectId}.
  // 🔌 Integratsiyalar kartasi shu orqali konsolni O'SHA profilda ochadi va vazifa
  // matnini oldindan to'ldiradi (operator konsolga kirib qo'lda tanlamasin).
  const openConsoleAt = (profile, url, extra = {}) => {
    setConsoleDeepLink({ profile, url, nonce: Date.now(), ...extra });
    setActiveTab('console');
  };
  const [tasks, setTasks] = useState([]);
  // Vazifalar ro'yxati paginatsiyasi: dastlab oxirgi 10 ta, scroll qilganda yana 10 ta yuklanadi
  // (hammasini birdan yuklash/ko'rsatish sekin edi). tasksLimit faqat o'sadi (offset 0 dan).
  const [tasksLimit, setTasksLimit] = useState(10);
  const tasksLimitRef = useRef(10);
  useEffect(() => { tasksLimitRef.current = tasksLimit; }, [tasksLimit]);
  const [tasksHasMore, setTasksHasMore] = useState(true);
  const [tasksLoadingMore, setTasksLoadingMore] = useState(false);
  const tasksSentinelRef = useRef(null); // ro'yxat oxiridagi sentinel — ko'ringanda yana yuklaydi
  const [taskTrigger, setTaskTrigger] = useState(0);
  const [newTask, setNewTask] = useState('');
  const [agentDraftExt, setAgentDraftExt] = useState(null);   // {text, at} → ProcessesFlyout 🧠 Agent yozuv maydoni
  // 💬 Vazifa suhbati (📋 vazifa oynasi) — agent kod/baza/emulator/brauzer/konsolni ko'rib javob beradi va
  // kod o'zgarishi kerak bo'lsa vazifani <VAZIFA> bilan beradi (backend services/taskRefine.js, 2026-10-17).
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineMsgs, setRefineMsgs] = useState([]);   // [{ role: 'user'|'ai'|'err', text, draft }]
  const [refineInput, setRefineInput] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineSession, setRefineSession] = useState(null);
  const [refineActivity, setRefineActivity] = useState([]);   // agent hozir nima qilyapti (vosita chaqiruvlari)
  const chatUploadCache = React.useRef(new Map());              // biriktirma → /uploads url (har xabarda qayta yuklanmasin)
  // ↕ Avto-scroll FAQAT yangi xabar/«ishlayapman» chiqqanda (ilgari inline ref har render'da pastga tortardi —
  // panel 3 s'da yangilangani uchun yuqoriga o'qib bo'lmasdi). Agent javobi — BOSHI ko'rinsin (uzun javob).
  const refineBoxRef = React.useRef(null);
  useEffect(() => {
    const box = refineBoxRef.current; if (!box) return;
    const last = refineMsgs[refineMsgs.length - 1];
    if (!refineBusy && last && last.role === 'ai') {
      const all = box.querySelectorAll('[data-kh-msg]'); const el = all[all.length - 1];
      if (el) { box.scrollTop = Math.max(0, el.offsetTop - 4); return; }
    }
    box.scrollTop = box.scrollHeight;
  }, [refineMsgs.length, refineBusy]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [addingTask, setAddingTask] = useState(false); // Yuborish loading + double-click guard
  const [newTaskImages, setNewTaskImages] = useState([]); // [{file, preview}]
  const [imgViewer, setImgViewer] = useState(null);         // 🖼 formadagi rasmni katta ko'rish {images, index}
  const [selectedDesignIds, setSelectedDesignIds] = useState([]); // App Builder design pages attached to the next task
  const [selectedLogoIds, setSelectedLogoIds] = useState([]); // Logo dizayn logos attached to the next task
  const [designPickerOpen, setDesignPickerOpen] = useState(false); // modal: pick App Builder designs to attach
  const [logoPickerOpen, setLogoPickerOpen] = useState(false); // modal: pick logos to attach
  const [logoUploading, setLogoUploading] = useState(false); // 📤 Tayyor logoni yuklash
  const [applyingLogoId, setApplyingLogoId] = useState(null); // Logo dizayn — "Ilovaga biriktirish" tugmasi bosilgan logo id
  const [projectId, setProjectId] = useState('');
  const [taskType, setTaskType] = useState('developer'); // Keep task type selector logic unchanged
  const [requiresBuild, setRequiresBuild] = useState(false);
  // Claude-style sessions: false = CONTINUE each module's prior Claude Code session
  // (context accumulates + prompt cache hits); true = start a FRESH session (engine
  // resets the project's module contexts before this task). + the live context meter.
  const [newSessionMode, setNewSessionMode] = useState(false);
  // 🧪 Video-QA (auto_qa) 2026-10-31 da butunlay olib tashlandi (backend docs/knowledge/201) — sinov 🧪 Sinov tabidagi agent orqali.
  // Navbat rejimi: 'parallel' (default — bir nechta vazifa bir vaqtda) yoki 'sequential'
  // (har bir vazifa oldingisi merge+deploy bo'lgach boshlanadi). autoDeploy = tugagach
  // avtomatik merge+deploy (qo'lda Tasdiqlash shart emas).
  const [runMode, setRunMode] = useState('parallel');
  const [autoDeploy, setAutoDeploy] = useState(false);
  // 🔁 Tugagach tasdiqlab sinash (Nazoratchi) — har vazifa uchun alohida qaror: yuborilgach o'chadi.
  const [afterTest, setAfterTest] = useState(false);
  // 📝 Reja tasdig'i — yoqilgan bo'lsa vazifa kod yozishdan oldin reja ko'rsatib tasdiq kutadi.
  // Tanlov eslab qolinadi (operator yoqsa odatda doim shunday ishlamoqchi).
  const [planReview, setPlanReviewRaw] = useState(() => { try { return localStorage.getItem('kh_plan_review') === '1'; } catch (e) { return false; } });
  const setPlanReview = (v) => { setPlanReviewRaw(v); try { localStorage.setItem('kh_plan_review', v ? '1' : '0'); } catch (e) {} };
  const [contextSessions, setContextSessions] = useState([]); // [{module_name, percent, cache_percent, ...}]

  // ---------- Live Terminal State ----------
  const [liveLogs, setLiveLogs] = useState([]);
  const terminalContainerRef = useRef(null);
  const socketRef = useRef(null);
  // 🧭 Integratsiya ssenariysi oynasi (Integratsiyalar, 👤 Sizdan kutilmoqda, push havolasi ?playbook=ID).
  const [playbookRunId, setPlaybookRunId] = useState(() => { try { return Number(new URLSearchParams(window.location.search).get('playbook')) || null; } catch (e) { return null; } });

  // ---------- Emulator state ----------
  const [emulatorDevices, setEmulatorDevices] = useState([]);
  const [emulatorStatus, setEmulatorStatus] = useState('unknown'); // unknown|not_created|starting|running|reconnecting|stopped|error
  const [emulatorLogs, setEmulatorLogs]   = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [emulatorFrame, setEmulatorFrame] = useState('');
  const [emulatorScreenSize, setEmulatorScreenSize] = useState({ width: 1080, height: 1920 });
  const [isStreaming, setIsStreaming] = useState(false);
  const [showEmulator, setShowEmulator] = useState(false);
  // Multi-emulator grid (up to 3 per user). Keyed by slot (0,1,2). Slot 0 = the original emulator.
  const [emuList, setEmuList] = useState([]);             // [{id, slot, status, adb_device, adb_port, container_name, device_key}]
  const [emuMaxSlots, setEmuMaxSlots] = useState(3);
  const [projectEmuSlot, setProjectEmuSlot] = useState(null); // tanlangan loyiha biriktirilgan emulator slot (null = barchasiga)
  const [moduleEmuSlot, setModuleEmuSlot] = useState(null);   // 📌 tanlangan ILOVANING o'z sloti (null = loyiha bo'yicha) — 2026-10-28
  // 🧪 Loyihaning SINOV emulatorlari (agent / Nazoratchi / emu_start shu to'plamga qaraydi; settings.supervisor_slots_<pid>) —
  // 📌 biriktiruvdan alohida. Ilgari ko'rinmasdi: operator «barchasiga» biriktirsa ham agent E3/E4 bilan qolardi (Navbatchi #28).
  const [emuTestSlots, setEmuTestSlots] = useState(null);     // {slots, borrow:{slots,until}|null, others:[{projectId,name,slots}], maxSlots}
  // slot -> {slot, projectId, projectName, moduleId, moduleName} — HAR bir emulator qaysi
  // loyihaga biriktirilgani. 📲 (oxirgi APK'ni o'rnatish) aynan shu loyihaning ilovasini
  // o'rnatadi, katakda esa loyiha nomi ko'rinadi (chalkashlik yo'q).
  const [emuBindings, setEmuBindings] = useState({});
  // 🔗 Build vaqtidagi API manzili: 'code' (repodagidek) | 'test' | 'production'.
  // Backend URL APK ichiga in'ektsiya qilinmaydi — u ilova kodidagi BASE_URL da turadi,
  // shuning uchun build oldidan uni ANIQ tanlaymiz (emulatorda prod bazaga yozib
  // qo'ymaslik uchun). Tanlov loyiha bo'yicha eslab qolinadi.
  // DEFAULT = 'test': emulator SINOV joyi, shuning uchun ilova TEST env'ga ulansin.
  // ('code' default edi — repodagi BASE_URL ko'pincha PRODUCTION bo'lgani uchun har
  // sinov jonli bazaga yozib, u yerda keraksiz ma'lumot yig'ardi.)
  const [apiTarget, setApiTarget] = useState('test');
  // 📍 Lokatsiya (soxta GPS) — qaysi slot uchun modal ochilgan + saqlangan nuqtalar +
  // marshrut holati (slot -> {running,index,total,lat,lng}, socket'dan yangilanadi).
  const [locSlot, setLocSlot] = useState(null);
  const [locLat, setLocLat] = useState('');
  const [locLng, setLocLng] = useState('');
  const [locPoints, setLocPoints] = useState([]);       // saqlangan nuqtalar
  const [locBusy, setLocBusy] = useState(false);
  const [routePoints, setRoutePoints] = useState([]);   // marshrut uchun tanlangan ketma-ketlik
  const [routeSpeed, setRouteSpeed] = useState(40);
  const [routeLoop, setRouteLoop] = useState(false);
  const [emuRoute, setEmuRoute] = useState({});         // slot -> marshrut holati
  // 📲 o'rnatishda TAYYOR APK'lardan tanlash (qaysi slotga — apkPickerSlot).
  const [apkPickerSlot, setApkPickerSlot] = useState(null);
  const [apkChoices, setApkChoices] = useState([]);       // [{moduleId,moduleName,projectId,projectName,boundSlot,sizeBytes,builtAt}]
  const [apkChoicesLoading, setApkChoicesLoading] = useState(false);
  const [emuPresets, setEmuPresets] = useState([]);       // tayyor telefon turlari [{key,label,android,size,...}]
  const [emuFrames, setEmuFrames] = useState({});         // slot -> src-ready URL (data: yoki blob:)
  const emuFrameUrlRef = useRef({});                      // slot -> oxirgi blob URL (revoke uchun)
  const [emuAspect, setEmuAspect] = useState({});
  const emuAspectRef = useRef({});                        // socket-handler uchun (stale closure)         // slot -> "w / h" (qurilma aspect → ramka moslashadi)
  const [emuStreaming, setEmuStreaming] = useState({});   // slot -> bool
  const emuStreamingRef = useRef({});
  useEffect(() => { emuStreamingRef.current = emuStreaming; }, [emuStreaming]);
  // AUTO-LOCK: server harakatsizlikdan oqimni to'xtatganda slot true bo'ladi —
  // poster ustida "▶ Davom etish" overlay chiqadi (yangi kadr kelsa o'zi tozalanadi).
  const [emuIdleStopped, setEmuIdleStopped] = useState({}); // slot -> bool
  const emuIdleStoppedRef = useRef({});
  emuIdleStoppedRef.current = emuIdleStopped;
  // ☕ «Uxlatma» (/emu oynasi, 2.217.0): yoqilsa emulator oqimi harakatsizlikda to'xtamaydi (har 30 s
  // `emulator:keepalive`), kompyuter ekrani o'chmaydi (Screen Wake Lock), brauzer qurilmalar yashirin oynada ham
  // yopilmaydi (WebDevices.js shu kalitni o'qiydi). Tanlov shu brauzerda eslab qolinadi.
  const [emuAwake, setEmuAwake] = useState(() => { try { return localStorage.getItem('kh_emu_awake') === '1'; } catch (e) { return false; } });
  // Baribir shuncha daqiqadan keyin o'zi o'chadi (settings.emu_awake_max_min, Konfiguratsiya → 📱 Emulator oqimi; 0 = cheklovsiz).
  // Boshlangan vaqt `kh_emu_awake_at` da — bir nechta /emu oynasi bitta muddatni ko'radi.
  const [emuAwakeMax, setEmuAwakeMax] = useState(120);
  const [emuAwakeLeft, setEmuAwakeLeft] = useState(null); // qolgan daqiqa (tugmada ko'rsatiladi)
  // PREVIEW = oddiy PNG screencap oqimi (⚡HD/H.264 rejimi 2026-07-29 da OLIB TASHLANDI —
  // operator ishlatmasdi; backend'dagi h264 quvuri endi hech qachon chaqirilmaydi).
  const emuAutoPreviewRef = useRef(null); // socket-handlerlar uchun barqaror ko'rsatkich
  const [emuStatuses, setEmuStatuses] = useState({});     // slot -> live status
  const [emuBusy, setEmuBusy] = useState({});             // slot -> start/stop/reconnect in flight
  const [emuGridLogs, setEmuGridLogs] = useState([]);     // [{slot,msg,type}]
  const emuDragRef = useRef({});                          // slot -> {x,y,time,moved,fired,lp}
  const [emuTapFx, setEmuTapFx] = useState({});           // slot -> {x,y,id} optimistic tap ripple (cell-relative px)
  // 🤖 Agentik QA: agent shu emulatorni boshqarayotganda operator JONLI ko'radi —
  // ekranda ripple (qurilma koordinatasi → % bilan joylashadi), tagida oxirgi
  // harakatlar tasmasi, tepada "🤖 Agent" belgisi + "✋ Boshqaruvni olish".
  const [emuAgent, setEmuAgent] = useState({});           // slot -> {active, taskId, paused, action, detail}
  // 🔒 Administrator boshqaruvni olgan slotlar (docs/knowledge/184): slot -> {by, since}. Bosishlar to'xtaydi.
  const [emuAdminLocks, setEmuAdminLocks] = useState({});
  const emuAdminLocksRef = useRef({});
  useEffect(() => { emuAdminLocksRef.current = emuAdminLocks; }, [emuAdminLocks]);
  const [emuAgentLog, setEmuAgentLog] = useState({});     // slot -> [{action, detail, at}]
  // 🔊 Emulator ovozi: slot -> 'starting'|'on'. Boshida o'chiq (brauzer ovozni faqat bosishdan keyin chaladi).
  const { audio: emuAudio, toggleAudio: toggleEmuAudio } = useEmuAudio(socketRef, { notify: (m) => toast(m, { id: 'kh-emu-audio', duration: 6000 }) });
  const [emuAgentFx, setEmuAgentFx] = useState({});       // slot -> {xPct, yPct, id}
  // 🌐 Brauzer agenti (web QA): modul bo'yicha holat + oxirgi kadr + harakatlar.
  const [webAgent, setWebAgent] = useState({});           // moduleId -> {active, paused, taskId, action, detail, frame}
  const [webAgentLog, setWebAgentLog] = useState({});     // moduleId -> [{action, detail, at}]
  const [webAgentView, setWebAgentView] = useState(true); // true = agent kadri, false = mening iframe'im
  const [emuFullscreen, setEmuFullscreen] = useState([]); // fullscreen'dagi slotlar RO'YXATI (bir nechta birga)
  // Alohida-oyna popout FAQAT bitta emulatorga fokuslangan bo'lsa (/emu?slot=N) — o'sha slot.
  // (URL ochilishda o'zgarmaydi, shuning uchun har render'da qayta o'qish arzon + xavfsiz.)
  const popoutSlot = (() => {
    if (!emulatorOnly) return null;
    const s = new URLSearchParams(window.location.search).get('slot');
    return s == null || s === '' ? null : Number(s);
  })();
  // 🌐 Brauzer qurilmalar (B-slot, WebDevices.js, docs/knowledge/195): /web-devices javobi, to'liq ekrandagilar (id),
  // agent holati (qurilma kaliti bo'yicha, socket 'emulator:agent' surface='wdev').
  const [webDevs, setWebDevs] = useState(null);
  const [wdFullscreen, setWdFullscreen] = useState([]);
  const [wdAgent, setWdAgent] = useState({});
  // 🤖 «Agent» joylashuvi (2026-10-31, operator: «agent boshqarayotgan emulyator katta bo'lib ko'rinsin»): agent oxirgi
  // harakat qilgan qurilma — `e<slot>` yoki `wd:<kalit>`. Ikki qurilma orasida tez-tez sakrab ekran tebranmasin — almashish
  // oralig'i ≥ 2.5 s (oxirgi harakat qilingan qurilma g'olib).
  const [emuAgentFocus, setEmuAgentFocus] = useState(null);
  const agentFocusRef = useRef({ id: null, at: 0, timer: null });
  const bumpAgentFocus = (id) => {
    const r = agentFocusRef.current;
    clearTimeout(r.timer);
    if (r.id === id) { r.at = Date.now(); return; }
    const apply = () => { r.id = id; r.at = Date.now(); setEmuAgentFocus(id); };
    const wait = 2500 - (Date.now() - r.at);
    if (!r.id || wait <= 0) apply(); else r.timer = setTimeout(apply, wait);
  };
  // ⭐ To'liq ekran joylashuvi (khFsLayout): rejim + asosiy qurilma + o'ng ustun ulushi. Loyiha bo'yicha localStorage'da.
  const [emuFsLayout, setEmuFsLayout] = useState({ mode: 'fit', main: null, side: 0.28 });
  const [emuVp, setEmuVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setEmuVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const emuFsLsKey = `kh_emu_fs_${projectId || 0}`;
  const emuFsLsRead = () => { try { return JSON.parse(localStorage.getItem(emuFsLsKey) || 'null') || {}; } catch (e) { return {}; } };
  const emuFsLsWrite = (patch) => { try { localStorage.setItem(emuFsLsKey, JSON.stringify({ ...emuFsLsRead(), ...patch })); } catch (e) {} };
  useEffect(() => {
    const v = emuFsLsRead();
    setEmuFsLayout({ mode: ['main', 'agent'].includes(v.mode) ? v.mode : 'fit', main: v.main || null, side: Math.min(0.6, Math.max(0.15, Number(v.side) || 0.28)) });
  }, [emuFsLsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveEmuFsLayout = (patch, persist = true) => {
    setEmuFsLayout(prev => {
      const v = { ...prev, ...patch };
      if (persist) emuFsLsWrite({ mode: v.mode, main: v.main, side: v.side });
      return v;
    });
  };
  // ⭐ bosilgan qurilma asosiy bo'ladi; asosiyning o'zida bosilsa — yana «mos» (teng) joylashuvga qaytadi.
  const toggleEmuFsMain = (k) => saveEmuFsLayout(emuFsLayout.mode === 'main' && emuFsLayout.main === k ? { mode: 'fit' } : { mode: 'main', main: k });
  // 🔁 /emu oynasi to'liq ekranda yopilgan bo'lsa — keyingi ochilishda AYNAN o'sha qurilmalar bilan qayta ochiladi.
  const emuFsRestoreRef = useRef(false);
  const emuOnlyProjParam = emulatorOnly ? new URLSearchParams(window.location.search).get('project') : null;
  useEffect(() => {
    if (!emulatorOnly || popoutSlot != null || emuFsRestoreRef.current) return;
    if (webDevs === null) return;                                                       // brauzerlar ro'yxati hali kelmagan
    if (!(emuList || []).length && !(webDevs.devices || []).length) return;
    if (emuOnlyProjParam && String(projectId) !== String(emuOnlyProjParam)) return;   // loyiha hali tanlanmagan
    emuFsRestoreRef.current = true;
    const v = emuFsLsRead();
    if (!v.open || !Array.isArray(v.items)) return;
    const running = new Set((emuList || []).filter(e => (emuStatuses[e.slot] || e.status) === 'running').map(e => e.slot));
    const slots = v.items.filter(k => /^e\d+$/.test(String(k))).map(k => Number(String(k).slice(1))).filter(s => running.has(s));
    const wds = new Set((webDevs.devices || []).map(d => d.id));
    const wids = v.items.filter(k => /^w\d+$/.test(String(k))).map(k => Number(String(k).slice(1))).filter(id => wds.has(id));
    if (slots.length) { slots.forEach(s => emuAutoPreview(s)); setEmuFullscreen(slots); }
    if (wids.length) setWdFullscreen(wids);
  }, [emulatorOnly, emuList, projectId, webDevs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!emulatorOnly || popoutSlot != null || !emuFsRestoreRef.current) return;
    const v = emuFsLsRead();
    const items = [...emuFullscreen.map(s => `e${s}`), ...wdFullscreen.map(id => `w${id}`)];
    emuFsLsWrite({ open: items.length > 0, items: items.length ? items : (v.items || []) });
  }, [emuFullscreen, wdFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showPenpot, setShowPenpot] = useState(false);
  const [penpotUrl, setPenpotUrl] = useState(null);
  const [showAiDesign, setShowAiDesign] = useState(false);
  const [aiDesignPrompt, setAiDesignPrompt] = useState('');
  const [aiDesignLoading, setAiDesignLoading] = useState(false);
  const [aiDesignHistory, setAiDesignHistory] = useState([]); // [{prompt, svg, penpotUrl}]
  const [aiDesignImage, setAiDesignImage] = useState(null); // { base64, name }

  // ---------- App Builder state ----------
  const [abProjectId, setAbProjectId] = useState('');
  // 💥 Faol (yechilmagan/qaytgan) crashlar soni — Studio navidagi «💥 Crashlar» badge'i.
  // Yengil `GET /crash-reports/summary` (faqat COUNT) 30s da; CrashesPanel yuklaganda ham yangilaydi.
  const [crashActive, setCrashActive] = useState(0);
  const crashAuthFetchRef = useRef(authFetch); crashAuthFetchRef.current = authFetch; // stale-closure himoyasi
  const crashActiveProjRef = useRef(null);
  useEffect(() => {
    const pid = Number(abProjectId) || null;
    crashActiveProjRef.current = pid;
    setCrashActive(0);
    if (!pid || activeTab !== 'studio') return;
    let stop = false;
    const tick = async () => {
      if (!khPageVisible()) return;
      try {
        const r = await crashAuthFetchRef.current(`${process.env.REACT_APP_API_URL}/crash-reports/summary?projectId=${pid}`);
        const d = r.ok ? await r.json().catch(() => null) : null;
        if (!stop && d && crashActiveProjRef.current === pid) setCrashActive(Number(d.active || 0));
      } catch { /* transient — eski qiymat qoladi */ }
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abProjectId, activeTab]);
  // Socket listenerlari (bir marta o'rnatiladi) joriy proyektni ref orqali ko'radi
  const abProjectIdRef = useRef('');
  useEffect(() => { abProjectIdRef.current = abProjectId; }, [abProjectId]);
  const [abWorkspace, setAbWorkspace] = useState(null);
  const [abPages, setAbPages] = useState([]);
  const [abConnections, setAbConnections] = useState([]);
  const [abSelectedPageId, setAbSelectedPageId] = useState(null);
  const [abPrompt, setAbPrompt] = useState('');
  const [abGenerating, setAbGenerating] = useState(false);
  const [abDesignImage, setAbDesignImage] = useState(null); // {base64, name}
  const [abVersions, setAbVersions] = useState([]);
  const [abDragging, setAbDragging] = useState(null); // {pageId, startX, startY, origX, origY}
  // Yon menyu DEFAULT yig'ilgan (faqat ikonkalar) — operator 2026-09-18; tanlov brauzerda eslab qolinadi.
  const [importOpen, setImportOpen] = useState(false);   // 📥 Mavjud loyihani qo'shish modali
  const [navCollapsed, setNavCollapsedRaw] = useState(() => { try { const v = localStorage.getItem('kh_nav_collapsed'); return v == null ? true : v === '1'; } catch { return true; } });
  const setNavCollapsed = React.useCallback((v) => setNavCollapsedRaw(prev => { const next = typeof v === 'function' ? v(prev) : v; try { localStorage.setItem('kh_nav_collapsed', next ? '1' : '0'); } catch {} return next; }), []);
  const [mobileNavOpen, setMobileNavOpen] = useState(false); // lg dan tor ekranda sidebar ochiqmi
  const [theme, toggleTheme] = useTheme(); // 'light' | 'dark' — sidebar pastidagi 🌙/☀️ tugmasi
  const [abAddingConnection, setAbAddingConnection] = useState(null); // fromPageId
  const [abHoveredCard, setAbHoveredCard] = useState(null);
  const [abMousePos, setAbMousePos] = useState({ x: 0, y: 0 });
  const [abSelectedConnection, setAbSelectedConnection] = useState(null);
  const [abNewPageTitle, setAbNewPageTitle] = useState('');
  // ---------- Logo dizayn state ----------
  const [logoProjectId, setLogoProjectId] = useState('');
  const logoProjectIdRef = useRef('');
  useEffect(() => { logoProjectIdRef.current = logoProjectId; }, [logoProjectId]);
  const [logos, setLogos] = useState([]);
  const [logoPrompt, setLogoPrompt] = useState('');
  const [logoCount, setLogoCount] = useState(1);
  const [logoTransparent, setLogoTransparent] = useState(false); // shaffof fon (no background)
  const [logoExact, setLogoExact] = useState(false); // birga-bir ko'chirish — namuna rasmni AYNAN (raster) nusxalash
  const [logoGenerating, setLogoGenerating] = useState(false);
  const [logoProgress, setLogoProgress] = useState(''); // live progress text during gen
  const [logoImage, setLogoImage] = useState(null); // { base64, name } — namuna rasm (vision)
  const [logoJobId, setLogoJobId] = useState(null); // background job id (to'xtatish / qayta ochganda tiklash)
  const [logoLightbox, setLogoLightbox] = useState(null); // bosilgan logoni kattalashtirib ko'rsatish
  // ---------- Studio hub (unified) state ----------
  const [studioView, setStudioView] = useState('canvas'); // 'canvas' | 'pages' | 'emulator' | 'test'
  // 🤖 Tab ostidagi AI yozuvlari uchun: rol → model (+ alias qaysi aniq modelga ulanishi).
  const [aiTiers, setAiTiers] = useState(null);
  useEffect(() => {
    if (activeTab !== 'studio' && activeTab !== 'design') return undefined;
    let off = false;
    const api = process.env.REACT_APP_API_URL;
    const get = (path) => authFetch(`${api}${path}`).then(r => (r.ok ? r.json() : null)).catch(() => null);
    Promise.all([get('/claude/tiers'), get('/claude/alias-map')]).then(([t, a]) => {
      if (!off && Array.isArray(t)) setAiTiers({ tiers: t, aliases: (a && a.map) || {} });
    });
    return () => { off = true; };
    // authFetch har renderda yangi — unga bog'lasak so'rov aylanib qoladi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const studioViewRef = useRef('canvas');
  useEffect(() => { studioViewRef.current = studioView; }, [studioView]);
  const [studioModuleId, setStudioModuleId] = useState(''); // '' = Barchasi (all modules)
  // 🌐 Brauzer view — jonli test saytni Studio ichida ko'rish + "shu sahifadan vazifa"
  const [browserPath, setBrowserPath] = useState('/');        // URL satri (path qismi)
  const [browserFrameUrl, setBrowserFrameUrl] = useState(''); // iframe'ga berilgan to'liq URL
  const [browserModuleId, setBrowserModuleId] = useState(null); // qaysi web modul ko'rsatilyapti (null = avtomatik)
  const [browserFrameKey, setBrowserFrameKey] = useState(0);  // ↻ reload uchun
  // ↻ bosilganda iframe'ni HAQIQATAN yangilash uchun kesh-buzar. React key iframe'ni
  // qayta yaratadi, lekin brauzer AYNI URL uchun keshdagi nusxani beraveradi — operator
  // deploydan keyin eski sahifani ko'rib «bajarilmabdi» deb o'ylaydi (jonli hodisa #1472).
  const [browserBust, setBrowserBust] = useState(0);
  const bustUrl = (u) => {
    if (!u || !browserBust) return u;
    try { const x = new URL(u); x.searchParams.set('_kh', String(browserBust)); return x.toString(); }
    catch { return u + (u.includes('?') ? '&' : '?') + '_kh=' + browserBust; }
  };
  const reloadBrowserFrame = () => { setBrowserBust(Date.now()); setBrowserFrameKey(k => k + 1); };
  const [browserPagesOpen, setBrowserPagesOpen] = useState(false); // 📄 Sahifalar dropdown ochiq/yopiq
  const [browserPagesQuery, setBrowserPagesQuery] = useState('');  // dropdown ichidagi qidiruv
  const taskFileInputRef = useRef(null); // drawer vazifa formasi — yashirin fayl input (📎 Rasm chip ochadi)
  // 📄 Vazifaga ILOVA FAYL (HTML/PDF/matn) — rasmdan ALOHIDA: rasm vision bo'lib
  // ketadi, bu fayllar esa agent tomonidan O'QILADI (tayyor HTML → "shunga o'xshash sayt").
  const taskDocInputRef = useRef(null);
  const [newTaskFiles, setNewTaskFiles] = useState([]);   // [{path,name,size}]
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const canvasRef = React.useRef(null);
  const [lastFrameTime, setLastFrameTime] = useState(null);
  const [streamFrozen, setStreamFrozen] = useState(false);
  const isStreamingRef = useRef(false);
  const dragRef = useRef(null);
  const imgRef = useRef(null);

  // Phone visitors → dedicated mobile control surface (/m). Desktop override
  // via localStorage (kh_force_desktop, set by the 🖥 button in MobilePanel).
  useEffect(() => {
    if (emulatorOnly) return; // the pop-out emulator window is narrow but must NOT redirect to /m
    try {
      const small = window.innerWidth < 768;
      const forced = localStorage.getItem('kh_force_desktop') === '1';
      if (small && !forced && window.location.pathname !== '/m') {
        window.location.replace('/m' + window.location.search);
      }
    } catch (e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_API_URL ?? '';
    const token = localStorage.getItem('khorezmai_token') || '';
    const socket = io(socketUrl, { auth: { token } });
    socketRef.current = socket;

    socket.on('new_log', (data) => {
      setLiveLogs(prev => {
        const updated = [...prev, data];
        return updated.slice(-100); // Faqat oxirgi 100 qator logni saqlaymiz
      });

      if (data.status) {
        setTasks(prev => {
          let updatedMainId = null;
          let isMainTask = false;
          
          const newTasks = prev.map(t => {
            if (String(t.id) === String(data.taskId)) {
              isMainTask = true;
              return { ...t, status: data.status };
            }
            if (t.sub_tasks && t.sub_tasks.some(st => String(st.id) === String(data.taskId))) {
              updatedMainId = t.id;
              const newSubTasks = t.sub_tasks.map(st => 
                String(st.id) === String(data.taskId) ? { ...st, status: data.status } : st
              );
              return { ...t, sub_tasks: newSubTasks };
            }
            return t;
          });

          if (data.finished && data.status === 'completed') {
            if (isMainTask) {
              const mainTask = newTasks.find(t => String(t.id) === String(data.taskId));
              if (!mainTask.sub_tasks || mainTask.sub_tasks.length === 0) {
                setTimeout(() => toast.success('Asosiy vazifa bajarildi!'), 0);
              }
            } else if (updatedMainId) {
              const mainTask = newTasks.find(t => t.id === updatedMainId);
              if (mainTask && mainTask.sub_tasks.every(st => st.status === 'completed' || st.status === 'failed')) {
                setTimeout(() => toast.success('Barcha qism vazifalar muvaffaqiyatli yakunlandi!'), 0);
              }
            }
          }
          return newTasks;
        });

        if (data.finished) {
          setTaskTrigger(t => t + 1);
        }
      }
    });

    socket.on('emulator:frame', (payload) => {
      // Shape: {slot, data, fmt?}. data = base64 STRING (eski backend, PNG) YOKI BINARY
      // ArrayBuffer (yangi backend: JPEG/PNG Buffer — base64'siz, ~kichikroq). Ikkalasini ham
      // src-READY URL'ga aylantirib state'ga qo'yamiz (render to'g'ridan-to'g'ri src qiladi).
      // Blob URL'lar oqib ketmasligi uchun eskisi kechiktirilib revoke qilinadi (2s —
      // <img> yangi kadrni yuklab ulgurishi uchun).
      const slot = (payload && typeof payload === 'object') ? (payload.slot || 0) : 0;
      const data = (payload && typeof payload === 'object') ? payload.data : payload;
      let src;
      if (typeof data === 'string') {
        src = `data:image/png;base64,${data}`;
      } else if (data) {
        try {
          const mime = payload && payload.fmt === 'jpg' ? 'image/jpeg' : 'image/png';
          src = URL.createObjectURL(new Blob([data], { type: mime }));
        } catch { return; }
        const old = emuFrameUrlRef.current[slot];
        emuFrameUrlRef.current[slot] = src;
        if (old && old.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(old); } catch {} }, 2000);
      } else return;
      setEmuFrames(f => ({ ...f, [slot]: src }));
      if (slot === 0) setEmulatorFrame(src); // legacy single-panel
      setLastFrameTime(Date.now());
      // Kadr keldi = oqim tirik (boshqa oyna davom ettirgan bo'lishi mumkin) — overlay tozalanadi.
      setEmuIdleStopped(s => (s && s[slot]) ? { ...s, [slot]: false } : s);
    });

    // AUTO-LOCK: serverda N daqiqa foydalanuvchi harakati bo'lmagani uchun oqim to'xtatildi
    // (telefon auto-lock kabi; Konfiguratsiya → Emulator oqimi sozlamasi). Poster qoladi,
    // ustida "▶ Davom etish" overlay chiqadi.
    socket.on('emulator:stream_idle_stop', (payload) => {
      const slot = (payload && payload.slot) || 0;
      setEmuStreaming(s => ({ ...s, [slot]: false }));
      setEmuIdleStopped(s => ({ ...s, [slot]: true }));
    });
    // ☕ Server «Uyg'oq» muddati tugaganini aytdi (boshqa oyna yoki soat farqi) — tugma o'chadi.
    socket.on('emulator:awake_expired', () => {
      try { localStorage.setItem('kh_emu_awake', '0'); localStorage.removeItem('kh_emu_awake_at'); } catch (e) {}
      setEmuAwake(false);
    });

    socket.on('connect', () => {
      // re-subscribe every slot we were streaming (grid) + legacy slot 0
      const streaming = emuStreamingRef.current || {};
      Object.keys(streaming).forEach(slot => { if (streaming[slot]) socket.emit('emulator:start_stream', { slot: +slot }); });
      if (isStreamingRef.current) socket.emit('emulator:start_stream', { slot: 0 });
    });

    socket.on('emulator:status', (data) => {
      // Filter: only process events for the current user
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (myId && data.userId && String(data.userId) !== String(myId)) return;
      const slot = data.slot || 0;
      setEmuStatuses(s => ({ ...s, [slot]: data.status }));
      setEmuBusy(b => ({ ...b, [slot]: false }));
      if (slot === 0) setEmulatorStatus(data.status); // legacy
      // Auto-(re)start preview when an emulator comes online (P1: default = HD, fallback PNG)
      if (data.status === 'running') {
        setTimeout(() => {
          if (emuAutoPreviewRef.current) emuAutoPreviewRef.current(slot);
          else { socket.emit('emulator:start_stream', { slot }); setEmuStreaming(st => ({ ...st, [slot]: true })); }
        }, 800);
      }
    });

    // 🔒 Administrator emulatorni boshqaruvga oldi / qaytardi — faqat o'z emulatorlarim.
    socket.on('emulator:admin_lock', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (!data || (myId && String(data.userId) !== String(myId))) return;
      const slot = data.slot || 0;
      // Qulfni o'zim (admin, /emu-view oynasidan) olgan bo'lsam — o'z katagimni to'smayman.
      const mine = data.adminId != null && myId != null && String(data.adminId) === String(myId);
      setEmuAdminLocks(s => { const n = { ...s }; if (data.locked && !mine) n[slot] = { by: data.by, since: data.since }; else delete n[slot]; return n; });
    });
    socket.on('emulator:locked', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (data && myId && data.userId && String(data.userId) !== String(myId)) return;
      toast(`🔒 Administrator (${(data && data.by) || 'admin'}) boshqaryapti — hozircha faqat ko'rasiz`, { id: 'emu-admin-lock' });
    });

    // 🤖 Agent harakatlari (server /internal/emu-agent orqali uzatadi).
    socket.on('operator:confirmation', (d) => { try { khNotifyConfirm(d); } catch (e) { } });
    attachConfirmBlink(socket);   // 🔔 kutilayotgan tasdiq — sarlavha miltillaydi (src/confirmBlink.js)
    socket.on('emulator:agent', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (myId && data.userId && String(data.userId) !== String(myId)) return;
      // 🌐 B-qurilma (operator bilan umumiy brauzer) — qurilma kaliti bo'yicha (WebDevices.js).
      if (data.surface === 'wdev') {
        const k = String(data.deviceKey || '');
        if (!k) return;
        if (data.phase === 'end') { setWdAgent(a => ({ ...a, [k]: { active: false } })); return; }
        if (data.action || data.phase === 'start') bumpAgentFocus(`wd:${k}`);
        setWdAgent(a => ({ ...a, [k]: { active: true, paused: !!data.paused, taskId: data.taskId,
          label: data.label || (a[k] && a[k].label) || null, startedAt: data.startedAt || (a[k] && a[k].startedAt) || null,
          last: data.action ? { action: data.action, detail: data.detail } : ((a[k] && a[k].last) || null) } }));
        return;
      }
      // 🌐 WEB sirti — emulator emas, MODUL bo'yicha (busiz web hodisalari
      // xato ravishda "Emulator 1 da agent ishlayapti" deb ko'rsatilardi).
      if (data.surface === 'web') {
        const mid = Number(data.moduleId) || 0;
        if (data.phase === 'end') { setWebAgent(a => ({ ...a, [mid]: { active: false } })); return; }
        setWebAgent(a => ({
          ...a,
          [mid]: {
            active: true, paused: !!data.paused, taskId: data.taskId,
            label: data.label || (a[mid] && a[mid].label) || null, startedAt: data.startedAt || (a[mid] && a[mid].startedAt) || null,
            action: data.action, detail: data.detail,
            frame: data.frame || (a[mid] && a[mid].frame) || null,
          },
        }));
        if (data.action) setWebAgentLog(l => ({ ...l, [mid]: [...(l[mid] || []).slice(-4), { action: data.action, detail: data.detail, at: Date.now() }] }));
        return;
      }
      const slot = Number(data.slot) || 0;
      if (data.phase === 'end') {
        setEmuAgent(a => ({ ...a, [slot]: { active: false } }));
        return;
      }
      if (data.action || data.phase === 'start') bumpAgentFocus(`e${slot}`);
      setEmuAgent(a => ({
        ...a,
        [slot]: { active: true, taskId: data.taskId, paused: !!data.paused, action: data.action, detail: data.detail,
          label: data.label || (a[slot] && a[slot].label) || null, startedAt: data.startedAt || (a[slot] && a[slot].startedAt) || null,
          projectId: data.projectId || (a[slot] && a[slot].projectId) || null },
      }));
      if (data.action) {
        setEmuAgentLog(l => ({ ...l, [slot]: [...(l[slot] || []).slice(-4), { action: data.action, detail: data.detail, at: Date.now() } ] }));
      }
      // Ripple: agent QURILMA koordinatasini yuboradi → ramka aspect'i qurilmanikiga
      // teng (emuAspect), shuning uchun foizga aylantirish aniq.
      if (Number.isFinite(data.x) && Number.isFinite(data.y)) {
        const asp = (emuAspectRef.current || {})[slot];
        const m = String(asp || '').match(/(\d+)\s*\/\s*(\d+)/);
        if (m) {
          setEmuAgentFx(f => ({ ...f, [slot]: { xPct: (data.x / Number(m[1])) * 100, yPct: (data.y / Number(m[2])) * 100, id: Date.now() } }));
        }
      }
    });

    socket.on('emulator:log', (data) => {
      setEmuGridLogs(prev => [...prev.slice(-60), { slot: data.slot || 0, msg: data.msg, type: data.type }]);
      setEmulatorLogs(prev => [...prev.slice(-100), { msg: data.msg, type: data.type }]); // legacy
    });

    // 📍 Marshrut (GPS harakat simulyatsiyasi) progressi — har tikda bitta event.
    socket.on('emulator:route', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (myId && data.userId && String(data.userId) !== String(myId)) return;
      const slot = data.slot || 0;
      setEmuRoute(prev => ({ ...prev, [slot]: { running: !!data.running, index: data.index, total: data.total, lat: data.lat, lng: data.lng } }));
      if (!data.running) toast.success(`📍 Emulator ${slot + 1}: marshrut tugadi`);
    });

    // TZ→dizayn fire-and-stream progressi (generate-from-tz endi uzun Claude
    // jarayoni — natija HTTP javobda emas, shu socket eventida keladi).
    socket.on('tz_progress', (data) => {
      if (String(data.projectId) !== String(abProjectIdRef.current)) return;
      if (data.stage === 'plan') {
        setTzProgress('TZ tahlil qilinmoqda (reja)...');
      } else if (data.stage === 'page') {
        setTzProgress(`${(data.done || 0) + 1}/${data.total || '?'}: ${data.pageTitle || 'sahifa'}...`);
      } else if (data.stage === 'done') {
        const p = data.payload || {};
        if (p.workspace) setAbWorkspace(p.workspace);
        setAbPages(p.pages || []);
        setAbConnections(p.connections || []);
        setAbSelectedPageId(null);
        setShowTzModal(false);
        setTzText('');
        setTzGenerating(false);
        setTzProgress('');
        toast.success(`"${p.appName || 'App'}" — ${(p.pages || []).length} ta sahifa yaratildi`);
      } else if (data.stage === 'cancelled') {
        setTzGenerating(false);
        setTzProgress('');
        setShowTzModal(false);
        toast(`⏹ TZ dizayn to'xtatildi${data.done ? ` — ${data.done} ta sahifa saqlandi` : ''}`);
        if (data.done && abProjectIdRef.current) abFetchWorkspace(abProjectIdRef.current); // saqlanganlarni ko'rsatish
      } else if (data.stage === 'error') {
        setTzGenerating(false);
        setTzProgress('');
        toast.error(data.error || 'TZ generatsiya xatosi');
      }
    });

    // 🏷 Logoni dizaynga joylash — har saqlangan sahifa kanvasda darhol yangilanadi.
    socket.on('design_logo_progress', (data) => {
      if (String(data.projectId) !== String(abProjectIdRef.current)) return;
      if (data.stage === 'page') {
        setAbLogoJobId(data.jobId);
        setAbLogoProgress(`${(data.done || 0) + 1}/${data.total}: ${data.pageTitle || 'sahifa'}...`);
      } else if (data.stage === 'saved') {
        if (data.svg) setAbPages(prev => prev.map(p => (p.id === data.pageId ? { ...p, svg_content: data.svg } : p)));
      } else if (data.stage === 'page_error') {
        toast.error(`"${data.pageTitle}": ${data.error}`);
      } else if (data.stage === 'done' || data.stage === 'cancelled' || data.stage === 'error') {
        setAbLogoJobId(null);
        setAbLogoProgress('');
        if (data.stage === 'done') toast.success(`🏷 Logo ${data.changed || 0} ta sahifaga joylandi${data.skipped ? ` (${data.skipped} tasida joy yo'q)` : ''}`);
        else if (data.stage === 'cancelled') toast(`⏹ To'xtatildi — ${data.changed || 0} ta sahifaga joylangan`);
        else toast.error(data.error || 'Logo joylashda xato');
      }
    });

    // Logo dizayn fire-and-stream — variantlar birma-bir keladi (~2 daqiqa/dona).
    socket.on('logo_progress', (data) => {
      if (String(data.projectId) !== String(logoProjectIdRef.current)) return;
      if (data.stage === 'working') {
        setLogoProgress(`${data.done || 0}/${data.total} tayyor — chizilmoqda...`);
      } else if (data.stage === 'variant') {
        if (data.logo) setLogos(prev => [data.logo, ...prev]);
        setLogoProgress(`${data.done}/${data.total} tayyor...`);
      } else if (data.stage === 'done') {
        setLogoGenerating(false);
        setLogoProgress('');
        setLogoJobId(null);
        fetchLogos(logoProjectIdRef.current); // yo'qolgan 'variant' eventlarini ham reconcile qiladi
        toast.success(`✅ ${data.count} ta logo yaratildi`);
      } else if (data.stage === 'cancelled') {
        setLogoGenerating(false);
        setLogoProgress('');
        setLogoJobId(null);
        fetchLogos(logoProjectIdRef.current);
        toast(`⏹ Logo yaratish to'xtatildi${data.done ? ` — ${data.done} ta variant saqlandi` : ''}`);
      } else if (data.stage === 'error') {
        setLogoGenerating(false);
        setLogoProgress('');
        setLogoJobId(null);
        toast.error(data.error || 'Logo yaratishda xato');
      }
    });

    // 🚀 Sehrgar qadamlari — har qadam holati (running/ok/fail) jonli keladi.
    socket.on('wizard_step', (d) => {
      if (!d) return;
      // Boshqa modulning sehrgari ochiq kartani chalkashtirmasin (server_log bilan bir xil qoida).
      if (selectedModuleRef.current?.id != d.moduleId) return;
      if (d.key === '_init' && Array.isArray(d.steps)) {
        setWizardSteps(d.steps.map(x => ({ ...x, status: 'pending' })));
        return;
      }
      if (d.key === '_error') {
        // Uzilgan joyni ko'rsatamiz: ishlab turgan qadam qizil bo'ladi.
        setWizardSteps(prev => prev.map(x => x.status === 'running' ? { ...x, status: 'fail', detail: d.detail } : x));
        return;
      }
      setWizardSteps(prev => prev.map(x => x.key === d.key ? { ...x, status: d.status, detail: d.detail } : x));
    });

    socket.on('server_log', (data) => {
      if (selectedModuleRef.current?.id != data.moduleId) return;
      if (data.type === 'env_required') {
        setServerEnvRequired({ envVars: data.envVars || [], envExample: data.envExample || '' });
        setServerRunning(false);
        return;
      }
      if (data.type === 'fields_updated' && data.fields) {
        setSelectedModule(prev => prev ? { ...prev, ...data.fields } : prev);
        setSelectedProjectModules(prev => prev.map(m => String(m.id) === String(data.moduleId) ? { ...m, ...data.fields } : m));
      }
      if (data.type === 'mobile_installed') {
        // Tell the user EXACTLY which emulator got the app — the cure for "build qildim,
        // app paydo bo'lmadi" (they were watching a different emulator). If they're in the
        // grid, live-stream those slots so the app shows up where it landed.
        const slots = Array.isArray(data.slots) ? data.slots : [];
        if (slots.length) {
          toast.success(`📲 ${data.projectName ? `«${data.projectName}» ` : 'App '}Emulator ${slots.map(s => s + 1).join(', ')} ga o'rnatildi — o'sha emulatorni oching`);
          if (studioViewRef.current === 'emulator') {
            slots.forEach(s => {
              if (emuAutoPreviewRef.current) emuAutoPreviewRef.current(s);
              else if (!emuStreamingRef.current?.[s]) {
                socket.emit('emulator:start_stream', { slot: s });
                setEmuStreaming(st => ({ ...st, [s]: true }));
              }
            });
          }
        }
        return;
      }
      if (data.type === 'done') {
        setServerRunning(false);
        if (selectedModuleRef.current) loadServerHealth(selectedModuleRef.current.id);
        return;
      }
      if (data.log && data.log !== 'DONE') {
        setServerLogs(prev => [...prev.slice(-200), { log: data.log, type: data.type }]);
      }
    });

    return () => {
      socket.emit('emulator:stop_stream');
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (terminalContainerRef.current) {
      terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
    }
  }, [liveLogs]);

  const requestAiDesign = async () => {
    if (!aiDesignPrompt.trim() || aiDesignLoading) return;
    const prompt = aiDesignPrompt.trim();
    const image = aiDesignImage;
    setAiDesignLoading(true);
    setAiDesignPrompt('');
    setAiDesignImage(null);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/penpot/ai-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, imageBase64: image?.base64 || null }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      setAiDesignHistory(h => [{ prompt, svg: data.svg, penpotUrl: data.penpotFileUrl, image: image?.base64 }, ...h]);
    } catch (e) {
      toast.error('Xatolik yuz berdi');
    } finally {
      setAiDesignLoading(false);
    }
  };

  // Load public config (Penpot URL, GitLab admin-token presence)
  const [gitlabAdminTokenConfigured, setGitlabAdminTokenConfigured] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/config`)
      .then(r => r.json())
      .then(d => {
        if (d.penpotUrl) setPenpotUrl(d.penpotUrl);
        setGitlabAdminTokenConfigured(!!d.gitlabAdminTokenConfigured);
      })
      .catch(() => {});
  }, []);

  // ---------- App Builder functions ----------
  const abFetchWorkspace = async (projectId) => {
    if (!projectId) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/${projectId}`);
      const data = await res.json();
      setAbWorkspace(data.workspace);
      setAbPages(data.pages || []);
      setAbConnections(data.connections || []);
      setAbSelectedPageId(null);
      setAbVersions([]);
    } catch (e) { console.error(e); }
  };

  // Unified project selection for the Studio hub — keeps the three legacy
  // selectors (abProjectId / projectId / pagesProjectId) in lockstep so all the
  // existing task/page/canvas plumbing keeps working unchanged.
  const selectStudioProject = (id) => {
    setAbProjectId(id);
    setProjectId(id);
    setPagesProjectId(id);
    setLogoProjectId(id); // 🏷 Logo (Dizayn ichida) ham shu proyektga lockstep — task formdagi logolar mos bo'lsin
    setSelectedLogoIds([]);
    setLogoImage(null); setLogoGenerating(false); setLogoProgress(''); setLogoJobId(null); // boshqa loyihaning jarayoni ko'rinmasin (tiklash effekti qayta topadi)
    setStudioModuleId('');
    setAbSelectedPageId(null);
    setBrowserModuleId(null);   // Brauzer tanlovi loyihaga bog'liq — yangi loyihada avtomatikka qaytadi
    setBrowserPath('/');
    setBrowserFrameUrl('');
    // Default view must match the ACTIVE sidebar section — 🖼️ Dizayn → canvas, 🏗️ Studio →
    // a Studio view (test). Forcing 'canvas' regardless of activeTab caused the "sidebar=Studio
    // but content=Dizayn" mismatch when a project auto-selected on fresh load / re-auth.
    setStudioView(activeTab === 'studio' ? 'test' : (studioView === 'logo' ? 'logo' : 'canvas'));
    if (id) {
      abFetchWorkspace(id);
      fetchSelectedProjectModules(id);
      fetchLogos(id);
      fetchProjectEmuBinding(id); // emulator biriktiruvi shu loyihaga mos bo'lsin
    } else {
      setAbWorkspace(null);
      setAbPages([]);
      setAbConnections([]);
      setSelectedProjectModules([]);
      setLogos([]);
      setProjectEmuSlot(null);
    }
  };

  const abAddPage = async () => {
    const title = abNewPageTitle.trim() || 'New Page';
    const route = '/' + title.toLowerCase().replace(/\s+/g, '-');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/${abProjectId}/pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, route, module_id: studioModuleId || null }),
      });
      const page = await res.json();
      setAbPages(prev => [...prev, page]);
      setAbSelectedPageId(page.id);
      setAbNewPageTitle('');
    } catch (e) { toast.error('Sahifa qo\'shishda xato'); }
  };

  const abDeletePage = async (pageId) => {
    if (!window.confirm('Bu sahifani o\'chirishni tasdiqlaysizmi?')) return;
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${pageId}`, { method: 'DELETE' });
      setAbPages(prev => prev.filter(p => p.id !== pageId));
      setAbConnections(prev => prev.filter(c => c.from_page_id !== pageId && c.to_page_id !== pageId));
      if (abSelectedPageId === pageId) { setAbSelectedPageId(null); setAbVersions([]); }
      toast.success('Sahifa o\'chirildi');
    } catch (e) { toast.error('Xato'); }
  };

  const abGenerate = async () => {
    if (!abPrompt.trim() || !abSelectedPageId || abGenerating) return;
    const img = abDesignImage;
    setAbGenerating(true);
    setAbDesignImage(null);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPageId}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: abPrompt, imageBase64: img?.base64 || null }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error); return; }
      setAbPages(prev => prev.map(p => p.id === abSelectedPageId ? { ...p, svg_content: data.svg } : p));
      setAbVersions(prev => [{ id: data.versionId, version_number: data.versionNumber, prompt: abPrompt, created_at: new Date().toISOString() }, ...prev]);
      setAbPrompt('');
      toast.success(`v${data.versionNumber} saqlandi`);
    } catch (e) { toast.error('Xato'); }
    finally { setAbGenerating(false); }
  };

  const abLoadVersions = async (pageId) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${pageId}/versions`);
      const data = await res.json();
      setAbVersions(data);
    } catch (e) {}
  };

  const abRevert = async (versionId) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPageId}/revert/${versionId}`, { method: 'POST' });
      const page = await res.json();
      setAbPages(prev => prev.map(p => p.id === abSelectedPageId ? page : p));
      toast.success('Versiyaga qaytildi');
    } catch (e) { toast.error('Xato'); }
  };

  const abUpdatePosition = async (pageId, x, y) => {
    await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${pageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position_x: Math.round(x), position_y: Math.round(y) }),
    });
  };

  const abAddConnection = async (toPageId) => {
    if (!abAddingConnection || abAddingConnection === toPageId || !abWorkspace) return;
    // prevent duplicate
    const exists = abConnections.find(c => c.from_page_id === abAddingConnection && c.to_page_id === toPageId);
    if (exists) { setAbAddingConnection(null); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId: abWorkspace.id, fromPageId: abAddingConnection, toPageId }),
      });
      const conn = await res.json();
      setAbConnections(prev => [...prev, conn]);
    } catch (e) {}
    setAbAddingConnection(null);
  };

  const abDeleteConnection = async (connId) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/connections/${connId}`, { method: 'DELETE' });
      setAbConnections(prev => prev.filter(c => c.id !== connId));
      setAbSelectedConnection(null);
    } catch (e) { toast.error('Xato'); }
  };

  const abSelectedPage = abPages.find(p => p.id === abSelectedPageId) || null;
  // Studio canvas filtered by the selected module tab ('' = Barchasi → all, incl. NULL-bucket)
  const visibleAbPages = studioModuleId
    ? abPages.filter(p => String(p.module_id) === String(studioModuleId))
    : abPages;
  const abUnbucketedCount = abPages.filter(p => p.module_id == null).length;
  const moduleTypeIcon = (t) => t === 'mobile' ? '📱' : t === 'backend' ? '⚙️' : '🌐';

  // ---------- ER diagram (backend DB schema) layout ----------
  const ERD_PALETTE = ['#16a34a', '#2563eb', '#db2777', '#9333ea', '#ea580c', '#0891b2', '#dc2626', '#ca8a04'];
  const layoutErd = (tables) => {
    const W = 260, GAP = 48, headerH = 38, rowH = 26, pad = 10;
    const n = tables.length;
    const cols = Math.max(1, Math.min(4, Math.ceil(Math.sqrt(n))));
    const boxes = [];
    let y = 0;
    for (let row = 0; row * cols < n; row++) {
      const rowTables = tables.slice(row * cols, row * cols + cols);
      let bandH = 0;
      rowTables.forEach((t, i) => {
        const h = headerH + Math.max(1, t.columns.length) * rowH + pad;
        boxes.push({ table: t, x: i * (W + GAP), y, w: W, h });
        bandH = Math.max(bandH, h);
      });
      y += bandH + GAP;
    }
    return boxes;
  };

  // ---------- Studio compare: pair a design page with a real page ----------
  const normalizeRoute = (r) => {
    if (!r) return '';
    let s = String(r).trim().toLowerCase().replace(/[_\s]+/g, '-');
    if (!s.startsWith('/')) s = '/' + s;
    s = s.replace(/\/+$/, '');
    return s || '/';
  };
  const stripPageWord = (s) => String(s || '')
    .replace(/(page|screen|view|component|tab)$/i, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '');
  // design page -> matched real page (from pagesList), or null
  const matchRealPage = (d) => {
    if (!pagesList.length) return null;
    // Only pair a design with real pages from the SAME module (when both are tagged) —
    // avoids a web design matching a mobile page in the combined "Barchasi" list.
    const pool = pagesList.filter(r => r._moduleId == null || d.module_id == null || String(r._moduleId) === String(d.module_id));
    if (d.real_page_ref) {
      const m = pool.find(r => r.relPath === d.real_page_ref);
      if (m) return m;
    }
    const dr = normalizeRoute(d.route);
    if (dr) {
      const m = pool.find(r => normalizeRoute(r.route) === dr);
      if (m) return m;
    }
    const dt = stripPageWord(d.title);
    if (dt) {
      let m = pool.find(r => stripPageWord(r.name) === dt);
      if (m) return m;
      // Lenient: one name contains the other (design "web login" ⊇ real "login").
      m = pool.find(r => { const rn = stripPageWord(r.name); return rn.length >= 3 && (dt.includes(rn) || rn.includes(dt)); });
      if (m) return m;
    }
    return null;
  };
  // Take the matched real PAGE object (not just a name) so per-module screenshots
  // never collide on a shared name (e.g. both modules have an "App").
  const screenshotUrlFor = (real) => {
    if (!real) return null;
    return real._screenshotUrl || pageScreenshots[`${real._moduleId}:${real.name}`] || pageScreenshots[real.name] || null;
  };

  // Smooth drag via document-level events
  const abDraggingRef = React.useRef(null);
  React.useEffect(() => {
    if (!abDragging) { abDraggingRef.current = null; return; }
    abDraggingRef.current = abDragging;
    const onMove = (e) => {
      const d = abDraggingRef.current;
      if (!d) return;
      const nx = Math.max(0, d.origX + e.clientX - d.startX);
      const ny = Math.max(0, d.origY + e.clientY - d.startY);
      setAbPages(prev => prev.map(p => p.id === d.pageId ? { ...p, position_x: nx, position_y: ny } : p));
    };
    const onUp = () => {
      const d = abDraggingRef.current;
      if (d) {
        setAbPages(prev => {
          const page = prev.find(p => p.id === d.pageId);
          if (page) abUpdatePosition(d.pageId, page.position_x, page.position_y);
          return prev;
        });
      }
      setAbDragging(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [abDragging]); // eslint-disable-line react-hooks/exhaustive-deps

  // TZ-based generation
  const [showTzModal, setShowTzModal] = useState(false);
  const [tzText, setTzText] = useState('');
  const [tzGenerating, setTzGenerating] = useState(false);
  const [tzProgress, setTzProgress] = useState('');

  // ── PDF Export ───────────────────────────────────────────────────────
  const [abPdfExporting, setAbPdfExporting] = useState(false);
  const [abLogoJobId, setAbLogoJobId] = useState(null);     // 🏷 Logoni joylash — fon jarayoni (background_jobs id)
  const [abLogoProgress, setAbLogoProgress] = useState('');

  const svgToPngDataUrl = (svgString, width = 390, height = 844, transparent = false) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const scale = 2; // Retina quality
      canvas.width  = width  * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      if (!transparent) {           // logos export with transparent=true → preserve alpha
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
      }

      // Extract SVG dimensions
      const parser = new DOMParser();
      const doc    = parser.parseFromString(svgString, 'image/svg+xml');
      const svg    = doc.documentElement;
      if (!svg.getAttribute('width'))  svg.setAttribute('width',  String(width));
      if (!svg.getAttribute('height')) svg.setAttribute('height', String(height));

      const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
      const url  = URL.createObjectURL(blob);
      const img  = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        // Fallback: white page
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = url;
    });
  };

  const abExportPdf = async (singlePage = null) => {
    const pagesToExport = singlePage
      ? [singlePage]
      : abPages.filter(p => p.svg_content);

    if (!pagesToExport.length) {
      toast.error("Eksport qilish uchun dizayn yo'q");
      return;
    }

    setAbPdfExporting(true);
    toast('PDF tayyorlanmoqda...', { duration: 3000 });

    try {
      // Mobile phone size: 390x844 (iPhone 14 logical)
      const W = 390, H = 844;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [W, H] });

      for (let i = 0; i < pagesToExport.length; i++) {
        const page = pagesToExport[i];
        if (i > 0) pdf.addPage([W, H]);

        if (page.svg_content) {
          const pngData = await svgToPngDataUrl(page.svg_content, W, H);
          pdf.addImage(pngData, 'PNG', 0, 0, W, H);
        }

        // Page title at bottom
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        const title = page.title || `Sahifa ${i + 1}`;
        pdf.text(title, W / 2, H - 8, { align: 'center' });
      }

      const projectName = abPages[0]?.title
        ? abPages[0].title.replace(/[^a-zA-Z0-9]/g, '_')
        : 'khorezmai_design';
      pdf.save(`${projectName}_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success(`✅ PDF saqlandi (${pagesToExport.length} sahifa)`);
    } catch (e) {
      toast.error('PDF xatolik: ' + e.message);
    } finally {
      setAbPdfExporting(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  // ── Logo dizayn (per-project AI logos, attachable to engine tasks) ────
  const fetchLogos = async (pid) => {
    if (!pid) { setLogos([]); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/logos/${pid}`);
      if (!res.ok) return;
      const data = await res.json();
      setLogos(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleGenerateLogo = async () => {
    if (!logoProjectId || logoGenerating) return;
    // 🎯 Birga-bir ko'chirish = namuna rasmni AYNAN nusxalash (raster) — prompt shart emas,
    // lekin namuna rasm SHART. Oddiy generatsiya uchun esa prompt shart.
    const exact = logoExact && !!logoImage;
    if (logoExact && !logoImage) { toast.error('Birga-bir ko\'chirish uchun avval 🖼 namuna rasm yuklang'); return; }
    if (!exact && !logoPrompt.trim()) return;
    setLogoGenerating(true);
    setLogoProgress(exact ? 'Nusxalanmoqda...' : 'Yuborilmoqda...');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/logos/${logoProjectId}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: logoPrompt.trim(), count: logoCount, imageBase64: logoImage?.base64 || null, transparent: logoTransparent, exact }),
      });
      const data = await res.json();
      if (!res.ok || !data.started) throw new Error(data.error || 'Server xatosi');
      // Jarayon backend'da davom etadi (har variant ~2 daqiqa) — sahifani yopsangiz
      // ham. Natijalar `logo_progress` socket eventida birma-bir keladi; holat
      // ⏳ Jarayonlar sahifasida ham ko'rinadi (background_jobs registry).
      // (Birga-bir nusxa ONI — bir zumda `variant`+`done` keladi.)
      setLogoJobId(data.jobId || null);
      setLogoProgress(`0/${data.total || logoCount} tayyor...`);
      setLogoPrompt('');
      setLogoImage(null);
      setLogoExact(false);
    } catch (e) {
      toast.error('Logo yaratishda xato: ' + e.message);
      setLogoGenerating(false);
      setLogoProgress('');
    }
  };

  // Fon jarayonini (logo/TZ) to'xtatish — backend bajarilayotgan `claude` child'ni
  // o'ldiradi, tayyor bo'lgan variantlar saqlanib qoladi.
  const cancelBgJob = async (id) => {
    if (!id) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/jobs/${id}/cancel`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Server xatosi');
      toast("⏹ To'xtatish so'raldi...");
    } catch (e) { toast.error('Xato: ' + e.message); }
  };

  const handleDeleteLogo = async (id) => {
    if (!window.confirm('Bu logoni o\'chirishni tasdiqlaysizmi?')) return;
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/logos/logo/${id}`, { method: 'DELETE' });
      setLogos(prev => prev.filter(l => l.id !== id));
      setSelectedLogoIds(prev => prev.filter(x => x !== id));
      toast.success('Logo o\'chirildi');
    } catch (e) { toast.error('Xato'); }
  };

  // ⭐ Asosiy logo — 🎨 Dizayn → «🏷 Logoni joylash» va yangi dizaynlar shu logoni ishlatadi.
  const handleSetPrimaryLogo = async (logo) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/logos/logo/${logo.id}/primary`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Xato'); return; }
      setLogos(prev => prev.map(l => ({ ...l, is_primary: l.id === logo.id ? 1 : 0 })).sort((a, b) => (b.is_primary || 0) - (a.is_primary || 0)));
      toast.success("⭐ Asosiy logo belgilandi — Dizayn → 🏷 Logoni joylash shu logoni qo'yadi");
    } catch (e) { toast.error('Xato'); }
  };

  // 📤 Tayyor logo (dizayner fayli) — AI qayta chizmaydi, aynan o'zi saqlanadi;
  // loyihada ⭐ asosiy logo bo'lmasa, shu asosiy bo'ladi.
  const handleUploadLogoFile = (file) => {
    if (!file || !logoProjectId) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setLogoUploading(true);
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/logos/${logoProjectId}/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: ev.target.result, name: file.name }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { toast.error(d.error || 'Yuklashda xato'); return; }
        await fetchLogos(logoProjectId);
        toast.success(d.is_primary ? '📤 Logo yuklandi va ⭐ asosiy qilindi' : '📤 Logo yuklandi');
      } catch (e) { toast.error('Yuklashda xato'); }
      finally { setLogoUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleDownloadLogo = async (logo) => {
    try {
      // transparent=true → the logo's own background (if any) is in the SVG; rounded corners stay transparent (not white)
      const dataUrl = await svgToPngDataUrl(logo.svg_content, 512, 512, true);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${(logo.name || 'logo').replace(/[^a-zA-Z0-9_-]+/g, '_')}.png`;
      a.click();
    } catch (e) { toast.error('Yuklab olishda xato'); }
  };

  // "Ilovaga biriktirish" — logoni to'g'ridan-to'g'ri ilovaga joriy qilib, uning
  // KEYINGI VERSIYASINI yasash uchun engine super-vazifasi yaratadi. Logo SVG →
  // PNG → /upload (handleAddTask'dagi kabi), so'ng /tasks (project_id, no module
  // → pending_architecture; arxitektor web/mobil dev sub-vazifalarga bo'ladi va
  // engine logo rasmini ularga vision sifatida uzatadi).
  const handleApplyLogo = async (logo) => {
    if (!logo?.svg_content || !logoProjectId || applyingLogoId) return;
    if (!window.confirm(`"${logo.name}" logosini ilovaga joriy qilib, uning shu logo bilan keyingi versiyasini yasashni boshlaymizmi?`)) return;
    setApplyingLogoId(logo.id);
    try {
      // Logo SVG → PNG → upload (CSP-safe blob decode, viewBox o'lchamida)
      const vb = (logo.svg_content || '').match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
      const w = vb ? Math.round(parseFloat(vb[1])) : 512;
      const h = vb ? Math.round(parseFloat(vb[2])) : 512;
      const dataUrl = await svgToPngDataUrl(logo.svg_content, w, h);
      const blob = dataURLtoBlob(dataUrl);
      const file = new File([blob], `logo-${logo.id}.png`, { type: 'image/png' });
      const u = await uploadImageFile(file);
      if (!u) throw new Error('Logo yuklanmadi');

      const instruction = `Ilova brendini yangilash: biriktirilgan logoni ("${logo.name}") ilovaga joriy qil. Logoni quyidagi joylarda ishlat — web va mobil ilovaning sarlavhasi (header)/asosiy ekrandagi logotip, yuklanish (splash) ekrani, favicon hamda mobil app ikonkasi va boshqa brending elementlari. Rang palitrasini logoga uyg'unlashtir. Ilovaning shu logo bilan keyingi to'liq versiyasini yasab chiq.`;

      await authFetch(`${process.env.REACT_APP_API_URL}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: logoProjectId,
          instruction,
          task_type: 'developer',
          requires_build: false,
          image_url: u,
        }),
      });
      toast.success('✅ Vazifa yaratildi — ilova logo bilan yangilanadi. ⏳ Jarayonlar sahifasida kuzating.');
      fetchTasks();
    } catch (e) {
      toast.error('Xato: ' + (e.message || 'biriktirib bo\'lmadi'));
    } finally {
      setApplyingLogoId(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  const abGenerateFromTz = async () => {
    if (!tzText.trim() || !abProjectId || tzGenerating) return;
    setTzGenerating(true);
    setTzProgress('TZ yuborilmoqda...');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/${abProjectId}/generate-from-tz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tz: tzText, module_id: studioModuleId || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.started) {
        toast.error(data.error || 'Xato');
        setTzGenerating(false);
        setTzProgress('');
        return;
      }
      // Jarayon backend'da davom etadi (Claude — bir necha daqiqa). Progress +
      // yakuniy natija `tz_progress` socket eventida keladi; modal o'sha yerda
      // yopiladi (listener yuqorida, socket useEffect ichida).
      setTzProgress('TZ tahlil qilinmoqda (reja)...');
    } catch (e) {
      toast.error('Xatolik yuz berdi');
      setTzGenerating(false);
      setTzProgress('');
    }
  };

  // 🏷 Logoni joylash — loyihaning ⭐ asosiy logosini mavjud dizayn sahifalariga qo'yadi
  // (modul filtri tanlangan bo'lsa — faqat o'sha modul). AI faqat joyini belgilaydi,
  // logoni server qo'yadi; natija `design_logo_progress` socket eventida keladi.
  const abApplyLogo = async () => {
    if (!abProjectId || abLogoJobId) return;
    const scope = abPages.filter(p => p.svg_content && (!studioModuleId || String(p.module_id) === String(studioModuleId)));
    if (!scope.length) { toast.error("Logo joylanadigan sahifa yo'q"); return; }
    if (!window.confirm(`⭐ Asosiy logo ${scope.length} ta dizayn sahifasiga joylansinmi?\n\nAI har sahifada logo joyini topadi (eski belgi o'rniga), logoning o'zini tizim qo'yadi. Har sahifa yangi versiya bo'lib saqlanadi — sahifani bosib 🕐 Versiyalar'dan qaytarish mumkin.`)) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/app-builder/${abProjectId}/apply-logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_id: studioModuleId || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.started) {
        if (d.code === 'NO_PRIMARY_LOGO') {
          if (window.confirm(`${d.error || "Asosiy logo yo'q"}\n\n🏷 Logo tabiga o'tilsinmi?`)) setStudioView('logo');
        } else toast.error(d.error || 'Xato');
        return;
      }
      setAbLogoJobId(d.jobId);
      setAbLogoProgress(`0/${d.total} sahifa...`);
    } catch (e) { toast.error('Xatolik yuz berdi'); }
  };

  // ---------- Claude model darajalari (rol → opus/sonnet/haiku) ----------
  // Hamma AI Claude Max'da — eski OpenRouter model/narx CRUD o'rniga oddiy
  // rol→daraja tanlovi (settings jadvalida saqlanadi, reload kerak emas).
  const [claudeTiers, setClaudeTiers] = useState([]); // [{role, model, modelSource, effort, effortSource, ...}]
  const [tierSaving, setTierSaving] = useState('');   // saqlanayotgan rol nomi
  const [emuIdleSetting, setEmuIdleSetting] = useState(null); // emulator auto-lock (daqiqa; 0 = hech qachon)
  const [emuOpt, setEmuOpt] = useState(null);             // ⚡ emulator optimizatsiyasi (emuTuning, `160`)
  const [emuOptSaving, setEmuOptSaving] = useState(false);
  const [emuIdleSaving, setEmuIdleSaving] = useState(false);
  const [emuAwakeMaxSetting, setEmuAwakeMaxSetting] = useState(null); // ☕ «Uxlatma» muddati (daqiqa; 0 = cheklovsiz)
  const [emuAwakeMaxSaving, setEmuAwakeMaxSaving] = useState(false);
  // ⚙️ Engine cheklovi sozlamalari (Konfiguratsiya kartasi): parallellik + cap'da to'xtatish.
  // Yon menyudagi panel faqat jonli bar'lar + cap raqamlarini ko'rsatadi.
  const [engineCfg, setEngineCfg] = useState({ cc: 1, enabled: false, cap5: '', capW: '', used5: 0, usedW: 0, budget: '' });
  const [engineCfgLoaded, setEngineCfgLoaded] = useState(false);
  const [engineCfgSaving, setEngineCfgSaving] = useState(false);
  const [tierCustom, setTierCustom] = useState({});   // {role: true} — "to'liq ID" rejimi ochiq
  const [catalogModels, setCatalogModels] = useState([]); // /claude/models — CLI bilan ISHLAYDIGAN modellar (status ok)
  const [tierCustomText, setTierCustomText] = useState({}); // {role: 'claude-...'} kiritilayotgan ID
  // Alias QAYSI modelga tushayotgani — tanlov ro'yxatida ham ko'rsatiladi, chunki
  // «oxirgi Opus» degan yozuv chalg'itgan edi (aslida Opus 4.8 ga ulanardi).
  const [aliasMap, setAliasMap] = useState(null);

  // ---------- Selected project modules (for mobile detection in tasks tab) ----------
  const [selectedProjectModules, setSelectedProjectModules] = useState([]);
  // 📱 Loyihada BIR NECHTA mobil modul bo'lishi mumkin (advertising: `mobile` +
  // `company-app`). Ilgari hamma joy `find(m => m.module_type === 'mobile')` qilardi,
  // ya'ni DOIM birinchisi quriladi va operator ikkinchisini umuman qura olmasdi.
  // Endi tanlov bitta joyda va loyiha bo'yicha eslab qolinadi.
  const mobileModules = React.useMemo(
    () => (selectedProjectModules || []).filter(m => m.module_type === 'mobile'), [selectedProjectModules]);
  const [mobileModId, setMobileModId] = useState(null);
  React.useEffect(() => {
    if (!mobileModules.length) { setMobileModId(null); return; }
    let saved = null;
    try { saved = localStorage.getItem(`kh_mobmod_${projectId || 'x'}`); } catch {}
    const ok = mobileModules.find(m => String(m.id) === String(saved));
    setMobileModId(ok ? ok.id : mobileModules[0].id);
  }, [projectId, mobileModules]);
  const activeMobileModule = mobileModules.find(m => String(m.id) === String(mobileModId)) || mobileModules[0] || null;
  const selectMobileModule = (id) => {
    setMobileModId(id);
    try { localStorage.setItem(`kh_mobmod_${projectId || 'x'}`, String(id)); } catch {}
  };
  // NOTE: must be declared AFTER selectedProjectModules (it reads it) — else TDZ crash.
  const selectedStudioModule = selectedProjectModules.find(m => String(m.id) === String(studioModuleId)) || null;

  // 🌐 Brauzer view nishoni. Loyihada BIR NECHTA web modul bo'lishi mumkin
  // (masalan `frontend` = admin panel VA `website` = landing) — ilgari faqat
  // BIRINCHISI ko'rsatilardi va ikkinchisini ochishning iloji yo'q edi.
  // Endi toolbar'da tanlagich bor: browserModuleId (null = avtomatik).
  const browserWebModules = selectedProjectModules.filter(m => isWebModule(m) && m.test_url);
  const browserModule =
    browserWebModules.find(m => String(m.id) === String(browserModuleId)) ||
    ((isWebModule(selectedStudioModule) && selectedStudioModule.test_url)
      ? selectedStudioModule
      : (browserWebModules[0] || null));

  // 🗄️ Database view (Studio) moduli: tanlangan modul backend bo'lsa o'sha, aks holda
  // loyihaning birinchi backend moduli. Backend modul yo'q bo'lsa view ham, tab ham chiqmaydi.
  const dbModule = (selectedStudioModule?.module_type === 'backend')
    ? selectedStudioModule
    : (selectedProjectModules.find(m => m.module_type === 'backend') || null);

  const openBrowserPath = (p) => {
    if (!browserModule?.test_url) return;
    const base = browserModule.test_url.replace(/\/+$/, '');
    const path = String(p !== undefined ? p : browserPath || '/');
    setBrowserFrameUrl(base + (path.startsWith('/') ? path : '/' + path));
    setBrowserBust(0);
    setBrowserFrameKey(k => k + 1);
  };

  // Hozir ochiq sahifa kontekstida vazifa: sahifa URL'i tavsif boshiga qo'shiladi —
  // arxitektor/dev agent aynan shu sahifani nishonga oladi.
  const taskFromBrowserPage = () => {
    const url = browserFrameUrl || browserModule?.test_url || '';
    if (!url) return;
    setNewTask(prev => (prev || '').includes(`[Sahifa: ${url}]`) ? prev : `[Sahifa: ${url}] ${prev || ''}`);   // Agent yo'q (mijoz) — forma
    setAgentDraftExt({ text: `[Sahifa: ${url}] `, at: Date.now() });
    toast("📝 Sahifa manzili qo'shildi — nima qilish kerakligini yozing");
  };

  const fetchSelectedProjectModules = async (id) => {
    if (!id) { setSelectedProjectModules([]); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${id}/modules`);
      const data = await res.json();
      setSelectedProjectModules(Array.isArray(data) ? data : []);
    } catch (e) { setSelectedProjectModules([]); }
  };

  // ---------- Emulator handlers ----------
  const fetchEmulatorDevices = async () => {
    try {
      const [devRes, statusRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/emulator/devices`),
        authFetch(`${process.env.REACT_APP_API_URL}/emulator/status`),
      ]);
      const devData = await devRes.json();
      setEmulatorDevices(Array.isArray(devData) ? devData : []);
      if (devData.length > 0 && !selectedDevice) setSelectedDevice(devData[0]);
      if (statusRes.ok) {
        const s = await statusRes.json();
        setEmulatorStatus(s.adb_ok ? 'running' : s.status || 'unknown');
      }
    } catch (e) { console.error(e); }
  };

  const [isReconnecting, setIsReconnecting] = useState(false);
  const [apkInfo, setApkInfo] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isHotReloading, setIsHotReloading] = useState(false);
  const reconnectDevice = async () => {
    setIsReconnecting(true);
    // Stop interval but keep isStreamingRef=true so stream auto-restarts after reconnect
    socketRef.current?.emit('emulator:stop_stream');
    setEmulatorLogs([]);
    setEmulatorStatus('reconnecting');
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/reconnect`, { method: 'POST' });
      // Result comes via socket emulator:status + emulator:log events
      // Stream auto-restarts when emulator:status 'running' fires
    } catch (e) {
      toast.error('Xatolik yuz berdi');
      setEmulatorStatus('error');
    } finally {
      setIsReconnecting(false);
    }
  };

  // Emulator controls used by the mobile module Overview card.
  const startEmulator = async () => {
    setEmulatorLogs([]);
    setEmulatorStatus('starting');
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/start`, { method: 'POST' });
      // Progress + final status arrive via socket (emulator:log / emulator:status).
    } catch (e) {
      toast.error('Emulatorni ishga tushirishda xato');
      setEmulatorStatus('error');
    }
  };
  const stopEmulator = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stop`, { method: 'POST' });
      setEmulatorStatus('stopped');
    } catch (e) {
      toast.error('Xatolik yuz berdi');
    }
  };
  const openEmulatorForModule = (m) => {
    setShowEmulator(true);
    fetchEmulatorDevices();
    if (m?.workspace_path) fetchApkInfo(m.workspace_path);
  };

  const fetchApkInfo = async (workspacePath) => {
    if (!workspacePath) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/apk-info?workspacePath=${encodeURIComponent(workspacePath)}`);
      const data = await res.json();
      setApkInfo(data);
    } catch (e) { setApkInfo(null); }
  };

  const installApk = async () => {
    if (!apkInfo?.apkPath) return;
    setIsInstalling(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/install`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apkPath: apkInfo.apkPath }),
      });
      const data = await res.json();
      if (data.ok) toast.success(data.message);
      else toast.error(data.error || 'O\'rnatish xato');
    } catch (e) { toast.error('Xatolik'); }
    finally { setIsInstalling(false); }
  };

  // Install the LAST-BUILT APK onto ONE emulator slot WITHOUT rebuilding (fast — for a
  // newly-added 2nd/3rd emulator). Streams progress over server_log; toast is the cue.
  // MUHIM: manba = shu SLOTGA biriktirilgan loyiha (📌), tanlangan loyiha EMAS. Alohida
  // oyna (/emu) o'z proyekt tanlovini bilmasligi mumkin (ochilganda ro'yxatdagi BIRINCHI
  // loyiha tanlanardi) — shu sabab TezHal bilan ishlab turib bosilganda viloyatga ilovasi
  // o'rnatilardi. Biriktiruv bo'lmasa — tanlangan loyihaning mobil moduliga tushadi.
  // ── 📍 Lokatsiya (soxta GPS) — Android Studio "Extended Controls → Location" ekvivalenti.
  // Backend redroid'da Android test-provider'ini (gps/network/fused), AVD slotida esa
  // qo'shimcha `emu geo fix` ni ishlatadi; nuqta DB'da saqlanib restartda tiklanadi.
  const openLocPicker = async (slot) => {
    setLocSlot(slot);
    setRoutePoints([]);
    try {
      const [cur, saved] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/emulator/location?slot=${slot}`),
        authFetch(`${process.env.REACT_APP_API_URL}/emulator/locations`),
      ]);
      const c = await cur.json().catch(() => ({}));
      const s = await saved.json().catch(() => ({}));
      setLocLat(c.lat == null ? '' : String(c.lat));
      setLocLng(c.lng == null ? '' : String(c.lng));
      if (c.route) setEmuRoute(prev => ({ ...prev, [slot]: c.route }));
      setLocPoints(Array.isArray(s.points) ? s.points : []);
    } catch (e) { /* modal baribir ochiladi */ }
  };

  const applyLocation = async (slot, lat, lng) => {
    setLocBusy(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/location`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, lat: Number(lat), lng: Number(lng) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Lokatsiya o\'rnatilmadi'); return; }
      setLocLat(String(d.lat)); setLocLng(String(d.lng));
      setEmuRoute(prev => ({ ...prev, [slot]: { running: false } }));
      toast.success(`📍 Emulator ${slot + 1}: ${d.lat}, ${d.lng}`);
    } catch (e) { toast.error('Xatolik'); }
    finally { setLocBusy(false); }
  };

  const saveLocationPoint = async () => {
    const name = window.prompt('Nuqta nomi (masalan: MegaPlanet):', '');
    if (!name || !name.trim()) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/locations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), lat: Number(locLat), lng: Number(locLng) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      const s = await (await authFetch(`${process.env.REACT_APP_API_URL}/emulator/locations`)).json();
      setLocPoints(Array.isArray(s.points) ? s.points : []);
      toast.success('Nuqta saqlandi');
    } catch (e) { toast.error('Xatolik'); }
  };

  const deleteLocationPoint = async (id) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/locations/${id}`, { method: 'DELETE' });
      setLocPoints(prev => prev.filter(p => p.id !== id));
      setRoutePoints(prev => prev.filter(p => p.id !== id));
    } catch (e) { toast.error('Xatolik'); }
  };

  const startRoute = async (slot) => {
    if (routePoints.length < 2) { toast.error('Kamida 2 ta nuqta tanlang'); return; }
    setLocBusy(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/route`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, speedKmh: routeSpeed, loop: routeLoop, points: routePoints.map(p => ({ lat: p.lat, lng: p.lng })) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Marshrut boshlanmadi'); return; }
      setEmuRoute(prev => ({ ...prev, [slot]: { running: true, index: 0, total: d.total } }));
      // Server nuqtalarni haqiqiy ko'chalarga moslay oldimi — buni AYTIB turamiz:
      // marshrut xizmati javob bermasa eski to'g'ri-chiziq ("uchib ketish") holatiga
      // qaytadi, va operator buni bilmasa "tuzatilmabdi" deb o'ylaydi.
      toast.success(d.snapped
        ? `🛣 Yo'l bo'yicha marshrut — ${d.roadKm} km · ${routeSpeed} km/soat`
        : `🚗 Marshrut boshlandi (to'g'ri chiziq — yo'l xizmati javob bermadi) — ${routeSpeed} km/soat`);
    } catch (e) { toast.error('Xatolik'); }
    finally { setLocBusy(false); }
  };

  const stopRoute = async (slot) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/route/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }),
      });
      setEmuRoute(prev => ({ ...prev, [slot]: { running: false } }));
    } catch (e) { toast.error('Xatolik'); }
  };

  // ✋ Agentdan boshqaruvni olish / ▶ qaytarish. Agent keyingi harakatida to'xtaydi
  // (MCP server har harakatda serverdan `paused` javobini oladi).
  const emuAgentControl = async (slot, action) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/agent-control`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, action }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Boshqaruv o\'zgarmadi'); return; }
      setEmuAgent(a => ({ ...a, [slot]: { ...(a[slot] || {}), paused: action !== 'resume' } }));
      toast(action === 'resume' ? '▶ Boshqaruv agentga qaytarildi' : '✋ Boshqaruv sizda — agent to\'xtadi');
    } catch (e) { toast.error('Xatolik'); }
  };

  // ⏹ Sinovni BUTUNLAY to'xtatish. ✋ pauza faqat boshqaruvni oladi — agent kutib turadi
  // va 35 daqiqalik oyna tugaguncha slot band qoladi. To'xtatish esa `claude` bolasini
  // o'ldiradi va runni yopadi (xulosa bo'lmaydi). Loyiha id'si /emulator/list dagi
  // agent.projectId dan keladi (server ketayotgan runni slot bo'yicha topadi).
  const emuAgentStop = async (slot) => {
    // projectId /emulator/list dagi agent.projectId dan keladi (server ketayotgan runni
    // slot bo'yicha topadi). Bo'lmasa — mobil modul id'si bilan urinamiz (server uni
    // loyihaga aylantiradi); u ham yo'q bo'lsa JIM {} yubormaymiz, sababini aytamiz.
    const pid = emuAgent[slot]?.projectId || null;
    const mid = pid ? null : (selectedProjectModules.find(m => isMobileModule(m)) || {}).id || null;
    if (!pid && !mid) { toast.error("Qaysi sinov ekanini aniqlab bo'lmadi — 📱 Emulator panelidagi ⏹ dan foydalaning"); return; }
    if (!window.confirm("Sinovni butunlay to'xtatasizmi? Agent darhol to'xtaydi va xulosa bo'lmaydi.")) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/agent-qa/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pid ? { projectId: pid } : { moduleId: mid }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || "To'xtatib bo'lmadi"); return; }
      toast.success("⏹ Sinov to'xtatildi");
      setEmuAgent(x => ({ ...x, [slot]: { ...(x[slot] || {}), active: false } }));
    } catch (e) { toast.error('Xatolik'); }
  };

  // 🌐 Brauzer agentidan boshqaruvni olish / qaytarish (emulatordagi bilan bir xil).
  const webAgentControl = async (moduleId, action) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/agent-control`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surface: 'web', moduleId, action }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Boshqaruv o\'zgarmadi'); return; }
      setWebAgent(a => ({ ...a, [moduleId]: { ...(a[moduleId] || {}), paused: action !== 'resume' } }));
      toast(action === 'resume' ? '▶ Boshqaruv agentga qaytarildi' : '✋ Boshqaruv sizda — agent to\'xtadi');
    } catch (e) { toast.error('Xatolik'); }
  };

  // 📲 → TAYYOR (build qilingan) APK'lar ro'yxatini ochadi — qaysi ilovani o'rnatishni
  // foydalanuvchi O'ZI tanlaydi (biriktirilgani 📌 bilan tepada turadi).
  const openApkPicker = async (slot) => {
    setApkPickerSlot(slot);
    setApkChoices([]);
    setApkChoicesLoading(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/apk-list`);
      const d = await res.json().catch(() => ({}));
      setApkChoices(res.ok && Array.isArray(d.apks) ? d.apks : []);
    } catch (e) { setApkChoices([]); }
    finally { setApkChoicesLoading(false); }
  };
  const fmtApkSize = (n) => (n == null ? '' : `${(n / 1048576).toFixed(0)} MB`);
  // uz-UZ locale oyni "M07" deb chiqaradi — shuning uchun qo'lda: "27.07 00:10"
  const fmtApkTime = (ms) => {
    try {
      const d = new Date(ms); const p = (n) => String(n).padStart(2, '0');
      return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
    } catch { return ''; }
  };

  // `apk` berilsa — AYNAN o'sha ilova o'rnatiladi (ro'yxatdan tanlangan).
  const installApkToSlot = async (slot, apk = null) => {
    const bound = emuBindings[slot];
    const selMod = activeMobileModule;
    const moduleId = apk?.moduleId || bound?.moduleId || selMod?.id;
    const label = apk?.projectName
      || (bound?.moduleId ? (bound.projectName || 'loyiha')
        : (projects.find(p => String(p.id) === String(projectId))?.name || 'loyiha'));
    if (!moduleId) {
      toast.error(bound
        ? `«${bound.projectName}» loyihasida mobil modul yo'q`
        : 'Mobil modul topilmadi — loyihani emulatorga biriktiring (📌)');
      return;
    }
    setApkPickerSlot(null);
    toast(`📲 Emulator ${slot + 1} ga «${label}» o'rnatilmoqda...`);
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}/mobile/install`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot }),
      });
    } catch (e) { toast.error('Xatolik'); }
  };

  // Har qanday faylni (rasm/video/audio/hujjat) brauzerdan tanlab emulatorga
  // yuklash. Redroid'da haqiqiy kamera yo'q, shuning uchun yuklash oqimlarini
  // (registratsiya avatari, "galereyadan tanlash", video/fayl yuklash) shu
  // orqali sinaladi — fayl to'g'ri papkaga (Pictures/Movies/Music/Download)
  // adb-push qilinib MediaStore'ga indekslanadi. Maks 200MB.
  const handleUploadEmuFile = async (slot, file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Fayl 200MB dan katta — kichikroq fayl tanlang'); return; }
    toast(`📎 Emulator ${slot + 1} ga yuklanmoqda... (${(file.size / 1048576).toFixed(1)}MB)`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slot', String(slot));
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/upload-file`, { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) toast.success(`📎 Emulator ${slot + 1}: fayl ${d.where || 'qurilmaga'} qo'shildi`);
      else toast.error(d.error || 'Fayl yuklashda xato');
    } catch (e) { toast.error('Fayl yuklashda xato'); }
  };

  // 🛡 Device Owner (kiosk ilovalarini sinash — Idora haydovchi planshet ilovasi): emulatorda ilovani qurilma egasi
  // qilish / olib tashlash. Qaytarish qiyin (ilova testOnly bo'lmasa emulator ma'lumotini tozalash kerak) — har safar tasdiq.
  const emuDeviceOwner = async (slot) => {
    const api = process.env.REACT_APP_API_URL;
    try {
      const st = await authFetch(`${api}/emulator/device-owner?slot=${slot}`).then(r => r.json()).catch(() => ({}));
      if (!st.ok) { toast.error(st.error || "Emulator holatini o'qib bo'lmadi (ishlab turibdimi?)"); return; }
      if ((st.owners || []).length) {
        if (!window.confirm(`🛡 Emulator ${slot + 1}: Device Owner — ${st.owners.join(', ')}.\n\nOlib tashlansinmi? Ilova testOnly bo'lmasa olib tashlanmaydi — unda ilovaning o'zidagi kiosk'dan chiqish yoki emulator ma'lumotini tozalash (qurilma turini almashtirish) kerak.`)) return;
        const res = await authFetch(`${api}/emulator/device-owner?slot=${slot}`, { method: 'DELETE' });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.ok) toast.success(`🛡 Emulator ${slot + 1}: Device Owner olib tashlandi`);
        else toast.error(d.error || 'Olib tashlanmadi', { duration: 9000 });
        return;
      }
      const pkg = window.prompt(`🛡 Emulator ${slot + 1}: qaysi ilova qurilma egasi (Device Owner) bo'lsin? Paket nomi — ilova AVVAL o'rnatilgan bo'lsin, qurilmada Google akkaunt bo'lmasin.\n\n⚠️ Qaytarish qiyin: odatda emulator ma'lumotini tozalash kerak bo'ladi.`, apkInfo?.packageName || '');
      if (!pkg || !pkg.trim()) return;
      toast(`🛡 Emulator ${slot + 1}: ${pkg.trim()} Device Owner qilinmoqda...`);
      const res = await authFetch(`${api}/emulator/device-owner`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, packageName: pkg.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) toast.success(`🛡 ${d.already ? 'Allaqachon Device Owner' : 'Device Owner tayyor'}: ${d.component}`);
      else toast.error(d.error || "Device Owner o'rnatilmadi", { duration: 9000 });
    } catch (e) { toast.error('Xatolik'); }
  };

  const launchApp = async () => {
    if (!apkInfo?.packageName) return;
    setIsLaunching(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/launch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageName: apkInfo.packageName }),
      });
      const data = await res.json();
      if (!data.ok) toast.error('Ishga tushirishda xato');
    } catch (e) { toast.error('Xatolik'); }
    finally { setIsLaunching(false); }
  };

  const hotReload = async (workspacePath) => {
    if (!workspacePath) return;
    setIsHotReloading(true);
    toast('Hot reload...');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/hot-reload`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspacePath, framework: apkInfo?.framework || 'rn' }),
      });
      const data = await res.json();
      if (data.ok) toast.success(data.message);
      else toast.error(data.message || 'Hot reload xato');
    } catch (e) { toast.error('Xatolik'); }
    finally { setIsHotReloading(false); }
  };

  const startStream = async (deviceId) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/screen-size/${encodeURIComponent(deviceId)}`);
      const size = await res.json();
      setEmulatorScreenSize(size);
    } catch (e) {}
    socketRef.current?.emit('emulator:start_stream');
    isStreamingRef.current = true;
    setIsStreaming(true);
    setLastFrameTime(Date.now());
    setStreamFrozen(false);
  };

  const stopStream = () => {
    socketRef.current?.emit('emulator:stop_stream');
    isStreamingRef.current = false;
    setIsStreaming(false);
    setEmulatorFrame('');
    setLastFrameTime(null);
    setStreamFrozen(false);
  };

  const scaleCoords = (clientX, clientY) => {
    if (!imgRef.current) return { x: 0, y: 0 };
    const rect = imgRef.current.getBoundingClientRect();
    const scaleX = emulatorScreenSize.width / rect.width;
    const scaleY = emulatorScreenSize.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Send each gesture exactly ONCE over the socket. (Previously it also POSTed
  // via REST → every tap/key fired twice, and a drag fired both a swipe AND a
  // trailing tap — the "double press / stray taps" instability.)
  const sendTap = (x, y) => {
    socketRef.current?.emit('emulator:tap', { x, y });
  };

  const handleEmulatorMouseDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };

  // Tap vs swipe decided ONLY here (no onClick handler → no stray tap after a swipe).
  const handleEmulatorMouseUp = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist >= 8) {
      const from = scaleCoords(dragRef.current.x, dragRef.current.y);
      const to = scaleCoords(e.clientX, e.clientY);
      socketRef.current?.emit('emulator:swipe', {
        x1: from.x, y1: from.y, x2: to.x, y2: to.y,
        duration: Math.min(Date.now() - dragRef.current.time + 100, 1000),
      });
    } else {
      const to = scaleCoords(e.clientX, e.clientY);
      sendTap(to.x, to.y);
    }
    dragRef.current = null;
  };

  const sendKey = (keycode) => {
    socketRef.current?.emit('emulator:keyevent', { keycode });
  };

  // Open the emulator's NATIVE app drawer (HOME → swipe-up gesture, done server-side).
  const openAppDrawer = () => { socketRef.current?.emit('emulator:app_drawer'); };

  // ── Multi-emulator grid (up to 3 per user) ──────────────────────────────────
  const EMU_DEV_W = 720, EMU_DEV_H = 1280; // fallback device size; emuMediaInfo reads the real frame size live

  const fetchEmuList = async (autostream = false) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/list`);
      if (!res.ok) return;
      const data = await res.json();
      setEmuMaxSlots(data.maxSlots || 3);
      const list = data.emulators || [];
      // MERGE, don't clobber: a cell added via ➕ (addEmulator) is browser-only — the
      // backend has NO user_emulators row for it until the user picks a device type
      // (setDevice) or presses ▶ (ensureRunning), both of which INSERT the row. So keep
      // any locally-added, not-yet-created slot the backend doesn't know about yet;
      // otherwise this 9s poll erases the new emulator cell within seconds of adding it.
      setEmuList(prev => {
        const backendSlots = new Set(list.map(e => e.slot));
        const pending = (prev || []).filter(e => !backendSlots.has(e.slot) && e.status === 'not_created');
        return pending.length ? [...list, ...pending].sort((a, b) => a.slot - b.slot) : list;
      });
      // Server yagona haqiqat: lokal 'running' qolib, server o'chgan desa — yangilaymiz (socket hodisasi yo'qolsa
      // to'liq ekran/katak abadiy «ishlayapti» ko'rsatardi). 'starting'/'reconnecting' ga tegilmaydi (o'tish holati).
      setEmuStatuses(prev => { const n = { ...prev }; list.forEach(e => {
        if (n[e.slot] === undefined || (n[e.slot] === 'running' && ['stopped', 'error', 'not_created'].includes(e.status))) n[e.slot] = e.status;
      }); return n; });
      // 🤖 Agent holatini server BILAN solishtiramiz — socket 'end' hodisasi yo'qolsa ham
      // belgi osilib qolmaydi (server yagona haqiqat manbai).
      setEmuAgent(prev => {
        const n = { ...prev };
        list.forEach(e => {
          if (e.agent && e.agent.active) n[e.slot] = { ...(n[e.slot] || {}), active: true, paused: !!e.agent.paused, taskId: e.agent.taskId, projectId: e.agent.projectId || (n[e.slot] || {}).projectId || null,
            label: e.agent.label || (n[e.slot] || {}).label || null, startedAt: e.agent.startedAt || (n[e.slot] || {}).startedAt || null };
          else if (n[e.slot]?.active) n[e.slot] = { active: false };
        });
        return n;
      });
      const meId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      setEmuAdminLocks(() => { const n = {}; list.forEach(e => { if (e.adminLock && String(e.adminLock.adminId) !== String(meId)) n[e.slot] = e.adminLock; }); return n; });
      if (autostream) list.forEach(e => { if (e.status === 'running') (emuAutoPreviewRef.current ? emuAutoPreviewRef.current(e.slot) : emuStartStream(e.slot)); });
    } catch (e) {}
    // Telefon turlari ro'yxatini bir marta yuklab olamiz (dropdown uchun).
    if (emuPresets.length === 0) {
      try {
        const pr = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/presets`);
        if (pr.ok) { const pd = await pr.json(); if (Array.isArray(pd.presets)) setEmuPresets(pd.presets); }
      } catch (e) {}
    }
  };

  // BARCHA emulator biriktiruvlari (slot → loyiha + mobil modul). Alohida oyna (/emu) o'z
  // proyekt tanlovini bilmasligi mumkin, shuning uchun 📲 o'rnatish manbasi shu ro'yxat.
  const fetchEmuBindings = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/bindings`);
      if (!res.ok) return;
      const d = await res.json();
      if (!Array.isArray(d.bindings)) return;
      const bySlot = {};
      d.bindings.forEach(b => { bySlot[Number(b.slot)] = b; });
      setEmuBindings(bySlot);
    } catch (e) {}
  };

  // Tanlangan loyihaning emulator biriktiruvini yuklash (per-user, per-project).
  const fetchProjectEmuBinding = async (pid) => {
    if (!pid) { setProjectEmuSlot(null); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${pid}/emulator-binding`);
      if (!res.ok) { setProjectEmuSlot(null); return; }
      const d = await res.json();
      setProjectEmuSlot(d.slot == null ? null : Number(d.slot));
    } catch (e) { setProjectEmuSlot(null); }
  };

  // Loyihani biror emulatorga biriktirish (yoki bekor qilish, slot=null).
  const setProjectEmuBinding = async (slot) => {
    if (!projectId) { toast.error('Avval loyiha tanlang'); return; }
    const prev = projectEmuSlot;
    setProjectEmuSlot(slot); // optimistik
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/emulator-binding`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Saqlanmadi');
      toast.success(slot == null ? 'Biriktiruv bekor qilindi (barcha emulatorlarga)' : `Loyiha ${slot + 1}-emulatorga biriktirildi`);
      fetchEmuBindings(); // kataklardagi loyiha nomi + 📲 nishoni darhol yangilansin
    } catch (e) { setProjectEmuSlot(prev); toast.error('Xato: ' + e.message); }
  };

  const fetchEmuTestSlots = async (pid) => {
    if (!pid) { setEmuTestSlots(null); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/supervisor/slots?projectId=${pid}`);
      setEmuTestSlots(res.ok ? await res.json() : null);
    } catch (e) { setEmuTestSlots(null); }
  };
  const toggleEmuTestSlot = async (slot) => {
    if (!projectId || !emuTestSlots) return;
    const cur = emuTestSlots.slots || [];
    const next = cur.includes(slot) ? cur.filter(x => x !== slot) : [...cur, slot].sort((a, b) => a - b);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/supervisor/slots`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: Number(projectId), set: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Saqlanmadi');
      toast.success(next.length ? `🧪 Sinov emulatorlari: ${next.map(x => `E${x + 1}`).join(', ')}` : '🧪 Sinov emulatorlari cheklanmagan — istalganini ishlatadi');
      fetchEmuTestSlots(projectId);
    } catch (e) { toast.error('Xato: ' + e.message); }
  };
  React.useEffect(() => { fetchEmuTestSlots(projectId); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 📌 Ilova (mobil modul) ↔ emulator (2026-10-28, operator: «har bir app uchun alohida emulator biriktirib bo'lmayapti»).
  // Ilova biriktiruvi loyihanikidan USTUN (backend services/emuBinding.js); null = loyiha biriktiruvi amal qiladi.
  const fetchModuleEmuBinding = async (mid) => {
    if (!mid) { setModuleEmuSlot(null); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mid}/emulator-binding`);
      if (!res.ok) { setModuleEmuSlot(null); return; }
      const d = await res.json();
      setModuleEmuSlot(d.slot == null ? null : Number(d.slot));
    } catch (e) { setModuleEmuSlot(null); }
  };
  const setModuleEmuBinding = async (slot) => {
    const mod = activeMobileModule;
    if (!mod) return;
    const prev = moduleEmuSlot;
    setModuleEmuSlot(slot);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/emulator-binding`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Saqlanmadi');
      toast.success(slot == null ? `«${mod.name}» endi loyiha biriktiruviga bo'ysunadi` : `«${mod.name}» ${slot + 1}-emulatorga biriktirildi`);
      fetchEmuBindings();
    } catch (e) { setModuleEmuSlot(prev); toast.error('Xato: ' + e.message); }
  };
  const activeMobModId = activeMobileModule ? activeMobileModule.id : null;
  React.useEffect(() => { fetchModuleEmuBinding(activeMobModId); }, [activeMobModId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔄 Ekranni aylantirish (portret ↔ landshaft) — kadrlar o'zi aylangan keladi, katak nisbati kadrga moslashadi.
  const emuRotate = (slot) => {
    if (emuLockedNotice(slot)) return;
    const sock = socketRef.current;
    if (!sock) return;
    sock.emit('emulator:rotate', { slot }, (r) => {
      if (r && r.ok) toast.success(r.rotation === 1 ? `E${slot + 1}: landshaft (yotiq)` : `E${slot + 1}: portret (tik)`, { id: `emu-rot-${slot}` });
      else toast.error(`Aylantirib bo'lmadi: ${(r && r.error) || 'emulator javob bermadi'}`, { id: `emu-rot-${slot}` });
    });
  };

  // Slotning telefon turini o'zgartirish — emulator qayta yaratiladi (keyingi ▶ start'da).
  const setEmuDevice = async (slot, deviceKey) => {
    const cur = (emuList.find(e => e.slot === slot) || {}).device_key || 'standart';
    if (deviceKey === cur) return;
    const preset = emuPresets.find(p => p.key === deviceKey);
    const curPreset = emuPresets.find(p => p.key === cur);
    const versionChange = preset && curPreset && curPreset.android !== preset.android;
    const msg = `Emulator ${slot + 1} turini "${preset ? preset.label : deviceKey}" ga o'zgartirasizmi? Emulator qayta yaratiladi`
      + (versionChange ? ", Android versiyasi o'zgargani uchun o'rnatilgan ilovalar o'chadi." : '.');
    if (!window.confirm(msg)) return;
    setEmuBusy(b => ({ ...b, [slot]: true }));
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/device`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, deviceKey }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'xato');
      if (emuStreamingRef.current[slot]) emuStopStream(slot);
      setEmuStatuses(s => ({ ...s, [slot]: 'not_created' }));
      toast.success(`Tur: ${preset ? preset.label : deviceKey}` + (d.wiped ? ' (ilovalar tozalandi)' : ''));
      await fetchEmuList();
    } catch (e) { toast.error('Xato: ' + (e.message || '')); }
    finally { setEmuBusy(b => ({ ...b, [slot]: false })); }
  };

  const emuStartStream = (slot) => {
    socketRef.current?.emit('emulator:start_stream', { slot });
    setEmuStreaming(s => ({ ...s, [slot]: true }));
  };
  const emuStopStream = (slot) => {
    socketRef.current?.emit('emulator:stop_stream', { slot });
    setEmuStreaming(s => ({ ...s, [slot]: false }));
    setEmuFrames(f => { const n = { ...f }; delete n[slot]; return n; });
  };
  // AUTO-LOCK'dan keyin davom ettirish (overlay tugmasi): flag tozalanadi + oqim qayta boshlanadi.
  const emuResumeStream = (slot) => {
    setEmuIdleStopped(s => ({ ...s, [slot]: false }));
    if (emuAutoPreviewRef.current) emuAutoPreviewRef.current(slot); else emuStartStream(slot);
  };
  // Muddat sozlamasi (/emu oynasi uchun; Konfiguratsiya kartasi o'zi alohida o'qiydi).
  useEffect(() => {
    if (!emulatorOnly) return;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stream-settings`);
        const d = await r.json().catch(() => ({}));
        if (r.ok && d && d.awakeMaxMinutes !== undefined) setEmuAwakeMax(Number(d.awakeMaxMinutes) || 0);
      } catch (e) {}
    })();
  }, [emulatorOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleEmuAwake = () => {
    const nv = !emuAwake;
    try { if (nv) localStorage.setItem('kh_emu_awake_at', String(Date.now())); else localStorage.removeItem('kh_emu_awake_at'); } catch (e) {}
    setEmuAwake(nv);
  };
  useEffect(() => {
    try { localStorage.setItem('kh_emu_awake', emuAwake ? '1' : '0'); } catch (e) {}
    if (!emuAwake || !emulatorOnly) { setEmuAwakeLeft(null); return undefined; }
    // ⏱ Muddat: boshlangan vaqtdan emuAwakeMax daqiqa o'tsa o'zi o'chadi.
    let at = 0;
    try { at = Number(localStorage.getItem('kh_emu_awake_at')) || 0; if (!at) { at = Date.now(); localStorage.setItem('kh_emu_awake_at', String(at)); } } catch (e) { at = Date.now(); }
    const deadline = emuAwakeMax > 0 ? at + emuAwakeMax * 60_000 : 0;
    const expire = () => {
      try { localStorage.removeItem('kh_emu_awake_at'); } catch (e) {}
      setEmuAwake(false);
      toast(`💤 «Uyg'oq» rejim ${emuAwakeMax % 60 === 0 ? `${emuAwakeMax / 60} soat` : `${emuAwakeMax} daqiqa`}dan keyin o'zi o'chdi — kerak bo'lsa yana yoqing`, { id: 'kh-awake-exp', duration: 8000 });
    };
    if (deadline && Date.now() >= deadline) { expire(); return undefined; }
    const tickLeft = () => setEmuAwakeLeft(deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 60_000)) : null);
    tickLeft();
    const tLeft = setInterval(tickLeft, 30_000);
    const tEnd = deadline ? setTimeout(expire, deadline - Date.now()) : null;
    let lock = null, alive = true;
    const wake = async () => {
      try {
        if (!alive || document.visibilityState !== 'visible' || !navigator.wakeLock || (lock && !lock.released)) return;
        lock = await navigator.wakeLock.request('screen');
        if (!alive) lock.release();
      } catch (e) { /* brauzer ruxsat bermadi (quvvat tejash) — oqim baribir to'xtamaydi */ }
    };
    const ping = () => {
      const sock = socketRef.current;
      const st = emuStreamingRef.current || {};
      if (sock) Object.keys(st).forEach(sl => { if (st[sl]) sock.emit('emulator:keepalive', { slot: +sl }); });
      // Yoqishdan OLDIN to'xtab ulgurgan oqim (yoki socket uzilib qolgan payt) — o'zi davom ettiriladi.
      const idle = emuIdleStoppedRef.current || {};
      Object.keys(idle).forEach(sl => { if (idle[sl]) emuResumeStream(+sl); });
    };
    wake(); ping();
    const t = setInterval(ping, 30_000);
    const onVis = () => { if (document.visibilityState === 'visible') wake(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(t); clearInterval(tLeft); if (tEnd) clearTimeout(tEnd); document.removeEventListener('visibilitychange', onVis); try { if (lock) lock.release(); } catch (e) {} };
  }, [emuAwake, emulatorOnly, emuAwakeMax]); // eslint-disable-line react-hooks/exhaustive-deps
  const awakeMaxLabel = emuAwakeMax > 0 ? (emuAwakeMax % 60 === 0 ? `${emuAwakeMax / 60} soat` : `${emuAwakeMax} daqiqa`) : '';
  const awakeLeftLabel = emuAwakeLeft == null ? '' : emuAwakeLeft >= 60 ? `${Math.floor(emuAwakeLeft / 60)}:${String(emuAwakeLeft % 60).padStart(2, '0')}` : `${emuAwakeLeft} daq`;
  const emuAwakeBtn = (rail) => (
    <button key="kh-awake" onClick={toggleEmuAwake}
      title={emuAwake
        ? `☕ Uyg'oq: emulator oqimi harakatsiz turganda ham to'xtamaydi, kompyuter ekrani o'chmaydi, brauzerlar yopilmaydi (shu oyna ochiq turganda).${awakeLeftLabel ? ` ${awakeLeftLabel} qoldi — keyin o'zi o'chadi.` : ''} Bosib o'chiring.`
        : `Sinovni kuzatayotganda «▶ Davom etish» bilan to'xtab qolmasin: bosing — emulator oqimi to'xtamaydi, kompyuter ekrani o'chmaydi, brauzerlar yopilmaydi (shu oyna ochiq turganda).${awakeMaxLabel ? ` ${awakeMaxLabel}dan keyin o'zi o'chadi.` : ''}`}
      className={rail
        ? `w-[76px] py-2 rounded-lg font-semibold text-[11px] flex flex-col items-center gap-0.5 transition shrink-0 ${emuAwake ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`
        : `px-3 py-1.5 text-sm rounded-lg font-medium transition ${emuAwake ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
      {rail
        ? <><span className="text-base leading-none">{emuAwake ? '☕' : '💤'}</span>{emuAwake ? "Uyg'oq" : 'Uxlatma'}{emuAwake && awakeLeftLabel && <span className="text-[9px] opacity-80 font-normal">{awakeLeftLabel}</span>}</>
        : (emuAwake ? `☕ Uyg'oq${awakeLeftLabel ? ` · ${awakeLeftLabel}` : ''}` : '💤 Uxlatma')}
    </button>
  );

  // Auto-ko'rsatishning yagona kirish nuqtasi — oddiy PNG screencap oqimi.
  // (⚡HD/H.264 rejimi olib tashlandi — operator ishlatmasdi.)
  const emuAutoPreview = (slot) => {
    slot = +slot || 0;
    if (emuStreamingRef.current[slot]) return;
    emuStartStream(slot);
  };
  useEffect(() => { emuAutoPreviewRef.current = emuAutoPreview; });

  // VISIBILITY-PAUZA: tab/oyna yashiringanda (fonga o'tganda) emulator oqimini TO'XTATADI,
  // ko'ringanda qayta tiklaydi. Fon tabning WebSocket'i tirik qoladi (socket.io ping),
  // shuning uchun busiz uning screencap/screenrecord loop'lari emulator serverini bosaverardi
  // — aynan shu bir necha fon oynasi infra CPU'ni 100% qilgan edi. emuStreaming HOLATI
  // saqlanadi (state o'zgarmaydi) → ko'ringanda aynan o'sha slotlar tiklanadi.
  useEffect(() => {
    const onVis = () => {
      const sock = socketRef.current;
      if (!sock) return;
      if (document.visibilityState === 'hidden') {
        sock.emit('emulator:stop_stream');   // slot yo'q → shu socketning BARCHA PNG oqimlari
      } else {
        // Ko'rindi → PNG oqimlarni tikla
        const streaming = emuStreamingRef.current || {};
        Object.keys(streaming).forEach(slot => { if (streaming[slot]) sock.emit('emulator:start_stream', { slot: +slot }); });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emuStart = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true }));
    setEmuStatuses(s => ({ ...s, [slot]: 'starting' }));
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) });
      emuStartStream(slot);
      setTimeout(() => fetchEmuList(), 1500);
    } catch (e) { toast.error('Xato: ' + (e.message || '')); setEmuBusy(b => ({ ...b, [slot]: false })); }
    // busy cleared when emulator:status (running/error) arrives
  };
  const emuStop = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true }));
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) });
      emuStopStream(slot);
      setEmuStatuses(s => ({ ...s, [slot]: 'stopped' }));
    } catch (e) { toast.error('Xato'); }
    finally { setEmuBusy(b => ({ ...b, [slot]: false })); }
  };
  const emuReconnect = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true }));
    setEmuStatuses(s => ({ ...s, [slot]: 'reconnecting' }));
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/emulator/reconnect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) });
    } catch (e) { toast.error('Xato'); setEmuBusy(b => ({ ...b, [slot]: false })); }
    // status via socket clears busy
  };
  const addEmulator = () => {
    const used = new Set((emuList || []).map(e => e.slot));
    let next = -1;
    for (let s = 0; s < emuMaxSlots; s++) { if (!used.has(s)) { next = s; break; } }
    if (next < 0) { toast(`Maksimal ${emuMaxSlots} ta emulator`); return; }
    // To'xtagan katak qo'shamiz — foydalanuvchi telefon turini tanlab, ▶ bilan boshlaydi
    // (har slotni har xil qurilma qilish uchun; default = Standart, shunchaki ▶ bossa ham bo'ladi).
    setEmuList(prev => [...prev, { slot: next, status: 'not_created' }]);
    setEmuStatuses(s => ({ ...s, [next]: 'not_created' }));
    toast('Telefon turini tanlab, ▶ Ishga tushiring');
  };

  // Per-cell tap/swipe — scale from the displayed frame to ACTUAL device pixels.
  // CRITICAL: use the live frame's own dimensions (PNG naturalWidth / video videoWidth)
  // as the device coordinate space — NOT a hardcoded 1080×1920. Redroid ignores the
  // REDROID_REAL_RESOLUTION env, so the real screen is 720×1280; hardcoding 1080×1920
  // made `input tap` land ~1.5× off (every tap missed its target → "clicks don't work").
  // Using the <img>/<video> element's own rect also drops the 4px container border offset.
  const emuMediaInfo = (el) => {
    const m = el.querySelector('video, img');
    const box = (m || el).getBoundingClientRect();
    const dw = (m && (m.videoWidth || m.naturalWidth)) || EMU_DEV_W;
    const dh = (m && (m.videoHeight || m.naturalHeight)) || EMU_DEV_H;
    // The media renders with object-contain → the painted content is LETTERBOXED inside the
    // element box when the device aspect ≠ the frame aspect (e.g. a 1080×2400 phone in a 9:16
    // frame → black bars left/right). Map taps within the CONTENT rect, not the element box,
    // else taps land off by the bar width ("bosgan joyim ishlamaydi, qora chiziqqa bossam ishlaydi").
    let r = box;
    if (dw > 0 && dh > 0 && box.width > 0 && box.height > 0) {
      const scale = Math.min(box.width / dw, box.height / dh);
      const cw = dw * scale, ch = dh * scale;
      r = { left: box.left + (box.width - cw) / 2, top: box.top + (box.height - ch) / 2, width: cw, height: ch };
    }
    return { r, dw, dh };
  };
  // Live device aspect per slot ("w / h") → frame moslashadi (qora chiziq yo'q). Frame yuklanganda o'rnatiladi.
  useEffect(() => { emuAspectRef.current = emuAspect; }, [emuAspect]);
  const onEmuMedia = (slot, el) => {
    const w = (el && (el.naturalWidth || el.videoWidth)) || 0;
    const h = (el && (el.naturalHeight || el.videoHeight)) || 0;
    if (w && h) setEmuAspect(a => (a[slot] === `${w} / ${h}` ? a : { ...a, [slot]: `${w} / ${h}` }));
  };
  const emuScale = (el, clientX, clientY) => {
    const { r, dw, dh } = emuMediaInfo(el);
    return { x: (clientX - r.left) * (dw / (r.width || 1)), y: (clientY - r.top) * (dh / (r.height || 1)) };
  };
  // Pointer-based gestures (mouse + touch + pen). setPointerCapture so a swipe that
  // leaves the small cell still completes (a mouseup OUTSIDE the element used to be lost
  // → swipes silently dropped). Long-press fires on a timer; an instant optimistic ripple
  // confirms the tap so the laggy (~5fps, ~600ms) stream doesn't feel unresponsive.
  const EMU_LONGPRESS_MS = 480;
  const emuDevDist = (el, dx, dy) => { const { r, dw } = emuMediaInfo(el); return Math.sqrt(dx * dx + dy * dy) * (dw / (r.width || 1)); };
  // 🔒 Administrator boshqaryapti — bosish yuborilmaydi (server ham rad etadi), sababi aytiladi.
  const emuLockedNotice = (slot) => {
    const l = emuAdminLocksRef.current[slot];
    if (!l) return false;
    toast(`🔒 Administrator (${l.by || 'admin'}) boshqaryapti — hozircha faqat ko'rasiz`, { id: 'emu-admin-lock' });
    return true;
  };
  const emuPointerDown = (slot, e) => {
    if (emuLockedNotice(slot)) return;
    const el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch {}
    const r = el.getBoundingClientRect();
    setEmuTapFx(f => ({ ...f, [slot]: { x: e.clientX - r.left, y: e.clientY - r.top, id: Date.now() } }));
    const lp = setTimeout(() => {
      const d = emuDragRef.current[slot]; if (!d || d.moved || d.fired) return;
      d.fired = 'long';
      const to = emuScale(el, e.clientX, e.clientY);
      socketRef.current?.emit('emulator:longpress', { slot, x: to.x, y: to.y });
    }, EMU_LONGPRESS_MS);
    emuDragRef.current[slot] = { x: e.clientX, y: e.clientY, time: Date.now(), moved: false, fired: false, lp };
  };
  const emuPointerMove = (slot, e) => {
    const d = emuDragRef.current[slot]; if (!d || d.fired) return;
    if (emuDevDist(e.currentTarget, e.clientX - d.x, e.clientY - d.y) > 28) d.moved = true;
  };
  const emuPointerUp = (slot, e) => {
    const d = emuDragRef.current[slot]; if (!d) return;
    clearTimeout(d.lp);
    emuDragRef.current[slot] = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (d.fired) return; // long-press already sent
    const el = e.currentTarget;
    if (emuDevDist(el, e.clientX - d.x, e.clientY - d.y) >= 28) {
      const from = emuScale(el, d.x, d.y), to = emuScale(el, e.clientX, e.clientY);
      socketRef.current?.emit('emulator:swipe', { slot, x1: from.x, y1: from.y, x2: to.x, y2: to.y, duration: Date.now() - d.time });
    } else {
      const to = emuScale(el, e.clientX, e.clientY);
      socketRef.current?.emit('emulator:tap', { slot, x: to.x, y: to.y });
    }
  };
  const emuKey = (slot, keycode) => { if (emuLockedNotice(slot)) return; socketRef.current?.emit('emulator:keyevent', { slot, keycode }); };
  const emuAppDrawer = (slot) => { if (emuLockedNotice(slot)) return; socketRef.current?.emit('emulator:app_drawer', { slot }); };
  // Fullscreen (bir nechta) boshqaruvi
  const emuAddFullscreen = (slot) => { emuAutoPreview(slot); setEmuFullscreen(fs => fs.includes(slot) ? fs : [...fs, slot]); };
  const emuRemoveFullscreen = (slot) => setEmuFullscreen(fs => fs.filter(s => s !== slot));
  const emuCloseFullscreen = () => {
    // Bitta-emulatorli popout oynasida ✕ = oynani yopish; aks holda faqat fullscreen'dan chiqish.
    if (emulatorOnly && popoutSlot != null) { try { window.close(); } catch {} }
    setEmuFullscreen([]);
    setWdFullscreen([]);
  };
  // 🌐 Brauzer qurilmalar: ro'yxat (faqat emulator kataklari ko'rinadigan joyda — /emu oynasi), to'liq ekran, ✋.
  const fetchWebDevs = async () => {
    if (!projectId) return;
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/web-devices?projectId=${projectId}`);
      const d = r.ok ? await r.json() : null;
      setWebDevs(d && Array.isArray(d.devices) ? d : { devices: [], links: [], viewports: {}, maxSlots: 3 });
      if (d && Array.isArray(d.devices)) {
        setWdAgent(prev => { const n = { ...prev }; d.devices.forEach(x => { if (x.agent) n[x.key] = { ...x.agent }; else if (n[x.key] && n[x.key].active) n[x.key] = { active: false }; }); return n; });
        setWdFullscreen(fs => fs.filter(id => d.devices.some(x => x.id === id)));
      }
    } catch (e) { setWebDevs(w => w || { devices: [], links: [], viewports: {}, maxSlots: 3 }); }
  };
  const wdToggleFullscreen = (id) => setWdFullscreen(fs => fs.includes(id) ? fs.filter(x => x !== id) : [...fs, id]);
  // 🤖 «Agent» joylashuvi: agent ishlayotgan qurilma to'liq ekranda bo'lmasa — o'zi qo'shiladi (operator, 2026-10-31: «ekranga
  // sig'sin deb bir nechtasini tanlagan bo'laman, agent ishlayotganini qo'shish esimdan chiqadi»). Faqat tanlangan loyihaniki;
  // operator tanlaganlari olib tashlanmaydi. Yangi (agent yaratgan) brauzer ro'yxatda hali yo'q bo'lsa — ro'yxat yangilanadi.
  useEffect(() => {
    if (emuFsLayout.mode !== 'agent' || !emuAgentFocus || !(emuFullscreen.length || wdFullscreen.length)) return;
    if (emuAgentFocus.startsWith('wd:')) {
      const key = emuAgentFocus.slice(3);
      const m = /^wd-u\d+-p(\d+)-s\d+$/.exec(key);
      if (m && projectId && String(m[1]) !== String(projectId)) return;
      const d = ((webDevs && webDevs.devices) || []).find(x => x.key === key);
      if (!d) { fetchWebDevs(); return; }
      if (!wdFullscreen.includes(d.id)) wdToggleFullscreen(d.id);
      return;
    }
    const slot = Number(emuAgentFocus.slice(1));
    const agP = emuAgent[slot] && emuAgent[slot].projectId;
    const bP = emuBindings[slot] && emuBindings[slot].projectId;
    if (projectId && ((agP && String(agP) !== String(projectId)) || (!agP && bP && String(bP) !== String(projectId)))) return;
    const st = emuStatuses[slot] || ((emuList || []).find(e => e.slot === slot) || {}).status;
    if (st === 'running' && !emuFullscreen.includes(slot)) emuAddFullscreen(slot);
  }, [emuAgentFocus, emuFsLayout.mode, webDevs]); // eslint-disable-line react-hooks/exhaustive-deps
  const wdAgentControl = async (deviceKey, action) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/agent-control`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surface: 'wdev', deviceKey, action }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || "Boshqaruv o'zgarmadi"); return; }
      setWdAgent(a => ({ ...a, [deviceKey]: { ...(a[deviceKey] || {}), paused: action !== 'resume' } }));
      toast(action === 'resume' ? '▶ Boshqaruv agentga qaytarildi' : "✋ Boshqaruv sizda — agent to'xtadi");
    } catch (e) { toast.error('Xatolik'); }
  };
  const emuFullscreenAllRunning = () => {
    const running = (emuList || []).filter(e => (emuStatuses[e.slot] || e.status) === 'running').map(e => e.slot);
    const wds = ((webDevs && webDevs.devices) || []).filter(d => d.running).map(d => d.id);   // 🌐 ochiq brauzerlar ham
    if (!running.length && !wds.length) return;
    running.forEach(s => emuAutoPreview(s));
    setEmuFullscreen(running);
    setWdFullscreen(wds);
  };

  // ---------- Pages (Varaqlar) state ----------
  const [pagesProjectId, setPagesProjectId] = useState('');
  const [pagesList, setPagesList] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState('');
  const [capturingPage, setCapturingPage] = useState('');
  const [pageScreenshots, setPageScreenshots] = useState({}); // {pageName: url}
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState('');
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [svgPreview, setSvgPreview] = useState(null); // design page object for the large SVG preview modal
  // Backend DB ER diagram (read-only, introspected from the live test-env DB)
  const [dbSchema, setDbSchema] = useState(null); // {ok, engine, tables, relations} | {ok:false, reason}
  const [dbSchemaLoading, setDbSchemaLoading] = useState(false);
  const [dbSchemaModuleId, setDbSchemaModuleId] = useState('');
  // 🚀 Production bazasi (FAQAT O'QISH, faqat loyiha egasi — backend tekshiradi). dbEnv = tanlangan,
  // dbSchemaEnv = ekrandagi sxema qaysi muhitdan yuklangan (qatorlar shu muhitdan o'qiladi).
  const [dbEnv, setDbEnv] = useState(() => { try { return dbOnly && new URLSearchParams(window.location.search).get('env') === 'prod' ? 'prod' : 'test'; } catch (e) { return 'test'; } });
  const [dbSchemaEnv, setDbSchemaEnv] = useState('test');
  // 🔎 ERD filtri — jadval nomi bo'yicha (vergul / bo'sh joy bilan bir nechta so'z). «🔗» yoqilsa
  // topilgan jadvallarning FK qo'shnilari ham chiqadi (bog'lanish chiziqlari uzilib qolmasin).
  const [dbTableFilter, setDbTableFilter] = useState('');
  const [dbFilterWithRel, setDbFilterWithRel] = useState(false);
  const [showLoginCredModal, setShowLoginCredModal] = useState(false);
  const [webCredentials, setWebCredentials] = useState({ username: '', password: '' });
  // Studio "eye" table-data viewer: { table:{name,columns}, page, pageSize, rows, total, loading } | null
  const [dbRowsModal, setDbRowsModal] = useState(null);
  // 🔎 Qidiruv + ↕ saralash (2026-10-10) — serverda (sahifalash serverda). Ref: barcha loadDbRows chaqiruvlari o'qiydi.
  const dbRowsQueryRef = useRef({ q: '', sort: '', dir: 'asc' });
  const dbSearchTimerRef = useRef(null);

  const DB_PAGE_SIZES = [10, 25, 50, 100];
  // PK columns from the schema metadata (used to identify rows for edit/delete).
  const dbPkCols = (table) => (table?.columns || []).filter(c => c.isPK).map(c => c.name);
  const openDbRows = (table) => {
    dbRowsQueryRef.current = { q: '', sort: '', dir: 'asc' };
    setDbRowsModal({
      table, page: 1, pageSize: 10, rows: [], total: 0, loading: true,
      engine: null, pkCols: dbPkCols(table), selected: new Set(), editing: null, editValues: null, busy: false,
    });
    loadDbRows(table.name, 1, 10);
  };
  const loadDbRows = async (tableName, page, pageSize) => {
    setDbRowsModal(m => (m ? { ...m, loading: true } : m));
    try {
      const ps = pageSize || (dbRowsModal && dbRowsModal.pageSize) || 10;
      const dq = dbRowsQueryRef.current;
      const qs = `${dq.q ? `&q=${encodeURIComponent(dq.q)}` : ''}${dq.sort ? `&sort=${encodeURIComponent(dq.sort)}&dir=${dq.dir}` : ''}`;
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${dbSchemaModuleId}/${dbSchemaEnv === 'prod' ? 'prod/' : ''}db-schema/rows?table=${encodeURIComponent(tableName)}&page=${page}&pageSize=${ps}${qs}`);
      const data = await res.json();
      if (!data.ok) { toast.error(data.message || 'Maʼlumotlar yuklanmadi'); setDbRowsModal(m => (m ? { ...m, loading: false } : m)); return; }
      setDbRowsModal(m => {
        if (!m || m.table.name !== tableName) return m;
        // No schema PK (e.g. mongo collection) → fall back to _id when present.
        let pkCols = m.pkCols;
        if ((!pkCols || !pkCols.length) && data.rows && data.rows[0] && Object.prototype.hasOwnProperty.call(data.rows[0], '_id')) pkCols = ['_id'];
        return { ...m, page, pageSize: ps, engine: data.engine || m.engine, pkCols, rows: data.rows || [], total: data.total || 0, loading: false, selected: new Set(), editing: null, editValues: null, q: dq.q, sort: dq.sort, dir: dq.dir, env: data.env || 'test', maskedColumns: data.maskedColumns || [] };
      });
    } catch (e) {
      toast.error('Maʼlumotlar yuklanmadi');
      setDbRowsModal(m => (m ? { ...m, loading: false } : m));
    }
  };
  const searchDbRows = (text) => {
    dbRowsQueryRef.current = { ...dbRowsQueryRef.current, q: text };
    setDbRowsModal(m => (m ? { ...m, qInput: text } : m));
    clearTimeout(dbSearchTimerRef.current);
    dbSearchTimerRef.current = setTimeout(() => { if (dbRowsModal) loadDbRows(dbRowsModal.table.name, 1, dbRowsModal.pageSize); }, 450);
  };
  // Ustun sarlavhasi: 1-bosish ↑, 2- ↓, 3- saralashsiz.
  const sortDbRows = (col) => {
    if (!dbRowsModal) return;
    const cur = dbRowsQueryRef.current;
    const next = cur.sort !== col ? { sort: col, dir: 'asc' } : cur.dir === 'asc' ? { sort: col, dir: 'desc' } : { sort: '', dir: 'asc' };
    dbRowsQueryRef.current = { ...cur, ...next };
    loadDbRows(dbRowsModal.table.name, 1, dbRowsModal.pageSize);
  };
  const dbRowKey = (row, pkCols) => pkCols.map(c => String(row?.[c])).join('|'); // stable per-row id within a page
  const dbPkObj = (row, pkCols) => { const o = {}; pkCols.forEach(c => { o[c] = row?.[c]; }); return o; };
  const toggleDbRow = (key) => setDbRowsModal(m => {
    if (!m) return m;
    const sel = new Set(m.selected); if (sel.has(key)) sel.delete(key); else sel.add(key);
    return { ...m, selected: sel };
  });
  const toggleDbSelectAll = () => setDbRowsModal(m => {
    if (!m) return m;
    const keys = m.rows.map(r => dbRowKey(r, m.pkCols));
    const all = keys.length && keys.every(k => m.selected.has(k));
    return { ...m, selected: all ? new Set() : new Set(keys) };
  });
  const deleteDbRows = async (rowsToDelete) => {
    const m = dbRowsModal; if (!m || !rowsToDelete.length) return;
    const pks = rowsToDelete.map(r => dbPkObj(r, m.pkCols));
    if (!window.confirm(`${pks.length} ta qatorni o'chirishni tasdiqlaysizmi? Bu amalni qaytarib bo'lmaydi.`)) return;
    setDbRowsModal(x => (x ? { ...x, busy: true } : x));
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${dbSchemaModuleId}/db-schema/rows`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table: m.table.name, pks }),
      });
      const data = await res.json();
      if (!data.ok) { toast.error(data.message || 'O\'chirishda xato'); setDbRowsModal(x => (x ? { ...x, busy: false } : x)); return; }
      toast.success(`${pks.length} ta qator o'chirildi`);
      const newTotal = Math.max(0, m.total - pks.length);
      const lastPage = Math.max(1, Math.ceil(newTotal / m.pageSize));
      setDbRowsModal(x => (x ? { ...x, busy: false } : x));
      loadDbRows(m.table.name, Math.min(m.page, lastPage), m.pageSize);
    } catch (e) {
      toast.error('O\'chirishda xato'); setDbRowsModal(x => (x ? { ...x, busy: false } : x));
    }
  };
  const startDbEdit = (idx) => setDbRowsModal(m => {
    if (!m) return m;
    const row = m.rows[idx];
    const cols = (m.table.columns && m.table.columns.length) ? m.table.columns.map(c => c.name) : [...new Set(m.rows.flatMap(r => Object.keys(r || {})))];
    const ev = {};
    cols.forEach(c => { const v = row[c]; ev[c] = { value: (v === null || v === undefined) ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v)), isNull: (v === null || v === undefined) }; });
    return { ...m, editing: idx, editValues: ev };
  });
  const cancelDbEdit = () => setDbRowsModal(m => (m ? { ...m, editing: null, editValues: null } : m));
  const setDbEditVal = (col, patch) => setDbRowsModal(m => (m ? { ...m, editValues: { ...m.editValues, [col]: { ...m.editValues[col], ...patch } } } : m));
  const saveDbEdit = async () => {
    const m = dbRowsModal; if (!m || m.editing === null) return;
    const row = m.rows[m.editing];
    const changes = {};
    Object.entries(m.editValues).forEach(([c, ev]) => {
      if (m.pkCols.includes(c)) return; // PK is read-only (keeps the WHERE stable)
      const orig = row[c];
      const origStr = (orig === null || orig === undefined) ? null : (typeof orig === 'object' ? JSON.stringify(orig) : String(orig));
      const newVal = ev.isNull ? null : ev.value;
      if (origStr !== newVal) changes[c] = newVal;
    });
    if (!Object.keys(changes).length) { cancelDbEdit(); return; }
    setDbRowsModal(x => (x ? { ...x, busy: true } : x));
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${dbSchemaModuleId}/db-schema/rows`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table: m.table.name, pk: dbPkObj(row, m.pkCols), changes }),
      });
      const data = await res.json();
      if (!data.ok) { toast.error(data.message || 'Saqlashda xato'); setDbRowsModal(x => (x ? { ...x, busy: false } : x)); return; }
      toast.success('Saqlandi');
      setDbRowsModal(x => (x ? { ...x, busy: false, editing: null, editValues: null } : x));
      loadDbRows(m.table.name, m.page, m.pageSize);
    } catch (e) {
      toast.error('Saqlashda xato'); setDbRowsModal(x => (x ? { ...x, busy: false } : x));
    }
  };

  const fetchDbSchema = async (moduleId, envArg) => {
    if (!moduleId) return;
    const mod = (dbModule && String(dbModule.id) === String(moduleId)) ? dbModule : (selectedProjectModules || []).find(x => String(x.id) === String(moduleId));
    const env = ((envArg || dbEnv) === 'prod' && mod && mod.prod_remote_host) ? 'prod' : 'test';
    setDbSchemaLoading(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}/${env === 'prod' ? 'prod/' : ''}db-schema`);
      const data = await res.json();
      setDbSchema(data);
      setDbSchemaModuleId(String(moduleId));
      setDbSchemaEnv(env);
    } catch (e) {
      setDbSchema({ ok: false, reason: 'error', message: 'Sxema yuklanmadi' });
      setDbSchemaModuleId(String(moduleId));
      setDbSchemaEnv(env);
    } finally {
      setDbSchemaLoading(false);
    }
  };

  const fetchPages = async (pid, moduleIdArg) => {
    const id = pid || pagesProjectId || abProjectId;
    if (!id) return;
    const proj = projects.find(p => String(p.id) === String(id));
    if (!proj) return;

    const res0 = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${id}/modules`);
    const modules = await res0.json();
    // Which modules to load real pages for: the selected tab, or — when "Barchasi" —
    // ALL web+mobile modules combined (so web AND mobile designs both get real pages).
    const selModId = moduleIdArg !== undefined ? moduleIdArg : studioModuleId;
    let targets;
    if (selModId) {
      const m = (modules || []).find(x => String(x.id) === String(selModId));
      targets = m ? [m] : [];
    } else {
      targets = (modules || []).filter(m => isWebModule(m) || m.module_type === 'mobile');
    }
    targets = targets.filter(m => m && m.workspace_path);
    if (!targets.length) {
      setPagesError('Modul workspace_path topilmadi');
      setPagesList([]);
      return;
    }

    setPagesLoading(true);
    setPagesError('');
    try {
      const allPages = [];
      const shots = {};
      for (const mod of targets) {
        let data;
        try {
          const res = await authFetch(`${process.env.REACT_APP_API_URL}/pages?workspacePath=${encodeURIComponent(mod.workspace_path)}`);
          data = await res.json();
          if (!res.ok || !Array.isArray(data)) continue;
        } catch { continue; }
        const isMobile = mod.module_type === 'mobile';
        const prefix = isMobile ? id : `web_${id}`;
        for (const p of data) {
          const safe = p.name.replace(/[^a-zA-Z0-9_\-]/g, '_');
          const url = `${process.env.REACT_APP_API_URL}/page-screenshots/${prefix}/${safe}.png?t=${Date.now()}`;
          allPages.push({ ...p, _moduleId: mod.id, _moduleType: mod.module_type, _screenshotUrl: url });
          shots[`${mod.id}:${p.name}`] = url;
          shots[p.name] = url; // backward-compat for the non-compare pages grid
        }
      }
      setPagesList(allPages);
      setPageScreenshots(shots);
    } catch (e) {
      setPagesError('Server xatosi');
    } finally {
      setPagesLoading(false);
    }
  };

  // Joriy kadr src-URL (data: PNG yoki blob: JPEG/PNG) → toza PNG base64.
  // Backend /pages/screenshot .png kutadi; kadr endi JPEG blob bo'lishi mumkin,
  // shuning uchun canvas orqali har doim PNG'ga normalize qilamiz.
  const frameToPngBase64 = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth || 720; c.height = img.naturalHeight || 1280;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png').split(',')[1]);
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('frame load failed'));
    img.src = src;
  });

  const capturePageScreenshot = async (pageName) => {
    if (!emulatorFrame) { toast.error('Avval emulator streamni boshlang'); return; }
    setCapturingPage(pageName);
    try {
      const imageBase64 = await frameToPngBase64(emulatorFrame);
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/pages/screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: pagesProjectId, pageName, imageBase64 }),
      });
      const data = await res.json();
      if (res.ok) {
        setPageScreenshots(prev => ({ ...prev, [pageName]: `${process.env.REACT_APP_API_URL}${data.url}?t=${Date.now()}` }));
        toast.success(`"${pageName}" screenshot saqlandi`);
      } else {
        toast.error(data.error || 'Xato');
      }
    } catch (e) {
      toast.error('Xatolik');
    } finally {
      setCapturingPage('');
    }
  };

  // Which modules to generate screenshots for: the selected tab, or ALL web+mobile
  // (when "Barchasi"). Used by both the click handler and the generator.
  const screenshotTargets = (modules) => {
    if (studioModuleId) {
      const m = (modules || []).find(x => String(x.id) === String(studioModuleId));
      return (m && m.workspace_path) ? [m] : [];
    }
    return (modules || []).filter(m => (isWebModule(m) || m.module_type === 'mobile') && m.workspace_path);
  };

  const handleGenerateClick = async () => {
    if (!pagesProjectId) return;
    const resModules = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${pagesProjectId}/modules`);
    const modules = await resModules.json();
    const targets = screenshotTargets(modules);
    if (!targets.length) { toast.error('Mos modul topilmadi'); return; }
    // Any web target → collect login creds first (the modal then runs the generator
    // for ALL targets). Mobile-only → run directly.
    if (targets.some(m => isWebModule(m))) {
      setShowLoginCredModal(true);
    } else {
      generatePageScreenshots(null, modules);
    }
  };

  const generatePageScreenshots = async (credentials, cachedModules) => {
    if (!pagesProjectId) return;
    const proj = projects.find(p => String(p.id) === String(pagesProjectId));
    if (!proj) return;

    setShowLoginCredModal(false);
    setIsGenerating(true);
    setPagesError('');

    try {
      let modules = cachedModules;
      if (!modules) {
        const r = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${pagesProjectId}/modules`);
        modules = await r.json();
      }
      const targets = screenshotTargets(modules);
      if (!targets.length) { toast.error('Modul workspace topilmadi'); return; }

      const allPages = [];
      const shots = {};
      let okCount = 0;
      // Generate per module (web via Playwright, mobile via emulator) and MERGE — so a
      // failing mobile module never blocks the web one (and vice versa).
      for (const target of targets) {
        let res, data;
        try {
          if (target.module_type === 'mobile') {
            const apkRes = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/apk-info?workspacePath=${encodeURIComponent(target.workspace_path)}`);
            const apkData = await apkRes.json();
            if (!apkData.packageName) { toast.error(`${target.name}: package name aniqlanmadi — avval APK build qiling`); continue; }
            setGenerateProgress(`📱 ${apkData.packageName} emulator orqali...`);
            res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/generate-page-screenshots`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ workspacePath: target.workspace_path, packageName: apkData.packageName, projectId: pagesProjectId }),
            });
          } else {
            const testUrl = target.test_url || proj.test_url;
            if (!testUrl) { toast.error(`${target.name}: test_url yo'q`); continue; }
            setGenerateProgress(`🌐 Playwright orqali ${testUrl}...`);
            res = await authFetch(`${process.env.REACT_APP_API_URL}/web/generate-page-screenshots`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                workspacePath: target.workspace_path, testUrl, projectId: pagesProjectId,
                loginUsername: credentials?.username || '', loginPassword: credentials?.password || '',
              }),
            });
          }
          data = await res.json();
          if (!res.ok) { toast.error(`${target.name}: ${data.error || 'xato'}`); continue; }
        } catch (e) { toast.error(`${target.name}: server xatosi`); continue; }

        okCount++;
        (data.pages || []).forEach(p => {
          const url = p.screenshotUrl ? `${process.env.REACT_APP_API_URL}${p.screenshotUrl}?t=${Date.now()}` : null;
          allPages.push({ ...p, _moduleId: target.id, _moduleType: target.module_type, _screenshotUrl: url });
          if (url) { shots[`${target.id}:${p.name}`] = url; shots[p.name] = url; }
        });
      }

      setPagesList(allPages);
      setPageScreenshots(shots);
      if (okCount) toast.success(`${allPages.length} ta sahifa generatsiya qilindi`);
    } catch (e) {
      toast.error('Xatolik yuz berdi');
    } finally {
      setIsGenerating(false);
      setGenerateProgress('');
    }
  };

  // ---------- Users management state (admin only) ----------
  const [usersList, setUsersList] = useState([]);
  const [newUserForm, setNewUserForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [userFormError, setUserFormError] = useState('');
  const [userPwEdits, setUserPwEdits] = useState({}); // {userId: 'yangi parol'} — admin parol almashtirish
  // ⚙️ Per-user engine token cheklovi (Foydalanuvchilar sahifasida).
  // userLimits: {userId: {fiveHour:{used,cap}, weekly:{used,cap}, blocked}} — /token-usage/limits dan.
  // userLimitEdits: {'<id>:5h'|'<id>:w': 'M token'} — tahrirlanayotgan qiymatlar.
  const [userLimits, setUserLimits] = useState({});
  const [globalLimits, setGlobalLimits] = useState(null); // {fiveHour:{cap}, weekly:{cap}, enabled} — shaxsiy karta yonida ko'rinadi
  const [userLimitEdits, setUserLimitEdits] = useState({});
  const [savingLimitId, setSavingLimitId] = useState(null);
  const [userNameEdits, setUserNameEdits] = useState({}); // {userId: 'yangi nom'} — admin foydalanuvchi nomini tahrirlash
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users`);
      const data = await res.json();
      setUsersList(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleCreateUser = async () => {
    const { username, email, password, role } = newUserForm;
    if (!username.trim() || !email.trim() || !password.trim()) {
      setUserFormError('Barcha maydonlarni to\'ldiring');
      return;
    }
    setIsSubmittingUser(true);
    setUserFormError('');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, role }),
      });
      const data = await res.json();
      if (!res.ok) { setUserFormError(data.error || 'Xato'); return; }
      setNewUserForm({ username: '', email: '', password: '', role: 'user' });
      toast.success('Foydalanuvchi yaratildi');
      fetchUsers();
    } catch (e) {
      setUserFormError('Server xatosi');
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Foydalanuvchini o\'chirishni tasdiqlaysizmi?')) return;
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/users/${id}`, { method: 'DELETE' });
      toast.success('O\'chirildi');
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  // Admin: foydalanuvchi parolini almashtirish (PUT /users/:id {password})
  const handleChangePassword = async (id) => {
    const pw = (userPwEdits[id] || '').trim();
    if (pw.length < 6) { toast.error("Parol kamida 6 ta belgidan iborat bo'lishi kerak"); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      setUserPwEdits(s => ({ ...s, [id]: '' }));
      toast.success('🔑 Parol yangilandi');
    } catch (e) { toast.error('Server xatosi'); }
  };

  // Admin: foydalanuvchi nomini almashtirish (PUT /users/:id {username})
  const handleChangeUsername = async (u) => {
    const name = (userNameEdits[u.id] ?? '').trim();
    if (name.length < 3) { toast.error("Foydalanuvchi nomi kamida 3 ta belgidan iborat bo'lishi kerak"); return; }
    if (name === u.username) { setUserNameEdits(s => { const c = { ...s }; delete c[u.id]; return c; }); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: name }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      setUserNameEdits(s => { const c = { ...s }; delete c[u.id]; return c; });
      toast.success('✏️ Foydalanuvchi nomi yangilandi');
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // 👥 Biriktirishda rol ANIQ tanlanadi (2026-11-02: rol so'ralmasdi, server jimgina 👑 Egasi qilardi — mijoz «jamshid»
  // Idora'ga egasi bo'lib qo'shilib qolgan). Chipda rol ko'rinadi va o'sha yerda o'zgartiriladi.
  const [attachRole, setAttachRole] = useState({});   // userId → tanlangan rol (default 'developer')
  const handleAttachProject = async (userId, projectId) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${userId}/project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, role: attachRole[userId] || 'developer' }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  const handleProjectRole = async (userId, projectId, role) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/members/${userId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Rol o\'zgarmadi'); return; }
      toast.success('Rol o\'zgardi');
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  const handleDetachProject = async (userId, projectId) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/users/${userId}/project/${projectId}`, { method: 'DELETE' });
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  // Admin: bir foydalanuvchining bitta bo'limga ruxsatini yoqish/o'chirish.
  // null (= barchasi) holatda toggle qilinsa, to'liq ro'yxatdan farqlanadi.
  // Har foydalanuvchining engine token cheklovi + o'z sarfini o'qish (admin).
  // Foydalanuvchilar sahifasi ochilganda va saqlangandan keyin chaqiriladi.
  const fetchUserLimits = React.useCallback(async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits`);
      if (!res.ok) return;
      const d = await res.json();
      const map = {};
      (d.users || []).forEach(x => { map[x.id] = x; });
      setUserLimits(map);
      if (d.fiveHour && d.weekly) setGlobalLimits({ fiveHour: d.fiveHour, weekly: d.weekly, enabled: !!d.enabled });
    } catch (e) { /* jim — panel bo'sh ko'rinadi, sahifa buzilmaydi */ }
  }, [authFetch]);

  // Umumiy (global) cheklovni shaxsiy qiymatga TENGLASHTIRISH — bir bosishda (admin).
  // Aks holda admin Konfiguratsiya tabiga o'tib o'sha raqamni qayta terishi kerak edi;
  // amalda u buni qilmasdi va shaxsiy cap "ishlamasdi".
  const handleRaiseGlobalLimits = async (cap5, capW) => {
    const body = {};
    if (cap5) body.limit_5h_tokens = Math.round(cap5);
    if (capW) body.limit_weekly_tokens = Math.round(capW);
    if (!Object.keys(body).length) return;
    setSavingLimitId('global');
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Saqlanmadi'); return; }
      toast.success(`Umumiy cheklov ko'tarildi${cap5 ? ` · 5 soat ${Math.round(cap5 / 1e6)}M` : ''}${capW ? ` · haftalik ${Math.round(capW / 1e6)}M` : ''} — engine 10 soniyada ko'radi`);
      fetchUserLimits();
    } catch (e) { toast.error('Server xatosi'); }
    finally { setSavingLimitId(null); }
  };

  // Bo'sh qiymat → null (cheklov olib tashlanadi). M token → xom token.
  const handleSaveUserLimits = async (userId) => {
    const toTokens = (v) => {
      const raw = String(v ?? '').trim();
      if (raw === '') return null;
      const n = parseInt(raw, 10);
      return n > 0 ? n * 1e6 : null;
    };
    const cur = userLimits[userId] || {};
    const v5 = userLimitEdits[`${userId}:5h`] ?? (cur.fiveHour?.cap ? Math.round(cur.fiveHour.cap / 1e6) : '');
    const vw = userLimitEdits[`${userId}:w`] ?? (cur.weekly?.cap ? Math.round(cur.weekly.cap / 1e6) : '');
    setSavingLimitId(userId);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits/user/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit_5h_tokens: toTokens(v5), limit_weekly_tokens: toTokens(vw) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Saqlanmadi'); return; }
      toast.success((toTokens(v5) === null && toTokens(vw) === null) ? 'Cheklov olib tashlandi' : 'Cheklov saqlandi');
      // Shaxsiy cap umumiydan KATTA bo'lsa u hech qachon ishlamaydi — engine umumiyda
      // to'xtaydi. Buni saqlash paytida darhol aytamiz (kartadagi ogohlantirish bilan birga).
      const g5 = Number(globalLimits?.fiveHour?.cap || 0), gw = Number(globalLimits?.weekly?.cap || 0);
      if (globalLimits?.enabled && ((toTokens(v5) && g5 > 0 && toTokens(v5) > g5) || (toTokens(vw) && gw > 0 && toTokens(vw) > gw))) {
        toast((t) => <span>⚠️ Umumiy (global) cheklov {Math.round(g5 / 1e6)}M / {Math.round(gw / 1e6)}M — engine avval SHUNGA to'xtaydi. Umumiyni ham ko'taring (kartadagi ↑ tugma yoki Konfiguratsiya → ⚙️ Engine cheklovi).</span>, { duration: 9000, icon: '⚠️' });
      }
      setUserLimitEdits(s => { const n = { ...s }; delete n[`${userId}:5h`]; delete n[`${userId}:w`]; return n; });
      fetchUserLimits();
    } catch (e) { toast.error('Server xatosi'); }
    finally { setSavingLimitId(null); }
  };

  const handleToggleSection = async (u, key) => {
    const current = Array.isArray(u.allowed_sections) ? u.allowed_sections : CLIENT_SECTION_KEYS;
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_sections: next }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // 🤖 F3: platforma (operator Max) Claude hisobidan foydalanish ruxsati. O'chirilsa foydalanuvchi
  // o'z tokenini ulaydi yoki loyiha egasining hisobida ishlaydi (services/claudeAccount.js).
  const handleToggleClaudePlatform = async (u) => {
    const on = Number(u.claude_platform) !== 1;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}/claude-platform`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: on }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      toast.success(on ? '🤖 Platforma Claude hisobi ruxsat etildi' : '🤖 Platforma Claude hisobi yopildi');
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // 📱 Shaxsiy emulator slotlari soni (2026-10-11): bo'sh = standart 3, 1..6. Server: users.emulator_slots.
  const handleSetEmulatorSlots = async (u, val) => {
    const v = val === '' ? null : Number(val);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emulator_slots: v }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      toast.success(`📱 ${u.username}: ${v == null ? 'standart (3)' : v} ta emulator`);
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // 🌐 Shaxsiy brauzer qurilma soni (2026-10-30): bo'sh = standart 3, 1..8 (loyiha boshiga). Server: users.web_device_slots.
  const handleSetWebDeviceSlots = async (u, val) => {
    const v = val === '' ? null : Number(val);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ web_device_slots: v }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      toast.success(`🌐 ${u.username}: ${v == null ? 'standart (3)' : v} ta brauzer (har loyihada)`);
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // Hisobni yoqish/o'chirish. O'chirilgan foydalanuvchi tizimga kira olmaydi va
  // qo'lidagi tokeni ham darhol ishlamay qoladi (server tomonda tekshiriladi).
  const handleToggleUserActive = async (u) => {
    const disabling = u.is_active !== 0;
    if (disabling && !window.confirm(`"${u.username}" tizimga kira olmaydigan bo'ladi. Davom etamizmi?`)) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !disabling }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.error || 'Xato'); return; }
      toast.success(disabling ? `"${u.username}" o'chirildi` : `"${u.username}" yoqildi`);
      fetchUsers();
    } catch (e) { toast.error('Server xatosi'); }
  };

  // ---------- State for projects (using useReducer) ----------
  const [projects, dispatch] = useReducer(projectReducer, []);
  // 👥 F2: joriy loyihadagi rolim. Admin — hammasi; `my_role` kelmagan eski javob — cheklamaymiz
  // (server baribir tekshiradi). Egasi bo'lmaganga egasiga xos tablar ko'rsatilmaydi.
  const myProjRole = (() => {
    if (user?.role === 'admin') return 'owner';
    const p = (projects || []).find(pp => String(pp.id) === String(abProjectId));
    if (p && p.my_role) return p.my_role;
    // Loyiha tanlanmagan: faqat 🤝 mijoz bo'lgan hisobga ham mijoz ko'rinishi (egasiga xos tablar chiqmasin).
    const all = projects || [];
    return all.length && all.every(pp => pp.my_role === 'client') ? 'client' : 'owner';
  })();
  const isProjOwner = myProjRole === 'owner';
  // 🤝 Mijoz: ko'radi (xarita, sinov topilmalari, hujjatlar, crashlar, vazifalar holati), vazifa/izoh qoldiradi —
  // ish tugmalari, kod/baza/emulator/sarf yashirin (server ham rad etadi, 207). Faqat mijoz bo'lgan hisob — isPureClient.
  const isProjClient = myProjRole === 'client';
  // 🔐 Admin shu foydalanuvchi uchun yopgan / «faqat ko'rish» qilgan elementlar (users.ui_access, backend docs/knowledge/208).
  const uiAcc = (user?.role === 'admin' || !user?.ui_access || typeof user.ui_access !== 'object') ? {} : user.ui_access;
  const uiMode = (k) => uiAcc[k] || 'full';
  const uiShow = (k) => uiMode(k) !== 'hide';
  const studioTabOk = (v) => uiShow('studio.' + v) && (!isProjClient || CLIENT_STUDIO_VIEWS.includes(v));
  const isPureClient = user?.role !== 'admin' && (projects || []).length > 0 && (projects || []).every(p => p.my_role === 'client');
  // 🔁 Vazifa kartasidagi «Sina» → 🧠 Loyiha agenti (ProcessesPanel tanlovlar paneli). Agent faqat egasi/dasturchiga ochiq (190).
  const agentTestOkFor = (t) => {
    if (!uiShow('nav.processes') || !uiShow('nav.agent')) return false;
    const role = user?.role === 'admin' ? 'owner' : (((projects || []).find(p => String(p.id) === String(t.project_id)) || {}).my_role || 'owner');
    return !['viewer', 'reviewer', 'client'].includes(role);
  };
  // Shu loyiha ochiq va «🌙 Fonda» tanlanmagan → ⏳ Jarayonlar → 🧠 Agent ochilib xabar o'zi yuboriladi (OperatorChat, bir marta);
  // aks holda xabar o'sha loyihaning oxirgi tanlangan suhbatiga to'g'ridan-to'g'ri ketadi (agent fonda ishlaydi).
  const sendAgentTest = async (t, text, background) => {
    const pid = t.project_id;
    if (!background && abProjectId && String(pid) === String(abProjectId)) {
      setAgentDraftExt({ text, send: true, projectId: abProjectId, at: Date.now() });
      toast.success("🧠 Agentga yuborildi — o'ngdagi 🧠 Agent oynasida kuzating");
      return true;
    }
    let thread;
    try { thread = Number(localStorage.getItem(`kh_agent_thread_${pid}`)) || undefined; } catch (e) { }
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/operator/${pid}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, thread }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Agentga yuborilmadi'); return false; }
      const pn = ((projects || []).find(p => String(p.id) === String(pid)) || {}).name || `#${pid}`;
      toast.success(`🧠 «${pn}» agentiga yuborildi — fonda sinaydi; natija 🧠 Agent suhbatida va Telegram'da`);
      return true;
    } catch (e) { toast.error("Ulanib bo'lmadi"); return false; }
  };
  React.useEffect(() => {
    if (activeTab !== 'studio' || studioTabOk(studioView)) return;
    const first = ['map', 'qa', 'docs', 'crashes', 'browser', 'emulator', 'database', 'files', 'status', 'architecture', 'todo', 'publish', 'secrets', 'security'].find(studioTabOk);
    if (first) setStudioView(first);
  }, [isProjClient, activeTab, studioView, JSON.stringify(uiAcc)]); // eslint-disable-line react-hooks/exhaustive-deps
  // Foydalanuvchining bitta loyihasi bo'lsa — o'zi tanlanadi (har kirishda «Loyiha tanlang…» qilib yurmasin; operator 2026-11-02).
  React.useEffect(() => {
    if (!abProjectId && (projects || []).length === 1) selectStudioProject(String(projects[0].id));
  }, [abProjectId, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- NEW: state for project creation ----------
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectRemoteHost, setNewProjectRemoteHost] = useState('');
  const [newProjectSshUser, setNewProjectSshUser] = useState('');
  const [newProjectDeployPath, setNewProjectDeployPath] = useState('');
  const [newProjectRestartCmd, setNewProjectRestartCmd] = useState('');
  const [newProjectTestUrl, setNewProjectTestUrl] = useState('');
  const [newProjectProductionUrl, setNewProjectProductionUrl] = useState('');
  const [projectError, setProjectError] = useState('');
  const [taskSidebarOpen, setTaskSidebarOpen] = useState(true);
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // ---------- State for editing a project ----------
  const [editingProject, setEditingProject] = useState(null);
  // NEW: local error for the inline edit form
  const [editError, setEditError] = useState('');

  // ---------- NEW: State for delete/update operations ----------
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [updatingProjectId, setUpdatingProjectId] = useState(null);
  const [projectListError, setProjectListError] = useState('');

  // ---------- Server management state ----------
  const [serverHealth, setServerHealth]         = useState({ test: 'unknown', production: 'unknown' });
  const [serverLogs, setServerLogs]             = useState([]);
  const [serverRunning, setServerRunning]       = useState(false);
  const [wizardSteps, setWizardSteps]           = useState([]);   // 🚀 test muhiti sehrgari qadamlari
  const [serverEnvRequired, setServerEnvRequired] = useState(null);
  const [serverEnvVars, setServerEnvVars]       = useState({});

  // ---------- Project detail view ----------
  const [selectedProject, setSelectedProject]         = useState(null);
  const [projectDetailTab, setProjectDetailTab]       = useState('overview');

  // GitLab connect form
  // ---------- Module detail state ----------
  const [selectedModule, setSelectedModule]       = useState(null);
  const [moduleDetailTab, setModuleDetailTab]     = useState('overview');
  const selectedModuleRef                         = useRef(null);
  useEffect(() => { selectedModuleRef.current = selectedModule; }, [selectedModule]);

  const [glUrl, setGlUrl]         = useState('');
  const [glToken, setGlToken]     = useState('');
  const [glBranch, setGlBranch]   = useState('main');
  const [glConnecting, setGlConnecting] = useState(false);
  const [glConnectErr, setGlConnectErr] = useState('');
  const [glInfo, setGlInfo]         = useState(null);
  const [glInfoLoading, setGlInfoLoading] = useState(false);
  const [glCiTemplate, setGlCiTemplate] = useState('node');
  const [glCiLoading, setGlCiLoading]   = useState(false);
  const [glCiMsg, setGlCiMsg]           = useState('');

  const loadServerHealth = async (moduleId) => {
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}/server/health`);
      if (r.ok) setServerHealth(await r.json());
    } catch {}
  };

  const handleStartServer = async (envVarsOverride = null) => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    setServerRunning(true);
    setServerLogs([]);
    setServerEnvRequired(null);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/server/start-test`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ env_vars: envVarsOverride || serverEnvVars }),
    }).catch(() => setServerRunning(false));
  };

  // New: provision a fresh test env on the infrastructure server (DB + app +
  // nginx subdomain + AGENTS.md). Live logs via the same server_log socket.
  // 🚀 SEHRGAR: repo → boshlang'ich kod → test muhiti → tekshiruv, bitta bosishda.
  // Qadamlar `wizard_step` socket eventida keladi (pastdagi socket useEffect'da).
  const handleTestEnvWizard = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (mod.module_type === 'mobile') {
      toast.error("Mobil modulda test muhiti bo'lmaydi — sinov emulatorda o'tadi");
      return;
    }
    setServerRunning(true);
    setServerLogs([]);
    setWizardSteps([
      { key: 'repo',  label: 'GitLab repozitoriy',        status: 'pending' },
      { key: 'code',  label: "Boshlang'ich kod",          status: 'pending' },
      { key: 'env',   label: 'Test muhiti (Docker + TLS)', status: 'pending' },
      { key: 'check', label: 'Tekshiruv',                 status: 'pending' },
    ]);
    const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/test-env/wizard`, {
      method: 'POST',
    }).catch(() => null);
    if (!r || !r.ok) { setServerRunning(false); toast.error('Sehrgarni boshlab bo\'lmadi'); }
  };

  const handleSetupTestEnv = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (mod.module_type === 'mobile') {
      toast.error("Mobile modullari uchun test env emulator orqali ishlaydi");
      return;
    }
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/test-env/setup`, {
      method: 'POST',
    }).catch(() => setServerRunning(false));
  };

  const handleRedeployTestEnv = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/test-env/redeploy`, {
      method: 'POST',
    }).catch(() => setServerRunning(false));
  };

  // Deploy the module to ITS production server (separate from the test-env). Streams over
  // the same server_log socket as the test redeploy (serverLogs + serverRunning).
  const handleProdDeploy = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (!mod.prod_remote_host || !mod.prod_ssh_user || !mod.prod_deploy_path || !mod.prod_restart_cmd) {
      toast.error("Avval Production konfiguratsiyani to'ldiring (Host, SSH User, Deploy Path, Deploy/Restart Cmd)");
      return;
    }
    if (!window.confirm(`Production'ga deploy qilamizmi? ${mod.prod_ssh_user}@${mod.prod_remote_host}:${mod.prod_deploy_path}`)) return;
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/prod/deploy`, {
      method: 'POST',
    }).then(async r => { if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Xato'); setServerRunning(false); } })
      .catch(() => setServerRunning(false));
  };

  const handleTeardownTestEnv = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (!window.confirm("Test environmentni butunlay o'chirish? Containerlar, DB, fayllar yo'qoladi (GitLab kodi tegmaydi).")) return;
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/test-env/teardown`, {
      method: 'POST',
    }).catch(() => setServerRunning(false));
  };

  // AI Agent (Claude Code) — run a coding task against the module repo
  const [aiTaskInstruction, setAiTaskInstruction] = useState('');
  const handleRunAiAgent = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (!aiTaskInstruction.trim()) { toast.error('Vazifa matnini yozing'); return; }
    if (!mod.gitlab_project_id) { toast.error('Avval GitLab repo ulanishi kerak'); return; }
    if (!mod.test_url) {
      if (!window.confirm("Test env hali sozlanmagan. Claude Code faqat repo ichida ishlay oladi (push qiladi, lekin redeploy bo'lmaydi). Davom etamizmi?")) return;
    }
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/ai-agent/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction: aiTaskInstruction.trim() }),
    }).catch(() => setServerRunning(false));
  };

  // Mobile: build the APK on the infra server + install + launch on the
  // user's emulator. Live logs stream over the same server_log socket.
  const handleMobileBuild = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    if (mod.module_type !== 'mobile') return;
    if (!mod.gitlab_project_id) { toast.error('Avval GitLab repo ulanishi kerak'); return; }
    setServerRunning(true);
    setServerLogs([]);
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/mobile/build`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiTarget }),
    }).catch(() => setServerRunning(false));
  };

  // Mobile: build a SIGNED release AAB (Play Store) on the infra server. Same
  // server_log stream as the APK build (serverRunning/serverLogs). Keystore must be set.
  const handleAabBuild = async () => {
    const mod = selectedModuleRef.current;
    if (!mod || mod.module_type !== 'mobile') return;
    if (!mod.gitlab_project_id) { toast.error('Avval GitLab repo ulanishi kerak'); return; }
    // AAB = Play Store uchun. 🔗 tanlagich default'i 🧪 TEST (emulator sinovi uchun),
    // shuning uchun do'konga TEST serverga ulangan build ketib qolmasin — so'raymiz.
    if (apiTarget === 'test' && !window.confirm(
      "⚠️ AAB Google Play uchun, lekin 🔗 API «🧪 test env» tanlangan.\n\n" +
      "Do'konga chiqadigan build TEST serverga ulanadi. Davom etaymi?\n" +
      "(Bekor qilib, 🔗 dan «🚀 production» yoki «koddagidek» ni tanlang.)")) return;
    setServerRunning(true);
    setServerLogs([]);
    toast('📦 AAB build boshlandi (~bir necha daqiqa)');
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/mobile/aab/build`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiTarget }),
    }).catch(() => setServerRunning(false));
  };

  // Studio-context APK build+install: finds the project's mobile module and routes
  // its build logs (server_log socket, filtered by module id) into serverLogs.
  const handleStudioMobileBuild = () => {
    const mod = activeMobileModule;
    if (!mod) { toast.error('Mobil modul topilmadi'); return; }
    if (!mod.gitlab_project_id) { toast.error('Avval GitLab repo ulanishi kerak'); return; }
    selectedModuleRef.current = mod;   // immediate, so early build logs aren't dropped
    setSelectedModule(mod);            // keep the ref in sync via its effect
    setServerLogs([]);
    setServerRunning(true);
    toast(`🔨 «${projects.find(p => String(p.id) === String(projectId))?.name || 'Loyiha'}» APK build boshlandi (~4 daqiqa)`);
    authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/mobile/build`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiTarget }),
    }).catch(() => setServerRunning(false));
  };

  // Download the built APK as a file (to install on a real phone).
  const downloadApkFile = async () => {
    const mod = selectedModuleRef.current?.module_type === 'mobile'
      ? selectedModuleRef.current
      : activeMobileModule;
    if (!mod) { toast.error('Mobil modul topilmadi'); return; }
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/mobile/apk-link`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) { toast.error(data.error || 'APK topilmadi'); return; }
      const mb = (data.sizeBytes / 1048576).toFixed(0);
      toast.success(`Yuklab olinmoqda: ${data.filename} (${mb} MB)`);
      window.location.href = `${process.env.REACT_APP_API_URL}${data.url}`;
    } catch (e) { if (e.message !== 'unauthorized') toast.error('Xato: ' + (e.message || '')); }
  };

  const openProjectDetail = (proj) => {
    setSelectedProject(proj);
    setSelectedModule(null);
    setProjectDetailTab('overview');
    setServerLogs([]);
    setServerHealth({ test: 'unknown', production: 'unknown' });
    fetchSelectedProjectModules(proj.id);
  };

  const openModuleDetail = (mod) => {
    setSelectedModule(mod);
    // 🔄 Ro'yxat ESKIRGAN bo'lishi mumkin: sahifa uzoq ochiq turadi, modulni esa boshqa sessiya
    // yoki Claude o'zgartirgan bo'ladi (2026-09-29: Sadulla frontend prod URL/serveri kartada
    // ko'rinmadi — ro'yxat qo'shilishdan oldin yuklangan, «Running» esa yangidan so'ralgan).
    // Eski obyekt bilan ✎ Tahrirlash → Saqlash bazadagi yangi qiymatlarni ustidan yozardi.
    if (mod && mod.project_id) {
      authFetch(`${process.env.REACT_APP_API_URL}/projects/${mod.project_id}/modules`)
        .then(r => (r.ok ? r.json() : null))
        .then(list => {
          const fresh = Array.isArray(list) ? list.find(m => m.id === mod.id) : null;
          if (fresh) setSelectedModule(cur => (cur && cur.id === fresh.id ? { ...cur, ...fresh } : cur));
        })
        .catch(() => {});
    }
    setModuleDetailTab('overview');
    setGlUrl(mod.gitlab_url || '');
    setGlToken('');   // token endi API'dan qaytmaydi (2026-10-12) — bo'sh maydon = saqlangani qoladi
    setGlBranch(mod.gitlab_branch || 'main');
    setGlInfo(null);
    setGlConnectErr('');
    setGlCiMsg('');
    setServerLogs([]);
    setServerEnvRequired(null);
    setServerEnvVars({});
    setServerRunning(false);
    setServerHealth({ test: 'unknown', production: 'unknown' });
    loadServerHealth(mod.id);
    if (mod.gitlab_project_id) loadGlInfo(mod.id);
  };

  const loadGlInfo = async (moduleId) => {
    setGlInfoLoading(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}/gitlab/info`);
      if (r.ok) setGlInfo(await r.json());
    } catch {}
    finally { setGlInfoLoading(false); }
  };

  const handleGlConnect = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    setGlConnecting(true); setGlConnectErr('');
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/gitlab/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gitlab_url: glUrl, gitlab_token: glToken, gitlab_branch: glBranch }),
      });
      const d = await r.json();
      if (!r.ok) { setGlConnectErr(d.error || 'Xatolik'); return; }
      const updated = { ...mod, gitlab_url: glUrl, gitlab_token: null, gitlab_token_set: true, gitlab_branch: glBranch, gitlab_project_id: d.gitlab_project_id };
      setSelectedModule(updated);
      setSelectedProjectModules(prev => prev.map(m => m.id === mod.id ? updated : m));
      await loadGlInfo(mod.id);
    } catch (e) { setGlConnectErr(e.message); }
    finally { setGlConnecting(false); }
  };

  const handleGlDisconnect = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/gitlab`, { method: 'DELETE' });
    const updated = { ...mod, gitlab_url: null, gitlab_token: null, gitlab_project_id: null };
    setSelectedModule(updated);
    setSelectedProjectModules(prev => prev.map(m => m.id === mod.id ? updated : m));
    setGlInfo(null); setGlUrl(''); setGlToken('');
  };

  const handleGlCiSetup = async () => {
    const mod = selectedModuleRef.current;
    if (!mod) return;
    setGlCiLoading(true); setGlCiMsg('');
    try {
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${mod.id}/gitlab/setup-cicd`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: glCiTemplate }),
      });
      const d = await r.json();
      setGlCiMsg(r.ok ? '✅ .gitlab-ci.yml muvaffaqiyatli yuklandi' : `❌ ${d.error || 'Xatolik'}`);
      if (r.ok) await loadGlInfo(mod.id);
    } catch (e) { setGlCiMsg(`❌ ${e.message}`); }
    finally { setGlCiLoading(false); }
  };

  // ---------- State for project modules ----------
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [selectedProjectForModules, setSelectedProjectForModules] = useState(null);
  const [projectModules, setProjectModules] = useState([]);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModulePath, setNewModulePath] = useState('');
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editModuleName, setEditModuleName] = useState('');
  const [editModulePath, setEditModulePath] = useState('');
  const [newModuleType, setNewModuleType] = useState('web');
  const [editModuleType, setEditModuleType] = useState('web');
  // Auto-create GitLab repo when adding a module (default: on if admin token is set)
  const [autoCreateGitlab, setAutoCreateGitlab] = useState(true);

  // /tasks dedup: ProcessesPanel (7s) + ProcessesFlyout (9s) + action-refreshlar bir vaqtga
  // to'g'ri kelganda bitta so'rov yetadi; sekin API'da parallel chaqiruvlar ham yig'ilmasin.
  const tasksFetchGuard = useRef({ t: 0, inflight: false });
  const fetchTasks = async () => {
    const g = tasksFetchGuard.current;
    if (g.inflight || Date.now() - g.t < 1500) return;
    g.inflight = true;
    try {
      // Faqat joriy oynani (tasksLimit) yuklaymiz — ref orqali, eski closure'da ham yangi qiymat.
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/tasks?limit=${tasksLimitRef.current}`);
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) { setTasks(data); setTasksHasMore(data.length >= tasksLimitRef.current); }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      g.t = Date.now();
      g.inflight = false;
    }
  };

  // Scroll qilganda eskilarini yuklash — oynani 10 taga kengaytiradi (offset 0 dan qayta o'qiydi,
  // shu bilan poll/socket yangilanishlari yuklangan oynani saqlaydi). data o'smasa — tugadi.
  const loadMoreTasks = async () => {
    if (tasksLoadingMore || !tasksHasMore) return;
    setTasksLoadingMore(true);
    const prevCount = tasks.length;
    const newLimit = tasksLimitRef.current + 10;
    tasksLimitRef.current = newLimit;
    setTasksLimit(newLimit);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/tasks?limit=${newLimit}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) { setTasks(data); setTasksHasMore(data.length > prevCount); }
      }
    } catch (e) { /* keep */ }
    finally { setTasksLoadingMore(false); }
  };

  // Infinite-scroll: sentinel ko'rinishga kirganda eski vazifalarni avto-yuklash (Vazifalar tab).
  useEffect(() => {
    if (activeTab !== 'tasks' || !tasksHasMore) return;
    const el = tasksSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0] && entries[0].isIntersecting) loadMoreTasks();
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tasksHasMore, tasksLoadingMore, tasks.length]);

  const fetchProjects = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/projects`);
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      const data = await response.json();
      dispatch({ type: 'SET_PROJECTS', payload: data });
      // Only update projectId if it's not already set or if the current project doesn't exist
      if (data.length > 0 && (!projectId || !data.some(p => p.id.toString() === projectId))) {
        setProjectId(data[0].id.toString());
      } else if (data.length === 0) {
        setProjectId('');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Could add state for error message to display to user
    }
  };

  const fetchAiData = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/claude/tiers`);
      const data = await res.json();
      setClaudeTiers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching Claude tiers:', error);
    }
    // Emulator oqimi auto-lock sozlamasi (Konfiguratsiya kartasi uchun)
    try {
      const r2 = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stream-settings`);
      const d2 = await r2.json().catch(() => ({}));
      if (r2.ok && d2 && d2.idleMinutes !== undefined) setEmuIdleSetting(d2.idleMinutes);
      if (r2.ok && d2 && d2.awakeMaxMinutes !== undefined) setEmuAwakeMaxSetting(Number(d2.awakeMaxMinutes) || 0);
    } catch {}
    // ⚡ Emulator optimizatsiyasi (Konfiguratsiya kartasi)
    try {
      const r4 = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/opt-settings`);
      const d4 = await r4.json().catch(() => ({}));
      if (r4.ok && d4 && d4.enabled !== undefined) setEmuOpt(d4);
    } catch {}
    // Engine cheklovi sozlamalari (parallellik + cap'da to'xtatish)
    try {
      const r3 = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits`);
      const d3 = await r3.json().catch(() => ({}));
      if (r3.ok && d3) {
        setEngineCfg({
          cc: d3.concurrency || 1, enabled: !!d3.enabled,
          cap5: Math.round((d3.fiveHour?.cap || 0) / 1e6),
          capW: Math.round((d3.weekly?.cap || 0) / 1e6),
          used5: d3.fiveHour?.used || 0, usedW: d3.weekly?.used || 0,
          budget: d3.taskBudget != null ? String(Math.round(d3.taskBudget / 1e6)) : '12',
        });
        setEngineCfgLoaded(true);
      }
    } catch {}
  };

  // BITTA foydalanuvchining oyna hisobini NOLGA tushirish (Foydalanuvchilar sahifasi).
  // Har foydalanuvchining cheklov oynasi MUSTAQIL yuradi — bu amal boshqalarga TEGMAYDI.
  // Yozuvlar O'CHIRILMAYDI — reset vaqtidan oldingi sarf shunchaki shu foydalanuvchining
  // oynasiga qo'shilmaydi (tarix/hisobotlar butun qoladi).
  // NB: parametr `win` deb ataladi — `window` deb atalsa global `window` obyektini
  // yashirib qo'yadi (window.confirm ishlamay qoladi).
  const [resettingWin, setResettingWin] = useState(''); // '<userId>:<win>'
  const resetUserWindow = async (u, win) => {
    const nomi = win === 'fiveHour' ? '5 soatlik' : win === 'weekly' ? 'haftalik' : 'ikkala';
    const ok = window.confirm(
      `${u.username} uchun ${nomi} token hisobi NOLGA tushiriladi va uning vazifalari darhol davom etadi.\n\n` +
      `Faqat SHU foydalanuvchiga ta'sir qiladi — boshqalarning hisobi o'z holicha qoladi.\n\n` +
      `Token yozuvlari o'chirilmaydi (tarix saqlanadi) — faqat cheklov oynasi qaytadan boshlanadi.`
    );
    if (!ok) return;
    setResettingWin(`${u.id}:${win}`);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits/user/${u.id}/reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ window: win }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Xato'); return; }
      toast.success(`♻️ ${u.username}: ${nomi} hisob nolga tushdi`);
      fetchUserLimits();
    } catch (e) { toast.error('Server xatosi'); }
    finally { setResettingWin(''); }
  };

  // Engine cheklovi sozlamasini saqlash (optimistik; xatoda eski qiymat qaytadi).
  // Faqat o'zgargan maydon yuboriladi — cap raqamlariga tegilmaydi.
  const saveEngineCfg = async (patch) => {
    const prev = engineCfg;
    const next = {
      ...prev,
      cc: patch.engine_concurrency !== undefined ? patch.engine_concurrency : prev.cc,
      enabled: patch.engine_limit_enabled !== undefined ? patch.engine_limit_enabled : prev.enabled,
    };
    setEngineCfgSaving(true);
    setEngineCfg(next);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/limits`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'xato');
      if (patch.limit_5h_tokens !== undefined || patch.limit_weekly_tokens !== undefined) {
        // Cap sarfdan past bo'lsa engine DARHOL to'xtaydi — buni ochiq aytamiz.
        const c5 = patch.limit_5h_tokens || 0, cw = patch.limit_weekly_tokens || 0;
        const low = (c5 > 0 && prev.used5 >= c5) || (cw > 0 && prev.usedW >= cw);
        if (low) toast('Cap joriy sarfdan PAST — engine yangi vazifa boshlamaydi', { icon: '⚠️', duration: 6000 });
        else toast.success('Cap saqlandi');
      } else if (patch.engine_concurrency !== undefined) {
        toast.success(`🧵 Parallellik: ${next.cc} ta vazifa bir vaqtda`);
      } else if (patch.engine_task_token_budget !== undefined) {
        toast.success(patch.engine_task_token_budget > 0 ? `🎯 Bitta vazifa byudjeti: ${Math.round(patch.engine_task_token_budget / 1e6)}M token` : '🎯 Bitta vazifa byudjeti: cheksiz');
      } else {
        toast.success(next.enabled ? "⏸ Cap'ga yetganda engine to'xtaydi" : "Cheklov endi faqat ko'rsatkich — engine to'xtamaydi");
      }
    } catch (e) {
      setEngineCfg(prev);
      toast.error('Saqlanmadi: ' + (e.message || ''));
    } finally { setEngineCfgSaving(false); }
  };

  // Emulator auto-lock sozlamasini saqlash (optimistik; xatoda eski qiymat qaytadi).
  const saveEmuIdleSetting = async (v) => {
    const prev = emuIdleSetting;
    setEmuIdleSaving(true);
    setEmuIdleSetting(v);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stream-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idleMinutes: v }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'xato');
      toast.success(v === 0 ? "Emulator oqimi: hech qachon to'xtamaydi" : `Emulator oqimi: ${v} daqiqa harakatsizlikda to'xtaydi`);
    } catch (e) {
      setEmuIdleSetting(prev);
      toast.error('Saqlanmadi: ' + (e.message || ''));
    } finally { setEmuIdleSaving(false); }
  };

  // ☕ «Uxlatma» muddatini saqlash (optimistik; xatoda eski qiymat qaytadi).
  const saveEmuAwakeMax = async (v) => {
    const prev = emuAwakeMaxSetting;
    setEmuAwakeMaxSaving(true);
    setEmuAwakeMaxSetting(v);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/stream-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ awakeMaxMinutes: v }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'xato');
      toast.success(v === 0 ? "«Uyg'oq» rejim: o'zi o'chmaydi" : `«Uyg'oq» rejim: ${v % 60 === 0 ? `${v / 60} soat` : `${v} daqiqa`}dan keyin o'zi o'chadi`);
    } catch (e) {
      setEmuAwakeMaxSetting(prev);
      toast.error('Saqlanmadi: ' + (e.message || ''));
    } finally { setEmuAwakeMaxSaving(false); }
  };

  // ⚡ Emulator optimizatsiyasi: saqlab, holatni serverdan qayta o'qiymiz (bitta kalit = aynan eski holat).
  const saveEmuOpt = async (patch) => {
    setEmuOptSaving(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/opt-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'xato');
      toast.success(patch.enabled === false ? "Optimizatsiya o'chirildi — keyingi ishga tushirishdan eski holat"
        : patch.enabled === true ? 'Emulator optimizatsiyasi yoqildi' : 'Saqlandi — darhol kuchda');
      const r = await authFetch(`${process.env.REACT_APP_API_URL}/emulator/opt-settings`);
      const d2 = await r.json().catch(() => ({}));
      if (r.ok && d2 && d2.enabled !== undefined) setEmuOpt(d2);
    } catch (e) {
      toast.error('Saqlanmadi: ' + (e.message || ''));
    } finally { setEmuOptSaving(false); }
  };

  // patch = { model? , effort? } — only the changed field is sent.
  const handleTierChange = async (role, patch) => {
    setTierSaving(role);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/claude/tiers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, ...patch }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Xato');
      setClaudeTiers(prev => prev.map(t => t.role === role ? {
        ...t,
        ...(patch.model !== undefined ? { model: patch.model, modelSource: 'db' } : {}),
        ...(patch.effort !== undefined ? { effort: patch.effort, effortSource: patch.effort ? 'db' : 'default' } : {}),
      } : t));
      const what = patch.model !== undefined ? `model → ${patch.model}` : `effort → ${patch.effort || 'standart'}`;
      toast.success(`${role}: ${what}`);
    } catch (e) {
      toast.error('Saqlanmadi: ' + e.message);
    } finally {
      setTierSaving('');
    }
  };

  const fetchModules = async (id) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${id}/modules`);
      const data = await res.json();
      setProjectModules(data || []);
    } catch(e) { console.error(e); }
  };

  const openModulesModal = async (project) => {
    setSelectedProjectForModules(project);
    setIsModuleModalOpen(true);
    await fetchModules(project.id);
  };

  const closeModulesModal = () => {
    setIsModuleModalOpen(false);
    setSelectedProjectForModules(null);
    setProjectModules([]);
    setNewModuleName('');
    setNewModulePath('');
    setEditingModuleId(null);
  };

  const handleAddModule = async () => {
    if (!newModuleName.trim()) return;
    try {
      // workspace_path is auto-generated by the backend from project + module name
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${selectedProjectForModules.id}/modules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newModuleName.trim(),
          module_type: newModuleType,
          auto_create_gitlab: autoCreateGitlab,
        })
      });
      const created = await res.json().catch(() => ({}));
      if (created?._gitlab_auto_create_error) {
        toast.error(`GitLab repo yaratilmadi: ${created._gitlab_auto_create_error}`);
      } else if (created?._gitlab_auto_created) {
        toast.success(`GitLab repo yaratildi: ${created.gitlab_url}`);
      }
      setNewModuleName('');
      setNewModulePath('');
      setNewModuleType('web');
      fetchModules(selectedProjectForModules.id);
    } catch(e) {}
  };

  // Create a GitLab repo on demand for a module that doesn't have one yet.
  const handleCreateGitlabRepo = async (moduleId) => {
    if (!window.confirm("Ushbu modul uchun GitLab'da yangi private repo yaratilsinmi?")) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}/gitlab/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),  // empty = use admin env token
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'GitLab repo yaratishda xatolik');
      toast.success(`Repo yaratildi: ${data.gitlab_url}`);
      fetchModules(selectedProjectForModules.id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUpdateModule = async (id) => {
    if (!editModuleName.trim()) return;
    try {
      // workspace_path is preserved on the backend if not sent
      await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editModuleName.trim(), module_type: editModuleType })
      });
      setEditingModuleId(null);
      fetchModules(selectedProjectForModules.id);
    } catch(e) {}
  };

  const handleDeleteModule = async (id) => {
    if (!window.confirm("Rostdan ham ushbu modulni o'chirmokchimisiz?")) return;
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${id}`, { method: 'DELETE' });
      fetchModules(selectedProjectForModules.id);
    } catch(e) {}
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (user?.role === 'admin') {
          await Promise.all([fetchTasks(), fetchProjects(), fetchAiData()]);
        } else {
          // Regular users only load their assigned projects and tasks
          const [, res] = await Promise.all([
            fetchTasks(),
            authFetch(`${process.env.REACT_APP_API_URL}/auth/my-projects`),
          ]);
          const data = await res.json();
          dispatch({ type: 'SET_PROJECTS', payload: Array.isArray(data) ? data : [] });
          if (data.length > 0) setProjectId(data[0].id.toString());
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (taskTrigger > 0) {
      fetchTasks();
    }
  }, [taskTrigger]);

  // Refresh role + section permissions from the server on load. The stored user
  // (localStorage) is stale if the admin changed access since last login, so we
  // re-read /auth/me and update only when something actually changed (no loop).
  useEffect(() => {
    if (!token) return;
    authFetch(`${process.env.REACT_APP_API_URL}/auth/me`)
      .then(r => (r.ok ? r.json() : null))
      .then(me => {
        if (!me || !me.id) return;
        const fresh = { id: me.id, username: me.username, role: me.role, allowed_sections: me.allowed_sections ?? null, ui_access: me.ui_access || {} };
        const cur = { id: user?.id, username: user?.username, role: user?.role, allowed_sections: user?.allowed_sections ?? null, ui_access: user?.ui_access || {} };
        if (JSON.stringify(fresh) !== JSON.stringify(cur)) login(token, fresh);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-admin: keep the active tab on an ALLOWED client section. Picks the first
  // allowed section on load (default is the admin 'tasks'), and redirects away if
  // the current tab isn't permitted (admin revoked it). 'noaccess' = nothing granted.
  useEffect(() => {
    if (!user || user.role === 'admin') return;
    const keys = Array.isArray(user.allowed_sections) ? user.allowed_sections : CLIENT_SECTION_KEYS;
    const allowed = (k) => keys.includes(k);
    if (!CLIENT_SECTION_KEYS.includes(activeTab) || !allowed(activeTab)) {
      setActiveTab(CLIENT_SECTION_KEYS.find(allowed) || 'noaccess');
    }
  }, [user, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // /emu popout: URL'dan (?project=) ASOSIY oynada tanlangan loyihani oladi. Busiz popout
  // o'z App nusxasini ko'taradi va boshlang'ich yuklash ro'yxatdagi BIRINCHI loyihani
  // tanlaydi → 📲/🔨 boshqa loyiha ilovasini o'rnatardi (TezHal turib viloyatga tushardi).
  // Loyihalar yuklangandan KEYIN qo'llaymiz — aks holda boshlang'ich default uni bosib ketadi.
  // 🌐 /emu oynasida brauzer qurilmalar ro'yxati — loyiha tanlangach va har 10 s (agent holati / o'chgan brauzer).
  useEffect(() => {
    if (!emulatorOnly || !token || !projectId) return undefined;
    fetchWebDevs();
    const id = setInterval(() => { if (khPageVisible()) fetchWebDevs(); }, 10000);
    return () => clearInterval(id);
  }, [emulatorOnly, token, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const emuOnlyProjApplied = useRef(false);
  useEffect(() => {
    if (!emulatorOnly || !token || emuOnlyProjApplied.current) return;
    if (!(projects || []).length) return;
    const p = new URLSearchParams(window.location.search).get('project');
    if (!p) return;
    emuOnlyProjApplied.current = true;
    selectStudioProject(p);
  }, [emulatorOnly, token, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // /db popout: URL'dan (?project=&module=) loyiha + modulni o'qib, DB ko'rinishini ochadi.
  const dbOnlyTargetRef = useRef(null);
  useEffect(() => {
    if (!dbOnly || !token) return;
    const params = new URLSearchParams(window.location.search);
    const proj = params.get('project');
    dbOnlyTargetRef.current = params.get('module');
    if (proj) selectStudioProject(proj);
    setActiveTab('studio');
    setStudioView('database'); // selectStudioProject'dan KEYIN — u studioView'ni 'test'ga qo'yadi, biz DB qilamiz
  }, [dbOnly, token]); // eslint-disable-line react-hooks/exhaustive-deps
  // Modullar yuklangach maqsad backend modulni tanlaydi (URL'dagi module, aks holda birinchi
  // backend). studioModuleId o'zgarishi yuqoridagi schema-fetch effektini ishga tushiradi.
  useEffect(() => {
    if (!dbOnly) return;
    const mods = selectedProjectModules || [];
    if (!mods.length) return;
    const tid = dbOnlyTargetRef.current;
    const target = (tid && mods.find(m => String(m.id) === String(tid)))
      || mods.find(m => m.module_type === 'backend');
    if (target) setStudioModuleId(String(target.id));
  }, [dbOnly, selectedProjectModules]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 Sirlar ko'rinishi modulga bog'liq ("Barchasi" ma'nosiz) — kirilganda
  // hech narsa tanlanmagan bo'lsa backend modulni (bo'lmasa birinchisini) tanlaymiz.
  useEffect(() => {
    if (studioView !== 'secrets' || studioModuleId) return;
    const mods = selectedProjectModules || [];
    if (!mods.length) return;
    const target = mods.find(m => m.module_type === 'backend') || mods[0];
    setStudioModuleId(String(target.id));
  }, [studioView, studioModuleId, selectedProjectModules]);

  useEffect(() => {
    if (activeTab === 'users' && user?.role === 'admin') { fetchUsers(); fetchUserLimits(); }
    if (activeTab === 'pages') fetchPages();
    if ((activeTab === 'studio' || activeTab === 'design') && abProjectId) abFetchWorkspace(abProjectId); // eslint-disable-line react-hooks/exhaustive-deps
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Studio shell is shared by two sidebar sections: 🖼️ Dizayn (canvas + compare)
  // and 🏗️ Studio (browser/emulator/test/publish/database). Keep studioView valid for
  // whichever section is active so the visible view always matches the available tabs.
  useEffect(() => {
    // 🏷 Logo endi Dizayn ichidagi tab (operator, 2026-10-04) — eski 'logo' bo'limiga kelgan yo'l shu yerga.
    if (activeTab === 'logo') { setActiveTab('design'); setStudioView('logo'); return; }
    if (activeTab === 'design' && !['canvas', 'pages', 'style', 'harmony', ...(canSee('logo') ? ['logo'] : [])].includes(studioView)) setStudioView('canvas');
    else if (activeTab === 'studio' && !['browser', 'emulator', 'qa', 'publish', 'database', 'status', 'architecture', 'map', 'docs', 'files', 'crashes', 'secrets', 'todo', 'security', 'team'].includes(studioView)) setStudioView('qa'); // eski 'test'/'findings' → 'qa' (birlashtirildi 2026-09-13)
    // Dizayn bo'limida backend modul filtrda ko'rsatilmaydi (dizayni yo'q) — boshqa
    // tabda backend tanlab qolingan bo'lsa "Barchasi"ga qaytaramiz.
    if (activeTab === 'design' && selectedStudioModule?.module_type === 'backend') { setStudioModuleId(''); setAbSelectedPageId(null); }
  }, [activeTab, studioView, studioModuleId, selectedProjectModules]); // eslint-disable-line react-hooks/exhaustive-deps

  // Logo bo'limiga kirganda: fon'da hali ishlayotgan logo jarayoni bo'lsa, uni
  // tiklaymiz (sahifa yopilgan bo'lsa ham backend davom etgan — spinner +
  // ⏹ To'xtatish tugmasi qaytadi). Tugagan/yo'q bo'lsa hech narsa o'zgarmaydi.
  useEffect(() => {
    if (activeTab !== 'design' || studioView !== 'logo' || !logoProjectId) return;
    authFetch(`${process.env.REACT_APP_API_URL}/jobs/active`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => {
        const j = (Array.isArray(list) ? list : []).find(x =>
          x.job_type === 'logo' && String(x.project_id) === String(logoProjectId) &&
          (x.status === 'running' || x.status === 'cancelling'));
        if (j) {
          setLogoGenerating(true);
          setLogoJobId(j.id);
          setLogoProgress(j.detail || `${j.done || 0}/${j.total || '?'} tayyor...`);
        }
      }).catch(() => {});
  }, [activeTab, studioView, logoProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🏷 Dizayn → Logo: logolar navbar'da tanlangan loyihaga ergashadi (o'z selektori yo'q).
  useEffect(() => {
    if (activeTab !== 'design' || studioView !== 'logo' || !abProjectId) return;
    if (String(logoProjectId) !== String(abProjectId)) { setLogoProjectId(abProjectId); setSelectedLogoIds([]); }
    fetchLogos(abProjectId);
  }, [activeTab, studioView, abProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // RELIABLE BACKSTOP: logo generatsiyasi ~2-3 daqiqa/variant — bir necha variant
  // 5-10 daqiqa. Bu uzoq oynada socket eventi yo'qolishi mumkin (tab fon'da,
  // socket uzilib-ulanishi), shunda 2-3-variant ko'rinmay qoladi. Shuning uchun
  // faol logo job'ni har 8s da poll qilamiz: tugaganda DB'dan TO'LIQ ro'yxatni
  // qayta yuklaymiz (haqiqiy manba), spinnerni o'chiramiz. Socket faqat tezkor
  // ko'rinish uchun; bu effekt natija to'liq ko'rinishini KAFOLATLAYDI.
  useEffect(() => {
    if (!logoJobId || !logoGenerating) return;
    let stopped = false;
    const tick = async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/jobs/active`);
        if (!res.ok) return;
        const list = await res.json();
        const j = (Array.isArray(list) ? list : []).find(x => x.id === logoJobId);
        if (!j || stopped) return;
        if (j.status === 'running' || j.status === 'cancelling') {
          setLogoProgress(j.detail || `${j.done || 0}/${j.total || '?'} tayyor...`);
        } else {
          stopped = true; // terminal: done | error | cancelled
          await fetchLogos(logoProjectIdRef.current); // DB = manba: barcha variantlar ko'rinadi
          setLogoGenerating(false);
          setLogoJobId(null);
          setLogoProgress('');
          if (j.status === 'done') toast.success(`✅ ${j.done} ta logo tayyor`);
          else if (j.status === 'cancelled') toast(`⏹ To'xtatildi — ${j.done} ta variant saqlandi`);
          else toast.error(j.detail || 'Logo yaratishda xato');
        }
      } catch (e) {}
    };
    const t = setInterval(() => { if (khPageVisible()) tick(); }, 8000);
    tick();
    return () => { stopped = true; clearInterval(t); };
  }, [logoJobId, logoGenerating]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🏷 Logoni joylash: Dizayn ochilganda fon'da ishlayotgan jarayonni tiklaymiz...
  useEffect(() => {
    if (activeTab !== 'design' || !abProjectId) return;
    authFetch(`${process.env.REACT_APP_API_URL}/jobs/active`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => {
        const j = (Array.isArray(list) ? list : []).find(x =>
          x.job_type === 'design_logo' && String(x.project_id) === String(abProjectId) &&
          (x.status === 'running' || x.status === 'cancelling'));
        if (j) { setAbLogoJobId(j.id); setAbLogoProgress(j.detail || `${j.done || 0}/${j.total || '?'} sahifa...`); }
      }).catch(() => {});
  }, [activeTab, abProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ...va socket eventi yo'qolsa ham (tab fon'da) tugaganini /jobs/active dan bilib,
  // sahifalarni DB'dan qayta yuklaymiz.
  useEffect(() => {
    if (!abLogoJobId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/jobs/active`);
        if (!res.ok || stopped) return;
        const list = await res.json();
        const j = (Array.isArray(list) ? list : []).find(x => x.id === abLogoJobId);
        if (stopped) return;
        if (j && (j.status === 'running' || j.status === 'cancelling')) { if (j.detail) setAbLogoProgress(j.detail); return; }
        stopped = true;
        setAbLogoJobId(null);
        setAbLogoProgress('');
        if (abProjectIdRef.current) abFetchWorkspace(abProjectIdRef.current);
      } catch (e) {}
    };
    const t = setInterval(() => { if (khPageVisible()) tick(); }, 10000);
    return () => { stopped = true; clearInterval(t); };
  }, [abLogoJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Studio compare view: (re)load real pages when entering it or switching module
  useEffect(() => {
    if ((activeTab === 'studio' || activeTab === 'design') && studioView === 'pages' && abProjectId) {
      fetchPages(abProjectId, studioModuleId);
    }
    // 🌐 Brauzer: kirganda test saytni ochamiz + tezkor sahifa linklari uchun
    // real-page skanini yuklaymiz (loyihaning web moduli bo'yicha).
    if (activeTab === 'studio' && studioView === 'browser' && browserModule?.test_url) {
      const base = browserModule.test_url.replace(/\/+$/, '');
      const path = browserPath && browserPath.startsWith('/') ? browserPath : '/';
      setBrowserFrameUrl(base + path);
      if (abProjectId) fetchPages(abProjectId, String(browserModule.id));
    }
  }, [studioView, studioModuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dizayn canvas: load the real built pages so each design node shows a ✅/○ "haqiqiy
  // bor" badge and the inspector can compare + link to the backend. (The backend DB ER
  // diagram moved to Studio → 🗄️ Database — see the database effect below.)
  useEffect(() => {
    if ((activeTab === 'studio' || activeTab === 'design') && studioView === 'canvas') {
      if (abProjectId) fetchPages(abProjectId, studioModuleId);
    }
  }, [studioView, studioModuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Studio → 🗄️ Database view: read the resolved backend module's live test-env DB schema.
  useEffect(() => {
    if (activeTab === 'studio' && studioView === 'database' && dbModule) {
      fetchDbSchema(String(dbModule.id));
    }
  }, [studioView, studioModuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Studio emulator view lifecycle: load devices/APK on enter, stop the stream on leave
  // (the emulator is a single per-user resource — don't leak frames after navigating away).
  useEffect(() => {
    // Also drive this on the standalone pop-out emulator window (/emulators) so it
    // auto-loads + streams the user's emulators even though it has no Studio tabs.
    if (emulatorOnly || (activeTab === 'studio' && studioView === 'emulator')) {
      fetchEmulatorDevices();
      // Asosiy Emulator tab endi emulator KATAKLARINI ko'rsatmaydi (faqat alohida oynada) —
      // shu sabab bu tabda OQIM ham ketmaydi (autostream faqat popout=emulatorOnly'da). Ro'yxat
      // baribir yuklanadi (soni/holati/biriktirish uchun), lekin stream YO'Q → tejamli.
      fetchEmuList(emulatorOnly); // popout: auto-stream; asosiy tab: faqat ro'yxat (streamsiz)
      fetchProjectEmuBinding(projectId); // tanlangan loyiha qaysi emulatorga biriktirilgan
      fetchEmuBindings();                // har emulator qaysi loyihaniki (katak yorlig'i + 📲 manbasi)
      if (activeMobileModule?.workspace_path) fetchApkInfo(activeMobileModule.workspace_path);
      // Re-poll the list so a transient fetch failure (API event-loop freeze) self-heals
      // instead of leaving an empty grid, and running/stopped status stays fresh.
      const pollId = setInterval(() => { if (khPageVisible()) fetchEmuList(false); }, 9000);
      return () => clearInterval(pollId);
    } else {
      if (isStreamingRef.current) stopStream();
      // leaving the view → stop ALL grid streams (don't leak frames / 3rd-server CPU)
      socketRef.current?.emit('emulator:stop_stream');
      setEmuStreaming({});
      setEmuFullscreen([]); // ko'rinishdan chiqishda fullscreen overlay'ni ham yop
    }
  }, [studioView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Popout (/emu?slot=N): fokuslangan emulator ishlab tursa — avtomatik to'liq ekran
  // (oyna aynan shu emulator uchun ochilgan). Bir marta (emuFullscreen bo'sh bo'lsa).
  useEffect(() => {
    if (!emulatorOnly || popoutSlot == null) return;
    const emu = (emuList || []).find(e => e.slot === popoutSlot);
    if (!emu) return;
    const st = emuStatuses[popoutSlot] || emu.status;
    if (st === 'running' && emuFullscreen.length === 0) emuAddFullscreen(popoutSlot);
  }, [emulatorOnly, popoutSlot, emuList, emuStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin 📱 Emulator modal: load the grid (+ auto-stream running ones) when it opens.
  useEffect(() => {
    if (!showEmulator) return;
    fetchEmuList(true);
    fetchEmuBindings();
    const pollId = setInterval(() => { if (khPageVisible()) fetchEmuList(false); }, 9000);
    return () => clearInterval(pollId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmulator]);

  useEffect(() => {
    fetchSelectedProjectModules(projectId);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // API-manzil tanlovi loyiha bo'yicha eslab qolinadi (har build'da qayta tanlamaslik uchun).
  useEffect(() => {
    if (!projectId) return;
    const v = localStorage.getItem(`kh_api_target_${projectId}`);
    // Saqlangan tanlov bo'lmasa — 🧪 TEST (emulator = sinov joyi, prod'ga tegmasin).
    setApiTarget(['code', 'test', 'production'].includes(v) ? v : 'test');
  }, [projectId]);
  const changeApiTarget = (v) => {
    setApiTarget(v);
    if (projectId) localStorage.setItem(`kh_api_target_${projectId}`, v);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isStreamingRef.current && lastFrameTime) {
        const frozen = Date.now() - lastFrameTime > 3000;
        setStreamFrozen(frozen);
        // Auto-restart stream if frozen >8s (silent, no user action needed)
        if (Date.now() - lastFrameTime > 8000) {
          socketRef.current?.emit('emulator:start_stream');
          setLastFrameTime(Date.now()); // reset timer to avoid spamming
        }
      } else {
        setStreamFrozen(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lastFrameTime]);

  const addImagesToQueue = (files) => {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
    const items = valid.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setNewTaskImages(prev => [...prev, ...items]);
  };

  const handleTaskImagePaste = (e) => {
    const files = Array.from(e.clipboardData.items)
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile());
    if (files.length) addImagesToQueue(files);
  };

  const pasteImageTo = (setter) => (e) => {
    const file = Array.from(e.clipboardData.items)
      .find(i => i.type.startsWith('image/'))
      ?.getAsFile();
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setter({ base64: ev.target.result.split(',')[1], name: 'clipboard.png' });
    reader.readAsDataURL(file);
  };

  const uploadImageFile = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await authFetch(`${process.env.REACT_APP_API_URL}/upload`, { method: 'POST', body: formData });
    if (!res.ok) return null;
    const data = await res.json();
    return data.imageUrl;
  };

  // Decode a data: URL → Blob WITHOUT fetch() — the production CSP `connect-src`
  // blocks fetch('data:...'), which silently broke design uploads.
  const dataURLtoBlob = (dataURL) => {
    const [head, b64] = dataURL.split(',');
    const mime = (head.match(/data:([^;]+)/) || [, 'image/png'])[1];
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  // Ilova fayllarni serverga yuklaydi (rasm emas — /upload-file, tur bo'yicha filtr serverda).
  const addDocsToTask = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingDoc(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/upload-file`, { method: 'POST', body: fd });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { alert(d.error || `${file.name}: yuklanmadi`); continue; }
        setNewTaskFiles(prev => [...prev, { path: d.path, name: d.name, size: d.size }]);
      }
    } catch (e) { alert('Fayl yuklanmadi — qayta urinib ko\'ring'); }
    finally { setUploadingDoc(false); }
  };

  // 📝 B3 (2026-10-04): 💬 Suhbatdagi Boshqaruvchi vazifani o'zi yaratmaydi — qoralamani shu formaga qo'yadi
  // (draft_task). Matn operatorniki bo'lib qoladi: tekshiradi, rasm/fayl qo'shadi, «Yuborish»ni o'zi bosadi.
  const applyTaskDraft = (d) => {
    if (!d || !d.instruction) return;
    const cur = (newTask || '').trim();
    if (cur && cur !== String(d.instruction).trim()
      && !window.confirm("Formada yozilgan matn bor — Boshqaruvchi qoralamasi bilan almashtirilsinmi?")) return;
    const pid = d.project_id ? String(d.project_id) : '';
    if (pid && String(abProjectId) !== pid) selectStudioProject(pid);
    setNewTask(String(d.instruction));
    if (typeof d.plan_review === 'boolean') setPlanReview(d.plan_review);
    if (typeof d.auto_deploy === 'boolean') setAutoDeploy(d.auto_deploy);
    setSelectedDesignIds(Array.isArray(d.design_page_ids) ? d.design_page_ids : []);
    setSelectedLogoIds(Array.isArray(d.logo_ids) ? d.logo_ids : []);
    toast.success("📝 Qoralama formaga qo'yildi — tekshirib «Yuborish»ni bosing", { duration: 5000 });
  };

  // overrideText — 💬 suhbatdagi «🚀 Vazifa qilib yuborish» (agent tuzgan matn; biriktirmalar formadagi holicha).
  const handleAddTask = async (overrideText) => {
    const taskText = (typeof overrideText === 'string' ? overrideText : newTask).trim();
    if (!taskText || !projectId) return;
    if (addingTask) return; // ikki marta bosishni bloklash (dublikat vazifa oldini olish)
    setAddingTask(true);

    try {
      const urls = [];
      // 1. Manually attached images
      for (const { file } of newTaskImages) {
        const u = await uploadImageFile(file);
        if (u) urls.push(u);
      }
      // 2. Selected App Builder designs → rasterize SVG → PNG → upload (visual spec for aider)
      for (const id of selectedDesignIds) {
        const page = abPages.find(p => p.id === id);
        if (!page?.svg_content) continue;
        const vb = page.svg_content.match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
        const w = vb ? Math.round(parseFloat(vb[1])) : 390;
        const h = vb ? Math.round(parseFloat(vb[2])) : 844;
        try {
          const dataUrl = await svgToPngDataUrl(page.svg_content, w, h);
          const blob = dataURLtoBlob(dataUrl);
          const file = new File([blob], `design-${id}.png`, { type: 'image/png' });
          const u = await uploadImageFile(file);
          if (u) urls.push(u);
        } catch (e) { /* skip a design that fails to rasterize */ }
      }
      // 3. Selected logos (Logo dizayn section) → rasterize SVG → PNG → upload
      for (const id of selectedLogoIds) {
        const lg = logos.find(l => l.id === id);
        if (!lg?.svg_content) continue;
        const vb = lg.svg_content.match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
        const w = vb ? Math.round(parseFloat(vb[1])) : 512;
        const h = vb ? Math.round(parseFloat(vb[2])) : 512;
        try {
          const dataUrl = await svgToPngDataUrl(lg.svg_content, w, h);
          const blob = dataURLtoBlob(dataUrl);
          const file = new File([blob], `logo-${id}.png`, { type: 'image/png' });
          const u = await uploadImageFile(file);
          if (u) urls.push(u);
        } catch (e) { /* skip a logo that fails to rasterize */ }
      }
      const image_url = urls.length === 0 ? null : urls.length === 1 ? urls[0] : JSON.stringify(urls);

      const res = await authFetch(`${process.env.REACT_APP_API_URL}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          instruction: taskText,
          task_type: taskType,
          requires_build: requiresBuild,
          image_url: image_url,
          attachments: newTaskFiles.length ? JSON.stringify(newTaskFiles) : null,
          new_session: newSessionMode,
          // run_mode YUBORILMAYDI — engine o'zi hal qiladi (yuqoridagi izohga qarang).
          auto_deploy: autoDeploy,
          plan_review: planReview,
          after_test: afterTest
        })
      });
      // authFetch faqat 401'da throw qiladi — 500/502/timeout'da res qaytadi. Faqat
      // res.ok bo'lsa formani tozalaymiz, aks holda matn saqlanib qoladi (qayta yuborish uchun).
      if (!res.ok) {
        try { toast.error("Vazifa qo'shilmadi — matn saqlanib qoldi, qayta yuboring"); } catch (e) {}
        return;
      }
      // 🗂 B2: formadan berilgan vazifa 💬 Suhbat lentasiga ham karta bo'lib tushadi (joriy Boshqaruvchi
      // suhbati bo'lsa; ruxsat/egalikni server tekshiradi — bo'lmasa jim o'tadi).
      try {
        const created = await res.json().catch(() => ({}));
        const ctid = Number(localStorage.getItem('kh_conductor_thread')) || 0;
        if (ctid && created && created.id) {
          authFetch(`${process.env.REACT_APP_API_URL}/conductor/threads/${ctid}/events`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind: 'task', taskId: created.id, source: 'form' }),
          }).catch(() => {});
        }
      } catch (e) {}
      setNewTask('');
      setNewTaskImages([]);
      setRefineMsgs([]); setRefineSession(null); setRefineOpen(false); setRefineInput(''); setRefineActivity([]); chatUploadCache.current.clear();
      setNewTaskFiles([]);
      setSelectedDesignIds([]);
      setSelectedLogoIds([]);
      setRequiresBuild(false);
      setNewSessionMode(false);
      setAfterTest(false);
      // Navbat rejimi + autoDeploy ATAYIN reset qilinmaydi — foydalanuvchi ko'p vazifani
      // bir xil rejimda ketma-ket berishi mumkin (masalan "ketma-ket + avto-deploy" yoqib
      // bir nechta vazifa tashlaydi). Rejim tanlovi keyingi vazifa uchun saqlanadi.
      try { toast.success("Vazifa qo'shildi"); } catch (e) {}
      fetchTasks();
    } catch (error) {
      console.error('Error adding task:', error);
      try { toast.error("Xato — matn saqlanib qoldi, qayta yuboring"); } catch (e) {}
    } finally {
      setAddingTask(false);
    }
  };

  // Live "Kontekst oynasi" meter: per-module Claude Code session usage for the selected
  // project. Refetched on project change + polled while the task panel is open (sessions
  // only change when a developer task finishes, so 15s is plenty).
  const fetchContextSessions = async (pid) => {
    if (!pid) { setContextSessions([]); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/token-usage/context/${pid}`);
      if (!res.ok) return;
      const data = await res.json();
      setContextSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (e) { /* keep last known */ }
  };
  useEffect(() => {
    if (!projectId) { setContextSessions([]); return; }
    fetchContextSessions(projectId);
    const t = setInterval(() => { if (khPageVisible()) fetchContextSessions(projectId); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // ---------- Project CRUD handlers ----------
  const handleAddProject = async () => {
    // Validate input
    if (!newProjectName.trim()) {
      setProjectError("Loyiha nomi bo'sh bo'lishi mumkin emas");
      return;
    }
    
    // Prepare data and update UI state
    const projectData = {
      name: newProjectName.trim(),
      description: newProjectDescription.trim(),
    };
    
    setIsSubmittingProject(true);
    setProjectError('');
    
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(response);
        throw new Error(errorMsg);
      }
      
      const created = await response.json();
      
      // Batch state updates to minimize re-renders
      dispatch({ type: 'ADD_PROJECT', payload: created });
      setProjectId(created.id.toString());
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectRemoteHost('');
      setNewProjectSshUser('');
      setNewProjectDeployPath('');
      setNewProjectRestartCmd('');
      
    } catch (error) {
      console.error('Project creation error:', error);
      setProjectError('Loyiha qo\'shilmadi: ' + (error.message || 'Tarmoq xatosi yuz berdi'));
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleUpdateProject = async (id, name, description) => {
    const safeName = (name || '').trim();
    const safeDescription = (description || '').trim();

    if (!safeName) { setEditError("Loyiha nomi bo'sh bo'lishi mumkin emas"); return; }
    if (safeName.length > 255) { setEditError("Loyiha nomi 255 belgidan oshmasligi kerak"); return; }

    const original = projects.find(p => String(p.id) === String(id));
    if (original && original.name === safeName && (original.description || '') === safeDescription) {
      setEditingProject(null); setEditError(''); return;
    }

    const projectData = { name: safeName, description: safeDescription };

    setUpdatingProjectId(id);
    setEditError('');
    setProjectListError('');

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData)
      });
      
      if (!response.ok) {
        const errorMsg = await extractErrorMessage(response);
        throw new Error(errorMsg);
      }

      // Try to use the server's response as the source of truth.
      // Fall back to our local data if the body is empty or unparseable.
      let updatedProject = { id, ...projectData };
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const body = await response.json();
          if (body && typeof body === 'object') {
            updatedProject = { ...updatedProject, ...body };
          }
        }
      } catch (parseErr) {
        console.warn('Could not parse update response body:', parseErr);
      }

      // Update state only after successful API call
      dispatch({
        type: 'UPDATE_PROJECT',
        payload: updatedProject
      });

      setEditingProject(null);
      setEditError('');
      
    } catch (error) {
      console.error('Update project error:', error);
      // Keep the edit form open and show the error inline so the user can fix it
      setEditError('Loyihani yangilashda xatolik: ' + (error.message || 'Tarmoq xatosi'));
    } finally {
      setUpdatingProjectId(null);
    }
  };

  const handleDeleteProject = async (id) => {
    // Type-to-confirm — loyiha nomini aynan yozmasa o'chmaydi (tasodifiy o'chirishni oldini oladi).
    const project = projects.find(p => String(p.id) === String(id));
    const projectName = project ? project.name : '';
    if (!projectName) return;
    const typed = window.prompt(
      `⚠️ DIQQAT: "${projectName}" loyihasi Khorezmai'dan o'chiriladi. Bu amalni BEKOR QILIB BO'LMAYDI.\n\n` +
      `O'CHADI (Khorezmai bazasi): modullar, vazifalar, dizaynlar, logolar, Reja, crashlar, topilmalar, a'zoliklar va modul SIRLARI (.env qiymatlari).\n\n` +
      `O'CHADI (server): modullarning TEST MUHITLARI — konteynerlar, test bazasi, subdomen va sertifikat.\n\n` +
      `O'CHMAYDI: GitLab repolar (kod GitLab'da qoladi), serverdagi workspace papkalar, mobil build papkasi (keystore), production server, emulatorlar.\n\n` +
      `Tasdiqlash uchun loyiha nomini aynan yozing:\n${projectName}`
    );
    if (typed === null) return;                    // Bekor qilindi
    if (typed.trim() !== projectName) {
      window.alert(`Nom mos kelmadi ("${typed.trim()}" ≠ "${projectName}") — o'chirish bekor qilindi.`);
      return;
    }

    setDeletingProjectId(id);
    setProjectListError('');

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorMsg = await extractErrorMessage(response);
        throw new Error(errorMsg);
      }
      // Backend test muhitlarini ham tozalaydi (konteyner + DB + subdomen) — natijani aytamiz,
      // aks holda operator «konteynerlar qoldimi?» deb bilmaydi. GitLab/workspace qoladi.
      const result = await response.json().catch(() => ({}));
      const td = Array.isArray(result.teardown) ? result.teardown : [];
      const tdOk = td.filter(t => t.ok).length, tdBad = td.length - tdOk;
      toast.success(`✅ «${projectName}» o'chirildi${td.length ? ` · 🧹 ${tdOk} ta test muhiti tozalandi` : ''}${tdBad ? ` · ⚠️ ${tdBad} tasi tozalanmadi (log)` : ''} · GitLab repo va workspace qoldi`, { duration: 7000 });
      
      // Handle state updates after successful API call
      if (String(projectId) === String(id)) {
        // Find the next project to select
        const remaining = projects.filter(p => String(p.id) !== String(id));
        const next = remaining.length > 0 ? remaining[0] : null;
        setProjectId(next ? next.id.toString() : '');
      }
      
      // If we were editing the deleted project, close the editor
      if (editingProject && String(editingProject.id) === String(id)) {
        setEditingProject(null);
        setEditError('');
      }

      // Update projects list
      dispatch({ type: 'DELETE_PROJECT', payload: id });
      
    } catch (error) {
      console.error('Delete project error:', error);
      setProjectListError('Loyihani o\'chirishda xatolik: ' + (error.message || 'Tarmoq xatosi'));
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Cancel editing: close form and clear any inline errors
  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditError('');
  };

  // Keyboard handling for edit inputs (Enter = save, Escape = cancel)
  const handleEditKeyDown = (e, proj) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (updatingProjectId !== proj.id && editingProject?.name?.trim()) {
        handleUpdateProject(
          proj.id,
          editingProject.name,
          editingProject.description
        );
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // (status stuff remains exactly as before)
  const handleStatusChange = (id, newStatus) => {
    setTasks(prev =>
      prev.map(task =>
        task.id === id ? { ...task, status: newStatus } : task
      )
    );
  };

  const statusColors = {
    pending_architecture: 'bg-purple-200 dark:bg-purple-800/60 text-purple-900 dark:text-purple-200',
    pending: 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-900 dark:text-yellow-200',
    completed: 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-200',
    'in_progress': 'bg-blue-200 dark:bg-blue-800/60 text-blue-900 dark:text-blue-200',
    failed: 'bg-red-200 dark:bg-red-800/60 text-red-900 dark:text-red-200',
    cancelled: 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300',
    awaiting_clarification: 'bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-200',
    awaiting_plan_approval: 'bg-indigo-200 dark:bg-indigo-800/60 text-indigo-900 dark:text-indigo-200',
    awaiting_owner_approval: 'bg-violet-200 dark:bg-violet-800/60 text-violet-900 dark:text-violet-200',
  };

  const statusLabels = {
    pending_architecture: 'Rejalashtirilmoqda',
    pending: 'Kutilmoqda',
    completed: 'Bajarildi',
    'in_progress': 'Jarayonda',
    failed: 'Xatolik',
    cancelled: "To'xtatilgan",
    awaiting_clarification: '❓ Aniqlik kerak',
    awaiting_plan_approval: '📝 Reja tasdiq kutmoqda',
    awaiting_owner_approval: '🙋 Ruxsat kutmoqda',
  };

  const isActiveStatus = (status) => status === 'in_progress' || status === 'pending_architecture';

  // ---------- AI Models & Config Handlers ----------
  // Multi-emulator GRID: all the user's emulators (up to 3) side by side, each with its
  // own live screen + start/stop/reconnect + touch/keys. Build/Install targets emulator 1.
  const emuStatusBadge = (st) => ({
    running:      <span className="text-green-600 dark:text-green-400 text-xs font-semibold">● Ishlayapti</span>,
    starting:     <span className="text-yellow-600 dark:text-yellow-400 text-xs font-semibold animate-pulse">● Ishga tushmoqda</span>,
    reconnecting: <span className="text-yellow-600 dark:text-yellow-400 text-xs font-semibold animate-pulse">● Qayta ulanmoqda</span>,
    stopping:     <span className="text-gray-500 dark:text-gray-400 text-xs font-semibold">● To'xtatilmoqda</span>,
    stopped:      <span className="text-gray-400 text-xs font-semibold">● To'xtatilgan</span>,
    error:        <span className="text-red-500 text-xs font-semibold">● Xatolik</span>,
    not_created:  <span className="text-gray-400 text-xs font-semibold">● Yaratilmagan</span>,
  }[st] || <span className="text-gray-400 text-xs font-semibold">● —</span>);

  const renderEmulatorGrid = (onClose, onlySlot = null) => {
    const used = new Set((emuList || []).map(e => e.slot));
    let canAddSlot = -1;
    for (let s = 0; s < emuMaxSlots; s++) { if (!used.has(s)) { canAddSlot = s; break; } }
    const selProj = projects.find(p => String(p.id) === String(projectId));
    // Emulator KATAKLARI faqat alohida oynada (emulatorOnly) yoki admin modalda (onClose) ko'rinadi.
    // Asosiy Studio Emulator tab (ikkalasi ham yo'q) endi faqat boshqaruv + "alohida oynada ochish".
    const showCells = emulatorOnly || !!onClose;
    return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-5 max-h-screen overflow-y-auto">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="font-bold text-gray-900 dark:text-white">📱 Emulatorlar <span className="text-xs font-normal text-gray-400">({(emuList || []).length}/{emuMaxSlots})</span></h2>
        <div className="flex items-center gap-2 flex-wrap">
          {mobileModules.length > 1 && (
            <select
              value={activeMobileModule?.id || ''}
              onChange={e => selectMobileModule(e.target.value)}
              title="Loyihada bir nechta mobil ilova bor — qaysi biri build qilinadi / o'rnatiladi / yuklab olinadi"
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 focus:outline-none">
              {mobileModules.map(m => <option key={m.id} value={m.id}>📱 {m.name}</option>)}
            </select>
          )}
          <select
            value={apiTarget}
            onChange={e => changeApiTarget(e.target.value)}
            title="Ilova QAYSI backendga ulanishi: 'Koddagidek' — repodagi BASE_URL o'zgarmaydi; 'Test' / 'Production' — build oldidan BASE_URL almashtiriladi (repoga commit qilinmaydi)"
            className={`border rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 focus:outline-none ${apiTarget === 'production' ? 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-400' : apiTarget === 'test' ? 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}
          >
            <option value="code">🔗 API: koddagidek</option>
            <option value="test">🧪 API: test env</option>
            <option value="production">🚀 API: production</option>
          </select>
          <button
            onClick={handleStudioMobileBuild}
            disabled={serverRunning}
            title="Repodan APK build qilib BARCHA ishlab turgan emulatorlarga o'rnatadi (~4 daqiqa)"
            className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 rounded-lg font-medium transition"
          >{serverRunning ? '⏳ Build...' : '🔨 Build + Install'}</button>
          {/* 📥 Tayyor APK'ni FAYL qilib yuklab olish — haqiqiy telefonga (yoki mijozga)
              berish uchun. Ilgari bu tugma faqat ADMIN modul sahifasida bor edi,
              shuning uchun klient uni umuman topa olmasdi. */}
          <button
            onClick={downloadApkFile}
            title="So'nggi build qilingan APK faylni yuklab olish — haqiqiy telefonga o'rnatish yoki mijozga yuborish uchun"
            className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition">
            📥 APK yuklab olish
          </button>
          {apkInfo?.hasApk && (
            <button onClick={installApk} disabled={isInstalling}
              className="px-3 py-1.5 text-sm bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50 rounded-lg font-medium transition">
              {isInstalling ? '⏳ O\'rnatish...' : '📦 Emulatorga o\'rnatish'}
            </button>
          )}
          {emulatorOnly && emuAwakeBtn(false)}
          {showCells && canAddSlot >= 0 && (
            <button onClick={addEmulator}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-medium transition">
              ➕ Yangi emulator
            </button>
          )}
          {showCells && onlySlot == null && ((emuList || []).filter(e => (emuStatuses[e.slot] || e.status) === 'running').length + ((webDevs && webDevs.devices) || []).filter(d => d.running).length) > 1 && (
            <button
              onClick={emuFullscreenAllRunning}
              title="Ishlab turgan BARCHA emulatorni birdan to'liq ekranda yonma-yon ochish"
              className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 rounded-lg font-medium transition">
              ⛶ Hammasini to'liq
            </button>
          )}
          {!emulatorOnly && (
            <button
              onClick={() => window.open(`/emu${projectId ? `?project=${projectId}` : ''}`, 'kh_emulators', 'width=1300,height=1000,noopener')}
              title="Emulatorlarni alohida oynada ochish — barcha emulator per-katak ko'rinishida; DB / Vazifalar bilan yonma-yon ishlash uchun (oynani kattalashtirsangiz emulatorlar ham kattalashadi)"
              className="px-3 py-1.5 text-sm bg-gray-700 text-white hover:bg-gray-800 rounded-lg font-medium transition">
              🪟 Emulatorlarni ochish
            </button>
          )}
          {onClose && <button onClick={onClose} className="ml-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl font-bold leading-none">×</button>}
        </div>
      </div>

      {selProj && mobileModules.length > 1 && activeMobileModule && (
        <div className="mb-2 flex items-center gap-2 flex-wrap bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 rounded-lg px-3 py-2">
          <span className="text-sm text-primary-900 dark:text-primary-200 font-medium">📌 «<span translate="no">{activeMobileModule.name}</span>» ilovasi:</span>
          <select
            value={moduleEmuSlot == null ? '' : moduleEmuSlot}
            onChange={e => setModuleEmuBinding(e.target.value === '' ? null : Number(e.target.value))}
            title="Shu ilovani alohida emulatorga biriktiring — build+install, sinov va 📲 shu ilova uchun aynan o'sha emulatorda. Loyihadagi har ilova o'z emulatorida ishlaydi."
            className="border border-primary-200 dark:border-primary-800 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 focus:outline-none"
          >
            <option value="">Loyiha bo'yicha{projectEmuSlot == null ? '' : ` (${projectEmuSlot + 1}-emulator)`}</option>
            {Array.from({ length: emuMaxSlots }, (_, s) => <option key={s} value={s}>{s + 1}-emulator</option>)}
          </select>
          <span className="text-xs text-primary-500">
            {moduleEmuSlot != null ? `«${activeMobileModule.name}» faqat ${moduleEmuSlot + 1}-emulatorga` : projectEmuSlot != null ? `loyiha biriktiruvi: ${projectEmuSlot + 1}-emulator` : 'loyiha biriktirilmagan — barcha ishlab turganlarga'}
          </span>
        </div>
      )}
      {selProj && (
        <div className="mb-3 flex items-center gap-2 flex-wrap bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 rounded-lg px-3 py-2">
          <span className="text-sm text-primary-900 dark:text-primary-200 font-medium">📌 «{selProj.name}» loyihasi{mobileModules.length > 1 ? ' (o\'z emulatori yo\'q ilovalar)' : ''}:</span>
          <select
            value={projectEmuSlot == null ? '' : projectEmuSlot}
            onChange={e => setProjectEmuBinding(e.target.value === '' ? null : Number(e.target.value))}
            title="Loyihani bitta emulatorga biriktiring — build+install faqat o'shanga o'rnatiladi (ko'p loyiha bilan chalkashmaslik uchun)"
            className="border border-primary-200 dark:border-primary-800 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 focus:outline-none"
          >
            <option value="">Biriktirilmagan (barchasiga)</option>
            {Array.from({ length: emuMaxSlots }, (_, s) => <option key={s} value={s}>{s + 1}-emulator</option>)}
          </select>
          <span className="text-xs text-primary-500">
            {projectEmuSlot == null ? 'build+install barcha ishlab turgan emulatorlarga' : `build+install faqat ${projectEmuSlot + 1}-emulatorga`}
          </span>
        </div>
      )}
      {selProj && emuTestSlots && (
        <div className="mb-3 flex items-center gap-1.5 flex-wrap bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900 rounded-lg px-3 py-2" data-kh="emu-test-slots">
          <span className="text-sm text-emerald-900 dark:text-emerald-200 font-medium mr-1"
            title="🧠 Agent, 🧑‍💼 Nazoratchi va agent sinovi shu loyiha uchun FAQAT shu emulatorlarni ishlatadi. 📌 biriktiruv (APK qayerga o'rnatiladi) bundan alohida. Hech biri tanlanmasa — cheklanmagan.">🧪 Sinov emulatorlari:</span>
          {Array.from({ length: Math.max(emuMaxSlots, emuTestSlots.maxSlots || 0) }, (_, s) => {
            const on = (emuTestSlots.slots || []).includes(s);
            const lent = emuTestSlots.borrow && (emuTestSlots.borrow.slots || []).includes(s);
            const other = (emuTestSlots.others || []).filter(o => (o.slots || []).includes(s)).map(o => o.name || `#${o.projectId}`);
            return (
              <button key={s} type="button" onClick={() => toggleEmuTestSlot(s)}
                title={`${on ? 'Bosilsa olib tashlanadi' : "Bosilsa qo'shiladi"}${lent ? ` · vaqtincha olingan — ${(() => { const d = new Date(emuTestSlots.borrow.until); const p2 = (n) => String(n).padStart(2, '0'); return `${p2(d.getDate())}.${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`; })()} da o'zi qaytadi` : ''}${other.length ? ` · ${other.join(', ')} loyihasiga ham ajratilgan` : ''}`}
                className={`px-2 py-0.5 rounded-md text-xs font-semibold border transition ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                E{s + 1}{lent ? ' ⏳' : ''}{!on && other.length ? <span className="ml-0.5 font-normal opacity-70" translate="no">·{other[0].slice(0, 8)}</span> : null}
              </button>
            );
          })}
          <span className="text-xs text-emerald-700 dark:text-emerald-400 ml-1">
            {(emuTestSlots.slots || []).length ? `agent va sinov faqat ${(emuTestSlots.slots || []).map(x => `E${x + 1}`).join(', ')} da` : 'cheklanmagan — istalgan emulator'}
          </span>
        </div>
      )}

      <EmuCompatCard authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL}
        moduleId={activeMobileModule?.id}
        moduleName={activeMobileModule?.name} />

      <ExpoQrCard authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={projectId} apiTarget={apiTarget} />

      {!showCells ? (
        <div className="text-center py-10">
          <p className="text-5xl mb-3">📱🪟</p>
          <p className="text-gray-700 dark:text-gray-300 mb-1 font-semibold">Emulatorlar alohida oynada ochiladi</p>
          <p className="text-xs text-gray-400 mb-4">Boshqa ish (DB / Vazifalar) bilan yonma-yon + tejamli — bu tabda oqim ketmaydi</p>
          <button onClick={() => window.open(`/emu${projectId ? `?project=${projectId}` : ''}`, 'kh_emulators', 'width=1300,height=1000,noopener')}
            className="px-5 py-2.5 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800 transition">🪟 Emulatorlarni ochish</button>
          <p className="text-xs text-gray-400 mt-3">{(emuList || []).length} ta emulator · {(emuList || []).filter(e => (emuStatuses[e.slot] || e.status) === 'running').length} ta ishlab turibdi</p>
        </div>
      ) : (emuList || []).length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-4xl mb-2">📱</p>
          <p className="text-sm">Emulator yo'q — <button onClick={addEmulator} className="text-emerald-600 dark:text-emerald-400 underline font-medium">birinchisini ishga tushiring</button></p>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center gap-4">
          {[...(emuList || [])].filter(e => onlySlot == null || e.slot === onlySlot).sort((a, b) => a.slot - b.slot).map(emu => {
            const slot = emu.slot;
            const st = emuStatuses[slot] || emu.status || 'not_created';
            const frame = emuFrames[slot];
            const busy = !!emuBusy[slot];
            const running = st === 'running';
            const bound = emuBindings[slot]; // shu emulator qaysi loyihaniki (📌)
            return (
              <div key={slot} className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col items-center" style={{ width: 'clamp(260px, 31vw, 480px)' }}>
                <div className="flex items-center justify-between w-full mb-1.5">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    Emulator {slot + 1}
                    {emuAgent[slot]?.active && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${emuAgent[slot].paused ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' : 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-400 animate-pulse'}`}
                        title={`${emuAgent[slot].paused ? 'Agent to\'xtatilgan — boshqaruv sizda' : 'Agent ishlayapti'}${khAgentWho(emuAgent[slot]) ? ' — ' + khAgentWho(emuAgent[slot]) : ''}`}>
                        {emuAgent[slot].paused ? '⏸ Siz' : '🤖 Agent'}
                      </span>
                    )}
                    {bound ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 font-medium"
                        title={`«${bound.projectName}${bound.perModule && bound.moduleName ? ' · ' + bound.moduleName : ''}» shu emulatorga biriktirilgan — 📲 aynan shu ilovani o'rnatadi`}>📌 {bound.projectName}{bound.perModule && bound.moduleName ? ` · ${bound.moduleName}` : ''}</span>
                    ) : selProj && projectEmuSlot === slot && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 font-medium" title={`«${selProj.name}» shu emulatorga biriktirilgan`}>📌</span>
                    )}
                  </span>
                  {emuStatusBadge(st)}
                </div>
                <select
                  value={emu.device_key || 'standart'}
                  disabled={running || busy}
                  title={running ? "Turini o'zgartirish uchun avval to'xtating" : 'Telefon turi (Android versiyasi + ekran)'}
                  onChange={e => setEmuDevice(slot, e.target.value)}
                  className="w-full mb-1.5 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 text-xs bg-white dark:bg-gray-800 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {(emuPresets.length ? emuPresets : [{ key: 'standart', label: 'Standart', android: '11', size: '720×1280' }]).map(p => (
                    <option key={p.key} value={p.key}>📱 {p.label} · A{p.android} · {p.size}</option>
                  ))}
                </select>
                <div
                  className="border-4 border-gray-800 rounded-lg overflow-hidden bg-black select-none cursor-crosshair w-full relative touch-none"
                  style={{ aspectRatio: emuAspect[slot] || '9 / 16' }}
                  onPointerDown={(e) => running && emuPointerDown(slot, e)}
                  onPointerMove={(e) => running && emuPointerMove(slot, e)}
                  onPointerUp={(e) => running && emuPointerUp(slot, e)}
                >
                  {running && emuTapFx[slot] && <span key={emuTapFx[slot].id} className="kh-emu-ping" style={{ left: emuTapFx[slot].x, top: emuTapFx[slot].y }} />}
                  {running && emuAgentFx[slot] && <span key={'ag' + emuAgentFx[slot].id} className="kh-emu-ping" style={{ left: `${emuAgentFx[slot].xPct}%`, top: `${emuAgentFx[slot].yPct}%` }} />}
                  {emuAdminLocks[slot] && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none text-[11px] font-semibold bg-amber-500 text-black px-2 py-1 rounded shadow whitespace-nowrap">🔒 Administrator ({emuAdminLocks[slot].by}) boshqaryapti</div>}
                  {/* AUTO-LOCK overlay: harakatsizlikdan to'xtagan — bosish davom ettiradi */}
                  {running && emuIdleStopped[slot] && !emuStreaming[slot] && (
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerUp={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); emuResumeStream(slot); }}
                      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/60 text-white cursor-pointer"
                    >
                      <span className="text-2xl">⏸</span>
                      <span className="text-[11px]">Harakatsizlikdan to'xtadi</span>
                      <span className="px-3 py-1.5 bg-green-600 rounded-lg text-xs font-semibold">▶ Davom etish</span>
                    </button>
                  )}
                  {frame ? (
                    <img src={frame} alt="screen" onLoad={e => onEmuMedia(slot, e.target)} className="w-full h-full object-contain pointer-events-none" draggable={false} />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 text-xs gap-2 p-2 text-center">
                      {(busy || st === 'starting' || st === 'reconnecting')
                        ? <><div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full pointer-events-none" /><span className="pointer-events-none">Tayyorlanmoqda...</span></>
                        : running
                          ? (emuStreaming[slot]
                              ? <span className="pointer-events-none">Yuklanmoqda...</span>
                              : <button onClick={() => emuStartStream(slot)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">▶ Stream</button>)
                          : <span className="pointer-events-none">○ O'chiq</span>}
                    </div>
                  )}
                </div>
                {emuAgent[slot]?.active && (
                  <div className={`w-full mt-1.5 rounded-lg px-2 py-1.5 text-[11px] ${emuAgent[slot].paused ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800' : 'bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold ${emuAgent[slot].paused ? 'text-amber-700 dark:text-amber-400' : 'text-sky-700 dark:text-sky-400'}`}>
                        {emuAgent[slot].paused ? '⏸ Agent to\'xtatilgan' : '🤖 Agent ishlayapti'}
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => emuAgentControl(slot, emuAgent[slot].paused ? 'resume' : 'pause')}
                          title={emuAgent[slot].paused ? 'Boshqaruvni agentga qaytarish' : 'Agentni to\'xtatib, o\'zingiz boshqarish'}
                          className={`px-2 py-0.5 rounded font-semibold text-white ${emuAgent[slot].paused ? 'bg-sky-600 hover:bg-sky-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                          {emuAgent[slot].paused ? '▶ Agentga qaytarish' : '✋ Boshqaruvni olish'}
                        </button>
                        <button
                          onClick={() => emuAgentStop(slot)}
                          title="Sinovni butunlay to'xtatish (xulosa bo'lmaydi)"
                          className="px-2 py-0.5 rounded font-semibold text-white bg-rose-600 hover:bg-rose-700">
                          ⏹ To'xtatish
                        </button>
                      </span>
                    </div>
                    {khAgentWho(emuAgent[slot]) && <div className="font-medium text-gray-700 dark:text-gray-200 truncate" title={khAgentWho(emuAgent[slot])} translate="no">{khAgentWho(emuAgent[slot])}</div>}
                    {(emuAgentLog[slot] || []).slice(-3).map((l, i) => (
                      <div key={i} className="text-gray-500 dark:text-gray-400 truncate">· {l.action}{l.detail ? ` — ${String(l.detail).slice(0, 60)}` : ''}</div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2 justify-center">
                  {running ? (
                    <>
                      {emuStreaming[slot]
                        ? <button onClick={() => emuStopStream(slot)} title="Stream'ni to'xtatish" className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition">⏹ Stream</button>
                        : <button onClick={() => emuStartStream(slot)} title="Ekranni ko'rsatish" className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition">▶ Stream</button>}
                      <button onClick={() => emuAddFullscreen(slot)} title="To'liq ekran (bir nechta emulatorni birga tanlash mumkin)" className="px-2 py-1 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded hover:bg-primary-800 dark:hover:bg-primary-700 transition">⛶ To'liq</button>
                      <button onClick={() => emuStop(slot)} disabled={busy} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 transition">⏹ To'xtatish</button>
                      <button onClick={() => emuReconnect(slot)} disabled={busy} title="Qayta ulanish" className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 transition">🔌</button>
                    </>
                  ) : (
                    <button onClick={() => emuStart(slot)} disabled={busy} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition">
                      {busy ? '⏳...' : '▶ Ishga tushirish'}
                    </button>
                  )}
                </div>
                {running && (
                  <>
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => emuKey(slot, 4)}   title="Back"   className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition">←</button>
                      <button onClick={() => emuKey(slot, 3)}   title="Home"   className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition">⌂</button>
                      <button onClick={() => emuKey(slot, 187)} title="Recent" className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition">▣</button>
                      <button onClick={() => emuAppDrawer(slot)} title="Ilovalar" className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition">📱</button>
                      <button onClick={() => emuRotate(slot)} title="Aylantirish — portret ↔ landshaft (telefon/planshetni yotqizib sinash)" className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition">⟳</button>
                      <EmuAudioBtn slot={slot} audio={emuAudio} onToggle={toggleEmuAudio} className="px-2 py-1 text-xs rounded transition"
                        offCls="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600" />
                      <button onClick={() => openApkPicker(slot)}
                        title={`Tayyor (build qilingan) ilovalardan tanlab shu emulatorga o'rnatish — qayta build qilinmaydi${bound ? ` · biriktirilgani: «${bound.projectName}»` : ''}`}
                        className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 hover:bg-blue-200 dark:hover:bg-blue-800/70 text-blue-700 dark:text-blue-400 rounded transition">📲</button>
                      <button onClick={() => openLocPicker(slot)}
                        title="Soxta GPS lokatsiyasi — nuqta belgilash yoki marshrut bo'ylab harakat simulyatsiyasi (Android Studio'dagi Location paneli kabi)"
                        className={`px-2 py-1 text-xs rounded transition ${emuRoute[slot]?.running ? 'bg-green-500 text-white animate-pulse' : 'bg-emerald-100 dark:bg-emerald-900/50 hover:bg-emerald-200 dark:hover:bg-emerald-800/70 text-emerald-700 dark:text-emerald-400'}`}>📍</button>
                      <button onClick={() => document.getElementById(`emu-file-${slot}`)?.click()} title="Emulatorga fayl yuklash (rasm/video/audio/hujjat) — Redroid'da kamera yo'q, yuklashni sinash uchun shu orqali fayl qo'shing. Rasm/video → galereya, boshqasi → Fayllar/Download" className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/50 hover:bg-purple-200 dark:hover:bg-purple-800/70 text-purple-700 dark:text-purple-400 rounded transition">📎</button>
                      <input id={`emu-file-${slot}`} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadEmuFile(slot, f); e.target.value = ''; }} />
                      <button onClick={() => emuDeviceOwner(slot)} title="🛡 Device Owner — kiosk ilovasini (masalan haydovchi planshet ilovasi) shu emulatorda qurilma egasi qilish yoki olib tashlash" className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-800/70 text-amber-700 dark:text-amber-400 rounded transition">🛡</button>
                    </div>
                    <input
                      type="text"
                      placeholder="Matn → Enter"
                      className="mt-1.5 w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { socketRef.current?.emit('emulator:text', { slot, text: e.target.value }); e.target.value = ''; } }}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {emuGridLogs.length > 0 && (
        <div className="mt-4 bg-gray-900 rounded-lg">
          <div className="flex items-center justify-between px-2 pt-1.5">
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Emulator logi</span>
            <button onClick={() => setEmuGridLogs([])} className="text-gray-400 hover:text-white text-base leading-none px-1">×</button>
          </div>
          <div className="px-2 pb-2 max-h-28 overflow-y-auto">
            {emuGridLogs.map((l, i) => (
              <div key={i} className={`text-xs font-mono ${l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : 'text-gray-300'}`}>
                <span className="text-gray-500 dark:text-gray-400">[E{(l.slot ?? 0) + 1}]</span> {l.msg}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 📍 LOKATSIYA — soxta GPS: bitta nuqta yoki marshrut bo'ylab harakat.
          Redroid'da Android test-provider'i (gps/network/fused), AVD'da qo'shimcha
          `emu geo fix`. Nuqta DB'da saqlanadi → emulator restartidan keyin tiklanadi. */}
      {locSlot != null && (() => {
        const rt = emuRoute[locSlot] || {};
        return (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setLocSlot(null); }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="font-bold text-gray-900 dark:text-white">📍 Emulator {locSlot + 1} — lokatsiya</p>
                <p className="text-xs text-gray-400">Soxta GPS: ilova shu nuqtani haqiqiy deb qabul qiladi</p>
              </div>
              <button onClick={() => setLocSlot(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">×</button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              {/* Bitta nuqta */}
              <div>
                <div className="flex gap-2 items-end">
                  <label className="flex-1 text-xs text-gray-500 dark:text-gray-400">Kenglik (lat)
                    <input value={locLat} onChange={e => setLocLat(e.target.value)} placeholder="41.3673"
                      className="mt-0.5 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                  </label>
                  <label className="flex-1 text-xs text-gray-500 dark:text-gray-400">Uzunlik (lng)
                    <input value={locLng} onChange={e => setLocLng(e.target.value)} placeholder="69.2910"
                      className="mt-0.5 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                  </label>
                  <button onClick={() => applyLocation(locSlot, locLat, locLng)} disabled={locBusy || !locLat || !locLng}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition">
                    {locBusy ? '⏳' : '📍 Belgilash'}
                  </button>
                </div>
                <button onClick={saveLocationPoint} disabled={!locLat || !locLng}
                  className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-40">➕ Shu nuqtani saqlash</button>
              </div>

              {/* Saqlangan nuqtalar */}
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Saqlangan nuqtalar</p>
                {locPoints.length === 0 ? (
                  <p className="text-xs text-gray-400">Hali saqlangan nuqta yo'q — koordinatani kiritib «➕ Shu nuqtani saqlash» bosing.</p>
                ) : locPoints.map(p => (
                  <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 group">
                    <button onClick={() => { setLocLat(String(p.lat)); setLocLng(String(p.lng)); applyLocation(locSlot, p.lat, p.lng); }}
                      title="Shu nuqtani darhol belgilash" className="flex-1 text-left">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</span>
                      <span className="text-xs text-gray-400 ml-2">{p.lat}, {p.lng}</span>
                    </button>
                    <button onClick={() => setRoutePoints(prev => [...prev, p])} title="Marshrutga qo'shish"
                      className="text-xs px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 opacity-70 hover:opacity-100 transition">🛣 +</button>
                    <button onClick={() => deleteLocationPoint(p.id)} title="O'chirish"
                      className="text-xs text-gray-300 hover:text-red-500">🗑</button>
                  </div>
                ))}
              </div>

              {/* Marshrut — harakat simulyatsiyasi */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">🛣 Marshrut (harakat simulyatsiyasi)</p>
                {routePoints.length === 0 ? (
                  <p className="text-xs text-gray-400 mb-2">Saqlangan nuqtalar ustiga kelib <b>🛣 +</b> bosib ketma-ketlik yig'ing (kamida 2 ta) — GPS shu yo'l bo'ylab «harakatlanadi».</p>
                ) : (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {routePoints.map((p, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 flex items-center gap-1">
                        {i + 1}. {p.name}
                        <button onClick={() => setRoutePoints(prev => prev.filter((_, j) => j !== i))} className="text-primary-500 hover:text-red-500">×</button>
                      </span>
                    ))}
                    <button onClick={() => setRoutePoints([])} className="text-xs text-gray-400 hover:text-red-500 px-1">tozalash</button>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={routeSpeed} onChange={e => setRouteSpeed(Number(e.target.value))}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 focus:outline-none">
                    <option value={5}>🚶 Piyoda · 5 km/soat</option>
                    <option value={20}>🚲 Velosiped · 20 km/soat</option>
                    <option value={40}>🚗 Shahar · 40 km/soat</option>
                    <option value={80}>🛣 Tezyurar · 80 km/soat</option>
                    <option value={120}>🏎 Magistral · 120 km/soat</option>
                    <option value={150}>🏁 Poyga · 150 km/soat</option>
                    <option value={200}>🚀 Juda tez · 200 km/soat</option>
                    <option value={500}>✈️ Samolyot · 500 km/soat</option>
                    <option value={1000}>🛩 Reaktiv · 1000 km/soat</option>
                    <option value={2000}>⚡ Tovushdan tez · 2000 km/soat</option>
                    <option value={3000}>☄️ Maksimal · 3000 km/soat</option>
                  </select>
                  <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <input type="checkbox" checked={routeLoop} onChange={e => setRouteLoop(e.target.checked)} /> 🔁 Takrorlash
                  </label>
                  {rt.running ? (
                    <button onClick={() => stopRoute(locSlot)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition">⏹ To'xtatish</button>
                  ) : (
                    <button onClick={() => startRoute(locSlot)} disabled={locBusy || routePoints.length < 2}
                      className="px-3 py-1.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition">▶ Boshlash</button>
                  )}
                </div>
                {rt.running && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-600 transition-all" style={{ width: `${Math.round(((rt.index || 0) / Math.max(1, rt.total || 1)) * 100)}%` }} />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {(rt.index || 0)}/{rt.total} qadam{rt.lat != null && ` · ${Number(rt.lat).toFixed(5)}, ${Number(rt.lng).toFixed(5)}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 📲 TAYYOR APK'lar — qaysi ilovani shu emulatorga o'rnatishni tanlash.
          Biriktirilgani (📌) tepada + tavsiya sifatida belgilanadi; qolganlari
          eng yangi build bo'yicha. Build qilinmagan modul ro'yxatga tushmaydi. */}
      {apkPickerSlot != null && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setApkPickerSlot(null); }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="font-bold text-gray-900 dark:text-white">📲 Emulator {apkPickerSlot + 1} ga o'rnatish</p>
                <p className="text-xs text-gray-400">Tayyor (build qilingan) ilovalardan birini tanlang — qayta build qilinmaydi</p>
              </div>
              <button onClick={() => setApkPickerSlot(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">×</button>
            </div>
            <div className="p-3 overflow-y-auto">
              {apkChoicesLoading ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  <div className="animate-spin w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full mx-auto mb-2" />
                  Tayyor ilovalar qidirilmoqda...
                </div>
              ) : apkChoices.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  <p className="text-3xl mb-2">📦</p>
                  <p>Hali birorta APK build qilinmagan.</p>
                  <p className="text-xs mt-1">Avval 🔨 <b>Build + Install</b> bosing (~4 daqiqa).</p>
                </div>
              ) : (
                [...apkChoices]
                  .sort((a, b) => (b.boundSlot === apkPickerSlot) - (a.boundSlot === apkPickerSlot) || b.builtAt - a.builtAt)
                  .map(a => {
                    const mine = a.boundSlot === apkPickerSlot;
                    const other = a.boundSlot != null && a.boundSlot !== apkPickerSlot;
                    return (
                      <button key={a.moduleId} onClick={() => installApkToSlot(apkPickerSlot, a)}
                        className={`w-full text-left px-3 py-2.5 mb-2 rounded-lg border transition ${mine ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/60' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-white text-sm">{a.projectName}</span>
                          <span className="text-xs text-gray-400">{a.moduleName}</span>
                          {mine && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-200 dark:bg-primary-800/60 text-primary-900 dark:text-primary-200 font-medium">📌 shu emulatorniki</span>}
                          {other && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 font-medium">⚠️ {a.boundSlot + 1}-emulatorga biriktirilgan</span>}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{fmtApkSize(a.sizeBytes)} · {fmtApkTime(a.builtAt)} da build qilingan</div>
                      </button>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen — one emulator big (portrait), touch-interactive */}
      {showCells && projectId && webDevs && (
        <WebDevicesSection authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={projectId} data={webDevs}
          reload={fetchWebDevs} agents={wdAgent} onAgentControl={wdAgentControl} fsIds={wdFullscreen} onToggleFs={wdToggleFullscreen} />
      )}
      {(emuFullscreen.length > 0 || wdFullscreen.length > 0) && (() => {
        // Bir nechta emulatorni BIRGA to'liq ekranda ko'rsatish (yonma-yon). Balandlik
        // sondan kelib chiqadi (ko'proq = kichikroq), gorizontal kerak bo'lsa scroll.
        // ⭐ 2026-10-29: teng 1/n kenglik o'rniga khFsLayout — qurilma SHAKLIGA mos ('fit') yoki ⭐ asosiy ('main').
        // Eski izoh (tarix uchun): balandlik ham vh, ham KENGLIK bo'yicha cheklanadi: n ta portret telefon
        // yonma-yon sig'ishi kerak (aks holda 2-emulator ekrandan chiqib ketardi).
        // Boshqaruv chap USTUNDA (92px) — yuqori qator yo'q, shuning uchun telefon bo'yiga
        // to'liq (100vh − ostidagi boshqaruv qatori) ochiladi; kenglik bo'yicha n ta portret
        // telefon yonma-yon sig'ishi ham hisobga olinadi (aks holda ekrandan chiqib ketadi).
        // 2026-09-14: balandlik 100vh − 62px deb QOTIRILGAN edi — agent sinayotganda telefon
        // ostida IKKINCHI qator (✋ Boshqaruvni olish · ⏹) chiqadi va Back/Home/Matn qatori
        // ekrandan pastga tushib ketardi (operator skrinshoti). Endi telefon ustun ichida
        // FLEX bilan cho'ziladi (qolgan qatorlar qancha joy olsa shuncha kamayadi), kenglik
        // esa n ta portret sig'ishi bilan cheklanadi (letterbox bo'lsa tap xaritasi
        // object-contain kontent to'rtburchagini o'zi hisoblaydi — emuMediaInfo).
        // Ustun ICHIDA telefon FLEX bilan cho'ziladi (ostidagi qatorlar qancha joy olsa shuncha kamayadi) — shuning uchun
        // chrome (qatorlar balandligi) taxminiy; xato bo'lsa ekran letterbox bo'ladi, tap xaritasi buni o'zi hisoblaydi.
        // 🌐 Brauzer qurilmalar ham shu joylashuvda (kalit `w<id>`); ekran ustida manzil qatori, ostida boshqaruv — chrome kattaroq.
        const fsWebs = wdFullscreen.map(id => ((webDevs && webDevs.devices) || []).find(d => d.id === id)).filter(Boolean);
        const fsItems = [
          ...emuFullscreen.map(slot => ({ k: `e${slot}`, slot, ar: khAr(emuAspect[slot], 9 / 16), chrome: emuAgent[slot]?.active ? 100 : 56 })),
          ...fsWebs.map(d => ({ k: `w${d.id}`, dev: d, ar: wdAspect(d), chrome: wdAgent[d.key]?.active ? 140 : 92 })),
        ];
        if (!fsItems.length) return null;
        const fsMulti = fsItems.length > 1;
        // ⭐ Asosiy rejimga o'tganda: avval tanlangani (hali to'liq ekranda bo'lsa), bo'lmasa eng KENG qurilma (planshet).
        const fsMainPick = (emuFsLayout.main && fsItems.some(i => i.k === emuFsLayout.main)) ? emuFsLayout.main
          : (fsMulti ? [...fsItems].sort((a, b) => b.ar - a.ar)[0].k : null);
        // 🤖 Agent rejimi = ⭐ Asosiy, faqat katta qurilmani agent tanlaydi (oxirgi harakati). Agent hali hech narsa
        // qilmagan / qurilmasi to'liq ekranda emas — oxirgi tanlangan asosiy (yoki eng keng) qoladi.
        const fsFollowK = (() => {
          const f = emuAgentFocus;
          if (!f) return null;
          if (f.startsWith('wd:')) { const d = fsWebs.find(x => x.key === f.slice(3)); return d ? `w${d.id}` : null; }
          return fsItems.some(i => i.k === f) ? f : null;
        })();
        const fsLay = khFsLayout(fsItems, emuFsLayout.mode === 'agent' ? { ...emuFsLayout, mode: 'main', main: fsFollowK || fsMainPick } : emuFsLayout, emuVp);
        // ↔ O'ng ustun kengligini sudrash (faqat ⭐ asosiy rejimda). Sudrash paytida saqlanmaydi, qo'yib yuborilganda saqlanadi.
        const fsSplitDown = (e) => { try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) {} };
        const fsSplitMove = (e) => {
          if (!(e.buttons & 1)) return;
          const side = (emuVp.w - 12 - e.clientX) / (fsLay.W || 1);
          saveEmuFsLayout({ side: Math.min(0.6, Math.max(0.15, side)) }, false);
        };
        const fsSplitUp = (e) => { try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (er) {} saveEmuFsLayout({}, true); };
        // To'liq ekranda qaysi emulatorlar borligini shu yerdan boshqaramiz — overlay
        // gridni to'sib qo'yadi, shuning uchun katakdagi ⛶ tugmasiga yetib bo'lmaydi.
        const fsCandidates = [...(emuList || [])]
          .filter(e => (emuStatuses[e.slot] || e.status) === 'running')
          .sort((a, b) => a.slot - b.slot);
        const wdCandidates = (webDevs && webDevs.devices) || [];
        // To'liq ekrandan FAQAT ✕ Yopish chiqaradi. Fonga bosish ATAYIN yopmaydi: operator
        // telefon yonidagi qora joyga tegib (yoki swipe'ni tashqarida tugatib) sinov
        // o'rtasida rejimdan chiqib ketardi (2026-09-12 shikoyati).
        return (
          <div className={`fixed inset-0 z-[60] bg-black/95 flex flex-row py-2 pr-3 pl-[104px] ${fsLay.mode === 'main' ? 'items-stretch overflow-hidden' : 'flex-wrap items-center justify-center gap-4 overflow-auto'}`}>
            {/* CHAP USTUN: ✕ Yopish · 🤖 sinov holati · E1/E2/E3. Ilgari bular YUQORI qatorda edi
                va 3-emulator qo'shilganda telefonlar shu qator hisobiga kichrayardi (operator,
                2026-09-13). Vertikal ustun 92px oladi, telefonlar bo'yiga to'liq ochiladi. */}
            <div className="fixed left-0 top-0 bottom-0 w-[92px] z-10 flex flex-col items-center gap-2 py-3 bg-black/60 border-r border-white/10 overflow-y-auto">
              <button onClick={emuCloseFullscreen} title="To'liq ekrandan chiqish (faqat shu tugma)"
                className="w-[76px] py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-[11px] flex flex-col items-center gap-0.5 transition shrink-0">
                <span className="text-base leading-none">✕</span>Yopish
              </button>
              {emulatorOnly && emuAwakeBtn(true)}
              <AgentQaFsStrip authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} rail
                projectIds={[
                  ...emuFullscreen.map(s => emuAgent[s]?.projectId),
                  ...emuFullscreen.map(s => emuBindings[s]?.projectId),
                  Number(abProjectId) || null,
                ]}
                slots={emuFullscreen} emuAgent={emuAgent} onControl={emuAgentControl} />
              {(fsCandidates.length + wdCandidates.length) > 1 && (
                <div className="flex flex-col items-stretch gap-1.5 w-[76px] mt-1">
                  <span className="text-[10px] text-gray-400 text-center leading-tight">To'liq ekranda</span>
                  {wdCandidates.map(d => {
                    const on = wdFullscreen.includes(d.id);
                    const ag = wdAgent[d.key];
                    return (
                      <button key={`w${d.id}`} onClick={() => wdToggleFullscreen(d.id)}
                        title={on ? `B${d.slot + 1} «${d.label}» ni to'liq ekrandan olib tashlash` : `🌐 B${d.slot + 1} «${d.label}» brauzerini yonma-yon qo'shish`}
                        className={`px-1.5 py-1.5 rounded-lg text-xs font-semibold transition flex flex-col items-center leading-tight ${on ? 'bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-600' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
                        <span>{on ? '✓' : '+'} B{d.slot + 1}{ag?.active ? (ag.paused ? ' ⏸' : ' 🤖') : ''}</span>
                        <span className="text-[9px] opacity-80 truncate max-w-[70px]" translate="no">{d.label}</span>
                      </button>
                    );
                  })}
                  {fsCandidates.map(e => {
                    const on = emuFullscreen.includes(e.slot);
                    const bound = emuBindings[e.slot];
                    const ag = emuAgent[e.slot];
                    return (
                      <button key={e.slot}
                        onClick={() => (on ? emuRemoveFullscreen(e.slot) : emuAddFullscreen(e.slot))}
                        title={on ? `Emulator ${e.slot + 1} ni to'liq ekrandan olib tashlash` : `Emulator ${e.slot + 1} ni yonma-yon qo'shish${bound ? ` («${bound.projectName}»)` : ''}`}
                        className={`px-1.5 py-1.5 rounded-lg text-xs font-semibold transition flex flex-col items-center leading-tight ${on ? 'bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-600' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
                        <span>{on ? '✓' : '+'} E{e.slot + 1}{ag?.active ? (ag.paused ? ' ⏸' : ' 🤖') : ''}</span>
                        {bound && <span className="text-[9px] opacity-80 truncate max-w-[70px]">{bound.projectName}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              {fsMulti && (
                <div className="flex flex-col items-stretch gap-1.5 w-[76px] mt-1">
                  <span className="text-[10px] text-gray-400 text-center leading-tight">Joylashuv</span>
                  <button onClick={() => saveEmuFsLayout({ mode: 'fit' })}
                    title="Qurilmalar shakliga mos — gorizontal planshet ko'proq joy oladi, telefon yonida"
                    className={`px-1.5 py-1.5 rounded-lg text-xs font-semibold transition ${fsLay.mode === 'fit' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>⚖ Mos</button>
                  <button onClick={() => saveEmuFsLayout({ mode: 'main', main: fsMainPick })}
                    title="⭐ Asosiy qurilma katta, qolganlari o'ngdagi ustunda. Qaysi biri asosiy — qurilma ostidagi ☆ tugmasi bilan tanlanadi; ustun kengligini o'rtadagi chiziqni sudrab o'zgartiring."
                    className={`px-1.5 py-1.5 rounded-lg text-xs font-semibold transition ${fsLay.mode === 'main' && emuFsLayout.mode !== 'agent' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>⭐ Asosiy</button>
                  <button onClick={() => saveEmuFsLayout({ mode: 'agent' })}
                    title="🤖 Agent qaysi emulator yoki brauzerda ishlayotgan bo'lsa — o'sha katta bo'lib ko'rinadi; agent boshqasiga o'tsa, katta ekran ham o'tadi. Agent ishlamayotganda oxirgi katta qurilma qoladi. ☆ bossangiz — o'sha qurilma doimiy asosiy bo'ladi (⭐ rejim)."
                    className={`px-1.5 py-1.5 rounded-lg text-xs font-semibold transition ${emuFsLayout.mode === 'agent' ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>🤖 Agent</button>
                </div>
              )}
            </div>
            {fsLay.mode === 'main' ? (
              <>
                <div className="flex items-center justify-center shrink-0" style={{ width: fsLay.mainW }}>{fsCol(fsLay.main, fsLay.cols[fsLay.main], true)}</div>
                <div onPointerDown={fsSplitDown} onPointerMove={fsSplitMove} onPointerUp={fsSplitUp}
                  title="Sudrab o'ng ustun kengligini o'zgartiring"
                  className="shrink-0 cursor-col-resize flex items-center justify-center group touch-none" style={{ width: KH_FS_SPLIT }}>
                  <div className="w-1 h-24 rounded-full bg-white/20 group-hover:bg-primary-500 transition" />
                </div>
                <div className="flex flex-col items-center justify-center gap-4 shrink-0 overflow-y-auto" style={{ width: fsLay.sideW }}>
                  {fsLay.rest.map(k => fsCol(k, fsLay.cols[k], false))}
                </div>
              </>
            ) : fsItems.map(it => fsCol(it.k, fsLay.cols[it.k], false))}
          </div>
        );
        // `e<slot>` — emulator ustuni, `w<id>` — 🌐 brauzer qurilma (WebDeviceScreen).
        function fsCol(k, colStyle, isMain) {
          if (String(k)[0] === 'w') {
            const d = fsWebs.find(x => `w${x.id}` === k);
            if (!d) return null;
            return (
              <WebDeviceScreen key={k} fs authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} device={d} agent={wdAgent[d.key]}
                onAgentControl={wdAgentControl} onChanged={fetchWebDevs} onRemoveFs={() => wdToggleFullscreen(d.id)}
                isMain={isMain} onToggleMain={fsMulti ? () => toggleEmuFsMain(k) : null} style={colStyle} />
            );
          }
          return fsEmuCol(Number(String(k).slice(1)), colStyle, isMain);
        }
        function fsEmuCol(slot, colStyle, isMain) {
              const frame = emuFrames[slot];
              // 🤖 Agent shu qurilmada sinayaptimi — gridda belgi/tasma/✋ bor edi, to'liq
              // ekranda esa YO'Q edi (operator: "to'liq rejimda agent ishlayotganini bilmayapman").
              const ag = emuAgent[slot]?.active ? emuAgent[slot] : null;
              const agLast = ag ? (emuAgentLog[slot] || []).slice(-1)[0] : null;
              // ⏻ Emulator holati — gridda ko'rinardi, to'liq ekranda esa oxirgi kadr qotib qolib, emulator o'chgani
              // bilinmasdi (operator, 2026-10-21). Grid bilan bir manba: emuStatuses (socket) → emuList (poll).
              const fsSt = emuStatuses[slot] || ((emuList || []).find(e => e.slot === slot) || {}).status || 'not_created';
              const fsOff = fsSt !== 'running';
              const fsWait = fsSt === 'starting' || fsSt === 'reconnecting' || !!emuBusy[slot];
              return (
                <div key={slot} className="flex flex-col items-center gap-2 shrink-0" style={colStyle}>
                  <div className={`border-4 rounded-lg overflow-hidden bg-black cursor-crosshair select-none relative touch-none ${fsOff ? 'border-red-600' : ag ? (ag.paused ? 'border-amber-500' : 'border-sky-500') : 'border-gray-800'}`}
                       style={{ flex: '1 1 0', minHeight: 0, maxWidth: '100%', aspectRatio: emuAspect[slot] || '9 / 16' }}
                       onPointerDown={(e) => emuPointerDown(slot, e)} onPointerMove={(e) => emuPointerMove(slot, e)} onPointerUp={(e) => emuPointerUp(slot, e)}>
                    {emuTapFx[slot] && <span key={emuTapFx[slot].id} className="kh-emu-ping" style={{ left: emuTapFx[slot].x, top: emuTapFx[slot].y }} />}
                    {emuAgentFx[slot] && <span key={'ag' + emuAgentFx[slot].id} className="kh-emu-ping" style={{ left: `${emuAgentFx[slot].xPct}%`, top: `${emuAgentFx[slot].yPct}%` }} />}
                    {emuAdminLocks[slot] && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none text-[11px] font-semibold bg-amber-500 text-black px-2 py-1 rounded shadow whitespace-nowrap">🔒 Administrator ({emuAdminLocks[slot].by}) boshqaryapti</div>}
                    {ag && (
                      /* PASTDA: bir telefonli rejimda telefon 92vh bo'lib yuqori qator (sinov
                         holati + selektor) bilan ustma-ust tushardi. */
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none max-w-[94%]">
                        <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-lg backdrop-blur text-white flex items-center gap-1.5 whitespace-nowrap ${ag.paused ? 'bg-amber-600/90' : 'bg-sky-600/90'}`}>
                          <span className={ag.paused ? '' : 'animate-pulse'}>{ag.paused ? '⏸ Boshqaruv sizda — agent kutmoqda' : '🤖 Agent ishlayapti'}</span>
                          {!ag.paused && agLast && <span className="opacity-80 truncate max-w-[220px]">· {agLast.action}{agLast.detail ? ` — ${String(agLast.detail).slice(0, 40)}` : ''}</span>}
                        </div>
                      </div>
                    )}
                    {fsOff && (
                      <div onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
                        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/80 text-white text-center px-4 cursor-default">
                        <span className="text-3xl">{fsWait ? '⏳' : fsSt === 'error' ? '⚠️' : '⏻'}</span>
                        <span className="text-sm font-semibold">
                          {fsWait ? 'Emulator yoqilmoqda…' : fsSt === 'error' ? "Emulator xato bilan to'xtadi" : fsSt === 'not_created' ? 'Emulator yaratilmagan' : "Emulator o'chdi"}
                        </span>
                        {!fsWait && fsSt !== 'not_created' && (
                          <span className="text-[11px] opacity-70">Ekrandagi rasm — o'chishdan oldingi oxirgi kadr</span>
                        )}
                        {!fsWait && fsSt !== 'not_created' && (
                          <button onClick={(e) => { e.stopPropagation(); emuStart(slot); }}
                            className="mt-1 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-semibold">▶ Yoqish</button>
                        )}
                      </div>
                    )}
                    {/* AUTO-LOCK overlay (to'liq ekranda ham) */}
                    {emuIdleStopped[slot] && !emuStreaming[slot] && (
                      <button
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); emuResumeStream(slot); }}
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/60 text-white cursor-pointer"
                      >
                        <span className="text-3xl">⏸</span>
                        <span className="text-sm">Harakatsizlikdan to'xtadi</span>
                        <span className="px-4 py-2 bg-green-600 rounded-lg text-sm font-semibold">▶ Davom etish</span>
                      </button>
                    )}
                    {frame
                      ? <img src={frame} alt="screen" onLoad={e => onEmuMedia(slot, e.target)} className="w-full h-full object-contain pointer-events-none" draggable={false} />
                      : <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 pointer-events-none">Yuklanmoqda...</div>}
                  </div>
                  {ag && (
                    <div className={`shrink-0 flex flex-wrap items-center justify-center gap-1.5 text-[11px] rounded-lg px-2 py-1 max-w-full ${ag.paused ? 'bg-amber-500/20 text-amber-100' : 'bg-sky-500/20 text-sky-100'}`}
                         onClick={(e) => e.stopPropagation()}>
                      <span className="font-semibold">{ag.paused ? '⏸ Agent to\'xtatilgan' : '🤖 Agent ishlayapti'}</span>
                      {khAgentWho(ag) && <span className="basis-full text-center opacity-90 truncate" title={khAgentWho(ag)} translate="no">{khAgentWho(ag)}</span>}
                      <button onClick={() => emuAgentControl(slot, ag.paused ? 'resume' : 'pause')}
                        title={ag.paused ? 'Boshqaruvni agentga qaytarish' : 'Agentni to\'xtatib, o\'zingiz boshqarish'}
                        className={`px-2 py-0.5 rounded font-semibold text-white ${ag.paused ? 'bg-sky-600 hover:bg-sky-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                        {ag.paused ? '▶ Agentga qaytarish' : '✋ Boshqaruvni olish'}
                      </button>
                      <button onClick={() => emuAgentStop(slot)} title="Sinovni butunlay to'xtatish (xulosa bo'lmaydi)"
                        className="px-2 py-0.5 rounded font-semibold text-white bg-rose-600 hover:bg-rose-700">⏹ To'xtatish</button>
                    </div>
                  )}
                  {/* Ixcham boshqaruv qatori (har telefon ostida) */}
                  <div className="shrink-0 flex flex-wrap items-center justify-center gap-1.5 text-white max-w-full px-1">
                    <span className={`text-xs font-semibold ${fsOff ? 'text-red-400' : ''}`} title={fsOff ? `Holat: ${fsSt}` : 'Ishlab turibdi'}>E{slot + 1}{fsOff ? ' ⏻' : ''}</span>
                    <button onClick={() => emuKey(slot, 4)}   title="Back"   className="px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition">←</button>
                    <button onClick={() => emuKey(slot, 3)}   title="Home"   className="px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition">⌂</button>
                    <button onClick={() => emuKey(slot, 187)} title="Recent" className="px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition">▣</button>
                    <button onClick={() => emuAppDrawer(slot)} title="Ilovalar" className="px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition">📱</button>
                    <button onClick={() => emuRotate(slot)} title="Aylantirish — portret ↔ landshaft (telefon/planshetni yotqizib sinash)" className="px-2 py-1 bg-white/10 rounded hover:bg-white/20 text-sm transition">⟳</button>
                    <EmuAudioBtn slot={slot} audio={emuAudio} onToggle={toggleEmuAudio} className="px-2 py-1 rounded text-sm transition"
                      offCls="bg-white/10 hover:bg-white/20" />
                    {/* 📍 soxta GPS — gridda bor edi, to'liq ekranda YO'Q edi (operator, 2026-09-18).
                        Modal z-[70] > overlay z-[60], shuning uchun to'liq ekran ustida ochiladi. */}
                    <button onClick={() => openLocPicker(slot)}
                      title="Soxta GPS lokatsiyasi — nuqta belgilash yoki marshrut bo'ylab harakat simulyatsiyasi"
                      className={`px-2 py-1 rounded text-sm transition ${emuRoute[slot]?.running ? 'bg-green-500 animate-pulse' : 'bg-emerald-600/70 hover:bg-emerald-500'}`}>📍</button>
                    {/* 📲 tayyor APK'dan o'rnatish + 📎 fayl yuklash — gridda bor edi, to'liq ekranda YO'Q edi.
                        Fayl inputi ALOHIDA id bilan (grid mount bo'lmagan holatga bog'lanmasin). */}
                    <button onClick={() => openApkPicker(slot)}
                      title={`Tayyor (build qilingan) ilovalardan tanlab shu emulatorga o'rnatish — qayta build qilinmaydi${emuBindings[slot] ? ` · biriktirilgani: «${emuBindings[slot].projectName}»` : ''}`}
                      className="px-2 py-1 rounded text-sm transition bg-blue-600/70 hover:bg-blue-500">📲</button>
                    <button onClick={() => document.getElementById(`emu-file-fs-${slot}`)?.click()}
                      title="Emulatorga fayl yuklash (rasm/video/audio/hujjat) — rasm/video → galereya, boshqasi → Fayllar/Download"
                      className="px-2 py-1 rounded text-sm transition bg-purple-600/70 hover:bg-purple-500">📎</button>
                    <input id={`emu-file-fs-${slot}`} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadEmuFile(slot, f); e.target.value = ''; }} />
                    <button onClick={() => emuDeviceOwner(slot)}
                      title="🛡 Device Owner — kiosk ilovasini shu emulatorda qurilma egasi qilish yoki olib tashlash"
                      className="px-2 py-1 rounded text-sm transition bg-amber-600/70 hover:bg-amber-500">🛡</button>
                    <input type="text" placeholder="Matn↵"
                           className="w-24 bg-white/10 text-white placeholder-gray-500 dark:placeholder-gray-400 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                           onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { socketRef.current?.emit('emulator:text', { slot, text: e.target.value }); e.target.value = ''; } }} />
                    {fsMulti && (
                      <button onClick={() => toggleEmuFsMain(`e${slot}`)}
                        title={isMain ? "Asosiy — bossangiz qurilmalar yana teng (shakliga mos) joylashadi" : "Shu qurilmani asosiy (katta) qilish — qolganlari o'ng ustunga o'tadi"}
                        className={`px-2 py-1 rounded text-sm transition ${isMain ? 'bg-yellow-500 text-black hover:bg-yellow-400' : 'bg-white/10 hover:bg-white/20'}`}>{isMain ? '★' : '☆'}</button>
                    )}
                    <button onClick={() => emuRemoveFullscreen(slot)} title="Shu emulatorni to'liq ekrandan olib tashlash" className="px-2 py-1 bg-red-600/80 rounded hover:bg-red-600 text-sm transition">✕</button>
                  </div>
                </div>
              );
        }
      })()}
    </div>
    );
  };

  // Reusable emulator panel (screen + controls). Rendered both inside the admin
  // modal (with a close button) and inline in the Studio emulator view (onClose=null).
  // imgRef is a singleton — only one instance is ever mounted at a time.
  const renderEmulatorPanel = (onClose) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-5 flex gap-6 items-start max-h-screen overflow-y-auto">
      {/* Left: screen */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-gray-900 dark:text-white">Sizning emulatoringiz</h2>
          {onClose && (
            <button onClick={onClose} className="ml-8 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl font-bold leading-none">×</button>
          )}
        </div>

        {/* Status badge */}
        <div className="flex items-center gap-2 mb-2 text-xs">
          {{
            running:      <span className="text-green-400">● Ishlayapti</span>,
            starting:     <span className="text-yellow-400 animate-pulse">● Ishga tushmoqda...</span>,
            reconnecting: <span className="text-yellow-400 animate-pulse">● Qayta ulanmoqda...</span>,
            stopped:      <span className="text-gray-400">● To'xtatilgan</span>,
            error:        <span className="text-red-400">● Xatolik</span>,
            not_created:  <span className="text-gray-500 dark:text-gray-400">● Yaratilmagan</span>,
          }[emulatorStatus] || <span className="text-gray-500 dark:text-gray-400">● Noma'lum</span>}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button
            onClick={() => reconnectDevice()}
            disabled={isReconnecting}
            title="Smart reconnect: ADB → adbd restart → container restart"
            className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition"
          >
            {isReconnecting ? '⏳ Ulanmoqda...' : '🔌 Reconnect'}
          </button>
          {!isStreaming ? (
            <button onClick={() => startStream('docker')}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
              ▶ Stream
            </button>
          ) : (
            <button onClick={stopStream} className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition">
              ⏹ Stop
            </button>
          )}
        </div>

        {/* Reconnect logs — closeable (× clears them) so they don't keep
            pushing the emulator screen down once reconnect is done. */}
        {emulatorLogs.length > 0 && (
          <div className="mb-3 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between px-2 pt-1.5">
              <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Qayta ulanish logi</span>
              <button
                onClick={() => setEmulatorLogs([])}
                title="Yopish"
                className="text-gray-400 hover:text-white text-base leading-none px-1"
              >×</button>
            </div>
            <div className="px-2 pb-2 max-h-28 overflow-y-auto">
              {emulatorLogs.map((l, i) => (
                <div key={i} className={`text-xs font-mono ${l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : 'text-gray-300'}`}>{l.msg}</div>
              ))}
            </div>
          </div>
        )}

        {/* Device screen */}
        <div
          ref={imgRef}
          className="border-4 border-gray-800 rounded-lg overflow-hidden bg-black select-none cursor-crosshair relative"
          style={{ width: 300, height: 533 }}
          onMouseDown={handleEmulatorMouseDown}
          onMouseUp={handleEmulatorMouseUp}
        >
          {/* Stream frozen warning — absolute overlay pinned to the top of the
              screen frame. It used to sit in normal flow ABOVE the emulator, so
              every time it toggled on/off it pushed the emulator down/up (jank).
              As an overlay inside this fixed-size relative box it never reflows
              the emulator. stopPropagation so tapping it isn't read as a tap. */}
          {streamFrozen && (
            <div
              className="absolute top-0 left-0 right-0 z-10 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 text-xs flex items-center justify-center gap-2 shadow"
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            >
              ⚠️ Stream muzladi —
              <button onClick={() => { socketRef.current?.emit('emulator:start_stream'); setLastFrameTime(Date.now()); }}
                className="underline font-semibold">Qayta boshlash</button>
            </div>
          )}
          {emulatorFrame ? (
            <img
              src={emulatorFrame}
              alt="screen"
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 text-sm pointer-events-none gap-2">
              {isStreaming
                ? <><div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full"/><span>Yuklanmoqda...</span></>
                : <span>▶ Boshlash tugmasini bosing</span>
              }
            </div>
          )}
        </div>
      </div>

      {/* Right: controls */}
      <div className="flex flex-col gap-2 pt-10 min-w-[110px]">
        {/* Hardware keys + text input — only meaningful while the screen is streaming */}
        {isStreaming && (
          <>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Hardware</p>
            <button onClick={() => sendKey(4)}   className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">← Back</button>
            <button onClick={() => sendKey(3)}   className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">⌂ Home</button>
            <button onClick={() => sendKey(187)} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">▣ Recent</button>
            <button onClick={openAppDrawer} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">📱 Ilovalar</button>
            <button onClick={() => sendKey(24)}  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">🔊 Vol+</button>
            <button onClick={() => sendKey(25)}  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition">🔉 Vol-</button>

            <p className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-1">Matn</p>
            <input
              type="text"
              placeholder="Enter bosing..."
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              onKeyDown={e => {
                if (e.key === 'Enter' && e.target.value) {
                  socketRef.current?.emit('emulator:text', { text: e.target.value });
                  e.target.value = '';
                }
              }}
            />
          </>
        )}

        {/* App management */}
        <p className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-1">Ilova</p>

        <button
          onClick={handleStudioMobileBuild}
          disabled={serverRunning}
          title="Repodan APK build qilib emulatorga o'rnatadi (~4 daqiqa)"
          className="px-3 py-2 bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 rounded-lg text-sm font-medium transition"
        >
          {serverRunning ? '⏳ Build...' : '🔨 Build + Install'}
        </button>

        {apkInfo?.hasApk ? (
          <button
            onClick={installApk}
            disabled={isInstalling}
            className="px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm font-medium transition"
          >
            {isInstalling ? '⏳ O\'rnatmoqda...' : '📦 APK O\'rnatish'}
          </button>
        ) : (
          <p className="text-xs text-gray-400 italic">APK topilmadi</p>
        )}

        {apkInfo?.hasApk && (
          <button
            onClick={downloadApkFile}
            title="APK faylni yuklab olish — haqiqiy telefonga o'rnatish uchun"
            className="px-3 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-sm font-medium transition"
          >
            📥 APK yuklab olish
          </button>
        )}

        <button
          onClick={launchApp}
          disabled={isLaunching || !apkInfo?.packageName}
          title={apkInfo?.packageName || 'Package name topilmadi'}
          className="px-3 py-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 rounded-lg text-sm font-medium transition"
        >
          {isLaunching ? '⏳...' : '▶ Ishga tushirish'}
        </button>

        <button
          onClick={() => {
            hotReload(activeMobileModule?.workspace_path);
          }}
          disabled={isHotReloading}
          className="px-3 py-2 bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 rounded-lg text-sm font-medium transition"
        >
          {isHotReloading ? '⏳ Reloading...' : '🔥 Hot Reload'}
        </button>
      </div>
    </div>
  );

  // ── 🗄️ Database view (ERD + rows) — bitta helper, HAM Studio tab ichida HAM
  //    alohida-oyna popout'da (/db) ishlatiladi (emulator popout naqshi). ──
  const filterDbTables = (schema) => {
    const all = (schema && schema.tables) || [];
    const terms = dbTableFilter.toLowerCase().split(/[,\s]+/).map(x => x.trim()).filter(Boolean);
    if (!terms.length) return all;
    const base = new Set(all.filter(t => terms.some(q => String(t.name).toLowerCase().includes(q))).map(t => t.name));
    const show = new Set(base);
    if (dbFilterWithRel) {
      for (const r of (schema.relations || [])) {
        if (base.has(r.fromTable)) show.add(r.toTable);
        if (base.has(r.toTable)) show.add(r.fromTable);
      }
    }
    return all.filter(t => show.has(t.name));
  };
  const renderDatabaseInner = () => (
                <div className="flex-1 min-h-0 overflow-auto">
                  {!dbModule ? (
                    <div className="text-center py-16 text-gray-400">
                      <p className="text-4xl mb-3">🗄</p>
                      <p className="font-medium text-gray-500 dark:text-gray-400">Loyihada backend modul yo'q</p>
                      <p className="text-sm mt-1">Ma'lumotlar bazasini ko'rish uchun backend modul kerak.</p>
                    </div>
                  ) : (<>
                  {/* Toolbar */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <button
                      onClick={() => fetchDbSchema(String(dbModule.id))}
                      disabled={dbSchemaLoading}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition"
                    >
                      {dbSchemaLoading ? '⏳ O\'qilmoqda...' : '↻ Yangilash'}
                    </button>
                    {!dbOnly && (
                      <button
                        onClick={() => window.open(`/db?project=${encodeURIComponent(abProjectId || '')}&module=${encodeURIComponent(dbModule.id)}${dbSchemaEnv === 'prod' ? '&env=prod' : ''}`, 'kh_database', 'width=1400,height=1000,noopener')}
                        title="Ma'lumotlar bazasini alohida oynada ochish — jadvallar + yozuvlar; Emulator / Vazifalar bilan yonma-yon ishlash uchun (oynani kattalashtirsangiz chizma ham kengayadi)"
                        className="px-3 py-1.5 text-sm bg-gray-700 text-white hover:bg-gray-800 rounded-lg font-medium transition"
                      >
                        🪟 Alohida oynada ochish
                      </button>
                    )}
                    <span className="text-xs px-2 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 font-medium">📦 {dbModule.name}</span>
                    {dbModule.prod_remote_host && (
                      <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm" role="group"
                        title="Qaysi baza: test muhiti yoki production (production — faqat o'qish, faqat loyiha egasi; maxfiy ustunlar yashiriladi)">
                        {[['test', '🧪 Test'], ['prod', '🚀 Production']].map(([k, label]) => (
                          <button key={k} type="button" disabled={dbSchemaLoading}
                            onClick={() => { if (k === dbSchemaEnv && k === dbEnv) return; setDbEnv(k); setDbSchema(null); setDbRowsModal(null); fetchDbSchema(String(dbModule.id), k); }}
                            className={`px-3 py-1.5 font-medium transition disabled:opacity-50 ${dbEnv === k ? (k === 'prod' ? 'bg-red-600 text-white' : 'bg-primary-700 text-white') : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    {dbSchema?.ok && (
                      <>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">
                          {dbSchema.engine === 'postgres' ? '🐘 PostgreSQL' : dbSchema.engine === 'mysql' ? '🐬 MySQL' : dbSchema.engine === 'mongo' ? '🍃 MongoDB' : dbSchema.engine}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{dbTableFilter.trim() ? `${filterDbTables(dbSchema).length} / ` : ''}{dbSchema.tables.length} ta jadval</span>
                        <input
                          id="db-table-filter"
                          type="search"
                          value={dbTableFilter}
                          onChange={e => setDbTableFilter(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') setDbTableFilter(''); }}
                          placeholder="🔎 Jadval qidirish (client, dengi…)"
                          title="Jadval nomi bo'yicha filtr. Bir nechtasini vergul bilan yozing. Esc — tozalash"
                          className="w-64 max-w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        {dbTableFilter.trim() && (
                          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none" title="Topilgan jadvallarga FK orqali bog'langan jadvallarni ham ko'rsatish">
                            <input type="checkbox" checked={dbFilterWithRel} onChange={e => setDbFilterWithRel(e.target.checked)} />
                            🔗 Bog'liqlari bilan
                          </label>
                        )}
                      </>
                    )}
                    {dbSchemaEnv === 'prod' ? (
                      <span className="text-xs ml-auto font-medium text-red-600 dark:text-red-400" title="Production bazasi faqat o'qiladi: tahrirlash/o'chirish yo'q, parol/token/OTP kabi ustunlar yashiriladi, jadval ochilishi faoliyat lentasiga yoziladi">🚀 PRODUCTION — faqat o'qish{dbSchema?.host ? ` · ${dbSchema.host}` : ''}</span>
                    ) : (
                      <span className="text-xs text-gray-400 ml-auto">🔒 Sxema faqat o'qish — ma'lumotni 👁 orqali tahrirlash mumkin</span>
                    )}
                  </div>

                  {dbSchemaLoading && (!dbSchema || dbSchemaModuleId !== String(dbModule.id)) ? (
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-16 justify-center">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Sxema o'qilmoqda...
                    </div>
                  ) : !dbSchema?.ok ? (
                    <div className="text-center py-16 text-gray-400">
                      <p className="text-4xl mb-3">🗄</p>
                      {dbSchema?.reason === 'no_db' && dbSchemaEnv !== 'prod' ? (
                        <>
                          <p className="font-medium text-gray-500 dark:text-gray-400">Bu modul uchun test-env DB ishlamayapti</p>
                          <p className="text-sm mt-1">DB chizmasini ko'rish uchun avval test muhitni sozlang (test-env setup).</p>
                        </>
                      ) : (
                        <p className="text-sm">{dbSchema?.message || 'Sxema mavjud emas'}</p>
                      )}
                    </div>
                  ) : dbSchema.tables.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <p className="text-4xl mb-3">🗄</p>
                      <p className="text-sm">Jadval topilmadi — migratsiyalar ishga tushganmi?</p>
                    </div>
                  ) : (() => {
                    const visibleTables = filterDbTables(dbSchema);
                    if (!visibleTables.length) return (
                      <div className="text-center py-16 text-gray-400">
                        <p className="text-4xl mb-3">🔎</p>
                        <p className="text-sm">«<span translate="no">{dbTableFilter.trim()}</span>» bo'yicha jadval topilmadi</p>
                        <button onClick={() => setDbTableFilter('')} className="mt-3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">Filtrni tozalash</button>
                      </div>
                    );
                    // Rang jadvalning TO'LIQ ro'yxatdagi o'rniga bog'liq — filtrlaganda o'zgarmasin
                    const colorIdx = {};
                    dbSchema.tables.forEach((t, i) => { colorIdx[t.name] = i; });
                    const boxes = layoutErd(visibleTables);
                    const rectOf = {};
                    boxes.forEach(b => { rectOf[b.table.name] = b; });
                    const totalW = Math.max(700, ...boxes.map(b => b.x + b.w)) + 20;
                    const totalH = Math.max(400, ...boxes.map(b => b.y + b.h)) + 20;
                    return (
                      <div className="relative bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-auto" style={{ minHeight: 400 }}>
                        <div className="relative" style={{ width: totalW, height: totalH }}>
                          {/* FK relationship lines */}
                          <svg className="absolute inset-0 pointer-events-none" style={{ width: totalW, height: totalH, overflow: 'visible', zIndex: 1 }}>
                            <defs>
                              <marker id="erd-arrow" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                                <path d="M0,0 L0,8 L10,4 z" fill="#6366f1"/>
                              </marker>
                            </defs>
                            {dbSchema.relations.map((r, i) => {
                              const fb = rectOf[r.fromTable], tb = rectOf[r.toTable];
                              if (!fb || !tb) return null;
                              const fromRight = (tb.x + tb.w / 2) >= (fb.x + fb.w / 2);
                              const x1 = fromRight ? fb.x + fb.w : fb.x;
                              const y1 = fb.y + fb.h / 2;
                              const x2 = fromRight ? tb.x : tb.x + tb.w;
                              const y2 = tb.y + tb.h / 2;
                              return (
                                <g key={i}>
                                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6366f1" strokeWidth="2" opacity="0.75" markerEnd="url(#erd-arrow)"/>
                                </g>
                              );
                            })}
                          </svg>
                          {/* Table boxes */}
                          {boxes.map((b, idx) => (
                            <div
                              key={b.table.name}
                              style={{ position: 'absolute', left: b.x, top: b.y, width: b.w, zIndex: 2 }}
                              className="group bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden select-none"
                            >
                              <div className="px-3 py-2 text-white font-semibold text-sm flex items-center justify-between gap-2" style={{ background: ERD_PALETTE[(colorIdx[b.table.name] ?? idx) % ERD_PALETTE.length] }}>
                                <span className="truncate">{b.table.name}</span>
                                <button
                                  onClick={() => openDbRows(b.table)}
                                  title="Maʼlumotlarni ko'rish"
                                  className="shrink-0 opacity-0 group-hover:opacity-100 transition hover:bg-white/25 rounded px-1 leading-none"
                                >
                                  👁
                                </button>
                              </div>
                              <div>
                                {b.table.columns.map(col => (
                                  <div key={col.name} className="flex items-center gap-2 px-3 py-1 text-xs border-t border-gray-100 dark:border-gray-700">
                                    <span className="w-4 shrink-0 text-center" title={col.isPK ? 'Primary key' : col.isFK ? 'Foreign key' : ''}>{col.isPK ? '🔑' : col.isFK ? '🔗' : ''}</span>
                                    <span className={`flex-1 truncate ${col.isPK ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`} title={col.name}>{col.name}</span>
                                    <span className="text-gray-400 font-mono truncate" title={col.type}>{col.type}{col.nullable ? '' : ' *'}</span>
                                  </div>
                                ))}
                                {b.table.columns.length === 0 && (
                                  <div className="px-3 py-2 text-xs text-gray-400 italic border-t border-gray-100 dark:border-gray-700">kolleksiya</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  </>)}
                </div>
  );

  // 🗄️ Row-viewer/tahrir modali — HAM asosiy Studio return'da HAM /db popout'da ishlatiladi.
  const renderDbRowsModal = () => {
    if (!dbRowsModal) return null;
    const m = dbRowsModal;
    const isProdRows = m.env === 'prod' || dbSchemaEnv === 'prod';
    const canMutate = (m.pkCols && m.pkCols.length > 0) && !isProdRows;
    const cols = (m.table.columns && m.table.columns.length)
      ? m.table.columns.map(c => c.name)
      : [...new Set(m.rows.flatMap(r => Object.keys(r || {})))];
    const selectedRows = m.rows.filter(r => m.selected.has(dbRowKey(r, m.pkCols)));
    const allSelected = m.rows.length > 0 && m.rows.every(r => m.selected.has(dbRowKey(r, m.pkCols)));
    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget && !m.busy) setDbRowsModal(null); }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                🗄 <span className="font-mono">{m.table.name}</span>
                <span className="text-sm font-normal text-gray-400">({m.total} ta yozuv)</span>
                {isProdRows && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white">🚀 PRODUCTION · faqat o'qish</span>}
              </h2>
              <div className="flex items-center gap-3">
                {/* ⟳ Qayta o'qish (2026-10-10, operator: «view qilganda reload ham bo'lsin») — shu sahifa va o'lcham bilan. */}
                <button onClick={() => loadDbRows(m.table.name, m.page || 1, m.pageSize)} disabled={m.loading || m.busy}
                  title="Yangilash — jadvaldan qiymatlarni qayta o'qish"
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40">
                  <svg className={`w-5 h-5 ${m.loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </button>
                <button onClick={() => setDbRowsModal(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none">✕</button>
              </div>
            </div>
            {/* Toolbar: page-size + selection actions */}
            <div className="flex items-center gap-3 px-5 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-sm flex-wrap">
              <label className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                Sahifada:
                <select value={m.pageSize} disabled={m.loading || m.busy}
                  onChange={e => loadDbRows(m.table.name, 1, parseInt(e.target.value, 10))}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                  {DB_PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <div className="relative">
                <input type="search" value={m.qInput ?? m.q ?? ''} onChange={e => searchDbRows(e.target.value)}
                  placeholder="🔎 Qidirish (barcha ustunlar)…" title="Qatorning istalgan ustunidan qidiradi (katta-kichik harf farqsiz)"
                  className="w-64 border border-gray-200 dark:border-gray-700 rounded-lg pl-2.5 pr-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" />
              </div>
              {m.sort && (
                <button onClick={() => { dbRowsQueryRef.current = { ...dbRowsQueryRef.current, sort: '', dir: 'asc' }; loadDbRows(m.table.name, 1, m.pageSize); }}
                  className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200" title="Saralashni bekor qilish">↕ {m.sort} {m.dir === 'desc' ? '↓' : '↑'} ✕</button>
              )}
              {canMutate && m.selected.size > 0 && (
                <>
                  <span className="text-gray-600 dark:text-gray-400 font-medium">{m.selected.size} ta tanlandi</span>
                  <button disabled={m.busy} onClick={() => deleteDbRows(selectedRows)}
                    className="px-3 py-1.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition">
                    🗑 Tanlanganlarni o'chirish
                  </button>
                  <button disabled={m.busy} onClick={() => setDbRowsModal(x => (x ? { ...x, selected: new Set() } : x))}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">Bekor qilish</button>
                </>
              )}
              {isProdRows && (m.maskedColumns || []).length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400" title="Production'da parol, token, OTP kabi ustunlar qiymati ko'rsatilmaydi">🙈 Yashirilgan: {m.maskedColumns.join(', ')}</span>
              )}
              {!canMutate && !isProdRows && !m.loading && m.rows.length > 0 && (
                <span className="text-amber-600 dark:text-amber-400 text-xs">⚠️ Birlamchi kalit (PK) yo'q — tahrirlash/o'chirish mavjud emas</span>
              )}
              {m.busy && <span className="text-primary-600 dark:text-primary-500 text-xs">⏳ Bajarilmoqda...</span>}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {m.loading ? (
                <div className="text-center py-16 text-gray-400">⏳ Yuklanmoqda...</div>
              ) : !m.rows.length ? (
                <div className="text-center py-16 text-gray-400">{m.q ? `«${m.q}» bo'yicha hech narsa topilmadi` : "Maʼlumot yo'q"}</div>
              ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        {canMutate && (
                          <th className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 w-8">
                            <input type="checkbox" checked={allSelected} onChange={toggleDbSelectAll} disabled={m.busy} />
                          </th>
                        )}
                        {cols.map(c => (
                          <th key={c} onClick={() => !m.loading && sortDbRows(c)} title="Bosing: o'sish ↑ / kamayish ↓ / saralashsiz"
                            className="text-left font-semibold text-gray-600 dark:text-gray-400 px-3 py-2 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap cursor-pointer select-none hover:text-gray-900 dark:hover:text-white">
                            {c}{m.sort === c ? (m.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                          </th>
                        ))}
                        {canMutate && <th className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 w-20 text-right">Amal</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {m.rows.map((row, i) => {
                        const key = dbRowKey(row, m.pkCols);
                        const isEditing = m.editing === i;
                        const isSel = m.selected.has(key);
                        return (
                        <tr key={i} className={`${isSel ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                          {canMutate && (
                            <td className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 text-center">
                              <input type="checkbox" checked={isSel} onChange={() => toggleDbRow(key)} disabled={m.busy || isEditing} />
                            </td>
                          )}
                          {cols.map(c => {
                            const v = row[c];
                            const text = (v === null || v === undefined) ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
                            if (isEditing) {
                              const isPk = m.pkCols.includes(c);
                              const ev = m.editValues[c] || { value: '', isNull: false };
                              return (
                                <td key={c} className="px-2 py-1 border-b border-gray-100 dark:border-gray-700 align-top">
                                  {isPk ? (
                                    <span className="text-gray-400 font-mono text-xs" title="Birlamchi kalit — o'zgartirib bo'lmaydi">{text || <span className="italic">null</span>}</span>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <input
                                        value={ev.isNull ? '' : ev.value}
                                        disabled={ev.isNull}
                                        placeholder={ev.isNull ? 'NULL' : ''}
                                        onChange={e => setDbEditVal(c, { value: e.target.value })}
                                        className="w-full min-w-[90px] border border-primary-200 dark:border-primary-800 rounded px-1.5 py-1 text-xs font-mono focus:border-primary-500 focus:outline-none disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-300 disabled:italic"
                                      />
                                      <button type="button" title="NULL qilish"
                                        onClick={() => setDbEditVal(c, { isNull: !ev.isNull })}
                                        className={`shrink-0 text-[10px] px-1 rounded ${ev.isNull ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-500' : 'text-gray-300 hover:text-gray-500 dark:hover:text-gray-300'}`}>∅</button>
                                    </div>
                                  )}
                                </td>
                              );
                            }
                            return (
                              <td key={c} className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 max-w-xs truncate font-mono text-xs" title={text}>
                                {(v === null || v === undefined) ? <span className="text-gray-300 italic">null</span> : text}
                              </td>
                            );
                          })}
                          {canMutate && (
                            <td className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 text-right whitespace-nowrap">
                              {isEditing ? (
                                <div className="flex items-center gap-1 justify-end">
                                  <button disabled={m.busy} onClick={saveDbEdit} title="Saqlash" className="px-1.5 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-40">💾</button>
                                  <button disabled={m.busy} onClick={cancelDbEdit} title="Bekor qilish" className="px-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40">✕</button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 justify-end">
                                  <button disabled={m.busy || m.editing !== null} onClick={() => startDbEdit(i)} title="Tahrirlash" className="px-1.5 text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-40">✏️</button>
                                  <button disabled={m.busy || m.editing !== null} onClick={() => deleteDbRows([row])} title="O'chirish" className="px-1.5 text-red-400 hover:text-red-600 dark:hover:text-red-300 disabled:opacity-40">🗑</button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
              )}
            </div>
            {m.total > m.pageSize && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  {((m.page - 1) * m.pageSize) + 1}–{Math.min(m.page * m.pageSize, m.total)} / {m.total}
                </span>
                <div className="flex items-center gap-2">
                  <button disabled={m.page <= 1 || m.loading || m.busy}
                    onClick={() => loadDbRows(m.table.name, m.page - 1, m.pageSize)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition">‹ Oldingi</button>
                  <span className="text-gray-600 dark:text-gray-400">{m.page} / {Math.max(1, Math.ceil(m.total / m.pageSize))}</span>
                  <button disabled={m.page >= Math.ceil(m.total / m.pageSize) || m.loading || m.busy}
                    onClick={() => loadDbRows(m.table.name, m.page + 1, m.pageSize)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition">Keyingi ›</button>
                </div>
              </div>
            )}
          </div>
        </div>
    );
  };

  // ── Standalone pop-out window (/emulators) — just the emulator grid, its own
  //    socket = independent streams, so you can watch it beside DB/Vazifalar. ──
  if (emulatorOnly) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-3">
        <Toaster position="top-right" toastOptions={{ className: "dark:!bg-gray-700 dark:!text-white" }} />
        {/* 🔊 Ovozli sinov subtitri shu oynada ham (agentni shu yerda kuzatasiz); ovoz sukut bo'yicha asosiy oynada. */}
        <NarrationPlayer apiUrl={process.env.REACT_APP_API_URL} token={token} projectId={abProjectId || projectId} where="emu" />
        <div className="max-w-[1900px] mx-auto">
          {/* 🤖 Agent sinovi qatori bu oynadan OLIB TASHLANDI (operator, 2026-09-18): sinov
              endi faqat Studio → 🧪 Sinov tabida boshlanadi; popout = faqat emulatorlar (qator
              gridni pastga surib, ochiq sinov natijasi kataklarni to'sib qo'yardi). Jonli agent
              holati kataklarda (🤖 belgisi, ripple, ✋) va to'liq ekranda (AgentQaFsStrip) qoladi. */}
          {renderEmulatorGrid(null, popoutSlot)}
          {(serverRunning || serverLogs.length > 0) && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">🔨 Build loglari {serverRunning && '(davom etmoqda...)'}</p>
              <div className="bg-gray-900 rounded-lg p-3 max-h-56 overflow-y-auto">
                {serverLogs.map((l, i) => (
                  <div key={i} className={`text-xs font-mono whitespace-pre-wrap ${l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : 'text-gray-300'}`}>{l.log}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Standalone pop-out window (/db?project=&module=) — just the DB ERD + rows,
  //    so you can watch it beside the Emulator / Vazifalar windows (emulator naqshi). ──
  if (dbOnly) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Toaster position="top-right" toastOptions={{ className: "dark:!bg-gray-700 dark:!text-white" }} />
        <div className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <span className="text-lg font-bold text-gray-900 dark:text-white">🗄️ Ma'lumotlar bazasi</span>
          {dbModule && <span className="text-xs px-2 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 font-medium">📦 {dbModule.name}</span>}
          <button onClick={() => { try { window.close(); } catch {} }}
            className="ml-auto text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm">✕ Yopish</button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col p-4">
          {renderDatabaseInner()}
        </div>
        {renderDbRowsModal()}
      </div>
    );
  }

  // ❓ Yordam uchun "hozir qayerdaman" — javob aynan shu bo'limga tegishli bo'lsin.
  // Barcha state e'lon qilingandan KEYIN hisoblanadi (TDZ oq ekran tuzog'i).
  const helpArea = (activeTab === 'design' && studioView === 'logo') ? 'logo' : activeTab === 'studio'
    ? ({
        canvas: 'studio.canvas', pages: 'studio.compare', style: 'studio.style', browser: 'studio.browser',
        emulator: 'studio.emulator', database: 'studio.database', qa: 'studio.qa',
        crashes: 'studio.crashes', publish: 'studio.publish',
        files: 'studio.files', status: 'studio.status', security: 'studio.security',
        todo: 'studio.todo', architecture: 'studio.architecture', docs: 'studio.docs', map: 'studio.map',
      }[studioView] || 'studio.task')
    : activeTab === 'projects'
      // Modul sahifasi ham shu tab ostida ochiladi — o'shanda bo'lim 'module'
      // bo'lishi kerak, aks holda modul yozuvlari ustunlik ballini olmaydi.
      ? (selectedModule ? 'module' : 'projects')
      : ({
          processes: 'processes', logo: 'logo', health: 'health',
          configuration: 'config', users: 'users', console: 'console',
          emulators: 'studio.emulator', design: 'studio.canvas', pages: 'studio.compare',
          seckb: 'seckb', knowledge: 'knowledge', forecast: 'forecast', reengineering: 'reengineering', integrations: 'integrations',
        }[activeTab] || 'general');

  // 📋 Vazifa berish formasi — endi ⏳ Jarayonlar popupida (istalgan varaqdan
  // ochiladi), pastki panjarada emas. ELEMENT sifatida saqlanadi (komponent
  // EMAS): inline komponent har render'da qayta mount bo'lib, matn yozayotganda
  // fokusni yo'qotardi.
  const resetRefine = () => { setRefineOpen(false); setRefineMsgs([]); setRefineSession(null); setRefineInput(''); setRefineActivity([]); };
  // 📎 Formadagi biriktirmalar → /uploads (dizayn/logo PNG'ga) — vazifa yuborishdagi bilan bir xil; kesh bilan.
  const collectChatAttachments = async () => {
    const cache = chatUploadCache.current;
    const images = [];
    for (const it of newTaskImages) {
      let u = cache.get(it.file);
      if (!u) { u = await uploadImageFile(it.file); if (u) cache.set(it.file, u); }
      if (u) images.push({ url: u, kind: 'image' });
    }
    const raster = async (key, svg, w0, h0, name, kind, label) => {
      let u = cache.get(key);
      if (!u) {
        const vb = svg.match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
        const w = vb ? Math.round(parseFloat(vb[1])) : w0, h = vb ? Math.round(parseFloat(vb[2])) : h0;
        try {
          const blob = dataURLtoBlob(await svgToPngDataUrl(svg, w, h));
          u = await uploadImageFile(new File([blob], name, { type: 'image/png' }));
          if (u) cache.set(key, u);
        } catch (e) { u = null; }
      }
      if (u) images.push({ url: u, kind, label });
    };
    for (const id of selectedDesignIds) { const pg = abPages.find(p => p.id === id); if (pg?.svg_content) await raster(`d${id}`, pg.svg_content, 390, 844, `design-${id}.png`, 'design', pg.title); }
    for (const id of selectedLogoIds) { const lg = logos.find(l => l.id === id); if (lg?.svg_content) await raster(`l${id}`, lg.svg_content, 512, 512, `logo-${id}.png`, 'logo', lg.name); }
    return { images, files: newTaskFiles.map(f => ({ path: f.path, name: f.name })) };
  };
  // 💬 Vazifa suhbati: matn maydonidagi xabar + biriktirmalar agentga (services/taskRefine.js) — u kod/baza/
  // emulator/brauzer/konsolni ko'rib javob beradi, kod o'zgarishi kerak bo'lsa <VAZIFA> beradi.
  const sendRefine = async () => {
    const pid = Number(projectId || abProjectId) || 0;
    if (!pid) { toast.error('Avval proyekt tanlang'); return; }
    if (refineBusy) return;
    const message = newTask.trim();
    if (!message) { toast.error('Xabar yozing'); return; }
    const history = refineMsgs.filter(m => m.role !== 'err')
      .map(m => ({ role: m.role === 'user' ? 'user' : 'ai', text: m.draft ? `${m.text}\n<VAZIFA>${m.draft}</VAZIFA>` : m.text }));
    const nAtt = newTaskImages.length + selectedDesignIds.length + selectedLogoIds.length + newTaskFiles.length;
    setRefineMsgs(prev => [...prev, { role: 'user', text: message, att: nAtt }]);
    setNewTask(''); setRefineBusy(true); setRefineOpen(true); setRefineActivity([]);
    const base = process.env.REACT_APP_API_URL;
    try {
      const attachments = await collectChatAttachments();
      const r = await authFetch(`${base}/task-refine`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: pid, message, sessionId: refineSession, history, attachments }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Xatolik');
      let res = null;
      for (let i = 0; i < 280; i++) {   // ≤ ~14 daqiqa (emulator/konsol bilan ishlash uzoqroq)
        await new Promise(ok => setTimeout(ok, 3000));
        const g = await authFetch(`${base}/task-refine/${d.jobId}`);
        const j = await g.json().catch(() => ({}));
        if (!g.ok) throw new Error(j.error || 'Xatolik');
        if (Array.isArray(j.activity)) setRefineActivity(j.activity);
        if (j.status !== 'running') { res = j; break; }
      }
      if (!res) throw new Error('Javob juda uzoq kechikdi — qayta yuboring');
      if (res.status === 'error') throw new Error(res.error || 'Xatolik');
      if (res.sessionId) setRefineSession(res.sessionId);
      setRefineMsgs(prev => [...prev, { role: 'ai', text: res.reply || '', draft: res.draft || null }]);
    } catch (e) {
      if (e && e.message !== 'unauthorized') setRefineMsgs(prev => [...prev, { role: 'err', text: `⚠️ ${e.message}` }]);
    } finally { setRefineBusy(false); setRefineActivity([]); }
  };

  const taskFormEl = (
                      <div className="kh-task-form bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-lg p-4 w-full lg:w-96 shrink-0">
                        {/* Selected project — bitta ixcham qator (label + select yonma-yon) */}
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide shrink-0">📁 Proyekt</label>
                          <select
                            value={abProjectId}
                            onChange={e => selectStudioProject(e.target.value)}
                            className="flex-1 min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-700 focus:bg-white dark:focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 transition"
                          >
                            <option value="">Proyekt tanlang...</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        {/* 💬 Vazifa suhbati — agent kod/baza/emulator/brauzer/konsolni ko'rib javob beradi */}
                        {refineMsgs.length > 0 && (
                          <div className="mb-2 border border-primary-200 dark:border-primary-800 rounded-lg bg-primary-50/40 dark:bg-gray-900/40">
                            <div className="flex items-center justify-between px-2 py-1 border-b border-primary-100 dark:border-gray-700">
                              <span className="text-[11px] font-semibold text-primary-700 dark:text-primary-400">💬 Suhbat — kod, baza, 📱 emulator, 🌐 brauzer, 🔐 konsolni ko'radi</span>
                              <button type="button" onClick={resetRefine} disabled={refineBusy} className="text-[11px] text-gray-400 hover:text-red-500 disabled:opacity-40" title="Suhbatni yangidan boshlash">🗑 Tozalash</button>
                            </div>
                            <div className="kh-refine-box relative max-h-96 overflow-y-auto p-2 space-y-2 text-xs" ref={refineBoxRef}>
                              {refineMsgs.map((m, i) => (
                                <div key={i} data-kh-msg="1" className={m.role === 'user'
                                  ? 'ml-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-gray-800 dark:text-gray-200'
                                  : (m.role === 'err' ? 'text-red-600 dark:text-red-400' : 'mr-2 text-gray-800 dark:text-gray-200')}>
                                  <div className="whitespace-pre-wrap break-words" translate={m.role === 'user' ? 'no' : undefined}>{m.text}</div>
                                  {m.role === 'user' && m.att > 0 && <div className="mt-0.5 text-[10px] text-gray-400">📎 {m.att} ta biriktirma</div>}
                                  {m.draft && (
                                    <div className="mt-1.5 border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded p-2">
                                      <div className="text-[10px] font-semibold text-green-700 dark:text-green-400 mb-1">📋 Vazifa</div>
                                      <div className="whitespace-pre-wrap break-words max-h-48 overflow-y-auto" translate="no">{m.draft}</div>
                                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                                        <button type="button" disabled={addingTask || !abProjectId} onClick={() => handleAddTask(m.draft)}
                                          className="px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-[11px] font-semibold disabled:opacity-50">
                                          {addingTask ? '⏳ Yuborilmoqda…' : '🚀 Vazifa qilib yuborish'}</button>
                                        <button type="button" onClick={() => setNewTask(m.draft)}
                                          className="px-2 py-1 rounded border border-green-400 dark:border-green-700 text-green-700 dark:text-green-400 text-[11px]">✏️ Formaga (tahrirlash)</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {refineBusy && (
                                <div className="text-gray-500 dark:text-gray-400 italic">
                                  ⏳ Ishlayapman…
                                  {refineActivity.length > 0 && (
                                    <div className="not-italic mt-1 space-y-0.5 font-mono text-[10px] text-gray-400 dark:text-gray-500">
                                      {refineActivity.slice(-5).map((a, i) => <div key={i} className="truncate" translate="no">{a}</div>)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <textarea
                          className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                          rows="3"
                          placeholder={refineMsgs.length ? "Javob yoki savol yozing… (⌘/Ctrl+Enter — suhbatga)" : "Vazifa yoki savol yozing… (rasm uchun Ctrl+V; ⌘/Ctrl+Enter — suhbatga)"}
                          value={newTask}
                          onChange={e => setNewTask(e.target.value)}
                          onPaste={handleTaskImagePaste}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendRefine(); } }}
                        />
                        {newTaskFiles.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {newTaskFiles.map((fl, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
                                <span title={fl.name}>{/\.(mp3|wav|ogg|m4a|aac)$/i.test(fl.name) ? '🎵' : '📄'} {fl.name.length > 28 ? fl.name.slice(0, 26) + '…' : fl.name}</span>
                                <button onClick={() => setNewTaskFiles(prev => prev.filter((_, idx) => idx !== i))}
                                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-300">×</button>
                              </span>
                            ))}
                          </div>
                        )}
                        {newTaskImages.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {newTaskImages.map((img, i) => (
                              <div key={i} className="relative group w-16 h-16 rounded overflow-hidden border border-gray-300 dark:border-gray-600 shadow-sm">
                                <img src={img.preview} alt="" title="Katta qilib ko'rish" className="w-full h-full object-cover cursor-zoom-in"
                                  onClick={() => setImgViewer({ images: newTaskImages.map(x => x.preview), index: i })} />
                                <button
                                  onClick={() => setNewTaskImages(prev => prev.filter((_, idx) => idx !== i))}
                                  className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* IXCHAM biriktirish qatori: 📎 rasm + 🎨 dizayn + 🏷️ logo — tanlash
                            popup (modal) orqali; tanlanganda son + kichik preview'lar chiqadi. */}
                        {(() => {
                          const designs = visibleAbPages.filter(p => p.svg_content);
                          const allLogos = logos.filter(l => l.svg_content);
                          const selDesigns = designs.filter(d => selectedDesignIds.includes(d.id));
                          const selLogos = allLogos.filter(l => selectedLogoIds.includes(l.id));
                          const chipCls = 'px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/40 text-[11px] font-medium text-gray-600 dark:text-gray-400 transition disabled:opacity-40 disabled:cursor-not-allowed';
                          return (
                            <div className="mb-3">
                              <input
                                ref={taskFileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={e => { addImagesToQueue(e.target.files); e.target.value = ''; }}
                              />
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <button type="button" onClick={() => taskFileInputRef.current?.click()} className={chipCls} title="Rasm biriktirish (yoki matn maydoniga Ctrl+V)">
                                  📎 Rasm{newTaskImages.length > 0 && <span className="text-primary-700 dark:text-primary-500 font-semibold"> {newTaskImages.length}</span>}
                                </button>
                                <input
                                  ref={taskDocInputRef}
                                  type="file"
                                  accept=".html,.htm,.pdf,.docx,.pptx,.md,.txt,.csv,.json,.svg,.xml,.yml,.yaml,.mp3,.wav,.ogg,.m4a,.aac"
                                  multiple
                                  className="hidden"
                                  onChange={e => { addDocsToTask(e.target.files); e.target.value = ''; }}
                                />
                                <button type="button" onClick={() => taskDocInputRef.current?.click()} disabled={uploadingDoc} className={chipCls}
                                  title="HTML / PDF / matn fayl biriktirish — agent uni O'QIYDI (masalan: tayyor HTML tashlab «shunga o'xshash sayt qil» deyish). 🎵 Ovoz fayli (mp3/wav/ogg/m4a) — agent uni ilovaga qo'shadi (masalan bildirishnoma ovozi)">
                                  {uploadingDoc ? '⏳' : '📄'} Fayl{newTaskFiles.length > 0 && <span className="text-primary-700 dark:text-primary-500 font-semibold"> {newTaskFiles.length}</span>}
                                </button>
                                <button type="button" disabled={designs.length === 0} onClick={() => setDesignPickerOpen(true)} className={chipCls}
                                  title={designs.length === 0 ? "Bu proyektda dizayn yo'q (🖼️ Dizayn bo'limida yarating)" : "Dizayn biriktirish — agent shu dizayn(lar)ga qarab kod yozadi. Ko'p rasm = ko'proq token."}>
                                  🎨 Dizayn{selectedDesignIds.length > 0 && <span className="text-primary-700 dark:text-primary-500 font-semibold"> {selectedDesignIds.length}</span>}
                                </button>
                                <button type="button" disabled={allLogos.length === 0} onClick={() => setLogoPickerOpen(true)} className={chipCls}
                                  title={allLogos.length === 0 ? "Logo yo'q — Dizayn → 🏷 Logo'da yarating" : "Logo biriktirish — vazifaga vision-ref sifatida qo'shiladi"}>
                                  🏷️ Logo{selectedLogoIds.length > 0 && <span className="text-primary-700 dark:text-primary-500 font-semibold"> {selectedLogoIds.length}</span>}
                                </button>
                              </div>
                              {(selDesigns.length > 0 || selLogos.length > 0) && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {selDesigns.map(d => (
                                    <div key={`d${d.id}`} className="relative group rounded-md overflow-hidden border-2 border-primary-500" style={{ width: 44, height: 56 }} title={d.title}>
                                      <div className="w-full h-full bg-gray-50 dark:bg-gray-700 overflow-hidden" dangerouslySetInnerHTML={{ __html: d.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%;object-fit:cover"') }} />
                                      <button type="button" onClick={() => setSelectedDesignIds(prev => prev.filter(x => x !== d.id))} className="absolute top-0 right-0 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">×</button>
                                    </div>
                                  ))}
                                  {selLogos.map(l => (
                                    <div key={`l${l.id}`} className="relative group rounded-md overflow-hidden border-2 border-primary-500 bg-gray-50 dark:bg-gray-700" style={{ width: 44, height: 44 }} title={l.name}>
                                      <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: l.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%"') }} />
                                      <button type="button" onClick={() => setSelectedLogoIds(prev => prev.filter(x => x !== l.id))} className="absolute top-0 right-0 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">×</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Design picker modal */}
                        {designPickerOpen && (() => {
                          const designs = visibleAbPages.filter(p => p.svg_content);
                          return (
                            <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setDesignPickerOpen(false)}>
                              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-between p-4 border-b">
                                  <h3 className="font-semibold text-gray-700 dark:text-gray-300">🎨 Dizayn tanlash <span className="text-primary-700 dark:text-primary-500 text-sm font-normal">({selectedDesignIds.length} tanlandi)</span></h3>
                                  <button type="button" onClick={() => setDesignPickerOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl leading-none">×</button>
                                </div>
                                <div className="p-4 overflow-y-auto grid grid-cols-3 sm:grid-cols-4 gap-3">
                                  {designs.map(d => {
                                    const sel = selectedDesignIds.includes(d.id);
                                    return (
                                      <button key={d.id} type="button" onClick={() => setSelectedDesignIds(prev => sel ? prev.filter(x => x !== d.id) : [...prev, d.id])} title={d.title} className={`relative rounded-lg overflow-hidden border-2 transition ${sel ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'}`} style={{ height: 150 }}>
                                        <div className="w-full h-full bg-gray-50 dark:bg-gray-700 overflow-hidden" dangerouslySetInnerHTML={{ __html: d.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%;object-fit:contain"') }} />
                                        {sel && <span className="absolute top-1 right-1 bg-primary-700 dark:bg-primary-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">✓</span>}
                                        {d.title && <span className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-[10px] px-1 py-0.5 truncate">{d.title}</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="p-3 border-t flex justify-between items-center">
                                  <button type="button" onClick={() => setSelectedDesignIds([])} className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-300">Tozalash</button>
                                  <button type="button" onClick={() => setDesignPickerOpen(false)} className="bg-primary-700 dark:bg-primary-600 text-white px-5 py-1.5 rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700">Tayyor</button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Logo picker modal */}
                        {logoPickerOpen && (() => {
                          const allLogos = logos.filter(l => l.svg_content);
                          return (
                            <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setLogoPickerOpen(false)}>
                              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-between p-4 border-b">
                                  <h3 className="font-semibold text-gray-700 dark:text-gray-300">🏷️ Logo tanlash <span className="text-primary-700 dark:text-primary-500 text-sm font-normal">({selectedLogoIds.length} tanlandi)</span></h3>
                                  <button type="button" onClick={() => setLogoPickerOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl leading-none">×</button>
                                </div>
                                <div className="p-4 overflow-y-auto grid grid-cols-3 sm:grid-cols-5 gap-3">
                                  {allLogos.map(l => {
                                    const sel = selectedLogoIds.includes(l.id);
                                    return (
                                      <button key={l.id} type="button" onClick={() => setSelectedLogoIds(prev => sel ? prev.filter(x => x !== l.id) : [...prev, l.id])} title={l.name} className={`relative rounded-lg overflow-hidden border-2 transition bg-gray-50 dark:bg-gray-700 ${sel ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'}`} style={{ height: 110 }}>
                                        <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: l.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%"') }} />
                                        {sel && <span className="absolute top-1 right-1 bg-primary-700 dark:bg-primary-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">✓</span>}
                                        {l.name && <span className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-[10px] px-1 py-0.5 truncate">{l.name}</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="p-3 border-t flex justify-between items-center">
                                  <button type="button" onClick={() => setSelectedLogoIds([])} className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-300">Tozalash</button>
                                  <button type="button" onClick={() => setLogoPickerOpen(false)} className="bg-primary-700 dark:bg-primary-600 text-white px-5 py-1.5 rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700">Tayyor</button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {!isProjClient && <ContextWindowPanel sessions={contextSessions} newSessionMode={newSessionMode} setNewSessionMode={setNewSessionMode} runMode={runMode} setRunMode={setRunMode} autoDeploy={autoDeploy} setAutoDeploy={setAutoDeploy} planReview={planReview} setPlanReview={setPlanReview} afterTest={afterTest} setAfterTest={setAfterTest} />}

                        <div className="flex gap-2">
                        {!isProjClient && (
                        <button type="button"
                          className="flex-1 border border-primary-600 dark:border-primary-500 text-primary-700 dark:text-primary-300 bg-white dark:bg-gray-800 px-3 py-2.5 rounded-lg font-semibold hover:bg-primary-50 dark:hover:bg-primary-900/30 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                          onClick={sendRefine}
                          disabled={refineBusy || !newTask.trim() || !abProjectId}
                          title="Agentga yozish — u kod, test baza, emulator, brauzer va konsolni ko'rib javob beradi; kerak bo'lsa vazifa tuzadi (kod o'zgarmaydi)"
                        >
                          {refineBusy ? '⏳' : '💬'} Suhbat
                        </button>
                        )}
                        <button
                          className="flex-1 bg-primary-700 dark:bg-primary-600 text-white px-3 py-2.5 rounded-lg font-semibold shadow-sm hover:bg-primary-800 dark:hover:bg-primary-700 transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                          onClick={handleAddTask}
                          disabled={addingTask || !newTask.trim() || !abProjectId}
                          title="To'g'ridan-to'g'ri vazifa sifatida yuborish (developer agent bajaradi)"
                        >
                          {addingTask && <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                          {addingTask ? 'Yuborilmoqda…' : '🚀 Vazifa'}
                        </button>
                        </div>
                      </div>
  );

  // ── Flowbite uslubidagi qobiq: tepada oq navbar, chapda oq ikonkali sidebar,
  //    kontent kulrang (gray-50) fonda. Bo'limlar ma'lumotdan chiziladi — ko'rinish
  //    sharti (`show`) avvalgi tugmalardagi bilan AYNAN bir xil.
  const isAdminUser = user?.role === 'admin';
  const navItems = [
    { key: 'projects',      label: 'Proyektlar',          icon: 'folder',    group: 1, show: isAdminUser },
    { key: 'tasks',         label: 'Vazifalar',           icon: 'tasks',     group: 1, show: isAdminUser },
    { key: 'studio',        label: 'Studio',              icon: 'studio',    group: 1, show: !isAdminUser && canSee('studio') },
    { key: 'design',        label: 'Dizayn',              icon: 'brush',     group: 1, show: !isAdminUser && canSee('design') },
    { key: 'processes',     label: 'Jarayonlar',          icon: 'clock',     group: 1, show: canSee('processes') },
    { key: 'smm',           label: 'SMM',                 icon: 'megaphone', group: 1, show: canSee('smm') },
    { key: 'callcenter',    label: 'Call Center',         icon: 'call',      group: 1, show: canSee('callcenter') },
    { key: 'configuration', label: 'Konfiguratsiya',      icon: 'cog',       group: 2, show: isAdminUser },
    { key: 'users',         label: 'Foydalanuvchilar',    icon: 'users',     group: 2, show: isAdminUser },
    { key: 'emulators',     label: 'Emulatorlar',         icon: 'phone',     group: 2, show: isAdminUser },
    { key: 'forecast',      label: 'Prognoz',             icon: 'chart',     group: 2, show: canSee('forecast') },
    { key: 'reengineering', label: 'Reengineering',       icon: 'wrench',    group: 2, show: canSee('reengineering') },
    { key: 'integrations',  label: 'Integratsiyalar',     icon: 'plug',      group: 2, show: canSee('integrations') },
    { key: 'knowledge',     label: 'Tajriba bazasi',      icon: 'book',      group: 2, show: canSee('knowledge') },
    { key: 'seckb',         label: 'Xavfsizlik bilimlari', icon: 'shield',   group: 2, show: isAdminUser },
    { key: 'console',       label: 'Konsollar',           icon: 'lock',      group: 2, show: !!canSeeConsole },
  ].filter(it => it.show);
  // Navbar'dagi joriy bo'lim: sidebar'da bo'lmagan tablar (🩺 Salomatlik flyout'dan ochiladi,
  // 🔒 ruxsat yo'q ekrani) uchun zaxira — ikonkalar NAV_ICON_PATHS'dan.
  const NAV_EXTRA = { health: { label: 'Salomatlik', icon: 'heart' }, noaccess: { label: "Ruxsat yo'q", icon: 'lock' } };
  const curNav = navItems.find(it => it.key === activeTab) || NAV_EXTRA[activeTab] || null;
  const renderNavGroup = (group) => (
    <ul className="py-3 space-y-1">
      {navItems.filter(it => it.group === group).map(it => {
        const active = activeTab === it.key;
        return (
          <li key={it.key}>
            <button
              onClick={() => { setActiveTab(it.key); setMobileNavOpen(false); }}
              title={it.label}
              className={`flex items-center w-full p-2 text-base rounded-lg group transition ${active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'} ${navCollapsed ? 'justify-center' : ''}`}
            >
              <NavIcon name={it.icon} className={`w-6 h-6 shrink-0 transition duration-75 group-hover:text-gray-900 dark:group-hover:text-white ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`} />
              {!navCollapsed && <span className="ml-3 truncate">{it.label}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Navbar (Flowbite: fixed, oq, pastki chiziq) */}
      <nav className="fixed z-30 w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start min-w-0">
              <button
                onClick={() => setMobileNavOpen(o => !o)}
                title="Menyu"
                className="p-2 text-gray-600 dark:text-gray-400 rounded-lg cursor-pointer lg:hidden hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-700"
              >
                <NavIcon name="menu" className="w-6 h-6" />
              </button>
              <button
                onClick={() => setNavCollapsed(c => !c)}
                title={navCollapsed ? "Sidebar'ni kengaytirish" : "Sidebar'ni yig'ish"}
                className="hidden p-2 text-gray-600 dark:text-gray-400 rounded-lg cursor-pointer lg:inline-flex hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-700"
              >
                <NavIcon name="menu" className="w-6 h-6" />
              </button>
              <div className="flex items-center ml-1 sm:ml-2 md:mr-4 shrink-0">
                <img src="/logo192.png" alt="" className="kh-brand-logo h-8 w-8 mr-2 sm:mr-3 rounded-lg object-cover shrink-0" />
                {/* Versiya — logotip matni OSTIDA (menyuda alohida joy olmasin, operator 2026-09-18) */}
                <div className="hidden sm:flex flex-col items-start justify-center h-10 leading-none">
                  <span className="text-xl font-semibold sm:text-2xl whitespace-nowrap text-gray-900 dark:text-white leading-none">Khorezmai</span>
                  <VersionBadge authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
                </div>
              </div>
              {/* Joriy bo'lim — «› [ikonka] Nom» HAR sidebar bandi uchun (operator 2026-09-19: «hamma
                  sidemenu bosilganda tepada Studio dek chiqsin»). Ikonka sidebar'dagi NavIcon'ning O'ZI —
                  emoji (🏗️/⏳) aralashmasin. Studio/Dizayn sarlavhasi komand-bar'dan shu yerga ko'chgan edi. */}
              {curNav && (
                <span className="hidden md:flex items-center gap-1.5 ml-2 text-base font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                  <span className="text-gray-300 dark:text-gray-600 font-normal mr-1">›</span>
                  <NavIcon name={curNav.icon} className="w-5 h-5 shrink-0 text-gray-700 dark:text-gray-300" />
                  {curNav.label}
                </span>
              )}
              {/* 📁 Loyiha tanlash — tepada (Studio/Dizayn/Logo/vazifa formasi bilan lockstep:
                  selectStudioProject uchala legacy selector'ni birga yuritadi). */}
              {projects.length > 0 && (
                <label className="flex items-center ml-2 min-w-0 shrink sm:shrink-0" title="Qaysi loyiha ustida ishlayotganingiz — Studio, Dizayn, Logo va vazifa formasi shu tanlovga ergashadi">
                  <select
                    value={abProjectId}
                    onChange={e => selectStudioProject(e.target.value)}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-700 focus:bg-white dark:focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 transition min-w-0 max-w-[9.5rem] sm:max-w-[14rem]"
                  >
                    <option value="">Loyiha tanlang…</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              )}
              {/* 👥 Jamoa — Studio tablari qatoridan loyiha tanlagich yoniga ko'chdi (operator, 2026-09-25):
                  jamoa LOYIHAGA tegishli, shuning uchun loyiha tanlangandagina chiqadi va istalgan
                  bo'limdan bir bosishda Studio → Jamoa ni ochadi. */}
              {abProjectId && !isAdminUser && canSee('studio') && !isProjClient && uiShow('studio.team') && (
                <button type="button" onClick={() => { setActiveTab('studio'); setStudioView('team'); }}
                  title="Loyiha jamoasi: a'zolar, rollar, taklif havolasi"
                  className={`ml-2 h-9 min-w-0 shrink inline-flex items-center gap-1.5 px-3 rounded-lg border text-sm font-medium transition ${activeTab === 'studio' && studioView === 'team' ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  <NavIcon name="users" className="w-4 h-4 shrink-0" />
                  {/* «TezHalga jamoa qo'shish» (operator, 2026-09-25) — loyiha nomi + jo'nalish qo'shimchasi:
                      k/g bilan tugasa -ka, q bilan -qa, qolganida -ga. Nom tarjima qilinmaydi. */}
                  {(() => {
                    const nm = String((projects.find(p => String(p.id) === String(abProjectId)) || {}).name || '').trim();
                    const suf = /[kg]$/i.test(nm) ? 'ka' : /q$/i.test(nm) ? 'qa' : 'ga';
                    return nm
                      ? <span className="hidden sm:inline-block max-w-[16rem] truncate"><span translate="no">{nm}{suf}</span> jamoa qo'shish</span>
                      : <span className="hidden sm:inline">Jamoa</span>;
                  })()}
                </button>
              )}
            </div>
            {/* O'ng tomon — BITTA flex qator, hammasi bir xil balandlik (h-10) va bo'shliq (gap-2):
                ⚡ sarf · 🩺 salomatlik · 👤 kutilmoqda · ⏳ jarayonlar. Ilgari har biri `fixed right-[N rem]`
                edi — o'lcham/joylashuv har xil chiqardi (operator, 2026-09-18). Popuplar `fixed top-16 right-4`. */}
            <div className="flex items-center gap-1.5 sm:gap-2 ml-2 min-w-0 shrink overflow-x-auto sm:shrink-0 sm:overflow-visible">
              {/* 🖱 Bu nima? — navbar'da (ilgari o'ng-pastdagi ❓ edi, «Yuborish» tugmalarini to'sardi). `area` joriy
                  ko'rinishdan — javob aynan shu bo'limga tegishli bo'ladi. */}
              <HelpWidget authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} area={helpArea} projectId={abProjectId} />
              {/* 🖥 Serverlar — CPU/RAM/Disk/tarmoq; tugmada eng yuqori CPU (403 bo'lsa umuman chizilmaydi) */}
              <ServerStats flyout authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
              {/* 📜 Engine loglari — ilgari Studio pastidagi «Engine loglar & Vazifa berish» paneli (2026-11-04). */}
              {uiShow('studio.engine_panel') && (
                <EngineLogFlyout logs={liveLogs} onClear={() => setLiveLogs([])} />
              )}
              {/* ⚡ Mening sarfim — tepada, 5 soatlik foiz tugmaning o'zida; bosilsa batafsil popup.
                  Admin uchun EMAS — uning sarfi 👥 Token sarfi modalida (AdminTokenStats). */}
              {user?.role !== 'admin' && !isPureClient && uiShow('nav.tokens') && (
                <TokenStats flyout authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
              )}
              {/* 🩺 Salomatlik — tepada, ⏳ Jarayonlar va 👤 yonida: ishlamayotgan servislar soni badge'da,
                  popup'da faqat nosozlar. Kim salomatlik bo'limini ko'ra olsa, u ham ko'radi. */}
              {(user?.role === 'admin' || canSee('health')) && (
                <HealthFlyout
                  authFetch={authFetch}
                  apiUrl={process.env.REACT_APP_API_URL}
                  onOpenFull={() => setActiveTab('health')}
                />
              )}
              {/* 👤 Sizdan kutilmoqda — platforma to'xtab odamni kutayotgan hamma narsa
                  (savol, tasdiq, konsol login, rejadagi 👤 qadam) bitta ro'yxatda. Ilgari
                  ular turli ekranlarda yotardi va konsol login umuman xabar bermasdi. */}
              {uiShow('nav.waiting') && <WaitingPanel
                authFetch={authFetch}
                apiUrl={process.env.REACT_APP_API_URL}
                onOpenTask={() => setActiveTab(user?.role === 'admin' ? 'tasks' : 'processes')}
                onOpenConsole={canSeeConsole ? () => setActiveTab('console') : null}
                onOpenPlaybook={(it) => setPlaybookRunId(it.runId)}
                onOpenTodo={(it) => {
                  if (it.projectId) selectStudioProject(it.projectId);
                  setActiveTab('studio'); setStudioView('todo');
                }}
                agentOkFor={(it) => !!(it.projectId || abProjectId) && agentTestOkFor({ project_id: it.projectId || abProjectId })}
                onAskAgent={(it, text) => {
                  // 🧠 Agentga — element loyihasiga o'tib (Agent tabi tanlangan loyihaniki), matn yozuv maydoniga tushadi.
                  const other = it.projectId && String(it.projectId) !== String(abProjectId);
                  if (other) selectStudioProject(String(it.projectId));
                  setTimeout(() => setAgentDraftExt({ text, at: Date.now() }), other ? 400 : 0);
                  toast("🧠 Agent yozuv maydoniga qo'yildi — izoh qo'shib Enter bosing");
                }}
              />}
              {/* Global Jarayonlar flyout — pinned top-right on every tab (peek at running work
                  without leaving the Emulator / Database / Browser view). */}
              {/* ⏳ Jarayonlar = 📋 Vazifa (forma + jarayonlar) + 💬 Suhbat (🎙 Boshqaruvchi) — bitta tugma, bitta panel
                  (operator, 2026-10-04: alohida 🎙 tugmasi o'rniga). Suhbat tabi Boshqaruvchiga ruxsat bo'lsa chiqadi. */}
              {/* 🔊 Ovozli sinov: tanlangan loyiha sinovida agent gapirsa — subtitr + ovoz (services/narrate.js). */}
              <NarrationPlayer apiUrl={process.env.REACT_APP_API_URL} token={token} projectId={abProjectId} />
              {/* 🔎 «Jarayonlar» yopiq, lekin loyihada agentga savol ruxsati bor (mijoz) — panel faqat 🧠 Agent tabi bilan (2026-11-04). */}
              {(uiShow('nav.processes') || (uiShow('nav.agent') && !!(projects.find(p => String(p.id) === String(abProjectId)) || {}).my_agent_ask)) && (
                <ProcessesFlyout
                  canProcesses={canSee('processes') && uiShow('nav.processes')}
                  canAgent={uiShow('nav.agent')}
                  token={token}
                  onUiAction={(ui) => {
                    // 🎙 «Eskiz konsolini och» → Boshqaruvchi konsolni ishga tushirgach sahifa o'sha konsolga o'tadi
                    // (attach: agent ochgan sessiyaga ULANADI, qayta ochmaydi — agent ishiga xalaqit bermasin).
                    if (ui && ui.action === 'open_console' && canSeeConsole) openConsoleAt(ui.profile, undefined, { attach: true });
                    // 📝 B3: Boshqaruvchi qoralamasi → 📋 Vazifa formasi (panel o'zi Vazifa tabiga o'tadi).
                    if (ui && ui.action === 'task_draft') applyTaskDraft(ui);
                  }}
                  authFetch={authFetch}
                  apiUrl={process.env.REACT_APP_API_URL}
                  selectedProject={abProjectId ? { id: abProjectId, name: (projects.find(p => String(p.id) === String(abProjectId)) || {}).name, my_role: myProjRole, my_agent_ask: !!(projects.find(p => String(p.id) === String(abProjectId)) || {}).my_agent_ask } : null}
                  tasks={tasks}
                  refreshTasks={fetchTasks}
                  onGoConsole={canSeeConsole ? () => setActiveTab('console') : null}
                  taskForm={taskFormEl}
                  ctxPanel={isProjClient ? null : <ContextWindowPanel sessions={contextSessions} ctxOnly />}
                  agentDraftExt={agentDraftExt}
                  onAgentTest={sendAgentTest}
                  agentTestOk={agentTestOkFor}
                  onLoadMore={loadMoreTasks}
                  hasMore={tasksHasMore}
                  loadingMore={tasksLoadingMore}
                />
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar (Flowbite: oq, o'ng chiziq, navbar ostidan boshlanadi) */}
      <aside
        className={`fixed top-0 left-0 z-20 flex-col flex-shrink-0 h-full pt-16 font-normal bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-200 ${navCollapsed ? 'lg:w-16 w-64' : 'w-64'} ${mobileNavOpen ? 'flex' : 'hidden'} lg:flex`}
      >
        <div className="relative flex flex-col flex-1 min-h-0 pt-0">
          <div className="flex-1 min-h-0 px-3 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
            {renderNavGroup(1)}
            {navItems.some(it => it.group === 2) && renderNavGroup(2)}
            {/* Sarf / server / engine ko'rsatkichlari — qora sidebar uchun yozilgan
                (bg-gray-800, text-gray-300…); oq fonda `.kh-side-stats` CSS xaritasi
                ularni och ranglarga o'giradi (index.css). */}
            {!navCollapsed && user?.role === 'admin' && (
              <div className="kh-side-stats pt-3 pb-2">
                {user?.role === 'admin' && <AdminTokenStats authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />}
                {/* Engine holati — server ko'rsatkichlaridan KEYIN (pastda) */}
                {user?.role === 'admin' && <EngineLimitPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />}
              </div>
            )}
          </div>
          <div className={`flex items-center gap-2 p-3 border-t border-gray-200 dark:border-gray-700 shrink-0 ${navCollapsed ? 'lg:flex-col lg:justify-center' : ''}`}>
            <div className="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded-full shrink-0 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-500" title={user?.username}>
              {(user?.username || '?').slice(0, 1).toUpperCase()}
            </div>
            {!navCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.username}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{user?.role === 'admin' ? 'Administrator' : 'Operator'}</div>
              </div>
            )}
            {/* 🌐 Til — En (standart) / O'zbek / Русский / … (2.100.0). Lug'at DOM'ga runtime'da qo'llanadi (src/i18n.js). */}
            <LangSwitcher compact />
            <button onClick={() => toggleTheme()} title={theme === 'dark' ? 'Kunduzgi rejim' : 'Tungi rejim'}
              className="p-2 text-gray-500 dark:text-gray-400 rounded-lg hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <NavIcon name={theme === 'dark' ? 'sun' : 'moon'} className="w-5 h-5" />
            </button>
            {/* 🔐 Ikki bosqichli kirish (TOTP) — sozlash / zaxira kodlar (docs/knowledge/148) */}
            <button onClick={() => window.location.assign('/2fa-setup')} title="Ikki bosqichli kirish (2FA)"
              className="p-2 text-base leading-none text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">🔐</button>
            <button onClick={logout} title="Chiqish"
              className="p-2 text-gray-500 dark:text-gray-400 rounded-lg hover:text-red-600 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <NavIcon name="logout" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
      {mobileNavOpen && (
        <div className="fixed inset-0 z-10 bg-gray-900/50 lg:hidden" onClick={() => setMobileNavOpen(false)} />
      )}



      {/* Main Content */}
      <div className={`kh-main-wrap ${navCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 transition-all duration-200`}>
        <main className="px-4 pt-6 pb-10 lg:px-6">
        {/* To'liq kenglik: Studio/Dizayn/SMM/CallCenter + ⏳ Jarayonlar (2026-09-18, operator — karta
            sarlavhalari 896px'da 3 qatorga o'ralib, chiplar sig'masdi). Qolgan tablar max-w-4xl. */}
        <div className={(activeTab === 'studio' || activeTab === 'design' || activeTab === 'smm' || activeTab === 'callcenter' || activeTab === 'processes' || activeTab === 'knowledge' || activeTab === 'forecast' || activeTab === 'reengineering' || activeTab === 'integrations' || activeTab === 'console' || activeTab === 'health') ? 'w-full' : 'max-w-4xl mx-auto'}>
          {activeTab === 'processes' && (
            <ProcessesPanel
              authFetch={authFetch}
              apiUrl={process.env.REACT_APP_API_URL}
              tasks={tasks}
              refreshTasks={fetchTasks}
              onGoConsole={canSeeConsole ? () => setActiveTab('console') : null}
              onLoadMore={loadMoreTasks}
              hasMore={tasksHasMore}
              loadingMore={tasksLoadingMore}
              selectedProject={abProjectId ? { id: abProjectId, name: (projects.find(p => String(p.id) === String(abProjectId)) || {}).name, my_role: myProjRole } : null}
              onAgentTest={sendAgentTest}
              agentTestOk={agentTestOkFor}
            />
          )}
          {activeTab === 'smm' && <SmmPanel />}
          {activeTab === 'callcenter' && <CallCenterPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} token={token} projects={projects} initialProjectId={abProjectId} />}
          {activeTab === 'noaccess' && (
            <div className="text-center py-32 text-gray-400 select-none">
              <p className="text-6xl mb-4">🔒</p>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400 mb-2">Ruxsat yo'q</p>
              <p className="text-lg">Sizga hech qanday bo'lim biriktirilmagan. Administrator bilan bog'laning.</p>
            </div>
          )}
          {activeTab === 'projects' && selectedProject && selectedModule && (
            <ModuleDetail
              project={selectedProject}
              module={selectedModule}
              onBack={() => { setSelectedModule(null); setServerLogs([]); }}
              emulatorStatus={emulatorStatus}
              emulatorLogs={emulatorLogs}
              onEmulatorStart={startEmulator}
              onEmulatorRestart={reconnectDevice}
              onEmulatorStop={stopEmulator}
              onOpenEmulator={openEmulatorForModule}
              onRefreshEmulator={fetchEmulatorDevices}
              detailTab={moduleDetailTab}
              setDetailTab={setModuleDetailTab}
              glUrl={glUrl} setGlUrl={setGlUrl}
              glToken={glToken} setGlToken={setGlToken}
              glBranch={glBranch} setGlBranch={setGlBranch}
              glConnecting={glConnecting} glConnectErr={glConnectErr}
              glInfo={glInfo} glInfoLoading={glInfoLoading}
              glCiTemplate={glCiTemplate} setGlCiTemplate={setGlCiTemplate}
              glCiLoading={glCiLoading} glCiMsg={glCiMsg}
              onGlConnect={handleGlConnect}
              onGlDisconnect={handleGlDisconnect}
              onGlCiSetup={handleGlCiSetup}
              onGlRefresh={() => loadGlInfo(selectedModule.id)}
              serverHealth={serverHealth}
              serverLogs={serverLogs}
              serverRunning={serverRunning}
              serverEnvRequired={serverEnvRequired}
              serverEnvVars={serverEnvVars}
              setServerEnvVars={setServerEnvVars}
              onStartTestServer={handleStartServer}
              onSetupTestEnv={handleSetupTestEnv}
              onTestEnvWizard={handleTestEnvWizard}
              wizardSteps={wizardSteps}
              onRedeployTestEnv={handleRedeployTestEnv}
              onTeardownTestEnv={handleTeardownTestEnv}
              onProdDeploy={handleProdDeploy}
              aiTaskInstruction={aiTaskInstruction}
              setAiTaskInstruction={setAiTaskInstruction}
              onRunAiAgent={handleRunAiAgent}
              onMobileBuild={handleMobileBuild}
              apiTarget={apiTarget}
              onApiTargetChange={changeApiTarget}
              onAabBuild={handleAabBuild}
              onRefreshHealth={() => loadServerHealth(selectedModule.id)}
              onProvideEnv={() => handleStartServer(serverEnvVars)}
              authFetch={authFetch}
              apiUrl={process.env.REACT_APP_API_URL}
              onModuleUpdate={updatedMod => {
                setSelectedModule(updatedMod);
                setSelectedProjectModules(prev => prev.map(m => m.id === updatedMod.id ? updatedMod : m));
              }}
            />
          )}

          {activeTab === 'projects' && selectedProject && !selectedModule && (
            <ProjectDetail
              project={selectedProject}
              modules={selectedProjectModules}
              onBack={() => setSelectedProject(null)}
              onOpenModule={openModuleDetail}
              onDeleteModule={async (moduleId) => {
                await authFetch(`${process.env.REACT_APP_API_URL}/project-modules/${moduleId}`, { method: 'DELETE' });
                setSelectedProjectModules(prev => prev.filter(m => m.id !== moduleId));
              }}
              onAddModule={async (moduleData) => {
                const r = await authFetch(`${process.env.REACT_APP_API_URL}/projects/${selectedProject.id}/modules`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(moduleData),
                });
                if (r.ok) {
                  const newMod = await r.json();
                  setSelectedProjectModules(prev => [...prev, newMod]);
                }
              }}
            />
          )}

          {activeTab === 'projects' && !selectedProject && (
            <>
              <div className="flex items-center justify-between mb-8 flex-wrap gap-2"><h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Loyihalar boshqaruvi</h1><button onClick={() => setImportOpen(true)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">📥 Mavjud loyihani qo'shish</button></div>

        {/* ---------- NEW: Project creation form ---------- */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded p-4 mb-6">
          <h2 className="text-lg font-semibold mb-2">Yangi loyiha qo&#39;shish</h2>
          <div className="flex flex-wrap gap-2 mb-2">
            <input
              type="text"
              placeholder="Loyiha nomi"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              className="border rounded p-2 w-full sm:w-auto flex-1"
            />
            <input
              type="text"
              placeholder="Tavsif (ixtiyoriy)"
              value={newProjectDescription}
              onChange={e => setNewProjectDescription(e.target.value)}
              className="border rounded p-2 w-full sm:w-auto flex-1"
            />
          </div>
          <div className="flex">
            <button
              className={`bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={handleAddProject}
              disabled={isSubmittingProject || !newProjectName.trim()}
            >
              {isSubmittingProject ? 'Yuborilmoqda...' : "Qo'shish"}
            </button>
          </div>
          {projectError && (
            <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{projectError}</div>
          )}
        </div>

        {/* ---------- Existing projects list (new state management) ---------- */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded p-4 mb-6">
          <h2 className="text-lg font-semibold mb-2">Mavjud loyihalar</h2>

          {/* Inline error message for delete/update operations */}
          {projectListError && (
            <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 text-sm rounded flex justify-between items-start">
              <span>{projectListError}</span>
              <button
                className="ml-2 text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 font-bold"
                onClick={() => setProjectListError('')}
                aria-label="Xabarni yopish"
              >
                ×
              </button>
            </div>
          )}

          {projects.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">Hozircha loyiha yo'q</p>
          ) : (
            <ul className="space-y-2">
              {projects.map(proj => {
                const isEditing = editingProject && editingProject.id === proj.id;
                const isUpdating = updatingProjectId === proj.id;
                const isDeleting = deletingProjectId === proj.id;
                return (
                  <li key={proj.id} className="border-b pb-2">
                    <div className="flex items-center justify-between">
                      {isEditing ? (
                        <div className="flex-1 flex flex-wrap gap-2 items-center">
                          <div className="flex flex-col gap-2 w-full">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={editingProject.name}
                                onChange={e =>
                                  setEditingProject(prev => ({ ...prev, name: e.target.value }))
                                }
                                className="border rounded p-1 flex-1 min-w-[120px]"
                                placeholder="Loyiha nomi"
                                autoFocus
                                maxLength={255}
                                disabled={isUpdating}
                              />
                              <input
                                type="text"
                                value={editingProject.description}
                                onChange={e =>
                                  setEditingProject(prev => ({ ...prev, description: e.target.value }))
                                }
                                className="border rounded p-1 flex-1 min-w-[120px]"
                                placeholder="Tavsif"
                                disabled={isUpdating}
                              />
                            </div>
                            <div className="flex justify-end gap-2">
                              <button
                                className="bg-blue-500 text-white px-2 py-1 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isUpdating || !editingProject.name.trim()}
                                onClick={() => {
                                  handleUpdateProject(
                                    proj.id,
                                    editingProject.name,
                                    editingProject.description
                                  );
                                }}
                              >
                                {isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
                              </button>
                              <button
                                className="bg-gray-300 dark:bg-gray-600 px-2 py-1 rounded text-sm disabled:opacity-50"
                                disabled={isUpdating}
                                onClick={handleCancelEdit}
                              >
                                Bekor qilish
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <button
                            className="font-medium text-primary-800 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-200 hover:underline text-left"
                            onClick={() => openProjectDetail(proj)}
                          >
                            {proj.name}
                          </button>
                          {proj.description && (
                            <span className="text-sm text-gray-600 dark:text-gray-400 ml-2"> - {proj.description}</span>
                          )}
                        </div>
                      )}
                      {!isEditing && (
                        <div className="flex space-x-2 items-center">
                          <button
                            className="text-primary-700 dark:text-primary-500 hover:text-primary-900 dark:hover:text-primary-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isDeleting}
                            onClick={() => {
                              setEditError('');
                              setEditingProject({
                                id: proj.id,
                                name: proj.name || '',
                                description: proj.description || '',
                                remote_host: proj.remote_host || '',
                                ssh_user: proj.ssh_user || '',
                                deploy_path: proj.deploy_path || '',
                                restart_cmd: proj.restart_cmd || '',
                                test_url: proj.test_url || '',
                                production_url: proj.production_url || ''
                              });
                            }}
                          >
                            Tahrirlash
                          </button>
                          <button
                            className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 text-sm ml-2 disabled:opacity-50"
                            disabled={isDeleting}
                            onClick={() => openModulesModal(proj)}
                          >
                            Modullar
                          </button>
                          <button
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-sm ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isDeleting}
                            onClick={() => handleDeleteProject(proj.id)}
                          >
                            {isDeleting ? "O'chirilmoqda..." : "O'chirish"}
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Inline error for this edit row */}
                    {isEditing && editError && (
                      <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{editError}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
            </>
          )}

          {activeTab === 'tasks' && (
            <div className="flex gap-4 min-h-0">

              {/* Project sidebar */}
              <div className={`shrink-0 transition-all duration-200 ${taskSidebarOpen ? 'w-52' : 'w-10'}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  {taskSidebarOpen && <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Loyihalar</h2>}
                  <button
                    onClick={() => setTaskSidebarOpen(o => !o)}
                    className="ml-auto p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition"
                    title={taskSidebarOpen ? 'Yopish' : 'Ochish'}
                  >
                    {taskSidebarOpen ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    )}
                  </button>
                </div>
                {taskSidebarOpen && (
                  <div className="space-y-1">
                    {projects.map(p => (
                      <div key={p.id}>
                        <button
                          onClick={() => setProjectId(p.id.toString())}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                            String(projectId) === String(p.id)
                              ? 'bg-primary-700 dark:bg-primary-600 text-white shadow'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {p.name}
                        </button>
                        {p.test_url && (
                          <a
                            href={p.test_url}
                            target="_blank"
                            rel="noreferrer"
                            title={p.test_url}
                            className="flex items-center gap-1 mt-0.5 ml-3 text-xs text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-200 hover:underline truncate"
                          >
                            🌐 Saytni ochish
                          </a>
                        )}
                        {p.production_url && (
                          <a
                            href={p.production_url}
                            target="_blank"
                            rel="noreferrer"
                            title={p.production_url}
                            className="flex items-center gap-1 mt-0.5 ml-3 text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 hover:underline truncate"
                          >
                            🚀 Production
                          </a>
                        )}
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-sm text-gray-400 px-1">Loyihalar yo'q</p>
                    )}
                  </div>
                )}
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                {(() => {
                  const selProject = projects.find(p => String(p.id) === String(projectId));
                  return (
                    <div className="flex items-center justify-between mb-5 gap-4">
                      <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl shrink-0">
                        {selProject?.name || 'Vazifalar'}
                      </h1>
                      <div className="flex gap-2 flex-wrap items-center">
                          {selProject?.test_url && (
                            <a href={selProject.test_url} target="_blank" rel="noreferrer"
                              className="text-xs px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400 font-medium hover:bg-yellow-200 dark:hover:bg-yellow-800/70 transition">
                              🧪 Test
                            </a>
                          )}
                          {selProject?.production_url && (
                            <a href={selProject.production_url} target="_blank" rel="noreferrer"
                              className="text-xs px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 font-medium hover:bg-green-200 dark:hover:bg-green-800/70 transition">
                              🚀 Production
                            </a>
                          )}
                          {selectedProjectModules.some(m => m.module_type === 'mobile') && (
                            <button
                              onClick={() => {
                                setShowEmulator(true);
                                fetchEmulatorDevices();
                                if (activeMobileModule?.workspace_path) fetchApkInfo(activeMobileModule.workspace_path);
                              }}
                              className="text-xs px-3 py-1.5 rounded-full bg-gray-800 text-white font-medium hover:bg-gray-700 transition">
                              📱 Emulator
                            </button>
                          )}
                          {penpotUrl && (
                            <button
                              onClick={() => setShowAiDesign(true)}
                              className="text-xs px-3 py-1.5 rounded-full bg-purple-600 text-white font-medium hover:bg-purple-700 transition">
                              🎨 AI Dizayn
                            </button>
                          )}
                        </div>
                    </div>
                  );
                })()}

                {/* Live Terminal */}
                <div ref={terminalContainerRef} className="bg-gray-900 shadow rounded p-4 mb-5" style={{ height: '260px', overflowY: 'auto' }}>
                  <div className="text-green-400 font-mono text-sm whitespace-pre-wrap">
                    {liveLogs.length === 0 ? (
                      <div>[Kutish rejimi] Engine loglari bu yerda ko'rinadi...</div>
                    ) : (
                      liveLogs.map((log, index) => (
                        <span key={index} className={log.isError ? 'text-red-400' : 'text-green-400'}>
                          {log.taskId ? `[Task ${log.taskId}] ` : ''}
                          {log.finished ? '\n---- TUGADI ----\n' : log.output}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Task form */}
                {projectId && (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded p-4 mb-5">
                    {/* Selected project — visible + changeable right here */}
                    <div className="mb-3 pb-3 border-b border-gray-100 dark:border-gray-700">
                      <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">📁 Proyekt</label>
                      <select
                        value={projectId}
                        onChange={e => setProjectId(e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-700 focus:bg-white dark:focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                      >
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    {selectedProjectModules.some(m => m.module_type === 'mobile') && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={requiresBuild}
                          onChange={e => setRequiresBuild(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-primary-700 dark:text-primary-500 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-600"
                        />
                        <span>Apk ni qayta build qilish (Install)</span>
                      </label>
                    )}
                    <textarea
                      className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                      rows="3"
                      placeholder="Yangi vazifa matnini (instruction) kiriting... (rasm qo'yish uchun Ctrl+V)"
                      value={newTask}
                      onChange={e => setNewTask(e.target.value)}
                      onPaste={handleTaskImagePaste}
                    />

                    {/* Image previews */}
                    {newTaskImages.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {newTaskImages.map((img, i) => (
                          <div key={i} className="relative group w-20 h-20 rounded overflow-hidden border border-gray-300 dark:border-gray-600 shadow-sm">
                            <img src={img.preview} alt="" title="Katta qilib ko'rish" className="w-full h-full object-cover cursor-zoom-in"
                              onClick={() => setImgViewer({ images: newTaskImages.map(x => x.preview), index: i })} />
                            <button
                              onClick={() => setNewTaskImages(prev => prev.filter((_, idx) => idx !== i))}
                              className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rasm yuklash (ixtiyoriy):</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={e => addImagesToQueue(e.target.files)}
                        className="text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-800 hover:file:bg-primary-100"
                      />
                    </div>
                    {!isProjClient && <ContextWindowPanel sessions={contextSessions} newSessionMode={newSessionMode} setNewSessionMode={setNewSessionMode} runMode={runMode} setRunMode={setRunMode} autoDeploy={autoDeploy} setAutoDeploy={setAutoDeploy} planReview={planReview} setPlanReview={setPlanReview} afterTest={afterTest} setAfterTest={setAfterTest} />}

                    <button
                      className="bg-primary-700 dark:bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-800 dark:hover:bg-primary-700 transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      onClick={handleAddTask}
                      disabled={addingTask || !newTask.trim() || !projectId}
                    >
                      {addingTask && <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                      {addingTask ? 'Yuborilmoqda…' : 'Yuborish'}
                    </button>
                  </div>
                )}

                {/* Tasks list — filtered to selected project */}
                <div className="space-y-4">
                  {tasks.filter(t => String(t.project_id) === String(projectId)).map(task => (
                    <div key={task.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-lg p-4 flex flex-col">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-lg font-medium">{task.instruction}</p>
                          <div className="mt-1">
                            <span className={`inline-flex items-center text-xs font-semibold rounded-full px-2 py-1 ${statusColors[task.status] || 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'}`}>
                              {isActiveStatus(task.status) && <SpinnerIcon />}
                              {statusLabels[task.status] || task.status}
                            </span>
                            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Asosiy Vazifa</span>
                          </div>
                          {(() => {
                            let videos = [];
                            let screenshots = [];
                            try { videos = typeof task.video_urls === 'string' ? JSON.parse(task.video_urls) : (task.video_urls || []); } catch(e){}
                            try { screenshots = typeof task.screenshot_urls === 'string' ? JSON.parse(task.screenshot_urls) : (task.screenshot_urls || []); } catch(e){}
                            return (
                              <div className="mt-4 flex flex-wrap gap-4">
                                {screenshots.map((url, i) => (
                                  <div key={`s-${i}`} className="w-48 h-auto border rounded overflow-hidden shadow-sm">
                                    <a href={`${process.env.REACT_APP_API_URL}${url}`} target="_blank" rel="noreferrer">
                                      <img src={`${process.env.REACT_APP_API_URL}${url}`} alt="screenshot" className="w-full h-auto object-cover" />
                                    </a>
                                  </div>
                                ))}
                                {videos.map((url, i) => {
                                  const isWebm = url.endsWith('.webm');
                                  return (
                                    <div key={`v-${i}`} className="w-64 h-auto border rounded overflow-hidden shadow-sm bg-black">
                                      <video controls className="w-full h-auto">
                                        <source src={`${process.env.REACT_APP_API_URL}${url}`} type={isWebm ? "video/webm" : "video/mp4"} />
                                      </video>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {task.sub_tasks && task.sub_tasks.length > 0 && (
                        <div className="mt-4 pl-4 border-l-2 border-primary-200 dark:border-primary-800 space-y-2">
                          {task.sub_tasks.map(sub => (
                            <div key={sub.id} className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                              <p className="text-md text-gray-900 dark:text-white"><span className="font-bold mr-2 text-primary-600 dark:text-primary-500">#{sub.priority}</span>{sub.instruction}</p>
                              <div className="mt-1">
                                <span className={`inline-flex items-center text-xs font-semibold rounded-full px-2 py-1 ${statusColors[sub.status] || 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'}`}>
                                  {isActiveStatus(sub.status) && <SpinnerIcon />}
                                  {statusLabels[sub.status] || sub.status}
                                </span>
                                <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Modul: {sub.module_name}</span>
                              </div>
                              {(() => {
                                let videos = [];
                                let screenshots = [];
                                try { videos = typeof sub.video_urls === 'string' ? JSON.parse(sub.video_urls) : (sub.video_urls || []); } catch(e){}
                                try { screenshots = typeof sub.screenshot_urls === 'string' ? JSON.parse(sub.screenshot_urls) : (sub.screenshot_urls || []); } catch(e){}
                                return (
                                  <div className="mt-3 flex flex-wrap gap-4">
                                    {screenshots.map((url, i) => (
                                      <div key={`sub-s-${i}`} className="w-32 h-auto border rounded overflow-hidden shadow-sm">
                                        <a href={`${process.env.REACT_APP_API_URL}${url}`} target="_blank" rel="noreferrer">
                                          <img src={`${process.env.REACT_APP_API_URL}${url}`} alt="screenshot" className="w-full h-auto object-cover" />
                                        </a>
                                      </div>
                                    ))}
                                    {videos.map((url, i) => {
                                      const isWebm = url.endsWith('.webm');
                                      return (
                                        <div key={`sub-v-${i}`} className="w-48 h-auto border rounded overflow-hidden shadow-sm bg-black">
                                          <video controls className="w-full h-auto">
                                            <source src={`${process.env.REACT_APP_API_URL}${url}`} type={isWebm ? "video/webm" : "video/mp4"} />
                                          </video>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {projectId && tasks.filter(t => String(t.project_id) === String(projectId)).length === 0 && (
                    <p className="text-gray-400 text-center py-8">Bu loyiha uchun vazifalar yo'q</p>
                  )}
                  {/* Infinite-scroll sentinel: ko'ringanda eski vazifalar yuklanadi (bosib ham bo'ladi) */}
                  {tasksHasMore ? (
                    <div
                      ref={tasksSentinelRef}
                      onClick={loadMoreTasks}
                      className="py-4 text-center text-sm text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 select-none"
                    >
                      {tasksLoadingMore ? '⏳ Yuklanmoqda...' : '⬇ Eski vazifalarni yuklash'}
                    </div>
                  ) : (
                    tasks.length > 0 && <div className="py-3 text-center text-xs text-gray-300 select-none">— hammasi yuklandi —</div>
                  )}
                </div>

                {/* Emulator button — only for mobile projects */}
              </div>
            </div>
          )}

          {activeTab === 'configuration' && (
            <>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-8">AI Konfiguratsiyasi</h1>

              {/* 🎙 Boshqaruvchi — model, kunlik $ limit, kimga yoqilgan (OpenRouter, pullik) */}
              <div className="mb-6 [&>div]:mt-0"><ConductorAdmin authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} /></div>

              {/* Hamma AI Claude Max'da ishlaydi — rol uchun faqat daraja tanlanadi */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2 border-b pb-2">
                  <h2 className="text-xl font-semibold">🤖 Claude model darajalari</h2>
                  <span className="text-xs text-gray-400">Claude Max obunasi — to'lov yo'q</span>
                </div>
                <p className="text-xs text-gray-400 mb-1">
                  Har bir rol uchun <b>Model</b>: <b>opus</b> (eng kuchli, sekin) · <b>sonnet</b> (muvozanatli) ·{' '}
                  <b>haiku</b> (tez). Bu <b>aliaslar</b> Claude Code CLI qaysi modelga ulasa — o'shani ishlatadi, va u
                  eng yangi modeldan <b>orqada qolishi mumkin</b> (pastda aynan nimaga ulanayotgani ko'rinib turibdi).
                  Eng yangi modelni aniq ishlatish uchun uni «Aniq versiya» ro'yxatidan tanlang — u <b>pin</b> qilinadi.
                  Bu ro'yxat <b>avtomatik</b>: server har kuni CLI orqali qaysi modellar ishlashini tekshiradi, yangi
                  model chiqsa shu yerda paydo bo'ladi (CLI eski bo'lsa — yangilash tugmasi chiqadi).
                </p>
                <ModelCatalogBar authFetch={authFetch} onModels={setCatalogModels} />
                <AliasMap authFetch={authFetch} onLoad={setAliasMap} />
                <p className="text-xs text-gray-400 mb-6">
                  <b>Effort</b> = o'ylash chuqurligi: <b>low</b> (tez/arzon) → <b>max</b> (eng chuqur). <b>Standart</b> =
                  Claude Code o'zicha tanlaydi. O'zgarish darhol kuchga kiradi (reload kerak emas).
                </p>

                {/* Sarlavhalar */}
                <div className="hidden md:flex items-center gap-3 px-1 mb-2 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                  <span className="w-44">Rol</span>
                  <span className="w-56">Model</span>
                  <span className="w-36">Effort</span>
                  <span>Manba</span>
                </div>

                <div className="space-y-4">
                  {[
                    { role: 'architect', label: '🏛 Architect', hint: 'Vazifani modullarga ajratish' },
                    { role: 'developer', label: '👨‍💻 Developer', hint: 'Kod yozish (engine + AI Agent)' },
                    { role: 'design', label: '🎨 Dizayn', hint: 'SVG sahifa/logo generatsiya' },
                    { role: 'qa', label: '🧪 QA', hint: 'Test taklif/tekshiruv' },
                    { role: 'diagnostics', label: '🩺 Diagnostika', hint: 'Deploy tahlil + Docker sozlash' },
                    { role: 'refine', label: '💬 Vazifa suhbati', hint: "Vazifa oynasidagi suhbat — kod, baza, emulator, brauzer, konsolni ko'rib javob beradi va vazifa tuzadi" },
                  ].map(({ role, label, hint }) => {
                    const tier = claudeTiers.find(t => t.role === role);
                    const model = tier?.model || 'sonnet';
                    const isAlias = MODEL_ALIASES.includes(model);
                    const pinModels = catalogModels.some(m => m.status === 'ok')
                      ? catalogModels.filter(m => m.status === 'ok').map(m => ({ value: m.id, label: m.label }))
                      : KNOWN_MODELS;
                    const isKnown = KNOWN_MODEL_VALUES.has(model) || pinModels.some(m => m.value === model);
                    const customOpen = tierCustom[role] || !isKnown;
                    const busy = !tier || tierSaving === role;
                    return (
                      <div key={role} className="flex items-start gap-3 flex-wrap">
                        <div className="w-44">
                          <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">{label}</p>
                          <p className="text-[10px] text-gray-400">{hint}</p>
                        </div>

                        {/* Model */}
                        <div className="w-56">
                          <select
                            className="w-56 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                            value={customOpen ? '__custom__' : model}
                            disabled={busy}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__custom__') {
                                setTierCustom(c => ({ ...c, [role]: true }));
                                setTierCustomText(t => ({ ...t, [role]: isAlias ? '' : model }));
                              } else {
                                setTierCustom(c => ({ ...c, [role]: false }));
                                handleTierChange(role, { model: v });
                              }
                            }}
                          >
                            {/* Aliaslar ISHLAYDI — faqat ular «eng oxirgi» modelga emas,
                                CLI qaysi versiyaga bog'lagan bo'lsa o'shanga tushadi. Shuning
                                uchun ro'yxatda AYNAN nimaga ulanayotgani yoziladi. */}
                            <optgroup label="Aliaslar — CLI qaysi modelga ulasa">
                              {MODEL_ALIASES.map(a => (
                                <option key={a} value={a}>
                                  {aliasMap && aliasMap[a] ? `${a} → ${aliasMap[a]}` : a}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Aniq versiya (pin)">
                              {pinModels.map(m => (
                                <option key={m.value} value={m.value}>{m.label} — {m.value}</option>
                              ))}
                            </optgroup>
                            <option value="__custom__">✏️ To'liq ID...</option>
                          </select>
                          {customOpen && (
                            <div className="flex gap-1 mt-1">
                              <input
                                type="text"
                                placeholder="claude-opus-4-8"
                                value={tierCustomText[role] ?? (isAlias ? '' : model)}
                                disabled={busy}
                                onChange={(e) => setTierCustomText(t => ({ ...t, [role]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter' && tierCustomText[role]?.trim()) handleTierChange(role, { model: tierCustomText[role].trim() }); }}
                                className="w-32 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-primary-500 outline-none"
                              />
                              <button
                                onClick={() => tierCustomText[role]?.trim() && handleTierChange(role, { model: tierCustomText[role].trim() })}
                                disabled={busy || !tierCustomText[role]?.trim()}
                                className="px-2 py-1 bg-primary-700 dark:bg-primary-600 text-white rounded text-xs hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50"
                              >✓</button>
                            </div>
                          )}
                        </div>

                        {/* Effort */}
                        <select
                          className="w-36 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                          value={tier?.effort || ''}
                          disabled={busy}
                          onChange={(e) => handleTierChange(role, { effort: e.target.value })}
                        >
                          <option value="">Standart</option>
                          <option value="low">low</option>
                          <option value="medium">medium</option>
                          <option value="high">high</option>
                          <option value="xhigh">xhigh</option>
                          <option value="max">max</option>
                        </select>

                        <span className="text-xs text-gray-400 flex-1 min-w-[7rem] pt-2">
                          {tierSaving === role ? '⏳ saqlanmoqda...' :
                           tier?.modelSource === 'db' ? '💾 sozlangan' :
                           tier?.modelSource === 'env' ? `⚙️ env` : 'standart'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 🛡️ Xavfsizlik skanining AI modeli — Max (bepul) yoki OpenRouter (pullik) */}
              <SecurityModelCard authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL}
                isAdmin={user?.role === 'admin'} />

              {/* 📱 Emulator oqimi — auto-lock: harakatsizlikda jonli ekran oqimi to'xtaydi */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700 mt-6">
                <h2 className="text-xl font-semibold mb-2 border-b pb-2">📱 Emulator oqimi</h2>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">Harakatsizlikda to'xtatish (auto-lock)</p>
                    <p className="text-[11px] text-gray-400">
                      Telefondagi auto-lock kabi: emulatorga tap/swipe/matn kabi harakat qilinmasa, jonli ekran oqimi
                      tanlangan vaqtdan keyin to'xtaydi — server CPU behuda ishlamaydi. Davom etish = ekrandagi
                      "▶ Davom etish" tugmasi. Engine testlari/buildlariga ta'sir qilmaydi.
                    </p>
                  </div>
                  <select
                    value={emuIdleSetting === null ? 2 : emuIdleSetting}
                    disabled={emuIdleSaving || emuIdleSetting === null}
                    onChange={e => saveEmuIdleSetting(+e.target.value)}
                    className="w-40 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                  >
                    <option value={1}>1 daqiqa</option>
                    <option value={2}>2 daqiqa</option>
                    <option value={3}>3 daqiqa</option>
                    <option value={4}>4 daqiqa</option>
                    <option value={5}>5 daqiqa</option>
                    <option value={0}>Hech qachon</option>
                  </select>
                  <span className="text-xs text-gray-400 w-28">
                    {emuIdleSaving ? '⏳ saqlanmoqda...' : emuIdleSetting === null ? '⏳ yuklanmoqda...' : '💾 darhol kuchda'}
                  </span>
                </div>
                <div className="flex items-center gap-4 flex-wrap mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">☕ «Uxlatma» muddati</p>
                    <p className="text-[11px] text-gray-400">
                      /emu oynasidagi «💤 Uxlatma» tugmasi yoqilsa oqim harakatsizlikda ham to'xtamaydi, kompyuter ekrani o'chmaydi.
                      Unutib qoldirilsa server emulator ekranini to'xtovsiz olib turmasligi uchun shu vaqtdan keyin o'zi o'chadi.
                    </p>
                  </div>
                  <select
                    value={emuAwakeMaxSetting === null ? 120 : emuAwakeMaxSetting}
                    disabled={emuAwakeMaxSaving || emuAwakeMaxSetting === null}
                    onChange={e => saveEmuAwakeMax(+e.target.value)}
                    className="w-40 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                  >
                    <option value={30}>30 daqiqa</option>
                    <option value={60}>1 soat</option>
                    <option value={120}>2 soat</option>
                    <option value={240}>4 soat</option>
                    <option value={480}>8 soat</option>
                    <option value={0}>O'zi o'chmasin</option>
                  </select>
                  <span className="text-xs text-gray-400 w-28">
                    {emuAwakeMaxSaving ? '⏳ saqlanmoqda...' : emuAwakeMaxSetting === null ? '⏳ yuklanmoqda...' : '💾 darhol kuchda'}
                  </span>
                </div>
              </div>

              {/* ⚡ Emulator optimizatsiyasi — bitta kalit bilan AYNAN eski holatga qaytadi (emuTuning, docs/knowledge/160) */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700 mt-6">
                <h2 className="text-xl font-semibold mb-2 border-b pb-2">⚡ Emulator optimizatsiyasi</h2>
                {emuOpt === null ? (
                  <p className="text-sm text-gray-400">⏳ yuklanmoqda...</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex-1 min-w-[16rem]">
                        <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">Emulatorlar serverni qotirmasin</p>
                        <p className="text-[11px] text-gray-400">
                          Yoqilganda: Google Play emulatori kamroq resurs bilan ishlaydi, barcha emulatorlar umumiy
                          protsessor/xotira chegarasida (yuk oshsa server emas, faqat emulatorlar sekinlashadi), bir xil
                          xotira birlashtiriladi, bir vaqtda bittadan yuklanadi va limitdan oshmaydi. O'chirilsa — aynan
                          eski holat: 4 yadro · 3 GB, limitsiz, 60 daqiqa. O'zgarish keyingi ishga tushirishdan kuchga kiradi.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => saveEmuOpt({ enabled: !emuOpt.enabled })}
                        disabled={emuOptSaving}
                        className={`w-56 px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${emuOpt.enabled
                          ? 'text-white bg-primary-700 hover:bg-primary-800'
                          : 'text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700'}`}
                      >
                        {emuOpt.enabled ? '✅ Yoqilgan' : "⏸ O'chirilgan (eski holat)"}
                      </button>
                    </div>
                    {emuOpt.enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="block mb-1">Bir vaqtda maksimal emulator</span>
                          <select
                            value={emuOpt.maxRunning}
                            disabled={emuOptSaving}
                            onChange={e => saveEmuOpt({ maxRunning: +e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                          >
                            {[2, 3, 4, 5, 6, 7, 8, 10, 12, 0].map(n => (
                              <option key={n} value={n}>{n === 0 ? 'Cheklovsiz' : `${n} ta`}</option>
                            ))}
                          </select>
                        </label>
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="block mb-1">Ishlatilmasa to'xtatish</span>
                          <select
                            value={emuOpt.idleStopMin}
                            disabled={emuOptSaving}
                            onChange={e => saveEmuOpt({ idleStopMin: +e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                          >
                            {[15, 20, 30, 45, 60, 90].map(n => (
                              <option key={n} value={n}>{n} daqiqa</option>
                            ))}
                          </select>
                        </label>
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="block mb-1">Emulator hajmi (Google Play)</span>
                          <select
                            value={emuOpt.avdSize}
                            disabled={emuOptSaving}
                            onChange={e => saveEmuOpt({ avdSize: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                          >
                            <option value="eco">Tejamkor — 2 yadro · 2 GB</option>
                            <option value="mid">O'rtacha — 3 yadro · 2.5 GB</option>
                            <option value="legacy">Eski — 4 yadro · 3 GB</option>
                            {emuOpt.avdSize === 'custom' && (
                              <option value="custom" disabled>{`Boshqa — ${emuOpt.cores} yadro · ${emuOpt.memoryMb} MB`}</option>
                            )}
                          </select>
                        </label>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Hozir ishlayapti: <b>{emuOpt.runningNow}</b> ta emulator
                      {emuOpt.ksm && emuOpt.ksm.run === 1 && emuOpt.ksm.savedMb != null
                        ? ` · xotira birlashtirish (KSM) tejadi: ${(emuOpt.ksm.savedMb / 1024).toFixed(1)} GB` : ''}
                      {emuOptSaving ? ' · ⏳ saqlanmoqda...' : ''}
                    </p>
                  </div>
                )}
              </div>

              {/* ⚙️ Engine cheklovi — parallellik + cap'ga yetganda to'xtatish.
                  Yon menyudagi panel endi faqat JONLI bar'larni va cap raqamlarini
                  ko'rsatadi; bu ikkala SOZLAMA shu yerda turadi. */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700 mt-6">
                <h2 className="text-xl font-semibold mb-2 border-b pb-2">⚙️ Engine cheklovi</h2>

                {/* UMUMIY cap raqamlari — ilgari yon menyuda edi, endi hamma
                    sozlama shu yerda. Joriy sarf yonida ko'rinib turadi, shuning
                    uchun cap sarfdan past/baland ekanini darhol ko'rasiz. */}
                <div className="flex items-end gap-4 flex-wrap py-3">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">🎯 Umumiy cap (barcha foydalanuvchilar)</p>
                    <p className="text-[11px] text-gray-400">
                      Engine'ning O'Z sarfi (real umumiy Max limiti emas). Joriy sarf:
                      {' '}<span className="font-mono text-gray-600 dark:text-gray-400">{((engineCfg.used5 || 0) / 1e6).toFixed(1)}M</span> (5 soat) ·
                      {' '}<span className="font-mono text-gray-600 dark:text-gray-400">{((engineCfg.usedW || 0) / 1e6).toFixed(1)}M</span> (7 kun).
                      {' '}⚠️ Cap'ni joriy sarfdan PAST qo'ysangiz engine darhol to'xtaydi.
                    </p>
                  </div>
                  <label className="text-[11px] text-gray-500 dark:text-gray-400">5 soatlik (M token)
                    <input type="number" min="0" value={engineCfg.cap5}
                      onChange={e => setEngineCfg(c => ({ ...c, cap5: e.target.value }))}
                      className="block mt-0.5 w-32 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none"/>
                  </label>
                  <label className="text-[11px] text-gray-500 dark:text-gray-400">Haftalik (M token)
                    <input type="number" min="0" value={engineCfg.capW}
                      onChange={e => setEngineCfg(c => ({ ...c, capW: e.target.value }))}
                      className="block mt-0.5 w-32 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none"/>
                  </label>
                  <button
                    onClick={() => saveEngineCfg({
                      limit_5h_tokens: (parseInt(engineCfg.cap5, 10) || 0) * 1e6,
                      limit_weekly_tokens: (parseInt(engineCfg.capW, 10) || 0) * 1e6,
                    })}
                    disabled={engineCfgSaving || !engineCfgLoaded}
                    className="text-sm bg-primary-700 dark:bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-40 transition">
                    💾 Saqlash
                  </button>
                </div>

                {/* ♻️ Hisobni nolga tushirish endi bu yerda EMAS — har foydalanuvchi
                    uchun alohida (Foydalanuvchilar sahifasi). Har kimning cheklov
                    oynasi mustaqil yuradi, shuning uchun hammani birdan nollash
                    chalg'ituvchi edi. */}
                <div className="flex items-center gap-2 flex-wrap py-3 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-[11px] text-gray-400">
                    ♻️ Hisobni nolga tushirish — <span className="text-gray-500 dark:text-gray-400 font-medium">Foydalanuvchilar</span> sahifasida,
                    {' '}har foydalanuvchi uchun alohida (har kimning oynasi mustaqil yuradi).
                  </span>
                </div>

                <div className="flex items-center gap-4 flex-wrap py-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">🧵 Parallellik — bir vaqtda nechta vazifa</p>
                    <p className="text-[11px] text-gray-400">
                      Engine bir vaqtning o'zida nechta qism-vazifani bajarishi. Bitta modulda hech qachon ikki
                      vazifa parallel ishlamaydi (workspace/branch bitta), shuning uchun parallellik turli
                      modullar/loyihalar bo'ylab beriladi. Ko'paytirish tezlashtiradi, lekin token sarfi va
                      server yuki ham ortadi. Engine 10 soniya ichida o'zi ko'radi — reload shart emas.
                    </p>
                  </div>
                  <select
                    value={engineCfg.cc}
                    disabled={engineCfgSaving || !engineCfgLoaded}
                    onChange={e => saveEngineCfg({ engine_concurrency: parseInt(e.target.value, 10) })}
                    className="w-40 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50"
                  >
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}{n === 1 ? ' (ketma-ket)' : ' ta parallel'}</option>)}
                  </select>
                  <span className="text-xs text-gray-400 w-28">
                    {engineCfgSaving ? '⏳ saqlanmoqda...' : !engineCfgLoaded ? '⏳ yuklanmoqda...' : '💾 darhol kuchda'}
                  </span>
                </div>

                <div className="flex items-center gap-4 flex-wrap py-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">🎯 Bitta vazifa byudjeti (M token)</p>
                    <p className="text-[11px] text-gray-400">
                      Bitta vazifa (super-vazifa + uning barcha qism-vazifalari: backend, mobil, QA tuzatishlari) jami sarflay
                      oladigan token. Oshsa vazifa «💸 Token byudjeti tugadi» bilan to'xtaydi — chegarani ko'tarib ▶ Davom
                      ettirasiz. Kesh-o'qish 10% sanaladi (narxiga mos): opus katta run odatda ~1–2M. 0 = cheksiz. 5 soatlik /
                      haftalik cap'dan alohida.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" step="1" value={engineCfg.budget}
                      disabled={engineCfgSaving || !engineCfgLoaded}
                      onChange={e => setEngineCfg(c => ({ ...c, budget: e.target.value }))}
                      className="w-24 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary-600 outline-none disabled:opacity-50" />
                    <button disabled={engineCfgSaving || !engineCfgLoaded || engineCfg.budget === ''}
                      onClick={() => saveEngineCfg({ engine_task_token_budget: Math.max(0, Math.round(Number(engineCfg.budget) || 0)) * 1e6 })}
                      className="px-3 py-2 text-sm rounded bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50">💾 Saqlash</button>
                  </div>
                  <span className="text-xs text-gray-400 w-28">
                    {engineCfgSaving ? '⏳ saqlanmoqda...' : !engineCfgLoaded ? '⏳ yuklanmoqda...' : '💾 30 s ichida kuchda'}
                  </span>
                </div>

                <div className="flex items-center gap-4 flex-wrap py-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex-1 min-w-[16rem]">
                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">⏸ Cap'ga yetganda engine'ni to'xtatish</p>
                    <p className="text-[11px] text-gray-400">
                      Yoqilganda: token cap'iga (5 soatlik yoki haftalik) yetilganda engine YANGI vazifa
                      boshlamaydi — ishlab turganlari tugaydi, navbatdagilar oyna yangilanguncha kutadi.
                      Shu bilan Max obunangizda o'zingiz uchun joy qoladi. O'chirilganda cheklovlar faqat
                      ko'rsatkich bo'lib qoladi — hech narsani to'xtatmaydi.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!engineCfg.enabled}
                      disabled={engineCfgSaving || !engineCfgLoaded}
                      onChange={e => saveEngineCfg({ engine_limit_enabled: e.target.checked })}
                      className="w-5 h-5 accent-primary-700 disabled:opacity-50"
                    />
                    <span className={`text-sm font-medium ${engineCfg.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                      {engineCfg.enabled ? 'Yoniq' : "O'chiq"}
                    </span>
                  </label>
                  <span className="text-xs text-gray-400 w-28">
                    {engineCfgSaving ? '⏳ saqlanmoqda...' : !engineCfgLoaded ? '⏳ yuklanmoqda...' : '💾 darhol kuchda'}
                  </span>
                </div>

                <p className="text-[11px] text-gray-400 pt-3 border-t border-gray-100 dark:border-gray-700">
                  Cap raqamlari (5 soatlik / haftalik) yon menyudagi <span className="text-gray-600 dark:text-gray-400">⚙️ Engine limiti</span> panelida,
                  har foydalanuvchiga alohida cheklov esa <span className="text-gray-600 dark:text-gray-400">Foydalanuvchilar</span> sahifasida.
                </p>
              </div>
            </>
          )}

          {activeTab === 'users' && user?.role === 'admin' && (
            <>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Foydalanuvchilar</h1>

              {/* Create user form */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Yangi foydalanuvchi</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Foydalanuvchi nomi"
                    value={newUserForm.username}
                    onChange={e => setNewUserForm(f => ({ ...f, username: e.target.value }))}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newUserForm.email}
                    onChange={e => setNewUserForm(f => ({ ...f, email: e.target.value }))}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="password"
                    placeholder="Parol"
                    value={newUserForm.password}
                    onChange={e => setNewUserForm(f => ({ ...f, password: e.target.value }))}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <select
                    value={newUserForm.role}
                    onChange={e => setNewUserForm(f => ({ ...f, role: e.target.value }))}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="user">Foydalanuvchi</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {userFormError && <p className="text-red-500 text-sm mb-2">{userFormError}</p>}
                <button
                  onClick={handleCreateUser}
                  disabled={isSubmittingUser}
                  className="bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-lg transition"
                >
                  {isSubmittingUser ? 'Yaratilmoqda...' : 'Yaratish'}
                </button>
              </div>

              {/* Users list */}
              <div className="space-y-4">
                {usersList.map(u => {
                  // Bir loyiha endi BIR NECHTA foydalanuvchiga biriktirilishi mumkin —
                  // faqat SHU foydalanuvchida yo'q loyihalarni taklif qilamiz (avval
                  // global filtr edi: birorta user olgan loyiha hech kimga chiqmasdi).
                  const availableProjects = projects.filter(p => !u.projects.some(up => String(up.id) === String(p.id)));
                  return (
                    <div key={u.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-white">{u.username}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${u.role === 'admin' ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'}`}>
                              {u.role === 'admin' ? 'Admin' : 'Foydalanuvchi'}
                            </span>
                            {/* Hisob holati — o'chirilgan bo'lsa tizimga kira olmaydi */}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${u.is_active === 0 ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400' : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'}`}>
                              {u.is_active === 0 ? "🚫 O'chirilgan" : '✅ Faol'}
                            </span>
                          </div>
                          <div className={`text-sm ${u.is_active === 0 ? 'text-gray-400 line-through' : 'text-gray-500 dark:text-gray-400'}`}>{u.email}</div>
                        </div>
                        {u.username !== user?.username && (
                          <div className="flex items-center gap-3 shrink-0">
                            <label className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400" title="Bir vaqtda nechta emulator qo'sha oladi (standart — 3). Har emulator serverdan ~3 yadro va ~4 GB oladi.">
                              📱
                              <select value={u.emulator_slots == null ? '' : String(u.emulator_slots)} onChange={e => handleSetEmulatorSlots(u, e.target.value)}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm">
                                <option value="">3 (standart)</option>
                                {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={String(n)}>{n} ta</option>)}
                              </select>
                            </label>
                            <label className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400" title="Har loyihada nechta 🌐 brauzer qurilma (B1, B2…) qo'sha oladi (standart — 3). Har ochiq brauzer serverdan ~0.8 GB xotira oladi; butun serverda bir vaqtda 12 tagacha ochiq turadi.">
                              🌐
                              <select value={u.web_device_slots == null ? '' : String(u.web_device_slots)} onChange={e => handleSetWebDeviceSlots(u, e.target.value)}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm">
                                <option value="">3 (standart)</option>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={String(n)}>{n} ta</option>)}
                              </select>
                            </label>
                            <button
                              onClick={() => handleToggleClaudePlatform(u)}
                              title={Number(u.claude_platform) === 1
                                ? "Platforma Claude hisobidan (operator Max) foydalanadi — bosib yoping"
                                : (Number(u.claude_own) ? "O'z Claude tokeni ulangan; platforma hisobi yopiq — bosib ruxsat bering" : "Platforma Claude hisobi yopiq — o'z tokenini ulaydi yoki loyiha egasining hisobida ishlaydi. Bosib ruxsat bering")}
                              className={`text-sm font-medium transition ${Number(u.claude_platform) === 1 ? 'text-primary-700 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'} hover:text-gray-900 dark:hover:text-white`}
                            >
                              🤖 {Number(u.claude_platform) === 1 ? 'Platforma' : (Number(u.claude_own) ? "O'z hisobi" : 'Hisobsiz')}
                            </button>
                            <button
                              onClick={() => handleToggleUserActive(u)}
                              title={u.is_active === 0 ? 'Hisobni yoqish — tizimga kira oladi' : "Hisobni o'chirish — tizimga kira olmaydi"}
                              className={`text-sm font-medium transition ${u.is_active === 0 ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                            >
                              {u.is_active === 0 ? '✅ Yoqish' : "🚫 O'chirish"}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-300 font-medium transition"
                              title="Foydalanuvchini butunlay o'chirish"
                            >
                              🗑 Butunlay
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Attached projects — admins implicitly have access
                          to everything, so we don't show per-project chips
                          or the attach dropdown for them. */}
                      {u.role === 'admin' ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400 italic flex items-center gap-1.5">
                          <span className="text-primary-600 dark:text-primary-500">∞</span>
                          <span>Barcha loyihalarga to'liq dostup</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {u.projects.map(p => (
                              <span key={p.id} className="flex items-center gap-1.5 text-sm bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-3 py-1 rounded-full font-medium">
                                {p.name}
                                <select value={p.role || 'developer'} onChange={e => handleProjectRole(u.id, p.id, e.target.value)}
                                  title="Shu loyihadagi roli" className="text-xs bg-transparent border-0 py-0 pl-1 pr-5 focus:ring-0 cursor-pointer">
                                  {PROJECT_ROLE_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select>
                                <button
                                  onClick={() => handleDetachProject(u.id, p.id)}
                                  className="text-green-500 hover:text-red-600 dark:hover:text-red-300 font-bold leading-none transition"
                                  title="Ajratish"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            {u.projects.length === 0 && (
                              <span className="text-sm text-gray-400 italic">Loyiha biriktirilmagan</span>
                            )}
                          </div>

                          {/* Attach project dropdown */}
                          {availableProjects.length > 0 && (<div className="flex flex-wrap items-center gap-2">
                            <select value={attachRole[u.id] || 'developer'} onChange={e => setAttachRole(s => ({ ...s, [u.id]: e.target.value }))}
                              title="Biriktiriladigan loyihadagi roli"
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800">
                              {PROJECT_ROLE_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                            <select
                              value=""
                              onChange={e => { if (e.target.value) handleAttachProject(u.id, e.target.value); }}
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="" disabled>+ Loyiha biriktirish</option>
                              {availableProjects.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>)}

                          {/* Bo'limlarga ruxsat — har bir bo'limni yoqish/o'chirish.
                              Yoqilgan bo'limlarnigina foydalanuvchi ko'radi va kira oladi. */}
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Bo'limlarga ruxsat</div>
                            <div className="flex flex-wrap gap-2">
                              {CLIENT_SECTIONS.map(s => {
                                const on = (Array.isArray(u.allowed_sections) ? u.allowed_sections : CLIENT_SECTION_KEYS).includes(s.key);
                                return (
                                  <button
                                    key={s.key}
                                    onClick={() => handleToggleSection(u, s.key)}
                                    title={on ? "Ruxsatni olib tashlash" : "Ruxsat berish"}
                                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition ${on ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 border-primary-200 dark:border-primary-800 hover:bg-primary-200 dark:hover:bg-primary-800/70' : 'bg-gray-50 dark:bg-gray-700 text-gray-400 border-gray-200 dark:border-gray-700 line-through hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                  >
                                    {on ? '✓ ' : ''}{s.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <UiAccessEditor user={u} authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} toast={toast} onSaved={fetchUsers} />
                        </>
                      )}

                      {/* Foydalanuvchi nomini tahrirlash (admin har qanday foydalanuvchi uchun) */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <input
                          type="text"
                          placeholder="Foydalanuvchi nomi (kamida 3 belgi)"
                          value={userNameEdits[u.id] ?? u.username}
                          onChange={e => setUserNameEdits(s => ({ ...s, [u.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleChangeUsername(u); }}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={() => handleChangeUsername(u)}
                          disabled={(userNameEdits[u.id] ?? u.username).trim().length < 3 || (userNameEdits[u.id] ?? u.username).trim() === u.username}
                          className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-40 transition"
                        >
                          ✏️ Nomni o'zgartirish
                        </button>
                      </div>

                      {/* ⚙️ ENGINE TOKEN CHEKLOVI — shu foydalanuvchi uchun alohida.
                          Bo'sh = cheklov yo'q (faqat umumiy cap amal qiladi). Kvotasi
                          tugasa FAQAT shu foydalanuvchining vazifalari kutadi. */}
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">⚙️ Engine token cheklovi</span>
                          {userLimits[u.id]?.blocked && (
                            <span className="text-[11px] font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-2 py-0.5 rounded-full">⛔ Cheklov tugadi — vazifalari kutmoqda</span>
                          )}
                        </div>
                        <div className="flex items-end gap-2 flex-wrap">
                          <label className="text-[11px] text-gray-500 dark:text-gray-400">
                            5 soatlik (M token)
                            <input
                              type="number" min="0" placeholder="cheklovsiz"
                              value={userLimitEdits[`${u.id}:5h`] ?? (userLimits[u.id]?.fiveHour?.cap ? Math.round(userLimits[u.id].fiveHour.cap / 1e6) : '')}
                              onChange={e => setUserLimitEdits(s => ({ ...s, [`${u.id}:5h`]: e.target.value }))}
                              className="block mt-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 w-36 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </label>
                          <label className="text-[11px] text-gray-500 dark:text-gray-400">
                            Haftalik (M token)
                            <input
                              type="number" min="0" placeholder="cheklovsiz"
                              value={userLimitEdits[`${u.id}:w`] ?? (userLimits[u.id]?.weekly?.cap ? Math.round(userLimits[u.id].weekly.cap / 1e6) : '')}
                              onChange={e => setUserLimitEdits(s => ({ ...s, [`${u.id}:w`]: e.target.value }))}
                              className="block mt-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 w-36 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </label>
                          <button
                            onClick={() => handleSaveUserLimits(u.id)}
                            disabled={savingLimitId === u.id}
                            className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-40 transition"
                          >
                            {savingLimitId === u.id ? '⏳ Saqlanmoqda…' : '💾 Cheklovni saqlash'}
                          </button>
                        </div>
                        {/* 🔴 IKKI CHEKLOV BOR va KICHIGI G'OLIB. Bu shaxsiy cap; engine'ni AVVAL
                            umumiy (global) cap to'xtatadi (Konfiguratsiya → ⚙️ Engine cheklovi).
                            2026-08-14 va 2026-09-12 da operator shu kartada capni ko'tarib
                            "oshirdim-ku" degan, engine esa global 50M da turgan. Shuning uchun
                            global qiymat shu yerda ko'rinadi va shaxsiy > global bo'lsa
                            ogohlantirish + bir bosishda globalni tenglashtirish. */}
                        {globalLimits && (() => {
                          const g5 = Number(globalLimits.fiveHour?.cap || 0), gw = Number(globalLimits.weekly?.cap || 0);
                          const toT = (v) => { const r = String(v ?? '').trim(); if (r === '') return null; const n = parseInt(r, 10); return n > 0 ? n * 1e6 : null; };
                          const u5 = userLimitEdits[`${u.id}:5h`] !== undefined ? toT(userLimitEdits[`${u.id}:5h`]) : (userLimits[u.id]?.fiveHour?.cap || null);
                          const uw = userLimitEdits[`${u.id}:w`] !== undefined ? toT(userLimitEdits[`${u.id}:w`]) : (userLimits[u.id]?.weekly?.cap || null);
                          const over5 = globalLimits.enabled && g5 > 0 && u5 && u5 > g5;
                          const overw = globalLimits.enabled && gw > 0 && uw && uw > gw;
                          const M = (n) => `${Math.round(Number(n) / 1e6)}M`;
                          return (
                            <div className={`mt-2 text-[11px] rounded-lg px-2.5 py-1.5 border ${over5 || overw ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'}`}>
                              <span>Umumiy (global) cheklov — hammaga birga: 5 soat <b>{g5 ? M(g5) : '∞'}</b> · haftalik <b>{gw ? M(gw) : '∞'}</b>{globalLimits.enabled ? '' : " (o'chirilgan)"}. Engine AVVAL shunga to'xtaydi — shaxsiy cheklov undan katta bo'lsa hech qachon ishlamaydi.</span>
                              {(over5 || overw) && (
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold">⚠️ Bu foydalanuvchining {over5 ? `5 soatlik ${M(u5)} > umumiy ${M(g5)}` : ''}{over5 && overw ? ', ' : ''}{overw ? `haftalik ${M(uw)} > umumiy ${M(gw)}` : ''} — engine baribir umumiyda to'xtaydi.</span>
                                  <button
                                    onClick={() => handleRaiseGlobalLimits(over5 ? u5 : null, overw ? uw : null)}
                                    disabled={savingLimitId === 'global'}
                                    className="px-2 py-0.5 rounded font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40">
                                    {savingLimitId === 'global' ? '⏳' : `↑ Umumiyni ham ${over5 ? M(u5) : ''}${over5 && overw ? ' / ' : ''}${overw ? M(uw) : ''} qilish`}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* Sarfni CAP'ga NISBATAN ko'rsatamiz. Faqat xom son ko'rsatilsa
                            "cheklov qo'ydim, lekin ishlamadi" chalkashligi chiqadi — aslida
                            cap sarfdan YUQORI bo'lgani uchun hali tegmagan bo'ladi. */}
                        <div className="mt-2 space-y-1">
                          {[['fiveHour', '5 soat'], ['weekly', '7 kun']].map(([k, lbl]) => {
                            const w = userLimits[u.id]?.[k] || {};
                            const used = w.used || 0, cap = w.cap || 0;
                            const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
                            const col = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-400' : 'bg-emerald-400';
                            return (
                              <div key={k} className="flex items-center gap-2">
                                <span className="text-[11px] text-gray-400 w-12 shrink-0">{lbl}</span>
                                {cap > 0 ? (
                                  <>
                                    <div className="w-40 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden shrink-0">
                                      <div className={`h-1.5 rounded-full ${col}`} style={{ width: `${pct}%` }}/>
                                    </div>
                                    <span className="text-[11px] font-mono text-gray-600 dark:text-gray-400">
                                      {(used / 1e6).toFixed(1)}M / {(cap / 1e6).toFixed(0)}M
                                      <span className={pct >= 100 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}> ({pct}%)</span>
                                    </span>
                                    {pct < 100
                                      ? <span className="text-[11px] text-gray-400">— hali yetmadi, {((cap - used) / 1e6).toFixed(1)}M qoldi</span>
                                      : w.freeInMinutes > 0
                                        ? <span className="text-[11px] text-orange-600 dark:text-orange-400">— ⏳ {fmtWait(w.freeInMinutes)} dan keyin bo'shaydi</span>
                                        : null}
                                  </>
                                ) : (
                                  <span className="text-[11px] text-gray-400">
                                    sarf <span className="font-mono text-gray-600 dark:text-gray-400">{(used / 1e6).toFixed(1)}M</span> · cheklov yo'q
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {/* ♻️ HISOBNI NOLGA TUSHIRISH — FAQAT shu foydalanuvchi uchun.
                            Har kimning cheklov oynasi mustaqil yuradi, shuning uchun bu
                            boshqalarning hisobiga TEGMAYDI. Yozuvlar o'chirilmaydi —
                            reset vaqtidan oldingi sarf oynaga qo'shilmaydi. */}
                        <div className="flex items-center gap-2 flex-wrap mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700">
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 mr-1">♻️ Hisobni nolga tushirish:</span>
                          {[['fiveHour', '⏱ 5 soatlik'], ['weekly', '📅 Haftalik'], ['both', '♻️ Ikkalasi']].map(([w, lbl]) => (
                            <button
                              key={w}
                              onClick={() => resetUserWindow(u, w)}
                              disabled={!!resettingWin}
                              className="text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/40 hover:text-orange-700 dark:hover:text-orange-300 hover:border-orange-300 dark:hover:border-orange-600 disabled:opacity-40 transition"
                            >
                              {resettingWin === `${u.id}:${w}` ? '⏳…' : lbl}
                            </button>
                          ))}
                          {(userLimits[u.id]?.resets?.fiveHour || userLimits[u.id]?.resets?.weekly) && (
                            <span className="text-[11px] text-gray-400">
                              oxirgi reset: {new Date(userLimits[u.id].resets.weekly || userLimits[u.id].resets.fiveHour).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1.5">
                          Bo'sh qoldirilsa cheklov olib tashlanadi (faqat umumiy cap qoladi).
                          {' '}Cheklov faqat <span className="text-gray-500 dark:text-gray-400">sarf cap'dan OSHGANDA</span> ishlaydi.
                          {' '}Nolga tushirish faqat <span className="text-gray-500 dark:text-gray-400">shu foydalanuvchiga</span> ta'sir qiladi — token tarixi o'chmaydi.
                        </div>
                      </div>

                      {/* Parol almashtirish (admin har qanday foydalanuvchi uchun) */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <input
                          type="password"
                          placeholder="Yangi parol (kamida 6 belgi)"
                          value={userPwEdits[u.id] || ''}
                          onChange={e => setUserPwEdits(s => ({ ...s, [u.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleChangePassword(u.id); }}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={() => handleChangePassword(u.id)}
                          disabled={(userPwEdits[u.id] || '').trim().length < 6}
                          className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-800 dark:hover:text-primary-200 disabled:opacity-40 transition"
                        >
                          🔑 Parolni o'zgartirish
                        </button>
                      </div>
                    </div>
                  );
                })}
                {usersList.length === 0 && (
                  <p className="text-gray-400 text-center py-8">Foydalanuvchilar yo'q</p>
                )}
              </div>
            </>
          )}

          {activeTab === 'console' && canSeeConsole && (
            <ConsolePanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL}
              deepLink={consoleDeepLink} onDeepLinkDone={() => setConsoleDeepLink(null)}
              appProjectId={abProjectId} />
          )}
          {activeTab === 'health' && (user?.role === 'admin' || canSee('health')) && (
            <HealthPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
          )}
          {activeTab === 'knowledge' && (user?.role === 'admin' || canSee('knowledge')) && (
            <KnowledgePanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
          )}

          {activeTab === 'forecast' && (user?.role === 'admin' || canSee('forecast')) && (
            <ForecastPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projects={projects} />
          )}

          {activeTab === 'reengineering' && (user?.role === 'admin' || canSee('reengineering')) && (
            <ReengineeringPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} projects={projects} onSelectProject={(id) => selectStudioProject(id)} />
          )}

          {activeTab === 'integrations' && (user?.role === 'admin' || canSee('integrations')) && (
            <IntegrationsPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} projects={projects} onSelectProject={(id) => selectStudioProject(id)} isAdmin={user?.role === 'admin'} userId={user?.id}
              onOpenConsole={canSeeConsole ? (profile, extra) => openConsoleAt(profile, undefined, extra) : null}
              onOpenPlaybook={(id) => setPlaybookRunId(id)} />
          )}

          {imgViewer && <ImageLightbox images={imgViewer.images} index={imgViewer.index} onClose={() => setImgViewer(null)} />}
          <ImportProjectModal open={importOpen} onClose={() => setImportOpen(false)} authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} onDone={async (pid) => { try { await fetchProjects(); } catch (e) {} selectStudioProject(String(pid)); setActiveTab(user?.role === 'admin' ? 'projects' : 'studio'); }} />

          {activeTab === 'seckb' && user?.role === 'admin' && (
            <SecurityKbPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
          )}

          {activeTab === 'emulators' && user?.role === 'admin' && (
            <AdminEmulatorsPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />
          )}

          {activeTab === 'pages' && (
            <>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Varaqlar</h1>
                {pagesProjectId && !isGenerating && (
                  <button
                    onClick={handleGenerateClick}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary-700 dark:bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 transition shadow"
                  >
                    ✨ Varaqlarni generatsiya qilish
                  </button>
                )}
              </div>

              {/* Project selector */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-6 flex items-end gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Proyekt</label>
                  <select
                    value={pagesProjectId}
                    onChange={e => { setPagesProjectId(e.target.value); fetchPages(e.target.value); }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Tanlang...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                {pagesProjectId && (
                  <button onClick={() => fetchPages()} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                    ↻ Yangilash
                  </button>
                )}
              </div>

              {pagesError && <p className="text-red-500 mb-4 text-sm">{pagesError}</p>}

              {/* Generation progress */}
              {isGenerating && (
                <div className="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg p-5 mb-6 flex items-center gap-3">
                  <svg className="animate-spin h-5 w-5 text-primary-700 dark:text-primary-500 shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  <div>
                    <p className="font-semibold text-primary-900 dark:text-primary-200">Generatsiya qilinmoqda...</p>
                    <p className="text-sm text-primary-700 dark:text-primary-500">{generateProgress || 'Emulator orqali sahifalar ochilmoqda'}</p>
                  </div>
                </div>
              )}

              {pagesLoading && !isGenerating && (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Skanlanmoqda...
                </div>
              )}

              {!pagesLoading && !isGenerating && pagesProjectId && pagesList.length === 0 && !pagesError && (
                <div className="text-center py-16 text-gray-400">
                  <p className="text-5xl mb-4">📱</p>
                  <p className="text-lg font-medium mb-2">Sahifalar topilmadi</p>
                  <p className="text-sm">Proyektda screens/pages papkasi bormi?</p>
                </div>
              )}

              {/* Pages grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {pagesList.map(page => (
                  <div key={page.name} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col group">
                    {/* Screenshot — click to open lightbox */}
                    <div
                      className="bg-gray-900 relative cursor-pointer"
                      style={{ paddingBottom: '177%' }}
                      onClick={() => pageScreenshots[page.name] && !pageScreenshots[page.name].includes('undefined') && setLightboxSrc(pageScreenshots[page.name])}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <PageScreenshot src={pageScreenshots[page.name]} alt={page.name} />
                      </div>
                      {pageScreenshots[page.name] && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition flex items-center justify-center">
                          <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transition">🔍</span>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3 flex flex-col gap-2">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" title={page.name}>{page.name}</p>
                      {page.route
                        ? <p className="text-xs text-primary-600 dark:text-primary-500 font-mono truncate">{page.route}</p>
                        : <p className="text-xs text-gray-400 truncate" title={page.relPath}>{page.relPath}</p>
                      }
                      <button
                        onClick={() => capturePageScreenshot(page.name)}
                        disabled={capturingPage === page.name || !emulatorFrame}
                        title={!emulatorFrame ? 'Emulator streamni boshlang' : 'Joriy ekranni saqlash'}
                        className="mt-1 w-full py-1.5 text-xs bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-40 transition"
                      >
                        {capturingPage === page.name ? 'Saqlanmoqda...' : '📸 Suratga ol'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ===== STUDIO HUB (unified: design canvas + real pages + emulator + tasks) ===== */}
          {(activeTab === 'studio' || activeTab === 'design') && (
            <div className="flex flex-col h-full min-h-0">
              {/* ===== Command bar (unified app-shell header) ===== */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4">
                {/* Row A: title · project · contextual canvas actions */}
                {/* Sarlavha («🏗️ Studio» / «🖼️ Dizayn») navbar'ga ko'chdi (operator, 2026-09-18) — tablar
                    to'liq kenglikni oladi va WRAP bo'ladi; gorizontal scroll (xunuk) yo'q. */}
                <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
                  {/* Tablar sarlavha bilan BIR qatorda (operator, 2026-09-18) — loyiha nomi endi navbar'dagi 📁 da, bu yerda takror ko'rsatilmaydi */}
                  {/* Loyiha tanlanmagan bo'lsa ham tablar ko'rinadi — bosilganda kontentda «Proyekt tanlanmagan» (operator, 2026-09-18) */}
                  {(
                    <div className="flex flex-wrap gap-x-1 gap-y-3.5 flex-1 min-w-0 items-center pb-2.5">
                      {/* 🖼️ Dizayn bo'limi tablari */}
                      {activeTab === 'design' && (<>
                      <button
                        onClick={() => setStudioView('canvas')}
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'canvas' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🎨 Dizayn
                        <StudioAiTag view="canvas" ai={aiTiers} />
                      </button>
                      <button
                        onClick={() => setStudioView('pages')}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'pages' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🔀 Solishtirish
                      </button>
                      <button
                        onClick={() => setStudioView('style')}
                        title="Dizayn yo'nalishi: tayyor uslub, havola/skrinshot namunalari, brief — agentlar shunga amal qiladi"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'style' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🎛 Uslub
                        <StudioAiTag view="style" ai={aiTiers} />
                      </button>
                      <button
                        onClick={() => setStudioView('harmony')}
                        title="Dizayn moslashtirish: mijoz shabloni bo'yicha hamma sahifalarni (rollar bo'yicha) rasmga olib solishtiradi va vazifalarni bittadan beradi — serverda ishlaydi"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'harmony' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🧭 Moslik
                      </button>
                      {canSee('logo') && (
                      <button
                        onClick={() => setStudioView('logo')}
                        title="Loyiha logolari: AI bilan yaratish, tayyor logoni yuklash, ⭐ asosiy logo — dizayn sahifalariga shu joylanadi"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'logo' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🏷 Logo
                        <StudioAiTag view="logo" ai={aiTiers} />
                      </button>
                      )}
                      </>)}
                      {/* 🏗️ Studio bo'limi tablari */}
                      {activeTab === 'studio' && (<>
                      {!isProjClient && (<>
                      {studioTabOk('browser') && (!abWorkspace || browserModule) && (
                        <button
                          onClick={() => setStudioView('browser')}
                          className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'browser' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          🌐 Brauzer
                          <StudioAiTag view="browser" ai={aiTiers} />
                        </button>
                      )}
                      {studioTabOk('emulator') && (!abWorkspace || selectedProjectModules.some(m => m.module_type === "mobile")) && (
                        <button
                          onClick={() => setStudioView('emulator')}
                          className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'emulator' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          📱 Emulator
                          <StudioAiTag view="emulator" ai={aiTiers} />
                        </button>
                      )}
                      {studioTabOk('database') && (!abWorkspace || selectedProjectModules.some(m => m.module_type === "backend")) && (
                        <button
                          onClick={() => setStudioView('database')}
                          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'database' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          🗄️ Database
                        </button>
                      )}
                      {studioTabOk('files') && (
                      <button
                        onClick={() => setStudioView('files')}
                        title="Modul kod-papkasidagi fayllarni ko'rish (faqat o'qish)"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'files' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        📁 Fayllar
                        <StudioAiTag view="files" ai={aiTiers} />
                      </button>
                      )}
                      {studioTabOk('status') && (
                      <button
                        onClick={() => setStudioView('status')}
                        title="Har bir modul (backend/web/mobile) qaysi task/branch'da ekani + mobil APK versiyasi"
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'status' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        📦 Holat
                      </button>
                      )}
                      {studioTabOk('architecture') && (
                      <button
                        onClick={() => setStudioView('architecture')}
                        title="Loyiha qaysi texnologiyalarda qurilgan — repolardan o'qilgan haqiqiy stack"
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'architecture' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🏛 Arxitektura
                      </button>
                      )}
                      </>)}
                      {studioTabOk('map') && (
                      <button
                        onClick={() => setStudioView('map')}
                        title="Loyiha xaritasi — etaplar × rollar: har joyning sinov rasmlari, u yerga yetib borish yo'li, holati va «Shu joyga vazifa» (mijoz bilan bir xil joyni ko'rib gaplashish uchun)"
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'map' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🗺 Xarita
                      </button>
                      )}
                      {studioTabOk('docs') && (
                      <button
                        onClick={() => setStudioView('docs')}
                        title="Loyiha hujjatlari: texnik hujjat, API, biznes sxemasi, sertifikatlar, sifat hisoboti"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'docs' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        📄 Hujjatlar
                        <StudioAiTag view="docs" ai={aiTiers} />
                      </button>
                      )}
                      {!isProjClient && studioTabOk('todo') && <button
                        onClick={() => setStudioView('todo')}
                        title="Loyiha rejasi — kanban doska (reja ≠ vazifa)"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'todo' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        📋 Reja
                        <StudioAiTag view="todo" ai={aiTiers} />
                      </button>}
                      {studioTabOk('qa') && (
                      <button
                        onClick={() => setStudioView('qa')}
                        title="Agent sinovi (emulator + brauzer) va sinovdan kelgan topilmalar"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'qa' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🧪 Sinov
                        <StudioAiTag view="qa" ai={aiTiers} />
                      </button>
                      )}
                      {studioTabOk('publish') && isProjOwner && (
                      <button
                        onClick={() => setStudioView('publish')}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'publish' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🚀 Publish
                      </button>
                      )}
                      {studioTabOk('secrets') && isProjOwner && (
                      <button
                        onClick={() => setStudioView('secrets')}
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'secrets' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🔑 Sirlar
                        <StudioAiTag view="secrets" ai={aiTiers} />
                      </button>
                      )}
                      {studioTabOk('crashes') && (
                      <button
                        onClick={() => setStudioView('crashes')}
                        title="Ilovadan kelgan real xatolar (crash) + avtomatik [CRASH-FIX] vazifalar"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'crashes' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        💥 Crashlar
                        {crashActive > 0 && (
                          <span title={`${crashActive} ta yechilmagan (yoki tuzatishdan keyin qaytgan) crash`}
                            className={`ml-0.5 min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold leading-none ${studioView === 'crashes' ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>
                            {crashActive > 99 ? '99+' : crashActive}
                          </span>
                        )}
                        <StudioAiTag view="crashes" ai={aiTiers} />
                      </button>
                      )}
                      {studioTabOk('security') && isProjOwner && (
                      <button
                        onClick={() => setStudioView('security')}
                        title="Loyihaning jonli manzillarini xavfsizlik skaneriga berish va skan boshlash"
                        className={`relative min-w-[6.5rem] justify-center flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${studioView === 'security' ? 'bg-primary-700 dark:bg-primary-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        🛡️ Xavfsizlik
                        <StudioAiTag view="security" ai={aiTiers} />
                      </button>
                      )}
                      </>)}
                    </div>
                  )}
                  {abWorkspace && studioView === 'canvas' && selectedStudioModule?.module_type !== 'backend' && (
                    <div className="flex items-center gap-2 ml-auto flex-wrap">
                      {abAddingConnection && (
                        <span className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 px-3 py-1.5 rounded-lg">
                          <span className="animate-pulse">●</span> Maqsad sahifani bosing
                          <button onClick={() => setAbAddingConnection(null)} className="ml-1 text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 font-bold">✕</button>
                        </span>
                      )}
                      <button
                        onClick={() => setShowTzModal(true)}
                        className="px-3.5 py-2 bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg text-sm font-semibold hover:opacity-90 transition shadow-sm"
                      >
                        📋 TZ dan yaratish
                      </button>
                      <input
                        type="text"
                        value={abNewPageTitle}
                        onChange={e => setAbNewPageTitle(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && abAddPage()}
                        placeholder="Sahifa nomi..."
                        className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm w-36 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 transition"
                      />
                      <button onClick={abAddPage}
                        className="px-3.5 py-2 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 transition shadow-sm">
                        + Sahifa
                      </button>
                      {abPages.some(p => p.svg_content) && (abLogoJobId ? (
                        <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="animate-pulse">🏷</span> {abLogoProgress || 'Logo joylanmoqda...'}
                          <button onClick={() => cancelBgJob(abLogoJobId)}
                            className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-800/70 transition shrink-0">
                            ⏹ To'xtatish
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={abApplyLogo}
                          title="Loyihaning ⭐ asosiy logosini dizayn sahifalariga joylaydi (modul tanlangan bo'lsa — faqat o'sha modul sahifalariga)"
                          className="px-3.5 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-700 dark:hover:text-primary-300 transition flex items-center gap-1.5"
                        >
                          🏷 Logoni joylash
                        </button>
                      ))}
                      {abPages.some(p => p.svg_content) && (
                        <button
                          onClick={() => abExportPdf()}
                          disabled={abPdfExporting}
                          title="Barcha sahifalarni PDF qilib saqlash"
                          className="px-3.5 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-300 disabled:opacity-50 transition flex items-center gap-1.5"
                        >
                          {abPdfExporting ? '⏳' : '📄'} PDF
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Row C: module filter — faqat modul filtri ISHLATILADIGAN view'larda (Dizayn: canvas/pages; Studio: test, secrets).
                    Brauzer/Emulator/Database/Fayllar/Holat o'z modulini o'zi aniqlaydi — bu qator ularda shovqin edi. */}
                {abWorkspace && selectedProjectModules.length > 0 && ((activeTab === 'design' && studioView !== 'logo') || studioView === 'secrets') && (
                  <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">
                    <span className="text-xs font-medium text-gray-400 shrink-0">Modul:</span>
                    <button
                      onClick={() => { setStudioModuleId(''); setAbSelectedPageId(null); }}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition ${studioModuleId === '' ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      Barchasi
                    </button>
                    {selectedProjectModules
                      .filter(m => !(activeTab === 'design' && m.module_type === 'backend')) /* Dizaynda backend keraksiz — dizayni yo'q */
                      .map(m => (
                      <button
                        key={m.id}
                        onClick={() => { setStudioModuleId(String(m.id)); setAbSelectedPageId(null); }}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition ${String(studioModuleId) === String(m.id) ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        {moduleTypeIcon(m.module_type)} {m.name} <ModId id={m.id} />
                      </button>
                    ))}
                    {abUnbucketedCount > 0 && studioModuleId === '' && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded-lg" title="Modulga biriktirilmagan sahifalar">
                        {abUnbucketedCount} ta tasniflanmagan
                      </span>
                    )}
                    {selectedStudioModule?.test_url && (
                      <a
                        href={selectedStudioModule.test_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Test muhiti (yangi oynada ochish)"
                        className="ml-auto text-xs text-primary-800 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 px-2.5 py-1 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/60 transition font-mono"
                      >
                        🔗 {selectedStudioModule.test_url.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                )}
              </div>

              {!abWorkspace && (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 select-none py-20">
                  <p className="font-semibold text-gray-500 dark:text-gray-400 text-lg">Proyekt tanlanmagan</p>
                  <button onClick={() => setImportOpen(true)} className="mt-3 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">📥 Mavjud loyihani qo'shish</button>
                </div>
              )}

              {/* ===== Studio → 🗄️ Database — read-only DB ER diagram (live test-env DB) ===== */}
              {abWorkspace && activeTab === 'studio' && studioView === 'database' && renderDatabaseInner()}

              {/* Dizayn canvas: backend modul tanlansa — ERD Studio → 🗄️ Database'ga ko'chirilgan */}
              {abWorkspace && activeTab === 'design' && studioView === 'canvas' && selectedStudioModule?.module_type === 'backend' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-4xl mb-3">🗄</p>
                    <p className="font-medium text-gray-500 dark:text-gray-400">Bu backend modul</p>
                    <p className="text-sm mt-1">Ma'lumotlar bazasi chizmasi 🏗️ Studio → 🗄️ Database bo'limiga ko'chirildi.</p>
                  </div>
                </div>
              )}

              {abWorkspace && studioView === 'canvas' && selectedStudioModule?.module_type !== 'backend' && (
                <div className="flex gap-0 flex-1 min-h-0 overflow-hidden relative">

                  {/* Canvas — full width, right panel overlaps */}
                  <div
                    ref={canvasRef}
                    className="relative flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto"
                    style={{ minHeight: 480, backgroundImage: 'radial-gradient(circle, rgba(100,116,139,0.12) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
                    onClick={() => { if (!abDragging) { setAbSelectedPageId(null); setAbSelectedConnection(null); } }}
                    onMouseMove={e => {
                      if (abAddingConnection) {
                        const r = e.currentTarget.getBoundingClientRect();
                        setAbMousePos({ x: e.clientX - r.left + e.currentTarget.scrollLeft, y: e.clientY - r.top + e.currentTarget.scrollTop });
                      }
                    }}
                  >
                    {/* SVG connection arrows */}
                    <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 3, width: '100%', height: '100%', overflow: 'visible' }}>
                      <defs>
                        <marker id="ab-arrow" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                          <path d="M0,0 L0,8 L10,4 z" fill="#6366f1"/>
                        </marker>
                        <marker id="ab-arrow-sel" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                          <path d="M0,0 L0,8 L10,4 z" fill="#ef4444"/>
                        </marker>
                      </defs>
                      {abConnections.map(c => {
                        const fp = visibleAbPages.find(p => p.id === c.from_page_id);
                        const tp = visibleAbPages.find(p => p.id === c.to_page_id);
                        if (!fp || !tp) return null;
                        const x1 = fp.position_x + 200; const y1 = fp.position_y + 110;
                        const x2 = tp.position_x; const y2 = tp.position_y + 110;
                        const mx = (x1+x2)/2; const my = (y1+y2)/2;
                        const isSel = abSelectedConnection === c.id;
                        const color = isSel ? '#ef4444' : '#6366f1';
                        return (
                          <g key={c.id} style={{ pointerEvents: 'all', cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setAbSelectedConnection(isSel ? null : c.id); setAbSelectedPageId(null); }}>
                            {/* Wide invisible hit area */}
                            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="16"/>
                            {/* Visible line */}
                            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={isSel ? 3 : 2}
                              markerEnd={isSel ? 'url(#ab-arrow-sel)' : 'url(#ab-arrow)'} opacity="0.85"
                              strokeDasharray={isSel ? '6 3' : 'none'}/>
                            {/* Midpoint delete button */}
                            {isSel && (
                              <g onClick={e => { e.stopPropagation(); abDeleteConnection(c.id); }}>
                                <circle cx={mx} cy={my} r="12" fill="#ef4444"/>
                                <text x={mx} y={my+4} textAnchor="middle" fontSize="14" fill="white" fontWeight="bold">×</text>
                              </g>
                            )}
                          </g>
                        );
                      })}
                      {/* Live preview line while connecting */}
                      {abAddingConnection && (() => {
                        const fp = visibleAbPages.find(p => p.id === abAddingConnection);
                        if (!fp) return null;
                        const x1 = fp.position_x + 200; const y1 = fp.position_y + 110;
                        return <line x1={x1} y1={y1} x2={abMousePos.x} y2={abMousePos.y}
                          stroke="#f97316" strokeWidth="2" strokeDasharray="6 4" opacity="0.8" style={{ pointerEvents: 'none' }}/>;
                      })()}
                    </svg>

                    {/* Page nodes */}
                    <div style={{ position: 'relative', minWidth: visibleAbPages.reduce((m,p) => Math.max(m, p.position_x+240), 700), minHeight: visibleAbPages.reduce((m,p) => Math.max(m, p.position_y+280), 500) }}>
                      {visibleAbPages.map(page => (
                        <div
                          key={page.id}
                          style={{ position: 'absolute', left: page.position_x, top: page.position_y, width: 200, zIndex: abDragging?.pageId === page.id ? 10 : 2 }}
                          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow border-2 select-none group ${abDragging?.pageId === page.id ? 'cursor-grabbing shadow-xl' : abAddingConnection ? 'cursor-pointer' : 'cursor-grab'} ${
                            abSelectedPageId === page.id ? 'border-primary-600 shadow-primary-200' :
                            abAddingConnection ? 'border-orange-300 dark:border-orange-700 hover:border-orange-400' : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                          }`}
                          onMouseEnter={() => setAbHoveredCard(page.id)}
                          onMouseLeave={() => setAbHoveredCard(null)}
                          onClick={e => {
                            e.stopPropagation();
                            if (abAddingConnection) { abAddConnection(page.id); return; }
                            if (!abDragging) { setAbSelectedPageId(page.id); setAbSelectedConnection(null); abLoadVersions(page.id); }
                          }}
                          onMouseDown={e => {
                            if (abAddingConnection) return;
                            e.stopPropagation();
                            e.preventDefault();
                            setAbDragging({ pageId: page.id, startX: e.clientX, startY: e.clientY, origX: page.position_x, origY: page.position_y });
                          }}
                        >
                          {/* SVG thumbnail */}
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-t-2xl overflow-hidden relative" style={{ height: 180 }}>
                            {page.svg_content ? (
                              <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: page.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%;object-fit:contain"') }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Preview yo'q</div>
                            )}
                            {/* Eye button — open large preview */}
                            {page.svg_content && (
                              <button
                                title="Kattalashtirib ko'rish"
                                onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
                                onClick={e => { e.stopPropagation(); setSvgPreview(page); }}
                                className="absolute top-1.5 right-1.5 w-7 h-7 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition z-10"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="p-2.5">
                            <div className="flex items-center justify-between gap-1">
                              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{page.title}</p>
                              {(() => {
                                const pm = selectedProjectModules.find(m => String(m.id) === String(page.module_id));
                                if (!pm || (pm.module_type !== 'web' && pm.module_type !== 'mobile')) return null;
                                const real = matchRealPage(page);
                                return <span title={real ? 'Haqiqiy sahifa bor' : 'Haqiqiy topilmadi'} className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 font-semibold ${real ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>{real ? '✅' : '○'}</span>;
                              })()}
                            </div>
                            <p className="text-gray-400 text-xs font-mono truncate">{page.route}</p>
                          </div>

                          {/* Right-side port — drag to connect */}
                          {!abAddingConnection && (abHoveredCard === page.id || abSelectedPageId === page.id) && (
                            <div
                              title="Bog'lash"
                              className="absolute top-1/2 -right-4 -translate-y-1/2 w-7 h-7 bg-primary-700 dark:bg-primary-600 rounded-full flex items-center justify-center cursor-crosshair shadow-md hover:bg-primary-800 dark:hover:bg-primary-700 transition z-20"
                              style={{ pointerEvents: 'all' }}
                              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
                              onClick={e => {
                                e.stopPropagation();
                                setAbAddingConnection(page.id);
                                const r = canvasRef.current?.getBoundingClientRect();
                                setAbMousePos({ x: page.position_x + 200, y: page.position_y + 110 });
                              }}
                            >
                              <span className="text-white text-sm font-bold">→</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {visibleAbPages.length === 0 && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 select-none">
                        <div className="w-16 h-16 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center text-3xl mb-3">📄</div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{studioModuleId ? 'Bu modulda sahifa yo\'q — qo\'shing yoki TZ dan yarating' : 'Sahifa qo\'shing'}</p>
                      </div>
                    )}
                  </div>

                  {/* Right panel — slides in as overlay when page selected */}
                  <div className={`absolute top-0 right-0 h-full flex flex-col gap-3 overflow-y-auto bg-transparent transition-all duration-200 ${abSelectedPage ? 'w-80 opacity-100 pointer-events-auto' : 'w-0 opacity-0 pointer-events-none'}`}
                    style={{ zIndex: 5 }}>
                  <div className="w-80 flex flex-col gap-3 overflow-y-auto h-full pl-2">
                    {abSelectedPage ? (
                      <>
                        {/* Page info */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                          <input
                            className="w-full font-bold text-gray-900 dark:text-white text-sm border border-transparent rounded-lg px-2 py-1 mb-1 hover:border-gray-300 dark:hover:border-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition"
                            value={abSelectedPage.title}
                            onChange={e => setAbPages(prev => prev.map(p => p.id === abSelectedPageId ? { ...p, title: e.target.value } : p))}
                            onBlur={e => {
                              const t = e.target.value.trim();
                              if (!t) return;
                              authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPageId}`, {
                                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ title: t }),
                              });
                            }}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                          />
                          <input
                            className="w-full text-xs text-gray-400 font-mono border border-transparent rounded-lg px-2 py-1 mb-2 hover:border-gray-300 dark:hover:border-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition"
                            value={abSelectedPage.route}
                            onChange={e => setAbPages(prev => prev.map(p => p.id === abSelectedPageId ? { ...p, route: e.target.value } : p))}
                            onBlur={e => {
                              const r = e.target.value.trim() || '/';
                              authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPageId}`, {
                                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ route: r }),
                              });
                            }}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                          />
                          {/* Module assignment */}
                          {selectedProjectModules.length > 0 && (
                            <select
                              value={abSelectedPage.module_id ?? ''}
                              onChange={e => {
                                const mid = e.target.value ? Number(e.target.value) : null;
                                setAbPages(prev => prev.map(p => p.id === abSelectedPageId ? { ...p, module_id: mid } : p));
                                authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPageId}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ module_id: mid }),
                                });
                              }}
                              className="w-full text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 mb-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                              <option value="">📁 Modul: tasniflanmagan</option>
                              {selectedProjectModules.map(m => (
                                <option key={m.id} value={m.id}>{moduleTypeIcon(m.module_type)} {m.name} #{m.id}</option>
                              ))}
                            </select>
                          )}
                          {/* Full SVG preview */}
                          {abSelectedPage.svg_content && (
                            <div className="mb-3">
                              <div className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-y-auto bg-gray-50 dark:bg-gray-700 mb-2" style={{ maxHeight: '60vh' }}>
                                <div dangerouslySetInnerHTML={{ __html: abSelectedPage.svg_content.replace(/<svg/, '<svg style="width:100%;height:auto;display:block"') }} />
                              </div>
                              <button
                                onClick={() => abExportPdf(abSelectedPage)}
                                disabled={abPdfExporting}
                                className="w-full py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                              >
                                {abPdfExporting ? '⏳ PDF tayyorlanmoqda...' : '📄 Bu sahifani PDF saqlash'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* 🔀 Compare with the real built page + backend link (merged Solishtirish) */}
                        {(() => {
                          const pageMod = selectedProjectModules.find(m => String(m.id) === String(abSelectedPage.module_id));
                          if (pageMod && pageMod.module_type !== 'web' && pageMod.module_type !== 'mobile') return null;
                          const real = matchRealPage(abSelectedPage);
                          const shot = screenshotUrlFor(real);
                          return (
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">🔀 Haqiqiy sahifa</p>
                              {real ? (
                                <>
                                  <div className="border border-gray-100 dark:border-gray-700 rounded-lg bg-gray-900 overflow-hidden cursor-pointer mb-1.5" style={{ height: 200 }}
                                       onClick={() => shot && setLightboxSrc(shot)}>
                                    {shot ? <PageScreenshot src={shot} alt={real.name} /> : <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs text-center px-2">Screenshot hali yo'q</div>}
                                  </div>
                                  <p className="text-[11px] text-green-600 dark:text-green-400 mb-2 truncate">✅ {real.name} <span className="text-gray-400 font-mono">{real.route}</span></p>
                                </>
                              ) : (
                                <p className="text-xs text-gray-400 text-center py-3">Mos haqiqiy sahifa topilmadi</p>
                              )}
                              <button onClick={handleGenerateClick} disabled={isGenerating}
                                      className="w-full py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50 transition mb-2">
                                {isGenerating ? '⏳ Generatsiya...' : '📸 Haqiqiy screenshotni yangilash'}
                              </button>
                              <select value={abSelectedPage.real_page_ref || ''}
                                      onChange={e => {
                                        const ref = e.target.value || null;
                                        setAbPages(prev => prev.map(p => p.id === abSelectedPage.id ? { ...p, real_page_ref: ref } : p));
                                        authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${abSelectedPage.id}`, {
                                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ real_page_ref: ref }),
                                        });
                                      }}
                                      className="w-full text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-200">
                                <option value="">🔗 Avto{real && !abSelectedPage.real_page_ref ? ` (${real.name})` : ''}</option>
                                {pagesList.filter(r => r._moduleId == null || String(r._moduleId) === String(abSelectedPage.module_id)).map(r => (
                                  <option key={r.relPath} value={r.relPath}>{r.name} — {r.route}</option>
                                ))}
                              </select>
                              {real && <BackendLinkInfo projectId={abProjectId} real={real} authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} />}
                            </div>
                          );
                        })()}

                        {/* AI Redesign */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">✨ AI Qayta Loyihalash</p>

                          {/* Image preview */}
                          {abDesignImage && (
                            <div className="flex items-center gap-2 mb-2 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg px-3 py-2">
                              <img src={`data:image/*;base64,${abDesignImage.base64}`} alt="" className="w-8 h-8 object-cover rounded border border-primary-200 dark:border-primary-800" />
                              <span className="flex-1 text-xs text-primary-800 dark:text-primary-300 truncate">{abDesignImage.name}</span>
                              <button onClick={() => setAbDesignImage(null)} className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 text-base leading-none">×</button>
                            </div>
                          )}

                          <textarea
                            value={abPrompt}
                            onChange={e => setAbPrompt(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && e.ctrlKey && abGenerate()}
                            onPaste={pasteImageTo(setAbDesignImage)}
                            placeholder={abDesignImage ? "Rasmga asoslanib yangilash..." : "Masalan: ko'k gradient fon... (rasm uchun Ctrl+V)"}
                            rows={3}
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                          />

                          <div className="flex gap-2">
                            {/* Image upload */}
                            <label className="cursor-pointer flex items-center justify-center w-9 h-9 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition shrink-0" title="Namuna rasm yuklash">
                              <span>🖼</span>
                              <input type="file" accept="image/*" className="hidden"
                                onChange={e => {
                                  const f = e.target.files?.[0];
                                  if (!f) return;
                                  const r = new FileReader();
                                  r.onload = ev => setAbDesignImage({ base64: ev.target.result.split(',')[1], name: f.name });
                                  r.readAsDataURL(f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            <button
                              onClick={abGenerate}
                              disabled={!abPrompt.trim() || abGenerating}
                              className="flex-1 py-2 bg-primary-700 dark:bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 transition"
                            >
                              {abGenerating ? '⏳ Generatsiya...' : '✨ Yangilash (Ctrl+Enter)'}
                            </button>
                          </div>
                        </div>

                        {/* Version history */}
                        {abVersions.length > 0 && (
                          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">🕐 Versiyalar</p>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {abVersions.map(v => (
                                <button key={v.id} onClick={() => abRevert(v.id)}
                                  className="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-primary-50 dark:hover:bg-primary-900/40 transition flex justify-between items-center">
                                  <span className="font-semibold text-primary-700 dark:text-primary-500">v{v.version_number}</span>
                                  <span className="text-gray-400 truncate ml-2 flex-1">{v.prompt}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => abDeletePage(abSelectedPageId)}
                          className="w-full py-2 border border-red-300 dark:border-red-700 text-red-500 rounded-lg text-sm hover:bg-red-50 dark:hover:bg-red-900/40 transition"
                        >
                          🗑 Sahifani o'chirish
                        </button>
                      </>
                    ) : null}
                  </div>
                  </div>
                </div>
              )}

              {/* ===== Solishtirish view: design ↔ real, auto-paired by route ===== */}
              {abWorkspace && studioView === 'pages' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  {(() => {
                    const selMod = selectedProjectModules.find(m => String(m.id) === String(studioModuleId));
                    if (selMod && selMod.module_type === 'backend') {
                      return <div className="text-center py-16 text-gray-400">⚙️ Backend modulida UI sahifalar yo'q.</div>;
                    }
                    const matchedRefs = new Set();
                    const pairs = visibleAbPages.map(d => {
                      const real = matchRealPage(d);
                      if (real) matchedRefs.add(real.relPath);
                      return { d, real };
                    });
                    const orphanReals = pagesList.filter(r => !matchedRefs.has(r.relPath));
                    const pairedCount = pairs.filter(p => p.real).length;
                    const isMobileSel = selMod ? selMod.module_type === 'mobile' : true;
                    return (
                      <>
                        {/* Toolbar */}
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          <button onClick={() => fetchPages(abProjectId, studioModuleId)} disabled={pagesLoading}
                            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition">
                            {pagesLoading ? '⏳ O\'qilmoqda...' : '↻ Haqiqiy sahifalarni o\'qish'}
                          </button>
                          {!isGenerating && (
                            <button onClick={handleGenerateClick}
                              className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 transition">
                              ✨ Screenshot generatsiya
                            </button>
                          )}
                          {visibleAbPages.length > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{pairedCount}/{visibleAbPages.length} sahifa juftlandi</span>
                          )}
                        </div>

                        {isGenerating && (
                          <div className="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-4 flex items-center gap-3">
                            <svg className="animate-spin h-5 w-5 text-primary-700 dark:text-primary-500 shrink-0" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            <p className="text-sm text-primary-800 dark:text-primary-300">{generateProgress || 'Screenshotlar olinmoqda...'}</p>
                          </div>
                        )}
                        {pagesError && <p className="text-red-500 text-sm mb-3">{pagesError}</p>}

                        {visibleAbPages.length === 0 && (
                          <div className="text-center py-16 text-gray-400">
                            <p className="text-3xl mb-2">📄</p>
                            <p className="text-sm">{studioModuleId ? 'Bu modulda dizayn sahifalari yo\'q' : 'Dizayn sahifalari yo\'q — Dizayn ko\'rinishida yarating'}</p>
                          </div>
                        )}

                        {/* Compare cards */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                          {pairs.map(({ d, real }) => (
                            <div key={d.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-3 gap-2">
                                <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" title={d.title}>{d.title}</p>
                                <span className="text-xs font-mono text-gray-400 truncate shrink-0">{d.route}</span>
                              </div>
                              <div className="flex gap-3">
                                {/* Design */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] uppercase font-semibold text-primary-600 dark:text-primary-500 mb-1 tracking-wide">🎨 Dizayn</p>
                                  <div className="border border-gray-100 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 overflow-hidden" style={{ height: 300 }}>
                                    {d.svg_content ? (
                                      <div className="w-full h-full overflow-hidden" dangerouslySetInnerHTML={{ __html: d.svg_content.replace(/<svg/, '<svg style="width:100%;height:100%;object-fit:contain;display:block"') }} />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Preview yo'q</div>
                                    )}
                                  </div>
                                </div>
                                {/* Real */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] uppercase font-semibold text-green-600 dark:text-green-400 mb-1 tracking-wide">{(real?._moduleType || selectedProjectModules.find(m => String(m.id) === String(d.module_id))?.module_type) === 'web' ? '🌐' : '📱'} Haqiqiy</p>
                                  <div
                                    className="border border-gray-100 dark:border-gray-700 rounded-lg bg-gray-900 overflow-hidden flex items-center justify-center cursor-pointer"
                                    style={{ height: 300 }}
                                    onClick={() => real && screenshotUrlFor(real) && setLightboxSrc(screenshotUrlFor(real))}
                                  >
                                    {real ? (
                                      <PageScreenshot src={screenshotUrlFor(real)} alt={real.name} />
                                    ) : (
                                      <div className="text-gray-500 dark:text-gray-400 text-xs text-center p-4">Mos haqiqiy sahifa topilmadi</div>
                                    )}
                                  </div>
                                  {/* Manual override */}
                                  <select
                                    value={d.real_page_ref || ''}
                                    onChange={e => {
                                      const ref = e.target.value || null;
                                      setAbPages(prev => prev.map(p => p.id === d.id ? { ...p, real_page_ref: ref } : p));
                                      authFetch(`${process.env.REACT_APP_API_URL}/app-builder/pages/${d.id}`, {
                                        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ real_page_ref: ref }),
                                      });
                                    }}
                                    className="w-full text-xs mt-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    title="Haqiqiy sahifani qo'lda biriktirish"
                                  >
                                    <option value="">🔗 Avto{real && !d.real_page_ref ? ` (${real.name})` : ''}</option>
                                    {pagesList.filter(r => r._moduleId == null || String(r._moduleId) === String(d.module_id)).map(r => (
                                      <option key={r.relPath} value={r.relPath}>{r.name} — {r.route}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Orphan real pages */}
                        {orphanReals.length > 0 && (
                          <div className="mt-6">
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Dizaynsiz haqiqiy sahifalar ({orphanReals.length})</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                              {orphanReals.map(r => (
                                <div key={r.relPath} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                                  <div className="bg-gray-900 rounded overflow-hidden mb-1 flex items-center justify-center" style={{ height: 150 }}>
                                    <PageScreenshot src={screenshotUrlFor(r)} alt={r.name} />
                                  </div>
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate" title={r.name}>{r.name}</p>
                                  <p className="text-[10px] font-mono text-gray-400 truncate">{r.route}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ===== Emulator view: test the real app inline (mobile module) ===== */}
              {abWorkspace && studioView === 'emulator' && (
                <div className="flex-1 min-h-0 overflow-auto pt-2">
                  <div className="flex justify-center">
                    {renderEmulatorGrid(null)}
                  </div>
                  {(serverRunning || serverLogs.length > 0) && (
                    <div className="max-w-3xl mx-auto mt-4">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">🔨 Build loglari {serverRunning && '(davom etmoqda...)'}</p>
                      <div className="bg-gray-900 rounded-lg p-3 max-h-56 overflow-y-auto">
                        {serverLogs.length === 0 ? (
                          <div className="text-xs font-mono text-gray-400">Build boshlanmoqda...</div>
                        ) : (
                          serverLogs.map((l, i) => (
                            <div key={i} className={`text-xs font-mono whitespace-pre-wrap ${l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-green-400' : 'text-gray-300'}`}>{l.log}</div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== Publish view (placeholder) ===== */}
              {/* 🌐 Brauzer — jonli test saytni Studio ichida ko'rish, o'zingiz sinab,
                  ochiq sahifa kontekstida to'g'ridan-to'g'ri vazifa berish */}
              {abWorkspace && studioView === 'browser' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  {browserModule?.test_url ? (
                    <div>
                      {/* Brauzer toolbar */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm px-3 py-2">
                        {/* Loyihada bir nechta web modul bo'lsa (frontend + website) —
                            qaysi birini ko'rish tanlanadi; bitta bo'lsa oddiy matn. */}
                        {browserWebModules.length > 1 ? (
                          <select
                            value={String(browserModule.id)}
                            onChange={e => {
                              setBrowserModuleId(e.target.value);
                              setBrowserPath('/');
                              setBrowserFrameUrl('');
                            }}
                            title="Qaysi saytni ko'rish"
                            className="text-xs font-mono shrink-0 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-600 cursor-pointer">
                            {browserWebModules.map(m => (
                              <option key={m.id} value={String(m.id)}>
                                🌐 {m.name} — {m.test_url.replace(/^https?:\/\//, '')}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-400 font-mono shrink-0" title="Test muhiti bazaviy URL">
                            🌐 {browserModule.test_url.replace(/^https?:\/\//, '')}
                          </span>
                        )}
                        <input
                          value={browserPath}
                          onChange={e => setBrowserPath(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') openBrowserPath(); }}
                          placeholder="/login.html"
                          className="flex-1 min-w-[160px] border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button onClick={() => openBrowserPath()} className="px-3 py-1.5 text-sm bg-primary-700 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 transition">
                          Ochish
                        </button>
                        <button onClick={reloadBrowserFrame} title="Sahifani yangilash (keshni chetlab o'tadi)" className="px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                          ↻
                        </button>
                        <a href={browserFrameUrl || browserModule.test_url} target="_blank" rel="noreferrer" title="Yangi oynada ochish" className="px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                          ⧉
                        </a>
                        {/* 📄 Sahifalar — ixcham dropdown (eski qator-qator chiplar o'rniga) */}
                        {(() => {
                          const seenRoutes = new Set();
                          const bPages = pagesList
                            .filter(p => {
                              if (String(p._moduleId) !== String(browserModule.id) || !p.route) return false;
                              if (seenRoutes.has(p.route)) return false;
                              seenRoutes.add(p.route);
                              return true;
                            })
                            .sort((a, b) => a.route.localeCompare(b.route));
                          if (bPages.length === 0) return null;
                          const q = browserPagesQuery.trim().toLowerCase();
                          const shown = q ? bPages.filter(p => (p.route + ' ' + (p.name || '')).toLowerCase().includes(q)) : bPages;
                          return (
                            <div className="relative shrink-0">
                              <button
                                onClick={() => { setBrowserPagesOpen(o => !o); setBrowserPagesQuery(''); }}
                                title="Sahifani ro'yxatdan tanlab ochish"
                                className={`px-3 py-1.5 text-sm rounded-lg border transition ${browserPagesOpen ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/40'}`}
                              >
                                📄 Sahifalar ({bPages.length}) ▾
                              </button>
                              {browserPagesOpen && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setBrowserPagesOpen(false)} />
                                  <div className="absolute right-0 top-full mt-1.5 z-20 w-[26rem] max-w-[90vw] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2.5">
                                    {bPages.length > 8 && (
                                      <input
                                        autoFocus
                                        value={browserPagesQuery}
                                        onChange={e => setBrowserPagesQuery(e.target.value)}
                                        placeholder="Qidirish..."
                                        className="w-full mb-2 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary-300"
                                      />
                                    )}
                                    <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
                                      {shown.map(p => (
                                        <button
                                          key={`${p._moduleId}:${p.name}`}
                                          onClick={() => { setBrowserPath(p.route); openBrowserPath(p.route); setBrowserPagesOpen(false); }}
                                          className={`text-xs px-2 py-1 rounded-full border transition font-mono ${browserFrameUrl.endsWith(p.route) ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/40'}`}
                                        >
                                          {p.route}
                                        </button>
                                      ))}
                                      {shown.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">Mos sahifa topilmadi</p>}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                        <button
                          onClick={taskFromBrowserPage}
                          title="Hozir ochiq sahifa kontekstida vazifa yozish (sahifa URL'i tavsifga qo'shiladi)"
                          className="px-3 py-1.5 text-sm bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg font-semibold hover:opacity-90 transition"
                        >
                          🛠 Shu sahifadan vazifa
                        </button>
                      </div>

                      {(() => {
                        const wa = webAgent[browserModule?.id] || {};
                        if (!wa.active) {
                          return (
                            <iframe
                              key={browserFrameKey}
                              src={bustUrl(browserFrameUrl)}
                              title="Test sayt"
                              className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                              style={{ height: '72vh' }}
                            />
                          );
                        }
                        return (
                          <div>
                            <div className={`flex items-center justify-between gap-2 flex-wrap rounded-t-2xl px-3 py-2 border border-b-0 ${wa.paused ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800' : 'bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800'}`}>
                              <span className={`text-sm font-semibold ${wa.paused ? 'text-amber-700 dark:text-amber-400' : 'text-sky-700 dark:text-sky-400'}`}>
                                {wa.paused ? '⏸ Agent to\'xtatilgan — boshqaruv sizda' : '🤖 Agent brauzerda sinayapti'}
                                {khAgentWho(wa) ? <span className="block text-xs font-normal opacity-80" translate="no">{khAgentWho(wa)}</span> : null}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => setWebAgentView(v => !v)}
                                  className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                                  {webAgentView ? '🖥 Mening ko\'rinishim' : '🤖 Agent ekrani'}
                                </button>
                                <button onClick={() => webAgentControl(browserModule.id, wa.paused ? 'resume' : 'pause')}
                                  className={`px-2.5 py-1 text-xs rounded-lg font-semibold text-white ${wa.paused ? 'bg-sky-600 hover:bg-sky-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                                  {wa.paused ? '▶ Agentga qaytarish' : '✋ Boshqaruvni olish'}
                                </button>
                              </div>
                            </div>
                            {webAgentView && wa.frame ? (
                              <img src={`${process.env.REACT_APP_API_URL}${wa.frame}`} alt="agent ekrani"
                                className="w-full bg-white dark:bg-gray-800 rounded-b-2xl border border-gray-200 dark:border-gray-700 object-contain"
                                style={{ height: '72vh' }} />
                            ) : webAgentView ? (
                              <div className="w-full bg-white dark:bg-gray-800 rounded-b-2xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center gap-2 px-6 text-center" style={{ height: '72vh' }}>
                                <div className="text-3xl">🖥</div>
                                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Agent brauzerga hali o'tmadi</div>
                                <div className="text-gray-400 text-xs max-w-md leading-relaxed">
                                  Ekran tasviri agent brauzerda birinchi amalni bajarganda paydo bo'ladi.
                                  Hozir u boshqa nishonda (📱 emulator) yoki kod/baza bilan ishlayotgan bo'lishi mumkin —
                                  bu normal, sinov ketyapti.
                                </div>
                                {wa.last && wa.last.action && (
                                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                    oxirgi amal: <span className="font-medium">{wa.last.action}</span>
                                    {wa.last.detail ? ` — ${String(wa.last.detail).slice(0, 70)}` : ''}
                                  </div>
                                )}
                                <button onClick={() => setWebAgentView(false)}
                                  className="mt-1 px-2.5 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                                  🖥 Mening ko'rinishimga qaytish
                                </button>
                              </div>
                            ) : (
                              <iframe key={browserFrameKey} src={bustUrl(browserFrameUrl)} title="Test sayt"
                                className="w-full bg-white dark:bg-gray-800 rounded-b-2xl border border-gray-200 dark:border-gray-700" style={{ height: '72vh' }} />
                            )}
                            <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                              {(webAgentLog[browserModule?.id] || []).slice(-3).map((l, i) => (
                                <div key={i} className="truncate">· {l.action}{l.detail ? ` — ${String(l.detail).slice(0, 80)}` : ''}</div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-gray-400 mt-2">
                        💡 Saytni shu yerda o'zingiz sinang. "🛠 Shu sahifadan vazifa" — URL satridagi sahifa kontekstida vazifa ochadi.
                        Iframe ichida boshqa sahifaga o'tsangiz, URL satri o'zgarmaydi (brauzer xavfsizligi) — vazifa berishdan oldin yo'lni tekshiring.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-24 text-gray-400 select-none">
                      <p className="text-5xl mb-3">🌐</p>
                      <p className="text-lg">Bu loyihada test muhiti sozlangan web modul yo'q.</p>
                      <p className="text-sm mt-1">Proyektlar → web modul → "Test muhiti sozlash" dan keyin bu yerda jonli sayt ko'rinadi.</p>
                    </div>
                  )}
                </div>
              )}

              {abWorkspace && studioView === 'publish' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <PublishPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId}
                    canOpenConsole={canSeeConsole} onOpenConsole={openConsoleAt} />
                </div>
              )}
              {/* 🔑 Sirlar — sir MODUL bo'yicha saqlanadi, shuning uchun yuqoridan
                  modul tanlangan bo'lishi kerak. Admin ham, loyiha a'zosi ham ko'radi
                  (backend a'zolikni tekshiradi). */}
              {abWorkspace && studioView === 'secrets' && (
                <div className="flex-1 min-h-0 overflow-auto p-4">
                  {selectedStudioModule ? (
                    <div className="max-w-2xl">
                      <SecretsCard
                        moduleId={selectedStudioModule.id}
                        authFetch={authFetch}
                        apiUrl={process.env.REACT_APP_API_URL}
                        serverRunning={false}
                        hasProd={!!(selectedStudioModule.prod_remote_host && selectedStudioModule.prod_deploy_path)}
                      />
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Yuqoridagi modul tablaridan birini tanlang — sirlar <b>modul bo'yicha</b> saqlanadi
                      (masalan backend uchun <code>ESKIZ_PASSWORD</code>).
                    </div>
                  )}
                </div>
              )}

              {abWorkspace && studioView === 'team' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <TeamPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} />
                </div>
              )}

              {abWorkspace && studioView === 'todo' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <TodoPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId}
                    onConsole={canSeeConsole ? (card) => {
                      // Reja kartasi → 🔐 Konsollar: profilni matndan taxmin qilamiz,
                      // topilmasa operator o'zi tanlaydi (vazifa matni baribir to'ldiriladi).
                      const txt = ((card.title || '') + ' ' + (card.detail || '')).toLowerCase();
                      const profile = guessConsoleProfile(txt);
                      setConsoleDeepLink({
                        profile, nonce: Date.now(), projectId: abProjectId,
                        // 🔗 Karta ID — konsolda ish tugagach natija AYNAN shu kartaga
                        // bog'lanadi (aks holda ish alohida qolib, rejada iz qoldirmasdi).
                        todoId: card.id, todoTitle: card.title,
                        task: card.detail ? card.title + '\n\n' + card.detail : card.title,
                      });
                      setActiveTab('console');
                    } : null} />
                </div>
              )}

              {abWorkspace && studioView === 'crashes' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <CrashesPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} onActiveCount={setCrashActive} readOnly={isProjClient || myProjRole === 'viewer' || uiMode('studio.crashes') === 'view'} />
                </div>
              )}

              {/* ===== 🧪 Sinov: agent sinovi + topilmalar BIR joyda (2026-09-13). Ilgari sinov qatori
                  Emulator/Brauzer ko'rinishlari ichida yotardi, Topilmalar alohida tab, va eski
                  Video-QA «Test» tabi ham bor edi — operator: «Test kerak emas, sinovni alohida tab
                  qil, topilmalar faqat sinovdan keladi — birlashtir». Web-only loyihada ham ishlaydi
                  (mobileModuleId=null → emulator chiplari yo'q, nishon = web modul). ===== */}
              {abWorkspace && studioView === 'qa' && (() => {
                const mobileMod = selectedProjectModules.find(m => isMobileModule(m)) || null;
                const webMods = selectedProjectModules.filter(m => isWebModule(m) && m.test_url);
                const anchorId = mobileMod ? mobileMod.id : (webMods[0] ? webMods[0].id : null);
                return (
                  <div className="flex-1 min-h-0 overflow-auto pt-2">
                    {(isProjClient || uiMode('studio.qa') === 'view') ? null : anchorId ? (
                      <AgentQaBar authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL}
                        moduleId={anchorId} mobileModuleId={mobileMod ? mobileMod.id : null}
                        surface={mobileMod ? 'mobile' : 'web'} emulators={mobileMod ? emuList : []}
                        webModules={webMods} defaultWebModuleId={mobileMod ? undefined : (webMods[0] || {}).id} wide />
                    ) : (
                      <div className="mb-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Sinash uchun nishon yo'q — loyihada mobil modul yoki test manzili bor web modul bo'lishi kerak.
                      </div>
                    )}
                    <FindingsPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} readOnly={isProjClient || myProjRole === 'viewer' || uiMode('studio.qa') === 'view'} />
                  </div>
                );
              })()}


              {/* ===== 🛡️ Xavfsizlik: loyiha manzillari → Security skaneri ===== */}
              {abWorkspace && studioView === 'security' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <SecurityPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} />
                </div>
              )}

              {/* ===== 📁 Fayllar: read-only file browser for a module's workspace ===== */}
              {abWorkspace && studioView === 'files' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <FileBrowserPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} modules={selectedProjectModules} defaultModuleId={studioModuleId} />
                </div>
              )}

              {/* ===== 📦 Holat: har bir modul qaysi task/branch'da + mobil APK versiyasi ===== */}
              {abWorkspace && studioView === 'status' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <DeployStatusPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} />
                </div>
              )}

              {abWorkspace && activeTab === 'studio' && studioView === 'map' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <ProjectMapPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} toast={toast} role={uiMode('studio.map') === 'view' ? 'viewer' : myProjRole}
                    onAskAgent={uiShow('nav.processes') && uiShow('nav.agent') && !['viewer', 'reviewer', 'client'].includes(myProjRole) ? (d) => setAgentDraftExt({ ...d, at: Date.now() }) : null} />
                </div>
              )}

              {abWorkspace && studioView === 'architecture' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <ArchitecturePanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} />
                </div>
              )}

              {abWorkspace && activeTab === 'design' && studioView === 'harmony' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <DesignAuditPanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId}
                    onAskAgent={uiShow('nav.processes') && uiShow('nav.agent') && !['viewer', 'reviewer', 'client'].includes(myProjRole) ? (d) => setAgentDraftExt({ ...d, at: Date.now() }) : null} />
                </div>
              )}

              {abWorkspace && activeTab === 'design' && studioView === 'style' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <DesignStylePanel authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} />
                </div>
              )}

              {/* 🏷 Logo — Dizayn ichidagi tab (2026-10-04, avval alohida «Logo dizayn» sidebar bo'limi edi).
                  Loyiha navbar'dagi tanlovdan (abProjectId → logoProjectId effekti). */}
              {abWorkspace && activeTab === 'design' && studioView === 'logo' && (
                <div className="flex-1 min-h-0 overflow-auto">

              {!logoProjectId ? (
                <div className="text-center py-24 text-gray-400 select-none">
                  <p className="text-6xl mb-4">🎨</p>
                  <p className="text-lg">Logo yaratish uchun avval proyekt tanlang</p>
                </div>
              ) : (
                <>
                  {/* Generate form */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-5">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">✨ Yangi logo yaratish</p>
                    <textarea
                      value={logoPrompt}
                      onChange={e => setLogoPrompt(e.target.value)}
                      onPaste={pasteImageTo(setLogoImage)}
                      rows={2}
                      placeholder="Logo tavsifini yozing... (masalan: zamonaviy minimal logo, ko'k-binafsha ranglar, K harfi belgisi). Namuna rasm uchun Ctrl+V"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
                    />

                    {/* Namuna rasm (vision) — "shunga o'xshash" logo uchun */}
                    {logoImage ? (
                      <div className="flex items-center gap-2 mt-2 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg p-2">
                        <img src={`data:image/*;base64,${logoImage.base64}`} alt="namuna" className="w-12 h-12 object-cover rounded-lg border border-primary-200 dark:border-primary-800" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-primary-800 dark:text-primary-300 truncate">🖼 Namuna: {logoImage.name}</p>
                          <p className="text-[10px] text-gray-400">AI shunga o'xshash uslubda logo chizadi</p>
                        </div>
                        <button onClick={() => setLogoImage(null)} className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 text-lg leading-none px-1">×</button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-primary-700 dark:hover:text-primary-300">
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-700">🖼 Namuna rasm</span>
                          <span className="text-gray-400">(ixtiyoriy — shunga o'xshash logo uchun)</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = ev => setLogoImage({ base64: ev.target.result.split(',')[1], name: file.name });
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <select
                        value={logoCount}
                        onChange={e => setLogoCount(Number(e.target.value))}
                        title="Nechta variant yaratilsin"
                        className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-2 text-sm bg-white dark:bg-gray-800 focus:outline-none"
                      >
                        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} ta variant</option>)}
                      </select>
                      <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none" title="Logoni orqa fonsiz (shaffof) yasaydi — yuklab olganda PNG shaffof bo'ladi">
                        <input type="checkbox" checked={logoTransparent} onChange={e => setLogoTransparent(e.target.checked)} className="w-4 h-4 accent-primary-700" />
                        🔲 Shaffof fon
                      </label>
                      <label
                        className={`flex items-center gap-1.5 text-sm select-none ${logoImage ? 'text-gray-600 dark:text-gray-400 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
                        title={logoImage ? "Namuna rasmni AYNAN nusxalaydi (rasterni SVG ichiga joylaydi) — AI qayta chizmaydi, bir zumda va tokensiz" : "Avval 🖼 namuna rasm yuklang — keyin birga-bir nusxalash mumkin"}
                      >
                        <input type="checkbox" checked={logoExact && !!logoImage} disabled={!logoImage} onChange={e => setLogoExact(e.target.checked)} className="w-4 h-4 accent-primary-700" />
                        🎯 Birga-bir ko'chirish
                      </label>
                      <button
                        onClick={handleGenerateLogo}
                        disabled={logoGenerating || (logoExact && logoImage ? false : !logoPrompt.trim())}
                        className="px-4 py-2 bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition"
                      >
                        {logoGenerating ? '⏳ Yaratilmoqda...' : (logoExact && logoImage ? '📋 Birga-bir nusxa olish' : '✨ Logo yaratish')}
                      </button>
                      {logoGenerating && (
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{logoProgress || 'AI logo chizmoqda'} (~2 daqiqa/variant — sahifani yopsangiz ham davom etadi)</span>
                          {logoJobId && (
                            <button
                              onClick={() => cancelBgJob(logoJobId)}
                              className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-800/70 transition shrink-0"
                            >
                              ⏹ To'xtatish
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <label
                      title="Dizaynerdan kelgan tayyor logo (PNG/JPG/WebP/SVG) — AI qayta chizmaydi, aynan o'zi saqlanadi"
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${logoUploading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                    >
                      {logoUploading ? '⏳ Yuklanmoqda...' : '📤 Tayyor logoni yuklash'}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg" className="hidden" disabled={logoUploading}
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; handleUploadLogoFile(f); }} />
                    </label>
                    <span className="text-xs text-gray-400">⭐ <b>Asosiy logo</b> — 🎨 Dizayn → «🏷 Logoni joylash» sahifalarga shu logoni qo'yadi, yangi dizaynlar ham uni o'zi ishlatadi.</span>
                  </div>

                  {/* Logo grid */}
                  {logos.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 select-none">
                      <p className="text-4xl mb-3">🖼</p>
                      <p>Bu proyektda hali logo yo'q — yuqorida tavsif yozib yarating</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {logos.map(l => (
                        <div key={l.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-3 group relative">
                          <div
                            onClick={() => setLogoLightbox(l)}
                            title="Kattalashtirib ko'rish"
                            className="aspect-square bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center cursor-zoom-in"
                            dangerouslySetInnerHTML={{ __html: (l.svg_content || '').replace(/<svg/, '<svg style="width:100%;height:100%"') }}
                          />
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-2 truncate" title={l.name}>{l.name}</p>
                          <p className="text-[10px] text-gray-400 truncate" title={l.prompt}>{l.prompt}</p>
                          {l.is_primary ? (
                            <div title="Dizayn → 🏷 Logoni joylash va yangi dizaynlar shu logoni ishlatadi"
                              className="mt-2 w-full px-2 py-1 text-xs font-semibold text-center text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                              ⭐ Asosiy logo
                            </div>
                          ) : (
                            <button
                              onClick={() => handleSetPrimaryLogo(l)}
                              title="Loyihaning asosiy logosi qilish — 🎨 Dizayn sahifalariga shu logo joylanadi"
                              className="mt-2 w-full px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-300 transition"
                            >
                              ⭐ Asosiy qilish
                            </button>
                          )}
                          <button
                            onClick={() => handleApplyLogo(l)}
                            disabled={applyingLogoId === l.id}
                            title="Logoni ilovaga joriy qilib, keyingi versiyani yasash uchun vazifa yaratadi"
                            className="mt-2 w-full px-2 py-1.5 text-xs font-semibold bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition"
                          >
                            {applyingLogoId === l.id ? '⏳ Biriktirilmoqda...' : '📲 Ilovaga biriktirish'}
                          </button>
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button
                              onClick={() => handleDownloadLogo(l)}
                              title="PNG yuklab olish"
                              className="bg-white/90 border border-gray-200 dark:border-gray-700 rounded-md w-7 h-7 flex items-center justify-center text-sm hover:bg-primary-50 dark:hover:bg-primary-900/40"
                            >⬇</button>
                            <button
                              onClick={() => handleDeleteLogo(l.id)}
                              title="O'chirish"
                              className="bg-white/90 border border-gray-200 dark:border-gray-700 rounded-md w-7 h-7 flex items-center justify-center text-sm hover:bg-red-50 dark:hover:bg-red-900/40"
                            >🗑</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-4">💡 <b>📲 Ilovaga biriktirish</b> — logoni to'g'ridan-to'g'ri ilovaga joriy qiladi va uning shu logo bilan keyingi versiyasini yasash uchun vazifa yaratadi (⏳ Jarayonlar sahifasida kuzating). Yoki Studio → "Vazifa berish" formasidagi 🏷️ Logo biriktirish orqali ham vazifaga qo'shsa bo'ladi.</p>

                  {/* Lightbox — logo ustiga bosilganda kattalashtirib ko'rsatadi */}
                  {logoLightbox && (
                    <div
                      onClick={() => setLogoLightbox(null)}
                      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
                    >
                      <div
                        onClick={e => e.stopPropagation()}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-5 max-w-2xl w-full max-h-[90vh] flex flex-col"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-white truncate" title={logoLightbox.name}>{logoLightbox.name}</p>
                            <p className="text-xs text-gray-400 truncate" title={logoLightbox.prompt}>{logoLightbox.prompt}</p>
                          </div>
                          <button onClick={() => setLogoLightbox(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold leading-none shrink-0">×</button>
                        </div>
                        <div
                          className="flex-1 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center p-4 min-h-0"
                          dangerouslySetInnerHTML={{ __html: (logoLightbox.svg_content || '').replace(/<svg/, '<svg style="width:100%;height:100%;max-height:70vh"') }}
                        />
                        <div className="flex gap-2 justify-end mt-4 flex-wrap">
                          <button
                            onClick={() => handleApplyLogo(logoLightbox)}
                            disabled={applyingLogoId === logoLightbox.id}
                            title="Logoni ilovaga joriy qilib, keyingi versiyani yasash uchun vazifa yaratadi"
                            className="px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition mr-auto"
                          >{applyingLogoId === logoLightbox.id ? '⏳ Biriktirilmoqda...' : '📲 Ilovaga biriktirish'}</button>
                          <button
                            onClick={() => handleDownloadLogo(logoLightbox)}
                            className="px-3 py-1.5 text-sm bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/60 transition"
                          >⬇ PNG yuklab olish</button>
                          <button
                            onClick={() => { handleDeleteLogo(logoLightbox.id); setLogoLightbox(null); }}
                            className="px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 transition"
                          >🗑 O'chirish</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
                </div>
              )}

              {abWorkspace && studioView === 'docs' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <DocsPanel readOnly={isProjClient || myProjRole === 'viewer' || uiMode('studio.docs') === 'view'} authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} projectId={abProjectId} projectName={(projects.find(p => String(p.id) === String(abProjectId)) || {}).name} />
                </div>
              )}



              {/* 📜 Engine loglari — navbar'dagi tugmada (EngineLogFlyout, 2026-11-04); pastki panel olib tashlandi. */}
            </div>
          )}

        </div>
        </main>
      </div>

      {/* Modules Modal */}
      {isModuleModalOpen && selectedProjectForModules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-700 rounded-t-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                "{selectedProjectForModules.name}" modullari
              </h2>
              <button 
                onClick={closeModulesModal}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl font-bold"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6 flex flex-col gap-3">
                <h3 className="font-semibold text-blue-900 dark:text-blue-200">Yangi modul qo'shish</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 border border-blue-300 dark:border-blue-700 rounded p-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Modul nomi (masalan: Frontend, Backend)"
                    value={newModuleName}
                    onChange={e => setNewModuleName(e.target.value)}
                  />
                  <select value={newModuleType} onChange={e => setNewModuleType(e.target.value)} className="border border-blue-300 dark:border-blue-700 rounded p-2 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="web">🌐 Web</option>
                    <option value="backend">⚙️ Backend</option>
                    <option value="mobile">📱 Mobile</option>
                  </select>
                  <button
                    onClick={handleAddModule}
                    disabled={!newModuleName.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    Qo'shish
                  </button>
                </div>
                {gitlabAdminTokenConfigured && (
                  <label className="flex items-center gap-2 text-xs text-blue-800 dark:text-blue-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoCreateGitlab}
                      onChange={e => setAutoCreateGitlab(e.target.checked)}
                      className="rounded"
                    />
                    <span>GitLab repo avtomat yaratilsin (admin tokeni orqali, private)</span>
                  </label>
                )}
              </div>

              {projectModules.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">Ushbu loyihada hozircha modullar yo'q.</p>
              ) : (
                <ul className="space-y-3">
                  {projectModules.map(m => (
                    <li key={m.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg shadow-sm">
                      {editingModuleId === m.id ? (
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            className="border border-gray-300 dark:border-gray-600 rounded p-2 focus:ring-2 focus:ring-primary-600 outline-none"
                            value={editModuleName}
                            onChange={e => setEditModuleName(e.target.value)}
                            placeholder="Modul nomi"
                          />
                          <select value={editModuleType} onChange={e => setEditModuleType(e.target.value)} className="border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-600 outline-none">
                            <option value="web">🌐 Web</option>
                            <option value="backend">⚙️ Backend</option>
                            <option value="mobile">📱 Mobile</option>
                          </select>
                          <div className="flex justify-end gap-2 mt-2">
                            <button 
                              onClick={() => setEditingModuleId(null)}
                              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                            >
                              Bekor qilish
                            </button>
                            <button 
                              onClick={() => handleUpdateModule(m.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                            >
                              Saqlash
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-gray-900 dark:text-white text-lg mb-1">{m.name} <ModId id={m.id} size="text-xs" /></div>
                            <div className="font-mono text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-1 px-2 rounded inline-block">{m.workspace_path}</div>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${isMobileModule(m) ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400' : isBackendModule(m) ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'}`}>
                              {modTypeLabel(m)}
                            </span>
                            {/* GitLab status row */}
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              {m.gitlab_connected ? (
                                <>
                                  <a
                                    href={m.gitlab_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-800/70"
                                    title={m.gitlab_url}
                                  >
                                    <span>🦊</span>
                                    <span className="font-mono">{m.gitlab_url?.replace(/^https?:\/\/(www\.)?gitlab\.com\//, '')}</span>
                                  </a>
                                  {m.gitlab_token_owner === 'admin' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400" title="Admin tomonidan boshqariladi — tokenni siz o'zgartira olmaysiz">
                                      🔒 admin-managed
                                    </span>
                                  )}
                                  {m.gitlab_token_owner === 'user' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400" title="Sizning shaxsiy tokeningiz ishlatilmoqda">
                                      shaxsiy
                                    </span>
                                  )}
                                </>
                              ) : (
                                <button
                                  onClick={() => handleCreateGitlabRepo(m.id)}
                                  className="text-xs px-2 py-0.5 rounded-full font-semibold bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/60"
                                  title="GitLab'da yangi private repo yaratish"
                                >
                                  + GitLab repo yarat
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <button
                              onClick={() => {
                                setEditingModuleId(m.id);
                                setEditModuleName(m.name);
                                setEditModulePath(m.workspace_path);
                                setEditModuleType(m.module_type || 'web');
                              }}
                              className="text-primary-700 dark:text-primary-500 hover:text-primary-900 dark:hover:text-primary-200 font-medium"
                            >
                              Tahrirlash
                            </button>
                            <button
                              onClick={() => handleDeleteModule(m.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
                            >
                              O'chirish
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Emulator Modal */}
      {/* DB table data viewer (Studio "eye" icon) — view / edit / multi-select delete */}
      {renderDbRowsModal()}

      {showEmulator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 overflow-auto" onClick={e => { if (e.target === e.currentTarget) { stopStream(); socketRef.current?.emit('emulator:stop_stream'); setEmuStreaming({}); setShowEmulator(false); } }}>
          {renderEmulatorGrid(() => { stopStream(); socketRef.current?.emit('emulator:stop_stream'); setEmuStreaming({}); setShowEmulator(false); })}
        </div>
      )}

      {/* Web Login Credentials Modal */}
      {showLoginCredModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Login ma'lumotlari</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Sahifalar autentifikatsiya talab qilsa kiriting. Bo'sh qoldirsangiz — tizimga kirmay screenshot olinadi.</p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Login / Email</label>
                <input
                  type="text"
                  value={webCredentials.username}
                  onChange={e => setWebCredentials(c => ({ ...c, username: e.target.value }))}
                  placeholder="admin@example.com"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Parol</label>
                <input
                  type="password"
                  value={webCredentials.password}
                  onChange={e => setWebCredentials(c => ({ ...c, password: e.target.value }))}
                  placeholder="••••••••"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onKeyDown={e => e.key === 'Enter' && generatePageScreenshots(webCredentials)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => generatePageScreenshots(webCredentials)}
                className="flex-1 bg-primary-700 dark:bg-primary-600 text-white font-semibold py-2 rounded-lg hover:bg-primary-800 dark:hover:bg-primary-700 transition"
              >
                ✨ Generatsiya qilish
              </button>
              <button
                onClick={() => generatePageScreenshots({ username: '', password: '' })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
              >
                Loginsiz
              </button>
              <button
                onClick={() => setShowLoginCredModal(false)}
                className="px-3 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}


      {/* AI Design Panel */}
      {showAiDesign && (
        <div className="fixed inset-0 z-50 flex bg-black bg-opacity-60" onClick={e => e.target === e.currentTarget && setShowAiDesign(false)}>
          <div className="ml-auto h-full w-full max-w-2xl bg-white dark:bg-gray-800 flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-900 text-white shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-purple-400 font-bold text-lg">🎨 AI Dizayn</span>
                {penpotUrl && (
                  <a href={penpotUrl} target="_blank" rel="noreferrer"
                    className="text-xs px-2 py-1 bg-purple-700 hover:bg-purple-600 rounded transition">
                    Penpot ↗
                  </a>
                )}
              </div>
              <button onClick={() => setShowAiDesign(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            {/* Chat history */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-gray-50 dark:bg-gray-900">
              {aiDesignHistory.length === 0 && !aiDesignLoading && (
                <div className="text-center text-gray-400 py-16">
                  <p className="text-5xl mb-3">🎨</p>
                  <p className="font-medium text-gray-600 dark:text-gray-400 mb-1">AI sizga dizayn qilib beradi</p>
                  <p className="text-sm">Masalan: "Login sahifasi dizayni qil" yoki "Ko'k rangdagi dashboard"</p>
                </div>
              )}

              {aiDesignLoading && (
                <div className="flex items-center gap-3 text-purple-600 dark:text-purple-400 py-4">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  <span className="font-medium">AI dizayn yaratmoqda...</span>
                </div>
              )}

              {aiDesignHistory.map((item, i) => (
                <div key={i} className="space-y-3">
                  {/* User prompt */}
                  <div className="flex justify-end gap-2 items-end">
                    {item.image && (
                      <img src={`data:image/*;base64,${item.image}`} alt="sample" className="w-12 h-12 object-cover rounded-lg border-2 border-purple-300 dark:border-purple-700 shrink-0" />
                    )}
                    <div className="bg-purple-600 text-white px-4 py-2 rounded-lg rounded-tr-sm max-w-xs text-sm">
                      {item.prompt}
                    </div>
                  </div>
                  {/* SVG result */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                    {item.svg ? (
                      <div className="overflow-auto max-h-96 p-2 bg-gray-50 dark:bg-gray-700"
                        dangerouslySetInnerHTML={{ __html: item.svg.replace(/<svg/, '<svg style="max-width:100%;height:auto"') }}
                      />
                    ) : (
                      <div className="p-4 text-gray-400 text-sm">SVG generatsiya qilinmadi</div>
                    )}
                    <div className="flex gap-2 p-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                      {item.penpotUrl && (
                        <a href={item.penpotUrl} target="_blank" rel="noreferrer"
                          className="flex-1 text-center text-xs py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium">
                          🎨 Penpot da ko'r
                        </a>
                      )}
                      <button
                        onClick={() => {
                          const blob = new Blob([item.svg], { type: 'image/svg+xml' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = 'design.svg'; a.click();
                        }}
                        className="flex-1 text-center text-xs py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        ⬇ SVG yuklab olish
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800 shrink-0">
              {/* Image preview */}
              {aiDesignImage && (
                <div className="flex items-center gap-2 mb-2 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg px-3 py-2">
                  <img src={`data:image/*;base64,${aiDesignImage.base64}`} alt="sample" className="w-10 h-10 object-cover rounded-lg border border-purple-200 dark:border-purple-800" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-purple-700 dark:text-purple-400 truncate">{aiDesignImage.name}</p>
                    <p className="text-xs text-purple-400">Namuna sifatida ishlatiladi</p>
                  </div>
                  <button onClick={() => setAiDesignImage(null)} className="text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 text-lg leading-none">×</button>
                </div>
              )}
              <div className="flex gap-2">
                {/* Image upload button */}
                <label className="shrink-0 cursor-pointer flex items-center justify-center w-10 h-10 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition" title="Namuna rasm yuklash">
                  <span className="text-lg">🖼</span>
                  <input type="file" accept="image/*" className="hidden" disabled={aiDesignLoading}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => {
                        const base64 = ev.target.result.split(',')[1];
                        setAiDesignImage({ base64, name: file.name });
                      };
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </label>
                <input
                  type="text"
                  value={aiDesignPrompt}
                  onChange={e => setAiDesignPrompt(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && requestAiDesign()}
                  onPaste={pasteImageTo(setAiDesignImage)}
                  placeholder={aiDesignImage ? 'Rasmga asoslanib dizayn qil...' : 'Login sahifasi, dashboard, profil ekrani... (rasm uchun Ctrl+V)'}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  disabled={aiDesignLoading}
                  autoFocus
                />
                <button
                  onClick={requestAiDesign}
                  disabled={!aiDesignPrompt.trim() || aiDesignLoading}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 transition text-sm"
                >
                  {aiDesignLoading ? '⏳' : '✨ Qil'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2 px-1">🖼 rasm yuklang yoki tasvirlab yozing → AI SVG yaratadi</p>
            </div>
          </div>
        </div>
      )}

      {/* TZ Modal */}
      {showTzModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">📋 TZ dan to'liq dizayn</h2>
                <p className="text-xs text-gray-400 mt-0.5">Texnik topshiriqni yozing yoki yuklang — AI barcha sahifalarni yaratadi</p>
              </div>
              <button onClick={() => !tzGenerating && setShowTzModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none">×</button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* File upload */}
              <div className="flex items-center gap-3">
                <label className="cursor-pointer px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-300 transition">
                  📎 Fayl yuklash (.txt, .md)
                  <input type="file" accept=".txt,.md,.text" className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = ev => setTzText(ev.target.result);
                      reader.readAsText(f);
                      e.target.value = '';
                    }} />
                </label>
                <span className="text-xs text-gray-400">yoki quyida yozing</span>
              </div>

              {/* TZ textarea */}
              <textarea
                value={tzText}
                onChange={e => setTzText(e.target.value)}
                placeholder={`Misol:\n\nIlova: Online do'kon\nPlatforma: Mobile (iOS/Android)\n\nSahifalar:\n1. Splash / Kirish ekrani\n2. Ro'yxatdan o'tish va Login\n3. Bosh sahifa - mahsulotlar katalogi\n4. Mahsulot tafsilotlari\n5. Savatcha\n6. Buyurtma berish\n7. Profil\n\nRanglar: Ko'k (#2563EB) va oq\nDizayn uslubi: Minimal, zamonaviy`}
                rows={14}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                disabled={tzGenerating}
              />

              {/* Progress */}
              {tzGenerating && (
                <div className="flex items-center gap-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg px-4 py-3">
                  <svg className="animate-spin h-5 w-5 text-primary-700 dark:text-primary-500 shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-primary-900 dark:text-primary-200">{tzProgress || 'Generatsiya qilinmoqda...'}</p>
                    <p className="text-xs text-primary-600 dark:text-primary-500">Bu 1-3 daqiqa vaqt olishi mumkin</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex gap-3 shrink-0">
              <button
                onClick={abGenerateFromTz}
                disabled={!tzText.trim() || tzGenerating}
                className="flex-1 py-3 bg-gradient-to-r from-primary-700 to-primary-700 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition text-sm"
              >
                {tzGenerating ? '⏳ Yaratilmoqda...' : '✨ To\'liq dizayn yaratish'}
              </button>
              <button
                onClick={() => !tzGenerating && setShowTzModal(false)}
                disabled={tzGenerating}
                className="px-5 py-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              >
                Bekor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300"
          >×</button>
          <img
            src={lightboxSrc}
            alt="screenshot"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Design SVG preview (eye button on canvas nodes) */}
      {svgPreview && (() => {
        // Pick modal width from the SVG's own aspect ratio: landscape (web/desktop)
        // designs get a wide modal, portrait (mobile) designs stay narrow.
        const vb = (svgPreview.svg_content || '').match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
        const w = vb ? parseFloat(vb[1]) : 0;
        const h = vb ? parseFloat(vb[2]) : 0;
        const isLandscape = w > 0 && h > 0 && w / h > 1.2;
        return (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-70 flex items-center justify-center p-6"
          onClick={() => setSvgPreview(null)}
        >
          <div
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-h-[92vh] flex flex-col overflow-hidden ${isLandscape ? 'max-w-5xl' : 'max-w-md'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 dark:text-white truncate">{svgPreview.title}</p>
                <p className="text-xs font-mono text-gray-400 truncate">{svgPreview.route}</p>
              </div>
              <button
                onClick={() => setSvgPreview(null)}
                className="ml-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold leading-none shrink-0"
              >×</button>
            </div>
            <div className="overflow-auto p-4 bg-gray-50 dark:bg-gray-700 flex justify-center">
              <div className="w-full" dangerouslySetInnerHTML={{ __html: (svgPreview.svg_content || '').replace(/<svg/, '<svg style="width:100%;height:auto;display:block"') }} />
            </div>
          </div>
        </div>
        );
      })()}

      {playbookRunId && (
        <PlaybookModal authFetch={authFetch} apiUrl={process.env.REACT_APP_API_URL} runId={playbookRunId}
          onClose={() => {
            setPlaybookRunId(null);
            try { const u = new URL(window.location.href); if (u.searchParams.has('playbook')) { u.searchParams.delete('playbook'); window.history.replaceState({}, '', u.toString()); } } catch (e) { }
          }}
          onOpenConsole={canSeeConsole ? () => { setPlaybookRunId(null); setActiveTab('console'); } : null}
          onOpenTodo={(pid) => { setPlaybookRunId(null); if (pid) selectStudioProject(pid); setActiveTab('studio'); setStudioView('todo'); }} />
      )}
      <Toaster position="top-right" toastOptions={{ className: "dark:!bg-gray-700 dark:!text-white" }} />
    </div>
  );
}

export default App;
